import React, { useState, useEffect, useRef } from 'react';
import { Map as MapLibreMap, Marker as MapLibreMarker, NavigationControl as MapLibreNavigationControl, setWorkerUrl as setMapLibreWorkerUrl } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Users, CheckCircle, Clock, Upload, LogOut, Database, Search, Filter, Plus, ArrowRight, AlertTriangle, FileText, Download, X, Trash2, Pencil, Menu, Lock, Heart, ChevronLeft, ClipboardList, MessageSquare, Send, ChevronRight, ChevronDown, Eye, EyeOff, Check, RefreshCw, CalendarClock, MapPin, ExternalLink, Paperclip, Play, RotateCcw, Bell, Bold, Italic, List, ListOrdered, Code, Link2, Heading2, Pin, HelpCircle, Image as ImageIcon, ChevronUp, Table, Quote, Archive } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import { mergeAttributes } from '@tiptap/core';
import TiptapStarterKit from '@tiptap/starter-kit';
import TiptapImage from '@tiptap/extension-image';
import TiptapLink from '@tiptap/extension-link';
import TiptapPlaceholder from '@tiptap/extension-placeholder';
import { TableKit as TiptapTableKit } from '@tiptap/extension-table';
import DOMPurify from 'dompurify';
import HiLargeLogo from './HiLarge.png';
import HISmallLogo from './HISmall.png';
import DefaultProfileIcon from './default-icon.png';
import { getTemplatesForOffice, CONSULTATION_OFFICES } from './documentTemplates';
import { getCareProTemplates } from './careProOnboardingTemplates';
import { saveCachedProfile, getCachedProfile, getLastKnownUserId, withTimeout } from './offlineProfileCache';
import { fillTemplate, bytesToBlob, formatDateValue, formatDateParts } from './pdfFill';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from './supabaseClient';
import {
  saveSessionLocal, getSessionLocal, getAllLocalSessions, markSessionSynced, getUnsyncedSessions, deleteSessionLocal,
  saveDocumentLocal, getDocumentsForSession, markDocumentSynced,
} from './consultationOfflineStore';
import { syncPendingConsultations, startAutoSync, stopAutoSync, onSyncStatusChange } from './consultationSync';
import TransferLetterPage from './TransferLetterPage';
import JSZip from 'jszip';

// maplibre-gl computes its worker script URL at runtime rather than via a
// webpack-analyzable static import (the source of the harmless "Critical
// dependency" build warning) -- left alone, that computed URL doesn't match
// any file CRA actually emits, the request 404s, and this app's SPA
// catch-all route serves index.html back instead (surfacing in the browser
// console as a "non-JavaScript MIME type" worker-load failure). Pointing it
// at a real, manually-copied static file sidesteps that entirely -- same
// pattern already used for pdf.worker.min.mjs (see GlobalWorkerOptions.workerSrc below).
setMapLibreWorkerUrl(`${process.env.PUBLIC_URL || ''}/maplibre-gl-worker.mjs`);

// Utility functions
// crypto.randomUUID() only exists in secure contexts (HTTPS, or localhost) —
// on a plain http://<lan-ip> origin (e.g. local dev testing on another
// device) it's simply undefined, so this falls back to a Math.random()-based
// v4 UUID. Fine for a locally-generated id like this; not security-sensitive.
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getProgress = (tasks) => {
  if (!tasks || tasks.length === 0) return 0;
  const completed = tasks.filter(t => t.status === 'completed').length;
  return Math.round((completed / tasks.length) * 100);
};

const formatPhoneNumber = (value) => {
  const digits = (value ?? '').replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return value || 'Unknown';
};

// Build a PostgREST or-filter clause for one column. Values are double-quoted
// so commas/parentheses in phone-number search terms don't get parsed as
// or-filter syntax delimiters.
const orIlike = (column, rawValue) => `${column}.ilike."%${rawValue.replace(/"/g, '')}%"`;

// One-time-per-address-change geocoding for the Visit Planner map. Fire and
// forget from the caller's perspective -- never blocks a client save, and
// never re-geocodes an address that hasn't changed (callers only invoke this
// when address_line1/city/state/postal_code actually differ from before).
const geocodeClientAddress = async (clientId, { address_line1, city, state, postal_code }) => {
  if (!address_line1?.trim()) return;
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/geocode-address`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
      body: JSON.stringify({ addressLine1: address_line1, city, state, postalCode: postal_code }),
    });
    const data = await res.json();
    if (data?.latitude != null && data?.longitude != null) {
      await supabase.from('clients').update({ latitude: data.latitude, longitude: data.longitude, geocoded_at: new Date().toISOString() }).eq('id', clientId);
    }
  } catch (err) {
    console.error('Geocoding failed (non-blocking):', err);
  }
};

// Visit status thresholds shared between the Visit Planner map, filters, and
// popup -- the one place "how urgent is this due date" gets decided, so
// tightening the early-eligible window later (visit_type_settings) only
// means changing the settings row, not this logic.
const computeVisitStatus = (dueDateStr, earlyWindowDays, today = new Date()) => {
  if (!dueDateStr) return 'unscheduled';
  const due = new Date(dueDateStr + 'T00:00:00');
  const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const daysUntilDue = Math.round((due - todayMid) / 86400000);
  if (daysUntilDue < 0) return 'overdue';
  if (daysUntilDue <= 7) return 'due_7';
  if (daysUntilDue <= 14) return 'due_14';
  if (daysUntilDue <= 30) return 'due_30';
  if (daysUntilDue <= earlyWindowDays) return 'eligible_early';
  return 'not_yet_eligible';
};

const VISIT_STATUS_META = {
  overdue: { label: 'Overdue', color: '#dc2626', badge: 'bg-red-100 text-red-700' },
  due_7: { label: 'Due within 7 days', color: '#ea580c', badge: 'bg-orange-100 text-orange-700' },
  due_14: { label: 'Due within 14 days', color: '#ca8a04', badge: 'bg-yellow-100 text-yellow-700' },
  due_30: { label: 'Due within 30 days', color: '#ca8a04', badge: 'bg-yellow-50 text-yellow-700' },
  eligible_early: { label: 'Eligible to complete early', color: '#16a34a', badge: 'bg-green-100 text-green-700' },
  not_yet_eligible: { label: 'Not yet eligible', color: '#6b7280', badge: 'bg-gray-100 text-gray-600' },
  unscheduled: { label: 'No visit scheduled', color: '#57534e', badge: 'bg-gray-100 text-gray-500' },
};

const STATUS_URGENCY_ORDER = ['overdue', 'due_7', 'due_14', 'due_30', 'eligible_early', 'not_yet_eligible', 'unscheduled'];
const moreUrgent = (a, b) => STATUS_URGENCY_ORDER.indexOf(a) <= STATUS_URGENCY_ORDER.indexOf(b) ? a : b;

// Free, MapLibre-native hosted vector tiles (OpenFreeMap) -- no key, no
// request cap, no Google Maps, effectively zero ongoing cost. Kept as a
// single constant so swapping to a self-hosted PMTiles file later is a
// one-line change, not a refactor.
const MAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';

// Toast Notification Component
const Toast = ({ message, show, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onClose, 300); // Wait for fade out animation
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show && !isVisible) return null;

  return (
    <div
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      }`}
    >
      <div className="bg-green-600 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
        <CheckCircle size={24} />
        <span className="font-semibold text-lg">{message}</span>
      </div>
    </div>
  );
};

// Modal Component
const AddHireModal = ({ show, onClose, onSubmit, formData, setFormData, error, applicationUrl, onDone }) => {
  const [copied, setCopied] = useState(false);
  if (!show) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(applicationUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (applicationUrl) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={36} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Hire Added!</h2>
            <p className="text-gray-700 mb-6">Share this link with <strong>{formData.fullName}</strong> so they can complete their application.</p>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4 text-left">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Application Link</p>
              <p className="text-sm text-gray-700 break-all font-mono">{applicationUrl}</p>
            </div>

            <button
              onClick={handleCopy}
              className={`w-full py-3 rounded-xl font-semibold transition mb-3 ${copied ? 'bg-green-600 text-white' : 'bg-[#2c4f26] text-white hover:bg-[#1f3a1c]'}`}
            >
              {copied ? '✓ Copied!' : 'Copy Link'}
            </button>
            <button
              onClick={onDone}
              className="w-full py-3 rounded-xl font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Add New Hire</h2>
            <button
              onClick={onClose}
              className="text-gray-600 hover:text-gray-600 transition"
              type="button"
            >
              <span className="text-2xl">&times;</span>
            </button>
          </div>
        </div>

        <form onSubmit={onSubmit} className="p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                placeholder="john@company.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Position <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                placeholder="Care Pro"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                required
              />
            </div>

            <div className="bg-[#f0f5f0] border border-[#c5d8c3] rounded-xl p-4">
              <p className="text-sm text-[#2c4f26]">
                A unique application link will be generated. Share it with the new hire — no account or password needed.
              </p>
            </div>
          </div>

          <div className="flex gap-4 mt-8">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-[#2c4f26] text-white rounded-xl font-semibold hover:bg-[#1f3a1c] transition shadow-lg shadow-[#2c4f26]/30"
            >
              Add New Hire
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Login Screen Component
const HomePage = ({ onLogin }) => (
  <div className="min-h-screen bg-white flex flex-col">
    {/* Navbar */}
    <nav className="border-b border-gray-100 sticky top-0 bg-white z-40">
      <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
        <img src={HiLargeLogo} alt="Home Instead" className="h-16 w-auto object-contain" />
        <div className="flex items-center gap-6">
          <a href="/support" className="text-sm font-medium text-gray-600 hover:text-[#2c4f26] transition">Support</a>
          <button onClick={onLogin} className="bg-[#2c4f26] text-white text-sm font-semibold px-5 py-2 rounded-lg hover:bg-[#1f3a1c] transition">Login</button>
        </div>
      </div>
    </nav>

    {/* Hero */}
    <div className="flex-1 flex items-center justify-center px-6 py-10">
      <div className="max-w-2xl w-full text-center">
        <div className="inline-flex items-center gap-2 bg-[#f0f5f0] text-[#2c4f26] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
          <span className="w-1.5 h-1.5 bg-[#2c4f26] rounded-full" />
          Manassas / Leesburg
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-4">
          Home Instead<br />CarePro &amp; Staff Portal
        </h1>
        <p className="text-lg text-gray-500 mb-8 max-w-lg mx-auto leading-relaxed">
          Submit a support request or sign in to the staff portal to manage onboarding, clients, and CarePro services.
        </p>
        <div className="flex gap-3 justify-center flex-wrap">
          <a href="/support" className="bg-[#2c4f26] text-white px-7 py-3 rounded-xl font-semibold text-sm hover:bg-[#1f3a1c] transition shadow-lg shadow-[#2c4f26]/20">
            Submit a Support Request
          </a>
          <button onClick={onLogin} className="border border-gray-200 text-gray-700 px-7 py-3 rounded-xl font-semibold text-sm hover:bg-gray-50 transition">
            Staff Login →
          </button>
        </div>
      </div>
    </div>

    {/* Footer */}
    <div className="border-t border-gray-100 py-5 text-center">
      <p className="text-xs text-gray-400">© {new Date().getFullYear()} Home Instead Manassas/Leesburg. All rights reserved.</p>
    </div>
  </div>
);

// Temporarily off — see the note where this is used below.
const REQUEST_ACCESS_ENABLED = false;

const LoginScreen = ({ onSubmit, onSignUp, onNewApplicant, onBack, error }) => {
  const [tab, setTab] = useState('signin'); // 'signin' | 'request'
  const [localEmail, setLocalEmail] = useState('');
  const [localPassword, setLocalPassword] = useState('');
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  // Corporate email security (Safe Links and similar) silently pre-visits
  // and burns one-time reset links before the person ever opens the email,
  // so the link itself can't be relied on. The same email also carries a
  // 6-digit code ({{ .Token }} in the Reset Password template) that a
  // scanner can't "click" — verifying that code establishes the same
  // recovery session a working link would (verifyOtp fires the identical
  // PASSWORD_RECOVERY event), landing on the same Set New Password screen.
  const [resetCode, setResetCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  // Request access fields
  const [reqName, setReqName] = useState('');
  const [reqEmail, setReqEmail] = useState('');
  const [reqPassword, setReqPassword] = useState('');
  const [reqConfirm, setReqConfirm] = useState('');
  const [reqError, setReqError] = useState('');
  const [reqSubmitting, setReqSubmitting] = useState(false);
  const [reqDone, setReqDone] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(localEmail, localPassword);
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setReqError('');
    if (reqPassword !== reqConfirm) { setReqError('Passwords do not match.'); return; }
    if (reqPassword.length < 6) { setReqError('Password must be at least 6 characters.'); return; }
    setReqSubmitting(true);
    try {
      await onSignUp(reqName.trim(), reqEmail.trim(), reqPassword);
      setReqDone(true);
    } catch (err) {
      setReqError(err.message || 'Failed to submit request.');
    } finally {
      setReqSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setIsResetting(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: window.location.origin,
      });

      if (error) throw error;

      setResetSent(true);
    } catch (err) {
      setResetError(err.message || 'Failed to send reset email');
    } finally {
      setIsResetting(false);
    }
  };

  const handleVerifyResetCode = async (e) => {
    e.preventDefault();
    setCodeError('');
    setVerifyingCode(true);
    try {
      const { error } = await supabase.auth.verifyOtp({ email: resetEmail, token: resetCode.trim(), type: 'recovery' });
      if (error) throw error;
      // Success hands off to the app-level PASSWORD_RECOVERY listener, which
      // shows the Set New Password screen — nothing further to do here.
    } catch (err) {
      setCodeError(err.message || 'Invalid or expired code');
    } finally {
      setVerifyingCode(false);
    }
  };

  if (forgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-md border border-gray-100">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img
                src={HiLargeLogo}
                alt="Home Instead Logo"
                className="h-24 w-auto object-contain"
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Reset Password</h1>
            <p className="text-gray-700 mt-2">Enter your email to receive a reset link</p>
          </div>

          {resetSent ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Check Your Email</h2>
              <p className="text-gray-600 mb-6">
                We've sent a code and a link to <strong>{resetEmail}</strong>. If the link doesn't work
                (some company email systems block it automatically), enter the 6-digit code from that email below instead.
              </p>

              <form onSubmit={handleVerifyResetCode} className="space-y-4 text-left mb-4">
                {codeError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{codeError}</div>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">6-Digit Code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-center text-lg tracking-[0.3em]"
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>
                <button
                  type="submit"
                  disabled={verifyingCode || resetCode.trim().length < 6}
                  className="w-full bg-[#2c4f26] text-white py-3.5 rounded-xl font-semibold hover:bg-[#1f3a1c] transition shadow-lg shadow-[#2c4f26]/30 disabled:opacity-50"
                >
                  {verifyingCode ? 'Verifying…' : 'Verify Code'}
                </button>
              </form>

              <button
                onClick={() => {
                  setForgotPassword(false);
                  setResetSent(false);
                  setResetEmail('');
                  setResetCode('');
                  setCodeError('');
                }}
                className="w-full text-gray-600 py-2 font-medium hover:text-gray-900 transition"
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              {resetError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  {resetError}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                />
              </div>

              <button
                type="submit"
                disabled={isResetting}
                className="w-full bg-[#2c4f26] text-white py-3.5 rounded-xl font-semibold hover:bg-[#1f3a1c] transition shadow-lg shadow-[#2c4f26]/30 disabled:opacity-50"
              >
                {isResetting ? 'Sending...' : 'Send Reset Link'}
              </button>

              {/* Always visible, independent of resetSent — that flag only
                  reflects THIS page load, but the code from an earlier
                  email/session is still valid, so there must always be a
                  way to use it without re-sending first. */}
              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-600 mb-3">Already have a 6-digit code from an earlier email?</p>
                {codeError && (
                  <div className="p-3 mb-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{codeError}</div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="flex-1 min-w-0 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-center tracking-[0.3em]"
                    placeholder="000000"
                    maxLength={6}
                  />
                  <button
                    type="button"
                    onClick={handleVerifyResetCode}
                    disabled={verifyingCode || resetCode.trim().length < 6 || !resetEmail.trim()}
                    className="shrink-0 px-5 py-3 border border-[#2c4f26] text-[#2c4f26] rounded-xl font-semibold hover:bg-[#2c4f26]/5 transition disabled:opacity-50"
                  >
                    {verifyingCode ? 'Verifying…' : 'Verify'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">Enter the email above first, then the code.</p>
              </div>

              <button
                type="button"
                onClick={() => setForgotPassword(false)}
                className="w-full text-gray-600 py-2 font-medium hover:text-gray-900 transition"
              >
                Back to Sign In
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-6">
      {onBack && (
        <button onClick={onBack} className="absolute top-6 left-6 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition">
          <ChevronLeft size={16} /> Back
        </button>
      )}
      <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-md border border-gray-100">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <img src={HiLargeLogo} alt="Home Instead Logo" className="h-24 w-auto object-contain" />
          </div>
        </div>

        {/* Tab switcher — Request Access temporarily hidden (see
            REQUEST_ACCESS_ENABLED below): it was handing out live, active
            "applicant"-role accounts with zero approval gate instead of
            the intended pending-caregiver-approval flow, because a
            database trigger auto-creates a profile before this form's own
            insert runs. Flip that flag back on once the underlying signup
            bug is fixed. */}
        {REQUEST_ACCESS_ENABLED && (
          <div className="flex rounded-xl bg-gray-100 p-1 mb-6">
            <button onClick={() => setTab('signin')} className={`flex-1 py-2 text-sm font-medium rounded-lg transition ${tab === 'signin' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>Sign In</button>
            <button onClick={() => setTab('request')} className={`flex-1 py-2 text-sm font-medium rounded-lg transition ${tab === 'request' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>Request Access</button>
          </div>
        )}

        {tab === 'signin' || !REQUEST_ACCESS_ENABLED ? (
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
              <input type="email" value={localEmail} onChange={(e) => setLocalEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" placeholder="you@company.com" required autoComplete="email" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
              <input type="password" value={localPassword} onChange={(e) => setLocalPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" placeholder="••••••••" required autoComplete="current-password" />
            </div>
            <div className="text-right">
              <button type="button" onClick={() => setForgotPassword(true)} className="text-sm text-[#2c4f26] hover:text-[#1f3a1c] font-medium">Forgot Password?</button>
            </div>
            <button type="submit" className="w-full bg-[#2c4f26] text-white py-3.5 rounded-xl font-semibold hover:bg-[#1f3a1c] transition shadow-lg shadow-[#2c4f26]/30">Sign In</button>
          </form>
        ) : reqDone ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Request Submitted</h2>
            <p className="text-gray-600 text-sm">Your access request has been submitted. You'll be able to sign in once an administrator approves your account.</p>
            <button onClick={() => { setReqDone(false); setTab('signin'); }} className="mt-6 text-sm text-[#2c4f26] font-medium hover:underline">Back to Sign In</button>
          </div>
        ) : (
          <form onSubmit={handleRequestAccess} className="space-y-4">
            <p className="text-sm text-gray-600 mb-2">Request caregiver portal access. An administrator will review and approve your account.</p>
            {reqError && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{reqError}</div>}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
              <input type="text" value={reqName} onChange={(e) => setReqName(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" placeholder="Jane Smith" required />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
              <input type="email" value={reqEmail} onChange={(e) => setReqEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" placeholder="you@email.com" required />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
              <input type="password" value={reqPassword} onChange={(e) => setReqPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" placeholder="••••••••" required />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm Password</label>
              <input type="password" value={reqConfirm} onChange={(e) => setReqConfirm(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" placeholder="••••••••" required />
            </div>
            <button type="submit" disabled={reqSubmitting} className="w-full bg-[#2c4f26] text-white py-3.5 rounded-xl font-semibold hover:bg-[#1f3a1c] transition shadow-lg shadow-[#2c4f26]/30 disabled:opacity-60">
              {reqSubmitting ? 'Submitting…' : 'Request Access'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// New Applicant Flow Component
const NewApplicantFlow = ({ onBack, onComplete, showToast }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    // Prescreening questions (placeholders)
    question1: '',
    question2: '',
    question3: '',
    question4: '',
    question5: ''
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Format as XXX-XXX-XXXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    updateField('phone', formatted);
  };

  const handleStep1Next = async () => {
    setError('');
    setIsLoading(true);

    try {
      // Check if email already exists in profiles
      const { data: existingProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id, email')
        .eq('email', formData.email.toLowerCase().trim())
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError;
      }

      if (existingProfile) {
        // Email exists - offer password reset
        setEmailExists(true);
        setStep(2);
      } else {
        // Email doesn't exist - continue to prescreening
        setEmailExists(false);
        setStep(2);
      }
    } catch (err) {
      console.error('Error checking email:', err);
      setError('Error checking email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendPasswordReset = async () => {
    setIsLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: window.location.origin
      });

      if (error) throw error;

      setResetEmailSent(true);
      showToast('Password reset email sent!');
    } catch (err) {
      console.error('Error sending reset email:', err);
      setError('Failed to send password reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitApplication = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Create the user account (trigger will auto-create profile)
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email.toLowerCase().trim(),
        password: Math.random().toString(36).slice(-12) + 'A1!', // Temporary password
        options: {
          data: {
            full_name: `${formData.firstName} ${formData.lastName}`,
            phone: formData.phone
          }
        }
      });

      if (authError) throw authError;

      // Send password reset so they can set their own password
      await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: window.location.origin
      });

      // Update profile with additional info (profile created by trigger)
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          phone: formData.phone
        })
        .eq('id', authData.user.id);

      // Don't throw on profile update error - profile may not exist yet due to trigger timing
      if (profileError) {
        console.warn('Profile update warning:', profileError);
      }

      // Store prescreening data (you can create a prescreening_responses table later)
      // For now, we'll just complete the flow

      showToast('Application submitted! Check your email to set your password.');
      onComplete();
    } catch (err) {
      console.error('Error submitting application:', err);
      setError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-lg border border-gray-100">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <img
              src={HiLargeLogo}
              alt="Home Instead Logo"
              className="h-20 w-auto object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">New Applicant</h1>
          <p className="text-gray-700 mt-1">Step {step} of {totalSteps}</p>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-8">
          <div
            className="bg-[#2c4f26] h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm mb-6">
            {error}
          </div>
        )}

        {/* Step 1: Basic Information */}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => updateField('firstName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => updateField('lastName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={handlePhoneChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                placeholder="123-456-7890"
                maxLength={12}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                placeholder="123 Main St"
                required
              />
            </div>

            <div className="grid grid-cols-6 gap-4">
              <div className="col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                  required
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => updateField('state', e.target.value.toUpperCase().slice(0, 2))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                  placeholder="TX"
                  maxLength={2}
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                <input
                  type="text"
                  value={formData.zip}
                  onChange={(e) => updateField('zip', e.target.value.replace(/\D/g, '').slice(0, 5))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                  placeholder="12345"
                  maxLength={5}
                  required
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={onBack}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Back to Login
              </button>
              <button
                onClick={handleStep1Next}
                disabled={isLoading || !formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.address || !formData.city || !formData.state || !formData.zip}
                className="flex-1 px-4 py-3 bg-[#2c4f26] text-white rounded-xl font-semibold hover:bg-[#1f3a1c] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Checking...' : 'Next'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Email Check Result / Prescreening */}
        {step === 2 && emailExists && (
          <div className="space-y-4">
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={32} className="text-yellow-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Account Already Exists</h2>
              <p className="text-gray-600">
                An account with the email <strong>{formData.email}</strong> already exists.
              </p>
            </div>

            {resetEmailSent ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <p className="text-gray-600 mb-6">
                  Password reset email sent! Check your inbox to reset your password.
                </p>
                <button
                  onClick={onBack}
                  className="w-full px-4 py-3 bg-[#2c4f26] text-white rounded-xl font-semibold hover:bg-[#1f3a1c] transition"
                >
                  Back to Login
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleSendPasswordReset}
                  disabled={isLoading}
                  className="w-full px-4 py-3 bg-[#2c4f26] text-white rounded-xl font-semibold hover:bg-[#1f3a1c] transition disabled:opacity-50"
                >
                  {isLoading ? 'Sending...' : 'Send Password Reset Email'}
                </button>
                <button
                  onClick={onBack}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  Back to Login
                </button>
              </div>
            )}
          </div>
        )}

        {step === 2 && !emailExists && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Prescreening Questions</h2>
            <p className="text-sm text-gray-700 mb-4">Please answer the following questions to continue your application.</p>

            {/* Placeholder prescreening questions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question 1 (Placeholder)</label>
              <input
                type="text"
                value={formData.question1}
                onChange={(e) => updateField('question1', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                placeholder="Your answer..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question 2 (Placeholder)</label>
              <input
                type="text"
                value={formData.question2}
                onChange={(e) => updateField('question2', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                placeholder="Your answer..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question 3 (Placeholder)</label>
              <input
                type="text"
                value={formData.question3}
                onChange={(e) => updateField('question3', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                placeholder="Your answer..."
              />
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep(1)}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                className="flex-1 px-4 py-3 bg-[#2c4f26] text-white rounded-xl font-semibold hover:bg-[#1f3a1c] transition"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 3: More Prescreening + Submit */}
        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Questions</h2>

            {/* More placeholder questions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question 4 (Placeholder)</label>
              <textarea
                value={formData.question4}
                onChange={(e) => updateField('question4', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                rows={3}
                placeholder="Your answer..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question 5 (Placeholder)</label>
              <textarea
                value={formData.question5}
                onChange={(e) => updateField('question5', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                rows={3}
                placeholder="Your answer..."
              />
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mt-4">
              <h3 className="font-medium text-gray-900 mb-2">Review Your Information</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Name:</strong> {formData.firstName} {formData.lastName}</p>
                <p><strong>Email:</strong> {formData.email}</p>
                <p><strong>Phone:</strong> {formData.phone}</p>
                <p><strong>Address:</strong> {formData.address}, {formData.city}, {formData.state} {formData.zip}</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep(2)}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Back
              </button>
              <button
                onClick={handleSubmitApplication}
                disabled={isLoading}
                className="flex-1 px-4 py-3 bg-[#2c4f26] text-white rounded-xl font-semibold hover:bg-[#1f3a1c] transition disabled:opacity-50"
              >
                {isLoading ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Applicant Detail View Component (Admin viewing specific applicant)
const ApplicantDetailView = ({
  applicant,
  profile,
  permissions,
  onBack,
  toggleTask,
  handleLogout,
  onUpdate,
  showToast
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: applicant.name,
    email: applicant.email,
    phone: applicant.phone || '',
    position: applicant.position,
    startDate: applicant.startDate
  });
  const [editError, setEditError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [documentModal, setDocumentModal] = useState({ isOpen: false, url: null, fileName: null, fileType: null, loading: false });
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, document: null, taskId: null, isDeleting: false });
  const [linkCopied, setLinkCopied] = useState(false);
  const [isEditingTasks, setIsEditingTasks] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [taskEditForm, setTaskEditForm] = useState({ title: '', description: '', allow_upload: false, is_admin_task: false });
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [newTaskForm, setNewTaskForm] = useState({ title: '', description: '', allow_upload: false, is_admin_task: false });
  const [orientationGroups, setOrientationGroups] = useState([]);
  const [savingOrientationField, setSavingOrientationField] = useState(false);

  useEffect(() => {
    if (applicant.source !== 'paradox') return;
    supabase.from('orientation_groups').select('id, name').order('session_datetime').then(({ data }) => setOrientationGroups(data || []));
  }, [applicant.source]);

  const updateOrientationField = async (fields) => {
    setSavingOrientationField(true);
    try {
      const { error } = await supabase.from('applicants').update(fields).eq('id', applicant.id);
      if (error) throw error;
      if (onUpdate) await onUpdate();
    } catch (err) {
      console.error('updateOrientationField error:', err);
      showToast('Failed to update');
    } finally {
      setSavingOrientationField(false);
    }
  };

  const progress = getProgress(applicant.tasks);

  // Task editing functions
  const handleEditTask = (task) => {
    setEditingTaskId(task.id);
    setTaskEditForm({ title: task.title, description: task.description || '', allow_upload: task.allow_upload || false, is_admin_task: task.is_admin_task || false });
  };

  const handleSaveTask = async (taskId) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({
          title: taskEditForm.title,
          description: taskEditForm.description,
          allow_upload: taskEditForm.allow_upload,
          is_admin_task: taskEditForm.is_admin_task
        })
        .eq('id', taskId);

      if (error) throw error;

      setEditingTaskId(null);
      showToast('Task updated successfully');
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error updating task:', error);
      showToast('Error updating task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      // First delete any associated documents
      const { error: docError } = await supabase
        .from('documents')
        .delete()
        .eq('task_id', taskId);

      if (docError) console.warn('Error deleting task documents:', docError);

      // Then delete the task
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;

      showToast('Task deleted successfully');
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error deleting task:', error);
      showToast('Error deleting task');
    }
  };

  const handleAddTask = async () => {
    if (!newTaskForm.title.trim()) return;

    try {
      const maxOrder = Math.max(...applicant.tasks.map(t => t.order_index || 0), 0);

      const { error } = await supabase
        .from('tasks')
        .insert({
          applicant_id: applicant.id,
          title: newTaskForm.title,
          description: newTaskForm.description,
          allow_upload: newTaskForm.allow_upload,
          is_admin_task: newTaskForm.is_admin_task,
          status: 'pending',
          order_index: maxOrder + 1
        });

      if (error) throw error;

      setNewTaskForm({ title: '', description: '', allow_upload: false, is_admin_task: false });
      setIsAddingTask(false);
      showToast('Task added successfully');
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error adding task:', error);
      showToast('Error adding task');
    }
  };

  const viewDocument = async (filePath, fileName, fileType) => {
    setDocumentModal({ isOpen: true, url: null, fileName, fileType, loading: true, error: null });

    try {
      // Get signed URL first
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('applicant-documents')
        .createSignedUrl(filePath, 60); // 60 seconds expiry

      if (signedUrlError) throw signedUrlError;

      // Fetch with cache disabled to always get fresh content
      const response = await fetch(signedUrlData.signedUrl, { cache: 'no-store' });

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setDocumentModal({ isOpen: true, url, fileName, fileType, loading: false, error: null });
    } catch (error) {
      console.error('Error viewing document:', error);
      const errorMsg = error?.message || String(error) || 'Unknown error';
      setDocumentModal({
        isOpen: true,
        url: null,
        fileName,
        fileType,
        loading: false,
        error: `File: ${filePath}\nError: ${errorMsg}`
      });
    }
  };

  const closeDocumentModal = () => {
    if (documentModal.url) {
      URL.revokeObjectURL(documentModal.url);
    }
    setDocumentModal({ isOpen: false, url: null, fileName: null, fileType: null, loading: false });
  };

  const handleDeleteDocument = async () => {
    if (!deleteConfirm.document) return;

    setDeleteConfirm(prev => ({ ...prev, isDeleting: true }));

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('applicant-documents')
        .remove([deleteConfirm.document.file_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .eq('id', deleteConfirm.document.id);

      if (dbError) throw dbError;

      // Reset task status to pending since document was removed
      const { error: taskError } = await supabase
        .from('tasks')
        .update({ status: 'pending' })
        .eq('id', deleteConfirm.taskId);

      if (taskError) throw taskError;

      showToast('Document deleted successfully');
      setDeleteConfirm({ isOpen: false, document: null, taskId: null, isDeleting: false });

      // Refresh data
      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      showToast('Error deleting document');
      setDeleteConfirm(prev => ({ ...prev, isDeleting: false }));
    }
  };
  const completedTasks = applicant.tasks.filter(t => t.status === 'completed').length;

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      const { error } = await supabase
        .from('applicants')
        .update({
          status: 'approved',
          approved_at: new Date().toISOString()
        })
        .eq('id', applicant.id);

      if (error) throw error;

      // Update local state
      applicant.status = 'approved';

      // Refresh admin data
      if (onUpdate) {
        await onUpdate();
      }

      showToast('Applicant approved successfully!');
    } catch (error) {
      console.error('Error approving applicant:', error);
      alert('Failed to approve applicant: ' + error.message);
    } finally {
      setIsApproving(false);
    }
  };

  const handleToggleStatus = async () => {
    setIsTogglingStatus(true);
    try {
      const newStatus = applicant.status === 'inactive' ? 'active' : 'inactive';

      const { error } = await supabase
        .from('applicants')
        .update({ status: newStatus })
        .eq('id', applicant.id);

      if (error) throw error;

      // Update local state
      applicant.status = newStatus;

      // Refresh admin data
      if (onUpdate) {
        await onUpdate();
      }

      showToast(`Applicant ${newStatus === 'inactive' ? 'deactivated' : 'reactivated'} successfully!`);
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update status: ' + error.message);
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const handleSaveEdit = async () => {
    setEditError('');
    setIsSaving(true);

    try {
      // Find the user_id from the applicants table first
      const { data: applicantData, error: fetchError } = await supabase
        .from('applicants')
        .select('user_id')
        .eq('id', applicant.id)
        .single();

      if (fetchError) {
        console.error('Fetch error:', fetchError);
        throw new Error('Failed to fetch applicant data: ' + fetchError.message);
      }

      console.log('Updating profile for user_id:', applicantData.user_id);

      // Update profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: editForm.name,
          email: editForm.email,
          phone: editForm.phone || null
        })
        .eq('id', applicantData.user_id)
        .select();

      if (profileError) {
        console.error('Profile update error:', profileError);
        throw new Error('Failed to update profile: ' + profileError.message + '. You may need to add UPDATE RLS policies.');
      }

      console.log('Profile updated:', profileData);
      console.log('Updating applicant for id:', applicant.id);

      // Update applicant
      const { data: updatedApplicant, error: applicantError } = await supabase
        .from('applicants')
        .update({
          position: editForm.position,
          start_date: editForm.startDate
        })
        .eq('id', applicant.id)
        .select();

      if (applicantError) {
        console.error('Applicant update error:', applicantError);
        throw new Error('Failed to update applicant: ' + applicantError.message + '. You may need to add UPDATE RLS policies.');
      }

      console.log('Applicant updated:', updatedApplicant);

      // Update local state
      applicant.name = editForm.name;
      applicant.email = editForm.email;
      applicant.phone = editForm.phone;
      applicant.position = editForm.position;
      applicant.startDate = editForm.startDate;

      setIsEditing(false);

      // Refresh admin data
      if (onUpdate) {
        await onUpdate();
      }

      showToast('Applicant details updated successfully!');
    } catch (error) {
      console.error('Error updating applicant:', error);
      setEditError(error.message || 'Failed to update applicant details');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-[#2c4f26] hover:text-[#1f3a1c] font-medium mb-6 text-sm"
        >
          ← Back to Dashboard
        </button>
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3 md:gap-4">
              <img
                src={applicant.profile_image || DefaultProfileIcon}
                alt={applicant.name}
                className="w-12 h-12 md:w-16 md:h-16 rounded-xl object-cover"
              />
              <div>
                <div className="flex flex-wrap items-center gap-2 md:gap-3">
                  <h1 className="text-xl md:text-3xl font-bold text-gray-900">{applicant.name}</h1>
                  {applicant.status === 'approved' && (
                    <span className="px-2 md:px-3 py-0.5 md:py-1 bg-green-100 text-green-700 text-xs md:text-sm font-semibold rounded-full">
                      Approved
                    </span>
                  )}
                  {applicant.status === 'inactive' && (
                    <span className="px-2 md:px-3 py-0.5 md:py-1 bg-gray-100 text-gray-700 text-xs md:text-sm font-semibold rounded-full">
                      Inactive
                    </span>
                  )}
                  {applicant.source === 'paradox' && (
                    <span className="px-2 md:px-3 py-0.5 md:py-1 bg-blue-100 text-blue-700 text-xs md:text-sm font-semibold rounded-full">
                      From Paradox
                    </span>
                  )}
                </div>
                <p className="text-sm md:text-base text-gray-600">{applicant.position}</p>
              </div>
            </div>
            {!isEditing && (
              <div className="flex flex-wrap gap-2 md:gap-3">
                {applicant.application_token && (
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}${window.location.pathname}?token=${applicant.application_token}`;
                      navigator.clipboard.writeText(url).then(() => {
                        setLinkCopied(true);
                        setTimeout(() => setLinkCopied(false), 2000);
                      });
                    }}
                    className={`px-3 md:px-4 py-2 rounded-lg text-sm font-semibold border transition ${linkCopied ? 'border-green-500 text-green-700 bg-green-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                  >
                    {linkCopied ? '✓ Copied!' : 'Copy App Link'}
                  </button>
                )}
                {progress === 100 && applicant.status !== 'approved' && (
                  <button
                    onClick={handleApprove}
                    disabled={isApproving}
                    className="px-3 md:px-4 py-2 bg-green-600 text-white rounded-lg text-sm md:text-base font-semibold hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isApproving ? 'Approving...' : applicant.source === 'paradox' ? 'Mark Fully Hired' : 'Approve'}
                  </button>
                )}
                {permissions?.can_edit_hires && (
                  <button
                    onClick={handleToggleStatus}
                    disabled={isTogglingStatus}
                    className={`px-3 md:px-4 py-2 rounded-lg text-sm md:text-base font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${
                      applicant.status === 'inactive'
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-600 text-white hover:bg-gray-700'
                    }`}
                  >
                    {isTogglingStatus ? 'Updating...' : applicant.status === 'inactive' ? 'Reactivate' : 'Inactive'}
                  </button>
                )}
                {permissions?.can_edit_hires && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-3 md:px-4 py-2 bg-[#2c4f26] text-white rounded-lg text-sm md:text-base font-semibold hover:bg-[#1f3a1c] transition"
                  >
                    Edit
                  </button>
                )}
              </div>
            )}
          </div>

          {applicant.source === 'paradox' && (
            <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-100 mb-6">
              <h3 className="text-base md:text-lg font-bold text-gray-900 mb-4">Orientation</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Orientation Group</label>
                  <select
                    value={applicant.orientation_group_id || ''}
                    disabled={savingOrientationField}
                    onChange={(e) => updateOrientationField({ orientation_group_id: e.target.value || null })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                  >
                    <option value="">Unassigned</option>
                    {orientationGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Drug Test</label>
                  <select
                    value={applicant.drug_test_status || 'not_scheduled'}
                    disabled={savingOrientationField}
                    onChange={(e) => updateOrientationField({ drug_test_status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                  >
                    <option value="not_scheduled">Not Scheduled</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="passed">Passed</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!applicant.ai_training_complete}
                      disabled={savingOrientationField}
                      onChange={(e) => updateOrientationField({ ai_training_complete: e.target.checked })}
                      className="w-4 h-4"
                    />
                    Activated Insights training complete
                  </label>
                </div>
              </div>
              {!applicant.ai_training_complete && (
                <p className="text-xs text-amber-700 mt-3">Reminder: Activated Insights training should be confirmed before Part 2 orientation.</p>
              )}
            </div>
          )}

          {isEditing ? (
            <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-100 mb-6">
              <h3 className="text-base md:text-lg font-bold text-gray-900 mb-4">Edit Applicant Details</h3>

              {editError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {editError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Position
                  </label>
                  <input
                    type="text"
                    value={editForm.position}
                    onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                    placeholder="Optional"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={editForm.startDate}
                    onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className="flex-1 px-6 py-3 bg-[#2c4f26] text-white rounded-xl font-semibold hover:bg-[#1f3a1c] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditError('');
                    setEditForm({
                      name: applicant.name,
                      email: applicant.email,
                      phone: applicant.phone || '',
                      position: applicant.position,
                      startDate: applicant.startDate
                    });
                  }}
                  disabled={isSaving}
                  className="flex-1 px-6 py-3 border border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
              <div className="bg-white rounded-xl p-3 md:p-4 border border-gray-100">
                <div className="text-xs md:text-sm text-gray-700 mb-1">Email</div>
                <div className="text-sm md:text-base text-gray-900 font-medium truncate">{applicant.email}</div>
              </div>
              <div className="bg-white rounded-xl p-3 md:p-4 border border-gray-100">
                <div className="text-xs md:text-sm text-gray-700 mb-1">Phone</div>
                <div className="text-sm md:text-base text-gray-900 font-medium">{applicant.phone || 'Not provided'}</div>
              </div>
              <div className="bg-white rounded-xl p-3 md:p-4 border border-gray-100">
                <div className="text-xs md:text-sm text-gray-700 mb-1">Start Date</div>
                <div className="text-sm md:text-base text-gray-900 font-medium">
                  {new Date(applicant.startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-8 border border-gray-100 mb-4 md:mb-6">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-gray-900 mb-1">Onboarding Progress</h2>
              <p className="text-sm md:text-base text-gray-600">{completedTasks} of {applicant.tasks.length} tasks completed</p>
            </div>
            <div className="text-right">
              <div className={`text-3xl md:text-5xl font-bold ${progress === 100 ? 'text-green-600' : 'text-[#2c4f26]'}`}>
                {progress}%
              </div>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 md:h-4">
            <div
              className={`h-3 md:h-4 rounded-full transition-all ${progress === 100 ? 'bg-green-600' : 'bg-[#2c4f26]'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl md:rounded-2xl border border-gray-100">
          <div className="p-4 md:p-6 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-lg md:text-xl font-bold text-gray-900">Onboarding Tasks</h2>
                <p className="text-xs md:text-sm text-gray-700 mt-1">
                  {profile?.role === 'owner' || profile?.role === 'admin_staff'
                    ? 'Click checkboxes to mark tasks as complete or incomplete'
                    : 'View task progress'}
                </p>
              </div>
              {permissions?.can_edit_hires && (
                <button
                  onClick={() => {
                    setIsEditingTasks(!isEditingTasks);
                    setEditingTaskId(null);
                    setIsAddingTask(false);
                  }}
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition w-full sm:w-auto ${
                    isEditingTasks
                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      : 'bg-[#2c4f26] text-white hover:bg-[#1f3a1c]'
                  }`}
                >
                  <Pencil size={16} />
                  {isEditingTasks ? 'Done Editing' : 'Edit Tasks'}
                </button>
              )}
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {applicant.tasks.map((task) => (
              <div
                key={task.id}
                className="p-4 md:p-6 hover:bg-gray-50 transition"
              >
                {editingTaskId === task.id ? (
                  // Inline edit form
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Task Title</label>
                      <input
                        type="text"
                        value={taskEditForm.title}
                        onChange={(e) => setTaskEditForm({ ...taskEditForm, title: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                      <textarea
                        value={taskEditForm.description}
                        onChange={(e) => setTaskEditForm({ ...taskEditForm, description: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={taskEditForm.allow_upload}
                          onChange={(e) => setTaskEditForm({ ...taskEditForm, allow_upload: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2c4f26]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2c4f26]"></div>
                      </label>
                      <span className="text-sm font-medium text-gray-700">Allow document upload</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={taskEditForm.is_admin_task}
                          onChange={(e) => setTaskEditForm({ ...taskEditForm, is_admin_task: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2c4f26]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2c4f26]"></div>
                      </label>
                      <span className="text-sm font-medium text-gray-700">Admin only task</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveTask(task.id)}
                        className="px-4 py-2 bg-[#2c4f26] text-white rounded-lg text-sm font-semibold hover:bg-[#1f3a1c] transition"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingTaskId(null)}
                        className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Normal task display
                  <div className="flex items-start gap-3 md:gap-5">
                    <div
                      className="mt-0.5 md:mt-1 cursor-pointer flex-shrink-0"
                      onClick={() => toggleTask(task.id, task.status)}
                    >
                      {task.status === 'completed' ? (
                        <div className="w-6 h-6 md:w-7 md:h-7 bg-green-600 rounded-lg flex items-center justify-center">
                          <CheckCircle size={16} className="text-white md:hidden" />
                          <CheckCircle size={20} className="text-white hidden md:block" />
                        </div>
                      ) : (
                        <div className="w-6 h-6 md:w-7 md:h-7 border-2 border-gray-300 rounded-lg hover:border-[#2c4f26] transition" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
                        <h3 className={`text-sm md:text-lg font-semibold ${task.status === 'completed' ? 'text-gray-600 line-through' : 'text-gray-900'}`}>
                          {task.title}
                        </h3>
                        {task.status === 'completed' && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            Done
                          </span>
                        )}
                        {task.allow_upload && !task.document && task.status !== 'completed' && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full flex items-center gap-1">
                            <Upload size={12} />
                            <span className="hidden sm:inline">Upload Required</span>
                            <span className="sm:hidden">Upload</span>
                          </span>
                        )}
                        {task.is_admin_task && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                            Admin
                          </span>
                        )}
                        {isEditingTasks && (
                          <div className="flex gap-1 md:gap-2 ml-auto">
                            <button
                              onClick={() => handleEditTask(task)}
                              className="p-1.5 md:p-2 text-gray-700 hover:text-[#2c4f26] hover:bg-gray-100 rounded-lg transition"
                              title="Edit task"
                            >
                              <Pencil size={14} className="md:hidden" />
                              <Pencil size={16} className="hidden md:block" />
                            </button>
                            <button
                              onClick={() => handleDeleteTask(task.id)}
                              className="p-1.5 md:p-2 text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                              title="Delete task"
                            >
                              <Trash2 size={14} className="md:hidden" />
                              <Trash2 size={16} className="hidden md:block" />
                            </button>
                          </div>
                        )}
                      </div>
                      {task.description && (
                        <p className="text-gray-600 text-xs md:text-sm mb-2">{task.description}</p>
                      )}
                      {task.document && (
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() => viewDocument(task.document.file_path, task.document.file_name, task.document.file_type)}
                            className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-blue-50 text-blue-700 rounded-lg text-xs md:text-sm font-medium hover:bg-blue-100 transition"
                          >
                            <FileText size={14} className="md:hidden" />
                            <FileText size={16} className="hidden md:block" />
                            <span className="truncate max-w-[120px] md:max-w-none">{task.document.file_name}</span>
                            <Download size={12} className="ml-1 md:hidden" />
                            <Download size={14} className="ml-1 hidden md:block" />
                          </button>
                          {(profile?.role === 'admin_staff' || profile?.role === 'owner') && (
                            <button
                              onClick={() => setDeleteConfirm({ isOpen: true, document: task.document, taskId: task.id, isDeleting: false })}
                              className="inline-flex items-center gap-1 px-2.5 md:px-3 py-1.5 md:py-2 bg-red-50 text-red-600 rounded-lg text-xs md:text-sm font-medium hover:bg-red-100 transition"
                              title="Delete document"
                            >
                              <Trash2 size={14} className="md:hidden" />
                              <Trash2 size={16} className="hidden md:block" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Add Task Section */}
            {isEditingTasks && (
              <div className="p-6">
                {isAddingTask ? (
                  <div className="space-y-4 bg-gray-50 p-4 rounded-xl">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Task Title</label>
                      <input
                        type="text"
                        value={newTaskForm.title}
                        onChange={(e) => setNewTaskForm({ ...newTaskForm, title: e.target.value })}
                        placeholder="Enter task title"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                      <textarea
                        value={newTaskForm.description}
                        onChange={(e) => setNewTaskForm({ ...newTaskForm, description: e.target.value })}
                        placeholder="Enter task description"
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newTaskForm.allow_upload}
                          onChange={(e) => setNewTaskForm({ ...newTaskForm, allow_upload: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2c4f26]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2c4f26]"></div>
                      </label>
                      <span className="text-sm font-medium text-gray-700">Allow document upload</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newTaskForm.is_admin_task}
                          onChange={(e) => setNewTaskForm({ ...newTaskForm, is_admin_task: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2c4f26]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2c4f26]"></div>
                      </label>
                      <span className="text-sm font-medium text-gray-700">Admin only task</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddTask}
                        disabled={!newTaskForm.title.trim()}
                        className="px-4 py-2 bg-[#2c4f26] text-white rounded-lg text-sm font-semibold hover:bg-[#1f3a1c] transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add Task
                      </button>
                      <button
                        onClick={() => {
                          setIsAddingTask(false);
                          setNewTaskForm({ title: '', description: '', allow_upload: false, is_admin_task: false });
                        }}
                        className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsAddingTask(true)}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-700 font-medium hover:border-[#2c4f26] hover:text-[#2c4f26] transition flex items-center justify-center gap-2"
                  >
                    <Plus size={20} />
                    Add New Task
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {progress === 100 && (
          <div className={`mt-4 md:mt-6 border-2 rounded-xl md:rounded-2xl p-4 md:p-8 text-center ${
            applicant.status === 'approved'
              ? 'bg-green-50 border-green-200'
              : 'border-green-200'
          }`} style={applicant.status !== 'approved' ? { backgroundColor: '#f0f5f0' } : {}}>
            <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 ${
              applicant.status === 'approved' ? 'bg-green-600' : ''
            }`} style={applicant.status !== 'approved' ? { backgroundColor: '#2c4f26' } : {}}>
              <CheckCircle size={24} className="text-white md:hidden" />
              <CheckCircle size={32} className="text-white hidden md:block" />
            </div>
            <h3 className={`text-lg md:text-2xl font-bold mb-1 md:mb-2 ${
              applicant.status === 'approved' ? 'text-green-900' : ''
            }`} style={applicant.status !== 'approved' ? { color: '#1a3018' } : {}}>
              {applicant.status === 'approved' ? 'Onboarding Approved!' : 'All Tasks Completed!'}
            </h3>
            <p className={`text-sm md:text-base ${applicant.status === 'approved' ? 'text-green-700' : ''}`} style={applicant.status !== 'approved' ? { color: '#2c4f26' } : {}}>
              {applicant.status === 'approved'
                ? `${applicant.name} has been approved and is all set for their first day on ${new Date(applicant.startDate).toLocaleDateString()}`
                : `${applicant.name} has completed all tasks and is ready for admin review`
              }
            </p>
          </div>
        )}

        {/* Document Viewer Modal */}
        {documentModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-4xl max-h-[90vh] w-full flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">{documentModal.fileName}</h3>
                <button
                  onClick={closeDocumentModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <X size={20} className="text-gray-700" />
                </button>
              </div>
              <div className="flex-1 overflow-auto p-4 flex items-center justify-center min-h-[400px]">
                {documentModal.loading ? (
                  <div className="text-gray-700">Loading document...</div>
                ) : documentModal.error ? (
                  <div className="text-center">
                    <AlertTriangle size={48} className="text-red-400 mx-auto mb-4" />
                    <p className="text-red-600 font-medium mb-2">Failed to load document</p>
                    <p className="text-gray-700 text-sm">{documentModal.error}</p>
                    <p className="text-gray-600 text-xs mt-4">The storage bucket may need public access enabled in Supabase.</p>
                  </div>
                ) : documentModal.url ? (
                  documentModal.fileType?.startsWith('image/') ? (
                    <img
                      src={documentModal.url}
                      alt={documentModal.fileName}
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : documentModal.fileType === 'application/pdf' ? (
                    <iframe
                      src={documentModal.url}
                      title={documentModal.fileName}
                      className="w-full h-full min-h-[500px]"
                    />
                  ) : (
                    <div className="text-center">
                      <FileText size={48} className="text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-600 mb-4">Preview not available for this file type</p>
                      <a
                        href={documentModal.url}
                        download={documentModal.fileName}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#2c4f26] text-white rounded-lg font-medium hover:bg-[#1f3a1c] transition"
                      >
                        <Download size={16} />
                        Download File
                      </a>
                    </div>
                  )
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 size={24} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Document</h3>
                  <p className="text-sm text-gray-700">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete <span className="font-medium">{deleteConfirm.document?.file_name}</span>?
                The task will be marked as incomplete.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm({ isOpen: false, document: null, taskId: null, isDeleting: false })}
                  disabled={deleteConfirm.isDeleting}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteDocument}
                  disabled={deleteConfirm.isDeleting}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition disabled:opacity-50"
                >
                  {deleteConfirm.isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Compact Applicants View Component
const ApplicantsView = ({ applicants, setView, handleLogout, onSelectApplicant, profile }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const getProgress = (tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    const completed = tasks.filter(t => t.status === 'completed').length;
    return Math.round((completed / tasks.length) * 100);
  };

  const filteredApplicants = applicants.filter(a => {
    const matchesSearch = a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         a.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         a.email?.toLowerCase().includes(searchTerm.toLowerCase());

    if (statusFilter === 'all') return matchesSearch;

    const progress = getProgress(a.tasks);
    if (statusFilter === 'approved') return matchesSearch && a.status === 'approved';
    if (statusFilter === 'pending') return matchesSearch && progress === 100 && a.status !== 'approved';
    if (statusFilter === 'in_progress') return matchesSearch && progress < 100 && a.status !== 'approved';
    return matchesSearch;
  });

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="mb-4 md:mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">All Applicants</h1>
          <p className="text-sm md:text-base text-gray-600">Compact view of all applicants and their onboarding status</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-3 md:p-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600" size={18} />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent text-sm"
              />
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex-1 md:flex-none px-3 md:px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent text-sm"
              >
                <option value="all">All Status</option>
                <option value="in_progress">In Progress</option>
                <option value="pending">Pending Review</option>
                <option value="approved">Approved</option>
              </select>
              <div className="text-xs md:text-sm text-gray-700 whitespace-nowrap">
                {filteredApplicants.length} found
              </div>
            </div>
          </div>

          {/* Desktop Table */}
          <table className="w-full hidden md:table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider">Applicant</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider">Position</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider">Start Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider">Progress</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredApplicants.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-700">No applicants found</td>
                </tr>
              ) : (
                filteredApplicants.map(applicant => {
                  const progress = getProgress(applicant.tasks);
                  const completedTasks = applicant.tasks?.filter(t => t.status === 'completed').length || 0;
                  const totalTasks = applicant.tasks?.length || 0;
                  let statusLabel, statusColor;
                  if (applicant.status === 'approved') { statusLabel = 'Approved'; statusColor = 'bg-green-100 text-green-700'; }
                  else if (applicant.status === 'inactive') { statusLabel = 'Inactive'; statusColor = 'bg-gray-100 text-gray-700'; }
                  else if (progress === 100) { statusLabel = 'Pending Review'; statusColor = 'bg-yellow-100 text-yellow-700'; }
                  else { statusLabel = 'In Progress'; statusColor = 'bg-blue-100 text-blue-700'; }

                  return (
                    <tr key={applicant.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img src={applicant.profile_image || DefaultProfileIcon} alt={applicant.name} className="w-8 h-8 rounded-full object-cover" />
                          <div>
                            <div className="font-medium text-gray-900">{applicant.name}</div>
                            <div className="text-xs text-gray-700">{applicant.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{applicant.position}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{new Date(applicant.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div className={`h-2 rounded-full ${progress === 100 ? 'bg-green-500' : 'bg-[#2c4f26]'}`} style={{ width: `${progress}%` }} />
                          </div>
                          <span className="text-xs text-gray-700">{completedTasks}/{totalTasks}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColor}`}>{statusLabel}</span></td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => onSelectApplicant(applicant)} className="text-[#2c4f26] hover:text-[#1f3a1c] text-sm font-medium">View →</button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {filteredApplicants.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-700">No applicants found</div>
            ) : (
              filteredApplicants.map(applicant => {
                const progress = getProgress(applicant.tasks);
                const completedTasks = applicant.tasks?.filter(t => t.status === 'completed').length || 0;
                const totalTasks = applicant.tasks?.length || 0;
                let statusLabel, statusColor;
                if (applicant.status === 'approved') { statusLabel = 'Approved'; statusColor = 'bg-green-100 text-green-700'; }
                else if (applicant.status === 'inactive') { statusLabel = 'Inactive'; statusColor = 'bg-gray-100 text-gray-700'; }
                else if (progress === 100) { statusLabel = 'Pending Review'; statusColor = 'bg-yellow-100 text-yellow-700'; }
                else { statusLabel = 'In Progress'; statusColor = 'bg-blue-100 text-blue-700'; }

                return (
                  <div key={applicant.id} className="p-4" onClick={() => onSelectApplicant(applicant)}>
                    <div className="flex items-center gap-3 mb-2">
                      <img src={applicant.profile_image || DefaultProfileIcon} alt={applicant.name} className="w-10 h-10 rounded-full object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{applicant.name}</div>
                        <div className="text-xs text-gray-700">{applicant.position}</div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColor}`}>{statusLabel}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-700">
                      <span>Starts {new Date(applicant.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      <span>{completedTasks}/{totalTasks} tasks</span>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full ${progress === 100 ? 'bg-green-500' : 'bg-[#2c4f26]'}`} style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Templates View Component
const TemplatesView = ({ templates, setTemplates, setView, handleLogout, showToast, profile, permissions }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ title: '', description: '', allow_upload: false, is_admin_task: false });
  const [editForm, setEditForm] = useState({ title: '', description: '', allow_upload: false, is_admin_task: false });

  const handleAddTemplate = async () => {
    if (!newTemplate.title.trim()) {
      return;
    }

    try {
      const { data, error } = await supabase
        .from('task_templates')
        .insert({
          title: newTemplate.title,
          description: newTemplate.description,
          allow_upload: newTemplate.allow_upload,
          is_admin_task: newTemplate.is_admin_task,
          order_index: templates.length
        })
        .select()
        .single();

      if (error) throw error;

      setTemplates([...templates, data]);
      setNewTemplate({ title: '', description: '', allow_upload: false, is_admin_task: false });
      setIsAdding(false);
      showToast('Template added successfully!');
    } catch (error) {
      console.error('Error adding template:', error);
      alert('Failed to add template: ' + error.message);
    }
  };

  const handleUpdateTemplate = async (id) => {
    if (!editForm.title.trim()) {
      return;
    }

    try {
      const { error } = await supabase
        .from('task_templates')
        .update({
          title: editForm.title,
          description: editForm.description,
          allow_upload: editForm.allow_upload,
          is_admin_task: editForm.is_admin_task
        })
        .eq('id', id);

      if (error) throw error;

      setTemplates(templates.map(t =>
        t.id === id ? { ...t, title: editForm.title, description: editForm.description, allow_upload: editForm.allow_upload, is_admin_task: editForm.is_admin_task } : t
      ));
      setEditingId(null);
      showToast('Template updated successfully!');
    } catch (error) {
      console.error('Error updating template:', error);
      alert('Failed to update template: ' + error.message);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template? This will not affect existing hires.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('task_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;

      const newTemplates = templates.filter(t => t.id !== id);
      // Re-index the remaining templates
      for (let i = 0; i < newTemplates.length; i++) {
        await supabase
          .from('task_templates')
          .update({ order_index: i })
          .eq('id', newTemplates[i].id);
        newTemplates[i].order_index = i;
      }

      setTemplates(newTemplates);
      showToast('Template deleted successfully!');
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template: ' + error.message);
    }
  };

  const handleMoveTemplate = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= templates.length) return;

    const newTemplates = [...templates];
    [newTemplates[index], newTemplates[newIndex]] = [newTemplates[newIndex], newTemplates[index]];

    try {
      // Update order_index for both templates
      await supabase
        .from('task_templates')
        .update({ order_index: newIndex })
        .eq('id', templates[index].id);

      await supabase
        .from('task_templates')
        .update({ order_index: index })
        .eq('id', templates[newIndex].id);

      newTemplates[index].order_index = newIndex;
      newTemplates[newIndex].order_index = index;

      setTemplates(newTemplates);
    } catch (error) {
      console.error('Error reordering templates:', error);
      alert('Failed to reorder template: ' + error.message);
    }
  };

  return (
    <div>
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">Task Templates</h1>
          <p className="text-sm md:text-base text-gray-600">
            Manage default onboarding tasks that will be assigned to all new hires. Changes only affect future hires.
          </p>
        </div>

        <div className="bg-white rounded-xl md:rounded-2xl border border-gray-100 overflow-hidden">
          <div className="p-4 md:p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h2 className="text-lg md:text-xl font-bold text-gray-900">Default Onboarding Tasks</h2>
            {permissions?.can_manage_templates && (
              <button
                onClick={() => setIsAdding(true)}
                className="bg-[#2c4f26] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#1f3a1c] transition flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <Plus size={18} />
                Add Task
              </button>
            )}
          </div>

          <div className="divide-y divide-gray-100">
            {isAdding && (
              <div className="p-6 bg-blue-50">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">New Task Template</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Task Title *
                    </label>
                    <input
                      type="text"
                      value={newTemplate.title}
                      onChange={(e) => setNewTemplate({ ...newTemplate, title: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                      placeholder="e.g., Complete I-9 Form"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={newTemplate.description}
                      onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                      placeholder="Provide instructions for the new hire..."
                      rows={3}
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newTemplate.allow_upload}
                        onChange={(e) => setNewTemplate({ ...newTemplate, allow_upload: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2c4f26]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2c4f26]"></div>
                    </label>
                    <span className="text-sm font-medium text-gray-700">Allow document upload</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newTemplate.is_admin_task}
                        onChange={(e) => setNewTemplate({ ...newTemplate, is_admin_task: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2c4f26]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2c4f26]"></div>
                    </label>
                    <span className="text-sm font-medium text-gray-700">Admin only task</span>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleAddTemplate}
                      className="flex-1 px-4 py-2 bg-[#2c4f26] text-white rounded-lg font-semibold hover:bg-[#1f3a1c] transition"
                    >
                      Add Template
                    </button>
                    <button
                      onClick={() => {
                        setIsAdding(false);
                        setNewTemplate({ title: '', description: '', allow_upload: false, is_admin_task: false });
                      }}
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {templates.map((template, index) => (
              <div key={template.id} className="p-4 md:p-6 hover:bg-gray-50 transition">
                {editingId === template.id ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Task Title *
                      </label>
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                        rows={3}
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editForm.allow_upload}
                          onChange={(e) => setEditForm({ ...editForm, allow_upload: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2c4f26]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2c4f26]"></div>
                      </label>
                      <span className="text-sm font-medium text-gray-700">Allow document upload</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editForm.is_admin_task}
                          onChange={(e) => setEditForm({ ...editForm, is_admin_task: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2c4f26]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2c4f26]"></div>
                      </label>
                      <span className="text-sm font-medium text-gray-700">Admin only task</span>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleUpdateTemplate(template.id)}
                        className="flex-1 px-4 py-2 bg-[#2c4f26] text-white rounded-lg font-semibold hover:bg-[#1f3a1c] transition"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 md:gap-4">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleMoveTemplate(index, 'up')}
                        disabled={index === 0}
                        className="text-gray-600 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => handleMoveTemplate(index, 'down')}
                        disabled={index === templates.length - 1}
                        className="text-gray-600 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
                        <span className="text-xs md:text-sm font-semibold text-gray-700">#{index + 1}</span>
                        <h3 className="text-base md:text-lg font-semibold text-gray-900">{template.title}</h3>
                        {template.allow_upload && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full flex items-center gap-1">
                            <Upload size={12} />
                            <span className="hidden sm:inline">Upload</span>
                          </span>
                        )}
                        {template.is_admin_task && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                            <span className="sm:hidden">Admin</span>
                            <span className="hidden sm:inline">Admin Only</span>
                          </span>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-gray-600 text-xs md:text-sm">{template.description}</p>
                      )}
                      {permissions?.can_manage_templates && (
                        <div className="flex gap-2 mt-3 md:hidden">
                          <button
                            onClick={() => {
                              setEditingId(template.id);
                              setEditForm({ title: template.title, description: template.description || '', allow_upload: template.allow_upload || false, is_admin_task: template.is_admin_task || false });
                            }}
                            className="px-3 py-1.5 text-sm font-medium text-[#2c4f26] hover:bg-[#f0f5f0] rounded-lg transition"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    {permissions?.can_manage_templates && (
                      <div className="hidden md:flex gap-2">
                        <button
                          onClick={() => {
                            setEditingId(template.id);
                            setEditForm({ title: template.title, description: template.description || '', allow_upload: template.allow_upload || false, is_admin_task: template.is_admin_task || false });
                          }}
                          className="px-3 py-1.5 text-sm font-medium text-[#2c4f26] hover:bg-[#f0f5f0] rounded-lg transition"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {templates.length === 0 && !isAdding && (
              <div className="p-12 text-center text-gray-700">
                <p className="text-lg font-medium mb-2">No task templates yet</p>
                <p className="text-sm">Click "Add Task" to create your first template</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Portal Component
const AdminPortal = ({
  applicants,
  profile,
  permissions,
  searchTerm,
  setSearchTerm,
  setView,
  setShowAddHireModal,
  setSelectedApplicant,
  handleLogout
}) => {
  const totalHires = applicants.length;
  const inProgress = applicants.filter(a => {
    const prog = getProgress(a.tasks);
    return prog > 0 && prog < 100;
  }).length;
  const completed = applicants.filter(a => a.status === 'approved').length;
  const pendingReview = applicants.filter(a => getProgress(a.tasks) === 100 && a.status !== 'approved').length;

  const filteredApplicants = applicants.filter(a =>
    a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.position.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#2c4f26]">Employee Onboarding</h1>
              <p className="text-gray-700 mt-1 text-sm md:text-base">Manage new hires and track their onboarding progress</p>
            </div>
            {permissions?.can_add_hires && (
              <button
                onClick={() => setShowAddHireModal(true)}
                className="bg-[#2c4f26] text-white px-4 md:px-6 py-2.5 md:py-3 rounded-xl font-semibold hover:bg-[#1f3a1c] transition shadow-lg shadow-[#2c4f26]/30 flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <Plus size={20} />
                Add New Hire
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-2 md:mb-4">
                <span className="text-xs md:text-sm font-semibold text-gray-700 uppercase tracking-wide">Total New Hires</span>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#f0f5f0] rounded-lg md:rounded-xl flex items-center justify-center">
                  <Users size={20} className="text-[#2c4f26] md:hidden" />
                  <Users size={24} className="text-[#2c4f26] hidden md:block" />
                </div>
              </div>
              <div className="text-2xl md:text-4xl font-bold text-gray-900 mb-1">{totalHires}</div>
              <div className="text-xs md:text-sm text-gray-700">Active onboardings</div>
            </div>

            <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-2 md:mb-4">
                <span className="text-xs md:text-sm font-semibold text-gray-700 uppercase tracking-wide">In Progress</span>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-50 rounded-lg md:rounded-xl flex items-center justify-center">
                  <Clock size={20} className="text-orange-600 md:hidden" />
                  <Clock size={24} className="text-orange-600 hidden md:block" />
                </div>
              </div>
              <div className="text-2xl md:text-4xl font-bold text-gray-900 mb-1">{inProgress}</div>
              <div className="text-xs md:text-sm text-gray-700">Currently onboarding</div>
            </div>

            <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-2 md:mb-4">
                <span className="text-xs md:text-sm font-semibold text-gray-700 uppercase tracking-wide">Completed</span>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-green-50 rounded-lg md:rounded-xl flex items-center justify-center">
                  <CheckCircle size={20} className="text-green-600 md:hidden" />
                  <CheckCircle size={24} className="text-green-600 hidden md:block" />
                </div>
              </div>
              <div className="text-2xl md:text-4xl font-bold text-gray-900 mb-1">{completed}</div>
              <div className="text-xs md:text-sm text-gray-700">Successfully onboarded</div>
            </div>

            <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-2 md:mb-4">
                <span className="text-xs md:text-sm font-semibold text-gray-700 uppercase tracking-wide">Pending Review</span>
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center" style={{ backgroundColor: '#f0f5f0' }}>
                  <CheckCircle size={20} className="md:hidden" style={{ color: '#2c4f26' }} />
                  <CheckCircle size={24} className="hidden md:block" style={{ color: '#2c4f26' }} />
                </div>
              </div>
              <div className="text-2xl md:text-4xl font-bold text-gray-900 mb-1">{pendingReview}</div>
              <div className="text-xs md:text-sm text-gray-700">Awaiting approval</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl md:rounded-2xl border border-gray-100">
          <div className="p-4 md:p-6 border-b border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 text-gray-600" size={18} />
                <input
                  type="text"
                  placeholder="Search by name or position..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 md:pl-12 pr-4 py-2.5 md:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm md:text-base"
                />
              </div>
              <div className="flex gap-2 md:gap-4">
                <button className="flex-1 md:flex-none px-3 md:px-5 py-2.5 md:py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition flex items-center justify-center gap-2 text-gray-700 font-medium text-sm md:text-base">
                  <Filter size={18} />
                  <span className="hidden sm:inline">All Department</span>
                  <span className="sm:hidden">Dept</span>
                </button>
                <button className="flex-1 md:flex-none px-3 md:px-5 py-2.5 md:py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition flex items-center justify-center gap-2 text-gray-700 font-medium text-sm md:text-base">
                  <span className="hidden sm:inline">All Status</span>
                  <span className="sm:hidden">Status</span>
                </button>
              </div>
            </div>
          </div>

          {filteredApplicants.length === 0 ? (
            <div className="p-12 text-center text-gray-700">
              <Users size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No applicants found</p>
              <p className="text-sm mt-2">Add applicants and tasks in your Supabase database</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 p-4 md:p-6">
              {filteredApplicants.map(applicant => {
                const progress = getProgress(applicant.tasks);
                const completedTasks = applicant.tasks.filter(t => t.status === 'completed').length;

                return (
                  <div key={applicant.id} className="bg-gray-50 rounded-xl md:rounded-2xl p-4 md:p-6 hover:shadow-md transition border border-gray-100">
                    <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-4">
                      <img
                        src={applicant.profile_image || DefaultProfileIcon}
                        alt={applicant.name}
                        className="w-12 h-12 md:w-14 md:h-14 rounded-lg md:rounded-xl object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="text-base md:text-lg font-bold text-gray-900 truncate">{applicant.name}</h3>
                          {applicant.status === 'approved' ? (
                            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                              approved
                            </span>
                          ) : applicant.status === 'inactive' ? (
                            <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">
                              inactive
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                              {progress === 100 ? 'pending review' : 'in progress'}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 text-xs md:text-sm font-medium">{applicant.position}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 md:gap-6 mb-3 md:mb-4 text-xs md:text-sm">
                      <div className="flex items-center gap-1 md:gap-2 text-gray-600">
                        <span className="font-medium">Operations</span>
                      </div>
                      <div className="flex items-center gap-1 md:gap-2 text-gray-600">
                        <Clock size={14} className="md:hidden" />
                        <Clock size={16} className="hidden md:block" />
                        <span>Starts {new Date(applicant.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    </div>

                    <div className="mb-3 md:mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs md:text-sm font-semibold text-gray-700">Onboarding Progress</span>
                        <span className="text-xs md:text-sm font-bold text-gray-900">{completedTasks}/{applicant.tasks.length} tasks</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 md:h-2">
                        <div
                          className={`h-1.5 md:h-2 rounded-full transition-all ${progress === 100 ? 'bg-green-600' : 'bg-[#2c4f26]'}`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    <button
                      onClick={() => setSelectedApplicant(applicant)}
                      className="w-full text-gray-700 font-semibold hover:text-gray-900 transition flex items-center justify-center gap-2 py-1.5 md:py-2 text-sm md:text-base"
                    >
                      View Details
                      <ArrowRight size={16} className="md:hidden" />
                      <ArrowRight size={18} className="hidden md:block" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Applicant Portal Component
const ApplicantPortal = ({
  currentApplicant,
  profile,
  toggleTask,
  uploadDocument,
  handleLogout,
  setView
}) => {
  if (!currentApplicant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">No applicant data found for your account.</p>
          <button
            onClick={handleLogout}
            className="px-6 py-3 bg-[#2c4f26] text-white rounded-xl font-semibold hover:bg-[#1f3a1c] transition flex items-center gap-2 mx-auto"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>
    );
  }

  // Filter out admin-only tasks from the applicant's view
  const visibleTasks = currentApplicant.tasks.filter(t => !t.is_admin_task);
  const progress = getProgress(visibleTasks);
  const completedTasks = visibleTasks.filter(t => t.status === 'completed').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setView('applicant')}
              className="flex items-center gap-2 md:gap-3 hover:opacity-80 transition"
            >
              <img
                src={HISmallLogo}
                alt="Home Instead"
                className="h-8 md:h-10 w-auto object-contain"
              />
              <span className="text-sm sm:text-lg md:text-xl font-bold text-[#2c4f26]">Home Instead Portal</span>
            </button>
            {profile && (
              <button
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-900 font-medium flex items-center gap-2 text-sm md:text-base"
              >
                <LogOut size={16} className="md:hidden" />
                <LogOut size={18} className="hidden md:block" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">Welcome, {currentApplicant?.name || profile?.full_name}!</h1>
          <p className="text-sm md:text-base text-gray-700">Complete your onboarding tasks to get started</p>
        </div>

        <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-8 border border-gray-100 mb-4 md:mb-6">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-gray-900 mb-1">Your Progress</h2>
              <p className="text-sm md:text-base text-gray-600">{completedTasks} of {visibleTasks.length} tasks completed</p>
            </div>
            <div className="text-right">
              <div className={`text-3xl md:text-5xl font-bold ${progress === 100 ? 'text-green-600' : 'text-[#2c4f26]'}`}>
                {progress}%
              </div>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 md:h-4">
            <div
              className={`h-3 md:h-4 rounded-full transition-all ${progress === 100 ? 'bg-green-600' : 'bg-[#2c4f26]'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl md:rounded-2xl border border-gray-100">
          <div className="p-4 md:p-6 border-b border-gray-100">
            <h2 className="text-lg md:text-xl font-bold text-gray-900">Onboarding Tasks</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {visibleTasks.map((task) => {
              const isApproved = currentApplicant.status === 'approved';
              return (
                <div
                  key={task.id}
                  className="p-4 md:p-6 hover:bg-gray-50 transition"
                >
                  <div className="flex items-start gap-3 md:gap-5">
                    <div
                      className={`mt-0.5 md:mt-1 flex-shrink-0 ${isApproved ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      onClick={() => !isApproved && toggleTask(task.id, task.status)}
                    >
                      {task.status === 'completed' ? (
                        <div className="w-6 h-6 md:w-7 md:h-7 bg-green-600 rounded-lg flex items-center justify-center">
                          <CheckCircle size={16} className="text-white md:hidden" />
                          <CheckCircle size={20} className="text-white hidden md:block" />
                        </div>
                      ) : (
                        <div className={`w-6 h-6 md:w-7 md:h-7 border-2 border-gray-300 rounded-lg ${!isApproved ? 'hover:border-[#2c4f26]' : ''} transition`} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
                        <h3 className={`text-sm md:text-lg font-semibold ${task.status === 'completed' ? 'text-gray-600 line-through' : 'text-gray-900'}`}>
                          {task.title}
                        </h3>
                        {task.status === 'completed' && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            Done
                          </span>
                        )}
                        {task.allow_upload && !task.document && task.status !== 'completed' && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full flex items-center gap-1">
                            <Upload size={12} />
                            <span className="hidden sm:inline">Upload Required</span>
                            <span className="sm:hidden">Upload</span>
                          </span>
                        )}
                      </div>
                      {task.description && (
                        <p className="text-gray-600 text-xs md:text-sm mb-3">{task.description}</p>
                      )}
                      {task.document && (
                        <div className="mt-3 inline-flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-green-50 text-green-700 rounded-lg text-xs md:text-sm">
                          <FileText size={14} className="md:hidden" />
                          <FileText size={16} className="hidden md:block" />
                          <span className="font-medium truncate max-w-[150px] md:max-w-none">{task.document.file_name}</span>
                        </div>
                      )}
                      {task.allow_upload && task.status !== 'completed' && !isApproved && !task.document && (
                        <div className="mt-3">
                          <input
                            type="file"
                            id={`file-${task.id}`}
                            className="hidden"
                            onChange={(e) => e.target.files[0] && uploadDocument(e.target.files[0], task.id)}
                          />
                          <label
                            htmlFor={`file-${task.id}`}
                            className="inline-flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-[#f0f5f0] text-[#2c4f26] rounded-lg text-xs md:text-sm font-semibold cursor-pointer hover:bg-[#e3ede3] transition"
                          >
                            <Upload size={14} className="md:hidden" />
                            <Upload size={16} className="hidden md:block" />
                            Upload Document
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {progress === 100 && (
          <div className="mt-4 md:mt-6 bg-green-50 border-2 border-green-200 rounded-xl md:rounded-2xl p-4 md:p-8 text-center">
            <div className="w-12 h-12 md:w-16 md:h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
              <CheckCircle size={24} className="text-white md:hidden" />
              <CheckCircle size={32} className="text-white hidden md:block" />
            </div>
            <h3 className="text-lg md:text-2xl font-bold text-green-900 mb-1 md:mb-2">All Tasks Completed!</h3>
            <p className="text-sm md:text-base text-green-700">You're all set for your first day on {new Date(currentApplicant.start_date).toLocaleDateString()}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Simple Line Chart Component
const LineChart = ({ data, title, color = 'customGreen' }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-600">
        No data available
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d.value), 1);
  const width = 100;
  const height = 60;
  const padding = 5;

  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * (width - padding * 2) + padding;
    const y = height - padding - ((d.value / maxValue) * (height - padding * 2));
    return `${x},${y}`;
  }).join(' ');

  const colorClasses = {
    indigo: { stroke: '#4f46e5', fill: '#4f46e5', bg: '#eef2ff' },
    green: { stroke: '#16a34a', fill: '#16a34a', bg: '#f0fdf4' },
    customGreen: { stroke: '#2c4f26', fill: '#2c4f26', bg: '#f0f5f0' }
  };

  const colors = colorClasses[color] || colorClasses.customGreen;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <span className="text-3xl font-bold" style={{ color: colors.stroke }}>
          {data[data.length - 1]?.value || 0}
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-48">
        <defs>
          <linearGradient id={`gradient-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style={{ stopColor: colors.stroke, stopOpacity: 0.2 }} />
            <stop offset="100%" style={{ stopColor: colors.stroke, stopOpacity: 0 }} />
          </linearGradient>
        </defs>
        <polyline
          fill={`url(#gradient-${color})`}
          stroke="none"
          points={`${padding},${height} ${points} ${width - padding},${height}`}
        />
        <polyline
          fill="none"
          stroke={colors.stroke}
          strokeWidth="2"
          points={points}
        />
        {data.map((d, i) => {
          const x = (i / (data.length - 1)) * (width - padding * 2) + padding;
          const y = height - padding - ((d.value / maxValue) * (height - padding * 2));
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="2"
              fill={colors.fill}
            />
          );
        })}
      </svg>
      <div className="flex justify-between text-xs text-gray-700">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
};

// Analytics View Component
const AnalyticsView = ({ activityLog, applicants, setView, handleLogout }) => {
  const [dateRange, setDateRange] = useState('30');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomRange, setShowCustomRange] = useState(false);

  // Calculate date range
  const getDateRange = () => {
    const end = new Date();
    let start = new Date();

    if (dateRange === 'custom' && customStartDate && customEndDate) {
      start = new Date(customStartDate);
      return { start, end: new Date(customEndDate) };
    }

    const days = parseInt(dateRange) || 30;
    start.setDate(start.getDate() - days);
    return { start, end };
  };

  const { start: startDate, end: endDate } = getDateRange();

  // Process data for charts
  const processChartData = () => {
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const dates = [];

    // Limit to reasonable number of data points for better visualization
    const maxPoints = 30;
    const step = Math.max(1, Math.ceil(daysDiff / maxPoints));

    for (let i = 0; i <= daysDiff; i += step) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      dates.push(date);
    }

    // Always include the end date
    if (dates[dates.length - 1].getTime() !== endDate.getTime()) {
      dates.push(new Date(endDate));
    }

    const newHiresData = dates.map(date => {
      const count = applicants.filter(a => {
        const createdDate = new Date(a.created_at || a.startDate);
        return createdDate <= date;
      }).length;
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: count
      };
    });

    const approvedData = dates.map(date => {
      const count = applicants.filter(a => {
        if (!a.approved_at) return false;
        const approvedDate = new Date(a.approved_at);
        return approvedDate <= date;
      }).length;
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: count
      };
    });

    return { newHiresData, approvedData };
  };

  const { newHiresData, approvedData } = processChartData();

  // Calculate summary stats
  const totalNewHires = applicants.filter(a => {
    const createdDate = new Date(a.created_at || a.startDate);
    createdDate.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    return createdDate >= start && createdDate <= end;
  }).length;

  const totalApproved = applicants.filter(a => {
    if (!a.approved_at) return false;
    const approvedDate = new Date(a.approved_at);
    approvedDate.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    return approvedDate >= start && approvedDate <= end;
  }).length;

  const approvalRate = applicants.length > 0
    ? Math.round((applicants.filter(a => a.status === 'approved').length / applicants.length) * 100)
    : 0;

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">Owner Analytics</h1>
          <p className="text-sm md:text-base text-gray-700">Track hiring trends and onboarding performance</p>
        </div>

        {/* Date Range Filter */}
        <div className="bg-white rounded-xl md:rounded-2xl border border-gray-100 p-4 md:p-6 mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
            <span className="text-sm font-semibold text-gray-700">Time Range:</span>
            <div className="flex flex-wrap gap-2">
              {['7', '30', '90'].map(days => (
                <button
                  key={days}
                  onClick={() => {
                    setDateRange(days);
                    setShowCustomRange(false);
                  }}
                  className={`px-3 md:px-4 py-2 rounded-lg text-sm md:text-base font-medium transition ${
                    dateRange === days && !showCustomRange
                      ? 'bg-[#2c4f26] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {days}d
                </button>
              ))}
              <button
                onClick={() => setShowCustomRange(!showCustomRange)}
                className={`px-3 md:px-4 py-2 rounded-lg text-sm md:text-base font-medium transition ${
                  showCustomRange
                    ? 'bg-[#2c4f26] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Custom
              </button>
            </div>
            {showCustomRange && (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center mt-2 md:mt-0">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent"
                />
                <span className="text-gray-700 text-sm hidden sm:inline">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent"
                />
                <button
                  onClick={() => setDateRange('custom')}
                  className="px-4 py-2 bg-[#2c4f26] text-white rounded-lg text-sm font-medium hover:bg-[#1f3a1c] transition"
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6">
          <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <span className="text-xs md:text-sm font-semibold text-gray-700 uppercase tracking-wide">New Hires</span>
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[#f0f5f0] rounded-lg md:rounded-xl flex items-center justify-center">
                <Users size={20} className="text-[#2c4f26] md:hidden" />
                <Users size={24} className="text-[#2c4f26] hidden md:block" />
              </div>
            </div>
            <div className="text-2xl md:text-4xl font-bold text-gray-900 mb-1">{totalNewHires}</div>
            <div className="text-xs md:text-sm text-gray-700">In selected period</div>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <span className="text-xs md:text-sm font-semibold text-gray-700 uppercase tracking-wide">Approved</span>
              <div className="w-10 h-10 md:w-12 md:h-12 bg-green-50 rounded-lg md:rounded-xl flex items-center justify-center">
                <CheckCircle size={20} className="text-green-600 md:hidden" />
                <CheckCircle size={24} className="text-green-600 hidden md:block" />
              </div>
            </div>
            <div className="text-2xl md:text-4xl font-bold text-gray-900 mb-1">{totalApproved}</div>
            <div className="text-xs md:text-sm text-gray-700">Completed onboarding</div>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <span className="text-xs md:text-sm font-semibold text-gray-700 uppercase tracking-wide">Approval Rate</span>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center" style={{ backgroundColor: '#f0f5f0' }}>
                <Database size={20} className="md:hidden" style={{ color: '#2c4f26' }} />
                <Database size={24} className="hidden md:block" style={{ color: '#2c4f26' }} />
              </div>
            </div>
            <div className="text-2xl md:text-4xl font-bold text-gray-900 mb-1">{approvalRate}%</div>
            <div className="text-xs md:text-sm text-gray-700">Overall completion rate</div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
          <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-100">
            <LineChart data={newHiresData} title="Total New Hires" color="customGreen" />
          </div>
          <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-100">
            <LineChart data={approvedData} title="Approved Hires" color="green" />
          </div>
        </div>

        {/* Activity Log */}
        <div className="bg-white rounded-xl md:rounded-2xl border border-gray-100">
          <div className="p-4 md:p-6 border-b border-gray-100">
            <h2 className="text-lg md:text-xl font-bold text-gray-900">Recent Activity Log</h2>
          </div>
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {activityLog.length === 0 ? (
              <div className="p-8 md:p-12 text-center text-gray-700">
                <Database size={36} className="mx-auto mb-4 text-gray-300 md:hidden" />
                <Database size={48} className="mx-auto mb-4 text-gray-300 hidden md:block" />
                <p className="text-base md:text-lg font-medium">No activity yet</p>
              </div>
            ) : (
              activityLog.map(log => (
                <div key={log.id} className="p-4 md:p-5 hover:bg-gray-50 transition">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm md:text-base font-semibold text-gray-900">
                        {log.profile?.full_name} {log.action.toLowerCase()}d {log.entity_type}
                      </p>
                      <p className="text-xs md:text-sm text-gray-700 mt-1">
                        {new Date(log.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Formats a staff phone number as the user types -- strips anything that
// isn't a digit and auto-inserts dashes (571-393-9093), capped at 10
// digits, so no one has to type the dashes themselves (or can type ones
// that land in the wrong place).
const formatStaffPhone = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};

// Free-standing department tag on a staff member's profile -- distinct
// from admin_permissions (which governs what a VIEWER can see/do):
// department governs group MEMBERSHIP, used e.g. to decide whose tasks
// show up on the Task Board's Staffing/Field Supervisors tabs. A plain
// list rather than a DB enum so adding a new department later is just
// adding a string here, no migration.
const STAFF_DEPARTMENTS = ['Staffing', 'Field Supervisors'];

// Invite Staff Modal Component
const InviteAdminModal = ({ show, onClose, onSubmit, formData, setFormData, error, isSubmitting }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">{formData.department === 'Field Supervisors' ? 'Add Field Supervisor' : 'Invite Staff'}</h2>
            <button
              onClick={onClose}
              className="text-gray-600 hover:text-gray-600 transition"
              type="button"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={onSubmit} className="p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                placeholder="Jane Smith"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                placeholder="jane@company.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: formatStaffPhone(e.target.value) }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                placeholder="555-123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Department</label>
              <select
                value={formData.department || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value || null }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition bg-white"
              >
                <option value="">No department</option>
                {STAFF_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            <div className="border-t border-gray-200 pt-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Permissions</h3>
              {(() => {
                const Toggle = ({ permKey }) => (
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={formData.permissions[permKey] || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, permissions: { ...prev.permissions, [permKey]: e.target.checked } }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2c4f26]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2c4f26]"></div>
                  </label>
                );
                const sections = [
                  {
                    viewKey: 'can_view_onboarding',
                    label: 'Onboarding',
                    desc: 'Access to employee onboarding section',
                    subPerms: [
                      { key: 'can_view_onboarding_dashboard', label: 'Dashboard', desc: 'Access to the onboarding dashboard overview' },
                      { key: 'can_view_applicants', label: 'Applicants', desc: 'Access to the applicants list' },
                      { key: 'can_view_hiring_pipeline', label: 'Hiring Pipeline', desc: 'Access to the Paradox hiring pipeline tracker and Interview Calendar' },
                      { key: 'can_view_orientation_groups', label: 'Orientation Groups', desc: 'Access to orientation group scheduling' },
                      { key: 'can_view_onboarding_templates', label: 'Templates', desc: "Access to onboarding's document templates sub-tab (not the top-level Action Templates tab)" },
                      { key: 'can_view_activated_insights', label: 'Activated Insights', desc: 'Access to Activated Insights training/compliance tracking' },
                      { key: 'can_add_hires', label: 'Add New Hires', desc: 'Can add new hires to the system' },
                      { key: 'can_edit_hires', label: 'Edit Hires', desc: 'Can edit existing hire details and tasks' },
                      { key: 'can_delete_hires', label: 'Delete Hires', desc: 'Can remove hires from the system' },
                      { key: 'can_manage_templates', label: 'Manage Templates', desc: 'Can create, edit, and delete task templates' },
                    ],
                  },
                  { viewKey: 'can_view_clients', label: 'Clients', desc: 'Access to client management section' },
                  { viewKey: 'can_view_wellsky_caregivers', label: 'Care Pros', desc: 'Access to the read-only Care Pros list synced from WellSky' },
                  { viewKey: 'can_view_incident_reports', label: 'Action Templates', desc: 'Access to the Action Templates tab' },
                  { viewKey: 'can_view_faq', label: 'FAQ', desc: 'Access to the staff FAQ/knowledge base', subPerms: [
                    { key: 'can_create_faq_articles', label: 'Create Articles', desc: 'Publish new FAQ articles directly' },
                    { key: 'can_edit_faq_articles', label: 'Modify Articles', desc: 'Edit any existing article directly, and approve or reject suggested edits from others' },
                    { key: 'can_suggest_faq_edits', label: 'Suggest Edits', desc: "Propose a change to an existing article for someone with Modify Articles to approve -- can't publish changes directly" },
                    { key: 'can_delete_faq_articles', label: 'Delete Articles', desc: 'Permanently delete FAQ articles' },
                    { key: 'can_pin_faq_articles', label: 'Pin Articles', desc: 'Pin or unpin an article so it floats to the top of the list' },
                    { key: 'can_comment_faq_articles', label: 'Comment', desc: 'Add comments/questions under an article' },
                  ]},
                  { viewKey: 'can_view_jotform_submissions', label: 'Form Submissions', desc: 'Access to the Jotform submissions inbox' },
                  { viewKey: 'can_view_todos', label: 'Task Board', desc: 'Access to the internal task board', subPerms: [
                    { key: 'can_view_all_todos', label: 'View All Tasks', desc: "See everyone's tasks, not just your own assigned/created ones" },
                    { key: 'can_delete_todos', label: 'Delete Tasks', desc: 'Permanently delete tasks' },
                    { key: 'can_view_staffing_tab', label: 'Staffing Tab', desc: 'See a dedicated Task Board tab listing every task assigned to someone in the Staffing department' },
                    { key: 'can_view_field_supervisors_tab', label: 'Field Supervisors Tab', desc: 'See a dedicated Task Board tab listing every task assigned to someone in the Field Supervisors department' },
                  ]},
                  { viewKey: 'can_view_meetings', label: 'Meeting Agenda', desc: 'Access to the Meeting Agenda tab -- view recurring meetings and add discussion topics to upcoming ones', subPerms: [
                    { key: 'can_create_meetings', label: 'Create Meetings', desc: 'Create new meetings -- can edit/delete and manage attendees/templates for the ones they create' },
                    { key: 'can_manage_all_meetings', label: 'Manage All Meetings', desc: 'Edit, delete, and manage attendees/templates for every meeting, not just ones they created' },
                  ]},
                  { viewKey: 'can_view_phone_screen', label: 'Phone Screen', desc: 'Access to phone screen management section', subPerms: [
                    { key: 'can_receive_phone_popups', label: 'Incoming Call Popups', desc: 'Show floating incoming/answered call notifications' },
                  ]},
                  { viewKey: 'can_view_care_consultation', label: 'Care Consultation', desc: 'Access to care consultation section', subPerms: [
                    { key: 'can_view_consultations', label: 'View Completed Forms', desc: 'Can view submitted consultation details' },
                    { key: 'can_edit_consultations', label: 'Edit Forms', desc: 'Can edit submitted consultation details' },
                    { key: 'can_delete_consultations', label: 'Delete Forms', desc: 'Can delete submitted consultations' },
                    { key: 'can_finalize_consultations', label: 'Finalize Forms', desc: 'Can mark consultations complete (Submit & Finish)' },
                    { key: 'can_view_consultation_archive', label: 'Consultation Archive', desc: 'Can view and archive consultations that have been sent to Sourced and WellSky' },
                  ]},
                  { viewKey: 'can_view_care_pro_transfer', label: 'Care Pro Transfer', desc: 'Access to care pro transfer onboarding applications', subPerms: [
                    { key: 'can_edit_applications', label: 'Edit Applications', desc: 'Can fill staff sections and mark applications complete' },
                    { key: 'can_delete_applications', label: 'Delete Applications', desc: 'Can delete submitted applications' },
                  ]},
                  { viewKey: 'can_receive_sms_notifications', label: 'SMS Notifications', desc: 'Allow this staff member to receive text notifications (Task Board + Support tickets). Consent is collected in person at hiring -- enabling this is what actually turns SMS on for them.' },
                  { viewKey: 'can_view_admin', label: 'Admin', desc: 'Access to admin panel — view, edit, and add users', subPerms: [
                    { key: 'can_invite_staff', label: 'Invite Staff', desc: 'Can invite new admin staff and set their permissions' },
                  ]},
                ];
                return (
                  <div className="space-y-3">
                    {sections.map(({ viewKey, label, desc, subPerms }) => (
                      <div key={viewKey} className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="flex items-center justify-between p-4 bg-gray-50">
                          <div>
                            <p className="font-semibold text-gray-900">{label}</p>
                            <p className="text-sm text-gray-700">{desc}</p>
                          </div>
                          <Toggle permKey={viewKey} />
                        </div>
                        {subPerms && formData.permissions[viewKey] && (
                          <div className="divide-y divide-gray-100">
                            {subPerms.map(({ key, label: subLabel, desc: subDesc }) => (
                              <div key={key} className="flex items-center justify-between px-4 py-3 pl-8 bg-white">
                                <div>
                                  <p className="text-sm font-medium text-gray-800">{subLabel}</p>
                                  <p className="text-xs text-gray-700">{subDesc}</p>
                                </div>
                                <Toggle permKey={key} />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-[#2c4f26] text-white py-3 rounded-xl font-semibold hover:bg-[#1f3a1c] transition shadow-lg shadow-[#2c4f26]/30 disabled:opacity-50"
              >
                {isSubmitting ? 'Sending Invite...' : 'Send Invite'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 border border-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Permissions Modal Component
const EditPermissionsModal = ({ show, admin, onClose, onSave, isSaving }) => {
  const [localPermissions, setLocalPermissions] = useState({
    can_add_hires: false, can_edit_hires: false, can_delete_hires: false, can_manage_templates: false,
    can_view_onboarding: false, can_view_clients: false, can_view_wellsky_caregivers: false, can_view_incident_reports: false, can_view_faq: false, can_create_faq_articles: false, can_edit_faq_articles: false, can_suggest_faq_edits: false, can_delete_faq_articles: false, can_pin_faq_articles: false, can_comment_faq_articles: false, can_view_jotform_submissions: false, can_view_todos: false, can_view_all_todos: false, can_delete_todos: false, can_view_staffing_tab: false, can_view_field_supervisors_tab: false, can_view_meetings: false, can_create_meetings: false, can_manage_all_meetings: false, can_view_carepro: false, can_view_phone_screen: false, can_receive_phone_popups: false,
    can_view_care_consultation: false, can_view_consultations: false, can_edit_consultations: false, can_delete_consultations: false, can_finalize_consultations: false, can_view_consultation_archive: false,
    can_view_care_pro_transfer: false, can_edit_applications: false, can_delete_applications: false,
    can_view_hiring_pipeline: false, can_view_onboarding_dashboard: false, can_view_applicants: false, can_view_orientation_groups: false, can_view_onboarding_templates: false, can_view_activated_insights: false,
    can_view_admin: false, can_invite_staff: false,
    can_view_support: false, can_reply_tickets: false, can_add_internal_notes: false, can_close_tickets: false, can_manage_tickets: false,
    can_receive_ticket_notifications: false, notify_categories: [], can_receive_sms_notifications: false, sms_notify_categories: [],
  });
  const [localPhone, setLocalPhone] = useState(formatStaffPhone(admin?.phone || ''));
  const [localDepartment, setLocalDepartment] = useState(admin?.department || '');

  useEffect(() => {
    setLocalPhone(formatStaffPhone(admin?.phone || ''));
    setLocalDepartment(admin?.department || '');
  }, [admin]);

  useEffect(() => {
    if (admin?.admin_permissions?.[0]) {
      const p = admin.admin_permissions[0];
      setLocalPermissions({
        can_add_hires: p.can_add_hires || false, can_edit_hires: p.can_edit_hires || false,
        can_delete_hires: p.can_delete_hires || false, can_manage_templates: p.can_manage_templates || false,
        can_view_onboarding: p.can_view_onboarding || false, can_view_clients: p.can_view_clients || false, can_view_wellsky_caregivers: p.can_view_wellsky_caregivers || false, can_view_incident_reports: p.can_view_incident_reports || false, can_view_faq: p.can_view_faq || false, can_create_faq_articles: p.can_create_faq_articles || false, can_edit_faq_articles: p.can_edit_faq_articles || false, can_suggest_faq_edits: p.can_suggest_faq_edits || false, can_delete_faq_articles: p.can_delete_faq_articles || false, can_pin_faq_articles: p.can_pin_faq_articles || false, can_comment_faq_articles: p.can_comment_faq_articles || false, can_view_todos: p.can_view_todos || false, can_view_all_todos: p.can_view_all_todos || false, can_delete_todos: p.can_delete_todos || false, can_view_staffing_tab: p.can_view_staffing_tab || false, can_view_field_supervisors_tab: p.can_view_field_supervisors_tab || false, can_view_meetings: p.can_view_meetings || false, can_create_meetings: p.can_create_meetings || false, can_manage_all_meetings: p.can_manage_all_meetings || false,
        can_view_carepro: p.can_view_carepro || false, can_view_phone_screen: p.can_view_phone_screen || false, can_receive_phone_popups: p.can_receive_phone_popups || false,
        can_view_care_consultation: p.can_view_care_consultation || false,
        can_view_consultations: p.can_view_consultations || false, can_edit_consultations: p.can_edit_consultations || false,
        can_delete_consultations: p.can_delete_consultations || false, can_finalize_consultations: p.can_finalize_consultations || false, can_view_consultation_archive: p.can_view_consultation_archive || false,
        can_view_care_pro_transfer: p.can_view_care_pro_transfer || false, can_edit_applications: p.can_edit_applications || false, can_delete_applications: p.can_delete_applications || false,
        can_view_hiring_pipeline: p.can_view_hiring_pipeline || false, can_view_onboarding_dashboard: p.can_view_onboarding_dashboard || false, can_view_applicants: p.can_view_applicants || false, can_view_orientation_groups: p.can_view_orientation_groups || false, can_view_onboarding_templates: p.can_view_onboarding_templates || false, can_view_activated_insights: p.can_view_activated_insights || false,
        can_view_admin: p.can_view_admin || false, can_invite_staff: p.can_invite_staff || false,
        can_view_support: p.can_view_support || false, can_reply_tickets: p.can_reply_tickets || false,
        can_add_internal_notes: p.can_add_internal_notes || false, can_close_tickets: p.can_close_tickets || false, can_manage_tickets: p.can_manage_tickets || false,
        can_receive_ticket_notifications: p.can_receive_ticket_notifications || false, notify_categories: p.notify_categories || [],
        can_receive_sms_notifications: p.can_receive_sms_notifications || false, sms_notify_categories: p.sms_notify_categories || [],
      });
    }
  }, [admin]);

  if (!show || !admin) return null;

  const handleSave = () => {
    onSave(admin.id, localPermissions, localPhone, localDepartment);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Edit Permissions</h2>
            <button
              onClick={onClose}
              className="text-gray-600 hover:text-gray-600 transition"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="mb-6 p-4 bg-gray-50 rounded-xl space-y-3">
            <div>
              <p className="font-semibold text-gray-900">{admin.full_name}</p>
              <p className="text-sm text-gray-700">{admin.email}</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Phone Number</label>
              <input
                type="tel"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm bg-white"
                placeholder="555-555-5555"
                value={localPhone}
                onChange={e => setLocalPhone(formatStaffPhone(e.target.value))}
              />
              <p className="text-xs text-gray-400 mt-1">Needed for SMS notifications. {admin.full_name} can also set this themselves from their own Account Settings.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Department</label>
              <select
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm bg-white"
                value={localDepartment}
                onChange={e => setLocalDepartment(e.target.value)}
              >
                <option value="">No department</option>
                {STAFF_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <p className="text-xs text-gray-400 mt-1">Groups this person into Task Board tabs like Staffing or Field Supervisors (separately granted below).</p>
            </div>
          </div>

          {(() => {
            const Toggle = ({ permKey }) => (
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={localPermissions[permKey]}
                  onChange={(e) => setLocalPermissions(prev => ({ ...prev, [permKey]: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2c4f26]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2c4f26]"></div>
              </label>
            );

            const sections = [
              {
                viewKey: 'can_view_onboarding',
                label: 'Onboarding',
                desc: 'Access to employee onboarding section',
                subPerms: [
                  { key: 'can_view_onboarding_dashboard', label: 'Dashboard', desc: 'Access to the onboarding dashboard overview' },
                  { key: 'can_view_applicants', label: 'Applicants', desc: 'Access to the applicants list' },
                  { key: 'can_view_hiring_pipeline', label: 'Hiring Pipeline', desc: 'Access to the Paradox hiring pipeline tracker and Interview Calendar' },
                  { key: 'can_view_orientation_groups', label: 'Orientation Groups', desc: 'Access to orientation group scheduling' },
                  { key: 'can_view_onboarding_templates', label: 'Templates', desc: "Access to onboarding's document templates sub-tab (not the top-level Action Templates tab)" },
                  { key: 'can_view_activated_insights', label: 'Activated Insights', desc: 'Access to Activated Insights training/compliance tracking' },
                  { key: 'can_add_hires', label: 'Add New Hires', desc: 'Can add new hires to the system' },
                  { key: 'can_edit_hires', label: 'Edit Hires', desc: 'Can edit existing hire details and tasks' },
                  { key: 'can_delete_hires', label: 'Delete Hires', desc: 'Can remove hires from the system' },
                  { key: 'can_manage_templates', label: 'Manage Templates', desc: 'Can create, edit, and delete task templates' },
                ],
              },
              { viewKey: 'can_view_clients', label: 'Clients', desc: 'Access to client management section' },
              { viewKey: 'can_view_incident_reports', label: 'Action Templates', desc: 'Access to the Action Templates tab' },
              { viewKey: 'can_view_faq', label: 'FAQ', desc: 'Access to the staff FAQ/knowledge base', subPerms: [
                { key: 'can_create_faq_articles', label: 'Create Articles', desc: 'Publish new FAQ articles directly' },
                { key: 'can_edit_faq_articles', label: 'Modify Articles', desc: 'Edit any existing article directly, and approve or reject suggested edits from others' },
                { key: 'can_suggest_faq_edits', label: 'Suggest Edits', desc: "Propose a change to an existing article for someone with Modify Articles to approve -- can't publish changes directly" },
                { key: 'can_delete_faq_articles', label: 'Delete Articles', desc: 'Permanently delete FAQ articles' },
                { key: 'can_pin_faq_articles', label: 'Pin Articles', desc: 'Pin or unpin an article so it floats to the top of the list' },
                { key: 'can_comment_faq_articles', label: 'Comment', desc: 'Add comments/questions under an article' },
              ]},
              { viewKey: 'can_view_jotform_submissions', label: 'Form Submissions', desc: 'Access to the Jotform submissions inbox' },
              { viewKey: 'can_view_todos', label: 'Task Board', desc: 'Access to the internal task board', subPerms: [
                { key: 'can_view_all_todos', label: 'View All Tasks', desc: "See everyone's tasks, not just your own assigned/created ones" },
                { key: 'can_delete_todos', label: 'Delete Tasks', desc: 'Permanently delete tasks' },
                { key: 'can_view_staffing_tab', label: 'Staffing Tab', desc: 'See a dedicated Task Board tab listing every task assigned to someone in the Staffing department' },
                { key: 'can_view_field_supervisors_tab', label: 'Field Supervisors Tab', desc: 'See a dedicated Task Board tab listing every task assigned to someone in the Field Supervisors department' },
              ]},
              { viewKey: 'can_view_meetings', label: 'Meeting Agenda', desc: 'Access to the Meeting Agenda tab -- view recurring meetings and add discussion topics to upcoming ones', subPerms: [
                { key: 'can_create_meetings', label: 'Create Meetings', desc: 'Create new meetings -- can edit/delete and manage attendees/templates for the ones they create' },
                { key: 'can_manage_all_meetings', label: 'Manage All Meetings', desc: 'Edit, delete, and manage attendees/templates for every meeting, not just ones they created' },
              ]},
              { viewKey: 'can_view_phone_screen', label: 'Phone Screen', desc: 'Access to phone screen management section', subPerms: [
                { key: 'can_receive_phone_popups', label: 'Incoming Call Popups', desc: 'Show floating incoming/answered call notifications' },
              ]},
              { viewKey: 'can_view_care_consultation', label: 'Care Consultation', desc: 'Access to care consultation section', subPerms: [
                { key: 'can_view_consultations', label: 'View Completed Forms', desc: 'Can view submitted consultation details' },
                { key: 'can_edit_consultations', label: 'Edit Forms', desc: 'Can edit submitted consultation details' },
                { key: 'can_delete_consultations', label: 'Delete Forms', desc: 'Can delete submitted consultations' },
                { key: 'can_finalize_consultations', label: 'Finalize Forms', desc: 'Can mark consultations complete (Submit & Finish)' },
                { key: 'can_view_consultation_archive', label: 'Consultation Archive', desc: 'Can view and archive consultations that have been sent to Sourced and WellSky' },
              ]},
              { viewKey: 'can_view_care_pro_transfer', label: 'Care Pro Transfer', desc: 'Access to care pro transfer onboarding applications', subPerms: [
                { key: 'can_edit_applications', label: 'Edit Applications', desc: 'Can fill staff sections and mark applications complete' },
                { key: 'can_delete_applications', label: 'Delete Applications', desc: 'Can delete submitted applications' },
              ]},
              { viewKey: 'can_view_support', label: 'Support', desc: 'Access to support ticket system', subPerms: [
                { key: 'can_receive_ticket_notifications', label: 'Ticket Notifications', desc: 'Receive email alerts for new tickets and customer replies' },
                { key: 'can_reply_tickets',      label: 'Reply to Tickets',  desc: 'Can send public replies to customers' },
                { key: 'can_add_internal_notes', label: 'Internal Notes',    desc: 'Can add internal notes visible only to staff' },
                { key: 'can_close_tickets',      label: 'Close Tickets',     desc: 'Can finalize and close resolved tickets' },
                { key: 'can_manage_tickets',     label: 'Manage Tickets',    desc: 'Can change status, priority, and assignment' },
              ]},
              { viewKey: 'can_receive_sms_notifications', label: 'SMS Notifications', desc: 'Allow this staff member to receive text notifications (Task Board + Support tickets). Consent is collected in person at hiring -- enabling this is what actually turns SMS on for them.' },
              { viewKey: 'can_view_admin', label: 'Admin', desc: 'Access to admin panel — view, edit, and add users', subPerms: [
                { key: 'can_invite_staff', label: 'Invite Staff', desc: 'Can invite new admin staff and set their permissions' },
              ]},
            ];

            return (
              <div className="space-y-3">
                {sections.map(({ viewKey, label, desc, subPerms }) => (
                  <div key={viewKey} className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between p-4 bg-gray-50">
                      <div>
                        <p className="font-semibold text-gray-900">{label}</p>
                        <p className="text-sm text-gray-700">{desc}</p>
                      </div>
                      <Toggle permKey={viewKey} />
                    </div>
                    {viewKey === 'can_receive_sms_notifications' && localPermissions[viewKey] && (
                      <div className="px-4 py-3 pl-8 bg-gray-50 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs text-gray-500">Only text for these specifically (nothing is sent for an unchecked category):</p>
                          <button
                            type="button"
                            onClick={() => setLocalPermissions(prev => ({ ...prev, sms_notify_categories: SMS_CATEGORY_GROUPS.flatMap(g => g.categories) }))}
                            className="shrink-0 text-xs font-semibold text-[#2c4f26] hover:underline"
                          >
                            Enable All
                          </button>
                        </div>
                        {SMS_CATEGORY_GROUPS.map(group => (
                          <div key={group.label}>
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{group.label}</p>
                            <div className="flex flex-wrap gap-2">
                              {group.categories.map(cat => {
                                const checked = (localPermissions.sms_notify_categories || []).includes(cat);
                                return (
                                  <label key={cat} className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border cursor-pointer transition ${checked ? 'bg-[#2c4f26]/10 border-[#2c4f26] text-[#2c4f26]' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => setLocalPermissions(prev => ({
                                        ...prev,
                                        sms_notify_categories: e.target.checked
                                          ? [...(prev.sms_notify_categories || []), cat]
                                          : (prev.sms_notify_categories || []).filter(c => c !== cat),
                                      }))}
                                      className="sr-only"
                                    />
                                    {SMS_CATEGORY_LABELS[cat]}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {subPerms && localPermissions[viewKey] && (
                      <div className="divide-y divide-gray-100">
                        {subPerms.map(({ key, label: subLabel, desc: subDesc }) => (
                          <React.Fragment key={key}>
                            <div className="flex items-center justify-between px-4 py-3 pl-8 bg-white">
                              <div>
                                <p className="text-sm font-medium text-gray-800">{subLabel}</p>
                                <p className="text-xs text-gray-700">{subDesc}</p>
                              </div>
                              <Toggle permKey={key} />
                            </div>
                            {key === 'can_receive_ticket_notifications' && localPermissions[key] && (
                              <div className="px-4 py-3 pl-8 bg-gray-50">
                                <p className="text-xs text-gray-500 mb-2">Only get emailed for these categories:</p>
                                <div className="flex flex-wrap gap-2">
                                  {TICKET_CATEGORIES.map(cat => {
                                    const checked = (localPermissions.notify_categories || []).includes(cat);
                                    return (
                                      <label key={cat} className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border cursor-pointer transition ${checked ? 'bg-[#2c4f26]/10 border-[#2c4f26] text-[#2c4f26]' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={(e) => setLocalPermissions(prev => ({
                                            ...prev,
                                            notify_categories: e.target.checked
                                              ? [...(prev.notify_categories || []), cat]
                                              : (prev.notify_categories || []).filter(c => c !== cat),
                                          }))}
                                          className="sr-only"
                                        />
                                        {TICKET_CATEGORY_LABELS[cat]}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}

          <div className="flex gap-4 pt-6">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 bg-[#2c4f26] text-white py-3 rounded-xl font-semibold hover:bg-[#1f3a1c] transition shadow-lg shadow-[#2c4f26]/30 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 border border-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-50 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Audit Log (Owner Only) — Care Consultation + Support tickets so far, other
// tabs later. Every INSERT/UPDATE/DELETE on the tables this is attached to
// is captured automatically server-side by the log_activity trigger (see
// add_consultation_audit_log_triggers.sql / add_ticket_audit_log_triggers.sql)
// — this view is purely a reader + a readable-summary layer over
// activity_log's raw {old, new} row snapshots. "VIEW" rows are the one thing
// that trigger can't see, logged explicitly from CareConsultationTab's
// logSessionView and AdminTicketDetailView's mount effect.
const AUDIT_PAGE_SIZE = 50;
const AUDIT_ENTITY_TYPES = ['consultation_sessions', 'consultation_documents', 'tickets', 'ticket_messages', 'ticket_attachments', 'activated_insights_import', 'action_templates'];
const AUDIT_TICKET_ENTITY_TYPES = new Set(['tickets', 'ticket_messages', 'ticket_attachments']);
const AUDIT_NOISY_FIELDS = new Set(['updated_at', 'id', 'session_id', 'ticket_id', 'message_id', 'updated_by', 'template_number']);

const auditLooksLikeDataUrl = (v) => typeof v === 'string' && v.startsWith('data:image');

// A raw JSON.stringify of an array of objects (e.g. the Hospital/Rehab
// Admission "permanent_care_pros" caregivers picker) blows past the old
// 80-char truncation on a SINGLE element, so adding more people to an
// already-non-empty list showed old/new as identical -- both got cut off
// right after the one element they had in common, hiding the actual
// change entirely. Rendered as readable names instead, joined by comma,
// so the diff actually shows what changed.
const auditFmtArrayItem = (item) => {
  if (item && typeof item === 'object') return item.name || item.title || item.topic || item.full_name || JSON.stringify(item).slice(0, 40);
  return String(item);
};

const auditFmtScalar = (v) => {
  if (auditLooksLikeDataUrl(v)) return '[signature]';
  if (v === null || v === undefined || v === '') return '(empty)';
  if (Array.isArray(v)) return v.length === 0 ? '(none)' : v.map(auditFmtArrayItem).join(', ');
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 80);
  return String(v).slice(0, 80);
};

const auditShallowDiff = (oldObj, newObj) => {
  const o = oldObj || {}; const n = newObj || {};
  const keys = [...new Set([...Object.keys(o), ...Object.keys(n)])];
  return keys.filter(k => JSON.stringify(o[k]) !== JSON.stringify(n[k]));
};

// "snake_case" / "dotted.key" -> "Snake Case" / "Dotted Key" -- a generic
// fallback rather than hand-mapping every column across two domains plus
// consultations' dynamic, user-defined field_values keys.
const auditFieldLabel = (key) => String(key).replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Generic "diff every top-level column, drilling into named jsonb blobs"
// walker shared by both domains below. Returns structured {field, from, to}
// rows rather than a pre-joined string -- the detail panel lists every
// changed field in full instead of truncating to "+N more", since it's
// already tucked behind a click.
const auditDiffParts = (old, neu, { jsonbKeys = [], resolve = {} } = {}) => {
  const topChanged = auditShallowDiff(old, neu).filter(k => !AUDIT_NOISY_FIELDS.has(k));
  const parts = [];
  for (const k of topChanged) {
    const ov = old?.[k]; const nv = neu?.[k];
    if (jsonbKeys.includes(k) && (typeof nv === 'object' || typeof ov === 'object')) {
      for (const nk of auditShallowDiff(ov, nv)) parts.push({ field: nk, from: auditFmtScalar(ov?.[nk]), to: auditFmtScalar(nv?.[nk]) });
    } else if (resolve[k]) {
      parts.push({ field: k, from: resolve[k](ov) ?? auditFmtScalar(ov), to: resolve[k](nv) ?? auditFmtScalar(nv) });
    } else {
      parts.push({ field: k, from: auditFmtScalar(ov), to: auditFmtScalar(nv) });
    }
  }
  return parts;
};

// Ticket writes always go through service-role edge functions (customers
// have no account to authenticate as, and staff-facing endpoints validate
// the caller separately rather than writing as them) — so
// activity_log.user_id is always null here. The row itself already
// records who did it (created_by / staff_user_id / sender_type), so the
// actor is resolved from there instead.
const summarizeTicketAuditEntry = (entry, ctx) => {
  const { ticketInfoFor, staffNameFor } = ctx;
  const old = entry.metadata?.old || null;
  const neu = entry.metadata?.new || null;
  const ticketId = entry.entity_type === 'tickets' ? entry.entity_id : (neu?.ticket_id ?? old?.ticket_id);
  const info = ticketInfoFor(ticketId);
  const ticketLabel = info ? `Ticket #${info.ticket_number} (${info.customer_name})` : '(deleted ticket)';

  let actorLabel;
  if (entry.entity_type === 'ticket_messages') {
    const senderType = neu?.sender_type ?? old?.sender_type;
    actorLabel = senderType === 'customer' ? 'Customer' : (staffNameFor(neu?.staff_user_id ?? old?.staff_user_id) || 'Staff');
  } else if (entry.entity_type === 'tickets' && entry.action === 'INSERT') {
    const createdBy = neu?.created_by;
    actorLabel = createdBy ? (staffNameFor(createdBy) || 'Staff') : 'Customer (public form)';
  } else {
    // Only staff-facing endpoints ever touch a ticket row after creation —
    // there's no per-update actor column to name who, specifically.
    actorLabel = 'Staff';
  }

  if (entry.entity_type === 'ticket_messages') {
    const kind = (neu?.is_internal ?? old?.is_internal) ? 'an internal note' : 'a reply';
    if (entry.action === 'INSERT') return `${actorLabel} added ${kind} on ${ticketLabel}`;
    if (entry.action === 'DELETE') return `${actorLabel} deleted ${kind} on ${ticketLabel}`;
  } else if (entry.entity_type === 'ticket_attachments') {
    const fileName = neu?.file_name ?? old?.file_name;
    if (entry.action === 'INSERT') return `${actorLabel} attached "${fileName}" to ${ticketLabel}`;
    if (entry.action === 'DELETE') return `${actorLabel} removed attachment "${fileName}" from ${ticketLabel}`;
  } else {
    if (entry.action === 'INSERT') return `${actorLabel} created ${ticketLabel}`;
    if (entry.action === 'DELETE') return `${actorLabel} deleted ${ticketLabel}`;
  }
  if (entry.action === 'VIEW') return `${actorLabel} viewed ${ticketLabel}`;
  return `${actorLabel} updated ${ticketLabel}`;
};

// import-activated-insights (the Grok/automation endpoint -- no human
// session, so there's no staff actor to name) logs one row per run here
// instead of relying on a table trigger, since a failed run never writes
// any activated_insights_training rows for a trigger to fire on.
const summarizeInsightsImportEntry = (entry) => {
  const m = entry.metadata || {};
  const locationLabel = m.location ? `location ${m.location}` : 'an unknown location';
  const staffLabel = entry.profile?.full_name || 'Staff';
  if (entry.action === 'IMPORT_SUCCESS') {
    return `Automated Activated Insights import for ${locationLabel} succeeded — ${m.imported ?? 0} record${m.imported === 1 ? '' : 's'} imported (${m.matched ?? 0} matched, ${m.unmatched ?? 0} unmatched)`;
  }
  if (entry.action === 'IMPORT_FAILURE') {
    return `Automated Activated Insights import for ${locationLabel} failed — ${m.error || 'unknown error'}`;
  }
  if (entry.action === 'REFRESH_TRIGGERED') {
    return `${staffLabel} triggered an on-demand Activated Insights refresh`;
  }
  if (entry.action === 'REFRESH_STATUS') {
    const REFRESH_STATUS_SUMMARY = { started: 'started', waiting_2fa: 'is waiting on 2FA', done: 'finished', failed: 'failed' };
    const verb = REFRESH_STATUS_SUMMARY[m.status] || m.status;
    return `Grok's Activated Insights refresh for ${locationLabel} ${verb}${m.status === 'failed' && m.detail ? ` — ${m.detail}` : ''}`;
  }
  return `${staffLabel} tried to trigger an on-demand Activated Insights refresh, but the webhook call failed`;
};

// Shared by the global (owner-only) Audit Log page and the per-template
// Audit Log tab on ActionTemplateDetail -- same row shape either way.
const summarizeActionTemplateAuditEntry = (entry) => {
  const staffLabel = entry.profile?.full_name || 'Unknown staff';
  const neu = entry.metadata?.new || null;
  const old = entry.metadata?.old || null;
  const templateType = neu?.template_type ?? old?.template_type;
  const typeLabel = actionTemplateTypeConfig(templateType)?.label || templateType || 'action template';
  const num = neu?.template_number ?? old?.template_number;
  const subject = `${typeLabel}${num ? ` #${num}` : ''}`;
  if (entry.action === 'INSERT') return `${staffLabel} created ${subject}`;
  if (entry.action === 'DELETE') return `${staffLabel} deleted ${subject}`;
  if (entry.action === 'VIEW') return `${staffLabel} viewed ${subject}`;
  return `${staffLabel} updated ${subject}`;
};

// Turns one activity_log row into a single plain-English line, with no
// field-level detail in it -- that lives in auditEntryDiffList below,
// shown only once someone opens "View details" for an UPDATE row. Never
// shows a raw signature data URL either way.
const summarizeAuditEntry = (entry, ctx) => {
  if (entry.entity_type === 'activated_insights_import') return summarizeInsightsImportEntry(entry);
  if (entry.entity_type === 'action_templates') return summarizeActionTemplateAuditEntry(entry);
  if (AUDIT_TICKET_ENTITY_TYPES.has(entry.entity_type)) return summarizeTicketAuditEntry(entry, ctx);

  const { clientNameFor, templateTitleFor } = ctx;
  const staffLabel = entry.profile?.full_name || (entry.user_id ? 'Unknown staff' : 'Client (remote signing)');
  const isDoc = entry.entity_type === 'consultation_documents';
  const old = entry.metadata?.old || null;
  const neu = entry.metadata?.new || null;
  const sessionId = isDoc ? (neu?.session_id ?? old?.session_id) : entry.entity_id;
  const clientLabel = clientNameFor(sessionId) || '(deleted session)';
  const docLabel = isDoc ? (templateTitleFor(neu?.template_key ?? old?.template_key) || (neu?.template_key ?? old?.template_key)) : null;
  const subject = isDoc ? `${docLabel} for ${clientLabel}` : `the consultation for ${clientLabel}`;

  if (entry.action === 'INSERT') return `${staffLabel} created ${subject}`;
  if (entry.action === 'DELETE') return `${staffLabel} deleted ${subject}`;
  if (entry.action === 'VIEW') return `${staffLabel} viewed ${subject}`;
  return `${staffLabel} updated ${subject}`;
};

// Structured field-level diff for an UPDATE row's "View details" panel --
// same branching as the summary above, kept separate so the main sentence
// never has to carry it.
const auditEntryDiffList = (entry, ctx) => {
  const old = entry.metadata?.old || null;
  const neu = entry.metadata?.new || null;
  if (AUDIT_TICKET_ENTITY_TYPES.has(entry.entity_type)) {
    return auditDiffParts(old, neu, { resolve: { assigned_to: ctx.staffNameFor } });
  }
  if (entry.entity_type === 'action_templates') {
    return auditDiffParts(old, neu, { jsonbKeys: ['fields'] });
  }
  return auditDiffParts(old, neu, { jsonbKeys: ['field_values', 'shared_data'] });
};

const AUDIT_ACTION_META = {
  INSERT: { label: 'Created', color: 'bg-green-100 text-green-700' },
  UPDATE: { label: 'Updated', color: 'bg-blue-100 text-blue-700' },
  DELETE: { label: 'Deleted', color: 'bg-red-100 text-red-700' },
  VIEW: { label: 'Viewed', color: 'bg-gray-100 text-gray-600' },
  IMPORT_SUCCESS: { label: 'Import Succeeded', color: 'bg-green-100 text-green-700' },
  IMPORT_FAILURE: { label: 'Import Failed', color: 'bg-red-100 text-red-700' },
  REFRESH_TRIGGERED: { label: 'Refresh Triggered', color: 'bg-blue-100 text-blue-700' },
  REFRESH_FAILED: { label: 'Refresh Failed', color: 'bg-red-100 text-red-700' },
  REFRESH_STATUS: { label: 'Refresh Progress', color: 'bg-gray-100 text-gray-600' },
};

const AuditLogView = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffFilter, setStaffFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sessionInfo, setSessionInfo] = useState({}); // id -> { client_name, office_code }
  const [ticketInfo, setTicketInfo] = useState({}); // id -> { ticket_number, customer_name, subject }
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    supabase.from('profiles').select('id, full_name').in('role', ['owner', 'admin_staff']).order('full_name')
      .then(({ data }) => setStaffUsers(data || []));
  }, []);

  const loadPage = async (offset) => {
    let query = supabase
      .from('activity_log')
      .select('*, profile:user_id(full_name)')
      .in('entity_type', AUDIT_ENTITY_TYPES)
      .order('created_at', { ascending: false })
      .range(offset, offset + AUDIT_PAGE_SIZE - 1);
    if (staffFilter !== 'all') query = query.eq('user_id', staffFilter);
    if (actionFilter !== 'all') query = query.eq('action', actionFilter);
    if (dateFrom) query = query.gte('created_at', `${dateFrom}T00:00:00`);
    if (dateTo) query = query.lte('created_at', `${dateTo}T23:59:59`);
    const { data, error } = await query;
    if (error) { console.error('Failed to load audit log:', error); return []; }
    return data || [];
  };

  // Resolves each entry's session (client_name + office_code, for document
  // template-title lookups) in one batched query rather than N+1 — sessions
  // that no longer exist (deleted) simply won't resolve, shown as such.
  const resolveSessionInfo = async (rows) => {
    const ids = new Set();
    rows.forEach(r => {
      const sid = r.entity_type === 'consultation_documents' ? (r.metadata?.new?.session_id ?? r.metadata?.old?.session_id) : r.entity_id;
      if (sid) ids.add(sid);
    });
    const missing = [...ids].filter(id => !sessionInfo[id]);
    if (missing.length === 0) return;
    const { data } = await supabase.from('consultation_sessions').select('id, client_name, office_code').in('id', missing);
    if (!data) return;
    setSessionInfo(prev => {
      const next = { ...prev };
      data.forEach(s => { next[s.id] = { client_name: s.client_name, office_code: s.office_code }; });
      return next;
    });
  };

  // Same batched-lookup shape as resolveSessionInfo, for the parent ticket
  // (rows for ticket_messages/ticket_attachments only carry ticket_id).
  const resolveTicketInfo = async (rows) => {
    const ids = new Set();
    rows.forEach(r => {
      if (!AUDIT_TICKET_ENTITY_TYPES.has(r.entity_type)) return;
      const tid = r.entity_type === 'tickets' ? r.entity_id : (r.metadata?.new?.ticket_id ?? r.metadata?.old?.ticket_id);
      if (tid) ids.add(tid);
    });
    const missing = [...ids].filter(id => !ticketInfo[id]);
    if (missing.length === 0) return;
    const { data } = await supabase.from('tickets').select('id, ticket_number, customer_name, subject').in('id', missing);
    if (!data) return;
    setTicketInfo(prev => {
      const next = { ...prev };
      data.forEach(t => { next[t.id] = { ticket_number: t.ticket_number, customer_name: t.customer_name, subject: t.subject }; });
      return next;
    });
  };

  const loadInitial = async () => {
    setLoading(true);
    const rows = await loadPage(0);
    setEntries(rows);
    setHasMore(rows.length === AUDIT_PAGE_SIZE);
    await Promise.all([resolveSessionInfo(rows), resolveTicketInfo(rows)]);
    setLoading(false);
  };

  useEffect(() => { loadInitial(); }, [staffFilter, actionFilter, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = async () => {
    setLoadingMore(true);
    const rows = await loadPage(entries.length);
    setEntries(prev => [...prev, ...rows]);
    setHasMore(rows.length === AUDIT_PAGE_SIZE);
    await Promise.all([resolveSessionInfo(rows), resolveTicketInfo(rows)]);
    setLoadingMore(false);
  };

  const clientNameFor = (sessionId) => sessionInfo[sessionId]?.client_name;
  const templateTitleFor = (templateKey) => {
    if (!templateKey) return null;
    for (const info of Object.values(sessionInfo)) {
      const t = getTemplatesForOffice(info.office_code || MANASSAS_OFFICE_CODE).find(t => t.key === templateKey);
      if (t) return t.title;
    }
    return null;
  };
  const ticketInfoFor = (ticketId) => ticketInfo[ticketId];
  const staffNameFor = (staffId) => staffUsers.find(s => s.id === staffId)?.full_name;
  const auditCtx = { clientNameFor, templateTitleFor, ticketInfoFor, staffNameFor };

  const toggleExpanded = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const term = searchTerm.trim().toLowerCase();
  const visibleEntries = term
    ? entries.filter(e => {
        if (AUDIT_TICKET_ENTITY_TYPES.has(e.entity_type)) {
          const ticketId = e.entity_type === 'tickets' ? e.entity_id : (e.metadata?.new?.ticket_id ?? e.metadata?.old?.ticket_id);
          const info = ticketInfoFor(ticketId);
          return `${info?.customer_name || ''} ${info?.subject || ''}`.toLowerCase().includes(term);
        }
        const sessionId = e.entity_type === 'consultation_documents' ? (e.metadata?.new?.session_id ?? e.metadata?.old?.session_id) : e.entity_id;
        return (clientNameFor(sessionId) || '').toLowerCase().includes(term);
      })
    : entries;

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Search client / customer</label>
          <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Name or subject…"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Staff</label>
          <select value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
            <option value="all">All staff</option>
            {staffUsers.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Action</label>
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
            <option value="all">All actions</option>
            <option value="INSERT">Created</option>
            <option value="UPDATE">Updated</option>
            <option value="DELETE">Deleted</option>
            <option value="VIEW">Viewed</option>
            <option value="IMPORT_SUCCESS">Import Succeeded</option>
            <option value="IMPORT_FAILURE">Import Failed</option>
            <option value="REFRESH_TRIGGERED">Refresh Triggered</option>
            <option value="REFRESH_FAILED">Refresh Failed</option>
            <option value="REFRESH_STATUS">Refresh Progress</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-600 text-sm">Loading…</div>
      ) : visibleEntries.length === 0 ? (
        <p className="text-sm text-gray-600 text-center py-12">No activity found for these filters.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
          {visibleEntries.map(entry => {
            const meta = AUDIT_ACTION_META[entry.action] || { label: entry.action, color: 'bg-gray-100 text-gray-600' };
            const isInsightsImport = entry.entity_type === 'activated_insights_import';
            const diff = entry.action === 'UPDATE' ? auditEntryDiffList(entry, auditCtx) : [];
            const insightsStats = isInsightsImport ? Object.entries({
              Location: entry.metadata?.location,
              Imported: entry.metadata?.imported,
              Matched: entry.metadata?.matched,
              Unmatched: entry.metadata?.unmatched,
              Error: entry.metadata?.error,
              'Webhook Status': entry.metadata?.webhookStatus,
              Status: entry.action === 'REFRESH_STATUS' ? entry.metadata?.status : undefined,
              Detail: entry.action === 'REFRESH_STATUS' ? entry.metadata?.detail : undefined,
            }).filter(([, v]) => v !== undefined && v !== null) : [];
            const showDetails = entry.action === 'UPDATE' || (isInsightsImport && insightsStats.length > 0);
            const isExpanded = expanded.has(entry.id);
            return (
              <div key={entry.id} className="p-4">
                <div className="flex items-start gap-3">
                  <span className={`shrink-0 mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.color}`}>{meta.label}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm text-gray-800">{summarizeAuditEntry(entry, auditCtx)}</p>
                      <span className="text-xs text-gray-500 shrink-0 whitespace-nowrap">{new Date(entry.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                    </div>
                    {showDetails && (
                      <>
                        <button type="button" onClick={() => toggleExpanded(entry.id)} className="text-xs text-[#2c4f26] font-medium mt-1.5 hover:underline">
                          {isExpanded ? 'Hide details' : isInsightsImport ? 'View details' : diff.length > 0 ? `View details (${diff.length} change${diff.length === 1 ? '' : 's'})` : 'View details'}
                        </button>
                        {isExpanded && (
                          <div className="mt-2">
                            {isInsightsImport ? (
                              <div className="bg-gray-50 border border-gray-200 rounded-lg divide-y divide-gray-200 overflow-hidden">
                                {insightsStats.map(([label, value]) => (
                                  <div key={label} className="px-3 py-2 text-xs flex flex-wrap items-baseline gap-x-1.5">
                                    <span className="font-semibold text-gray-700">{label}:</span>
                                    <span className="text-gray-800">{Array.isArray(value) ? value.join(', ') : String(value)}</span>
                                  </div>
                                ))}
                              </div>
                            ) : diff.length > 0 ? (
                              <div className="bg-gray-50 border border-gray-200 rounded-lg divide-y divide-gray-200 overflow-hidden">
                                {diff.map((d, i) => (
                                  <div key={i} className="px-3 py-2 text-xs flex flex-wrap items-baseline gap-x-1.5">
                                    <span className="font-semibold text-gray-700">{auditFieldLabel(d.field)}:</span>
                                    <span className="text-gray-400 line-through">{d.from}</span>
                                    <span className="text-gray-400">→</span>
                                    <span className="text-gray-800">{d.to}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400">No tracked field changes.</p>
                            )}
                            <details className="mt-2 text-xs">
                              <summary className="cursor-pointer text-gray-400 hover:text-gray-600 select-none">Raw data</summary>
                              <pre className="mt-1.5 bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-700 overflow-x-auto max-h-64 overflow-y-auto">
                                {JSON.stringify(entry.metadata, null, 2)}
                              </pre>
                            </details>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && hasMore && !term && (
        <div className="flex justify-center mt-4">
          <button onClick={loadMore} disabled={loadingMore} className="px-5 py-2 text-sm font-medium text-[#2c4f26] border border-[#2c4f26]/30 rounded-lg hover:bg-[#2c4f26]/5 disabled:opacity-50 transition">
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

// Admin Management View Component (Owner Only)
const AdminManagementView = ({ setView, handleLogout, showToast, profile, permissions }) => {
  const canInviteStaff = profile?.role === 'owner' || permissions?.can_invite_staff;
  const [mainTab, setMainTab] = useState('admin_staff'); // 'admin_staff' | 'field_supervisors' | 'audit_log'
  const [adminStatusTab, setAdminStatusTab] = useState('active'); // 'active' | 'deactivated'
  // Field Supervisors are just admin_staff accounts tagged with
  // department = 'Field Supervisors' (see STAFF_DEPARTMENTS) -- this tab
  // is a filtered view of the same adminStaff list, not a separate role
  // or table, so it gets its own status sub-tab but no separate fetch.
  const [fieldSupervisorStatusTab, setFieldSupervisorStatusTab] = useState('active'); // 'active' | 'deactivated'
  const [adminStaff, setAdminStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({
    fullName: '', email: '', phone: '', department: '',
    permissions: {
      can_add_hires: false, can_edit_hires: false, can_delete_hires: false, can_manage_templates: false,
      can_view_onboarding: false, can_view_clients: false, can_view_wellsky_caregivers: false, can_view_incident_reports: false, can_view_faq: false, can_create_faq_articles: false, can_edit_faq_articles: false, can_suggest_faq_edits: false, can_delete_faq_articles: false, can_pin_faq_articles: false, can_comment_faq_articles: false, can_view_jotform_submissions: false, can_view_todos: false, can_view_all_todos: false, can_delete_todos: false, can_view_staffing_tab: false, can_view_field_supervisors_tab: false, can_view_meetings: false, can_create_meetings: false, can_manage_all_meetings: false, can_view_carepro: false, can_view_phone_screen: false, can_receive_phone_popups: false,
      can_view_care_consultation: false, can_view_consultations: false, can_edit_consultations: false, can_delete_consultations: false, can_finalize_consultations: false, can_view_consultation_archive: false,
      can_view_care_pro_transfer: false, can_edit_applications: false, can_delete_applications: false,
      can_view_hiring_pipeline: false, can_view_onboarding_dashboard: false, can_view_applicants: false, can_view_orientation_groups: false, can_view_onboarding_templates: false, can_view_activated_insights: false,
      can_view_admin: false, can_invite_staff: false,
      can_view_support: false, can_reply_tickets: false, can_add_internal_notes: false, can_close_tickets: false, can_manage_tickets: false, can_receive_ticket_notifications: false, notify_categories: [], can_receive_sms_notifications: false, sms_notify_categories: [],
    }
  });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const { data: admins } = await supabase
        .from('profiles')
        .select('id, full_name, email, phone, department, role, status, last_sign_in_at')
        .eq('role', 'admin_staff')
        .order('full_name');

      const adminIds = (admins || []).map(a => a.id);
      let permsMap = {};
      if (adminIds.length > 0) {
        const { data: permissions } = await supabase
          .from('admin_permissions')
          .select('user_id, can_add_hires, can_edit_hires, can_delete_hires, can_manage_templates, can_view_onboarding, can_view_clients, can_view_wellsky_caregivers, can_view_incident_reports, can_view_faq, can_create_faq_articles, can_edit_faq_articles, can_suggest_faq_edits, can_delete_faq_articles, can_pin_faq_articles, can_comment_faq_articles, can_view_jotform_submissions, can_view_todos, can_view_all_todos, can_delete_todos, can_view_staffing_tab, can_view_field_supervisors_tab, can_view_meetings, can_create_meetings, can_manage_all_meetings, can_view_carepro, can_view_phone_screen, can_receive_phone_popups, can_view_care_consultation, can_view_consultations, can_edit_consultations, can_delete_consultations, can_finalize_consultations, can_view_consultation_archive, can_view_care_pro_transfer, can_edit_applications, can_delete_applications, can_view_hiring_pipeline, can_view_onboarding_dashboard, can_view_applicants, can_view_orientation_groups, can_view_onboarding_templates, can_view_activated_insights, can_view_admin, can_invite_staff, can_view_support, can_reply_tickets, can_add_internal_notes, can_close_tickets, can_manage_tickets, can_receive_ticket_notifications, notify_categories, can_receive_sms_notifications, sms_notify_categories')
          .in('user_id', adminIds);
        (permissions || []).forEach(p => { permsMap[p.user_id] = p; });
      }
      setAdminStaff((admins || []).map(a => ({ ...a, admin_permissions: permsMap[a.id] ? [permsMap[a.id]] : [] })));
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteAdmin = async (e) => {
    e.preventDefault();
    setInviteError('');
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-admin-staff', {
        body: { fullName: newAdminForm.fullName, email: newAdminForm.email, phone: newAdminForm.phone || null, department: newAdminForm.department || null, permissions: newAdminForm.permissions }
      });
      if (error) {
        let msg = error.message || 'Failed to invite admin staff';
        try {
          const body = await error.context?.json?.();
          if (body?.error) msg = Array.isArray(body.details) && body.details.length > 0 ? `${body.error}: ${body.details.join('; ')}` : body.error;
        } catch (_) {}
        throw new Error(msg);
      }
      if (!data?.success) throw new Error(data?.error || 'Failed to invite admin');
      showToast('Admin staff invited successfully!');
      setShowInviteModal(false);
      setNewAdminForm({ fullName: '', email: '', phone: '', department: '', permissions: { can_add_hires: false, can_edit_hires: false, can_delete_hires: false, can_manage_templates: false, can_view_onboarding: false, can_view_clients: false, can_view_wellsky_caregivers: false, can_view_incident_reports: false, can_view_faq: false, can_create_faq_articles: false, can_edit_faq_articles: false, can_suggest_faq_edits: false, can_delete_faq_articles: false, can_pin_faq_articles: false, can_comment_faq_articles: false, can_view_jotform_submissions: false, can_view_todos: false, can_view_all_todos: false, can_delete_todos: false, can_view_staffing_tab: false, can_view_field_supervisors_tab: false, can_view_meetings: false, can_create_meetings: false, can_manage_all_meetings: false, can_view_carepro: false, can_view_phone_screen: false, can_receive_phone_popups: false, can_view_care_consultation: false, can_view_consultations: false, can_edit_consultations: false, can_delete_consultations: false, can_finalize_consultations: false, can_view_consultation_archive: false, can_view_care_pro_transfer: false, can_edit_applications: false, can_delete_applications: false, can_view_hiring_pipeline: false, can_view_onboarding_dashboard: false, can_view_applicants: false, can_view_orientation_groups: false, can_view_onboarding_templates: false, can_view_activated_insights: false, can_view_admin: false, can_invite_staff: false, can_view_support: false, can_reply_tickets: false, can_add_internal_notes: false, can_close_tickets: false, can_manage_tickets: false, can_receive_ticket_notifications: false, notify_categories: [], can_receive_sms_notifications: false, sms_notify_categories: [] } });
      loadAll();
    } catch (err) {
      setInviteError(err.message || 'Failed to invite admin staff');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePermissions = async (adminId, newPermissions, newPhone, newDepartment) => {
    setIsSaving(true);
    try {
      if (newPhone !== undefined) {
        const { error: phoneError } = await supabase.from('profiles').update({ phone: newPhone.trim() || null, department: newDepartment || null }).eq('id', adminId);
        if (phoneError) throw phoneError;
      }
      const { error } = await supabase.from('admin_permissions').update({
        can_add_hires: newPermissions.can_add_hires, can_edit_hires: newPermissions.can_edit_hires, can_delete_hires: newPermissions.can_delete_hires,
        can_manage_templates: newPermissions.can_manage_templates, can_view_onboarding: newPermissions.can_view_onboarding,
        can_view_clients: newPermissions.can_view_clients, can_view_wellsky_caregivers: newPermissions.can_view_wellsky_caregivers, can_view_incident_reports: newPermissions.can_view_incident_reports, can_view_faq: newPermissions.can_view_faq, can_create_faq_articles: newPermissions.can_create_faq_articles, can_edit_faq_articles: newPermissions.can_edit_faq_articles, can_suggest_faq_edits: newPermissions.can_suggest_faq_edits, can_delete_faq_articles: newPermissions.can_delete_faq_articles, can_pin_faq_articles: newPermissions.can_pin_faq_articles, can_comment_faq_articles: newPermissions.can_comment_faq_articles, can_view_jotform_submissions: newPermissions.can_view_jotform_submissions, can_view_todos: newPermissions.can_view_todos, can_view_all_todos: newPermissions.can_view_all_todos, can_delete_todos: newPermissions.can_delete_todos, can_view_staffing_tab: newPermissions.can_view_staffing_tab, can_view_field_supervisors_tab: newPermissions.can_view_field_supervisors_tab, can_view_meetings: newPermissions.can_view_meetings, can_create_meetings: newPermissions.can_create_meetings, can_manage_all_meetings: newPermissions.can_manage_all_meetings, can_view_carepro: newPermissions.can_view_carepro,
        can_view_phone_screen: newPermissions.can_view_phone_screen, can_receive_phone_popups: newPermissions.can_receive_phone_popups,
        can_view_care_consultation: newPermissions.can_view_care_consultation, can_view_consultations: newPermissions.can_view_consultations,
        can_edit_consultations: newPermissions.can_edit_consultations, can_delete_consultations: newPermissions.can_delete_consultations, can_finalize_consultations: newPermissions.can_finalize_consultations, can_view_consultation_archive: newPermissions.can_view_consultation_archive,
        can_view_care_pro_transfer: newPermissions.can_view_care_pro_transfer, can_edit_applications: newPermissions.can_edit_applications, can_delete_applications: newPermissions.can_delete_applications,
        can_view_hiring_pipeline: newPermissions.can_view_hiring_pipeline, can_view_onboarding_dashboard: newPermissions.can_view_onboarding_dashboard, can_view_applicants: newPermissions.can_view_applicants, can_view_orientation_groups: newPermissions.can_view_orientation_groups, can_view_onboarding_templates: newPermissions.can_view_onboarding_templates, can_view_activated_insights: newPermissions.can_view_activated_insights,
        can_view_admin: newPermissions.can_view_admin, can_invite_staff: newPermissions.can_invite_staff,
        can_view_support: newPermissions.can_view_support, can_reply_tickets: newPermissions.can_reply_tickets,
        can_add_internal_notes: newPermissions.can_add_internal_notes, can_close_tickets: newPermissions.can_close_tickets,
        can_manage_tickets: newPermissions.can_manage_tickets,
        can_receive_ticket_notifications: newPermissions.can_receive_ticket_notifications, notify_categories: newPermissions.notify_categories || [],
        can_receive_sms_notifications: newPermissions.can_receive_sms_notifications, sms_notify_categories: newPermissions.sms_notify_categories || [],
        updated_at: new Date().toISOString()
      }).eq('user_id', adminId);
      if (error) throw error;
      showToast('Permissions updated successfully!');
      setShowEditModal(false);
      setEditingAdmin(null);
      loadAll();
    } catch (err) {
      showToast('Failed to update permissions');
    } finally {
      setIsSaving(false);
    }
  };

  const updateAdminStatus = async (userId, status) => {
    const { error } = await supabase.from('profiles').update({ status }).eq('id', userId);
    if (error) { showToast('Failed to update status'); return; }
    showToast(status === 'active' ? 'Staff reactivated' : 'Staff deactivated');
    loadAll();
  };

  const getPermissionBadges = (admin) => {
    const perms = admin.admin_permissions?.[0];
    if (!perms) return [];
    const badges = [];
    if (perms.can_view_onboarding) badges.push({ label: 'Onboarding', color: 'bg-green-100 text-green-700' });
    if (perms.can_view_clients) badges.push({ label: 'Clients', color: 'bg-blue-100 text-blue-700' });
    if (perms.can_view_wellsky_caregivers) badges.push({ label: 'Care Pros', color: 'bg-blue-100 text-blue-700' });
    if (perms.can_view_incident_reports) badges.push({ label: 'Action Templates', color: 'bg-amber-100 text-amber-700' });
    if (perms.can_view_faq) badges.push({ label: 'FAQ', color: 'bg-teal-100 text-teal-700' });
    if (perms.can_view_jotform_submissions) badges.push({ label: 'Form Submissions', color: 'bg-orange-100 text-orange-700' });
    if (perms.can_view_todos) badges.push({ label: perms.can_view_all_todos ? 'Task Board (All)' : 'Task Board', color: 'bg-indigo-100 text-indigo-700' });
    if (perms.can_view_meetings) badges.push({ label: 'Meeting Agenda', color: 'bg-purple-100 text-purple-700' });
    if (perms.can_view_phone_screen) badges.push({ label: 'Phone Screen', color: 'bg-indigo-100 text-indigo-700' });
    if (perms.can_view_care_consultation) badges.push({ label: 'Consult', color: 'bg-amber-100 text-amber-700' });
    if (perms.can_view_care_pro_transfer) badges.push({ label: 'CP Transfer', color: 'bg-teal-100 text-teal-700' });
    if (perms.can_view_hiring_pipeline) badges.push({ label: 'Hiring Pipeline', color: 'bg-rose-100 text-rose-700' });
    if (perms.can_view_support) badges.push({ label: 'Support', color: 'bg-cyan-100 text-cyan-700' });
    if (perms.can_receive_sms_notifications) badges.push({ label: 'SMS', color: 'bg-emerald-100 text-emerald-700' });
    if (perms.can_view_admin) badges.push({ label: 'Admin', color: 'bg-red-100 text-red-700' });
    if (perms.can_invite_staff) badges.push({ label: 'Invite Staff', color: 'bg-red-100 text-red-700' });
    return badges;
  };

  const fmtDate = (ts) => ts ? new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Never';
  const subTabCls = (active) => `px-3 py-1.5 text-xs font-medium rounded-lg transition ${active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`;

  // Field Supervisors kept out of the Admin Staff list entirely -- their
  // own tab below is where they're managed, so they don't show up twice.
  const filteredAdmins = adminStaff.filter(a => {
    if (a.department === 'Field Supervisors') return false;
    const s = a.status || 'active';
    if (adminStatusTab === 'active') return s === 'active';
    if (adminStatusTab === 'deactivated') return s === 'deactivated';
    return false;
  });

  const filteredFieldSupervisors = adminStaff.filter(a => {
    if (a.department !== 'Field Supervisors') return false;
    const s = a.status || 'active';
    if (fieldSupervisorStatusTab === 'active') return s === 'active';
    if (fieldSupervisorStatusTab === 'deactivated') return s === 'deactivated';
    return false;
  });

  const openAddFieldSupervisor = () => {
    setNewAdminForm(f => ({ ...f, department: 'Field Supervisors' }));
    setShowInviteModal(true);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Admin Management</h1>
        <p className="text-sm text-gray-600">Manage staff and field supervisor access</p>
      </div>

      {/* Main tabs */}
      <div className="flex rounded-xl bg-gray-100 p-1 mb-6 w-fit">
        <button onClick={() => setMainTab('admin_staff')} className={`px-5 py-2 text-sm font-medium rounded-lg transition ${mainTab === 'admin_staff' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>Admin Staff</button>
        <button onClick={() => setMainTab('field_supervisors')} className={`px-5 py-2 text-sm font-medium rounded-lg transition ${mainTab === 'field_supervisors' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>Field Supervisors</button>
        {profile?.role === 'owner' && (
          <button onClick={() => setMainTab('audit_log')} className={`px-5 py-2 text-sm font-medium rounded-lg transition ${mainTab === 'audit_log' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>Audit Log</button>
        )}
      </div>

      {mainTab === 'audit_log' && profile?.role === 'owner' && <AuditLogView />}

      {mainTab === 'admin_staff' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="p-4 md:p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              <button onClick={() => setAdminStatusTab('active')} className={subTabCls(adminStatusTab === 'active')}>Active</button>
              <button onClick={() => setAdminStatusTab('deactivated')} className={subTabCls(adminStatusTab === 'deactivated')}>Deactivated</button>
            </div>
            {canInviteStaff && (
              <button onClick={() => setShowInviteModal(true)} className="bg-[#2c4f26] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#1f3a1c] transition flex items-center justify-center gap-2 w-full sm:w-auto text-sm">
                <Plus size={16} /> Invite Staff
              </button>
            )}
          </div>
          {loading ? (
            <div className="p-12 text-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" /></div>
          ) : filteredAdmins.length === 0 ? (
            <div className="p-12 text-center text-gray-500"><Users size={40} className="mx-auto mb-3 opacity-30" /><p className="text-sm">No {adminStatusTab} admin staff</p></div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredAdmins.map(admin => (
                <div key={admin.id} className="p-4 md:p-6 hover:bg-gray-50 transition">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#2c4f26] rounded-full flex items-center justify-center text-white font-semibold shrink-0">
                        {admin.full_name?.charAt(0)?.toUpperCase() || 'A'}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900 text-sm">{admin.full_name}</p>
                          {admin.department && <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-orange-100 text-orange-700">{admin.department}</span>}
                        </div>
                        <p className="text-xs text-gray-600">{admin.email}</p>
                        {admin.phone && <p className="text-xs text-gray-500">{admin.phone}</p>}
                        <p className="text-xs text-gray-400 mt-0.5">Last login: {fmtDate(admin.last_sign_in_at)}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {getPermissionBadges(admin).map((badge, idx) => (
                            <span key={idx} className={`px-2 py-0.5 text-xs font-semibold rounded-full ${badge.color}`}>{badge.label}</span>
                          ))}
                          {getPermissionBadges(admin).length === 0 && <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">No permissions</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => { setEditingAdmin(admin); setShowEditModal(true); }} className="px-3 py-1.5 text-xs font-medium text-[#2c4f26] hover:bg-[#f0f5f0] rounded-lg transition flex items-center gap-1">
                        <Pencil size={12} /> Edit
                      </button>
                      {(admin.status || 'active') === 'active' ? (
                        <button onClick={() => updateAdminStatus(admin.id, 'deactivated')} className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition">Deactivate</button>
                      ) : (
                        <button onClick={() => updateAdminStatus(admin.id, 'active')} className="px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50 rounded-lg transition">Reactivate</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {mainTab === 'field_supervisors' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="p-4 md:p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              <button onClick={() => setFieldSupervisorStatusTab('active')} className={subTabCls(fieldSupervisorStatusTab === 'active')}>Active</button>
              <button onClick={() => setFieldSupervisorStatusTab('deactivated')} className={subTabCls(fieldSupervisorStatusTab === 'deactivated')}>Deactivated</button>
            </div>
            {canInviteStaff && (
              <button onClick={openAddFieldSupervisor} className="bg-[#2c4f26] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#1f3a1c] transition flex items-center justify-center gap-2 w-full sm:w-auto text-sm">
                <Plus size={16} /> Add Field Supervisor
              </button>
            )}
          </div>
          {loading ? (
            <div className="p-12 text-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" /></div>
          ) : filteredFieldSupervisors.length === 0 ? (
            <div className="p-12 text-center text-gray-500"><Users size={40} className="mx-auto mb-3 opacity-30" /><p className="text-sm">No {fieldSupervisorStatusTab} field supervisors</p></div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredFieldSupervisors.map(admin => (
                <div key={admin.id} className="p-4 md:p-6 hover:bg-gray-50 transition">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#2c4f26] rounded-full flex items-center justify-center text-white font-semibold shrink-0">
                        {admin.full_name?.charAt(0)?.toUpperCase() || 'F'}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 text-sm">{admin.full_name}</p>
                        <p className="text-xs text-gray-600">{admin.email}</p>
                        {admin.phone && <p className="text-xs text-gray-500">{admin.phone}</p>}
                        <p className="text-xs text-gray-400 mt-0.5">Last login: {fmtDate(admin.last_sign_in_at)}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {getPermissionBadges(admin).map((badge, idx) => (
                            <span key={idx} className={`px-2 py-0.5 text-xs font-semibold rounded-full ${badge.color}`}>{badge.label}</span>
                          ))}
                          {getPermissionBadges(admin).length === 0 && <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">No permissions</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => { setEditingAdmin(admin); setShowEditModal(true); }} className="px-3 py-1.5 text-xs font-medium text-[#2c4f26] hover:bg-[#f0f5f0] rounded-lg transition flex items-center gap-1">
                        <Pencil size={12} /> Edit
                      </button>
                      {(admin.status || 'active') === 'active' ? (
                        <button onClick={() => updateAdminStatus(admin.id, 'deactivated')} className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition">Deactivate</button>
                      ) : (
                        <button onClick={() => updateAdminStatus(admin.id, 'active')} className="px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50 rounded-lg transition">Reactivate</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <InviteAdminModal show={showInviteModal} onClose={() => { setShowInviteModal(false); setInviteError(''); }} onSubmit={handleInviteAdmin} formData={newAdminForm} setFormData={setNewAdminForm} error={inviteError} isSubmitting={isSubmitting} />
      <EditPermissionsModal show={showEditModal} admin={editingAdmin} onClose={() => { setShowEditModal(false); setEditingAdmin(null); }} onSave={handleUpdatePermissions} isSaving={isSaving} />
    </div>
  );
};

// ── TICKETING SYSTEM ─────────────────────────────────────────────────────────

const TICKET_CATEGORIES = ['scheduling', 'payroll', 'client_issues', 'hr', 'other'];
const TICKET_CATEGORY_LABELS = { scheduling: 'Scheduling', payroll: 'Payroll', client_issues: 'Client Issues', hr: 'HR', other: 'Other' };

// Fine-tuned SMS categories under can_receive_sms_notifications (the master
// switch) -- same shape as notify_categories/TICKET_CATEGORIES above, just
// for text messages instead of ticket-category emails. Grouped by system
// (Task Board vs Tickets) in the picker UI since that's how staff actually
// think about them.
const SMS_CATEGORY_GROUPS = [
  { label: 'Task Board', categories: ['task_assigned', 'task_watcher_added', 'task_updated', 'task_comment', 'task_completed', 'task_assignee_completed', 'task_reminder'] },
  { label: 'Support Tickets', categories: ['ticket_assigned', 'ticket_new', 'ticket_reply'] },
];
const SMS_CATEGORY_LABELS = {
  task_assigned: 'Assigned a Task',
  task_watcher_added: 'Added to a Task (Watching)',
  task_updated: 'Task Updated',
  task_comment: 'Task Comment',
  task_completed: 'My Task Completed',
  task_assignee_completed: 'Group Task Progress',
  task_reminder: 'Task Reminder',
  ticket_assigned: 'Ticket Assigned to Me',
  ticket_new: 'New Ticket',
  ticket_reply: 'Customer Reply',
};
const TICKET_STATUSES = { open: 'Open', waiting_on_customer: 'Waiting on Customer', waiting_on_staff: 'Waiting on Staff', resolved: 'Resolved', closed: 'Closed', auto_closed: 'Auto Closed: No Response 48 hours' };
// Statuses that are "closed-family" for filtering/display purposes, but
// never selectable from a manual status dropdown -- auto_closed is set only
// by ticket-maintenance's 48h sweep, never chosen by a person.
const TICKET_CLOSED_STATUSES = new Set(['closed', 'auto_closed']);
const TICKET_PRIORITIES = { low: 'Low', normal: 'Normal', high: 'High', urgent: 'Urgent' };

const statusColors = {
  open:                'bg-blue-100 text-blue-700',
  waiting_on_customer: 'bg-amber-100 text-amber-700',
  waiting_on_staff:    'bg-purple-100 text-purple-700',
  resolved:            'bg-green-100 text-green-700',
  closed:              'bg-gray-100 text-gray-600',
  auto_closed:         'bg-slate-200 text-slate-700',
};
const priorityColors = {
  low:    'bg-gray-100 text-gray-600',
  normal: 'bg-blue-100 text-blue-700',
  high:   'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

// ── Public: Privacy Policy / Terms & Conditions ──────────────────────────────
// Required for Twilio SMS campaign registration (A2P 10DLC / toll-free
// verification) -- must be publicly reachable with no login, which is why
// these are plain static routes (/privacy, /terms) rather than part of the
// authenticated app. Content here is standard boilerplate for an internal
// staff-notification SMS program, not a customer marketing list; have
// someone who owns compliance for the business review before relying on it.
const LEGAL_BRAND_NAME = 'Home Instead St. Clair (Lewis & Leigh, Inc.)';
const LEGAL_LAST_UPDATED = 'October 23, 2026';

const LegalPageShell = ({ title, children }) => (
  <div className="min-h-screen bg-[#f5f7f5]">
    <div className="bg-[#2c4f26] sticky top-0 z-40">
      <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-4">
        <img src={HiLargeLogo} alt="Home Instead" className="h-12 w-auto object-contain brightness-0 invert" />
        <div className="w-px h-8 bg-white/30" />
        <span className="text-xl font-bold text-white tracking-tight">{title}</span>
      </div>
    </div>
    <div className="max-w-3xl mx-auto px-5 py-10">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 space-y-5 text-sm text-gray-700 leading-relaxed">
        <p className="text-xs text-gray-400">Last updated: {LEGAL_LAST_UPDATED}</p>
        {children}
      </div>
    </div>
  </div>
);

const PrivacyPolicyPage = () => (
  <LegalPageShell title="Privacy Policy">
    <h1 className="text-2xl font-bold text-gray-900">Privacy Policy</h1>
    <p>
      This Privacy Policy describes how {LEGAL_BRAND_NAME} ("we," "us," or "our") collects, uses, and
      protects information through the Home Instead Portal, the internal system we use to manage staff
      scheduling, tasks, and compliance notifications, including by SMS text message.
    </p>
    <div>
      <h2 className="font-semibold text-gray-900 mb-1">Information We Collect</h2>
      <p>We collect the information staff members provide directly, including name, email address, and phone number, as well as information generated through use of the Portal, such as tasks assigned or completed and notification preferences.</p>
    </div>
    <div>
      <h2 className="font-semibold text-gray-900 mb-1">How We Use Information</h2>
      <p>We use this information to operate the Portal and to send operational notifications relevant to a staff member's work, including task assignments, reminders, compliance deadlines, and schedule updates, by email and, for staff who opt in, by SMS text message.</p>
    </div>
    <div>
      <h2 className="font-semibold text-gray-900 mb-1">SMS Opt-In Data</h2>
      <p>
        We do not sell or share your SMS opt-in data or personal information with third parties for
        marketing purposes. Consent to receive text notifications is collected in person, and phone
        numbers and SMS consent collected for text notifications are used solely to deliver the
        operational notifications described above and are not shared with any third party for their
        own marketing purposes.
      </p>
    </div>
    <div>
      <h2 className="font-semibold text-gray-900 mb-1">Data Retention &amp; Security</h2>
      <p>Information is retained for as long as needed to operate the Portal and support the employment relationship, and is protected using industry-standard access controls. Access to staff information within the Portal is limited to authorized personnel based on their role.</p>
    </div>
    <div>
      <h2 className="font-semibold text-gray-900 mb-1">Your Choices</h2>
      <p>Staff may opt out of SMS notifications at any time by replying STOP to any text message, or by notifying their manager. Opting out of SMS does not affect email notifications or employment status.</p>
    </div>
    <div>
      <h2 className="font-semibold text-gray-900 mb-1">Contact Us</h2>
      <p>Questions about this Privacy Policy can be submitted through the Portal's <a href="/support" className="text-[#2c4f26] underline">Support</a> page.</p>
    </div>
  </LegalPageShell>
);

const TermsPage = () => (
  <LegalPageShell title="Terms & Conditions">
    <h1 className="text-2xl font-bold text-gray-900">Terms &amp; Conditions</h1>
    <p>
      These Terms &amp; Conditions govern use of the Home Instead Portal, operated by {LEGAL_BRAND_NAME},
      by its staff members. By using the Portal, you agree to these terms.
    </p>
    <div>
      <h2 className="font-semibold text-gray-900 mb-1">Use of the Portal</h2>
      <p>The Portal is an internal tool for authorized staff members only, used to manage onboarding, scheduling, tasks, and related operational workflows. Accounts are issued by {LEGAL_BRAND_NAME} and may be deactivated at any time.</p>
    </div>
    <div>
      <h2 className="font-semibold text-gray-900 mb-1">SMS Terms</h2>
      <p>By providing your consent in person during hiring or onboarding, you agree to receive operational SMS messages from {LEGAL_BRAND_NAME} related to your work, including task assignments, reminders, and compliance notifications.</p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Message frequency varies based on your role and assigned tasks.</li>
        <li>Message and data rates may apply.</li>
        <li>Reply <strong>STOP</strong> at any time to unsubscribe from SMS notifications.</li>
        <li>Reply <strong>HELP</strong> for help, or contact us through the Portal's <a href="/support" className="text-[#2c4f26] underline">Support</a> page.</li>
        <li>Supported carriers are not liable for delayed or undelivered messages.</li>
        <li>See our <a href="/privacy" className="text-[#2c4f26] underline">Privacy Policy</a> for how your information is used.</li>
      </ul>
    </div>
    <div>
      <h2 className="font-semibold text-gray-900 mb-1">Changes</h2>
      <p>{LEGAL_BRAND_NAME} may update these Terms &amp; Conditions from time to time; continued use of the Portal after a change constitutes acceptance of the updated terms.</p>
    </div>
  </LegalPageShell>
);

// ── Public: Support Ticket Creation Page ─────────────────────────────────────

const SupportPage = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', category: 'other', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { ticketNumber, token }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) return setError('Name is required.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setError('Valid email is required.');
    if (!form.subject.trim()) return setError('Subject is required.');
    if (!form.message.trim()) return setError('Message is required.');
    setSubmitting(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/ticket-create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ customerName: form.name, customerEmail: form.email, subject: form.subject, category: form.category, message: form.message }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Submission failed.');
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const ticketUrl = result ? `/ticket/${result.ticketNumber}?token=${result.token}` : '';

  return (
    <div className="min-h-screen bg-[#f5f7f5]">
      {/* Header — matches CustomerTicketView */}
      <div className="bg-[#2c4f26] sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center gap-4">
          <img src={HiLargeLogo} alt="Home Instead" className="h-12 w-auto object-contain brightness-0 invert" />
          <div className="w-px h-8 bg-white/30" />
          <span className="text-xl font-bold text-white tracking-tight">Manassas/Leesburg - Support Center</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {result ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-[#2c4f26]" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ticket Submitted</h2>
            <p className="text-gray-600 mb-6">We've received your request and will respond as soon as possible. A confirmation has been sent to <strong>{form.email}</strong>.</p>
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
              <p className="text-xs text-gray-500 mb-1">Your ticket number</p>
              <p className="text-2xl font-bold text-[#2c4f26]">#{result.ticketNumber}</p>
            </div>
            <a href={ticketUrl} className="inline-block bg-[#2c4f26] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#1f3a1c] transition">View Your Ticket</a>
            <p className="text-xs text-gray-500 mt-4">Bookmark or save the link above to return to your ticket at any time.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare size={20} className="text-[#2c4f26]" />
                <h1 className="text-xl font-bold text-gray-900">Submit a Support Request</h1>
              </div>
              <p className="text-sm text-gray-600">We'll get back to you as soon as possible.</p>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
                  <input className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jane Smith" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                  <input type="email" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@example.com" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Subject <span className="text-red-500">*</span></label>
                <input className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Brief description of your issue" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Category</label>
                <select className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition bg-white" value={form.category} onChange={e => set('category', e.target.value)}>
                  {TICKET_CATEGORIES.map(c => <option key={c} value={c}>{TICKET_CATEGORY_LABELS[c]}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message <span className="text-red-500">*</span></label>
                <textarea rows={5} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition resize-none" value={form.message} onChange={e => set('message', e.target.value)} placeholder="Please describe your issue in as much detail as possible..." />
              </div>
              {error && <p className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-xl">{error}</p>}
              <button type="submit" disabled={submitting} className="w-full bg-[#2c4f26] text-white py-3.5 rounded-xl font-semibold hover:bg-[#1f3a1c] transition shadow-lg shadow-[#2c4f26]/30 disabled:opacity-50 flex items-center justify-center gap-2">
                {submitting ? 'Submitting…' : <><Send size={16} /> Submit Request</>}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

// Lets a screenshot on the clipboard (e.g. a Snipping Tool/Cmd+Shift+4
// capture copied, not saved to a file) attach directly from a paste into
// the reply textarea, same as pasting an image into a chat app -- the
// clipboard already hands it over as a real File, so it slots straight
// into the same files state/upload path as a picked file.
const handlePasteImages = (e, setFilesFn) => {
  const items = Array.from(e.clipboardData?.items || []);
  const imageFiles = items.filter(i => i.type.startsWith('image/')).map(i => i.getAsFile()).filter(Boolean);
  if (imageFiles.length === 0) return;
  const renamed = imageFiles.map((f, idx) => {
    const ext = (f.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
    return new File([f], `pasted-screenshot-${Date.now()}-${idx}.${ext}`, { type: f.type });
  });
  setFilesFn(prev => [...prev, ...renamed].slice(0, 5));
};

// ── Public: Customer Ticket View ─────────────────────────────────────────────

const CustomerTicketView = ({ ticketNumber, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [files, setFiles] = useState([]);
  // Local blob URLs for previewing a not-yet-sent file -- entirely
  // client-side, no upload/signing needed since it's just showing the
  // user their own pending attachment back to them.
  const filePreviews = React.useMemo(() => files.map(f => ({ file: f, url: f.type.startsWith('image/') ? URL.createObjectURL(f) : null })), [files]);
  useEffect(() => () => { filePreviews.forEach(p => p.url && URL.revokeObjectURL(p.url)); }, [filePreviews]);
  const [replying, setReplying] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [replySent, setReplySent] = useState(false);
  const [closing, setClosing] = useState(false);

  const [thumbUrls, setThumbUrls] = useState({});

  const getAttachmentUrl = async (attachmentId) => {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/ticket-attachment-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
      body: JSON.stringify({ ticketNumber: Number(ticketNumber), token, attachmentId }),
    });
    const result = await res.json();
    if (!result.success) throw new Error(result.error);
    return result.url;
  };

  const downloadAttachment = async (att) => {
    try {
      window.open(await getAttachmentUrl(att.id), '_blank', 'noopener');
    } catch (err) {
      alert('Failed to open attachment.');
    }
  };

  const loadTicket = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: result, error: rpcErr } = await supabase.rpc('get_customer_ticket', { p_number: Number(ticketNumber), p_token: token });
      if (rpcErr || !result) throw new Error('Ticket not found or invalid link.');
      setData(result);

      // Pre-sign image attachments across every message so they can render
      // as real inline thumbnails -- non-image attachments stay as a
      // filename chip, signed on click instead.
      const images = (result.messages || []).flatMap(m => (m.attachments || []).filter(a => (a.content_type || '').startsWith('image/')));
      if (images.length > 0) {
        const entries = await Promise.all(images.map(async a => {
          try { return [a.id, await getAttachmentUrl(a.id)]; } catch { return [a.id, null]; }
        }));
        setThumbUrls(Object.fromEntries(entries.filter(([, url]) => url)));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTicket(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setReplying(true);
    setReplyError('');
    try {
      const formData = new FormData();
      formData.append('ticketNumber', String(Number(ticketNumber)));
      formData.append('senderType', 'customer');
      formData.append('message', reply.trim());
      formData.append('token', token);
      files.forEach(f => formData.append('files', f));
      const res = await fetch(`${SUPABASE_URL}/functions/v1/ticket-reply`, {
        method: 'POST',
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: formData,
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Failed to send reply.');
      setReply('');
      setFiles([]);
      setReplySent(true);
      await loadTicket();
      setTimeout(() => setReplySent(false), 3000);
    } catch (err) {
      setReplyError(err.message);
    } finally {
      setReplying(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center"><div className="w-8 h-8 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin mx-auto mb-3" /><p className="text-gray-600 text-sm">Loading ticket…</p></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><X size={28} className="text-red-500" /></div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Ticket Not Found</h2>
        <p className="text-gray-600 text-sm">{error}</p>
        <p className="text-gray-500 text-xs mt-4">Please check the link in your confirmation email.</p>
      </div>
    </div>
  );

  const handleClose = async () => {
    if (!window.confirm('Mark this ticket as resolved? Staff will still review it before fully closing.')) return;
    setClosing(true);
    try {
      const { data: ok, error } = await supabase.rpc('customer_resolve_ticket', { p_number: Number(ticketNumber), p_token: token });
      if (error || !ok) throw new Error('Could not close ticket. Please try again.');
      await loadTicket();
    } catch (err) {
      alert(err.message);
    } finally {
      setClosing(false);
    }
  };

  const ticket = data;
  const messages = data.messages || [];
  const isClosed = TICKET_CLOSED_STATUSES.has(ticket.status) || ticket.status === 'resolved';
  const canClose = !isClosed;

  return (
    <div className="min-h-screen bg-[#f5f7f5]">
      {/* Header */}
      <div className="bg-[#2c4f26] sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-4">
          <img src={HiLargeLogo} alt="Home Instead" className="h-12 w-auto object-contain brightness-0 invert" />
          <div className="w-px h-8 bg-white/30" />
          <span className="text-xl font-bold text-white tracking-tight">Manassas/Leesburg  - Support Center</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-5">
        {/* Ticket info card */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="bg-[#2c4f26]/5 border-b border-[#2c4f26]/10 px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
            <span className="text-sm font-semibold text-[#2c4f26] tracking-wide">Ticket #{ticket.ticket_number}</span>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusColors[ticket.status] || 'bg-gray-100 text-gray-600'}`}>{TICKET_STATUSES[ticket.status] || ticket.status}</span>
              {canClose && (
                <button onClick={handleClose} disabled={closing} className="text-xs font-semibold px-4 py-1.5 rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 shadow-sm">
                  {closing ? 'Closing…' : 'Close Ticket'}
                </button>
              )}
            </div>
          </div>
          <div className="px-6 py-5">
            <h1 className="text-xl font-bold text-gray-900 mb-4">{ticket.subject}</h1>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Category</p>
                <p className="text-sm font-medium text-gray-700">{TICKET_CATEGORY_LABELS[ticket.category] || ticket.category}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Opened</p>
                <p className="text-sm font-medium text-gray-700">{new Date(ticket.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Last Updated</p>
                <p className="text-sm font-medium text-gray-700">{new Date(ticket.updated_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Resolved banner */}
        {ticket.status === 'resolved' && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-start gap-3">
            <CheckCircle size={18} className="text-green-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-800">You've marked this ticket as resolved.</p>
              <p className="text-xs text-green-700 mt-1">Our team will review and finalize it shortly. You can still add a reply below if you have further questions.</p>
            </div>
          </div>
        )}

        {/* Conversation thread — each message is a self-contained card */}
        <div className="space-y-4">
          {messages.map((msg) => {
            const isCustomer = msg.sender_type === 'customer';
            return (
              <div key={msg.id} className={`rounded-xl border overflow-hidden shadow-sm ${isCustomer ? 'bg-white border-gray-200' : 'bg-[#f8fbf8] border-[#cde0cd]'}`}>
                {/* Card header */}
                <div className={`flex items-center gap-3 px-5 py-3 border-b ${isCustomer ? 'border-gray-100' : 'border-[#cde0cd]'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isCustomer ? 'bg-gray-200 text-gray-700' : 'bg-[#2c4f26] text-white'}`}>
                    {msg.sender_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">{msg.sender_name}</span>
                      {!isCustomer && <span className="text-xs font-medium text-[#2c4f26] bg-[#2c4f26]/10 px-2 py-0.5 rounded-full">Support</span>}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 shrink-0">
                    {new Date(msg.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
                {/* Card body */}
                <div className="px-5 py-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{msg.body}</p>
                  {(msg.attachments || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {msg.attachments.map(att => (
                        thumbUrls[att.id] ? (
                          <img key={att.id} src={thumbUrls[att.id]} alt={att.file_name} title={att.file_name} onClick={() => downloadAttachment(att)} className="w-28 h-28 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition" />
                        ) : (
                          <button key={att.id} type="button" onClick={() => downloadAttachment(att)} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5 rounded-lg transition">
                            <FileText size={12} /> {att.file_name}
                          </button>
                        )
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Reply / Closed */}
        {TICKET_CLOSED_STATUSES.has(ticket.status) ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm">
            <p className="text-sm text-gray-600 mb-3">
              {ticket.status === 'auto_closed'
                ? <>This ticket was <strong>automatically closed</strong> after 48 hours with no response.</>
                : <>This ticket has been <strong>closed</strong>.</>}
            </p>
            <a href="/support" className="inline-block text-sm font-semibold text-[#2c4f26] border border-[#2c4f26] px-4 py-2 rounded-lg hover:bg-[#f0f5f0] transition">Open New Ticket →</a>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
              <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-xs font-bold shrink-0">
                {ticket.customer_name.charAt(0).toUpperCase()}
              </div>
              <span className="text-sm font-semibold text-gray-900">Your Reply</span>
            </div>
            <div className="px-5 py-4">
              {replySent && (
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 px-4 py-2.5 rounded-lg mb-3">
                  <CheckCircle size={14} /> Reply sent.
                </div>
              )}
              <form onSubmit={handleReply} className="space-y-3">
                <textarea rows={4} className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition resize-y min-h-[6rem] text-sm" value={reply} onChange={e => setReply(e.target.value)} onPaste={e => handlePasteImages(e, setFiles)} placeholder="Write your reply here… (you can paste a screenshot directly)" />
                <div>
                  <label className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 cursor-pointer transition">
                    <Paperclip size={13} /> Attach a file or screenshot
                    <input type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt" className="hidden" onChange={e => {
                      const picked = Array.from(e.target.files || []);
                      setFiles(prev => [...prev, ...picked].slice(0, 5));
                      e.target.value = '';
                    }} />
                  </label>
                  {filePreviews.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {filePreviews.map(({ file: f, url }, i) => (
                        url ? (
                          <div key={i} className="relative">
                            <img src={url} alt={f.name} title={f.name} className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                            <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 bg-gray-700 text-white rounded-full p-0.5 hover:bg-red-600 transition"><X size={10} /></button>
                          </div>
                        ) : (
                          <span key={i} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-lg h-fit">
                            {f.name}
                            <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="hover:text-red-500 transition"><X size={11} /></button>
                          </span>
                        )
                      ))}
                    </div>
                  )}
                </div>
                {replyError && <p className="text-sm text-red-600">{replyError}</p>}
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <p className="text-xs text-gray-400">Typically responds within 1 business day.</p>
                  <button type="submit" disabled={replying || !reply.trim()} className="bg-[#2c4f26] text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-[#1f3a1c] transition disabled:opacity-50 flex items-center gap-2">
                    {replying ? 'Sending…' : <><Send size={14} /> Send Reply</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Staff: Admin Tickets List ─────────────────────────────────────────────────

const AdminTicketsView = ({ profile, permissions, showToast }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffUsers, setStaffUsers] = useState([]);
  const [filters, setFilters] = useState({ status: '', priority: '', category: '', assigned: '', search: '' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showArchive, setShowArchive] = useState(false);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [newTicketError, setNewTicketError] = useState('');
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const loadTickets = async () => {
    setLoading(true);
    try {
      let q = supabase
        .from('tickets')
        .select('id, ticket_number, customer_name, customer_email, subject, category, status, priority, assigned_to, created_by, created_at, updated_at, last_customer_reply_at, last_staff_reply_at, scheduled_close_at')
        .order('updated_at', { ascending: false });
      const { data, error } = await q;
      if (error) throw error;
      setTickets(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // admin_staff only — owner accounts aren't meant to be an assignable
  // ticket handler, so they're excluded from both the "New Ticket" modal's
  // assign-to and the ticket detail's "Assigned To" (they share this same
  // staffUsers list).
  const loadStaff = async () => {
    const { data } = await supabase.from('profiles').select('id, full_name, email').eq('role', 'admin_staff').eq('status', 'active').order('full_name');
    setStaffUsers(data || []);
  };

  useEffect(() => { loadTickets(); loadStaff(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Opens the ticket a /tickets/<number> link (email, bookmark, shared URL)
  // pointed at, once the first load finishes -- same "don't distinguish
  // missing vs. no-access" reasoning as the Task Board's equivalent.
  const [handledDeepLink, setHandledDeepLink] = useState(false);
  useEffect(() => {
    if (handledDeepLink || loading) return;
    setHandledDeepLink(true);
    const m = window.location.pathname.match(/^\/tickets\/([^/]+)/);
    if (!m) return;
    const num = parseInt(m[1], 10);
    const found = tickets.find(t => t.ticket_number === num);
    if (found) {
      setSelectedTicket(found);
    } else {
      showToast('That ticket was not found, or you do not have access to it', 'error');
      window.history.replaceState({}, '', '/tickets');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, tickets]);

  const openTicket = (ticket) => {
    setSelectedTicket(ticket);
    window.history.pushState({}, '', `/tickets/${ticket.ticket_number}`);
  };
  const closeTicket = () => {
    setSelectedTicket(null);
    window.history.pushState({}, '', '/tickets');
  };

  // No realtime subscription on this list — refresh on refocus so a reply
  // or new ticket from another device/tab shows up without a manual reload.
  useEffect(() => {
    const onFocus = () => { if (!selectedTicket) loadTickets(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicket]);

  const handleCreateTicket = async (form) => {
    setNewTicketError('');
    setCreatingTicket(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/ticket-create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({
          customerName: form.customerName,
          customerEmail: form.customerEmail,
          subject: form.subject,
          category: form.category,
          message: form.message,
          assignedTo: form.assignTo || null,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to create ticket.');

      showToast(`Ticket #${data.ticketNumber} created${form.assignTo ? ' and assigned' : ''}.`);
      setShowNewTicketModal(false);
      await loadTickets();

      const { data: created } = await supabase
        .from('tickets')
        .select('id, ticket_number, customer_name, customer_email, subject, category, status, priority, assigned_to, created_by, created_at, updated_at, last_customer_reply_at, last_staff_reply_at')
        .eq('ticket_number', data.ticketNumber)
        .single();
      if (created) openTicket(created);
    } catch (err) {
      setNewTicketError(err.message);
    } finally {
      setCreatingTicket(false);
    }
  };

  if (selectedTicket) {
    return <AdminTicketDetailView ticket={selectedTicket} staffUsers={staffUsers} profile={profile} permissions={permissions} showToast={showToast} onBack={() => { closeTicket(); loadTickets(); }} />;
  }

  const activeTickets   = tickets.filter(t => !TICKET_CLOSED_STATUSES.has(t.status));
  const archivedTickets = tickets.filter(t => TICKET_CLOSED_STATUSES.has(t.status));
  const pool = showArchive ? archivedTickets : activeTickets;

  const filtered = pool.filter(t => {
    if (!showArchive && filters.status && t.status !== filters.status) return false;
    if (filters.priority && t.priority !== filters.priority) return false;
    if (filters.category && t.category !== filters.category) return false;
    if (filters.assigned && t.assigned_to !== filters.assigned) return false;
    if (filters.search) {
      const s = filters.search.toLowerCase();
      if (!(`${t.ticket_number} ${t.customer_name} ${t.customer_email} ${t.subject}`).toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const hasFilters = Object.values(filters).some(v => v);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{showArchive ? 'Ticket Archive' : 'Support Tickets'}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{showArchive ? `${archivedTickets.length} closed ticket${archivedTickets.length !== 1 ? 's' : ''}` : `${activeTickets.length} active ticket${activeTickets.length !== 1 ? 's' : ''}`}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => { setRefreshing(true); await loadTickets(); setRefreshing(false); }}
            disabled={refreshing}
            title="Refresh the list"
            className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
          >
            <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => { setShowArchive(v => !v); setFilters({ status: '', priority: '', category: '', assigned: '', search: '' }); }}
            className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition flex items-center gap-1.5 ${showArchive ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
          >
            {showArchive ? '← Active Tickets' : `Archive${archivedTickets.length > 0 ? ` (${archivedTickets.length})` : ''}`}
          </button>
          {!showArchive && (
            <button onClick={() => { setNewTicketError(''); setShowNewTicketModal(true); }} className="text-xs text-[#2c4f26] border border-[#2c4f26] rounded-lg px-3 py-1.5 hover:bg-[#f0f5f0] transition font-medium flex items-center gap-1.5">
              <Plus size={13} /> New Ticket
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 space-y-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" placeholder="Search by ticket #, name, email, or subject…" value={filters.search} onChange={e => setFilter('search', e.target.value)} />
        </div>
        <div className="flex gap-2 flex-wrap">
          {!showArchive && (
            <select className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:ring-1 focus:ring-[#2c4f26] transition" value={filters.status} onChange={e => setFilter('status', e.target.value)}>
              <option value="">Status: All</option>
              {Object.entries(TICKET_STATUSES).filter(([v]) => v !== 'closed' && v !== 'auto_closed').map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          )}
          {[
            { key: 'priority', label: 'Priority', options: Object.entries(TICKET_PRIORITIES) },
            { key: 'category', label: 'Category', options: TICKET_CATEGORIES.map(c => [c, TICKET_CATEGORY_LABELS[c]]) },
          ].map(({ key, label, options }) => (
            <select key={key} className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:ring-1 focus:ring-[#2c4f26] transition" value={filters[key]} onChange={e => setFilter(key, e.target.value)}>
              <option value="">{label}: All</option>
              {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ))}
          <select className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:ring-1 focus:ring-[#2c4f26] transition" value={filters.assigned} onChange={e => setFilter('assigned', e.target.value)}>
            <option value="">Assigned: All</option>
            <option value="unassigned">Unassigned</option>
            {staffUsers.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </select>
          {hasFilters && <button onClick={() => setFilters({ status: '', priority: '', category: '', assigned: '', search: '' })} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition">Clear filters</button>}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="w-7 h-7 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <MessageSquare size={36} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 text-sm">{hasFilters ? 'No tickets match your filters.' : 'No support tickets yet.'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['#', 'Customer', 'Subject', 'Category', 'Status', 'Priority', 'Assigned', 'Updated'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-600 whitespace-nowrap">{h}</th>
                  ))}
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(t => {
                  const assignedStaff = staffUsers.find(s => s.id === t.assigned_to);
                  const hasNewCustomerReply = t.last_customer_reply_at && (!t.last_staff_reply_at || new Date(t.last_customer_reply_at) > new Date(t.last_staff_reply_at));
                  return (
                    <tr key={t.id} onClick={() => openTicket(t)} className="hover:bg-gray-50 cursor-pointer transition">
                      <td className="px-4 py-3 text-[#2c4f26] font-semibold whitespace-nowrap">
                        {hasNewCustomerReply && <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-1.5 mb-0.5" />}
                        #{t.ticket_number}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-gray-900 text-xs">{t.customer_name}</div>
                        <div className="text-gray-500 text-xs">{t.customer_email}</div>
                      </td>
                      <td className="px-4 py-3 max-w-[200px]"><span className="truncate block text-gray-700">{t.subject}</span></td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{TICKET_CATEGORY_LABELS[t.category] || t.category}</td>
                      <td className="px-4 py-3 whitespace-nowrap"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[t.status] || ''}`}>{TICKET_STATUSES[t.status] || t.status}</span></td>
                      <td className="px-4 py-3 whitespace-nowrap"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${priorityColors[t.priority] || ''}`}>{TICKET_PRIORITIES[t.priority] || t.priority}</span></td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{assignedStaff ? assignedStaff.full_name : <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{new Date(t.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                      <td className="px-4 py-3"><ChevronRight size={16} className="text-gray-300" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AdminNewTicketModal
        show={showNewTicketModal}
        onClose={() => setShowNewTicketModal(false)}
        onSubmit={handleCreateTicket}
        isSubmitting={creatingTicket}
        error={newTicketError}
        staffUsers={staffUsers}
      />
    </div>
  );
};

// ── Staff: New Ticket Modal ───────────────────────────────────────────────────

const EMPTY_NEW_TICKET_FORM = { customerName: '', customerEmail: '', subject: '', category: 'other', message: '', assignTo: '' };

const AdminNewTicketModal = ({ show, onClose, onSubmit, isSubmitting, error, staffUsers }) => {
  const [form, setForm] = useState(EMPTY_NEW_TICKET_FORM);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!show) { setForm(EMPTY_NEW_TICKET_FORM); setValidationError(''); }
  }, [show]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidationError('');
    if (!form.customerName.trim()) return setValidationError('Customer name is required.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customerEmail.trim())) return setValidationError('A valid customer email is required.');
    if (!form.subject.trim()) return setValidationError('Subject is required.');
    if (!form.message.trim()) return setValidationError('Message is required.');
    onSubmit(form);
  };

  if (!show) return null;

  const inp = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm';
  const lbl = 'block text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900">New Ticket</h2>
            <p className="text-xs text-gray-500 mt-0.5">Create a ticket on a customer's behalf</p>
          </div>
          <button onClick={onClose} type="button" className="text-gray-400 hover:text-gray-600 transition"><X size={22} /></button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden flex-1">
          <div className="overflow-y-auto p-6 space-y-5 flex-1">
            {(error || validationError) && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error || validationError}</div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Full Name <span className="text-red-500">*</span></label>
                <input className={inp} value={form.customerName} onChange={e => set('customerName', e.target.value)} placeholder="Jane Smith" />
              </div>
              <div>
                <label className={lbl}>Email <span className="text-red-500">*</span></label>
                <input type="email" className={inp} value={form.customerEmail} onChange={e => set('customerEmail', e.target.value)} placeholder="jane@example.com" />
              </div>
            </div>
            <div>
              <label className={lbl}>Subject <span className="text-red-500">*</span></label>
              <input className={inp} value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Brief description of the issue" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Category</label>
                <select className={`${inp} bg-white`} value={form.category} onChange={e => set('category', e.target.value)}>
                  {TICKET_CATEGORIES.map(c => <option key={c} value={c}>{TICKET_CATEGORY_LABELS[c]}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Assign To</label>
                <select className={`${inp} bg-white`} value={form.assignTo} onChange={e => set('assignTo', e.target.value)}>
                  <option value="">Unassigned</option>
                  {staffUsers.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={lbl}>Message <span className="text-red-500">*</span></label>
              <textarea rows={5} className={`${inp} resize-none`} value={form.message} onChange={e => set('message', e.target.value)} placeholder="Describe the issue in as much detail as possible..." />
            </div>
          </div>
          <div className="p-6 border-t border-gray-100 flex justify-end gap-3 shrink-0">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="bg-[#2c4f26] text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-[#1f3a1c] transition disabled:opacity-50 flex items-center gap-2">
              {isSubmitting ? 'Creating…' : <><Send size={14} /> Create Ticket</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Staff: Admin Ticket Detail ────────────────────────────────────────────────

// For a datetime-local input: converts to/from the "YYYY-MM-DDTHH:mm" string
// it needs, in the viewer's local time (not UTC, so the picker shows the
// time a staffer actually typed rather than a UTC-shifted equivalent).
const toDatetimeLocalValue = (date) => {
  const d = new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AdminTicketDetailView = ({ ticket: initialTicket, staffUsers, profile, permissions, showToast, onBack }) => {
  const isOwner = profile?.role === 'owner';
  const canReply         = isOwner || permissions?.can_reply_tickets;
  const canInternalNotes = isOwner || permissions?.can_add_internal_notes;
  const canClose         = isOwner || permissions?.can_close_tickets;
  const canManage        = isOwner || permissions?.can_manage_tickets;
  const [ticket, setTicket] = useState(initialTicket);
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [thumbUrls, setThumbUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [files, setFiles] = useState([]);
  // Local blob URLs for previewing a not-yet-sent file -- entirely
  // client-side, no upload/signing needed since it's just showing the
  // user their own pending attachment back to them.
  const filePreviews = React.useMemo(() => files.map(f => ({ file: f, url: f.type.startsWith('image/') ? URL.createObjectURL(f) : null })), [files]);
  useEffect(() => () => { filePreviews.forEach(p => p.url && URL.revokeObjectURL(p.url)); }, [filePreviews]);
  const [isInternal, setIsInternal] = useState(false);
  const [statusChange, setStatusChange] = useState('');
  const [priorityChange, setPriorityChange] = useState('');
  const [assignTo, setAssignTo] = useState(ticket.assigned_to || '');
  const [scheduleClose, setScheduleClose] = useState(!!ticket.scheduled_close_at);
  const [scheduledCloseAt, setScheduledCloseAt] = useState(toDatetimeLocalValue(ticket.scheduled_close_at || Date.now() + 24 * 60 * 60 * 1000));
  const [submitting, setSubmitting] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [refreshingDetail, setRefreshingDetail] = useState(false);
  const [wellskyCaregivers, setWellskyCaregivers] = useState([]);
  // { matched: boolean } once a close succeeds -- drives the "send
  // transcript to WellSky" / "no match, download instead" popup.
  const [closePrompt, setClosePrompt] = useState(null);
  const [sendingTranscript, setSendingTranscript] = useState(false);
  const [downloadingTranscript, setDownloadingTranscript] = useState(false);

  useEffect(() => {
    supabase.from('wellsky_caregivers').select('id, first_name, last_name, email, wellsky_practitioner_id, wellsky_person_id')
      .then(({ data }) => setWellskyCaregivers(data || []));
  }, []);
  const matchedCaregiver = React.useMemo(
    () => matchCaregiverForTicket(ticket.customer_name, ticket.customer_email, wellskyCaregivers),
    [ticket.customer_name, ticket.customer_email, wellskyCaregivers]
  );

  const loadMessages = async () => {
    setLoading(true);
    const [{ data }, { data: attData }] = await Promise.all([
      supabase.from('ticket_messages').select('id, sender_type, staff_user_id, body, is_internal, created_at').eq('ticket_id', ticket.id).order('created_at', { ascending: true }),
      supabase.from('ticket_attachments').select('id, message_id, file_name, storage_path, file_size, content_type').eq('ticket_id', ticket.id),
    ]);
    setMessages(data || []);
    setAttachments(attData || []);
    setLoading(false);

    // Pre-sign image attachments so they can render as real inline
    // thumbnails, not just a filename chip -- non-image attachments
    // (PDFs, docs) stay as chips, signed on click instead.
    const images = (attData || []).filter(a => (a.content_type || '').startsWith('image/'));
    if (images.length > 0) {
      const entries = await Promise.all(images.map(async a => {
        const { data: signed } = await supabase.storage.from('ticket-attachments').createSignedUrl(a.storage_path, 3600);
        return [a.id, signed?.signedUrl];
      }));
      setThumbUrls(Object.fromEntries(entries.filter(([, url]) => url)));
    }
  };

  const attachmentsFor = (messageId) => attachments.filter(a => a.message_id === messageId);

  // Staff already have an authenticated session with direct storage read
  // access (staff_ticket_attachments policy), so no edge function needed
  // here -- unlike the customer-facing view, which has no Supabase session
  // at all and goes through ticket-attachment-url instead.
  const downloadAttachment = async (att) => {
    const { data, error } = await supabase.storage.from('ticket-attachments').createSignedUrl(att.storage_path, 300);
    if (error || !data?.signedUrl) { showToast('Failed to open attachment'); return; }
    window.open(data.signedUrl, '_blank', 'noopener');
  };

  const reloadTicket = async () => {
    const { data } = await supabase.from('tickets').select('*').eq('id', ticket.id).single();
    if (data) setTicket(data);
  };

  useEffect(() => {
    loadMessages();
    if (profile?.id) {
      supabase.from('activity_log').insert({
        user_id: profile.id, action: 'VIEW', entity_type: 'tickets', entity_id: ticket.id,
      }).then(({ error }) => { if (error) console.error('Failed to log view:', error); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No realtime subscription on this view — a reply sent from another
  // device (or another browser tab) wouldn't otherwise show up here until
  // something reloads it. Refresh on refocus covers the common case: staff
  // has a ticket open on one device, replies from another, then comes back.
  useEffect(() => {
    const onFocus = () => { loadMessages(); reloadTicket(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const staffName = (userId) => staffUsers.find(s => s.id === userId)?.full_name || 'Staff';

  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  // WellSky's document title validation rejects a title whose name portion
  // isn't a clean "First Last" -- confirmed live for Care Consultation
  // client names stored with a suffix or payer tag folded in (e.g. "Dych,
  // Jr.", "Marshall (VADC)"). Same defensive cleanup here, since a
  // caregiver's WellSky-stored name can carry the same kind of extra text.
  const CAREGIVER_NAME_SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v']);
  const ticketFileTitle = (ext) => {
    const rawName = matchedCaregiver ? `${matchedCaregiver.first_name || ''} ${matchedCaregiver.last_name || ''}`.trim() : '';
    const cleanName = rawName
      .replace(/[(),]/g, ' ')
      .split(/\s+/).filter(Boolean)
      .filter(w => !CAREGIVER_NAME_SUFFIXES.has(w.replace(/\.$/, '').toLowerCase()))
      .join(' ');
    const subject = (ticket.subject || 'Ticket').replace(/[/\\]/g, '-');
    return `${cleanName ? `${cleanName} - ` : ''}Ticket ${ticket.ticket_number} - ${subject}.${ext}`;
  };

  // Transcript excludes internal-only notes (and their attachments) --
  // those are staff-only annotations, not part of what the customer
  // actually said, so they don't belong in a record handed off externally.
  const buildTicketZip = async () => {
    const zip = new JSZip();
    const visibleMessages = [...messages].filter(m => !m.is_internal).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const lines = [
      `Ticket #${ticket.ticket_number} - ${ticket.subject}`,
      `Customer: ${ticket.customer_name} <${ticket.customer_email}>`,
      `Status: ${TICKET_STATUSES[ticket.status] || ticket.status}`,
      '', '---', '',
    ];
    visibleMessages.forEach(m => {
      const who = m.sender_type === 'staff' ? staffName(m.staff_user_id) : ticket.customer_name;
      const when = new Date(m.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
      lines.push(`${who} — ${when}`, m.body, '');
    });
    zip.file('transcript.txt', lines.join('\n'));

    const visibleMessageIds = new Set(visibleMessages.map(m => m.id));
    const visibleAttachments = attachments.filter(a => !a.message_id || visibleMessageIds.has(a.message_id));
    const usedNames = new Set(['transcript.txt']);
    for (const att of visibleAttachments) {
      // See buildTicketPdf's matching comment -- a bad attachment (network
      // blip, missing storage object on an older ticket) must not throw out
      // of the whole zip build.
      let blob;
      try {
        const { data } = await supabase.storage.from('ticket-attachments').createSignedUrl(att.storage_path, 300);
        if (!data?.signedUrl) continue;
        const res = await fetch(data.signedUrl);
        if (!res.ok) continue;
        blob = await res.blob();
      } catch {
        continue;
      }
      let name = att.file_name || 'attachment';
      if (usedNames.has(name)) {
        const extIdx = name.lastIndexOf('.');
        const base = extIdx > 0 ? name.slice(0, extIdx) : name;
        const ext = extIdx > 0 ? name.slice(extIdx) : '';
        let i = 2;
        while (usedNames.has(`${base} (${i})${ext}`)) i++;
        name = `${base} (${i})${ext}`;
      }
      usedNames.add(name);
      zip.file(name, blob);
    }
    return zip.generateAsync({ type: 'blob' });
  };

  // WellSky's DocumentReference upload only accepts application/pdf -- confirmed
  // live, it rejects application/zip with a 422 "'contentType' mismatch", and
  // application/pdf is the only contentType ever used successfully against this
  // same WellSky endpoint (see wellsky-documents). So unlike buildTicketZip
  // (still used for the local Download button, which has no such restriction),
  // this merges everything into one PDF: the transcript as text pages, then any
  // image/PDF attachments appended as further pages. Attachments pdf-lib can't
  // embed (audio, docx, etc.) are listed by name on a final page instead --
  // staff can still get the raw file via the Download button.
  const buildTicketPdf = async () => {
    const pdfDoc = await PDFDocument.create();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const pageWidth = 612, pageHeight = 792, margin = 50;
    const maxWidth = pageWidth - margin * 2;
    let page = pdfDoc.addPage([pageWidth, pageHeight]);
    let y = pageHeight - margin;

    const wrapLine = (text, size, f) => {
      const words = String(text).split(/\s+/);
      const lines = [];
      let current = '';
      for (const word of words) {
        const attempt = current ? `${current} ${word}` : word;
        if (f.widthOfTextAtSize(attempt, size) > maxWidth && current) {
          lines.push(current);
          current = word;
        } else {
          current = attempt;
        }
      }
      if (current) lines.push(current);
      return lines.length ? lines : [''];
    };

    const ensureSpace = (needed) => {
      if (y - needed < margin) {
        page = pdfDoc.addPage([pageWidth, pageHeight]);
        y = pageHeight - margin;
      }
    };

    const drawParagraph = (text, { size = 10, f = font, color = rgb(0.1, 0.1, 0.1), gapAfter = 10 } = {}) => {
      String(text).split('\n').forEach(rawLine => {
        wrapLine(rawLine, size, f).forEach(line => {
          ensureSpace(size + 4);
          page.drawText(line, { x: margin, y, size, font: f, color });
          y -= size + 4;
        });
      });
      y -= gapAfter;
    };

    drawParagraph(`Ticket #${ticket.ticket_number} - ${ticket.subject}`, { size: 14, f: boldFont, gapAfter: 6 });
    drawParagraph(`Customer: ${ticket.customer_name} <${ticket.customer_email}>`, { size: 10, gapAfter: 2 });
    drawParagraph(`Status: ${TICKET_STATUSES[ticket.status] || ticket.status}`, { size: 10, gapAfter: 14 });

    const visibleMessages = [...messages].filter(m => !m.is_internal).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    visibleMessages.forEach(m => {
      const who = m.sender_type === 'staff' ? staffName(m.staff_user_id) : ticket.customer_name;
      const when = new Date(m.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
      drawParagraph(`${who} — ${when}`, { size: 10, f: boldFont, gapAfter: 2 });
      drawParagraph(m.body, { size: 10, gapAfter: 12 });
    });

    const visibleMessageIds = new Set(visibleMessages.map(m => m.id));
    const visibleAttachments = attachments.filter(a => !a.message_id || visibleMessageIds.has(a.message_id));
    const skipped = [];
    for (const att of visibleAttachments) {
      // Each attachment is fetched and embedded independently -- a network
      // blip or a missing storage object on any one of them (more likely
      // the older a ticket is) must not throw "Failed to fetch" out of the
      // whole PDF build and abort sending the rest of the transcript.
      let bytes;
      try {
        const { data } = await supabase.storage.from('ticket-attachments').createSignedUrl(att.storage_path, 300);
        if (!data?.signedUrl) { skipped.push(att.file_name || 'attachment'); continue; }
        const res = await fetch(data.signedUrl);
        if (!res.ok) { skipped.push(att.file_name || 'attachment'); continue; }
        bytes = new Uint8Array(await res.arrayBuffer());
      } catch {
        skipped.push(att.file_name || 'attachment');
        continue;
      }
      const lowerName = (att.file_name || '').toLowerCase();
      try {
        if (lowerName.endsWith('.png') || lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg')) {
          const img = lowerName.endsWith('.png') ? await pdfDoc.embedPng(bytes) : await pdfDoc.embedJpg(bytes);
          const scale = Math.min(1, maxWidth / img.width, (pageHeight - margin * 2 - 30) / img.height);
          const imgPage = pdfDoc.addPage([pageWidth, pageHeight]);
          imgPage.drawText(att.file_name || 'Image', { x: margin, y: pageHeight - margin, size: 10, font: boldFont, color: rgb(0.1, 0.1, 0.1) });
          imgPage.drawImage(img, { x: margin, y: pageHeight - margin - 20 - img.height * scale, width: img.width * scale, height: img.height * scale });
        } else if (lowerName.endsWith('.pdf')) {
          const srcDoc = await PDFDocument.load(bytes);
          const copiedPages = await pdfDoc.copyPages(srcDoc, srcDoc.getPageIndices());
          copiedPages.forEach(p => pdfDoc.addPage(p));
        } else {
          skipped.push(att.file_name || 'attachment');
        }
      } catch {
        skipped.push(att.file_name || 'attachment');
      }
    }

    if (skipped.length > 0) {
      page = pdfDoc.addPage([pageWidth, pageHeight]);
      y = pageHeight - margin;
      drawParagraph('Additional attachments (not shown above -- available via Download in the portal):', { size: 10, f: boldFont, gapAfter: 6 });
      skipped.forEach(n => drawParagraph(`• ${n}`, { size: 10, gapAfter: 2 }));
    }

    return pdfDoc.save();
  };

  const sendTranscriptToWellsky = async () => {
    if (!matchedCaregiver?.wellsky_person_id) return;
    setSendingTranscript(true);
    try {
      const pdfBytes = await buildTicketPdf();
      const base64 = await blobToBase64(bytesToBlob(pdfBytes));
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/ticket-wellsky-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ ticketId: ticket.id, wellskyPersonId: matchedCaregiver.wellsky_person_id, base64, fileName: ticketFileTitle('pdf') }),
      });
      const result = await res.json();
      if (!result.ok) throw new Error(result.error || 'Failed to send to WellSky');
      showToast('Transcript sent to WellSky');
      setClosePrompt(null);
      await reloadTicket();
    } catch (err) {
      showToast(err.message || 'Failed to send transcript to WellSky', 'error');
    } finally {
      setSendingTranscript(false);
    }
  };

  const downloadTicketZip = async () => {
    setDownloadingTranscript(true);
    try {
      const zipBlob = await buildTicketZip();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = `Ticket-${ticket.ticket_number}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      setClosePrompt(null);
    } catch (err) {
      showToast(err.message || 'Failed to build ticket zip', 'error');
    } finally {
      setDownloadingTranscript(false);
    }
  };

  // Fired right after a ticket actually transitions into Closed, from
  // either close path below (the dedicated Finalize & Close button, or the
  // generic status dropdown + Save Changes).
  const promptAfterClose = () => {
    setClosePrompt({ matched: !!(matchedCaregiver && matchedCaregiver.wellsky_person_id) });
  };

  // Routes assignment changes through the ticket-assign edge function (rather
  // than a direct table update) so the newly-assigned staff member always
  // gets emailed, no matter which action on this page changed the assignee.
  const submitAssignmentIfChanged = async () => {
    if (assignTo === (ticket.assigned_to || '')) return;
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${SUPABASE_URL}/functions/v1/ticket-assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${session.access_token}` },
      body: JSON.stringify({ ticketNumber: ticket.ticket_number, assignedTo: assignTo || null }),
    });
    const result = await res.json();
    if (!result.success) throw new Error(result.error || 'Failed to update assignment.');
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const formData = new FormData();
      formData.append('ticketNumber', ticket.ticket_number);
      formData.append('senderType', 'staff');
      formData.append('message', reply.trim());
      formData.append('isInternal', String(isInternal));
      if (statusChange) formData.append('statusChange', statusChange);
      if (priorityChange) formData.append('priorityChange', priorityChange);
      files.forEach(f => formData.append('files', f));
      const res = await fetch(`${SUPABASE_URL}/functions/v1/ticket-reply`, {
        method: 'POST',
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${session.access_token}` },
        body: formData,
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Failed to send.');
      await submitAssignmentIfChanged();
      const justClosed = statusChange === 'closed' && ticket.status !== 'closed';
      setReply('');
      setFiles([]);
      setStatusChange('');
      setPriorityChange('');
      showToast(isInternal ? 'Internal note added.' : 'Reply sent.');
      await Promise.all([loadMessages(), reloadTicket()]);
      if (justClosed) promptAfterClose();
    } catch (err) {
      showToast(`Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMetaSave = async () => {
    setSavingMeta(true);
    try {
      const assignChanged = assignTo !== (ticket.assigned_to || '');
      const newScheduledCloseAt = scheduleClose ? new Date(scheduledCloseAt).toISOString() : null;
      const scheduledCloseChanged = newScheduledCloseAt !== (ticket.scheduled_close_at || null);

      const update = {};
      if (statusChange)   update.status   = statusChange;
      if (priorityChange) update.priority = priorityChange;
      if (scheduledCloseChanged) update.scheduled_close_at = newScheduledCloseAt;

      if (Object.keys(update).length) {
        const { error } = await supabase.from('tickets').update(update).eq('id', ticket.id);
        if (error) throw error;

        if (statusChange && statusChange !== ticket.status) {
          await supabase.from('ticket_status_history').insert({ ticket_id: ticket.id, changed_by: profile?.id || null, from_status: ticket.status, to_status: statusChange });
        }
      }

      if (assignChanged) await submitAssignmentIfChanged();

      if (!Object.keys(update).length && !assignChanged) { setSavingMeta(false); return; }

      showToast('Ticket updated.');
      const justClosed = update.status === 'closed' && ticket.status !== 'closed';
      setStatusChange('');
      setPriorityChange('');
      await reloadTicket();
      if (justClosed) promptAfterClose();
    } catch (err) {
      showToast(`Error: ${err.message}`);
    } finally {
      setSavingMeta(false);
    }
  };

  const selCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:ring-1 focus:ring-[#2c4f26] transition';

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
      {/* Header */}
      <div className="flex items-start gap-3 mb-5">
        <button onClick={onBack} className="mt-1 text-gray-400 hover:text-gray-700 transition"><ChevronLeft size={20} /></button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-[#2c4f26]">#{ticket.ticket_number}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[ticket.status] || ''}`}>{TICKET_STATUSES[ticket.status] || ticket.status}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${priorityColors[ticket.priority] || ''}`}>{TICKET_PRIORITIES[ticket.priority] || ticket.priority}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mt-0.5 truncate">{ticket.subject}</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm text-gray-500">{ticket.customer_name} · {ticket.customer_email}</p>
            {matchedCaregiver && wellskyCaregiverUrl(matchedCaregiver.wellsky_practitioner_id) ? (
              <a href={wellskyCaregiverUrl(matchedCaregiver.wellsky_practitioner_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:underline shrink-0">
                View in WellSky <ExternalLink size={12} />
              </a>
            ) : wellskyCaregivers.length > 0 ? (
              <span
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 shrink-0"
                title="No caregiver in the WellSky roster matched this ticket's customer by email or name"
              >
                No WellSky match
              </span>
            ) : null}
          </div>
          {ticket.created_by && (
            <p className="text-xs text-gray-400 mt-0.5">Logged by {staffName(ticket.created_by)} on behalf of the customer</p>
          )}
        </div>
        <button
          onClick={async () => { setRefreshingDetail(true); await Promise.all([loadMessages(), reloadTicket()]); setRefreshingDetail(false); }}
          disabled={refreshingDetail}
          title="Refresh this ticket"
          className="mt-1 p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition disabled:opacity-50 shrink-0"
        >
          <RefreshCw size={16} className={refreshingDetail ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
        {/* Conversation */}
        <div className="space-y-4">
          {ticket.status === 'resolved' && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-2">
              <CheckCircle size={15} className="text-green-600 shrink-0" />
              <p className="text-sm text-green-800">Customer marked this ticket as resolved. Use <strong>Finalize &amp; Close</strong> to complete it.</p>
            </div>
          )}
          {loading ? (
            <div className="flex justify-center py-12"><div className="w-7 h-7 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            messages.map((msg, idx) => {
              const isCustomer = msg.sender_type === 'customer';
              const isLoggedOpeningMessage = idx === 0 && isCustomer && ticket.created_by;
              return (
                <div key={msg.id} className={`rounded-2xl border p-4 ${msg.is_internal ? 'bg-amber-50 border-amber-200' : isCustomer ? 'bg-white border-gray-200' : 'bg-[#f0f5f0] border-green-200'}`}>
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${msg.is_internal ? 'text-amber-700' : isCustomer ? 'text-gray-700' : 'text-[#2c4f26]'}`}>
                        {isCustomer ? ticket.customer_name : staffName(msg.staff_user_id)}
                      </span>
                      {isLoggedOpeningMessage && <span className="text-xs text-gray-400">(submitted by {staffName(ticket.created_by)})</span>}
                      {msg.is_internal && <span className="text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded font-medium flex items-center gap-1"><EyeOff size={10} /> Internal</span>}
                      {!isCustomer && !msg.is_internal && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">Staff Reply</span>}
                    </div>
                    <span className="text-xs text-gray-400">{new Date(msg.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                  </div>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{msg.body}</p>
                  {attachmentsFor(msg.id).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {attachmentsFor(msg.id).map(att => (
                        thumbUrls[att.id] ? (
                          <img key={att.id} src={thumbUrls[att.id]} alt={att.file_name} title={att.file_name} onClick={() => downloadAttachment(att)} className="w-28 h-28 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition" />
                        ) : (
                          <button key={att.id} type="button" onClick={() => downloadAttachment(att)} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5 rounded-lg transition">
                            <FileText size={12} /> {att.file_name}
                          </button>
                        )
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Reply form */}
          {(canReply || canInternalNotes) ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Add Reply</h3>
              {canInternalNotes && (
                <button type="button" onClick={() => setIsInternal(v => !v)} className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg transition ${isInternal ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {isInternal ? <><EyeOff size={12} /> Internal Note</> : <><Eye size={12} /> Public Reply</>}
                </button>
              )}
            </div>
            {!canReply && isInternal === false && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-3">You don't have permission to send public replies. Switch to Internal Note.</p>
            )}
            <form onSubmit={handleReply} className="space-y-3">
              <textarea rows={4} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition resize-y min-h-[6rem] text-sm" value={reply} onChange={e => setReply(e.target.value)} onPaste={e => handlePasteImages(e, setFiles)} placeholder={(isInternal ? 'Internal note (only visible to staff)…' : 'Type your reply to the customer…') + ' (paste a screenshot directly)'} />
              <div>
                <label className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 cursor-pointer transition">
                  <Paperclip size={13} /> Attach files
                  <input type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt" className="hidden" onChange={e => {
                    const picked = Array.from(e.target.files || []);
                    setFiles(prev => [...prev, ...picked].slice(0, 5));
                    e.target.value = '';
                  }} />
                </label>
                {filePreviews.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {filePreviews.map(({ file: f, url }, i) => (
                      url ? (
                        <div key={i} className="relative">
                          <img src={url} alt={f.name} title={f.name} className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                          <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 bg-gray-700 text-white rounded-full p-0.5 hover:bg-red-600 transition"><X size={10} /></button>
                        </div>
                      ) : (
                        <span key={i} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-lg h-fit">
                          {f.name}
                          <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="hover:text-red-500 transition"><X size={11} /></button>
                        </span>
                      )
                    ))}
                  </div>
                )}
              </div>
              {canManage && (
                <div className="flex gap-3 flex-wrap">
                  <select className="text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white focus:ring-1 focus:ring-[#2c4f26]" value={statusChange} onChange={e => setStatusChange(e.target.value)}>
                    <option value="">{isInternal ? `Keep: ${TICKET_STATUSES[ticket.status]}` : 'Auto: Waiting on Customer'}</option>
                    {Object.entries(TICKET_STATUSES).filter(([v]) => v !== ticket.status && v !== 'auto_closed').map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    <option value={ticket.status}>Keep: {TICKET_STATUSES[ticket.status]}</option>
                  </select>
                </div>
              )}
              <button type="submit" disabled={submitting || !reply.trim() || (!canReply && !isInternal)} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition disabled:opacity-50 ${isInternal ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-[#2c4f26] hover:bg-[#1f3a1c] text-white'}`}>
                {submitting ? 'Sending…' : <><Send size={14} />{isInternal ? 'Save Note' : 'Send Reply'}</>}
              </button>
            </form>
          </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-center text-sm text-gray-500">You don't have permission to reply to tickets.</div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Ticket Details</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Category</p>
                <p className="text-sm font-medium text-gray-800">{TICKET_CATEGORY_LABELS[ticket.category] || ticket.category}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Created</p>
                <p className="text-sm text-gray-800">{new Date(ticket.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Created By</p>
                <p className="text-sm text-gray-800">{ticket.created_by ? staffName(ticket.created_by) : 'Customer'}</p>
              </div>
              {ticket.last_customer_reply_at && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Last Customer Reply</p>
                  <p className="text-sm text-gray-800">{new Date(ticket.last_customer_reply_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                </div>
              )}
            </div>
          </div>

          {(canManage || canClose) && (
          <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</h3>
            {canManage && (<>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select className={selCls} value={statusChange || ticket.status} onChange={e => setStatusChange(e.target.value)}>
                {Object.entries(TICKET_STATUSES).filter(([v]) => v !== 'auto_closed').map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Priority</label>
              <select className={selCls} value={priorityChange || ticket.priority} onChange={e => setPriorityChange(e.target.value)}>
                {Object.entries(TICKET_PRIORITIES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Assigned To</label>
              <select className={selCls} value={assignTo} onChange={e => setAssignTo(e.target.value)}>
                <option value="">Unassigned</option>
                {staffUsers.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <input type="checkbox" className="rounded border-gray-300 text-[#2c4f26] focus:ring-[#2c4f26]" checked={scheduleClose} onChange={e => setScheduleClose(e.target.checked)} />
                Schedule auto-close
              </label>
              {scheduleClose && (
                <>
                  <input type="datetime-local" className={`${selCls} mt-2`} value={scheduledCloseAt} onChange={e => setScheduledCloseAt(e.target.value)} />
                  <p className="text-xs text-gray-400 mt-1">Closes automatically if no one replies by then. Any reply cancels it.</p>
                </>
              )}
            </div>
            <button onClick={handleMetaSave} disabled={savingMeta} className="w-full bg-gray-800 text-white text-sm py-2 rounded-lg hover:bg-gray-900 transition disabled:opacity-50 font-medium">
              {savingMeta ? 'Saving…' : 'Save Changes'}
            </button>
            </>)}
            {canClose && ticket.status === 'resolved' && (
              <button
                onClick={async () => {
                  setSavingMeta(true);
                  const { error } = await supabase.from('tickets').update({ status: 'closed' }).eq('id', ticket.id);
                  if (!error) {
                    await supabase.from('ticket_status_history').insert({ ticket_id: ticket.id, changed_by: profile?.id || null, from_status: 'resolved', to_status: 'closed' });
                    showToast('Ticket closed.');
                    await reloadTicket();
                    promptAfterClose();
                  }
                  setSavingMeta(false);
                }}
                disabled={savingMeta}
                className="w-full bg-[#2c4f26] text-white text-sm py-2 rounded-lg hover:bg-[#1f3a1c] transition disabled:opacity-50 font-medium flex items-center justify-center gap-1.5"
              >
                <CheckCircle size={14} /> Finalize &amp; Close
              </button>
            )}
            {ticket.status === 'closed' && (
              <div className={canManage || (canClose && ticket.status === 'resolved') ? 'pt-3 border-t border-gray-100' : ''}>
                <button
                  onClick={promptAfterClose}
                  className="w-full bg-teal-600 text-white text-sm py-2 rounded-lg hover:bg-teal-700 transition font-medium flex items-center justify-center gap-1.5"
                >
                  <Upload size={14} /> {ticket.wellsky_transcript_sent_at ? 'Re-send to WellSky' : 'Send to WellSky'}
                </button>
                {ticket.wellsky_transcript_sent_at && (
                  <p className="text-xs text-gray-400 mt-1.5 text-center">
                    Sent {new Date(ticket.wellsky_transcript_sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {ticket.wellsky_transcript_sent_by ? ` by ${staffName(ticket.wellsky_transcript_sent_by)}` : ''}
                  </p>
                )}
              </div>
            )}
          </div>
          )}

          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Customer</h3>
            <p className="text-sm font-medium text-gray-900">{ticket.customer_name}</p>
            <p className="text-xs text-gray-500 mt-0.5">{ticket.customer_email}</p>
          </div>
        </div>
      </div>

      {closePrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setClosePrompt(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            {closePrompt.matched ? (
              <>
                <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center mb-4"><Upload size={20} className="text-teal-600" /></div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Send transcript to WellSky?</h3>
                <p className="text-sm text-gray-700 mb-6">
                  This ticket matched <span className="font-semibold">{matchedCaregiver ? jotformCaregiverDisplayName(matchedCaregiver) : 'a caregiver'}</span>. Build a PDF of the conversation and attachments and upload it to their WellSky profile?
                </p>
                <div className="flex gap-3">
                  <button onClick={sendTranscriptToWellsky} disabled={sendingTranscript} className="flex-1 bg-[#2c4f26] text-white py-2 rounded-lg text-sm font-semibold hover:bg-[#1f3a1c] disabled:opacity-50 transition">
                    {sendingTranscript ? 'Sending…' : 'Send to WellSky'}
                  </button>
                  <button onClick={() => setClosePrompt(null)} disabled={sendingTranscript} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition">Skip</button>
                </div>
              </>
            ) : (
              <>
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4"><Download size={20} className="text-gray-500" /></div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">No WellSky match found</h3>
                <p className="text-sm text-gray-700 mb-6">No caregiver in the WellSky roster matched this ticket's customer by email or name. Download a zip of the conversation and attachments to upload manually?</p>
                <div className="flex gap-3">
                  <button onClick={downloadTicketZip} disabled={downloadingTranscript} className="flex-1 bg-gray-800 text-white py-2 rounded-lg text-sm font-semibold hover:bg-gray-900 disabled:opacity-50 transition">
                    {downloadingTranscript ? 'Zipping…' : 'Download Zip'}
                  </button>
                  <button onClick={() => setClosePrompt(null)} disabled={downloadingTranscript} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition">Skip</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Self-service SMS opt-in -- also the actual consent record behind Twilio's
// "Opt-in method proof" requirement for SMS campaign registration, not just
// a UI nicety. Lets a staff member confirm/update their own phone number and
// explicitly agree to receive text notifications, with the standard SMS
// program disclosures (frequency, rates, opt-out) shown right at the
// checkbox rather than buried in a separate page.
const NAV_MAX_PINNED = 6;

const UserSettingsModal = ({ profile, navTabs, onClose, onSaved }) => {
  const [phone, setPhone] = useState(formatStaffPhone(profile?.phone || ''));
  const [pinnedTabs, setPinnedTabs] = useState(profile?.nav_pinned_tabs || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const addTab = (id) => setPinnedTabs(prev => prev.includes(id) || prev.length >= NAV_MAX_PINNED ? prev : [...prev, id]);
  const removeTab = (id) => setPinnedTabs(prev => prev.filter(x => x !== id));
  const moveTab = (index, dir) => setPinnedTabs(prev => {
    const target = index + dir;
    if (target < 0 || target >= prev.length) return prev;
    const next = prev.slice();
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });

  // SMS consent is collected in person (at hiring), not through a digital
  // checkbox here -- an admin enables SMS for someone in Edit Permissions
  // once that's done. This panel is just for keeping a phone number current.
  const save = async () => {
    setError('');
    setSaving(true);
    try {
      const update = { phone: phone.trim() || null, nav_pinned_tabs: pinnedTabs.length > 0 ? pinnedTabs : null };
      const { error: err } = await supabase.from('profiles').update(update).eq('id', profile.id);
      if (err) throw err;
      setSaved(true);
      onSaved?.(update);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Account Settings</h2>
            <p className="text-xs text-gray-400">{profile?.full_name}{profile?.email ? ` · ${profile.email}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          {saved ? (
            <div className="text-center py-4">
              <CheckCircle size={32} className="text-green-600 mx-auto mb-2" />
              <p className="text-sm font-semibold text-gray-900">Preferences saved</p>
            </div>
          ) : (
            <>
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400">Notifications</h3>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Phone Number</label>
                <input
                  type="tel"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm"
                  placeholder="555-555-5555"
                  value={phone}
                  onChange={e => setPhone(formatStaffPhone(e.target.value))}
                />
                <p className="text-xs text-gray-400 mt-1">Used for text notifications, if your admin has enabled them for you.</p>
              </div>

              {navTabs && navTabs.length > 0 && (() => {
                const tabLabel = (id) => navTabs.find(t => t.id === id)?.label || id;
                const availableToAdd = navTabs.filter(t => !pinnedTabs.includes(t.id));
                const atMax = pinnedTabs.length >= NAV_MAX_PINNED;
                return (
                  <div className="pt-2 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400">Navigation</h3>
                      <span className="text-xs text-gray-400">{pinnedTabs.length}/{NAV_MAX_PINNED}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">Choose up to {NAV_MAX_PINNED} tabs to show up front, in the order you want them — everything else lands under "More". Leave this empty to use the default order.</p>

                    {pinnedTabs.length > 0 && (
                      <div className="space-y-1 mb-2">
                        {pinnedTabs.map((id, i) => (
                          <div key={id} className="flex items-center gap-1.5 text-sm px-2 py-1.5 rounded-lg border border-[#2c4f26] bg-[#2c4f26]/10 text-[#2c4f26]">
                            <span className="flex-1 font-medium">{tabLabel(id)}</span>
                            <button type="button" onClick={() => moveTab(i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-white/60 disabled:opacity-30 disabled:cursor-not-allowed" title="Move up">
                              <ChevronUp size={14} />
                            </button>
                            <button type="button" onClick={() => moveTab(i, 1)} disabled={i === pinnedTabs.length - 1} className="p-1 rounded hover:bg-white/60 disabled:opacity-30 disabled:cursor-not-allowed" title="Move down">
                              <ChevronDown size={14} />
                            </button>
                            <button type="button" onClick={() => removeTab(id)} className="p-1 rounded hover:bg-white/60 text-red-600" title="Remove">
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {availableToAdd.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {availableToAdd.map(t => (
                          <button
                            type="button"
                            key={t.id}
                            onClick={() => addTab(t.id)}
                            disabled={atMax}
                            className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                          >
                            <Plus size={11} /> {t.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {error && <p className="text-xs text-red-600">{error}</p>}
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">{saved ? 'Close' : 'Cancel'}</button>
          {!saved && (
            <button onClick={save} disabled={saving} className="px-5 py-2 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Portal Layout - unified nav with tabs for all admin/owner views
const PortalLayout = ({ profile, permissions, isOfflineMode, activeTab, setActiveTab, view, setView, setSelectedApplicant, handleLogout, children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserSettings, setShowUserSettings] = useState(false);
  const [showMoreTabs, setShowMoreTabs] = useState(false);
  const moreTabsRef = useRef(null);
  // Local override so the nav re-splits immediately after saving in Account
  // Settings, without needing the whole app's profile state to refetch --
  // starts from whatever's on the profile record, null meaning "no
  // customization yet, use the default order."
  const [navPinnedTabs, setNavPinnedTabs] = useState(profile?.nav_pinned_tabs || null);

  // More than this and the row was overflowing (forcing horizontal scroll)
  // on accounts with every permission -- fold the rest behind a "More" menu
  // instead. Fixed split (not reordered around the active tab) so the row
  // never visibly reshuffles as someone switches tabs; the More button just
  // highlights itself when the active tab is one of the hidden ones.
  useEffect(() => {
    if (!showMoreTabs) return;
    const onClickOutside = (e) => { if (moreTabsRef.current && !moreTabsRef.current.contains(e.target)) setShowMoreTabs(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showMoreTabs]);

  const TABS = [
    { id: 'onboarding', label: 'Onboarding', permission: 'can_view_onboarding' },
    { id: 'clients', label: 'Clients', permission: 'can_view_clients' },
    { id: 'wellsky_caregivers', label: 'Care Pros', permission: 'can_view_wellsky_caregivers' },
    { id: 'templates', label: 'Action Templates', permission: 'can_view_incident_reports' },
    { id: 'faq', label: 'FAQ', permission: 'can_view_faq' },
    { id: 'jotform_submissions', label: 'Form Submissions', permission: 'can_view_jotform_submissions' },
    { id: 'todos', label: 'Task Board', permission: 'can_view_todos' },
    { id: 'meeting_agenda', label: 'Meeting Agenda', permission: 'can_view_meetings' },
    { id: 'phone_screen', label: 'Phone Screen', permission: 'can_view_phone_screen' },
    { id: 'care_consultation', label: 'Care Consultation', permission: 'can_view_care_consultation' },
    { id: 'care_pro_transfer', label: 'Care Pro Transfer', permission: 'can_view_care_pro_transfer' },
    { id: 'support', label: 'Support', permission: 'can_view_support' },
    { id: 'admin_panel', label: 'Admin', permission: 'can_view_admin' },
  ];

  const visibleTabs = profile?.role === 'owner'
    ? TABS
    : TABS.filter(t => permissions?.[t.permission]);
  const MAX_VISIBLE_TABS = 6;
  // A user's chosen pins win (in their chosen order, dropping any they no
  // longer have access to); everyone else keeps the original fixed-order
  // first-N cutoff.
  const hasCustomNav = Array.isArray(navPinnedTabs) && navPinnedTabs.length > 0;
  const primaryTabs = hasCustomNav
    ? navPinnedTabs.map(id => visibleTabs.find(t => t.id === id)).filter(Boolean).slice(0, MAX_VISIBLE_TABS)
    : visibleTabs.slice(0, MAX_VISIBLE_TABS);
  const overflowTabs = hasCustomNav
    ? visibleTabs.filter(t => !primaryTabs.some(p => p.id === t.id))
    : visibleTabs.slice(MAX_VISIBLE_TABS);
  const isOverflowActive = overflowTabs.some(t => t.id === activeTab);

  const clientsSubViews = [
    { label: 'Client List', view: 'client_list' },
    { label: 'Tasks', view: 'client_tasks' },
    { label: 'Visit Planner', view: 'visit_planner' },
  ];

  const onboardingSubViews = [
    ...(profile?.role === 'owner' || permissions?.can_view_onboarding_dashboard ? [{ label: 'Dashboard', view: 'admin' }] : []),
    ...(profile?.role === 'owner' || permissions?.can_view_applicants ? [{ label: 'Applicants', view: 'applicants' }] : []),
    ...(profile?.role === 'owner' || permissions?.can_view_hiring_pipeline ? [
      { label: 'Pipeline', view: 'hiring_pipeline' },
      { label: 'Interview Calendar', view: 'interview_calendar' },
    ] : []),
    ...(profile?.role === 'owner' || permissions?.can_view_orientation_groups ? [{ label: 'Orientation Groups', view: 'orientation_groups' }] : []),
    ...(profile?.role === 'owner' || permissions?.can_view_onboarding_templates ? [{ label: 'Templates', view: 'templates' }] : []),
    ...(profile?.role === 'owner' || permissions?.can_view_activated_insights ? [{ label: 'Activated Insights', view: 'activated_insights' }] : []),
    ...(profile?.role === 'owner' ? [
      { label: 'Analytics', view: 'analytics' },
    ] : []),
  ];

  const navigateTo = (newView) => {
    setSelectedApplicant(null);
    setView(newView);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between py-3 gap-4">
            <div className="flex items-center gap-3 shrink-0">
              <img src={HISmallLogo} alt="Home Instead" className="h-8 md:h-10 w-auto object-contain" />
              <span className="text-lg md:text-xl font-bold text-[#2c4f26]">Home Instead Portal</span>
              {isOfflineMode && (
                <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 rounded-full">
                  Offline — showing cached data
                </span>
              )}
            </div>

            {visibleTabs.length > 0 && (
              <div className="hidden lg:flex items-center gap-1 flex-1">
                {primaryTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-1.5 rounded-lg font-medium text-sm transition whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-[#2c4f26] text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
                {overflowTabs.length > 0 && (
                  <div className="relative" ref={moreTabsRef}>
                    <button
                      onClick={() => setShowMoreTabs(v => !v)}
                      className={`flex items-center gap-1 px-4 py-1.5 rounded-lg font-medium text-sm transition whitespace-nowrap ${
                        isOverflowActive
                          ? 'bg-[#2c4f26] text-white'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      {isOverflowActive ? overflowTabs.find(t => t.id === activeTab)?.label : 'More'}
                      <ChevronDown size={14} />
                    </button>
                    {showMoreTabs && (
                      <div className="absolute left-0 mt-1 min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-30">
                        {overflowTabs.map(tab => (
                          <button
                            key={tab.id}
                            onClick={() => { setActiveTab(tab.id); setShowMoreTabs(false); }}
                            className={`w-full text-left px-4 py-2 text-sm transition ${
                              activeTab === tab.id ? 'text-[#2c4f26] font-semibold bg-[#f0f5f0]' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-4 shrink-0">
              <button
                onClick={() => setShowUserSettings(true)}
                title="Account Settings"
                className="hidden lg:flex items-center gap-1 text-sm text-gray-700 hover:text-[#2c4f26] transition"
              >
                {profile?.full_name}
                <ChevronDown size={14} />
              </button>
              <button
                onClick={handleLogout}
                className="hidden lg:flex text-gray-600 hover:text-gray-900 font-medium items-center gap-2 text-sm"
              >
                <LogOut size={16} />
                Logout
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-gray-600 hover:text-gray-900"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {activeTab === 'onboarding' && (
            <div className="hidden lg:flex gap-2 border-t border-gray-100 py-1.5">
              {onboardingSubViews.map(item => (
                <button
                  key={item.view}
                  onClick={() => navigateTo(item.view)}
                  className={`text-sm px-3 py-1 rounded-lg transition ${
                    view === item.view
                      ? 'text-[#2c4f26] font-semibold bg-[#f0f5f0]'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}

          {activeTab === 'clients' && (
            <div className="hidden lg:flex gap-2 border-t border-gray-100 py-1.5">
              {clientsSubViews.map(item => (
                <button
                  key={item.view}
                  onClick={() => navigateTo(item.view)}
                  className={`text-sm px-3 py-1 rounded-lg transition ${
                    view === item.view || (item.view === 'client_list' && view !== 'client_tasks' && view !== 'visit_planner')
                      ? 'text-[#2c4f26] font-semibold bg-[#f0f5f0]'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}

          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200 flex flex-col gap-1">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 mb-1">Sections</p>
              {visibleTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                  className={`text-left font-medium py-2 px-3 rounded-lg text-sm ${
                    activeTab === tab.id ? 'bg-[#2c4f26] text-white' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              {activeTab === 'onboarding' && (
                <>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 mt-3 mb-1">Onboarding</p>
                  {onboardingSubViews.map(item => (
                    <button
                      key={item.view}
                      onClick={() => { navigateTo(item.view); setMobileMenuOpen(false); }}
                      className={`text-left text-sm py-2 px-3 rounded-lg ${
                        view === item.view ? 'text-[#2c4f26] font-medium bg-[#f0f5f0]' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </>
              )}
              {activeTab === 'clients' && (
                <>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 mt-3 mb-1">Clients</p>
                  {clientsSubViews.map(item => (
                    <button
                      key={item.view}
                      onClick={() => { navigateTo(item.view); setMobileMenuOpen(false); }}
                      className={`text-left text-sm py-2 px-3 rounded-lg ${
                        view === item.view || (item.view === 'client_list' && view !== 'client_tasks' && view !== 'visit_planner')
                          ? 'text-[#2c4f26] font-medium bg-[#f0f5f0]' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </>
              )}
              <div className="border-t border-gray-100 mt-3 pt-3">
                <button
                  onClick={() => { setShowUserSettings(true); setMobileMenuOpen(false); }}
                  className="text-left text-gray-600 font-medium py-2 px-3 text-sm"
                >
                  Account Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="text-left text-gray-600 font-medium py-2 px-3 flex items-center gap-2 text-sm"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>
      {showUserSettings && (
        <UserSettingsModal
          profile={profile}
          navTabs={visibleTabs}
          onClose={() => setShowUserSettings(false)}
          onSaved={(update) => setNavPinnedTabs(update.nav_pinned_tabs)}
        />
      )}
      {children}
    </div>
  );
};

// ── Calendar / visit helpers ──────────────────────────────────────────────────

const toDateStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseLocalDate = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };

const VISIT_TYPES = [
  { value: 'regular',   label: 'Regular Visit', color: 'bg-[#2c4f26]' },
  { value: 'nurse',     label: 'Nurse Visit',   color: 'bg-blue-500'  },
  { value: 'qa',        label: 'QA Visit',      color: 'bg-amber-500' },
  { value: 'companion', label: 'Companion',     color: 'bg-purple-500'},
];

const CAL_MONTHS   = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const CAL_DAYS_S   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const CAL_DAYS_L   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const buildCalDays = (year, month) => {
  const first = new Date(year, month, 1).getDay();
  const inMonth = new Date(year, month + 1, 0).getDate();
  const inPrev  = new Date(year, month, 0).getDate();
  const days = [];
  for (let i = first - 1; i >= 0; i--) days.push({ date: new Date(year, month - 1, inPrev - i), current: false });
  for (let i = 1; i <= inMonth; i++)    days.push({ date: new Date(year, month, i),              current: true  });
  for (let i = 1; days.length < 42; i++) days.push({ date: new Date(year, month + 1, i),         current: false });
  return days;
};

const buildRepeatDates = (startStr, repeat, untilStr) => {
  const base = parseLocalDate(startStr);
  const dates = [new Date(base)];
  if (repeat === 'none') return dates;
  const end = untilStr
    ? parseLocalDate(untilStr)
    : new Date(base.getFullYear() + 1, base.getMonth(), base.getDate());
  const baseDay = base.getDate();
  let cur = new Date(base);
  while (dates.length < 200) {
    if (repeat === 'weekly')        cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 7);
    else if (repeat === 'biweekly') cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 14);
    else {
      const nm = cur.getMonth() + 1, ny = nm > 11 ? cur.getFullYear() + 1 : cur.getFullYear(), am = nm % 12;
      cur = new Date(ny, am, Math.min(baseDay, new Date(ny, am + 1, 0).getDate()));
    }
    if (cur > end) break;
    dates.push(new Date(cur));
  }
  return dates;
};

// ── Add Visit Modal ───────────────────────────────────────────────────────────

const AddVisitModal = ({ show, date, clientId, onClose, onSaved }) => {
  const initDate = date ? toDateStr(date) : toDateStr(new Date());
  const [form, setForm] = useState({
    visit_date: initDate, start_time: '09:00', end_time: '',
    visit_type: 'regular', notes: '', repeat: 'none', repeat_until: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const dates   = buildRepeatDates(form.visit_date, form.repeat, form.repeat_until);
      const groupId = form.repeat !== 'none' ? generateUUID() : null;
      const rows = dates.map(d => ({
        client_id: clientId, visit_date: toDateStr(d),
        start_time: form.start_time || null, end_time: form.end_time || null,
        visit_type: form.visit_type, notes: form.notes.trim() || null,
        status: 'scheduled', repeat_group_id: groupId,
      }));
      const { error: dbErr } = await supabase.from('client_visits').insert(rows);
      if (dbErr) throw dbErr;
      onSaved(dates.length);
    } catch (err) {
      setError(err.message || 'Failed to schedule visit');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const dateObj    = parseLocalDate(form.visit_date);
  const dateLabel  = `${CAL_DAYS_L[dateObj.getDay()]}, ${CAL_MONTHS[dateObj.getMonth()]} ${dateObj.getDate()}, ${dateObj.getFullYear()}`;
  const repeatCount = form.repeat !== 'none' ? buildRepeatDates(form.visit_date, form.repeat, form.repeat_until).length : null;
  const inp = 'w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm';
  const lbl = 'block text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900">Schedule Visit</h2>
            <p className="text-xs text-gray-500 mt-0.5">{dateLabel}</p>
          </div>
          <button onClick={onClose} type="button" className="text-gray-400 hover:text-gray-600 transition"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="p-5 space-y-4">
            {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}
            <div>
              <label className={lbl}>Visit Type</label>
              <select className={inp + ' bg-white'} value={form.visit_type} onChange={e => set('visit_type', e.target.value)}>
                {VISIT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Start Time</label>
                <input type="time" className={inp} value={form.start_time} onChange={e => set('start_time', e.target.value)} />
              </div>
              <div>
                <label className={lbl}>End Time <span className="font-normal text-gray-400 text-xs">(optional)</span></label>
                <input type="time" className={inp} value={form.end_time} onChange={e => set('end_time', e.target.value)} />
              </div>
            </div>
            <div>
              <label className={lbl}>Repeat</label>
              <select className={inp + ' bg-white'} value={form.repeat} onChange={e => set('repeat', e.target.value)}>
                <option value="none">Does not repeat</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 weeks</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            {form.repeat !== 'none' && (
              <div>
                <label className={lbl}>Repeat until <span className="font-normal text-gray-400 text-xs">(blank = 1 year)</span></label>
                <input type="date" className={inp} value={form.repeat_until} min={form.visit_date} onChange={e => set('repeat_until', e.target.value)} />
              </div>
            )}
            <div>
              <label className={lbl}>Notes</label>
              <textarea rows={2} className={inp + ' resize-none'} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional notes for this visit…" />
            </div>
          </div>
          <div className="p-5 border-t border-gray-100 flex items-center gap-3 bg-white">
            {repeatCount && (
              <p className="text-xs text-gray-500 shrink-0">Creates <span className="font-semibold text-gray-700">{repeatCount}</span> visits</p>
            )}
            <div className="flex gap-2 ml-auto">
              <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition">Cancel</button>
              <button type="submit" disabled={saving} className="px-5 py-2.5 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-xl transition disabled:opacity-50 flex items-center gap-2">
                {saving ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</> : <><Plus size={15} /> Schedule</>}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Shift Calendar Section ────────────────────────────────────────────────────

const ShiftCalendarSection = ({ clientId, wellskyClientId, showToast }) => {
  const today = new Date();
  const [year, setYear]           = useState(today.getFullYear());
  const [month, setMonth]         = useState(today.getMonth());
  const [visits, setVisits]       = useState([]);
  const [loadingVis, setLoadingVis] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadVisits(); }, [year, month, wellskyClientId]);

  const loadVisits = async () => {
    setLoadingVis(true);
    try {
      const days = buildCalDays(year, month);
      if (wellskyClientId) {
        // WellSky is the source of truth for linked clients -- real synced
        // shifts (wellsky_visits), not the manually-entered client_visits
        // planner below, which stays only for a not-yet-linked client.
        const { data, error } = await supabase
          .from('wellsky_visits').select('*')
          .eq('wellsky_client_id', wellskyClientId)
          .gte('start_at', days[0].date.toISOString())
          .lte('start_at', new Date(days[days.length - 1].date.getTime() + 24 * 60 * 60 * 1000).toISOString())
          .order('start_at');
        if (error) throw error;
        setVisits((data || []).map(v => ({
          id: v.id,
          visit_date: toDateStr(new Date(v.start_at)),
          start_time: v.start_at ? new Date(v.start_at).toTimeString().slice(0, 5) : null,
          end_time: v.end_at ? new Date(v.end_at).toTimeString().slice(0, 5) : null,
          caregiverName: v.caregiver_name,
          status: v.status,
        })));
      } else {
        const { data, error } = await supabase
          .from('client_visits').select('*')
          .eq('client_id', clientId)
          .gte('visit_date', toDateStr(days[0].date))
          .lte('visit_date', toDateStr(days[days.length - 1].date))
          .order('start_time');
        if (error) throw error;
        setVisits(data || []);
      }
    } catch (err) {
      console.error('Error loading visits:', err);
    } finally {
      setLoadingVis(false);
    }
  };

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };
  const goToday   = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const calDays = buildCalDays(year, month);
  const visMap  = {};
  visits.forEach(v => { (visMap[v.visit_date] = visMap[v.visit_date] || []).push(v); });

  const isToday    = (d) => toDateStr(d) === toDateStr(today);
  const fmtTime    = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, '0')}${h >= 12 ? 'pm' : 'am'}`;
  };
  const typeColor = (type) => VISIT_TYPES.find(t => t.value === type)?.color || 'bg-gray-400';

  const openAdd = (d) => { setSelectedDate(d); setShowAddModal(true); };

  return (
    <div className="p-5">
      {/* Month navigation */}
      <div className="flex items-center gap-2 mb-4">
        <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-gray-100 transition text-gray-600"><ChevronLeft size={18} /></button>
        <h3 className="text-sm font-bold text-gray-900 flex-1 text-center">{CAL_MONTHS[month]} {year}</h3>
        <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-gray-100 transition text-gray-600"><ChevronRight size={18} /></button>
        <button onClick={goToday} className="ml-1 px-3 py-1 text-xs font-semibold text-[#2c4f26] bg-[#f0f5f0] hover:bg-[#e0ede0] rounded-lg transition">Today</button>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 mb-px">
        {CAL_DAYS_S.map(d => (
          <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1.5">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      {loadingVis ? (
        <div className="h-60 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#c5d8c3] border-t-[#2c4f26]" />
        </div>
      ) : (
        <div className="grid grid-cols-7 border-l border-t border-gray-200 rounded-b-xl overflow-hidden">
          {calDays.map((day, idx) => {
            const ds = toDateStr(day.date);
            const dayVisits = visMap[ds] || [];
            const todayCell = isToday(day.date);
            const chipColor = wellskyClientId ? 'bg-teal-600' : null;
            return (
              <div
                key={idx}
                onClick={wellskyClientId ? undefined : () => openAdd(day.date)}
                className={`border-r border-b border-gray-200 min-h-[76px] p-1 transition group
                  ${wellskyClientId ? '' : 'cursor-pointer'}
                  ${day.current ? 'bg-white hover:bg-[#f7fbf7]' : 'bg-gray-50 hover:bg-gray-100/80'}`}
              >
                <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold mb-1 mx-auto transition
                  ${todayCell ? 'bg-[#2c4f26] text-white' : day.current ? 'text-gray-700 group-hover:bg-[#e8f0e8]' : 'text-gray-300'}`}>
                  {day.date.getDate()}
                </div>
                {/* Desktop: time chips */}
                <div className="hidden sm:block space-y-px">
                  {wellskyClientId
                    ? dayVisits.slice(0, 2).map(v => {
                        const range = fmtTime(v.start_time) ? `${fmtTime(v.start_time)}${v.end_time ? `–${fmtTime(v.end_time)}` : ''}` : v.status;
                        return (
                          <div key={v.id} title={`${v.caregiverName || 'Unassigned caregiver'}${v.status ? ` · ${v.status}` : ''}`} className={`px-1 py-0.5 rounded text-white leading-tight ${chipColor}`}>
                            <div className="text-[9px] font-semibold truncate">{range}</div>
                            <div className="text-[9px] opacity-90 truncate">{v.caregiverName ? v.caregiverName.split(' ')[0] : 'Unassigned'}</div>
                          </div>
                        );
                      })
                    : dayVisits.slice(0, 3).map(v => (
                        <div key={v.id} className={`text-[10px] font-semibold px-1 py-px rounded text-white truncate leading-tight ${typeColor(v.visit_type)}`}>
                          {fmtTime(v.start_time) || v.visit_type}
                        </div>
                      ))
                  }
                  {dayVisits.length > (wellskyClientId ? 2 : 3) && (
                    <div className="text-[10px] text-gray-400 pl-1">+{dayVisits.length - (wellskyClientId ? 2 : 3)}</div>
                  )}
                </div>
                {/* Mobile: colored dots */}
                {dayVisits.length > 0 && (
                  <div className="sm:hidden flex flex-wrap gap-px justify-center mt-0.5">
                    {dayVisits.slice(0, 5).map(v => (
                      <div key={v.id} className={`w-1.5 h-1.5 rounded-full ${chipColor || typeColor(v.visit_type)}`} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      {wellskyClientId ? (
        <p className="mt-3 text-xs text-gray-400">Synced from WellSky — hover a shift for the assigned caregiver. Read-only here, edit in WellSky.</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {VISIT_TYPES.map(t => (
            <div key={t.value} className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${t.color}`} />
              <span className="text-xs text-gray-600">{t.label}</span>
            </div>
          ))}
          <span className="text-xs text-gray-400 ml-auto hidden sm:block">Click any date to add a visit</span>
        </div>
      )}

      {showAddModal && !wellskyClientId && (
        <AddVisitModal
          key={selectedDate ? toDateStr(selectedDate) : 'new'}
          show={showAddModal}
          date={selectedDate}
          clientId={clientId}
          onClose={() => setShowAddModal(false)}
          onSaved={(count) => {
            setShowAddModal(false);
            loadVisits();
            showToast(count > 1 ? `${count} visits scheduled!` : 'Visit scheduled!');
          }}
        />
      )}
    </div>
  );
};

// Real WellSky shift schedule for a caregiver, read from the wellsky_visits
// cache (kept fresh by wellsky-visits-sync on a rolling cron) rather than
// calling WellSky live per page view -- the client-side equivalent of this
// data now shows directly on the Shift Calendar section above instead of a
// separate section, since every client is WellSky-linked.
const WellskyVisitsSection = ({ caregiverId }) => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    supabase
      .from('wellsky_visits')
      .select('id, start_at, end_at, status, tasks, clients(first_name, last_name)')
      .eq('wellsky_caregiver_id', caregiverId)
      .order('start_at')
      .then(({ data, error: qError }) => {
        if (cancelled) return;
        if (qError) { setError('Failed to load visits'); setVisits([]); return; }
        setVisits(data || []);
      })
      .catch(() => { if (!cancelled) setError('Failed to load visits'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [caregiverId]);

  const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
  const fmtTimeOnly = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';

  if (loading) return <div className="p-8 text-center"><div className="w-6 h-6 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin mx-auto" /></div>;
  if (error) return <p className="p-5 text-sm text-red-600">{error}</p>;
  if (visits.length === 0) return <p className="p-5 text-sm text-gray-400 text-center">No shifts in the synced window.</p>;

  return (
    <div className="divide-y divide-gray-100">
      {visits.map(v => (
        <div key={v.id} className="p-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-gray-900">{fmtDateTime(v.start_at)}{v.end_at ? ` – ${fmtTimeOnly(v.end_at)}` : ''}</p>
            {v.status && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 shrink-0">{v.status}</span>}
          </div>
          <p className="text-xs text-gray-600 mt-0.5">
            {v.clients ? `${v.clients.first_name} ${v.clients.last_name}` : 'Unknown client'}
          </p>
          {v.tasks?.length > 0 && (
            <p className="text-xs text-gray-400 mt-1">{v.tasks.length} task{v.tasks.length === 1 ? '' : 's'}: {v.tasks.map(t => t.title).join(', ')}</p>
          )}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ClientDetailView = ({ client, allClients, onBack, onNavigate, onUpdate, showToast, backLabel = 'Clients' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ ...client });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [openSections, setOpenSections] = useState({ calendar: false, activity: false, files: false });

  const currentIdx = allClients.findIndex(c => c.id === client.id);
  const prevClient = currentIdx > 0 ? allClients[currentIdx - 1] : null;
  const nextClient = currentIdx < allClients.length - 1 ? allClients[currentIdx + 1] : null;

  const set = (k, v) => setEditForm(f => ({ ...f, [k]: v }));
  const fmtPhone = (v) => {
    const d = v.replace(/\D/g, '');
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
  };
  const addTag = (val) => {
    const tag = val.trim();
    if (tag && !(editForm.tags || []).includes(tag)) set('tags', [...(editForm.tags || []), tag]);
    setTagInput('');
  };
  const removeTag = (tag) => set('tags', (editForm.tags || []).filter(t => t !== tag));
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput); }
    else if (e.key === 'Backspace' && !tagInput && (editForm.tags || []).length > 0) removeTag(editForm.tags[editForm.tags.length - 1]);
  };

  const handleSave = async () => {
    setSaveError('');
    setIsSaving(true);
    try {
      const pending = tagInput.trim();
      const finalTags = pending && !(editForm.tags || []).includes(pending) ? [...(editForm.tags || []), pending] : (editForm.tags || []);
      const payload = {
        first_name: editForm.first_name.trim(),
        middle_name: editForm.middle_name?.trim() || null,
        last_name: editForm.last_name.trim(),
        preferred_name: editForm.preferred_name?.trim() || null,
        address_line1: editForm.address_line1?.trim() || null,
        address_line2: editForm.address_line2?.trim() || null,
        city: editForm.city?.trim() || null,
        state: editForm.state?.trim() || 'VA',
        postal_code: editForm.postal_code?.trim() || null,
        home_phone: editForm.home_phone?.trim() || null,
        mobile_phone: editForm.mobile_phone?.trim() || null,
        email: editForm.email?.trim() || null,
        location: editForm.location || null,
        notes: editForm.notes?.trim() || null,
        tags: finalTags,
        co_client_name: editForm.co_client_name?.trim() || null,
      };
      const { error } = await supabase.from('clients').update(payload).eq('id', client.id);
      if (error) throw error;
      const addressChanged = payload.address_line1 !== client.address_line1 || payload.city !== client.city
        || payload.state !== client.state || payload.postal_code !== client.postal_code;
      if (addressChanged) geocodeClientAddress(client.id, payload);
      showToast('Client updated successfully!');
      setTagInput('');
      setIsEditing(false);
      onUpdate({ ...client, ...payload });
    } catch (err) {
      setSaveError(err.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = () => { setEditForm({ ...client }); setTagInput(''); setSaveError(''); setIsEditing(true); };
  const cancelEdit = () => { setIsEditing(false); setSaveError(''); setTagInput(''); };
  const toggleSection = (key) => setOpenSections(s => ({ ...s, [key]: !s[key] }));

  const [statusSaving, setStatusSaving] = useState(false);
  const toggleStatus = async () => {
    const nextStatus = client.status === 'inactive' ? 'active' : 'inactive';
    setStatusSaving(true);
    try {
      const { error } = await supabase.from('clients').update({ status: nextStatus }).eq('id', client.id);
      if (error) throw error;
      showToast(`Client marked ${nextStatus}`);
      onUpdate({ ...client, status: nextStatus });
    } catch (err) {
      showToast(err.message || 'Failed to update status', 'error');
    } finally {
      setStatusSaving(false);
    }
  };

  const displayName = [client.first_name, client.middle_name, client.last_name].filter(Boolean).join(' ');
  const initials = `${client.first_name?.[0] || ''}${client.last_name?.[0] || ''}`.toUpperCase();
  const inp = 'w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm';
  const lbl = 'block text-sm font-semibold text-gray-700 mb-1.5';
  const sec = (t) => <h3 className="text-xs font-bold uppercase tracking-widest text-[#2c4f26] mb-3 pb-1 border-b border-[#e8f0e8]">{t}</h3>;
  const fieldLbl = 'text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1';

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
      {/* Breadcrumb + prev/next */}
      <div className="flex items-center justify-between mb-5">
        <nav className="flex items-center gap-1.5 text-sm">
          <button onClick={onBack} className="flex items-center gap-1 text-gray-500 hover:text-[#2c4f26] font-medium transition">
            <ChevronLeft size={15} />{backLabel}
          </button>
          <ChevronRight size={14} className="text-gray-400" />
          <span className="text-gray-900 font-semibold">{displayName}</span>
        </nav>
        <div className="flex items-center gap-1">
          <button onClick={() => onNavigate(prevClient)} disabled={!prevClient} title={prevClient ? `${prevClient.first_name} ${prevClient.last_name}` : ''} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"><ChevronLeft size={16} /></button>
          <button onClick={() => onNavigate(nextClient)} disabled={!nextClient} title={nextClient ? `${nextClient.first_name} ${nextClient.last_name}` : ''} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"><ChevronRight size={16} /></button>
        </div>
      </div>

      {/* ── Profile card (view mode) ──────────────────────────── */}
      {!isEditing && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-4">
          <div className="p-6">
            {/* Name row */}
            <div className="flex flex-col sm:flex-row gap-5 mb-5">
              <div className="shrink-0">
                <div className="w-20 h-20 bg-[#2c4f26] rounded-xl flex items-center justify-center text-white text-3xl font-bold select-none">{initials}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h1 className="text-2xl font-bold text-[#2c4f26] leading-tight">{displayName}</h1>
                    {client.preferred_name && (
                      <p className="text-sm text-gray-500 mt-0.5">Goes by: <span className="font-medium text-gray-700">{client.preferred_name}</span></p>
                    )}
                    {client.co_client_name && (
                      <p className="text-sm text-gray-500 mt-0.5">Co-client: <span className="font-medium text-gray-700">{client.co_client_name}</span></p>
                    )}
                    <div className="flex items-center gap-1.5 mt-2">
                      {client.location && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${client.location === '655' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                          Location {client.location}
                        </span>
                      )}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${client.status === 'inactive' ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700'}`}>
                        {client.status === 'inactive' ? 'Inactive' : 'Active'}
                      </span>
                      {wellskyClientUrl(client.wellsky_patient_id) && (
                        <a href={wellskyClientUrl(client.wellsky_patient_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:underline">
                          View in WellSky <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    <button
                      onClick={toggleStatus}
                      disabled={statusSaving}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition disabled:opacity-50 ${client.status === 'inactive' ? 'text-green-700 bg-green-50 hover:bg-green-100' : 'text-red-700 bg-red-50 hover:bg-red-100'}`}
                    >
                      {statusSaving ? 'Saving…' : client.status === 'inactive' ? 'Activate' : 'Deactivate'}
                    </button>
                    <button onClick={startEdit} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                      <Pencil size={13} /> Edit Profile
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-5 border-t border-gray-100">
              {/* Address */}
              <div>
                <p className={fieldLbl}>Address</p>
                {client.address_line1 ? (
                  <div className="text-sm text-gray-700 space-y-0.5">
                    <p className="font-medium text-[#2c4f26]">{client.address_line1}</p>
                    {client.address_line2 && <p>{client.address_line2}</p>}
                    <p>{[client.city, client.state, client.postal_code].filter(Boolean).join(', ')}</p>
                  </div>
                ) : <p className="text-sm text-gray-400">—</p>}
              </div>

              {/* Contact */}
              <div>
                <p className={fieldLbl}>Contact</p>
                <div className="text-sm space-y-1">
                  {client.home_phone
                    ? <p className="text-gray-700"><span className="text-gray-400 text-xs mr-1.5">HOME</span>{client.home_phone}</p>
                    : <p className="text-gray-400 text-xs">No home phone</p>}
                  {client.mobile_phone
                    ? <p className="text-gray-700"><span className="text-gray-400 text-xs mr-1.5">CELL</span>{client.mobile_phone}</p>
                    : <p className="text-gray-400 text-xs">No mobile phone</p>}
                  {client.email
                    ? <p className="text-gray-600 break-all">{client.email}</p>
                    : <p className="text-gray-400 text-xs">No email</p>}
                </div>
              </div>

              {/* Notes */}
              <div>
                <p className={fieldLbl}>Notes</p>
                {client.notes
                  ? <p className="text-sm text-gray-700 whitespace-pre-wrap">{client.notes}</p>
                  : <p className="text-sm text-gray-400">—</p>}
              </div>
            </div>

            {/* Tags */}
            {(client.tags || []).length > 0 && (
              <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap gap-1.5 items-center">
                <span className="text-xs font-semibold text-gray-500 mr-1">Tags:</span>
                {client.tags.map(tag => (
                  <span key={tag} className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#f0f5f0] text-[#2c4f26]">{tag}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Edit form ─────────────────────────────────────────── */}
      {isEditing && (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6 mb-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-gray-900">Edit Profile</h2>
            <button type="button" onClick={cancelEdit} className="text-gray-400 hover:text-gray-600 transition"><X size={20} /></button>
          </div>
          {saveError && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{saveError}</div>}

          <div>
            {sec('Personal Information')}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={lbl}>First Name <span className="text-red-500">*</span></label><input required className={inp} value={editForm.first_name} onChange={e => set('first_name', e.target.value)} /></div>
              <div><label className={lbl}>Middle Name</label><input className={inp} value={editForm.middle_name || ''} onChange={e => set('middle_name', e.target.value)} /></div>
              <div><label className={lbl}>Last Name <span className="text-red-500">*</span></label><input required className={inp} value={editForm.last_name} onChange={e => set('last_name', e.target.value)} /></div>
              <div><label className={lbl}>Preferred Name</label><input className={inp} value={editForm.preferred_name || ''} onChange={e => set('preferred_name', e.target.value)} /></div>
              <div><label className={lbl}>Co-client (e.g. spouse sharing this profile)</label><input className={inp} value={editForm.co_client_name || ''} onChange={e => set('co_client_name', e.target.value)} /></div>
            </div>
          </div>

          <div>
            {sec('Address')}
            <div className="space-y-4">
              <div><label className={lbl}>Address Line 1</label><input className={inp} value={editForm.address_line1 || ''} onChange={e => set('address_line1', e.target.value)} /></div>
              <div><label className={lbl}>Address Line 2</label><input className={inp} value={editForm.address_line2 || ''} onChange={e => set('address_line2', e.target.value)} /></div>
              <div className="grid grid-cols-5 gap-4">
                <div className="col-span-2"><label className={lbl}>City</label><input className={inp} value={editForm.city || ''} onChange={e => set('city', e.target.value)} /></div>
                <div className="col-span-1"><label className={lbl}>State</label><input className={inp} value={editForm.state || 'VA'} onChange={e => set('state', e.target.value.toUpperCase())} maxLength={2} /></div>
                <div className="col-span-2"><label className={lbl}>Postal Code</label><input className={inp} value={editForm.postal_code || ''} onChange={e => set('postal_code', e.target.value)} /></div>
              </div>
            </div>
          </div>

          <div>
            {sec('Contact Information')}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={lbl}>Home Phone</label><input type="tel" className={inp} value={editForm.home_phone || ''} onChange={e => set('home_phone', fmtPhone(e.target.value))} /></div>
              <div><label className={lbl}>Mobile Phone</label><input type="tel" className={inp} value={editForm.mobile_phone || ''} onChange={e => set('mobile_phone', fmtPhone(e.target.value))} /></div>
              <div className="sm:col-span-2"><label className={lbl}>Email</label><input type="email" className={inp} value={editForm.email || ''} onChange={e => set('email', e.target.value)} /></div>
            </div>
          </div>

          <div>
            {sec('Details')}
            <div className="space-y-4">
              <div>
                <label className={lbl}>Location</label>
                <select className={inp + ' bg-white'} value={editForm.location || ''} onChange={e => set('location', e.target.value)}>
                  <option value="">Select location…</option>
                  <option value="655">655 — Home Instead St. Clair (Lewis &amp; Leigh, Inc.)</option>
                  <option value="507">507 — Home Instead St. Clair (Jac Second, LLC)</option>
                </select>
              </div>
              <div><label className={lbl}>Notes</label><textarea rows={3} className={inp + ' resize-none'} value={editForm.notes || ''} onChange={e => set('notes', e.target.value)} /></div>
              <div>
                <label className={lbl}>Profile Tags</label>
                <div className="border border-gray-200 rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-[#2c4f26] focus-within:border-transparent transition min-h-[48px] flex flex-wrap gap-1.5 items-center">
                  {(editForm.tags || []).map(tag => (
                    <span key={tag} className="inline-flex items-center gap-1 bg-[#f0f5f0] text-[#2c4f26] text-xs font-semibold px-2.5 py-1 rounded-full">
                      {tag}<button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500 transition ml-0.5"><X size={11} /></button>
                    </span>
                  ))}
                  <input className="flex-1 min-w-[120px] outline-none text-sm py-1 bg-transparent" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={handleTagKeyDown} onBlur={() => { if (tagInput.trim()) addTag(tagInput); }} placeholder={(editForm.tags || []).length === 0 ? 'Type a tag and press Enter…' : 'Add another…'} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={cancelEdit} className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition">Cancel</button>
            <button type="button" onClick={handleSave} disabled={isSaving} className="px-6 py-2.5 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-xl transition disabled:opacity-50 flex items-center gap-2">
              {isSaving ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</> : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* ── Accordion sections ────────────────────────────────── */}
      {!isEditing && (
        <div className="space-y-2">
          {[
            { key: 'calendar', label: 'Shift Calendar', icon: <Clock size={16} /> },
            { key: 'activity', label: 'Activity', icon: <ClipboardList size={16} /> },
            { key: 'files', label: 'Files', icon: <FileText size={16} /> },
          ].map(({ key, label, icon }) => (
            <div key={key} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleSection(key)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition text-left"
              >
                <div className="flex items-center gap-2.5 text-sm font-semibold text-gray-700">
                  <span className="text-gray-400">{icon}</span>
                  {label}
                </div>
                <ChevronRight size={16} className={`text-gray-400 transition-transform duration-200 ${openSections[key] ? 'rotate-90' : ''}`} />
              </button>
              {openSections[key] && (
                key === 'calendar'
                  ? <div className="border-t border-gray-100"><ShiftCalendarSection clientId={client.id} wellskyClientId={client.wellsky_patient_id} showToast={showToast} /></div>
                  : <div className="border-t border-gray-100 px-5 py-8 text-center text-gray-400 text-sm">{label} coming soon</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CLIENTS_PAGE_SIZES = [100, 250, 500, 'all'];

const ClientsTab = ({ showToast }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [locCounts, setLocCounts] = useState({ '655': 0, '507': 0 });
  const [statusCounts, setStatusCounts] = useState({ active: 0, inactive: 0 });
  const [selectedClient, setSelectedClient] = useState(null);
  const [wellskyLastSyncedAt, setWellskyLastSyncedAt] = useState(null);

  // Debounce the search box so we don't fire a query on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Jump back to page 1 whenever the search, location filter, status filter, or page size changes.
  useEffect(() => { setPage(0); }, [debouncedSearch, locationFilter, statusFilter, pageSize]);

  useEffect(() => { loadClients(); }, [debouncedSearch, locationFilter, statusFilter, pageSize, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadLocationCounts(); loadStatusCounts(); }, []);
  useEffect(() => {
    // Not every client here comes from WellSky, and the incremental sync
    // only touches records WellSky reports as recently changed -- so this
    // reads the sync job's own last-run marker rather than any per-client
    // timestamp, which would go stale on unchanged rows.
    supabase.from('wellsky_sync_status').select('last_run_at').eq('id', 1).maybeSingle()
      .then(({ data }) => setWellskyLastSyncedAt(data?.last_run_at || null));
  }, []);

  const buildQuery = () => {
    // WellSky is the sole source of truth for clients now -- legacy rows
    // predating the sync (no wellsky_patient_id) are left in the database
    // but excluded here rather than deleted.
    let q = supabase.from('clients').select('*', { count: 'exact' }).not('wellsky_patient_id', 'is', null).order('last_name');
    if (locationFilter !== 'all') {
      q = q.eq('location', locationFilter);
    }
    if (statusFilter !== 'all') {
      q = q.eq('status', statusFilter);
    }
    if (debouncedSearch) {
      const term = debouncedSearch.replace(/%|_/g, ' ').trim();
      if (term) {
        const orParts = [
          orIlike('first_name', term),
          orIlike('last_name', term),
          orIlike('preferred_name', term),
          orIlike('co_client_name', term),
          orIlike('email', term),
          orIlike('mobile_phone', term),
          orIlike('home_phone', term),
          orIlike('work_phone', term),
        ];
        // Digits-only match against generated columns so a fragment like
        // "038507" matches "(703) 850-7642" even though those digits aren't
        // contiguous in the punctuated value.
        const digits = term.replace(/\D/g, '');
        if (digits.length >= 3) {
          orParts.push(orIlike('mobile_phone_digits', digits));
          orParts.push(orIlike('home_phone_digits', digits));
          orParts.push(orIlike('work_phone_digits', digits));
        }
        q = q.or(orParts.join(','));
      }
    }
    return q;
  };

  const loadClients = async () => {
    setLoading(true);
    try {
      if (pageSize === 'all') {
        // Supabase/PostgREST caps a single request at 1000 rows, so page
        // through in chunks even when the user asked to see "All".
        const chunk = 1000;
        let all = [];
        let from = 0;
        let total = 0;
        for (;;) {
          const { data, error, count } = await buildQuery().range(from, from + chunk - 1);
          if (error) throw error;
          if (typeof count === 'number') total = count;
          all = all.concat(data || []);
          if (!data || data.length < chunk) break;
          from += chunk;
        }
        setClients(all);
        setTotalCount(total);
      } else {
        const from = page * pageSize;
        const to = from + pageSize - 1;
        const { data, error, count } = await buildQuery().range(from, to);
        if (error) throw error;
        setClients(data || []);
        setTotalCount(count || 0);
      }
    } catch (err) {
      console.error('Error loading clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLocationCounts = async () => {
    try {
      const [r655, r507] = await Promise.all([
        supabase.from('clients').select('*', { count: 'exact', head: true }).not('wellsky_patient_id', 'is', null).eq('status', 'active').eq('location', '655'),
        supabase.from('clients').select('*', { count: 'exact', head: true }).not('wellsky_patient_id', 'is', null).eq('status', 'active').eq('location', '507'),
      ]);
      setLocCounts({ '655': r655.count || 0, '507': r507.count || 0 });
    } catch (err) {
      console.error('Error loading location counts:', err);
    }
  };

  const loadStatusCounts = async () => {
    try {
      const [rActive, rInactive] = await Promise.all([
        supabase.from('clients').select('*', { count: 'exact', head: true }).not('wellsky_patient_id', 'is', null).eq('status', 'active'),
        supabase.from('clients').select('*', { count: 'exact', head: true }).not('wellsky_patient_id', 'is', null).eq('status', 'inactive'),
      ]);
      setStatusCounts({ active: rActive.count || 0, inactive: rInactive.count || 0 });
    } catch (err) {
      console.error('Error loading status counts:', err);
    }
  };

  if (selectedClient) {
    return (
      <ClientDetailView
        client={selectedClient}
        allClients={clients}
        onBack={() => setSelectedClient(null)}
        onNavigate={(c) => setSelectedClient(c)}
        onUpdate={(updated) => { setSelectedClient(updated); loadClients(); loadStatusCounts(); }}
        showToast={showToast}
      />
    );
  }

  const getInitials = (c) => `${c.first_name?.[0] || ''}${c.last_name?.[0] || ''}`.toUpperCase();
  const fullName = (c) => {
    const name = [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(' ');
    return c.preferred_name ? `${name} (${c.preferred_name})` : name;
  };

  // True grand total (active + inactive, unfiltered) -- NOT totalCount,
  // which is scoped to whatever status/location/search filter is currently
  // active and is only meant for pagination math below. The stat cards
  // (Total Clients, Location 655/507, No Location) are deliberately
  // active-only -- locCounts is already scoped to status='active', and
  // activeTotalClients mirrors that same scope rather than active+inactive.
  const activeTotalClients = statusCounts.active;
  const noLoc = Math.max(0, activeTotalClients - locCounts['655'] - locCounts['507']);
  const rangeStart = totalCount === 0 ? 0 : page * (pageSize === 'all' ? totalCount : pageSize) + 1;
  const rangeEnd = pageSize === 'all' ? totalCount : Math.min((page + 1) * pageSize, totalCount);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Clients</h1>
          <p className="text-sm text-gray-600">{statusCounts.active} active · {statusCounts.inactive} inactive · {locCounts['655']} at 655 · {locCounts['507']} at 507</p>
          <p className="text-xs text-gray-400 mt-0.5">WellSky sync runs every 15 minutes{wellskyLastSyncedAt ? ` · last ran ${hiringFmtSyncAgo(wellskyLastSyncedAt)}` : ''}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs text-gray-500 mb-1">Active Clients</p>
          <p className="text-2xl font-bold text-[#2c4f26]">{activeTotalClients}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs text-gray-500 mb-1">Location 655</p>
          <p className="text-2xl font-bold text-blue-600">{locCounts['655']}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs text-gray-500 mb-1">Location 507</p>
          <p className="text-2xl font-bold text-purple-600">{locCounts['507']}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs text-gray-500 mb-1">No Location</p>
          <p className="text-2xl font-bold text-gray-400">{noLoc}</p>
        </div>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
              placeholder="Search by name, email, or phone…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 shrink-0">
            {[['active', 'Active'], ['inactive', 'Inactive'], ['all', 'All']].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setStatusFilter(val)}
                className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${statusFilter === val ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 shrink-0">
            {[['all', 'All'], ['655', '655'], ['507', '507']].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setLocationFilter(val)}
                className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${locationFilter === val ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500 hidden sm:inline">Show</span>
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
              {CLIENTS_PAGE_SIZES.map(size => (
                <button
                  key={size}
                  onClick={() => setPageSize(size)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition ${pageSize === size ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  {size === 'all' ? 'All' : size}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" />
          </div>
        ) : clients.length === 0 ? (
          <div className="p-16 text-center text-gray-500">
            <Users size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">
              {searchTerm || locationFilter !== 'all' || statusFilter !== 'all' ? 'No clients match your filters' : 'No clients synced from WellSky yet'}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Client</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Location</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Contact</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Tags</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {clients.map(c => (
                    <tr key={c.id} onClick={() => setSelectedClient(c)} className="hover:bg-gray-50 transition cursor-pointer">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-[#2c4f26] rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0">
                            {getInitials(c)}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="font-semibold text-gray-900 text-sm">{fullName(c)}</p>
                              {c.status === 'inactive' && (
                                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-600">Inactive</span>
                              )}
                            </div>
                            {c.co_client_name && <p className="text-xs text-gray-500">with {c.co_client_name}</p>}
                            {c.address_line1 && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                {c.address_line1}{c.city ? `, ${c.city}` : ''}{c.state ? `, ${c.state}` : ''}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {c.location
                          ? <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${c.location === '655' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{c.location}</span>
                          : <span className="text-xs text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-0.5 text-xs text-gray-700">
                          {c.mobile_phone && <p>M: {c.mobile_phone}</p>}
                          {c.home_phone && <p>H: {c.home_phone}</p>}
                          {c.email && <p className="text-gray-500">{c.email}</p>}
                          {!c.mobile_phone && !c.home_phone && !c.email && <span className="text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(c.tags || []).length > 0
                            ? c.tags.map(tag => <span key={tag} className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#f0f5f0] text-[#2c4f26]">{tag}</span>)
                            : <span className="text-xs text-gray-400">—</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {clients.map(c => (
                <div key={c.id} onClick={() => setSelectedClient(c)} className="p-4 hover:bg-gray-50 transition cursor-pointer">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#2c4f26] rounded-full flex items-center justify-center text-white font-bold shrink-0">
                      {getInitials(c)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <p className="font-semibold text-gray-900 text-sm">{fullName(c)}</p>
                          {c.status === 'inactive' && (
                            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-600">Inactive</span>
                          )}
                        </div>
                        {c.location && (
                          <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${c.location === '655' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {c.location}
                          </span>
                        )}
                      </div>
                      {c.co_client_name && <p className="text-xs text-gray-500">with {c.co_client_name}</p>}
                      {c.address_line1 && <p className="text-xs text-gray-500 mt-0.5">{c.address_line1}{c.city ? `, ${c.city}` : ''}</p>}
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-gray-600">
                        {c.mobile_phone && <p>M: {c.mobile_phone}</p>}
                        {c.home_phone && <p>H: {c.home_phone}</p>}
                      </div>
                      {(c.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {c.tags.map(tag => <span key={tag} className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#f0f5f0] text-[#2c4f26]">{tag}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination footer */}
        {!loading && pageSize !== 'all' && totalCount > 0 && (
          <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              Showing {rangeStart}–{rangeEnd} of {totalCount}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page + 1 >= totalPages}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Caregivers (read-only, synced from WellSky Practitioners) ────────────────
// WellSky is the source of truth for this data -- this app never writes it
// back, so there's no add/edit form here, just list/search/detail display.

// Confirmed live from the user's own WellSky login -- hisc655 is this
// agency's fixed app subdomain (matches the WELLSKY_APP_NAME secret), the
// same for both offices, not something per-location.
const WELLSKY_APP_URL = 'https://hisc655.clearcareonline.com';
const wellskyClientUrl = (wellskyPatientId) => wellskyPatientId ? `${WELLSKY_APP_URL}/clients/${wellskyPatientId}/` : null;
const wellskyCaregiverUrl = (wellskyPractitionerId) => wellskyPractitionerId ? `${WELLSKY_APP_URL}/caregivers/${wellskyPractitionerId}/` : null;

const WellskyCaregiversTab = ({ showToast }) => {
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selected, setSelected] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    // wellsky_sync_status, not max(synced_at) on this table -- the
    // incremental sync only touches caregivers WellSky reports as recently
    // updated, so an unchanged record's synced_at goes stale and can't
    // answer "is the sync job itself still running."
    supabase.from('wellsky_sync_status').select('last_run_at').eq('id', 1).maybeSingle()
      .then(({ data }) => setLastSyncedAt(data?.last_run_at || null));
  }, []);

  const loadCaregivers = async () => {
    setLoading(true);
    try {
      let q = supabase.from('wellsky_caregivers').select('*').order('last_name');
      if (statusFilter !== 'all') q = q.eq('active', statusFilter === 'active');
      if (debouncedSearch) {
        const term = debouncedSearch.replace(/%|_/g, ' ').trim();
        if (term) q = q.or([orIlike('first_name', term), orIlike('last_name', term), orIlike('email', term), orIlike('mobile_phone', term), orIlike('home_phone', term)].join(','));
      }
      const { data, error } = await q;
      if (error) throw error;
      setCaregivers(data || []);
    } catch (err) {
      console.error('Error loading caregivers:', err);
      showToast('Failed to load caregivers', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadCaregivers(); }, [debouncedSearch, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const getInitials = (c) => `${c.first_name?.[0] || ''}${c.last_name?.[0] || ''}`.toUpperCase();
  const fullName = (c) => `${c.first_name} ${c.last_name}`;

  if (selected) {
    const c = selected;
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#2c4f26] font-medium transition mb-5">
          <ChevronLeft size={15} /> Care Pros
        </button>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex gap-5 mb-5">
            <div className="w-20 h-20 bg-[#2c4f26] rounded-xl flex items-center justify-center text-white text-3xl font-bold shrink-0">{getInitials(c)}</div>
            <div>
              <h1 className="text-2xl font-bold text-[#2c4f26] leading-tight">{fullName(c)}</h1>
              <span className={`inline-flex items-center mt-2 px-2.5 py-0.5 rounded-full text-xs font-semibold ${c.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {c.active ? 'Active' : 'Inactive'}
              </span>
              {c.location && (
                <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${c.location === '655' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                  Location {c.location}
                </span>
              )}
              {c.hire_status && c.hire_status !== '0' && <span className="ml-2 text-xs text-gray-500">Status: {c.hire_status}</span>}
              {wellskyCaregiverUrl(c.wellsky_practitioner_id) && (
                <a href={wellskyCaregiverUrl(c.wellsky_practitioner_id)} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:underline align-middle">
                  View in WellSky <ExternalLink size={11} />
                </a>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-5 border-t border-gray-100">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Address</p>
              {c.address_line1 ? (
                <div className="text-sm text-gray-700 space-y-0.5">
                  <p className="font-medium text-[#2c4f26]">{c.address_line1}</p>
                  <p>{[c.city, c.state, c.postal_code].filter(Boolean).join(', ')}</p>
                </div>
              ) : <p className="text-sm text-gray-400">—</p>}
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Contact</p>
              <div className="text-sm space-y-1">
                {c.mobile_phone ? <p className="text-gray-700"><span className="text-gray-400 text-xs mr-1.5">CELL</span>{c.mobile_phone}</p> : <p className="text-gray-400 text-xs">No mobile phone</p>}
                {c.home_phone ? <p className="text-gray-700"><span className="text-gray-400 text-xs mr-1.5">HOME</span>{c.home_phone}</p> : <p className="text-gray-400 text-xs">No home phone</p>}
                {c.email ? <p className="text-gray-600 break-all">{c.email}</p> : <p className="text-gray-400 text-xs">No email</p>}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Employment</p>
              <div className="text-sm space-y-1 text-gray-700">
                {c.birth_date && <p>DOB: {c.birth_date}</p>}
                {c.gender && <p>Gender: {c.gender}</p>}
                {c.preferred_language && <p>Language: {c.preferred_language}</p>}
                {c.hired_date && <p>Hired: {c.hired_date}</p>}
                {c.eligible_to_rehire && <p>Eligible to rehire: {c.eligible_to_rehire}</p>}
                {c.external_id && <p>Payroll ID: {c.external_id}</p>}
              </div>
            </div>
          </div>
          {(c.deactivated_date || c.deactivation_reason) && (
            <div className="pt-4 mt-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Deactivation</p>
              <div className="text-sm space-y-1 text-gray-700">
                {c.deactivated_date && <p>Date: {c.deactivated_date}</p>}
                {c.deactivation_reason && <p>Reason: {c.deactivation_reason}</p>}
              </div>
            </div>
          )}
          {c.notes && (
            <div className="pt-4 mt-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Notes</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{c.notes}</p>
            </div>
          )}
          <div className="pt-4 mt-4 border-t border-gray-100 -mx-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 px-6">WellSky Visits</p>
            <WellskyVisitsSection caregiverId={c.wellsky_practitioner_id} />
          </div>
          <p className="text-xs text-gray-400 mt-5 pt-4 border-t border-gray-100">
            Synced from WellSky{c.synced_at ? ` · last synced ${hiringFmtSyncAgo(c.synced_at)}` : ''}. This record is read-only here — edit in WellSky.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Care Pros</h1>
        <p className="text-sm text-gray-600">{caregivers.length} shown · synced from WellSky every 15 minutes{lastSyncedAt ? ` · last synced ${hiringFmtSyncAgo(lastSyncedAt)}` : ''}, read-only</p>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
              placeholder="Search by name, email, or phone…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 shrink-0">
            {[['active', 'Active'], ['inactive', 'Inactive'], ['all', 'All']].map(([val, label]) => (
              <button key={val} onClick={() => setStatusFilter(val)} className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${statusFilter === val ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="p-16 text-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" /></div>
        ) : caregivers.length === 0 ? (
          <div className="p-16 text-center text-gray-500">
            <Users size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No caregivers match your filters</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {caregivers.map(c => (
              <div key={c.id} onClick={() => setSelected(c)} className="p-4 flex items-center gap-3 hover:bg-gray-50 transition cursor-pointer">
                <div className="w-9 h-9 bg-[#2c4f26] rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0">{getInitials(c)}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm">{fullName(c)}</p>
                  <p className="text-xs text-gray-500">{[c.city, c.state].filter(Boolean).join(', ')}</p>
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${c.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {c.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Action Templates ─────────────────────────────────────────────────────────
// Replaces the old single-purpose Incident Reports workflow (and before
// that, the email-reply-chain process where two people replying around the
// same time could lose edits or duplicate work). Each submission is an
// instance of some template_type -- only Termination of Services exists so
// far, more will be added one at a time. Type-specific field values live in
// action_templates.fields (jsonb), not one column per type, so adding a new
// template type is a frontend change here, not a migration.
//
// Concurrency: a submission is saved as a 'draft' the instant someone picks
// a template type -- visible immediately in the Open list -- specifically
// so a second person can see "someone's already handling this" instead of
// two people independently emailing about the same situation. Each field
// then saves independently through update_action_template_field (an atomic
// jsonb_set RPC), so two people editing different fields on the same draft
// can never clobber each other's write, same spirit as the old incident
// report's per-section saves.
// Pre-fills the Shipping Address field (type: 'address', an object of
// street/city/state/zip) on an Office Supply Request when the "Home
// Instead Office" checkbox is ticked -- see the officeAddressToggle field
// flag below and where it's handled in the field renderer.
const ACTION_TEMPLATE_OFFICE_ADDRESS = { street: '9309 Center St, Suite 104', city: 'Manassas', state: 'Virginia', zip: '20110' };

const ACTION_TEMPLATE_TYPES = [
  {
    type: 'termination_of_services',
    label: 'Termination of Services',
    description: 'Document a client service termination.',
    fields: [
      { key: 'reason_for_termination', label: 'Reason for termination', type: 'textarea', required: true },
      { key: 'effective_date', label: 'Effective date', type: 'date', required: true },
      { key: 'location', label: 'Location', type: 'text', required: true },
      { key: 'who_followed_up', label: 'Who followed up?', type: 'text' },
      { key: 'response_outcome', label: 'Response/outcome', type: 'textarea' },
      { key: 'charged_7_days_notice', label: "Charged 7 days’ notice", type: 'yesno' },
      { key: 'care_pros_notified_reassigned', label: 'Are Care Pros notified/reassigned?', type: 'yesno' },
      { key: 'additional_notes', label: 'Additional Notes', type: 'textarea' },
    ],
  },
  {
    type: 'office_supply_request',
    label: 'Office Supply Request',
    description: 'Request a product be ordered and shipped.',
    // Not about a client, and the requester is just whoever's creating it
    // (already on the record as created_by) -- no Client field at all for
    // this type.
    requiresClient: false,
    fields: [
      { key: 'product_link', label: 'Product Link', type: 'text', required: true },
      { key: 'quantity', label: 'Quantity', type: 'number', required: true },
      { key: 'shipping_address', label: 'Shipping Address', type: 'address', required: true, officeAddressToggle: true },
    ],
  },
  {
    type: 'hospital_rehab_discharge',
    label: 'Hospital/Rehab Discharge',
    description: 'Document a client discharged from a hospital or rehab facility.',
    fields: [
      { key: 'date_discharged', label: 'Date Discharged', type: 'date', required: true },
      { key: 'discharged_to', label: 'Discharged to?', type: 'text', required: true },
      { key: 'is_va_client_additional_services', label: 'Is the client a VA client receiving additional services in Hospital or Rehab?', type: 'yesno' },
    ],
  },
  {
    type: 'hospital_rehab_admission',
    label: 'Hospital/Rehab Admission',
    description: 'Document a client admitted to a hospital or rehab facility.',
    fields: [
      { key: 'date_admitted', label: 'Date Admitted', type: 'date', required: true },
      { key: 'hospital_room', label: 'Which hospital/rehab? Room number?', type: 'text', required: true },
      { key: 'reason_for_admission', label: 'Reason for admission', type: 'textarea' },
      { key: 'is_va_private_pay', label: 'Is this a VA client that is requesting private pay services during their admission?', type: 'yesno' },
      { key: 'requires_incident_report', label: 'Does this require an Incident Report?', type: 'yesno' },
      { key: 'client_on_hold', label: 'Has the Client been placed on hold?', type: 'yesno' },
      { key: 'follow_up_date', label: 'Follow up date - for staffing', type: 'followup' },
      { key: 'permanent_care_pros', label: 'Who were the permanent Care Pros?', type: 'caregivers' },
      { key: 'date_discharged', label: 'Date Discharged', type: 'date' },
      { key: 'other_notes', label: 'Other notes', type: 'textarea' },
    ],
  },
  {
    type: 'new_client_welcome',
    label: 'New Client Welcome',
    description: 'Document a new client coming on service.',
    fields: [
      { key: 'inquiry_taken_by', label: 'Who took inquiry/booked Care Consult?', type: 'text' },
      { key: 'referred_by', label: 'Who referred the client?', type: 'text' },
      // 'location' -- same key the Termination of Services auto-fill (see
      // selectClient) already looks for generically, so picking an
      // existing WellSky client here fills this in too when it's on file.
      { key: 'location', label: '655 or 507?', type: 'select', options: ['655', '507'] },
      { key: 'city', label: 'City', type: 'text' },
      { key: 'service_type', label: 'PC or C/HH?', type: 'select', options: ['PC', 'C/HH'] },
      { key: 'start_date', label: 'When do they start?', type: 'date' },
      { key: 'schedule', label: 'What is their schedule?', type: 'textarea' },
      { key: 'schedule_in_wellsky', label: 'Schedule in WellSky?', type: 'yesno' },
      { key: 'pets', label: 'Pets?', type: 'text' },
      { key: 'smoker', label: 'Smoker?', type: 'yesno' },
      { key: 'ao', label: 'Who is the AO?', type: 'text' },
      { key: 'other_notes', label: 'Other notes', type: 'textarea' },
    ],
  },
];
const actionTemplateTypeConfig = (type) => ACTION_TEMPLATE_TYPES.find(t => t.type === type);

// Generation rules, one conditional block per templateType. Returns []
// until the fields that block actually needs are filled in (so an
// incomplete draft doesn't propose a half-written task), and freezes once
// non-empty -- the effect calling this only recomputes while outcomes is
// still empty (see its own comment), by design: once generated, an
// outcome is a staff-editable proposal, not a live mirror of the form.
// ctx.staffUsers resolves a rule's named assignees (full_name match) to
// real ids; ctx.requesterName fills in "who asked for this" for template
// types with no Client field (see requiresClient on the type config).
const computeGeneratedOutcomes = (templateType, clientName, fieldValues, ctx = {}) => {
  const { requesterName, staffUsers = [] } = ctx;
  const findStaffByFirstName = (first) => staffUsers.find(s => (s.full_name || '').toLowerCase().startsWith(first.toLowerCase()))?.id;

  if (templateType === 'office_supply_request') {
    const productLink = fieldValues.product_link;
    const quantity = fieldValues.quantity;
    const addr = fieldValues.shipping_address;
    const addressComplete = addr && addr.street?.trim() && addr.city?.trim() && addr.state?.trim() && addr.zip?.trim();
    if (!productLink || !quantity || !addressComplete) return [];
    const assigneeIds = [findStaffByFirstName('Jack'), findStaffByFirstName('Rebecca')].filter(Boolean);
    if (assigneeIds.length === 0) return [];
    const addressLine = `${addr.street}, ${addr.city}, ${addr.state} ${addr.zip}`;
    return [{
      id: 'office-supply-task',
      type: 'task',
      title: `Office Supply Request - ${requesterName || 'Unknown'}`,
      detail: `Product Link: ${productLink}\nQuantity: ${quantity}\nShipping Address: ${addressLine}`,
      assigneeIds,
      dueInDays: 7,
    }];
  }

  if (templateType === 'hospital_rehab_discharge') {
    const dateDischarged = fieldValues.date_discharged;
    const dischargedTo = fieldValues.discharged_to;
    if (!clientName || !dateDischarged || !dischargedTo) return [];
    const nursingAssigneeIds = [findStaffByFirstName('Roxanna')].filter(Boolean);
    // An absolute date (discharge date + N days), not a relative dueInDays
    // -- these have a real anchor (when the client was actually
    // discharged), unlike office_supply_request which has no natural
    // field to anchor to and so counts from whenever it's published
    // instead.
    const addDays = (dateStr, days) => new Date(new Date(`${dateStr}T00:00:00`).getTime() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const dischargeDateLabel = new Date(`${dateDischarged}T00:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    const results = [];
    if (nursingAssigneeIds.length > 0) {
      results.push({
        id: 'hospital-rehab-nursing-reassessment',
        type: 'task',
        title: `Nursing Reassessment - ${clientName}`,
        detail: `${clientName} has been discharged on ${dischargeDateLabel} to ${dischargedTo}.\n\nPlease store completed reassessment documentation on client's WellSky profile.`,
        assigneeIds: nursingAssigneeIds,
        dueDate: addDays(dateDischarged, 7),
        priority: 'urgent',
      });
    }

    // Only for a VA client who was getting additional services in the
    // hospital/rehab -- their WellSky billing rate needs switching back
    // once they're discharged.
    if (fieldValues.is_va_client_additional_services === true) {
      const staffingIds = ['Latisha', 'Christina', 'Jessica', 'Lulibo', 'Nikkie', 'Yolandi', 'Emanuelle']
        .map(findStaffByFirstName).filter(Boolean);
      if (staffingIds.length > 0) {
        results.push({
          id: 'hospital-rehab-va-rates-change',
          type: 'task',
          title: `${clientName} - WellSky VA Rates Change`,
          detail: `Change ${clientName}'s WellSky billing rate back to the VA rate.`,
          assigneeIds: staffingIds,
          dueDate: addDays(dateDischarged, 1),
        });
      }
    }

    return results;
  }

  if (templateType === 'hospital_rehab_admission') {
    const dateAdmitted = fieldValues.date_admitted;
    const hospitalRoom = fieldValues.hospital_room;
    if (!clientName || !dateAdmitted) return [];

    // Reason for admission and Other notes aren't needed to trigger any
    // one outcome, but once filled in they're relevant context for every
    // task this template generates -- appended to each one's detail below.
    const extraNotes = [];
    if (fieldValues.reason_for_admission) extraNotes.push(`Reason for admission: ${fieldValues.reason_for_admission}`);
    if (fieldValues.other_notes) extraNotes.push(`Other notes: ${fieldValues.other_notes}`);
    const notesSuffix = extraNotes.length > 0 ? `\n\n${extraNotes.join('\n')}` : '';

    const carePros = fieldValues.permanent_care_pros;
    const carePerosLine = Array.isArray(carePros) && carePros.length > 0
      ? `\n\nPossible affected CarePros: ${carePros.map(c => c.name).join(', ')}`
      : '';

    const results = [];
    const staffingIds = ['Latisha', 'Christina', 'Jessica', 'Lulibo', 'Nikkie', 'Yolandi', 'Emanuelle']
      .map(findStaffByFirstName).filter(Boolean);
    if (staffingIds.length > 0) {
      results.push({
        id: 'hospital-rehab-admission-notify-care-pros',
        type: 'task',
        title: `${clientName} - Hospital/Rehab Admission - Notify Affected Care Pros`,
        detail: `Please notify the Care Pros affected by this client admission.${carePerosLine}${notesSuffix}`,
        assigneeIds: staffingIds,
        dueInDays: 1,
      });
      results.push({
        id: 'hospital-rehab-admission-review-schedule',
        type: 'task',
        title: `${clientName} - Hospital/Rehab Admission - Review and Adjust Schedule`,
        detail: `Review ${clientName}'s admission and determine if a schedule change is necessary due to the Hospital/Rehab admission.${notesSuffix}`,
        assigneeIds: staffingIds,
        dueInDays: 1,
      });
      // VA client requesting private pay during their admission -- their
      // WellSky rate needs updating, anchored to Date Admitted (not
      // publish time) same as the other date-anchored staffing tasks.
      if (fieldValues.is_va_private_pay === true) {
        const addDays = (dateStr, days) => new Date(new Date(`${dateStr}T00:00:00`).getTime() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
        results.push({
          id: 'hospital-rehab-admission-va-private-pay',
          type: 'task',
          title: `${clientName} - Hospital/Rehab Admission - VA Additional Services`,
          detail: `${clientName} has requested private pay services for their hospital/rehab admission, please update their rates in WellSky.${notesSuffix}`,
          assigneeIds: staffingIds,
          dueDate: addDays(dateAdmitted, 1),
        });
      }
    }

    if (hospitalRoom) {
      const ianId = findStaffByFirstName('Ian');
      if (ianId) {
        results.push({
          id: 'hospital-rehab-admission-facility-visit',
          type: 'task',
          title: `${clientName} - Hospital/Rehab Admission - Consider a Facility Visit`,
          detail: `${clientName} has been admitted to ${hospitalRoom}. Please consider a facility visit!${notesSuffix}`,
          assigneeIds: [ianId],
          dueInDays: 7,
        });
      }
      const gailId = findStaffByFirstName('Gail');
      if (gailId) {
        results.push({
          id: 'hospital-rehab-admission-get-well-card',
          type: 'task',
          title: `${clientName} - Hospital/Rehab Admission - Get Well Soon Card!`,
          detail: `${clientName} has been admitted to ${hospitalRoom}. Please send them a get well soon card.${notesSuffix}`,
          assigneeIds: [gailId],
          dueInDays: 7,
        });
      }
    }

    const followUp = fieldValues.follow_up_date;
    if (followUp?.date && staffingIds.length > 0) {
      // Recurrence is suppressed outright if Date Discharged is already
      // known at generation time -- the more common case (discharge
      // happens after this template is already published, with the
      // recurring task already live on the board) is handled separately,
      // see the date_discharged effect below that reaches into the real
      // todos row.
      const stillActive = !fieldValues.date_discharged;
      results.push({
        id: 'hospital-rehab-admission-followup-call',
        type: 'task',
        title: `${clientName} - Hospitalization / Rehab Follow up call`,
        detail: `${clientName} - Hospitalization / Rehab Follow up call${notesSuffix}`,
        assigneeIds: staffingIds,
        dueDate: followUp.date,
        recurrence: (followUp.recurring && stillActive) ? (followUp.recurrence || 'weekly') : null,
        recurrenceInterval: followUp.recurring && followUp.recurrence === 'weekly' ? (followUp.recurrence_interval || 1) : 1,
      });
    }

    return results;
  }

  return [];
};

const actionTemplateFieldValueLabel = (field, raw) => {
  if (field.type === 'address') {
    if (!raw) return '';
    const cityState = [raw.city, raw.state].filter(Boolean).join(', ');
    return [raw.street, [cityState, raw.zip].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
  }
  if (field.type === 'caregivers') return (Array.isArray(raw) ? raw : []).map(c => c.name).join(', ');
  if (field.type === 'followup') {
    if (!raw?.date) return '';
    const dateLabel = new Date(raw.date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    return raw.recurring ? `${dateLabel} (repeats ${todoRecurrenceLabel(raw.recurrence || 'weekly', raw.recurrence_interval || 1)})` : dateLabel;
  }
  if (raw === undefined || raw === null || raw === '') return '';
  if (field.type === 'yesno') return raw === true ? 'Yes' : 'No';
  if (field.type === 'date') return new Date(raw + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  return String(raw);
};

const NewTemplateTypeModal = ({ onClose, onPick }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900">New Template</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
      </div>
      <p className="text-xs text-gray-500 mb-4">Choose a template type to get started.</p>
      <div className="space-y-2">
        {ACTION_TEMPLATE_TYPES.map(t => (
          <button key={t.type} onClick={() => onPick(t.type)} className="w-full text-left p-4 border border-gray-200 rounded-xl hover:border-[#2c4f26] hover:bg-[#f0f5f0] transition">
            <p className="font-semibold text-gray-900 text-sm">{t.label}</p>
            {t.description && <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const ActionTemplateDetail = ({ template, profile, showToast, onBack, onUpdate, onDeleted }) => {
  const typeConfig = actionTemplateTypeConfig(template.template_type);
  const [clientName, setClientName] = useState(template.client_name || '');
  const [clientWellskyPatientId, setClientWellskyPatientId] = useState(template.client_wellsky_patient_id || null);
  const [wellskyClients, setWellskyClients] = useState([]);
  const [wellskyCaregivers, setWellskyCaregivers] = useState([]);
  const [fieldValues, setFieldValues] = useState(() => ({ ...(template.fields || {}) }));
  const [savingField, setSavingField] = useState(null);
  const [outcomes, setOutcomes] = useState(template.generated_outcomes || []);
  const [publishing, setPublishing] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notifyRecipients, setNotifyRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(true);
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState(null); // null = not loaded yet
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditEntries, setAuditEntries] = useState(null); // null = not loaded yet
  const [loadingAuditLog, setLoadingAuditLog] = useState(false);
  const [genOutcomeStaff, setGenOutcomeStaff] = useState([]);

  // Active staff roster, used only to resolve a generation rule's named
  // assignees (e.g. office_supply_request's "Jack and Rebecca") to real
  // ids by full_name match -- see computeGeneratedOutcomes.
  useEffect(() => {
    supabase.from('profiles').select('id, full_name').in('role', ['owner', 'admin_staff']).eq('status', 'active')
      .then(({ data }) => setGenOutcomeStaff(data || []));
  }, []);

  // Backs the Client field's ClientNamePicker -- the already-synced local
  // clients table (wellsky-sync refreshes it every 5 min), not a live
  // WellSky API search, so suggestions are instant. Skipped entirely for
  // a template type with no Client field at all.
  useEffect(() => {
    if (typeConfig?.requiresClient === false) return;
    supabase.from('clients').select('id, first_name, last_name, wellsky_patient_id, location')
      .then(({ data }) => setWellskyClients(data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Backs any 'caregivers' field (e.g. Hospital/Rehab Admission's "Who were
  // the permanent Care Pros?") the same way -- the already-synced local
  // wellsky_caregivers table, not a live API search. Only fetched when the
  // type actually has a field of that kind.
  useEffect(() => {
    if (!(typeConfig?.fields || []).some(f => f.type === 'caregivers')) return;
    supabase.from('wellsky_caregivers').select('id, first_name, last_name, wellsky_practitioner_id')
      .then(({ data }) => setWellskyCaregivers(data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Who publishing this template type will actually email -- configured
  // per template_type in action_template_settings (see the Settings page),
  // not per individual template, so this loads off the type alone and
  // works even before a new template has ever been saved. Action Templates
  // only ever emails (action-template-notify) -- there's no SMS leg to
  // this feature, so there's nothing SMS-related to show here.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingRecipients(true);
      const { data: settings } = await supabase.from('action_template_settings').select('recipient_user_ids').eq('template_type', template.template_type).maybeSingle();
      const ids = settings?.recipient_user_ids || [];
      if (ids.length === 0) {
        if (!cancelled) { setNotifyRecipients([]); setLoadingRecipients(false); }
        return;
      }
      const { data: people } = await supabase.from('profiles').select('id, full_name').in('id', ids);
      if (!cancelled) { setNotifyRecipients(people || []); setLoadingRecipients(false); }
    })();
    return () => { cancelled = true; };
  }, [template.template_type]);

  // Never persisted -- picking a template type just opens this form
  // locally. Nothing is saved to the database (and so nothing shows up as
  // an orphaned draft) until Save as Draft or Publish is clicked; clicking
  // Back just discards it.
  const isNew = !template.id;
  const isDraft = template.status === 'draft';
  const isEditable = isNew || isDraft;
  // Data-entry fields (Client + typeConfig.fields) stay editable even once
  // published -- a lot of this data (discharge dates, follow-up schedules,
  // outcomes of a visit) is only known well after the original template
  // went out. Only archived (a deliberate "done, locked" action) is
  // actually read-only. The Generated Outcomes panel's own manual
  // add/edit/remove controls are a separate, narrower concern and stay
  // gated to isEditable (draft-only) -- see the reconciliation effect
  // below for how a field edit here propagates to an already-materialized
  // task instead.
  const fieldsEditable = template.status !== 'archived';
  const isOwner = profile?.role === 'owner';

  // Every write path below sets this optimistically on the merged local
  // row -- the real updated_at/updated_by land in the DB via the trigger
  // (or the update_action_template_field RPC for per-field saves), but
  // none of those writes read a fresh row back, so without this the
  // displayed "Updated by/at" would go stale until the next full reload.
  const optimisticUpdateMeta = () => ({
    updated_at: new Date().toISOString(), updated_by: profile?.id || null,
    updater: profile ? { full_name: profile.full_name } : null,
  });

  const saveField = async (key, value) => {
    if (isNew) return;
    setSavingField(key);
    try {
      const { error } = await supabase.rpc('update_action_template_field', { p_id: template.id, p_key: key, p_value: value });
      if (error) throw error;
      onUpdate({ ...template, fields: { ...fieldValues, [key]: value }, client_name: clientName, ...optimisticUpdateMeta() });
    } catch (err) {
      showToast(err.message || 'Failed to save', 'error');
    } finally {
      setSavingField(null);
    }
  };

  // Overrides let selectClient (below) pass the just-picked name/id
  // straight through instead of reading clientName/clientWellskyPatientId
  // from this closure -- those two setState calls haven't actually
  // applied yet in the same tick a click handler calls this in.
  const saveClientName = async (overrideName, overridePatientId) => {
    if (isNew) return;
    try {
      const trimmed = overrideName !== undefined ? overrideName : (clientName.trim() || null);
      const patientId = overridePatientId !== undefined ? overridePatientId : clientWellskyPatientId;
      const { error } = await supabase.from('action_templates').update({ client_name: trimmed, client_wellsky_patient_id: patientId, updated_by: profile?.id || null }).eq('id', template.id);
      if (error) throw error;
      onUpdate({ ...template, client_name: trimmed, client_wellsky_patient_id: patientId, fields: fieldValues, ...optimisticUpdateMeta() });
    } catch (err) {
      showToast(err.message || 'Failed to save', 'error');
    }
  };

  // Typing invalidates whatever was previously picked (free text is no
  // longer guaranteed to be that client) -- selecting a suggestion is the
  // only way clientWellskyPatientId gets set again.
  const handleClientNameChange = (text) => {
    setClientName(text);
    setClientWellskyPatientId(null);
  };
  const selectClient = (c) => {
    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
    const patientId = c.wellsky_patient_id || null;
    setClientName(name);
    setClientWellskyPatientId(patientId);
    saveClientName(name, patientId);
    // Auto-fills a 'location' field (currently just Termination of
    // Services' office 655/507) from the picked client's own WellSky
    // location tag -- only when the type actually has that field, and
    // only when WellSky actually has it on file for them (most synced
    // clients don't, per the live data).
    const hasLocationField = (typeConfig?.fields || []).some(f => f.key === 'location');
    if (hasLocationField && c.location) {
      setFieldValues(v => ({ ...v, location: c.location }));
      saveField('location', c.location);
    }
  };

  // Persists the whole generated_outcomes array on an already-saved draft
  // (a brand-new, never-saved one has nothing to persist to yet -- it all
  // goes in via saveAsDraft's own insert) -- so removing/restoring/editing
  // an outcome sticks if you navigate away before publishing, the same way
  // field edits already autosave. One column, so a whole-array write
  // rather than the per-field RPC fields itself uses.
  const saveOutcomes = async (next) => {
    if (isNew) return;
    try {
      const { error } = await supabase.from('action_templates').update({ generated_outcomes: next, updated_by: profile?.id || null }).eq('id', template.id);
      if (error) throw error;
      onUpdate({ ...template, generated_outcomes: next, ...optimisticUpdateMeta() });
    } catch (err) {
      showToast(err.message || 'Failed to save', 'error');
    }
  };

  const saveAsDraft = async () => {
    setSavingDraft(true);
    try {
      const { data, error } = await supabase.from('action_templates').insert({
        template_type: template.template_type, status: 'draft',
        client_name: clientName.trim() || null, client_wellsky_patient_id: clientWellskyPatientId, fields: fieldValues, generated_outcomes: outcomes,
        created_by: profile?.id || null, updated_by: profile?.id || null,
      }).select('*, creator:created_by(full_name), updater:updated_by(full_name)').single();
      if (error) throw error;
      showToast('Saved as draft');
      onUpdate(data);
    } catch (err) {
      showToast(err.message || 'Failed to save draft', 'error');
    } finally {
      setSavingDraft(false);
    }
  };

  const deleteTemplate = async () => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from('action_templates').delete().eq('id', template.id);
      if (error) throw error;
      showToast('Draft deleted');
      onDeleted(template.id);
    } catch (err) {
      showToast(err.message || 'Failed to delete draft', 'error');
      setDeleting(false);
    }
  };

  // Recomputes from current field values whenever they change. Adds
  // whichever of the rule's outcomes aren't already present (matched by
  // id) -- not an all-or-nothing freeze on the whole array. A rule type
  // can define more than one possible outcome gated on different fields
  // (e.g. hospital_rehab_discharge's VA rates task only appears once that
  // checkbox is Yes, which commonly happens after the nursing task's own
  // required fields are already filled in and generated) -- freezing the
  // instant anything existed would mean a later-satisfied outcome could
  // never show up.
  //
  // Also keeps an already-generated outcome's title/detail/assignees/due
  // date live-synced to the fields it was built from -- so correcting a
  // typo in Discharged to?, say, actually updates the task (and so the
  // publish-time email, which is just whatever the outcome's content is
  // at that moment) instead of silently publishing stale text. Stops
  // touching a given outcome the moment it's removed or manually edited
  // (see editOutcome/editOutcomeAndSave's edited flag) -- it becomes
  // staff-owned at that point, same as before.
  // Pre-publish only (status 'open' is handled entirely by the
  // reconciliation effect below, which also needs to reach real todos
  // rows and isn't just a local-state merge) -- local-state-only merge,
  // nothing to reconcile against yet since nothing's been created.
  useEffect(() => {
    if (template.status === 'open') return;
    const computed = computeGeneratedOutcomes(template.template_type, clientName, fieldValues, { requesterName: profile?.full_name, staffUsers: genOutcomeStaff });
    const computedById = new Map(computed.map(c => [c.id, c]));
    setOutcomes(prev => {
      const existingIds = new Set(prev.map(o => o.id));
      const toAdd = computed.filter(c => !existingIds.has(c.id));
      let changed = toAdd.length > 0;
      const updated = prev.map(o => {
        if (o.removed || o.edited) return o;
        const fresh = computedById.get(o.id);
        if (!fresh) return o;
        const merged = { ...o, ...fresh };
        if (JSON.stringify(merged) !== JSON.stringify(o)) changed = true;
        return merged;
      });
      return changed ? [...updated, ...toAdd] : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.status, clientName, fieldValues, genOutcomeStaff]);

  // A boolean-toggle-driven outcome (Hospital/Rehab Discharge's VA rates
  // task, gated on its VA checkbox) retracts outright when the toggle
  // flips back off -- deletes the real Task Board task if it's already
  // been materialized (todo_assignees cascades with it), not just a
  // local soft-remove, per explicit request. The outcome is dropped from
  // the array entirely rather than flagged removed -- there's nothing to
  // Undo back to, since the task itself is actually gone. If the toggle
  // flips back on later, the reconciliation effect below creates a
  // brand-new task from scratch (same id, since it's no longer present
  // in the array to match against). Kept separate from the generation
  // effect above on purpose: that one only ever adds (clearing a plain
  // required field mid-edit, e.g. retyping Quantity on
  // office_supply_request, shouldn't auto-remove an outcome someone just
  // has to finish typing) -- a real on/off toggle is a different, more
  // deliberate kind of "no" than a momentarily-empty field.
  useEffect(() => {
    if (template.template_type !== 'hospital_rehab_discharge') return;
    if (fieldValues.is_va_client_additional_services === true) return;
    const target = outcomes.find(o => o.id === 'hospital-rehab-va-rates-change' && !o.removed);
    if (!target) return;
    (async () => {
      if (target.todo_id) await supabase.from('todos').delete().eq('id', target.todo_id);
      const next = outcomes.filter(o => o.id !== target.id);
      setOutcomes(next);
      saveOutcomes(next);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues.is_va_client_additional_services]);

  // Same pattern as the Hospital/Rehab Discharge VA-toggle effect above --
  // Hospital/Rehab Admission's VA Additional Services task retracts
  // outright, including deleting the real Task Board task, when the
  // answer flips back to No.
  useEffect(() => {
    if (template.template_type !== 'hospital_rehab_admission') return;
    if (fieldValues.is_va_private_pay === true) return;
    const target = outcomes.find(o => o.id === 'hospital-rehab-admission-va-private-pay' && !o.removed);
    if (!target) return;
    (async () => {
      if (target.todo_id) await supabase.from('todos').delete().eq('id', target.todo_id);
      const next = outcomes.filter(o => o.id !== target.id);
      setOutcomes(next);
      saveOutcomes(next);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues.is_va_private_pay]);

  // Keeps a PUBLISHED template's real Task Board tasks in sync with its
  // fields -- fields stay editable after publish (discharge dates,
  // follow-up schedules, etc. are often only known well after the
  // original template went out), and editing one that feeds an
  // already-materialized task needs to actually change that real task,
  // not just the template's own record of what it generated. A newly
  // satisfied rule (e.g. a trigger field filled in for the first time
  // after publish) gets materialized exactly like at publish time
  // (new todos row + 'assigned' notification). An already-materialized
  // one whose computed content changed gets its real todos row updated,
  // its assignees diffed (added ones get 'assigned'), and a standard
  // "task updated" notification goes out -- the same send-todo-notification
  // 'updated' event a manual edit on the Task Board fires, so assignees
  // see this exactly like any other task update. Removed/manually-edited
  // outcomes are left alone, same staff-ownership rule as everywhere else.
  // reconcilingRef guards against two overlapping runs (e.g. two fields
  // edited in quick succession) both deciding to materialize the same new
  // outcome before the first one's insert has landed.
  const reconcilingRef = useRef(false);
  useEffect(() => {
    if (template.status !== 'open') return;
    if (reconcilingRef.current) return;
    const computed = computeGeneratedOutcomes(template.template_type, clientName, fieldValues, { requesterName: profile?.full_name, staffUsers: genOutcomeStaff });
    if (computed.length === 0) return;
    const hasWork = computed.some(fresh => {
      const existing = outcomes.find(o => o.id === fresh.id);
      if (existing?.removed || existing?.edited) return false;
      if (!existing) return true;
      if (!existing.todo_id) return false;
      return JSON.stringify({ ...existing, ...fresh, todo_id: existing.todo_id }) !== JSON.stringify(existing);
    });
    if (!hasWork) return;

    reconcilingRef.current = true;
    (async () => {
      const sourceNote = `This task was automatically created by the "${typeConfig?.label || template.template_type}" Action Template #${template.template_number}.`;
      const next = [...outcomes];
      let changed = false;
      for (const fresh of computed) {
        const idx = next.findIndex(o => o.id === fresh.id);
        const existing = idx >= 0 ? next[idx] : null;
        if (existing?.removed || existing?.edited) continue;

        if (!existing) {
          if (fresh.type !== 'task' || !fresh.assigneeIds?.length) { next.push(fresh); changed = true; continue; }
          const dueDate = fresh.dueDate || (fresh.dueInDays ? new Date(Date.now() + fresh.dueInDays * 24 * 60 * 60 * 1000).toISOString().slice(0, 10) : null);
          const { data: todo, error } = await supabase.from('todos').insert({
            title: fresh.title, description: fresh.detail || null, priority: fresh.priority || 'medium', created_by: profile?.id || null, due_date: dueDate, wellsky_patient_id: clientWellskyPatientId,
            recurrence: fresh.recurrence || null, recurrence_interval: fresh.recurrence === 'weekly' ? (fresh.recurrenceInterval || 1) : 1,
          }).select('id').single();
          if (error) { console.error(error); next.push(fresh); changed = true; continue; }
          await supabase.from('todo_assignees').insert(fresh.assigneeIds.map(user_id => ({ todo_id: todo.id, user_id })));
          supabase.functions.invoke('send-todo-notification', { body: { type: 'assigned', todoId: todo.id, newAssigneeIds: fresh.assigneeIds, sourceNote } }).catch(() => {});
          next.push({ ...fresh, todo_id: todo.id });
          changed = true;
          continue;
        }

        if (!existing.todo_id) continue;
        const merged = { ...existing, ...fresh, todo_id: existing.todo_id };
        if (JSON.stringify(merged) === JSON.stringify(existing)) continue;

        const dueDate = merged.dueDate || (merged.dueInDays ? new Date(Date.now() + merged.dueInDays * 24 * 60 * 60 * 1000).toISOString().slice(0, 10) : null);
        await supabase.from('todos').update({
          title: merged.title, description: merged.detail || null, priority: merged.priority || 'medium', due_date: dueDate, wellsky_patient_id: clientWellskyPatientId,
          recurrence: merged.recurrence || null, recurrence_interval: merged.recurrence === 'weekly' ? (merged.recurrenceInterval || 1) : 1,
        }).eq('id', existing.todo_id);

        const prevAssignees = new Set(existing.assigneeIds || []);
        const freshAssignees = new Set(merged.assigneeIds || []);
        const added = (merged.assigneeIds || []).filter(id => !prevAssignees.has(id));
        const droppedIds = (existing.assigneeIds || []).filter(id => !freshAssignees.has(id));
        if (added.length > 0) await supabase.from('todo_assignees').insert(added.map(user_id => ({ todo_id: existing.todo_id, user_id })));
        if (droppedIds.length > 0) await supabase.from('todo_assignees').delete().eq('todo_id', existing.todo_id).in('user_id', droppedIds);
        if (added.length > 0) supabase.functions.invoke('send-todo-notification', { body: { type: 'assigned', todoId: existing.todo_id, newAssigneeIds: added, sourceNote } }).catch(() => {});
        supabase.functions.invoke('send-todo-notification', { body: { type: 'updated', todoId: existing.todo_id } }).catch(() => {});

        next[idx] = merged;
        changed = true;
      }
      if (changed) {
        setOutcomes(next);
        saveOutcomes(next);
      }
      reconcilingRef.current = false;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.status, clientName, fieldValues, genOutcomeStaff, outcomes]);

  // Soft-delete, not a real removal from the array -- "removed" outcomes
  // stay in generated_outcomes (skipped at publish, see publish() below)
  // so they can be undone before publishing and so the Timeline/Audit Log
  // still has a record that one was proposed and then pulled.
  const removeOutcome = (id) => {
    const outcome = outcomes.find(o => o.id === id);
    if (!outcome) return;
    const label = outcome.type === 'email' ? 'this email notification' : (outcome.title || 'this task');
    if (!window.confirm(`Remove ${label}? You can undo this before publishing.`)) return;
    const next = outcomes.map(o => o.id === id ? { ...o, removed: true } : o);
    setOutcomes(next);
    saveOutcomes(next);
  };
  const undoRemoveOutcome = (id) => {
    const next = outcomes.map(o => o.id === id ? { ...o, removed: false } : o);
    setOutcomes(next);
    saveOutcomes(next);
  };
  // Only offered for a manually-added outcome (id prefixed "manual-" --
  // see addOutcome), once it's already in the removed state. A
  // rule-generated outcome (e.g. office_supply_request's default task)
  // stays soft-delete-only forever, so there's always a record that the
  // system proposed it, even if staff pulled it.
  const permanentlyDeleteOutcome = (id) => {
    if (!window.confirm('Permanently delete this outcome? This cannot be undone.')) return;
    const next = outcomes.filter(o => o.id !== id);
    setOutcomes(next);
    saveOutcomes(next);
  };
  // edited: true marks this outcome as staff-owned from here on -- the
  // generation effect below keeps a rule-generated outcome's title/detail/
  // assignees/due date live-synced to the current field values right up
  // until the first manual edit, at which point it stops touching that
  // outcome at all (never silently overwrites something staff just typed).
  const editOutcome = (id, patch) => setOutcomes(prev => prev.map(o => o.id === id ? { ...o, ...patch, edited: true } : o));
  // Same-shape edit, but also persists immediately -- for discrete actions
  // (a picker click, a number input) rather than continuous typing (which
  // persists on blur instead, like every other field in this form).
  const editOutcomeAndSave = (id, patch) => {
    const next = outcomes.map(o => o.id === id ? { ...o, ...patch, edited: true } : o);
    setOutcomes(next);
    saveOutcomes(next);
  };
  // An editable template's generated outcomes default to a compact,
  // read-only display -- opening the title/detail textareas, assignee
  // picker, and due-days input for every outcome up front made simple
  // single-line titles unreadable. Editing is opt-in per outcome.
  const [editingOutcomeIds, setEditingOutcomeIds] = useState(() => new Set());
  const toggleOutcomeEditing = (id) => setEditingOutcomeIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const [showAddOutcomeMenu, setShowAddOutcomeMenu] = useState(false);
  const addOutcome = (type) => {
    const id = `manual-${Date.now()}`;
    const base = type === 'task'
      ? { id, type: 'task', title: '', detail: '', assigneeIds: [], dueInDays: null }
      : { id, type: 'email', recipientIds: [], message: '' };
    const next = [...outcomes, base];
    setOutcomes(next);
    setEditingOutcomeIds(prev => new Set(prev).add(id));
    setShowAddOutcomeMenu(false);
    saveOutcomes(next);
  };

  // fieldValues[f.key] === false is a real, valid "No" answer on a yesno
  // field -- only undefined/empty should count as missing, not falsy. An
  // 'address' field's value is an object, not a scalar, so it needs its
  // own completeness check rather than the undefined/null/'' test below.
  const isFieldEmpty = (v) => v === undefined || v === null || v === '';
  const isAddressFieldEmpty = (v) => !v || !v.street?.trim() || !v.city?.trim() || !v.state?.trim() || !v.zip?.trim();
  const missingRequired = (typeConfig?.requiresClient !== false && !clientName.trim()) || (typeConfig?.fields || []).some(f => {
    if (!f.required) return false;
    return f.type === 'address' ? isAddressFieldEmpty(fieldValues[f.key]) : isFieldEmpty(fieldValues[f.key]);
  });

  const publish = async () => {
    if (missingRequired) { showToast('Fill out all required fields first', 'error'); return; }
    setPublishing(true);
    try {
      const publishedFields = {
        status: 'open', client_name: clientName.trim() || null, client_wellsky_patient_id: clientWellskyPatientId, fields: fieldValues, generated_outcomes: outcomes,
        published_at: new Date().toISOString(), published_by: profile?.id || null, updated_by: profile?.id || null,
      };
      // A never-saved draft publishes in one step (insert straight to
      // 'open'); an already-saved draft just updates. Either way we read
      // the real row back, since a fresh insert does not yet have a
      // template_number client-side.
      const { data: savedTemplate, error } = isNew
        ? await supabase.from('action_templates').insert({ template_type: template.template_type, created_by: profile?.id || null, ...publishedFields }).select('*, creator:created_by(full_name), updater:updated_by(full_name)').single()
        : await supabase.from('action_templates').update(publishedFields).eq('id', template.id).select('*, creator:created_by(full_name), updater:updated_by(full_name)').single();
      if (error) throw error;

      // Materializes any non-removed task-type generated outcome as a real
      // Task Board task, assigned to everyone in outcome.assigneeIds. The
      // resulting todo_id is written back onto the outcome (and persisted)
      // so the Timeline tab can later look up that task's live
      // status/completion -- without this the link to the task this
      // template created would be thrown away right after creating it.
      // Due date is outcome.dueDate (an absolute date already resolved by
      // the rule, e.g. hospital_rehab_discharge's discharge-date + 7 days)
      // if set, else outcome.dueInDays resolved relative to right now --
      // computed here, at actual creation time, not back when the draft
      // was first filled out, so a dueInDays-based due date doesn't
      // silently drift if the draft sat around before being published. A
      // removed outcome is left exactly as-is (still flagged removed) --
      // it never gets materialized. The assigned-task notification gets a
      // note identifying this as Action-Template-generated, not a person
      // manually assigning it. clientWellskyPatientId (set by the Client
      // field's ClientNamePicker -- null for a type with no Client field,
      // or when the typed name wasn't actually picked from the list)
      // carries straight onto the task so it can link to the same WellSky
      // profile with no separate matching step.
      let outcomesChanged = false;
      const enrichedOutcomes = [];
      const sourceNote = `This task was automatically created by the "${typeConfig?.label || savedTemplate.template_type}" Action Template #${savedTemplate.template_number}.`;
      for (const outcome of outcomes) {
        if (outcome.removed || outcome.type !== 'task' || !outcome.assigneeIds?.length) { enrichedOutcomes.push(outcome); continue; }
        const dueDate = outcome.dueDate || (outcome.dueInDays ? new Date(Date.now() + outcome.dueInDays * 24 * 60 * 60 * 1000).toISOString().slice(0, 10) : null);
        const { data: todo, error: todoError } = await supabase.from('todos').insert({
          title: outcome.title, description: outcome.detail || null, priority: outcome.priority || 'medium', created_by: profile?.id || null, due_date: dueDate, wellsky_patient_id: clientWellskyPatientId,
          recurrence: outcome.recurrence || null, recurrence_interval: outcome.recurrence === 'weekly' ? (outcome.recurrenceInterval || 1) : 1,
        }).select('id').single();
        if (todoError) { console.error(todoError); enrichedOutcomes.push(outcome); continue; }
        await supabase.from('todo_assignees').insert(outcome.assigneeIds.map(user_id => ({ todo_id: todo.id, user_id })));
        supabase.functions.invoke('send-todo-notification', { body: { type: 'assigned', todoId: todo.id, newAssigneeIds: outcome.assigneeIds, sourceNote } }).catch(() => {});
        enrichedOutcomes.push({ ...outcome, todo_id: todo.id });
        outcomesChanged = true;
      }
      if (outcomesChanged) {
        await supabase.from('action_templates').update({ generated_outcomes: enrichedOutcomes }).eq('id', savedTemplate.id);
        savedTemplate.generated_outcomes = enrichedOutcomes;
      }

      // One-off email outcomes -- distinct from action_template_settings'
      // per-type default recipients, these are ad-hoc: whoever was picked
      // on just this template, with just this custom message.
      const adHocEmails = enrichedOutcomes
        .filter(o => o.type === 'email' && !o.removed && o.recipientIds?.length > 0)
        .map(o => ({ recipientUserIds: o.recipientIds, message: o.message || '' }));

      const { data: { session } } = await supabase.auth.getSession();
      fetch(`${SUPABASE_URL}/functions/v1/action-template-notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({
          templateId: savedTemplate.id,
          templateType: savedTemplate.template_type,
          templateLabel: typeConfig?.label || savedTemplate.template_type,
          templateNumber: savedTemplate.template_number,
          clientName,
          rows: (typeConfig?.fields || []).map(f => ({ label: f.label, value: actionTemplateFieldValueLabel(f, fieldValues[f.key]) })),
          outcomes: enrichedOutcomes.filter(o => o.type === 'task' && !o.removed).map(o => ({ title: o.title, detail: o.detail })),
          adHocEmails,
        }),
      }).catch(() => {});

      showToast('Template published');
      onUpdate(savedTemplate);
    } catch (err) {
      showToast(err.message || 'Failed to publish', 'error');
    } finally {
      setPublishing(false);
    }
  };

  const archive = async () => {
    if (!window.confirm('Archive this template? Archive is only for fully resolved, finalized reports.')) return;
    setArchiving(true);
    try {
      const { error } = await supabase.from('action_templates').update({ status: 'archived', updated_by: profile?.id || null }).eq('id', template.id);
      if (error) throw error;
      showToast('Archived');
      onUpdate({ ...template, status: 'archived', ...optimisticUpdateMeta() });
    } catch (err) {
      showToast(err.message || 'Failed to archive', 'error');
    } finally {
      setArchiving(false);
    }
  };

  const reopen = async () => {
    try {
      const { error } = await supabase.from('action_templates').update({ status: 'open', updated_by: profile?.id || null }).eq('id', template.id);
      if (error) throw error;
      showToast('Reopened');
      onUpdate({ ...template, status: 'open', ...optimisticUpdateMeta() });
    } catch (err) {
      showToast(err.message || 'Failed to reopen', 'error');
    }
  };

  // Timeline: a handful of curated milestones -- created, saved as draft
  // (only the one time it first happened, not every autosave), published,
  // any generated task's live status, and archived (this system's actual
  // "fully resolved, finalized" state -- see the Archive confirm text --
  // which doubles as the Completed milestone). All derived from this
  // template's own activity_log history rather than tracked separately;
  // the Audit Log tab below shows that same history in full, unfiltered.
  const loadTimeline = async () => {
    if (isNew || timelineEvents !== null) return;
    setLoadingTimeline(true);
    const { data: logRows } = await supabase.from('activity_log').select('*, profile:user_id(full_name)')
      .eq('entity_type', 'action_templates').eq('entity_id', template.id).order('created_at', { ascending: true });
    const rows = logRows || [];
    const events = [];

    const firstRow = rows.find(r => r.action === 'INSERT');
    if (firstRow) {
      events.push({ key: 'created', label: 'Created', at: firstRow.created_at, who: firstRow.profile?.full_name });
      if (firstRow.metadata?.new?.status === 'draft') {
        events.push({ key: 'draft', label: 'Saved as draft', at: firstRow.created_at, who: firstRow.profile?.full_name });
      }
    } else {
      // Predates this trigger -- fall back to the row's own columns
      // rather than showing nothing.
      events.push({ key: 'created', label: 'Created', at: template.created_at, who: template.creator?.full_name });
      if (template.status === 'draft') {
        events.push({ key: 'draft', label: 'Saved as draft', at: template.created_at, who: template.creator?.full_name });
      }
    }

    const publishRow = rows.find(r => r.metadata?.new?.status === 'open' && r.metadata?.old?.status !== 'open');
    if (publishRow) {
      events.push({ key: 'published', label: 'Published', at: publishRow.created_at, who: publishRow.profile?.full_name });
    } else if (template.published_at) {
      events.push({ key: 'published', label: 'Published', at: template.published_at, who: template.creator?.full_name });
    }

    const archiveRow = [...rows].reverse().find(r => r.metadata?.new?.status === 'archived' && r.metadata?.old?.status !== 'archived');
    if (archiveRow) {
      events.push({ key: 'archived', label: 'Completed (Archived)', at: archiveRow.created_at, who: archiveRow.profile?.full_name });
    }

    // Only ever populated once generation rules exist for a template type
    // (see computeGeneratedOutcomes) -- commonly empty today.
    const taskOutcomes = (template.generated_outcomes || outcomes).filter(o => o.type === 'task' && o.todo_id);
    if (taskOutcomes.length > 0) {
      const { data: todosData } = await supabase.from('todos').select('id, title, status, completed_at').in('id', taskOutcomes.map(o => o.todo_id));
      (todosData || []).forEach(t => {
        const statusLabel = t.status === 'done' ? 'Completed' : t.status === 'in_progress' ? 'In Progress' : 'Not Started';
        events.push({ key: `task-${t.id}`, label: `Task "${t.title}" — ${statusLabel}`, at: t.completed_at || null, who: null });
      });
    }

    events.sort((a, b) => {
      if (!a.at && !b.at) return 0;
      if (!a.at) return 1;
      if (!b.at) return -1;
      return new Date(a.at) - new Date(b.at);
    });
    setTimelineEvents(events);
    setLoadingTimeline(false);
  };
  const toggleTimeline = () => { const next = !showTimeline; setShowTimeline(next); if (next) loadTimeline(); };

  // Audit Log: every INSERT/UPDATE/DELETE on this template, unfiltered --
  // including every field edit/addition to the form (fields jsonb diffed
  // key-by-key via auditDiffParts, same helper the global owner-only Audit
  // Log page uses).
  const loadAuditLog = async () => {
    if (isNew || auditEntries !== null) return;
    setLoadingAuditLog(true);
    const { data } = await supabase.from('activity_log').select('*, profile:user_id(full_name)')
      .eq('entity_type', 'action_templates').eq('entity_id', template.id).order('created_at', { ascending: false });
    setAuditEntries(data || []);
    setLoadingAuditLog(false);
  };
  const toggleAuditLog = () => { const next = !showAuditLog; setShowAuditLog(next); if (next) loadAuditLog(); };
  const [auditExpanded, setAuditExpanded] = useState(() => new Set());
  const toggleAuditExpanded = (id) => setAuditExpanded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const inp = 'w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm disabled:bg-gray-50 disabled:text-gray-500';
  const lbl = 'block text-xs font-semibold text-gray-600 mb-1';

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#2c4f26] font-medium transition mb-5">
        <ChevronLeft size={15} /> Action Templates
      </button>

      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {typeConfig?.label || template.template_type}{template.template_number ? ` #${template.template_number}` : ''}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            {isNew && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">Unsaved</span>}
            {template.status === 'draft' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Draft</span>}
            {template.status === 'open' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Open</span>}
            {template.status === 'archived' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-600">Archived</span>}
          </div>
          {template.created_at && (
            <p className="text-xs text-gray-400 mt-1">
              Created {todoFmtDateTime(template.created_at)} by {template.creator?.full_name || 'Unknown'} · Updated {todoFmtDateTime(template.updated_at)} by {template.updater?.full_name || template.creator?.full_name || 'Unknown'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isDraft && (
            <button onClick={deleteTemplate} disabled={deleting} className="px-4 py-2 text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition disabled:opacity-50">
              {deleting ? 'Deleting…' : 'Delete Draft'}
            </button>
          )}
          {template.status === 'open' && (
            <button onClick={archive} disabled={archiving} className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition disabled:opacity-50">
              {archiving ? 'Archiving…' : 'Archive'}
            </button>
          )}
          {template.status === 'archived' && (
            <button onClick={reopen} className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition">Reopen</button>
          )}
          {isNew && (
            <button onClick={saveAsDraft} disabled={savingDraft || publishing} className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition disabled:opacity-50">
              {savingDraft ? 'Saving…' : 'Save as Draft'}
            </button>
          )}
          {isEditable && (
            <button onClick={publish} disabled={publishing || savingDraft} className="px-5 py-2 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50">
              {publishing ? 'Publishing…' : 'Publish'}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          {typeConfig?.requiresClient !== false && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={`${lbl} mb-0`}>Client <span className="text-red-500">*</span></label>
                {clientWellskyPatientId && wellskyClientUrl(clientWellskyPatientId) && (
                  <a href={wellskyClientUrl(clientWellskyPatientId)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:underline">
                    View in WellSky <ExternalLink size={11} />
                  </a>
                )}
              </div>
              <ClientNamePicker
                value={clientName}
                onChangeText={handleClientNameChange}
                onSelectClient={selectClient}
                onBlur={() => saveClientName()}
                clients={wellskyClients}
                disabled={!fieldsEditable}
              />
            </div>
          )}
          {(typeConfig?.fields || []).map(f => {
            const isOfficeAddress = f.type === 'address' && ['street', 'city', 'state', 'zip'].every(p => (fieldValues[f.key]?.[p] || '') === ACTION_TEMPLATE_OFFICE_ADDRESS[p]);
            const setAddressPart = (part, value) => setFieldValues(v => ({ ...v, [f.key]: { ...(v[f.key] || {}), [part]: value } }));
            const saveAddress = () => saveField(f.key, fieldValues[f.key] || {});
            const fieldEditable = fieldsEditable;
            return (
            <div key={f.key}>
              <div className="flex items-center justify-between mb-1">
                <label className={`${lbl} mb-0`}>{f.label} {f.required && <span className="text-red-500">*</span>}</label>
                {savingField === f.key && <span className="text-[10px] text-gray-400">Saving…</span>}
              </div>
              {f.officeAddressToggle && (
                <label className="flex items-center gap-2 mb-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    disabled={!fieldEditable}
                    checked={isOfficeAddress}
                    onChange={e => {
                      const v = e.target.checked ? { ...ACTION_TEMPLATE_OFFICE_ADDRESS } : { street: '', city: '', state: '', zip: '' };
                      setFieldValues(val => ({ ...val, [f.key]: v }));
                      saveField(f.key, v);
                    }}
                  />
                  <span className="text-xs font-medium text-gray-700">Home Instead Office</span>
                </label>
              )}
              {f.type === 'address' ? (
                <div className="space-y-2">
                  <input placeholder="Street address" className={inp} disabled={!fieldEditable}
                    value={fieldValues[f.key]?.street || ''}
                    onChange={e => setAddressPart('street', e.target.value)}
                    onBlur={() => saveAddress()} />
                  <div className="grid grid-cols-6 gap-2">
                    <input placeholder="City" className={`${inp} col-span-3`} disabled={!fieldEditable}
                      value={fieldValues[f.key]?.city || ''}
                      onChange={e => setAddressPart('city', e.target.value)}
                      onBlur={() => saveAddress()} />
                    <input placeholder="State" className={`${inp} col-span-2`} disabled={!fieldEditable}
                      value={fieldValues[f.key]?.state || ''}
                      onChange={e => setAddressPart('state', e.target.value)}
                      onBlur={() => saveAddress()} />
                    <input placeholder="ZIP" className={`${inp} col-span-1`} disabled={!fieldEditable}
                      value={fieldValues[f.key]?.zip || ''}
                      onChange={e => setAddressPart('zip', e.target.value)}
                      onBlur={() => saveAddress()} />
                  </div>
                </div>
              ) : f.type === 'textarea' ? (
                <textarea rows={3} className={`${inp} resize-y`} disabled={!fieldEditable} value={fieldValues[f.key] || ''}
                  onChange={e => setFieldValues(v => ({ ...v, [f.key]: e.target.value }))}
                  onBlur={e => saveField(f.key, e.target.value)} />
              ) : f.type === 'yesno' ? (
                <select className={inp} disabled={!fieldEditable} value={fieldValues[f.key] === true ? 'yes' : fieldValues[f.key] === false ? 'no' : ''}
                  onChange={e => { const v = e.target.value === 'yes'; setFieldValues(val => ({ ...val, [f.key]: v })); saveField(f.key, v); }}>
                  <option value="">—</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              ) : f.type === 'select' ? (
                <select className={inp} disabled={!fieldEditable} value={fieldValues[f.key] || ''}
                  onChange={e => { setFieldValues(val => ({ ...val, [f.key]: e.target.value })); saveField(f.key, e.target.value); }}>
                  <option value="">—</option>
                  {(f.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : f.type === 'followup' ? (
                <div className="space-y-2">
                  <input type="date" className={inp} disabled={!fieldEditable}
                    value={fieldValues[f.key]?.date || ''}
                    onChange={e => {
                      const next = { ...(fieldValues[f.key] || {}), date: e.target.value };
                      setFieldValues(v => ({ ...v, [f.key]: next }));
                    }}
                    onBlur={() => saveField(f.key, fieldValues[f.key] || {})} />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" disabled={!fieldEditable}
                      checked={!!fieldValues[f.key]?.recurring}
                      onChange={e => {
                        const next = { ...(fieldValues[f.key] || {}), recurring: e.target.checked, recurrence: fieldValues[f.key]?.recurrence || 'weekly', recurrence_interval: fieldValues[f.key]?.recurrence_interval || 1 };
                        setFieldValues(v => ({ ...v, [f.key]: next }));
                        saveField(f.key, next);
                      }} />
                    <span className="text-xs font-medium text-gray-700">Recurring follow-up</span>
                  </label>
                  {fieldValues[f.key]?.recurring && (
                    <div className="flex gap-2 pl-6">
                      <select className={`${inp} w-40`} disabled={!fieldEditable}
                        value={fieldValues[f.key]?.recurrence || 'weekly'}
                        onChange={e => {
                          const next = { ...(fieldValues[f.key] || {}), recurrence: e.target.value };
                          setFieldValues(v => ({ ...v, [f.key]: next }));
                          saveField(f.key, next);
                        }}>
                        {TODO_RECURRENCE_OPTIONS.filter(o => o.value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      {fieldValues[f.key]?.recurrence === 'weekly' && (
                        <select className={`${inp} w-32`} disabled={!fieldEditable}
                          value={fieldValues[f.key]?.recurrence_interval || 1}
                          onChange={e => {
                            const next = { ...(fieldValues[f.key] || {}), recurrence_interval: Number(e.target.value) };
                            setFieldValues(v => ({ ...v, [f.key]: next }));
                            saveField(f.key, next);
                          }}>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(n => <option key={n} value={n}>Every {n} wk{n === 1 ? '' : 's'}</option>)}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              ) : f.type === 'caregivers' ? (
                <div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {(fieldValues[f.key] || []).map(c => (
                      <span key={c.id || c.name} className="inline-flex items-center gap-1 bg-[#f0f5f0] text-[#2c4f26] text-xs font-semibold px-2.5 py-1 rounded-full">
                        {c.name}
                        {wellskyCaregiverUrl(c.wellsky_practitioner_id) && (
                          <a href={wellskyCaregiverUrl(c.wellsky_practitioner_id)} target="_blank" rel="noreferrer" className="hover:text-teal-700" title="View in WellSky">
                            <ExternalLink size={10} />
                          </a>
                        )}
                        {fieldEditable && (
                          <button type="button" onClick={() => {
                            const next = (fieldValues[f.key] || []).filter(x => x.id !== c.id);
                            setFieldValues(v => ({ ...v, [f.key]: next }));
                            saveField(f.key, next);
                          }} className="hover:text-red-500 transition ml-0.5"><X size={11} /></button>
                        )}
                      </span>
                    ))}
                    {(fieldValues[f.key] || []).length === 0 && <span className="text-xs text-gray-400">No one yet</span>}
                  </div>
                  {fieldEditable && (
                    <AssigneePicker
                      staffUsers={wellskyCaregivers
                        .filter(c => !(fieldValues[f.key] || []).some(sel => sel.id === c.id))
                        .map(c => ({ id: c.id, full_name: `${c.first_name || ''} ${c.last_name || ''}`.trim() }))}
                      onPick={(id) => {
                        const c = wellskyCaregivers.find(x => x.id === id);
                        if (!c) return;
                        const next = [...(fieldValues[f.key] || []), { id: c.id, name: `${c.first_name || ''} ${c.last_name || ''}`.trim(), wellsky_practitioner_id: c.wellsky_practitioner_id || null }];
                        setFieldValues(v => ({ ...v, [f.key]: next }));
                        saveField(f.key, next);
                      }}
                      placeholder="Add a Care Pro…"
                    />
                  )}
                </div>
              ) : (
                <input type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'} min={f.type === 'number' ? 1 : undefined} className={inp} disabled={!fieldEditable} value={fieldValues[f.key] || ''}
                  onChange={e => setFieldValues(v => ({ ...v, [f.key]: e.target.value }))}
                  onBlur={e => saveField(f.key, e.target.value)} />
              )}
            </div>
            );
          })}
          {!fieldsEditable && <p className="text-xs text-gray-400">This template has been archived and is read-only.</p>}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 h-fit">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h2 className="text-sm font-bold text-gray-900">Generated Outcomes</h2>
            {isEditable && (
              <div className="relative shrink-0">
                <button type="button" onClick={() => setShowAddOutcomeMenu(v => !v)} className="text-xs font-semibold text-[#2c4f26] hover:underline inline-flex items-center gap-1">
                  <Plus size={12} /> Add Outcome
                </button>
                {showAddOutcomeMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden w-40">
                    <button type="button" onClick={() => addOutcome('task')} className="w-full text-left px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1.5">
                      <ClipboardList size={12} /> Task
                    </button>
                    <button type="button" onClick={() => addOutcome('email')} className="w-full text-left px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 border-t border-gray-100">
                      <Send size={12} /> Email Notification
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-3">Tasks and follow-ups this template creates on publish.</p>
          {outcomes.length === 0 ? (
            <p className="text-xs text-gray-400">No generated outcomes for this template yet.</p>
          ) : (
            <div className="space-y-2">
              {outcomes.map(o => {
                const outcomeEditing = isEditable && editingOutcomeIds.has(o.id);
                if (o.removed) {
                  return (
                    <div key={o.id} className="border border-gray-100 bg-gray-50 rounded-lg p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs text-gray-400 line-through truncate">{o.type === 'email' ? 'Email Notification' : (o.title || 'Untitled task')}</p>
                        <p className="text-[11px] text-gray-400">Removed</p>
                      </div>
                      {isEditable && (
                        <div className="flex items-center gap-3 shrink-0">
                          <button type="button" onClick={() => undoRemoveOutcome(o.id)} className="text-xs text-[#2c4f26] font-medium hover:underline">Undo</button>
                          {o.id.startsWith('manual-') && (
                            <button type="button" onClick={() => permanentlyDeleteOutcome(o.id)} className="text-xs text-red-500 hover:underline">Delete</button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }
                const peopleKey = o.type === 'email' ? 'recipientIds' : 'assigneeIds';
                const peopleIds = o[peopleKey] || [];
                return (
                <div key={o.id} className="border border-gray-200 rounded-lg p-3">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full mb-2 ${o.type === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700'}`}>
                    {o.type === 'email' ? <><Send size={10} /> Email</> : <><ClipboardList size={10} /> Task</>}
                  </span>

                  {o.type === 'email' ? (
                    <>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Email Notification</p>
                      {outcomeEditing ? (
                        <textarea className="w-full text-xs text-gray-600 outline-none resize-y border border-gray-200 rounded-lg px-2 py-1.5" rows={3} placeholder="Message to include in the email…"
                          value={o.message || ''} onChange={e => editOutcome(o.id, { message: e.target.value })} onBlur={() => saveOutcomes(outcomes)} />
                      ) : (
                        o.message ? <p className="text-xs text-gray-600 whitespace-pre-wrap">{o.message}</p> : <p className="text-xs text-gray-400">No message</p>
                      )}
                    </>
                  ) : outcomeEditing ? (
                    <>
                      <textarea rows={2} className="w-full text-sm font-semibold text-gray-900 mb-1 outline-none resize-none" value={o.title} onChange={e => editOutcome(o.id, { title: e.target.value })} onBlur={() => saveOutcomes(outcomes)} />
                      <textarea className="w-full text-xs text-gray-600 outline-none resize-y" rows={2} value={o.detail || ''} onChange={e => editOutcome(o.id, { detail: e.target.value })} onBlur={() => saveOutcomes(outcomes)} />
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-gray-900">{o.title || 'Untitled task'}</p>
                      {o.detail && <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">{o.detail}</p>}
                    </>
                  )}

                  <div className="mt-2 pt-2 border-t border-gray-100 space-y-2">
                    <div>
                      <p className="text-[11px] font-semibold text-gray-500 mb-1">{o.type === 'email' ? 'To' : 'Assigned To'}</p>
                      {outcomeEditing ? (
                        <>
                          <div className="flex flex-wrap gap-1 mb-1.5">
                            {peopleIds.map(id => {
                              const s = genOutcomeStaff.find(x => x.id === id);
                              return (
                                <span key={id} className="inline-flex items-center gap-1 bg-[#f0f5f0] text-[#2c4f26] text-xs font-medium px-2 py-0.5 rounded-full">
                                  {s?.full_name || 'Unknown'}
                                  <button type="button" onClick={() => editOutcomeAndSave(o.id, { [peopleKey]: peopleIds.filter(x => x !== id) })} className="hover:text-red-500 transition"><X size={10} /></button>
                                </span>
                              );
                            })}
                            {peopleIds.length === 0 && <span className="text-xs text-gray-400">No one {o.type === 'email' ? 'selected' : 'assigned'}</span>}
                          </div>
                          <AssigneePicker
                            staffUsers={genOutcomeStaff.filter(s => !peopleIds.includes(s.id))}
                            onPick={id => editOutcomeAndSave(o.id, { [peopleKey]: [...peopleIds, id] })}
                          />
                        </>
                      ) : (
                        <p className="text-xs text-gray-600">{peopleIds.map(id => genOutcomeStaff.find(s => s.id === id)?.full_name || 'Unknown').join(', ') || `No one ${o.type === 'email' ? 'selected' : 'assigned'}`}</p>
                      )}
                    </div>
                    {o.type === 'task' && (
                      <div className="flex items-center gap-1.5">
                        <p className="text-[11px] font-semibold text-gray-500 shrink-0">Due</p>
                        {/* A rule can anchor the due date to a field on the form
                            (dueDate, an absolute date -- e.g. hospital_rehab_discharge's
                            discharge date + 7 days) instead of counting from
                            publish time (dueInDays) -- whichever key this
                            outcome actually has determines which editor shows. */}
                        {o.dueDate !== undefined ? (
                          outcomeEditing ? (
                            <input type="date" className="text-xs border border-gray-200 rounded-lg px-1.5 py-1" value={o.dueDate || ''} onChange={e => editOutcomeAndSave(o.id, { dueDate: e.target.value || null })} />
                          ) : (
                            <span className="text-xs text-gray-600">{o.dueDate ? new Date(o.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'No due date'}</span>
                          )
                        ) : outcomeEditing ? (
                          <>
                            <input type="number" min={0} className="w-14 text-xs border border-gray-200 rounded-lg px-1.5 py-1" value={o.dueInDays ?? ''} onChange={e => editOutcomeAndSave(o.id, { dueInDays: e.target.value === '' ? null : Number(e.target.value) })} />
                            <span className="text-xs text-gray-500">day{o.dueInDays === 1 ? '' : 's'} after publish</span>
                          </>
                        ) : (
                          <span className="text-xs text-gray-600">{o.dueInDays ? `${o.dueInDays} day${o.dueInDays === 1 ? '' : 's'} after publish` : 'No due date'}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {isEditable && (
                    <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-100">
                      <button type="button" onClick={() => toggleOutcomeEditing(o.id)} className="text-xs text-[#2c4f26] font-medium hover:underline inline-flex items-center gap-1">
                        <Pencil size={11} /> {outcomeEditing ? 'Done' : 'Edit'}
                      </button>
                      <button type="button" onClick={() => removeOutcome(o.id)} className="text-xs text-red-500 hover:underline">Remove</button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          <div className="pt-4 mt-4 border-t border-gray-100">
            <h2 className="text-sm font-bold text-gray-900 mb-1">Notifications</h2>
            <p className="text-xs text-gray-400 mb-3">Who gets emailed when this template is published. Action Templates only send email -- there's no SMS for this feature.</p>
            {loadingRecipients ? (
              <p className="text-xs text-gray-400">Loading…</p>
            ) : notifyRecipients.length === 0 ? (
              <p className="text-xs text-gray-400">
                No recipients configured for {typeConfig?.label || template.template_type}.
                {isOwner && ' Set them under Action Templates → Settings.'}
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {notifyRecipients.map(p => (
                  <span key={p.id} className="inline-flex items-center gap-1 bg-[#f0f5f0] text-[#2c4f26] text-xs font-medium px-2 py-1 rounded-full">
                    <Send size={10} /> {p.full_name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {!isNew && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mt-4">
          <button type="button" onClick={toggleTimeline} className="w-full flex items-center justify-between px-5 py-3.5 text-left">
            <h2 className="text-sm font-bold text-gray-900">Timeline</h2>
            {showTimeline ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>
          {showTimeline && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4">
              {loadingTimeline ? (
                <p className="text-xs text-gray-400">Loading…</p>
              ) : !timelineEvents || timelineEvents.length === 0 ? (
                <p className="text-xs text-gray-400">No timeline events yet.</p>
              ) : (
                <div className="space-y-3">
                  {timelineEvents.map(ev => (
                    <div key={ev.key} className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-[#2c4f26] mt-1.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800">{ev.label}{ev.who ? ` — ${ev.who}` : ''}</p>
                        <p className="text-xs text-gray-400">{ev.at ? todoFmtDateTime(ev.at) : 'Pending'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!isNew && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mt-4">
          <button type="button" onClick={toggleAuditLog} className="w-full flex items-center justify-between px-5 py-3.5 text-left">
            <h2 className="text-sm font-bold text-gray-900">Audit Log</h2>
            {showAuditLog ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>
          {showAuditLog && (
            <div className="border-t border-gray-100 divide-y divide-gray-100">
              {loadingAuditLog ? (
                <p className="text-xs text-gray-400 px-5 py-4">Loading…</p>
              ) : !auditEntries || auditEntries.length === 0 ? (
                <p className="text-xs text-gray-400 px-5 py-4">No activity recorded yet.</p>
              ) : (
                auditEntries.map(entry => {
                  const meta = AUDIT_ACTION_META[entry.action] || { label: entry.action, color: 'bg-gray-100 text-gray-600' };
                  const diff = entry.action === 'UPDATE' ? auditEntryDiffList(entry, {}) : [];
                  const isExpanded = auditExpanded.has(entry.id);
                  return (
                    <div key={entry.id} className="p-4">
                      <div className="flex items-start gap-3">
                        <span className={`shrink-0 mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.color}`}>{meta.label}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm text-gray-800">{summarizeActionTemplateAuditEntry(entry)}</p>
                            <span className="text-xs text-gray-500 shrink-0 whitespace-nowrap">{todoFmtDateTime(entry.created_at)}</span>
                          </div>
                          {entry.action === 'UPDATE' && (
                            <>
                              <button type="button" onClick={() => toggleAuditExpanded(entry.id)} className="text-xs text-[#2c4f26] font-medium mt-1.5 hover:underline">
                                {isExpanded ? 'Hide details' : diff.length > 0 ? `View details (${diff.length} change${diff.length === 1 ? '' : 's'})` : 'View details'}
                              </button>
                              {isExpanded && diff.length > 0 && (
                                <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg divide-y divide-gray-200 overflow-hidden">
                                  {diff.map((d, i) => (
                                    <div key={i} className="px-3 py-2 text-xs flex flex-wrap items-baseline gap-x-1.5">
                                      <span className="font-semibold text-gray-700">{auditFieldLabel(d.field)}:</span>
                                      <span className="text-gray-400 line-through">{d.from}</span>
                                      <span className="text-gray-400">→</span>
                                      <span className="text-gray-800">{d.to}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ActionTemplateSettingsView = ({ isOwner, onBack, showToast }) => {
  const [selectedType, setSelectedType] = useState(null);
  const [recipientsByType, setRecipientsByType] = useState({});
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: settingsRows }, { data: staff }] = await Promise.all([
      supabase.from('action_template_settings').select('*'),
      supabase.from('profiles').select('id, full_name').in('role', ['owner', 'admin_staff']).eq('status', 'active').order('full_name'),
    ]);
    const map = {};
    (settingsRows || []).forEach(row => { map[row.template_type] = row.recipient_user_ids || []; });
    setRecipientsByType(map);
    setStaffUsers(staff || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentIds = selectedType ? (recipientsByType[selectedType] || []) : [];
  const availableToAdd = staffUsers.filter(s => !currentIds.includes(s.id));

  const addRecipient = (userId) => {
    if (!selectedType || !userId) return;
    setRecipientsByType(prev => ({ ...prev, [selectedType]: [...(prev[selectedType] || []), userId] }));
  };
  const removeRecipient = (userId) => {
    setRecipientsByType(prev => ({ ...prev, [selectedType]: (prev[selectedType] || []).filter(id => id !== userId) }));
  };

  const save = async () => {
    if (!selectedType) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('action_template_settings').upsert({ template_type: selectedType, recipient_user_ids: currentIds }, { onConflict: 'template_type' });
      if (error) throw error;
      showToast('Notification settings saved');
    } catch (err) {
      showToast(err.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isOwner) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 text-center text-gray-500">
        <p className="text-sm">Only owners can manage Action Template settings.</p>
        <button onClick={onBack} className="mt-3 text-sm font-semibold text-[#2c4f26] hover:underline">Back</button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#2c4f26] font-medium transition mb-5">
        <ChevronLeft size={15} /> Action Templates
      </button>
      <h1 className="text-xl font-bold text-gray-900 mb-1">Template Settings</h1>
      <p className="text-sm text-gray-500 mb-5">Who gets emailed when each template type is published.</p>

      {loading ? (
        <div className="p-16 text-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-1 space-y-2">
            {ACTION_TEMPLATE_TYPES.map(t => (
              <button key={t.type} onClick={() => setSelectedType(t.type)} className={`w-full text-left p-3 rounded-xl border transition ${selectedType === t.type ? 'border-[#2c4f26] bg-[#f0f5f0]' : 'border-gray-200 hover:bg-gray-50'}`}>
                <p className="text-sm font-semibold text-gray-900">{t.label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{(recipientsByType[t.type] || []).length} recipient{(recipientsByType[t.type] || []).length === 1 ? '' : 's'}</p>
              </button>
            ))}
          </div>
          <div className="sm:col-span-2">
            {!selectedType ? (
              <p className="text-sm text-gray-400">Select a template type to manage its notification recipients.</p>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-1">{actionTemplateTypeConfig(selectedType)?.label}</h3>
                <p className="text-xs text-gray-500 mb-3">Staff notified by email when this template type is published.</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {currentIds.map(id => {
                    const s = staffUsers.find(x => x.id === id);
                    return (
                      <span key={id} className="inline-flex items-center gap-1 bg-[#f0f5f0] text-[#2c4f26] text-xs font-semibold px-2.5 py-1 rounded-full">
                        {s?.full_name || 'Unknown'}
                        <button type="button" onClick={() => removeRecipient(id)} className="hover:text-red-500 transition ml-0.5"><X size={11} /></button>
                      </span>
                    );
                  })}
                  {currentIds.length === 0 && <span className="text-xs text-gray-400">No one yet</span>}
                </div>
                <AssigneePicker staffUsers={availableToAdd} onPick={addRecipient} placeholder="Add a recipient…" />
                <div className="flex justify-end mt-3">
                  <button onClick={save} disabled={saving} className="px-4 py-2 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50">
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ActionTemplatesTab = ({ showToast, profile, permissions }) => {
  const isOwner = profile?.role === 'owner';
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [selected, setSelected] = useState(null);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('action_templates').select('*, creator:created_by(full_name), updater:updated_by(full_name)').order('template_number', { ascending: false });
      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      console.error('Error loading action templates:', err);
      showToast('Failed to load action templates', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadTemplates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Nothing is saved to the database here -- picking a type just opens the
  // form locally (see ActionTemplateDetail's isNew). It only becomes a real
  // row once Save as Draft or Publish is clicked, so clicking Back without
  // either leaves nothing behind.
  const createTemplate = (templateType) => {
    setShowTypePicker(false);
    setSelected({ id: null, template_type: templateType, status: 'new', template_number: null, client_name: '', fields: {}, generated_outcomes: [] });
  };

  // Used for both "first save" (not yet in templates list -- prepend) and
  // ordinary updates (already in the list -- replace in place).
  const handleTemplateSaved = (updated) => {
    setSelected(updated);
    setTemplates(prev => prev.some(t => t.id === updated.id) ? prev.map(t => t.id === updated.id ? updated : t) : [updated, ...prev]);
  };
  const handleTemplateDeleted = (id) => {
    setSelected(null);
    setTemplates(prev => prev.filter(t => t.id !== id));
  };

  if (selected) {
    return (
      <ActionTemplateDetail
        template={selected}
        profile={profile}
        showToast={showToast}
        onBack={() => { setSelected(null); loadTemplates(); }}
        onUpdate={handleTemplateSaved}
        onDeleted={handleTemplateDeleted}
      />
    );
  }

  if (showSettings) {
    return <ActionTemplateSettingsView isOwner={isOwner} onBack={() => setShowSettings(false)} showToast={showToast} />;
  }

  // "Open" deliberately surfaces drafts (not yet published) right alongside
  // genuinely open (published, unresolved) ones -- seeing "someone's
  // already started this" before you start your own is the whole point.
  const filtered = templates.filter(t => {
    if (statusFilter === 'open' && t.status === 'archived') return false;
    if (statusFilter === 'archived' && t.status !== 'archived') return false;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    const typeLabel = actionTemplateTypeConfig(t.template_type)?.label || t.template_type;
    return `${t.client_name || ''} ${typeLabel}`.toLowerCase().includes(term) || String(t.template_number).includes(term);
  });
  const openCount = templates.filter(t => t.status !== 'archived').length;
  const archivedCount = templates.filter(t => t.status === 'archived').length;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Action Templates</h1>
          <p className="text-sm text-gray-600">{openCount} open · {archivedCount} archived</p>
        </div>
        <div className="flex items-center gap-2">
          {isOwner && (
            <button onClick={() => setShowSettings(true)} className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition">
              Settings
            </button>
          )}
          <button onClick={() => setShowTypePicker(true)} className="bg-[#2c4f26] text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-[#1f3a1c] transition flex items-center gap-2 text-sm">
            <Plus size={16} /> New Template
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
              placeholder="Search by number, client, or type…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 shrink-0">
            {[['open', 'Open'], ['archived', 'Archive'], ['all', 'All']].map(([val, label]) => (
              <button key={val} onClick={() => setStatusFilter(val)} className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${statusFilter === val ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-gray-500">
            <AlertTriangle size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">{templates.length === 0 ? 'No templates yet' : 'No templates match your filters'}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map(t => {
              const typeConfig = actionTemplateTypeConfig(t.template_type);
              return (
                <div key={t.id} onClick={() => setSelected(t)} className="p-4 hover:bg-gray-50 transition cursor-pointer flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0 text-xs font-bold text-amber-700">
                    #{t.template_number}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">
                      {typeConfig?.requiresClient === false
                        ? `${typeConfig?.label || t.template_type} - ${t.creator?.full_name || 'Someone'}`
                        : (t.client_name || 'No client listed')}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{typeConfig?.label || t.template_type}</p>
                    <p className="text-xs text-gray-400 truncate">
                      Created {todoFmtDateTime(t.created_at)} by {t.creator?.full_name || 'Unknown'} · Updated {todoFmtDateTime(t.updated_at)} by {t.updater?.full_name || t.creator?.full_name || 'Unknown'}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    {t.status === 'draft' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Draft</span>}
                    {t.status === 'open' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Open</span>}
                    {t.status === 'archived' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-600">Archived</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showTypePicker && (
        <NewTemplateTypeModal onClose={() => setShowTypePicker(false)} onPick={createTemplate} />
      )}
    </div>
  );
};

// ── FAQ (staff knowledge base) ────────────────────────────────────────────────

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// Small, dependency-free markdown-ish renderer for FAQ article bodies.
// Escapes HTML entities FIRST, before any tag is generated, so raw
// <script>/<img onerror=...> etc. typed by a staff member can never
// execute -- it just renders back as literal text. Supports: # ## ###
// headings, **bold**, *italic*, `inline code`, ``` code blocks ```,
// - bullet / 1. numbered lists, [text](url) links (http/https/mailto
// only -- no javascript: URIs), and blank-line-separated paragraphs.
const renderMarkdown = (raw) => {
  if (!raw) return '';
  let text = escapeHtml(raw);

  const codeBlocks = [];
  text = text.replace(/```([\s\S]*?)```/g, (_, code) => {
    codeBlocks.push(code.trim());
    return `@@CB${codeBlocks.length - 1}@@`;
  });
  const inlineCodes = [];
  text = text.replace(/`([^`\n]+)`/g, (_, code) => {
    inlineCodes.push(code);
    return `@@IC${inlineCodes.length - 1}@@`;
  });

  text = text.replace(/^### (.+)$/gm, '<h3 class="text-base font-bold text-gray-900 mt-4 mb-1.5">$1</h3>');
  text = text.replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold text-gray-900 mt-5 mb-2">$1</h2>');
  text = text.replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold text-gray-900 mt-5 mb-2">$1</h1>');

  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>');

  // Images -- must run before the link pass below, since ![alt](url) would
  // otherwise match the link pattern too (leaving a stray leading "!").
  // Optional size override: ![alt](url =400x) or ![alt](url =400x300) sets
  // an explicit width (and optionally height) in pixels; omitted entirely,
  // an image defaults to a reasonable inline-screenshot size instead of
  // stretching to the full article width.
  text = text.replace(/!\[([^\]]*)\]\((https?:\/\/[^\s)]+?)(?:\s+=(\d+)x(\d*))?\)/g, (_, alt, src, w, h) => {
    const style = w ? ` style="width:${w}px;${h ? `height:${h}px;` : ''}"` : '';
    const sizeClass = w ? 'max-w-full' : 'max-w-md w-full';
    return `<img src="${src}" alt="${alt}" class="${sizeClass} h-auto rounded-lg border border-gray-200 my-3"${style} />`;
  });
  text = text.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[#2c4f26] underline font-medium">$1</a>');

  // Tables -- GFM-style: a header row, a |---|---| separator row, then body
  // rows. Runs after bold/links/images so cells can already contain those.
  text = text.replace(/(^|\n)(\|.+\|)\n(\|[-:|\s]+\|)\n((?:\|.+\|\n?)*)/g, (_, lead, headerLine, sepLine, bodyLines) => {
    const parseRow = (line) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
    const headers = parseRow(headerLine);
    const rows = bodyLines.split('\n').filter(l => l.trim()).map(parseRow);
    const thead = `<thead><tr>${headers.map(h => `<th class="text-left font-semibold text-gray-700 px-3 py-2 bg-gray-50 border-b-2 border-gray-200 whitespace-nowrap">${h}</th>`).join('')}</tr></thead>`;
    const tbody = `<tbody>${rows.map(r => `<tr class="border-b border-gray-100">${r.map(c => `<td class="px-3 py-2 align-top whitespace-nowrap">${c}</td>`).join('')}</tr>`).join('')}</tbody>`;
    return `${lead}<div class="overflow-x-auto my-3"><table class="w-full text-sm border-collapse">${thead}${tbody}</table></div>`;
  });

  text = text.replace(/(^|\n)((?:- .+(?:\n|$))+)/g, (_, lead, block) => {
    const items = block.trimEnd().split('\n').map(l => `<li>${l.replace(/^- /, '')}</li>`).join('');
    return `${lead}<ul class="list-disc pl-5 my-2 space-y-1">${items}</ul>`;
  });
  text = text.replace(/(^|\n)((?:\d+\. .+(?:\n|$))+)/g, (_, lead, block) => {
    const items = block.trimEnd().split('\n').map(l => `<li>${l.replace(/^\d+\. /, '')}</li>`).join('');
    return `${lead}<ol class="list-decimal pl-5 my-2 space-y-1">${items}</ol>`;
  });

  // A list/table/image regex above consumes its own trailing newline as
  // part of the match, so plain text right after one (on the very next
  // line) ends up only a single \n away -- below, that merges it into the
  // same "paragraph" as the block and it skips <p> wrapping entirely since
  // the merged blob starts with a block tag. Re-separate with a blank line
  // so the split below treats trailing text as its own paragraph.
  text = text.replace(/(<\/(?:ul|ol|table)>|\/>)\n(?!\n)/g, '$1\n\n');

  text = text.split(/\n{2,}/).map(block => {
    const trimmed = block.trim();
    if (!trimmed) return '';
    if (/^<(h1|h2|h3|ul|ol|div)/.test(trimmed)) return trimmed;
    return `<p class="mb-3 leading-relaxed">${trimmed.replace(/\n/g, '<br/>')}</p>`;
  }).join('\n');

  text = text.replace(/@@IC(\d+)@@/g, (_, i) => `<span class="inline-flex items-center gap-0.5 align-middle"><code class="bg-gray-100 text-[#b03030] px-1.5 py-0.5 rounded text-[0.85em] font-mono">${inlineCodes[Number(i)]}</code><button type="button" onclick="window.copyFaqText(this)" class="text-gray-400 hover:text-[#2c4f26] p-0.5 rounded hover:bg-gray-100 transition" title="Copy">${FAQ_COPY_ICON}</button></span>`);
  text = text.replace(/@@CB(\d+)@@/g, (_, i) => `<pre class="bg-gray-900 text-gray-100 rounded-lg p-3.5 overflow-x-auto my-3 text-xs font-mono leading-relaxed"><code>${codeBlocks[Number(i)]}</code></pre>`);

  return text;
};

// Plain-text snippet for article list cards. Tables are dropped entirely
// rather than having their cell text run together with no separators --
// a one-line jumble of a whole directory's names and numbers isn't a
// useful preview. Uses a detached element so entities (e.g. &amp;) decode
// properly, same as the browser would for any other rendered HTML.
const faqPreviewText = (body) => {
  if (!body) return '';
  const el = document.createElement('div');
  el.innerHTML = faqSanitize(body).replace(/<table[\s\S]*?<\/table>/g, ' ');
  return (el.textContent || '').replace(/\s+/g, ' ').trim();
};

// Markdown article bodies render via dangerouslySetInnerHTML, so an inline
// code span's copy button can't wire up a normal React onClick -- it calls
// this global instead. Reads .textContent (not the HTML string) so it
// always copies the real decoded text regardless of what got escaped.
const FAQ_COPY_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path></svg>';
const FAQ_CHECK_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
if (typeof window !== 'undefined') {
  window.copyFaqText = (btn) => {
    const code = btn.previousElementSibling;
    if (!code || !navigator.clipboard) return;
    navigator.clipboard.writeText(code.textContent).then(() => {
      btn.innerHTML = FAQ_CHECK_ICON;
      btn.classList.add('text-green-600');
      setTimeout(() => { btn.innerHTML = FAQ_COPY_ICON; btn.classList.remove('text-green-600'); }, 1200);
    }).catch(() => {});
  };
}

// Shared rich-text-ish field: toolbar (bold/italic/heading/lists/code/link/
// image) + Write/Preview toggle, rendering through renderMarkdown. Used by
// the FAQ article editor and the Task Board's description field alike --
// `bucket` picks which public storage bucket an uploaded image lands in, so
// each feature's images stay separated.
const MarkdownEditorField = ({ label = 'Content', value, onChange, showToast, bucket, rows = 10, placeholder, minHeight = '8rem' }) => {
  const [tab, setTab] = useState('write');
  const [uploadingImage, setUploadingImage] = useState(false);
  const textareaRef = useRef(null);
  const imageInputRef = useRef(null);

  const insertAtCursor = (text) => {
    const el = textareaRef.current;
    if (!el) { onChange(value + text); return; }
    const start = el.selectionStart, end = el.selectionEnd;
    onChange(value.slice(0, start) + text + value.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    });
  };
  const wrapSelection = (before, after = before) => {
    const el = textareaRef.current;
    if (!el) { onChange(value + before + after); return; }
    const start = el.selectionStart, end = el.selectionEnd;
    const selected = value.slice(start, end);
    onChange(value.slice(0, start) + before + selected + after + value.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + before.length + selected.length + (selected ? after.length : 0);
      el.setSelectionRange(pos, pos);
    });
  };
  const insertLinePrefix = (prefix) => {
    const el = textareaRef.current;
    if (!el) { onChange(prefix + value); return; }
    const start = el.selectionStart;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    onChange(value.slice(0, lineStart) + prefix + value.slice(lineStart));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + prefix.length;
      el.setSelectionRange(pos, pos);
    });
  };
  const uploadImageFile = async (file) => {
    setUploadingImage(true);
    try {
      const ext = (file.type.split('/')[1] || file.name.split('.').pop() || 'png').replace('jpeg', 'jpg');
      const path = `${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from(bucket).upload(path, file);
      if (error) throw error;
      const { data } = supabase.storage.from(bucket).getPublicUrl(path);
      const alt = file.name ? file.name.replace(/\.[^.]+$/, '') : 'image';
      insertAtCursor(`![${alt}](${data.publicUrl})`);
    } catch (err) {
      showToast(err.message || 'Failed to upload image', 'error');
    } finally {
      setUploadingImage(false);
    }
  };
  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Please choose an image file', 'error'); return; }
    uploadImageFile(file);
  };
  // Lets someone paste a screenshot straight from the clipboard (e.g. a Win
  // key+Shift+S / Cmd+Shift+4 capture) instead of having to save it to a
  // file first -- same pattern already used for ticket reply attachments.
  const handlePasteImage = (e) => {
    const item = Array.from(e.clipboardData?.items || []).find(i => i.type.startsWith('image/'));
    if (!item) return;
    const file = item.getAsFile();
    if (!file) return;
    e.preventDefault();
    uploadImageFile(file);
  };

  const lbl = 'block text-xs font-semibold text-gray-600 mb-1';
  const toolBtn = 'p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition';

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className={lbl + ' mb-0'}>{label}</label>
        <div className="flex rounded-lg bg-gray-100 p-0.5">
          <button type="button" tabIndex={-1} onClick={() => setTab('write')} className={`px-3 py-1 text-xs font-semibold rounded-md transition ${tab === 'write' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>Write</button>
          <button type="button" tabIndex={-1} onClick={() => setTab('preview')} className={`px-3 py-1 text-xs font-semibold rounded-md transition ${tab === 'preview' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>Preview</button>
        </div>
      </div>

      {tab === 'write' ? (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
            <button type="button" tabIndex={-1} title="Bold" onClick={() => wrapSelection('**')} className={toolBtn}><Bold size={14} /></button>
            <button type="button" tabIndex={-1} title="Italic" onClick={() => wrapSelection('*')} className={toolBtn}><Italic size={14} /></button>
            <button type="button" tabIndex={-1} title="Heading" onClick={() => insertLinePrefix('## ')} className={toolBtn}><Heading2 size={14} /></button>
            <button type="button" tabIndex={-1} title="Bullet list" onClick={() => insertLinePrefix('- ')} className={toolBtn}><List size={14} /></button>
            <button type="button" tabIndex={-1} title="Numbered list" onClick={() => insertLinePrefix('1. ')} className={toolBtn}><ListOrdered size={14} /></button>
            <button type="button" tabIndex={-1} title="Code" onClick={() => wrapSelection('`')} className={toolBtn}><Code size={14} /></button>
            <button type="button" tabIndex={-1} title="Link" onClick={() => wrapSelection('[', '](https://)')} className={toolBtn}><Link2 size={14} /></button>
            <button type="button" tabIndex={-1} title="Table" onClick={() => insertAtCursor('\n| Column 1 | Column 2 | Column 3 |\n| --- | --- | --- |\n| Row 1 | Row 1 | Row 1 |\n| Row 2 | Row 2 | Row 2 |\n')} className={toolBtn}><Table size={14} /></button>
            <div className="w-px h-5 bg-gray-200 mx-0.5" />
            <button type="button" tabIndex={-1} title="Insert image URL" onClick={() => insertAtCursor('![alt text](https://)')} className={toolBtn}><ImageIcon size={14} /></button>
            <button type="button" tabIndex={-1} title="Upload image" onClick={() => imageInputRef.current?.click()} disabled={uploadingImage} className={`${toolBtn} disabled:opacity-50`}>
              {uploadingImage ? <span className="text-xs px-1">Uploading…</span> : <Upload size={14} />}
            </button>
            <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} />
          </div>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={e => onChange(e.target.value)}
            onPaste={handlePasteImage}
            rows={rows}
            className="w-full px-3 py-3 text-sm font-mono focus:outline-none resize-y"
            placeholder={placeholder}
          />
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg p-4" style={{ minHeight }}>
          {value.trim() ? (
            <div className="text-sm text-gray-800" dangerouslySetInnerHTML={{ __html: renderMarkdown(value) }} />
          ) : (
            <p className="text-sm text-gray-400 italic">Nothing to preview yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

// Sanitized on both save and render, independently -- DOMPurify strips
// anything outside this allowlist (script tags, event-handler attributes,
// javascript: URIs, etc.) regardless of what the rich editor itself
// produced, so a direct DB edit or a future editor bug can't reopen a
// stored-XSS path the way raw unescaped HTML would.
const FAQ_SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p', 'h2', 'h3', 'strong', 'em', 'code', 'pre', 'ul', 'ol', 'li', 'a', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'br', 'hr', 'blockquote', 'div', 'span'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'style', 'width', 'height', 'class', 'colspan', 'rowspan'],
};
const faqSanitize = (html) => DOMPurify.sanitize(html || '', FAQ_SANITIZE_CONFIG);

// Wraps inline <code> (not code inside a <pre> block) with a copy button --
// applied at render time, after sanitizing, rather than stored in the DB,
// so it's consistent regardless of how old the content is and the button's
// onclick never has to pass through the sanitizer (which would strip it).
const faqAddCopyButtons = (html) => {
  const blocks = [];
  let out = html.replace(/<pre[\s\S]*?<\/pre>/g, (m) => { blocks.push(m); return `@@PRE${blocks.length - 1}@@`; });
  out = out.replace(/<code>([\s\S]*?)<\/code>/g, (_, inner) =>
    `<span class="inline-flex items-center gap-0.5 align-middle"><code class="bg-gray-100 text-[#b03030] px-1.5 py-0.5 rounded text-[0.85em] font-mono">${inner}</code><button type="button" onclick="window.copyFaqText(this)" class="text-gray-400 hover:text-[#2c4f26] p-0.5 rounded hover:bg-gray-100 transition" title="Copy">${FAQ_COPY_ICON}</button></span>`
  );
  blocks.forEach((b, i) => { out = out.replace(`@@PRE${i}@@`, b); });
  return out;
};
const faqRenderBody = (html) => faqAddCopyButtons(faqSanitize(html));

// Image node with a drag handle to resize, stored as a width (px) node
// attribute that round-trips through getHTML()/setContent() as an inline
// style -- a NodeView is the standard Tiptap way to attach custom DOM
// behavior (the drag handle) to a node without it becoming part of the
// document model itself.
const ResizableImage = TiptapImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        // Reads from either an inline width style or a width attribute, so
        // this round-trips correctly whether the HTML came from this
        // extension's own renderHTML or was authored/migrated externally.
        parseHTML: (element) => {
          const styleWidth = element.style.width;
          if (styleWidth && styleWidth.endsWith('px')) return parseInt(styleWidth, 10);
          const attr = element.getAttribute('width');
          return attr ? parseInt(attr, 10) : null;
        },
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const { width, ...rest } = HTMLAttributes;
    const style = `height:auto;border-radius:0.5rem;border:1px solid #e5e7eb;margin:0.75rem 0;display:block;${width ? `width:${width}px;max-width:100%;` : 'width:20rem;max-width:100%;'}`;
    return ['img', mergeAttributes(rest, { style })];
  },
  addNodeView() {
    return ({ node, getPos, editor }) => {
      const container = document.createElement('div');
      container.style.position = 'relative';
      container.style.display = 'inline-block';
      container.style.lineHeight = '0';

      const img = document.createElement('img');
      img.src = node.attrs.src;
      img.alt = node.attrs.alt || '';
      img.style.display = 'block';
      img.style.height = 'auto';
      img.style.borderRadius = '0.5rem';
      img.style.border = '1px solid #e5e7eb';
      img.style.margin = '0.75rem 0';
      img.style.maxWidth = '100%';
      img.style.width = node.attrs.width ? `${node.attrs.width}px` : '20rem';

      const handle = document.createElement('div');
      handle.style.position = 'absolute';
      handle.style.right = '2px';
      handle.style.bottom = '10px';
      handle.style.width = '14px';
      handle.style.height = '14px';
      handle.style.background = '#2c4f26';
      handle.style.borderRadius = '9999px';
      handle.style.border = '2px solid white';
      handle.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)';
      handle.style.cursor = 'nwse-resize';
      handle.style.display = 'none';

      container.addEventListener('mouseenter', () => { handle.style.display = 'block'; });
      container.addEventListener('mouseleave', () => { if (!resizing) handle.style.display = 'none'; });

      let resizing = false;
      handle.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        resizing = true;
        const startX = e.clientX;
        const startWidth = img.getBoundingClientRect().width;
        const onMove = (moveEvent) => {
          const next = Math.max(80, Math.round(startWidth + (moveEvent.clientX - startX)));
          img.style.width = `${next}px`;
        };
        const onUp = () => {
          resizing = false;
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onUp);
          if (typeof getPos === 'function') {
            const finalWidth = Math.round(img.getBoundingClientRect().width);
            editor.view.dispatch(editor.view.state.tr.setNodeMarkup(getPos(), undefined, { ...node.attrs, width: finalWidth }));
          }
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
      });

      container.appendChild(img);
      container.appendChild(handle);

      return {
        dom: container,
        update: (updated) => {
          if (updated.type.name !== 'image') return false;
          img.src = updated.attrs.src;
          img.alt = updated.attrs.alt || '';
          img.style.width = updated.attrs.width ? `${updated.attrs.width}px` : '20rem';
          return true;
        },
      };
    };
  },
});

// WYSIWYG editor for FAQ article bodies -- images render inline and resize
// by dragging the handle, instead of the Task Board's plain-markdown
// MarkdownEditorField (left untouched; this is FAQ-only, with its own HTML
// storage format rather than markdown source).
const FaqRichEditor = ({ content, onChange, showToast, placeholder, minHeight = '16rem', bucket = 'faq-images' }) => {
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);
  const editorRef = useRef(null);

  const uploadImageFile = async (file) => {
    if (!file.type.startsWith('image/')) { showToast('Please choose an image file', 'error'); return; }
    setUploadingImage(true);
    try {
      const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
      const path = `${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from(bucket).upload(path, file);
      if (error) throw error;
      const { data } = supabase.storage.from(bucket).getPublicUrl(path);
      editorRef.current?.chain().focus().setImage({ src: data.publicUrl, alt: file.name.replace(/\.[^.]+$/, '') }).run();
    } catch (err) {
      showToast(err.message || 'Failed to upload image', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const editor = useEditor({
    extensions: [
      TiptapStarterKit.configure({ heading: { levels: [2, 3] } }),
      ResizableImage,
      TiptapLink.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer', class: 'text-[#2c4f26] underline font-medium' } }),
      TiptapPlaceholder.configure({ placeholder: placeholder || 'Write the article here…' }),
      TiptapTableKit.configure({ table: { resizable: false } }),
    ],
    content: content || '',
    onUpdate: ({ editor: e }) => onChange(faqSanitize(e.getHTML())),
    editorProps: {
      attributes: { class: 'faq-rich-editor-prose text-sm text-gray-800 focus:outline-none' },
      handlePaste: (_view, event) => {
        const item = Array.from(event.clipboardData?.items || []).find(i => i.type.startsWith('image/'));
        if (!item) return false;
        const file = item.getAsFile();
        if (!file) return false;
        uploadImageFile(file);
        return true;
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { editorRef.current = editor; }, [editor]);
  useEffect(() => () => { editor?.destroy(); }, [editor]);

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) uploadImageFile(file);
  };

  const toolBtn = (active) => `p-2 rounded-lg transition ${active ? 'bg-[#2c4f26]/10 text-[#2c4f26]' : 'hover:bg-gray-100 text-gray-600'}`;

  if (!editor) return null;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200 flex-wrap">
        <button type="button" tabIndex={-1} title="Bold" onClick={() => editor.chain().focus().toggleBold().run()} className={toolBtn(editor.isActive('bold'))}><Bold size={14} /></button>
        <button type="button" tabIndex={-1} title="Italic" onClick={() => editor.chain().focus().toggleItalic().run()} className={toolBtn(editor.isActive('italic'))}><Italic size={14} /></button>
        <button type="button" tabIndex={-1} title="Heading" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={toolBtn(editor.isActive('heading', { level: 2 }))}><Heading2 size={14} /></button>
        <button type="button" tabIndex={-1} title="Bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()} className={toolBtn(editor.isActive('bulletList'))}><List size={14} /></button>
        <button type="button" tabIndex={-1} title="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={toolBtn(editor.isActive('orderedList'))}><ListOrdered size={14} /></button>
        <button type="button" tabIndex={-1} title="Code" onClick={() => editor.chain().focus().toggleCode().run()} className={toolBtn(editor.isActive('code'))}><Code size={14} /></button>
        <button type="button" tabIndex={-1} title="Link" onClick={() => {
          const url = window.prompt('Link URL (https://...)');
          if (!url) return;
          editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }} className={toolBtn(editor.isActive('link'))}><Link2 size={14} /></button>
        <button type="button" tabIndex={-1} title="Callout / quote" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={toolBtn(editor.isActive('blockquote'))}><Quote size={14} /></button>
        <div className="w-px h-5 bg-gray-200 mx-0.5" />
        <button type="button" tabIndex={-1} title="Insert table" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} className={toolBtn(false)}><Table size={14} /></button>
        <button type="button" tabIndex={-1} title="Upload image (or paste one directly)" onClick={() => imageInputRef.current?.click()} disabled={uploadingImage} className={`${toolBtn(false)} disabled:opacity-50`}>
          {uploadingImage ? <span className="text-xs px-1">Uploading…</span> : <ImageIcon size={14} />}
        </button>
        <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} />
      </div>
      <div className="px-3 py-3 overflow-x-auto" style={{ minHeight }}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

const FaqEditor = ({ initial, mode, canPin, existingCategories, onCancel, onSave, saving, showToast }) => {
  const [title, setTitle] = useState(initial?.title || '');
  const [category, setCategory] = useState(initial?.category || '');
  const [pinned, setPinned] = useState(initial?.pinned || false);
  const [body, setBody] = useState(initial?.body || '');
  const [note, setNote] = useState('');

  const inp = 'w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm';
  const lbl = 'block text-xs font-semibold text-gray-600 mb-1';
  // A Tiptap "empty" doc still serializes as e.g. "<p></p>", so stripping
  // tags (and checking for a leftover image) is what actually tells us
  // whether there's real content, not body.trim() against the HTML.
  const bodyHasContent = body && (body.replace(/<[^>]+>/g, '').trim() !== '' || body.includes('<img'));
  const canSave = title.trim() && bodyHasContent;
  const isSuggest = mode === 'suggest';
  const saveLabel = saving ? 'Saving…' : isSuggest ? 'Submit Suggestion' : (mode === 'edit' ? 'Save Changes' : 'Publish');

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
        {isSuggest && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800">
            You're suggesting a change to this article — someone with Modify Articles permission will review it before it goes live.
          </div>
        )}
        <div>
          <label className={lbl}>Title</label>
          <input className={inp} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. How to request time off" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={lbl}>Category</label>
            <input className={inp} list="faq-category-options" value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Onboarding" />
            <datalist id="faq-category-options">
              {existingCategories.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          {canPin && !isSuggest && (
            <div className="flex items-end pb-1.5">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} />
                <Pin size={13} /> Pin to top
              </label>
            </div>
          )}
        </div>

        <div>
          <label className={lbl}>Content</label>
          <FaqRichEditor
            content={initial?.body || ''}
            onChange={setBody}
            showToast={showToast}
            minHeight="22rem"
            placeholder="Write the article here. Paste a screenshot directly to attach it, then drag its corner to resize."
          />
        </div>

        {isSuggest && (
          <div>
            <label className={lbl}>Note for the reviewer (optional)</label>
            <input className={inp} value={note} onChange={e => setNote(e.target.value)} placeholder="Why are you suggesting this change?" />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">Cancel</button>
          <button onClick={() => onSave({ title: title.trim(), category: category.trim() || 'General', pinned, body: faqSanitize(body), note: note.trim() })} disabled={!canSave || saving} className="px-5 py-2 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50">
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const FaqTab = ({ showToast, profile, permissions }) => {
  const isOwner = profile?.role === 'owner';
  const canCreate = isOwner || permissions?.can_create_faq_articles;
  const canEdit = isOwner || permissions?.can_edit_faq_articles;
  const canSuggest = isOwner || permissions?.can_suggest_faq_edits;
  const canDeleteArticles = isOwner || permissions?.can_delete_faq_articles;
  const canPin = isOwner || permissions?.can_pin_faq_articles;
  const canComment = isOwner || permissions?.can_comment_faq_articles;

  const [articles, setArticles] = useState([]);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxSrc(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxSrc]);
  const [pendingCounts, setPendingCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null);
  const [editMode, setEditMode] = useState('create');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');

  const [suggestions, setSuggestions] = useState([]);
  const [comments, setComments] = useState([]);
  const [reviewingId, setReviewingId] = useState(null);
  const [commentBody, setCommentBody] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('faq_articles')
        .select('*, author:created_by(full_name), editor:updated_by(full_name)')
        .order('pinned', { ascending: false })
        .order('updated_at', { ascending: false });
      if (error) throw error;
      setArticles(data || []);

      if (canEdit) {
        const { data: pending } = await supabase.from('faq_article_suggestions').select('article_id').eq('status', 'pending');
        const counts = {};
        (pending ?? []).forEach(s => { counts[s.article_id] = (counts[s.article_id] || 0) + 1; });
        setPendingCounts(counts);
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to load FAQ articles', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDetail = async (articleId) => {
    const [{ data: sugg }, { data: com }] = await Promise.all([
      canEdit
        ? supabase.from('faq_article_suggestions').select('*, suggester:suggested_by(full_name)').eq('article_id', articleId).eq('status', 'pending').order('created_at', { ascending: true })
        : Promise.resolve({ data: [] }),
      supabase.from('faq_comments').select('*, author:user_id(full_name)').eq('article_id', articleId).order('created_at', { ascending: true }),
    ]);
    setSuggestions(sugg ?? []);
    setComments(com ?? []);
  };

  const openDetail = (article) => {
    setSelected(article);
    setView('detail');
    loadDetail(article.id);
  };

  const categories = Array.from(new Set(articles.map(a => a.category))).sort();

  const filtered = articles.filter(a => {
    if (categoryFilter !== 'All' && a.category !== categoryFilter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return a.title.toLowerCase().includes(q) || a.body.toLowerCase().includes(q) || a.category.toLowerCase().includes(q);
  });

  const totalPending = Object.values(pendingCounts).reduce((sum, n) => sum + n, 0);

  const handleSave = async (draft) => {
    setSaving(true);
    try {
      if (editMode === 'suggest') {
        const { error } = await supabase.from('faq_article_suggestions').insert({
          article_id: editing.id, suggested_by: profile?.id || null,
          title: draft.title, category: draft.category, body: draft.body, note: draft.note || null,
        });
        if (error) throw error;
        showToast('Suggestion submitted for review');
        setView('detail');
        await loadDetail(editing.id);
      } else if (editMode === 'edit') {
        const { error } = await supabase.from('faq_articles').update({
          title: draft.title, category: draft.category, body: draft.body, pinned: draft.pinned,
          updated_by: profile?.id || null, updated_at: new Date().toISOString(),
        }).eq('id', editing.id);
        if (error) throw error;
        showToast('Article updated');
        setView('list');
        setSelected(null);
        await load();
      } else {
        const { error } = await supabase.from('faq_articles').insert({
          title: draft.title, category: draft.category, body: draft.body, pinned: draft.pinned,
          created_by: profile?.id || null,
        });
        if (error) throw error;
        showToast('Article published');
        setView('list');
        setSelected(null);
        await load();
      }
      setEditing(null);
    } catch (err) {
      showToast(err.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This can't be undone.`)) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from('faq_articles').delete().eq('id', article.id);
      if (error) throw error;
      showToast('Article deleted');
      setView('list');
      setSelected(null);
      await load();
    } catch (err) {
      showToast(err.message || 'Failed to delete article', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleApprove = async (suggestion) => {
    setReviewingId(suggestion.id);
    try {
      const { error: updateErr } = await supabase.from('faq_articles').update({
        title: suggestion.title, category: suggestion.category, body: suggestion.body,
        updated_by: profile?.id || null, updated_at: new Date().toISOString(),
      }).eq('id', suggestion.article_id);
      if (updateErr) throw updateErr;
      const { error: reviewErr } = await supabase.from('faq_article_suggestions').update({
        status: 'approved', reviewed_by: profile?.id || null, reviewed_at: new Date().toISOString(),
      }).eq('id', suggestion.id);
      if (reviewErr) throw reviewErr;
      showToast('Suggestion approved and published');
      const { data: updated } = await supabase.from('faq_articles').select('*, author:created_by(full_name), editor:updated_by(full_name)').eq('id', suggestion.article_id).single();
      if (updated) setSelected(updated);
      await loadDetail(suggestion.article_id);
      await load();
    } catch (err) {
      showToast(err.message || 'Failed to approve suggestion', 'error');
    } finally {
      setReviewingId(null);
    }
  };

  const handleReject = async (suggestion) => {
    const note = window.prompt('Optional note for whoever suggested this (why it was rejected):') || null;
    setReviewingId(suggestion.id);
    try {
      const { error } = await supabase.from('faq_article_suggestions').update({
        status: 'rejected', review_note: note, reviewed_by: profile?.id || null, reviewed_at: new Date().toISOString(),
      }).eq('id', suggestion.id);
      if (error) throw error;
      showToast('Suggestion rejected');
      await loadDetail(suggestion.article_id);
      await load();
    } catch (err) {
      showToast(err.message || 'Failed to reject suggestion', 'error');
    } finally {
      setReviewingId(null);
    }
  };

  const handlePostComment = async () => {
    if (!commentBody.trim() || !selected) return;
    setPostingComment(true);
    try {
      const { error } = await supabase.from('faq_comments').insert({ article_id: selected.id, user_id: profile?.id || null, body: commentBody.trim() });
      if (error) throw error;
      setCommentBody('');
      await loadDetail(selected.id);
    } catch (err) {
      showToast(err.message || 'Failed to post comment', 'error');
    } finally {
      setPostingComment(false);
    }
  };

  const handleDeleteComment = async (comment) => {
    try {
      const { error } = await supabase.from('faq_comments').delete().eq('id', comment.id);
      if (error) throw error;
      await loadDetail(selected.id);
    } catch (err) {
      showToast(err.message || 'Failed to delete comment', 'error');
    }
  };

  if (view === 'editor') {
    return (
      <FaqEditor
        initial={editing}
        mode={editMode}
        canPin={canPin}
        existingCategories={categories}
        saving={saving}
        onCancel={() => { setView(editMode !== 'create' ? 'detail' : 'list'); }}
        onSave={handleSave}
        showToast={showToast}
      />
    );
  }

  if (view === 'detail' && selected) {
    return (
      <>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <button onClick={() => { setView('list'); setSelected(null); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#2c4f26] font-medium transition mb-5">
          <ChevronLeft size={15} /> FAQ
        </button>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 mb-4">
          <div className="flex items-start justify-between gap-4 mb-1">
            <div className="flex items-center gap-2 flex-wrap">
              {selected.pinned && <Pin size={14} className="text-amber-500 shrink-0" />}
              <h1 className="text-2xl font-bold text-gray-900">{selected.title}</h1>
            </div>
            <div className="flex gap-2 shrink-0">
              {canEdit ? (
                <button onClick={() => { setEditing(selected); setEditMode('edit'); setView('editor'); }} className="px-3 py-1.5 text-xs font-medium text-[#2c4f26] hover:bg-[#f0f5f0] rounded-lg transition flex items-center gap-1">
                  <Pencil size={12} /> Edit
                </button>
              ) : canSuggest ? (
                <button onClick={() => { setEditing(selected); setEditMode('suggest'); setView('editor'); }} className="px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 rounded-lg transition flex items-center gap-1">
                  <Pencil size={12} /> Suggest Edit
                </button>
              ) : null}
              {canDeleteArticles && (
                <button onClick={() => handleDelete(selected)} disabled={deleting} className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-6 flex-wrap">
            <span className="px-2 py-0.5 bg-gray-100 rounded-full font-medium">{selected.category}</span>
            <span>By {selected.author?.full_name || 'Unknown'}</span>
            {selected.updated_by && selected.updated_by !== selected.created_by && (
              <span>· Last edited by {selected.editor?.full_name || 'Unknown'}</span>
            )}
            <span>· {hiringFmtSyncAgo(selected.updated_at)}</span>
          </div>
          <div
            className="faq-article-body text-sm text-gray-800 overflow-x-auto"
            onClick={(e) => { if (e.target.tagName === 'IMG') setLightboxSrc(e.target.getAttribute('src')); }}
            dangerouslySetInnerHTML={{ __html: faqRenderBody(selected.body) }}
          />
        </div>

        {canEdit && suggestions.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-4 space-y-4">
            <h3 className="text-sm font-bold text-amber-900 flex items-center gap-1.5"><Pencil size={14} /> Suggested Edits ({suggestions.length})</h3>
            {suggestions.map(s => (
              <div key={s.id} className="bg-white border border-amber-200 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-2">Suggested by {s.suggester?.full_name || 'Unknown'} · {hiringFmtSyncAgo(s.created_at)}</p>
                {s.note && <p className="text-xs text-gray-600 italic mb-3">"{s.note}"</p>}
                <div className="space-y-3 text-xs">
                  {s.title !== selected.title && (
                    <div><p className="font-semibold text-gray-500 mb-0.5">Title</p><p className="text-gray-400 line-through">{selected.title}</p><p className="text-gray-900 font-medium">{s.title}</p></div>
                  )}
                  {s.category !== selected.category && (
                    <div><p className="font-semibold text-gray-500 mb-0.5">Category</p><p className="text-gray-400 line-through">{selected.category}</p><p className="text-gray-900 font-medium">{s.category}</p></div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-500 mb-0.5">Content (proposed)</p>
                    <div className="faq-article-body border border-gray-200 rounded-lg p-3 bg-gray-50 text-gray-800 overflow-x-auto" dangerouslySetInnerHTML={{ __html: faqRenderBody(s.body) }} />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <button onClick={() => handleReject(s)} disabled={reviewingId === s.id} className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50">Reject</button>
                  <button onClick={() => handleApprove(s)} disabled={reviewingId === s.id} className="px-3 py-1.5 text-xs font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50">
                    {reviewingId === s.id ? 'Working…' : 'Approve & Publish'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Comments {comments.length > 0 ? `(${comments.length})` : ''}</h3>
          <div className="space-y-3 mb-4">
            {comments.map(c => (
              <div key={c.id} className="flex items-start justify-between gap-2 text-sm">
                <div>
                  <p className="text-gray-800">{c.body}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{c.author?.full_name || 'Unknown'} · {hiringFmtSyncAgo(c.created_at)}</p>
                </div>
                {(c.user_id === profile?.id || canEdit) && (
                  <button onClick={() => handleDeleteComment(c)} className="text-gray-300 hover:text-red-500 transition shrink-0"><X size={13} /></button>
                )}
              </div>
            ))}
            {comments.length === 0 && <p className="text-xs text-gray-400">No comments yet.</p>}
          </div>
          {canComment && (
            <div className="flex gap-2">
              <input value={commentBody} onChange={e => setCommentBody(e.target.value)} placeholder="Add a comment or question…" className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" onKeyDown={e => { if (e.key === 'Enter') handlePostComment(); }} />
              <button onClick={handlePostComment} disabled={postingComment || !commentBody.trim()} className="px-4 py-2 text-xs font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50">Post</button>
            </div>
          )}
        </div>
      </div>
      {lightboxSrc && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightboxSrc(null)}
        >
          <button
            onClick={() => setLightboxSrc(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition p-2"
          >
            <X size={28} />
          </button>
          <img src={lightboxSrc} alt="" className="max-w-full max-h-full rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
        </div>
      )}
      </>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">FAQ</h1>
          <p className="text-sm text-gray-600">Staff reference guides.</p>
        </div>
        {canCreate && (
          <button onClick={() => { setEditing(null); setEditMode('create'); setView('editor'); }} className="bg-[#2c4f26] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#1f3a1c] transition flex items-center justify-center gap-2 text-sm shrink-0">
            <Plus size={16} /> New Article
          </button>
        )}
      </div>

      {canEdit && totalPending > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mb-4 text-sm text-amber-800 font-medium">
          {totalPending} suggested {totalPending === 1 ? 'edit' : 'edits'} awaiting your review
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles…" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" />
        </div>
        {categories.length > 0 && (
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition">
            <option value="All">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="p-16 text-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="p-16 text-center text-gray-500 bg-white rounded-2xl border border-gray-100">
          <HelpCircle size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">{articles.length === 0 ? 'No articles yet.' : 'No articles match your search'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {filtered.map(a => (
            <button key={a.id} onClick={() => openDetail(a)} className="text-left bg-white rounded-2xl border border-gray-100 p-4 hover:border-[#2c4f26]/30 hover:shadow-sm transition">
              <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-1.5 mb-1.5">
                {a.pinned && <Pin size={12} className="text-amber-500 shrink-0" />}
                <span className="flex-1">{a.title}</span>
                {canEdit && pendingCounts[a.id] > 0 && (
                  <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full shrink-0">{pendingCounts[a.id]} pending</span>
                )}
              </h3>
              <p className="text-xs text-gray-500 mb-2">{faqPreviewText(a.body).slice(0, 140)}{faqPreviewText(a.body).length > 140 ? '…' : ''}</p>
              <div className="flex items-center gap-2 text-[11px] text-gray-400">
                <span className="px-1.5 py-0.5 bg-gray-100 rounded-full font-medium text-gray-600">{a.category}</span>
                <span>{a.author?.full_name || 'Unknown'}</span>
                <span>· {hiringFmtSyncAgo(a.updated_at)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Form Submissions (Jotform capture + triage inbox) ────────────────────────
// Deliberately just capture + triage for now -- every Jotform form (used for
// many different things) feeds this one inbox via the jotform-webhook edge
// function. Per-form automation is a later phase built on top of this once
// it's proven out, not part of this pass.
const JOTFORM_STATUS_META = {
  new: { label: 'New', badge: 'bg-blue-100 text-blue-700' },
  reviewed: { label: 'Reviewed', badge: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completed', badge: 'bg-green-100 text-green-700' },
};

// Jotform's "pretty" string is every field joined as "Label:Value, Label:Value,
// ...", which reads as one run-on paragraph. Split back into rows: a field
// boundary is a ", " followed by what looks like the start of a new label
// (capitalized) -- confirmed against a real submission with a long
// instructional-paragraph label (full of its own internal commas) that this
// still splits correctly, since none of its internal ", " happen to be
// followed by a capital letter.
//
// Within a segment, the label/value split is the first ":" that isn't
// inside parentheses -- a parenthetical aside in a question's label (e.g.
// "(Ex: 1-15th...)") can itself contain a colon, which isn't the real
// label/value boundary. Confirmed against that same real submission: its
// one long label has exactly this shape, and this still isolates the
// actual trailing value correctly.
const prettyFieldSplitIndex = (segment) => {
  let depth = 0;
  for (let i = 0; i < segment.length; i++) {
    const c = segment[i];
    if (c === '(') depth++;
    else if (c === ')') depth = Math.max(0, depth - 1);
    else if (c === ':' && depth === 0) return i;
  }
  return -1;
};

const parsePrettySummary = (pretty) => {
  if (!pretty) return [];
  return pretty.split(/,\s+(?=[A-Z])/).map(seg => {
    const idx = prettyFieldSplitIndex(seg);
    const raw = idx === -1 ? { label: '', value: seg } : { label: seg.slice(0, idx), value: seg.slice(idx + 1) };
    return { label: raw.label.trim(), value: raw.value.replace(/\s+/g, ' ').trim() };
  }).filter(row => row.label || row.value);
};

// Jotform forms vary per-form and raw `answers` keys are auto-generated
// (q21_ifYour21-style) so detecting a client-name field by meaning only
// works against the human-readable pretty_summary labels -- same reasoning
// as parsePrettySummary itself. Matching logic mirrors wellsky-sync's loose
// name matcher (exact first_name+last_name or co_client_name, else an
// unambiguous token-subset match) so a submitted "Regina Harber" matches a
// clients row however punctuation/case differs.
const extractClientNameField = (pretty) => {
  const row = parsePrettySummary(pretty).find(r => /client/i.test(r.label));
  return row && row.value ? row.value : null;
};

const jotformLooseName = (s) => (s ?? '').toLowerCase().trim().replace(/[.\-'’â]/g, ' ').replace(/\s+/g, ' ').trim();

const matchClientByName = (name, clients) => {
  const target = jotformLooseName(name);
  if (!target) return null;
  const targetTokens = new Set(target.split(' ').filter(Boolean));
  const partials = [];
  for (const c of clients) {
    const full = jotformLooseName(`${c.first_name || ''} ${c.last_name || ''}`);
    const co = jotformLooseName(c.co_client_name || '');
    if (full === target || (co && co === target)) return c;
    const fullTokens = new Set(full.split(' ').filter(Boolean));
    if (fullTokens.size && [...fullTokens].every(t => targetTokens.has(t))) {
      partials.push(c);
    } else if (co) {
      const coTokens = new Set(co.split(' ').filter(Boolean));
      if (coTokens.size && [...coTokens].every(t => targetTokens.has(t))) partials.push(c);
    }
  }
  return partials.length === 1 ? partials[0] : null;
};

const jotformClientDisplayName = (c) => `${c.first_name} ${c.last_name}${c.co_client_name ? ` & ${c.co_client_name}` : ''}`;

// Care Pro forms don't all label the field "Care Pro Name" -- some (e.g. the
// Time Off Request form) just ask for "Name" + "Email", relying on the form
// itself being care-pro-specific. The explicit label is checked first; the
// generic "Name" fallback only kicks in when the form's own title marks it
// as a care-pro form, so a plain "Name" field on an unrelated form is never
// misread as a Care Pro.
const extractCareProNameField = (pretty, formTitle) => {
  const rows = parsePrettySummary(pretty);
  const explicit = rows.find(r => /care[\s-]*pro/i.test(r.label));
  if (explicit && explicit.value) return explicit.value;
  if (/care[\s-]*pro/i.test(formTitle || '')) {
    const generic = rows.find(r => /^name$/i.test(r.label.trim()));
    if (generic && generic.value) return generic.value;
  }
  return null;
};

const matchCaregiverByName = (name, caregivers) => {
  const target = jotformLooseName(name);
  if (!target) return null;
  const targetTokens = new Set(target.split(' ').filter(Boolean));
  const partials = [];
  for (const c of caregivers) {
    const full = jotformLooseName(`${c.first_name || ''} ${c.last_name || ''}`);
    if (full === target) return c;
    const fullTokens = new Set(full.split(' ').filter(Boolean));
    if (fullTokens.size && [...fullTokens].every(t => targetTokens.has(t))) partials.push(c);
  }
  return partials.length === 1 ? partials[0] : null;
};

const jotformCaregiverDisplayName = (c) => `${c.first_name} ${c.last_name}`;

// Support tickets have a reliable email on hand (unlike Jotform submissions),
// so try that first -- a CP's WellSky-on-file email is more often right than
// a fuzzy name match, which stays as the fallback for when they used a
// personal email the ticket form didn't ask them to change.
const matchCaregiverForTicket = (customerName, customerEmail, caregivers) => {
  const targetEmail = (customerEmail || '').trim().toLowerCase();
  if (targetEmail) {
    const emailMatch = caregivers.find(c => (c.email || '').trim().toLowerCase() === targetEmail);
    if (emailMatch) return emailMatch;
  }
  return customerName ? matchCaregiverByName(customerName, caregivers) : null;
};

const JotformSubmissionDetail = ({ submission, profile, clients, caregivers, showToast, onBack, onUpdate }) => {
  const [saving, setSaving] = useState(false);

  const changeStatus = async (status) => {
    setSaving(true);
    try {
      const update = { status, reviewed_by: profile?.id || null, reviewed_at: new Date().toISOString() };
      const { error } = await supabase.from('jotform_submissions').update(update).eq('id', submission.id);
      if (error) throw error;
      showToast(status === 'new' ? 'Reopened' : `Marked ${status}`);
      onUpdate({ ...submission, ...update });
    } catch (err) {
      showToast(err.message || 'Failed to update status', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition mb-4">
        <ChevronLeft size={16} /> Form Submissions
      </button>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{submission.form_title || 'Untitled form'}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Submitted {submission.submitted_at ? new Date(submission.submitted_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
            </p>
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shrink-0 ${JOTFORM_STATUS_META[submission.status]?.badge || 'bg-gray-100 text-gray-600'}`}>
            {JOTFORM_STATUS_META[submission.status]?.label || submission.status}
          </span>
        </div>

        {(() => {
          const clientNameRaw = extractClientNameField(submission.pretty_summary);
          const matchedClient = clientNameRaw ? matchClientByName(clientNameRaw, clients || []) : null;
          const careProNameRaw = extractCareProNameField(submission.pretty_summary, submission.form_title);
          const matchedCaregiver = careProNameRaw ? matchCaregiverByName(careProNameRaw, caregivers || []) : null;
          if (!clientNameRaw && !careProNameRaw) return null;
          return (
            <div className="border-b border-gray-100 bg-teal-50/50 divide-y divide-teal-100">
              {clientNameRaw && (
                <div className="px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
                  {matchedClient ? (
                    <>
                      <p className="text-sm text-gray-800">
                        <span className="text-gray-500">Matched client:</span>{' '}
                        <span className="font-semibold">{jotformClientDisplayName(matchedClient)}</span>
                      </p>
                      {wellskyClientUrl(matchedClient.wellsky_patient_id) && (
                        <a href={wellskyClientUrl(matchedClient.wellsky_patient_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:underline shrink-0">
                          View in WellSky <ExternalLink size={12} />
                        </a>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">No matching client found for "{clientNameRaw}"</p>
                  )}
                </div>
              )}
              {careProNameRaw && (
                <div className="px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
                  {matchedCaregiver ? (
                    <>
                      <p className="text-sm text-gray-800">
                        <span className="text-gray-500">Matched Care Pro:</span>{' '}
                        <span className="font-semibold">{jotformCaregiverDisplayName(matchedCaregiver)}</span>
                      </p>
                      {wellskyCaregiverUrl(matchedCaregiver.wellsky_practitioner_id) && (
                        <a href={wellskyCaregiverUrl(matchedCaregiver.wellsky_practitioner_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:underline shrink-0">
                          View in WellSky <ExternalLink size={12} />
                        </a>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">No matching Care Pro found for "{careProNameRaw}"</p>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        <div className="p-6 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Submission</p>
          {(() => {
            const rows = parsePrettySummary(submission.pretty_summary);
            if (rows.length === 0) return <p className="text-sm text-gray-400">No details available</p>;
            return (
              <div className="space-y-3">
                {rows.map((row, i) => (
                  <div key={i}>
                    {row.label && <p className="text-xs font-semibold text-gray-500">{row.label}</p>}
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{row.value || '—'}</p>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        <div className="p-6 flex flex-wrap gap-2">
          {submission.status !== 'reviewed' && (
            <button onClick={() => changeStatus('reviewed')} disabled={saving} className="px-4 py-2 text-sm font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-xl transition disabled:opacity-50">Mark Reviewed</button>
          )}
          {submission.status !== 'completed' && (
            <button onClick={() => changeStatus('completed')} disabled={saving} className="px-4 py-2 text-sm font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-xl transition disabled:opacity-50">Mark Completed</button>
          )}
          {submission.status !== 'new' && (
            <button onClick={() => changeStatus('new')} disabled={saving} className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition disabled:opacity-50">Reopen</button>
          )}
        </div>
      </div>
    </div>
  );
};

const JotformSubmissionsTab = ({ showToast, profile }) => {
  const [submissions, setSubmissions] = useState([]);
  const [clients, setClients] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('new');
  const [formTitleFilter, setFormTitleFilter] = useState('');
  const [selected, setSelected] = useState(null);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const [{ data, error }, { data: clientRows, error: clientError }, { data: caregiverRows, error: caregiverError }] = await Promise.all([
        supabase.from('jotform_submissions').select('*').order('submitted_at', { ascending: false }),
        supabase.from('clients').select('id, first_name, last_name, co_client_name, wellsky_patient_id'),
        supabase.from('wellsky_caregivers').select('id, first_name, last_name, wellsky_practitioner_id'),
      ]);
      if (error) throw error;
      if (clientError) throw clientError;
      if (caregiverError) throw caregiverError;
      setSubmissions(data || []);
      setClients(clientRows || []);
      setCaregivers(caregiverRows || []);
    } catch (err) {
      console.error('Error loading jotform submissions:', err);
      showToast('Failed to load form submissions', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadSubmissions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (selected) {
    return (
      <JotformSubmissionDetail
        submission={selected}
        profile={profile}
        clients={clients}
        caregivers={caregivers}
        showToast={showToast}
        onBack={() => { setSelected(null); loadSubmissions(); }}
        onUpdate={(updated) => { setSelected(updated); setSubmissions(prev => prev.map(s => s.id === updated.id ? updated : s)); }}
      />
    );
  }

  const formTitleOptions = Array.from(new Set(submissions.map(s => s.form_title).filter(Boolean))).sort();
  const filtered = submissions.filter(s => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (formTitleFilter && s.form_title !== formTitleFilter) return false;
    return true;
  });
  const newCount = submissions.filter(s => s.status === 'new').length;
  const reviewedCount = submissions.filter(s => s.status === 'reviewed').length;
  const completedCount = submissions.filter(s => s.status === 'completed').length;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Form Submissions</h1>
        <p className="text-sm text-gray-600">{newCount} new · {reviewedCount} reviewed · {completedCount} completed</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3">
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 shrink-0">
            {[['new', 'New'], ['reviewed', 'Reviewed'], ['completed', 'Completed'], ['all', 'All']].map(([val, label]) => (
              <button key={val} onClick={() => setStatusFilter(val)} className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${statusFilter === val ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                {label}
              </button>
            ))}
          </div>
          <select value={formTitleFilter} onChange={e => setFormTitleFilter(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm sm:ml-auto">
            <option value="">All Forms</option>
            {formTitleOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="p-16 text-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-gray-500">
            <FileText size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">{submissions.length === 0 ? 'No form submissions yet' : 'No submissions match your filters'}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map(s => {
              const clientNameRaw = extractClientNameField(s.pretty_summary);
              const matchedClient = clientNameRaw ? matchClientByName(clientNameRaw, clients) : null;
              const careProNameRaw = extractCareProNameField(s.pretty_summary, s.form_title);
              const matchedCaregiver = careProNameRaw ? matchCaregiverByName(careProNameRaw, caregivers) : null;
              return (
                <div key={s.id} onClick={() => setSelected(s)} className="p-4 hover:bg-gray-50 transition cursor-pointer flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900 text-sm truncate">{s.form_title || 'Untitled form'}</p>
                      {matchedClient && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 shrink-0">
                          {jotformClientDisplayName(matchedClient)}
                        </span>
                      )}
                      {matchedCaregiver && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 shrink-0">
                          {jotformCaregiverDisplayName(matchedCaregiver)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{s.pretty_summary || 'No details available'}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${JOTFORM_STATUS_META[s.status]?.badge || 'bg-gray-100 text-gray-600'}`}>
                      {JOTFORM_STATUS_META[s.status]?.label || s.status}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">{s.submitted_at ? new Date(s.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Task Board (simple internal task tracker) ────────────────────────────────
// Distinct from the Support ticket system by design -- a Kanban-style board
// instead of a list-with-replies, and built for multiple people to work one
// task together. Every save (core fields, status, assignee add/remove, a
// note) touches only what it owns, never a whole-row re-save, so concurrent
// edits from different assignees can't clobber each other -- same proven
// approach as Incident Reports.
const TODO_PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-400' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-500' },
  { value: 'high', label: 'High', color: 'bg-amber-500' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-500' },
];
const TODO_PRIORITY_MAP = Object.fromEntries(TODO_PRIORITIES.map(p => [p.value, p]));
const TODO_RECURRENCE_OPTIONS = [
  { value: '', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];
const TODO_RECURRENCE_LABEL = Object.fromEntries(TODO_RECURRENCE_OPTIONS.filter(o => o.value).map(o => [o.value, o.label]));
// "Weekly" supports a 1-12 week interval; every other cadence is fixed.
const todoRecurrenceLabel = (recurrence, interval) =>
  recurrence === 'weekly' && interval > 1 ? `Every ${interval} Weeks` : TODO_RECURRENCE_LABEL[recurrence] || recurrence;
const todoRecurrenceUnitPhrase = (recurrence, interval) => {
  if (recurrence === 'weekly') return interval > 1 ? `${interval} weeks` : 'one week';
  return `one ${{ daily: 'day', monthly: 'month', quarterly: 'quarter', annually: 'year' }[recurrence] || recurrence}`;
};
const TODO_STATUS_COLUMNS = [
  { key: 'not_started', label: 'Not Started' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'done', label: 'Completed' },
];
const todoAssigneeIds = (todo) => (todo.todo_assignees || []).map(a => a.user_id);
const todoWatcherIds = (todo) => (todo.todo_watchers || []).map(w => w.user_id);
const todoIsOverdue = (todo) => {
  if (!todo.due_date || todo.status === 'done') return false;
  const today = new Date().toISOString().slice(0, 10);
  if (todo.due_date < today) return true;
  if (todo.due_date > today) return false;
  // Due today -- only overdue once a specific due_time has actually passed;
  // no time set means "by end of day," so it's not overdue until tomorrow.
  if (!todo.due_time) return false;
  return new Date(`${todo.due_date}T${todo.due_time}`) < new Date();
};
// Which board column a task shows under for a given viewer. For a group
// task (requires_all_assignees), the shared todo.status stays 'in_progress'
// until every assignee has finished -- correct for the owner and for
// anyone who hasn't completed their own part yet, but a viewer who HAS
// completed their part wants to see it under Done on their own board
// rather than waiting on everyone else.
const todoDisplayStatus = (todo, viewerId) => {
  if (todo.requires_all_assignees) {
    const mine = (todo.todo_assignees || []).find(a => a.user_id === viewerId);
    if (mine?.completed_at) return 'done';
  }
  return todo.status;
};
// Board column ordering: urgent tasks float to the top regardless of due
// date (they're meant to stand out, not just sort in with everything else),
// then soonest due date first within each group, with no-due-date tasks
// pushed to the end rather than sorting first.
const todoBoardSort = (a, b) => {
  const aUrgent = a.priority === 'urgent' ? 0 : 1;
  const bUrgent = b.priority === 'urgent' ? 0 : 1;
  if (aUrgent !== bUrgent) return aUrgent - bUrgent;
  if (!a.due_date && !b.due_date) return 0;
  if (!a.due_date) return 1;
  if (!b.due_date) return -1;
  const dateCmp = a.due_date.localeCompare(b.due_date);
  if (dateCmp !== 0) return dateCmp;
  // Same day -- a specific time sorts ahead of "sometime today" (no time set).
  if (!a.due_time && !b.due_time) return 0;
  if (!a.due_time) return 1;
  if (!b.due_time) return -1;
  return a.due_time.localeCompare(b.due_time);
};
// Done column: most recently completed first, not by due date -- "what did
// we just finish" matters more there than "what was it originally due."
const todoDoneSort = (a, b) => {
  if (!a.completed_at && !b.completed_at) return 0;
  if (!a.completed_at) return 1;
  if (!b.completed_at) return -1;
  return b.completed_at.localeCompare(a.completed_at);
};
const todoInitials = (name) => (name || '').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
const todoFmtShort = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
// Due date, with the time appended only if one was actually set -- a task
// due "by end of day" just shows the date, same as before due_time existed.
const todoFmtDue = (todo) => {
  const datePart = todoFmtShort(todo.due_date + 'T00:00:00');
  if (!todo.due_time) return datePart;
  const timePart = new Date(`${todo.due_date}T${todo.due_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${datePart} at ${timePart}`;
};
const todoFmtDateTime = (d) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const todoDaysLeftLabel = (todo) => {
  if (!todo.due_date || todo.status === 'done') return null;
  const today = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00');
  const due = new Date(todo.due_date + 'T00:00:00');
  const diffDays = Math.round((due - today) / 86400000);
  if (diffDays < 0) return { text: `${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'} overdue`, cls: 'text-red-600 bg-red-50' };
  if (diffDays === 0) return { text: 'Due today', cls: 'text-amber-600 bg-amber-50' };
  if (diffDays === 1) return { text: '1 day left', cls: 'text-gray-600 bg-gray-100' };
  return { text: `${diffDays} days left`, cls: 'text-gray-600 bg-gray-100' };
};

// Type-to-filter picker used anywhere a staff member needs to be added to a
// task -- replaces a fixed-height checkbox/select list that got unwieldy to
// scroll through as the staff list grows.
const AssigneePicker = ({ staffUsers, onPick, disabled, placeholder = 'Search staff…' }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // Pre-highlights the first result so Enter works immediately without
  // requiring a Down press first -- same convention as most search-then-pick
  // UIs (command palettes, Spotlight, etc).
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const filtered = staffUsers.filter(s => (s.full_name || '').toLowerCase().includes(query.toLowerCase()));

  const pick = (id) => { onPick(id); setQuery(''); setOpen(false); setHighlightedIndex(0); };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const s = filtered[highlightedIndex];
      if (s) pick(s.id);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <input
        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm"
        placeholder={placeholder}
        value={query}
        disabled={disabled}
        onChange={e => { setQuery(e.target.value); setOpen(true); setHighlightedIndex(0); }}
        onFocus={() => { setOpen(true); setHighlightedIndex(0); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={handleKeyDown}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-44 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          {filtered.map((s, idx) => (
            <button
              type="button"
              key={s.id}
              ref={el => { if (idx === highlightedIndex) el?.scrollIntoView({ block: 'nearest' }); }}
              onMouseDown={() => pick(s.id)}
              onMouseEnter={() => setHighlightedIndex(idx)}
              className={`w-full text-left px-3 py-2 text-sm transition ${idx === highlightedIndex ? 'bg-[#f0f5f0] text-[#2c4f26]' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              {s.full_name}
            </button>
          ))}
        </div>
      )}
      {open && query && filtered.length === 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs text-gray-400">No matches</div>
      )}
    </div>
  );
};

// Single-field combobox for Action Templates' Client input -- unlike
// AssigneePicker (a search box that always empties itself, paired with
// pills rendered separately for a multi-select list), Client is one plain
// text value, so the input itself both displays and edits it. Picking a
// suggestion calls onSelectClient (name + the real WellSky link, never
// ambiguous since it's a direct selection); typing past that without
// picking one falls back to plain free text via onChangeText, for a
// client not yet synced from WellSky.
const ClientNamePicker = ({ value, onChangeText, onSelectClient, onBlur, clients, disabled, placeholder = 'Client name' }) => {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const term = value.trim().toLowerCase();
  const filtered = term.length >= 2
    ? clients.filter(c => `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase().includes(term)).slice(0, 8)
    : [];

  // onSelectClient already persists the freshly-picked name/id directly
  // (see ActionTemplateDetail's selectClient) -- the input's own onBlur
  // still fires right after (clicking a suggestion moves focus away too),
  // and calling the parent's onBlur save there as well would read this
  // component's still-stale value/clientWellskyPatientId closure from
  // before the pick, potentially overwriting the correct just-saved data.
  // This flag lets that one onBlur skip its save instead.
  const justPickedRef = useRef(false);
  const pick = (c) => { justPickedRef.current = true; onSelectClient(c); setOpen(false); setHighlightedIndex(0); };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedIndex(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const c = filtered[highlightedIndex]; if (c) pick(c); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="relative">
      <input
        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm"
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={e => { onChangeText(e.target.value); setOpen(true); setHighlightedIndex(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setTimeout(() => setOpen(false), 150);
          if (justPickedRef.current) { justPickedRef.current = false; return; }
          onBlur?.();
        }}
        onKeyDown={handleKeyDown}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-52 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          {filtered.map((c, idx) => (
            <button
              type="button"
              key={c.id}
              onMouseDown={() => pick(c)}
              onMouseEnter={() => setHighlightedIndex(idx)}
              className={`w-full text-left px-3 py-2 text-sm transition flex items-center justify-between gap-2 ${idx === highlightedIndex ? 'bg-[#f0f5f0] text-[#2c4f26]' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <span className="truncate">{c.first_name} {c.last_name}</span>
              {c.wellsky_patient_id && <ExternalLink size={11} className="shrink-0 text-teal-600" />}
            </button>
          ))}
        </div>
      )}
      {open && term.length >= 2 && filtered.length === 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs text-gray-400">No WellSky client matches -- this will be saved as plain text.</div>
      )}
    </div>
  );
};

const CreateTodoModal = ({ staffUsers, profile, showToast, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [priority, setPriority] = useState('medium');
  const [recurrence, setRecurrence] = useState('');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [watcherIds, setWatcherIds] = useState([]);
  const [requiresAll, setRequiresAll] = useState(false);
  const [scheduleTask, setScheduleTask] = useState(false);
  const [visibleFromAt, setVisibleFromAt] = useState(toDatetimeLocalValue(Date.now()));
  const [saving, setSaving] = useState(false);
  // Staged locally and only actually uploaded once the task row exists
  // (todo_attachments needs a real todo_id) -- same storage bucket and
  // table TodoDetailModal's own attachment picker uses.
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = React.useRef(null);

  const handleFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0) return;
    setPendingFiles(prev => [...prev, ...picked]);
  };
  const removePendingFile = (idx) => setPendingFiles(prev => prev.filter((_, i) => i !== idx));

  const toggleAssignee = (id) => setAssigneeIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  const assignEveryone = () => setAssigneeIds(staffUsers.filter(s => s.id !== profile?.id).map(s => s.id));
  const toggleWatcher = (id) => setWatcherIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  const watchEveryoneElse = () => setWatcherIds(staffUsers.filter(s => s.id !== profile?.id && !assigneeIds.includes(s.id)).map(s => s.id));

  const create = async () => {
    if (!title.trim()) { showToast('Title is required', 'error'); return; }
    if (recurrence && !dueDate) { showToast('A repeating task needs a due date — that\'s what each next occurrence is scheduled from', 'error'); return; }
    setSaving(true);
    try {
      const visibleFromIso = scheduleTask ? new Date(visibleFromAt).toISOString() : null;
      // If it's scheduled for later, don't ping assignees/watchers about a
      // task they can't even see yet -- it'll surface on its own (board and
      // daily digest) once visible_from arrives.
      const scheduledForFuture = !!visibleFromIso && new Date(visibleFromIso) > new Date();
      // A Tiptap "empty" doc still serializes as e.g. "<p></p>", so
      // stripping tags (and checking for a leftover image) is what
      // actually tells us whether there's real content, not a plain
      // .trim() against the HTML -- same check FaqEditor uses.
      const hasDescription = description && (description.replace(/<[^>]+>/g, '').trim() !== '' || description.includes('<img'));
      const { data: todo, error } = await supabase.from('todos').insert({
        title: title.trim(), description: hasDescription ? description : null, description_format: 'html', due_date: dueDate || null, due_time: dueDate ? (dueTime || null) : null,
        priority, created_by: profile?.id || null, requires_all_assignees: requiresAll, recurrence: recurrence || null,
        recurrence_interval: recurrence === 'weekly' ? recurrenceInterval : 1, visible_from: visibleFromIso,
      }).select('*').single();
      if (error) throw error;
      if (assigneeIds.length > 0) {
        const { error: assignErr } = await supabase.from('todo_assignees').insert(assigneeIds.map(user_id => ({ todo_id: todo.id, user_id })));
        if (assignErr) throw assignErr;
        if (!scheduledForFuture) {
          supabase.functions.invoke('send-todo-notification', { body: { type: 'assigned', todoId: todo.id, newAssigneeIds: assigneeIds } }).catch(() => {});
        }
      }
      if (watcherIds.length > 0) {
        const { error: watchErr } = await supabase.from('todo_watchers').insert(watcherIds.map(user_id => ({ todo_id: todo.id, user_id })));
        if (watchErr) throw watchErr;
        if (!scheduledForFuture) {
          supabase.functions.invoke('send-todo-notification', { body: { type: 'watcher_added', todoId: todo.id, newWatcherIds: watcherIds } }).catch(() => {});
        }
      }
      let attachmentError = null;
      if (pendingFiles.length > 0) {
        try {
          for (const file of pendingFiles) {
            const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
            const path = `${todo.id}/${crypto.randomUUID()}${ext ? `.${ext}` : ''}`;
            const { error: uploadError } = await supabase.storage.from('todo-attachments').upload(path, file);
            if (uploadError) throw uploadError;
            const { error: insertError } = await supabase.from('todo_attachments').insert({
              todo_id: todo.id, uploaded_by: profile?.id || null,
              file_name: file.name, storage_path: path, file_size: file.size, content_type: file.type,
            });
            if (insertError) throw insertError;
          }
        } catch (err) {
          // The task itself is already created at this point -- a failed
          // attachment upload shouldn't look like the whole task failed.
          attachmentError = err;
        }
      }
      showToast(attachmentError ? (attachmentError.message || 'Task created, but a file failed to upload') : 'Task created', attachmentError ? 'error' : undefined);
      onCreated();
    } catch (err) {
      showToast(err.message || 'Failed to create task', 'error');
    } finally {
      setSaving(false);
    }
  };

  const inp = 'w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm';
  const lbl = 'block text-xs font-semibold text-gray-600 mb-1';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-lg font-bold text-gray-900">New Task</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col md:flex-row md:items-stretch">
            <div className="flex-1 min-w-0 p-6 space-y-4 md:border-r md:border-gray-100">
              <div>
                <label className={lbl}>Title</label>
                <input autoFocus className={inp} value={title} onChange={e => setTitle(e.target.value)} placeholder="What needs to get done?" />
              </div>
              <div>
                <label className={lbl}>Description</label>
                <FaqRichEditor
                  content={description}
                  onChange={setDescription}
                  showToast={showToast}
                  bucket="task-images"
                  minHeight="12rem"
                  placeholder="Add any details… (paste a screenshot directly)"
                />
              </div>
              <div className="pt-1 border-t border-gray-100">
                <div className="flex items-center justify-between mt-4 mb-2">
                  <label className={`${lbl} mb-0`}>Attachments{pendingFiles.length > 0 ? ` (${pendingFiles.length})` : ''}</label>
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1 text-xs font-semibold text-[#2c4f26] hover:bg-[#f0f5f0] px-2 py-1 rounded-lg transition">
                    <Upload size={12} /> Add File
                  </button>
                  <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFilePick} />
                </div>
                {pendingFiles.length === 0 ? (
                  <p className="text-xs text-gray-400 pb-2">No files attached</p>
                ) : (
                  <div className="space-y-1.5 pb-2">
                    {pendingFiles.map((file, idx) => (
                      <div key={`${file.name}-${idx}`} className="flex items-center justify-between gap-2 px-2.5 py-1.5 border border-gray-100 rounded-lg text-sm">
                        <span className="flex items-center gap-1.5 min-w-0 text-gray-700">
                          <FileText size={13} className="shrink-0 text-gray-400" />
                          <span className="truncate">{file.name}</span>
                        </span>
                        <button type="button" onClick={() => removePendingFile(idx)} className="text-gray-300 hover:text-red-500 transition shrink-0"><X size={13} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="md:w-80 shrink-0 bg-gray-50 p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Due Date</label>
                  <input type="date" className={`${inp} bg-white`} value={dueDate} onChange={e => { setDueDate(e.target.value); if (!e.target.value) setDueTime(''); }} />
                </div>
                <div>
                  <label className={lbl}>Due Time <span className="font-normal text-gray-400 normal-case">— optional</span></label>
                  <input type="time" className={`${inp} bg-white`} value={dueTime} onChange={e => setDueTime(e.target.value)} disabled={!dueDate} />
                </div>
                <div>
                  <label className={lbl}>Priority</label>
                  <select className={`${inp} bg-white`} value={priority} onChange={e => setPriority(e.target.value)}>
                    {TODO_PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={lbl}>Repeat</label>
                <div className="flex gap-2">
                  <select className={`${inp} bg-white`} value={recurrence} onChange={e => setRecurrence(e.target.value)}>
                    {TODO_RECURRENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {recurrence === 'weekly' && (
                    <select className={`${inp} bg-white w-28 shrink-0`} value={recurrenceInterval} onChange={e => setRecurrenceInterval(Number(e.target.value))}>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(n => <option key={n} value={n}>Every {n} wk{n === 1 ? '' : 's'}</option>)}
                    </select>
                  )}
                </div>
                {recurrence && <p className="text-xs text-gray-400 mt-1">Marking this task done will automatically create the next one, due {todoRecurrenceUnitPhrase(recurrence, recurrenceInterval)} later. It stays off the board until that due date arrives.</p>}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={lbl + ' mb-0'}>Assigned To</label>
                  <button type="button" onClick={assignEveryone} className="text-xs font-semibold text-[#2c4f26] hover:underline">Assign Everyone</button>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {staffUsers.filter(s => assigneeIds.includes(s.id)).map(s => (
                    <span key={s.id} className="inline-flex items-center gap-1 bg-[#f0f5f0] text-[#2c4f26] text-xs font-semibold px-2.5 py-1 rounded-full">
                      {s.full_name}
                      <button type="button" onClick={() => toggleAssignee(s.id)} className="hover:text-red-500 transition ml-0.5"><X size={11} /></button>
                    </span>
                  ))}
                  {assigneeIds.length === 0 && <span className="text-xs text-gray-400">No one yet</span>}
                </div>
                <AssigneePicker staffUsers={staffUsers.filter(s => !assigneeIds.includes(s.id))} onPick={toggleAssignee} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={lbl + ' mb-0'}>Watching <span className="font-normal text-gray-400 normal-case">— visibility only</span></label>
                  <button type="button" onClick={watchEveryoneElse} className="text-xs font-semibold text-[#2c4f26] hover:underline">Watch Everyone Else</button>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {staffUsers.filter(s => watcherIds.includes(s.id)).map(s => (
                    <span key={s.id} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                      <Eye size={11} /> {s.full_name}
                      <button type="button" onClick={() => toggleWatcher(s.id)} className="hover:text-red-500 transition ml-0.5"><X size={11} /></button>
                    </span>
                  ))}
                  {watcherIds.length === 0 && <span className="text-xs text-gray-400">No one watching</span>}
                </div>
                <AssigneePicker staffUsers={staffUsers.filter(s => !assigneeIds.includes(s.id) && !watcherIds.includes(s.id))} onPick={toggleWatcher} placeholder="Add a watcher…" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={requiresAll} onChange={e => setRequiresAll(e.target.checked)} />
                <span className="text-xs font-semibold text-gray-800 inline-flex items-center gap-1">
                  Group task
                  <span title="Require every assignee to complete their own part before this task is marked done. Otherwise, any one assignee finishing it completes it for everyone."><HelpCircle size={12} className="text-gray-400" /></span>
                </span>
              </label>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={scheduleTask} onChange={e => setScheduleTask(e.target.checked)} />
                  <span className="text-xs font-semibold text-gray-800 inline-flex items-center gap-1">
                    Schedule for later
                    <span title="Hide this task from the board (and from assignees) until a date/time you pick, instead of showing it right away."><HelpCircle size={12} className="text-gray-400" /></span>
                  </span>
                </label>
                {scheduleTask && (
                  <input type="datetime-local" className="w-full mt-2 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition" value={visibleFromAt} onChange={e => setVisibleFromAt(e.target.value)} />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">Cancel</button>
          <button onClick={create} disabled={saving} className="px-5 py-2 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50">
            {saving ? 'Creating…' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TodoDetailModal = ({ todo, staffUsers, profile, permissions, showToast, onClose, onUpdate, onDeleted }) => {
  const [form, setForm] = useState({
    title: todo.title || '', description: todo.description || '',
    due_date: todo.due_date || '', due_time: todo.due_time || '', priority: todo.priority || 'medium',
    requires_all_assignees: todo.requires_all_assignees || false,
    recurrence: todo.recurrence || '', recurrence_interval: todo.recurrence_interval || 1,
  });
  const [scheduleTask, setScheduleTask] = useState(!!todo.visible_from);
  const [visibleFromAt, setVisibleFromAt] = useState(toDatetimeLocalValue(todo.visible_from || Date.now()));
  const [savingForm, setSavingForm] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingAssignee, setSavingAssignee] = useState(false);
  const [savingWatcher, setSavingWatcher] = useState(false);
  const [togglingCompletion, setTogglingCompletion] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const canDelete = profile?.role === 'owner' || permissions?.can_delete_todos || todo.created_by === profile?.id;
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [noteBody, setNoteBody] = useState('');
  const [postingNote, setPostingNote] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(true);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = React.useRef(null);

  const assigneeIds = todoAssigneeIds(todo);
  const assignees = (todo.todo_assignees || []).filter(a => a.profiles).map(a => ({ ...a.profiles, completed_at: a.completed_at || null }));
  const availableToAdd = staffUsers.filter(s => !assigneeIds.includes(s.id));
  const watcherIds = (todo.todo_watchers || []).map(w => w.user_id);
  const watchers = (todo.todo_watchers || []).filter(w => w.profiles).map(w => w.profiles);
  const availableToWatch = staffUsers.filter(s => !assigneeIds.includes(s.id) && !watcherIds.includes(s.id));
  // Only the creator (or an owner) can redefine the task itself -- enforced
  // for real by a DB trigger, this just keeps the UI from showing editable
  // fields/a Save button that would fail. Assignees still change status and
  // post notes below, which is how they report progress.
  const canEditCore = profile?.role === 'owner' || todo.created_by === profile?.id;
  // Watchers get read-only visibility + notes, not status controls -- "no
  // action required" is the whole point, so someone who's only watching
  // (not also an assignee, the creator, or an owner) doesn't see Start/Mark
  // Done/Reopen at all.
  const canActOnStatus = canEditCore || assigneeIds.includes(profile?.id);

  const loadNotes = async () => {
    setLoadingNotes(true);
    const { data } = await supabase.from('todo_notes').select('*, profiles(full_name)').eq('todo_id', todo.id).order('created_at', { ascending: false });
    setNotes(data || []);
    setLoadingNotes(false);
  };
  useEffect(() => { loadNotes(); }, [todo.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAttachments = async () => {
    setLoadingAttachments(true);
    const { data } = await supabase.from('todo_attachments').select('*, profiles(full_name)').eq('todo_id', todo.id).order('created_at', { ascending: false });
    setAttachments(data || []);
    setLoadingAttachments(false);
  };
  useEffect(() => { loadAttachments(); }, [todo.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAttachmentPick = async (e) => {
    const pickedFiles = Array.from(e.target.files || []);
    e.target.value = '';
    if (pickedFiles.length === 0) return;
    setUploadingAttachment(true);
    try {
      for (const file of pickedFiles) {
        const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
        const path = `${todo.id}/${crypto.randomUUID()}${ext ? `.${ext}` : ''}`;
        const { error: uploadError } = await supabase.storage.from('todo-attachments').upload(path, file);
        if (uploadError) throw uploadError;
        const { error: insertError } = await supabase.from('todo_attachments').insert({
          todo_id: todo.id, uploaded_by: profile?.id || null,
          file_name: file.name, storage_path: path, file_size: file.size, content_type: file.type,
        });
        if (insertError) throw insertError;
      }
      loadAttachments();
    } catch (err) {
      showToast(err.message || 'Failed to upload file', 'error');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const downloadAttachment = async (att) => {
    const { data, error } = await supabase.storage.from('todo-attachments').createSignedUrl(att.storage_path, 300);
    if (error || !data?.signedUrl) { showToast('Failed to open file', 'error'); return; }
    window.open(data.signedUrl, '_blank', 'noopener');
  };

  const deleteAttachment = async (att) => {
    try {
      await supabase.storage.from('todo-attachments').remove([att.storage_path]);
      const { error } = await supabase.from('todo_attachments').delete().eq('id', att.id);
      if (error) throw error;
      setAttachments(prev => prev.filter(a => a.id !== att.id));
    } catch (err) {
      showToast(err.message || 'Failed to delete file', 'error');
    }
  };

  const saveForm = async () => {
    if (form.recurrence && !form.due_date) { showToast('A repeating task needs a due date — that\'s what each next occurrence is scheduled from', 'error'); return; }
    setSavingForm(true);
    // A Tiptap "empty" doc still serializes as e.g. "<p></p>", so a plain
    // .trim() against the HTML never nulls out a cleared html-format
    // description the way it does for a plain-text markdown one.
    const descriptionValue = todo.description_format === 'html'
      ? ((form.description && (form.description.replace(/<[^>]+>/g, '').trim() !== '' || form.description.includes('<img'))) ? form.description : null)
      : (form.description.trim() || null);
    try {
      const update = { title: form.title.trim(), description: descriptionValue, due_date: form.due_date || null, due_time: form.due_date ? (form.due_time || null) : null, priority: form.priority, requires_all_assignees: form.requires_all_assignees, recurrence: form.recurrence || null, recurrence_interval: form.recurrence === 'weekly' ? form.recurrence_interval : 1, visible_from: scheduleTask ? new Date(visibleFromAt).toISOString() : null };
      const { error } = await supabase.from('todos').update(update).eq('id', todo.id);
      if (error) throw error;
      showToast('Saved');
      onUpdate({ ...todo, ...update });
      supabase.functions.invoke('send-todo-notification', { body: { type: 'updated', todoId: todo.id } }).catch(() => {});
    } catch (err) {
      showToast(err.message || 'Failed to save', 'error');
    } finally {
      setSavingForm(false);
    }
  };

  // Caller's own completion only -- flips their todo_assignees.completed_at
  // and lets complete_my_assignment() recompute the shared status from how
  // many assignees are done. Direct status writes are rejected server-side
  // for group tasks, so this RPC is the only path that works here.
  const toggleMyCompletion = async (completed) => {
    setTogglingCompletion(true);
    try {
      const { error } = await supabase.rpc('complete_my_assignment', { p_todo_id: todo.id, p_completed: completed });
      if (error) throw error;
      const updatedAssignees = (todo.todo_assignees || []).map(a => a.user_id === profile?.id ? { ...a, completed_at: completed ? new Date().toISOString() : null } : a);
      const total = updatedAssignees.length;
      const done = updatedAssignees.filter(a => a.completed_at).length;
      const status = total === 0 ? todo.status : done === 0 ? 'not_started' : done === total ? 'done' : 'in_progress';
      onUpdate({ ...todo, todo_assignees: updatedAssignees, status, completed_at: status === 'done' ? new Date().toISOString() : null });
      if (completed) {
        supabase.functions.invoke('send-todo-notification', { body: { type: 'assignee_completed', todoId: todo.id } }).catch(() => {});
      }
      if (status === 'done' && todo.status !== 'done') {
        supabase.functions.invoke('send-todo-notification', { body: { type: 'completed', todoId: todo.id } }).catch(() => {});
      }
    } catch (err) {
      showToast(err.message || 'Failed to update', 'error');
    } finally {
      setTogglingCompletion(false);
    }
  };

  const sendReminder = async () => {
    setSendingReminder(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-todo-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ type: 'reminder', todoId: todo.id }),
      });
      const result = await res.json();
      if (!result.ok) throw new Error(result.error || 'Failed to send reminder');
      showToast(result.remindedCount > 0 ? `Reminder sent to ${result.remindedCount} ${result.remindedCount === 1 ? 'person' : 'people'}` : 'Nobody left to remind — everyone has already completed their part');
    } catch (err) {
      showToast(err.message || 'Failed to send reminder', 'error');
    } finally {
      setSendingReminder(false);
    }
  };

  const changeStatus = async (status) => {
    setSavingStatus(true);
    try {
      const { error } = await supabase.from('todos').update({ status }).eq('id', todo.id);
      if (error) throw error;
      showToast(status === 'done' ? 'Marked done' : 'Updated');
      onUpdate({ ...todo, status, completed_at: status === 'done' ? new Date().toISOString() : null });
      if (status === 'done') {
        supabase.functions.invoke('send-todo-notification', { body: { type: 'completed', todoId: todo.id } }).catch(() => {});
      }
    } catch (err) {
      showToast(err.message || 'Failed to update status', 'error');
    } finally {
      setSavingStatus(false);
    }
  };

  const addAssignee = async (userId) => {
    if (!userId) return;
    setSavingAssignee(true);
    try {
      const { error } = await supabase.from('todo_assignees').insert({ todo_id: todo.id, user_id: userId });
      if (error) throw error;
      const added = staffUsers.find(s => s.id === userId);
      const updatedAssignees = [...(todo.todo_assignees || []), { user_id: userId, completed_at: null, profiles: added }];
      onUpdate({ ...todo, todo_assignees: updatedAssignees });
      showToast('Assignee added');
      supabase.functions.invoke('send-todo-notification', { body: { type: 'assigned', todoId: todo.id, newAssigneeIds: [userId] } }).catch(() => {});
    } catch (err) {
      showToast(err.message || 'Failed to add assignee', 'error');
    } finally {
      setSavingAssignee(false);
    }
  };

  const addEveryone = async () => {
    const ids = availableToAdd.filter(s => s.id !== profile?.id).map(s => s.id);
    if (ids.length === 0) return;
    setSavingAssignee(true);
    try {
      const { error } = await supabase.from('todo_assignees').insert(ids.map(user_id => ({ todo_id: todo.id, user_id })));
      if (error) throw error;
      const addedRows = ids.map(user_id => ({ user_id, completed_at: null, profiles: staffUsers.find(s => s.id === user_id) }));
      onUpdate({ ...todo, todo_assignees: [...(todo.todo_assignees || []), ...addedRows] });
      showToast('Assigned everyone');
      supabase.functions.invoke('send-todo-notification', { body: { type: 'assigned', todoId: todo.id, newAssigneeIds: ids } }).catch(() => {});
    } catch (err) {
      showToast(err.message || 'Failed to assign everyone', 'error');
    } finally {
      setSavingAssignee(false);
    }
  };

  const removeAssignee = async (userId) => {
    try {
      const { error } = await supabase.from('todo_assignees').delete().eq('todo_id', todo.id).eq('user_id', userId);
      if (error) throw error;
      onUpdate({ ...todo, todo_assignees: (todo.todo_assignees || []).filter(a => a.user_id !== userId) });
    } catch (err) {
      showToast(err.message || 'Failed to remove assignee', 'error');
    }
  };

  const addWatcher = async (userId) => {
    if (!userId) return;
    setSavingWatcher(true);
    try {
      const { error } = await supabase.from('todo_watchers').insert({ todo_id: todo.id, user_id: userId });
      if (error) throw error;
      const added = staffUsers.find(s => s.id === userId);
      onUpdate({ ...todo, todo_watchers: [...(todo.todo_watchers || []), { user_id: userId, profiles: added }] });
      showToast('Watcher added');
      supabase.functions.invoke('send-todo-notification', { body: { type: 'watcher_added', todoId: todo.id, newWatcherIds: [userId] } }).catch(() => {});
    } catch (err) {
      showToast(err.message || 'Failed to add watcher', 'error');
    } finally {
      setSavingWatcher(false);
    }
  };

  const removeWatcher = async (userId) => {
    try {
      const { error } = await supabase.from('todo_watchers').delete().eq('todo_id', todo.id).eq('user_id', userId);
      if (error) throw error;
      onUpdate({ ...todo, todo_watchers: (todo.todo_watchers || []).filter(w => w.user_id !== userId) });
    } catch (err) {
      showToast(err.message || 'Failed to remove watcher', 'error');
    }
  };

  const deleteTodo = async () => {
    if (!window.confirm('Delete this task? This cannot be undone.')) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from('todos').delete().eq('id', todo.id);
      if (error) throw error;
      showToast('Task deleted');
      onDeleted();
    } catch (err) {
      showToast(err.message || 'Failed to delete task', 'error');
      setDeleting(false);
    }
  };

  const postNote = async () => {
    if (!noteBody.trim()) return;
    setPostingNote(true);
    try {
      const trimmed = noteBody.trim();
      const { error } = await supabase.from('todo_notes').insert({ todo_id: todo.id, author_id: profile?.id || null, body: trimmed });
      if (error) throw error;
      setNoteBody('');
      loadNotes();
      supabase.functions.invoke('send-todo-notification', { body: { type: 'comment', todoId: todo.id, noteBody: trimmed } }).catch(() => {});
    } catch (err) {
      showToast(err.message || 'Failed to post note', 'error');
    } finally {
      setPostingNote(false);
    }
  };

  const inp = 'w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm';
  const lbl = 'block text-xs font-semibold text-gray-600 mb-1';

  const statusBadge = (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${todo.status === 'done' ? 'bg-green-100 text-green-700' : todo.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
      {TODO_STATUS_COLUMNS.find(c => c.key === todo.status)?.label || todo.status}
    </span>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${TODO_PRIORITY_MAP[todo.priority]?.color || 'bg-gray-400'}`} />
            <h2 className="text-lg font-bold text-gray-900">Task</h2>
            {todo.wellsky_patient_id && wellskyClientUrl(todo.wellsky_patient_id) && (
              <a href={wellskyClientUrl(todo.wellsky_patient_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded hover:bg-teal-100 transition">
                View in WellSky <ExternalLink size={10} />
              </a>
            )}
          </div>
          <div className="flex items-center gap-3">
            {canDelete && (
              <button onClick={deleteTodo} disabled={deleting} title="Delete task" className="text-gray-400 hover:text-red-600 transition disabled:opacity-50"><Trash2 size={18} /></button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col md:flex-row md:items-stretch">
            {/* Main column: title, description, notes/activity */}
            <div className="flex-1 min-w-0 p-6 space-y-5 md:border-r md:border-gray-100">
              {canEditCore ? (
                <>
                  <div>
                    <label className={lbl}>Title</label>
                    <input className={inp} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                  </div>
                  {/* description_format is set once at creation and never
                      changes -- a task created before the WYSIWYG editor
                      shipped keeps editing as plain markdown source here,
                      so its existing **bold**-style text doesn't reflow or
                      need to be rewritten. Only new tasks get the rich
                      editor. */}
                  {todo.description_format === 'html' ? (
                    <div>
                      <label className={lbl}>Description</label>
                      <FaqRichEditor
                        content={form.description}
                        onChange={v => setForm(f => ({ ...f, description: v }))}
                        showToast={showToast}
                        bucket="task-images"
                        minHeight="9rem"
                      />
                    </div>
                  ) : (
                    <MarkdownEditorField
                      label="Description"
                      value={form.description}
                      onChange={v => setForm(f => ({ ...f, description: v }))}
                      showToast={showToast}
                      bucket="task-images"
                      rows={6}
                      minHeight="9rem"
                    />
                  )}
                </>
              ) : (
                <>
                  <div>
                    <p className={lbl}>Title</p>
                    <p className="text-sm text-gray-900">{todo.title}</p>
                  </div>
                  {todo.description && (
                    <div>
                      <p className={lbl}>Description</p>
                      {todo.description_format === 'html' ? (
                        <div className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: faqSanitize(todo.description) }} />
                      ) : (
                        <div className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: renderMarkdown(todo.description) }} />
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-400">Only the creator can edit these fields.</p>
                </>
              )}

              <div className="pt-1 border-t border-gray-100">
                <div className="flex items-center justify-between mt-4 mb-2">
                  <label className={`${lbl} mb-0`}>Attachments{attachments.length > 0 ? ` (${attachments.length})` : ''}</label>
                  <button onClick={() => attachmentInputRef.current?.click()} disabled={uploadingAttachment} className="inline-flex items-center gap-1 text-xs font-semibold text-[#2c4f26] hover:bg-[#f0f5f0] px-2 py-1 rounded-lg transition disabled:opacity-50">
                    <Upload size={12} /> {uploadingAttachment ? 'Uploading…' : 'Add File'}
                  </button>
                  <input ref={attachmentInputRef} type="file" multiple className="hidden" onChange={handleAttachmentPick} />
                </div>
                {loadingAttachments ? (
                  <div className="text-center py-3"><div className="w-5 h-5 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin mx-auto" /></div>
                ) : attachments.length === 0 ? (
                  <p className="text-xs text-gray-400 pb-2">No files attached</p>
                ) : (
                  <div className="space-y-1.5 pb-2">
                    {attachments.map(att => (
                      <div key={att.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 border border-gray-100 rounded-lg text-sm hover:bg-gray-50 transition">
                        <button onClick={() => downloadAttachment(att)} className="flex items-center gap-1.5 min-w-0 text-gray-700 hover:text-[#2c4f26] transition text-left">
                          <FileText size={13} className="shrink-0 text-gray-400" />
                          <span className="truncate">{att.file_name}</span>
                        </button>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[11px] text-gray-400">{att.profiles?.full_name || 'Unknown'}</span>
                          {(att.uploaded_by === profile?.id || canDelete) && (
                            <button onClick={() => deleteAttachment(att)} className="text-gray-300 hover:text-red-500 transition"><X size={13} /></button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-1 border-t border-gray-100">
                <label className={`${lbl} mt-4 block`}>Notes</label>
                <div className="mb-3">
                  <textarea
                    rows={3}
                    className={`${inp} resize-y min-h-[5rem]`}
                    placeholder="Post an update…"
                    value={noteBody}
                    onChange={e => setNoteBody(e.target.value)}
                    onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') postNote(); }}
                  />
                  <div className="flex justify-end mt-1.5">
                    <button onClick={postNote} disabled={postingNote || !noteBody.trim()} className="px-3 py-1.5 text-xs font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50 shrink-0">
                      Post
                    </button>
                  </div>
                </div>
                {loadingNotes ? (
                  <div className="text-center py-4"><div className="w-5 h-5 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin mx-auto" /></div>
                ) : notes.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-2">No notes yet</p>
                ) : (
                  <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
                    {notes.map(n => (
                      <div key={n.id} className="text-sm">
                        <p className="text-gray-800 whitespace-pre-wrap">{n.body}</p>
                        <p className="text-xs text-gray-400">{n.profiles?.full_name || 'Unknown'} · {hiringFmtSyncAgo(n.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar: status, scheduling, and who's on the task */}
            <div className="md:w-80 shrink-0 bg-gray-50 p-6 space-y-5">
              <div>
                <label className={lbl}>Status</label>
                {todo.requires_all_assignees ? (
                  <div className="space-y-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${todo.status === 'done' ? 'bg-green-100 text-green-700' : todo.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                      {TODO_STATUS_COLUMNS.find(c => c.key === todo.status)?.label || todo.status} · {assignees.filter(a => a.completed_at).length}/{assignees.length} complete
                    </span>
                    {(() => {
                      const myAssignment = assignees.find(a => a.id === profile?.id);
                      if (!myAssignment) return null;
                      return myAssignment.completed_at ? (
                        <button onClick={() => toggleMyCompletion(false)} disabled={togglingCompletion} className="w-full flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition disabled:opacity-50">
                          <RotateCcw size={13} /> Undo My Completion
                        </button>
                      ) : (
                        <button onClick={() => toggleMyCompletion(true)} disabled={togglingCompletion} className="w-full flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg shadow-sm transition disabled:opacity-50">
                          <Check size={14} strokeWidth={3} /> Mark My Part Complete
                        </button>
                      );
                    })()}
                    <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden bg-white">
                      {assignees.map(a => (
                        <label key={a.id} className={`flex items-center justify-between gap-3 px-3 py-2 text-sm ${a.id === profile?.id ? 'cursor-pointer hover:bg-gray-50' : ''}`}>
                          <span className="text-gray-800">{a.full_name}{a.id === profile?.id ? ' (you)' : ''}</span>
                          <input
                            type="checkbox"
                            checked={!!a.completed_at}
                            disabled={a.id !== profile?.id || togglingCompletion}
                            onChange={e => toggleMyCompletion(e.target.checked)}
                          />
                        </label>
                      ))}
                      {assignees.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">Assign someone to start tracking completion.</p>}
                    </div>
                  </div>
                ) : canActOnStatus ? (
                  <div className="grid grid-cols-2 gap-2">
                    {todo.status !== 'in_progress' && todo.status !== 'done' && (
                      <button onClick={() => changeStatus('in_progress')} disabled={savingStatus} className="flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition disabled:opacity-50">
                        <Play size={13} className="fill-current" /> Start
                      </button>
                    )}
                    {todo.status !== 'done' && (
                      <button onClick={() => changeStatus('done')} disabled={savingStatus} className={`flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg shadow-sm transition disabled:opacity-50 ${todo.status === 'in_progress' ? 'col-span-2' : ''}`}>
                        <Check size={14} strokeWidth={3} /> Mark Completed
                      </button>
                    )}
                    {todo.status === 'done' && (
                      <button onClick={() => changeStatus('not_started')} disabled={savingStatus} className="col-span-2 flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg shadow-sm transition disabled:opacity-50">
                        <RotateCcw size={13} /> Reopen
                      </button>
                    )}
                  </div>
                ) : statusBadge}
                {todo.status === 'done' && todo.completed_at && <p className="text-xs font-semibold text-green-700 mt-2">Completed {todoFmtDateTime(todo.completed_at)}</p>}
              </div>

              {canEditCore && todo.status !== 'done' && (
                <button onClick={sendReminder} disabled={sendingReminder} className="w-full flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition disabled:opacity-50">
                  <Bell size={13} /> {sendingReminder ? 'Sending…' : 'Send Reminder'}
                </button>
              )}

              {canEditCore ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={lbl}>Due Date</label>
                      <input type="date" className={`${inp} bg-white`} value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value, due_time: e.target.value ? f.due_time : '' }))} />
                    </div>
                    <div>
                      <label className={lbl}>Due Time <span className="font-normal text-gray-400 normal-case">— optional</span></label>
                      <input type="time" className={`${inp} bg-white`} value={form.due_time} onChange={e => setForm(f => ({ ...f, due_time: e.target.value }))} disabled={!form.due_date} />
                    </div>
                    <div>
                      <label className={lbl}>Priority</label>
                      <select className={`${inp} bg-white`} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                        {TODO_PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={lbl}>Repeat</label>
                    <div className="flex gap-2">
                      <select className={`${inp} bg-white`} value={form.recurrence} onChange={e => setForm(f => ({ ...f, recurrence: e.target.value }))}>
                        {TODO_RECURRENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      {form.recurrence === 'weekly' && (
                        <select className={`${inp} bg-white w-28 shrink-0`} value={form.recurrence_interval} onChange={e => setForm(f => ({ ...f, recurrence_interval: Number(e.target.value) }))}>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(n => <option key={n} value={n}>Every {n} wk{n === 1 ? '' : 's'}</option>)}
                        </select>
                      )}
                    </div>
                    {form.recurrence && <p className="text-xs text-gray-400 mt-1">Marking this task done creates the next one, due {todoRecurrenceUnitPhrase(form.recurrence, form.recurrence_interval)} later. It stays off the board until then.</p>}
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.requires_all_assignees} onChange={e => setForm(f => ({ ...f, requires_all_assignees: e.target.checked }))} />
                    <span className="text-xs font-semibold text-gray-800 inline-flex items-center gap-1">
                      Group task
                      <span title="Require every assignee to complete their own part before this task is marked done. Otherwise, any one assignee finishing it completes it for everyone."><HelpCircle size={12} className="text-gray-400" /></span>
                    </span>
                  </label>
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={scheduleTask} onChange={e => setScheduleTask(e.target.checked)} />
                      <span className="text-xs font-semibold text-gray-800 inline-flex items-center gap-1">
                        Schedule for later
                        <span title="Hide this task from the board (and from assignees) until a date/time you pick."><HelpCircle size={12} className="text-gray-400" /></span>
                      </span>
                    </label>
                    {scheduleTask && (
                      <input type="datetime-local" className={`${inp} bg-white mt-2`} value={visibleFromAt} onChange={e => setVisibleFromAt(e.target.value)} />
                    )}
                  </div>
                  <button onClick={saveForm} disabled={savingForm} className="w-full px-4 py-2 text-xs font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50">
                    {savingForm ? 'Saving…' : 'Save'}
                  </button>
                </>
              ) : (
                <div className="space-y-1.5">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className={lbl}>Due Date</p>
                      <p className="text-sm text-gray-700">
                        {todo.due_date ? new Date(todo.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        {todo.due_date && todo.due_time && ` at ${new Date(`${todo.due_date}T${todo.due_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
                      </p>
                    </div>
                    <div>
                      <p className={lbl}>Priority</p>
                      <p className="text-sm text-gray-700">{TODO_PRIORITY_MAP[todo.priority]?.label || todo.priority}</p>
                    </div>
                  </div>
                  {todo.requires_all_assignees && <p className="text-xs font-semibold text-[#2c4f26]">Group task — everyone assigned must complete their own part.</p>}
                  {todo.recurrence && <p className="text-xs font-semibold text-[#2c4f26]">Repeats {todoRecurrenceLabel(todo.recurrence, todo.recurrence_interval).toLowerCase()} — marking this done creates the next occurrence, hidden from the board until it's due.</p>}
                  {todo.visible_from && new Date(todo.visible_from) > new Date() && <p className="text-xs font-semibold text-amber-700">Scheduled — hidden from the board until {todoFmtDateTime(todo.visible_from)}.</p>}
                </div>
              )}

              <div className="pt-1 border-t border-gray-200">
                <div className="flex items-center justify-between mb-1 mt-4">
                  <label className={lbl + ' mb-0'}>Assigned To</label>
                  {canEditCore && availableToAdd.length > 0 && (
                    <button onClick={addEveryone} disabled={savingAssignee} className="text-xs font-semibold text-[#2c4f26] hover:underline disabled:opacity-50">Assign Everyone</button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {assignees.map(a => {
                    // Only the task's creator or an owner can remove someone
                    // else -- but anyone can remove themselves (leave a task
                    // they're on), same as before.
                    const canRemove = canEditCore || a.id === profile?.id;
                    return (
                      <span key={a.id} className="inline-flex items-center gap-1 bg-[#f0f5f0] text-[#2c4f26] text-xs font-semibold px-2.5 py-1 rounded-full">
                        {a.full_name}
                        {canRemove && <button onClick={() => removeAssignee(a.id)} className="hover:text-red-500 transition ml-0.5"><X size={11} /></button>}
                      </span>
                    );
                  })}
                  {assignees.length === 0 && <span className="text-xs text-gray-400">Unassigned</span>}
                </div>
                {canEditCore && availableToAdd.length > 0 && (
                  <AssigneePicker staffUsers={availableToAdd} onPick={addAssignee} disabled={savingAssignee} placeholder="Add someone…" />
                )}
              </div>

              <div className="pt-1 border-t border-gray-200">
                <label className={`${lbl} mt-4 block`}>Watching <span className="font-normal text-gray-400 normal-case">— visibility only</span></label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {watchers.map(w => (
                    <span key={w.id} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                      <Eye size={11} /> {w.full_name}
                      <button onClick={() => removeWatcher(w.id)} className="hover:text-red-500 transition ml-0.5"><X size={11} /></button>
                    </span>
                  ))}
                  {watchers.length === 0 && <span className="text-xs text-gray-400">No one watching</span>}
                </div>
                {availableToWatch.length > 0 && (
                  <AssigneePicker staffUsers={availableToWatch} onPick={addWatcher} disabled={savingWatcher} placeholder="Add a watcher…" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TodosTab = ({ showToast, profile, permissions }) => {
  const canViewAll = profile?.role === 'owner' || permissions?.can_view_all_todos;
  const canViewStaffingTab = profile?.role === 'owner' || permissions?.can_view_staffing_tab;
  const canViewFieldSupervisorsTab = profile?.role === 'owner' || permissions?.can_view_field_supervisors_tab;
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewFilter, setViewFilter] = useState('assigned');
  const [staffFilter, setStaffFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);

  const loadTodos = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('todos').select('*, creator:created_by(full_name), todo_assignees(user_id, completed_at, profiles(id, full_name, department)), todo_watchers(user_id, profiles(id, full_name))').order('created_at', { ascending: false });
      if (error) throw error;
      setTodos(data || []);
    } catch (err) {
      console.error('Error loading todos:', err);
      showToast('Failed to load tasks', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadTodos(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    supabase.from('profiles').select('id, full_name, email, department').eq('role', 'admin_staff').eq('status', 'active').order('full_name')
      .then(({ data }) => setStaffUsers(data || []));
  }, []);

  // Opens the task a /taskboard/<id> link (email, bookmark, shared URL)
  // pointed at, once the first load finishes -- "not found" covers both a
  // bad id and a real task this viewer just doesn't have RLS access to,
  // deliberately not distinguished so a guessed id can't be used to probe
  // which task ids exist.
  const [handledDeepLink, setHandledDeepLink] = useState(false);
  useEffect(() => {
    if (handledDeepLink || loading) return;
    setHandledDeepLink(true);
    const m = window.location.pathname.match(/^\/taskboard\/([^/]+)/);
    if (!m) return;
    const found = todos.find(t => t.id === m[1]);
    if (found) {
      setSelected(found);
    } else {
      showToast('That task was not found, or you do not have access to it', 'error');
      window.history.replaceState({}, '', '/taskboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, todos]);

  const openTodo = (todo) => {
    setSelected(todo);
    window.history.pushState({}, '', `/taskboard/${todo.id}`);
  };
  const closeTodo = () => {
    setSelected(null);
    window.history.pushState({}, '', '/taskboard');
  };

  // Watcher-only tasks used to show up mixed into "Assigned to Me" (tagged
  // with a small Watching badge) -- people kept losing track of their real
  // assignments among the "no action needed" ones, so watching now gets its
  // own tab instead of being folded in here.
  const isAssignedToMe = (todo) => todoAssigneeIds(todo).includes(profile?.id);
  const isAssignedByMe = (todo) => todo.created_by === profile?.id;
  const isWatchingByMe = (todo) => todoWatcherIds(todo).includes(profile?.id);
  // A freshly auto-generated recurring task (see todos_advance_recurring)
  // carries visible_from = its own due date, so completing this period's
  // task early doesn't surface next period's on the board right away --
  // same field now also doubles as a manual "schedule for later" picker.
  const isTodoVisible = (todo) => !todo.visible_from || new Date(todo.visible_from) <= new Date();
  const search = searchQuery.trim().toLowerCase();
  // Board columns: Not Started / In Progress / Watching -- Completed moved
  // out to its own tab (mirroring where Watching used to live) since a
  // finished task doesn't need the same "don't let this slip" visibility
  // that an in-flight or watched one does. Exception: "Assigned by Me" --
  // the global Completed tab is scoped to tasks assigned TO you (or
  // everyone's, with View All Tasks), so a task someone created and
  // handed off to someone else never showed up as done anywhere for them.
  // This column is the fix, scoped to exactly this tab since that's the
  // one place that gap existed.
  const boardColumns = [
    { key: 'not_started', label: 'Not Started', match: (t) => todoDisplayStatus(t, profile?.id) === 'not_started' },
    { key: 'in_progress', label: 'In Progress', match: (t) => todoDisplayStatus(t, profile?.id) === 'in_progress' },
    // Watching doesn't apply to "Assigned by Me" -- you watch tasks to
    // keep an eye on someone else's work, not your own handed-off ones.
    ...(viewFilter !== 'created' ? [{ key: 'watching', label: 'Watching', match: (t) => isWatchingByMe(t) }] : []),
    ...(viewFilter === 'created' ? [{ key: 'completed_by_me', label: 'Completed', match: (t) => todoDisplayStatus(t, profile?.id) === 'done', sort: todoDoneSort }] : []),
  ];
  // Staffing/Field Supervisors tabs: every task with at least one assignee
  // tagged into that department on their profile (see STAFF_DEPARTMENTS
  // and the Department field on Edit Permissions) -- department is group
  // membership, independent of who's viewing, so this ignores
  // isAssignedToMe/isAssignedByMe entirely and just looks at assignees.
  const todoHasAssigneeInDepartment = (todo, department) =>
    (todo.todo_assignees || []).some(a => a.profiles?.department === department);
  const visibleTodos = (
    viewFilter === 'all' && canViewAll ? todos :
    viewFilter === 'staffing' && canViewStaffingTab ? todos.filter(t => todoHasAssigneeInDepartment(t, 'Staffing')) :
    viewFilter === 'field_supervisors' && canViewFieldSupervisorsTab ? todos.filter(t => todoHasAssigneeInDepartment(t, 'Field Supervisors')) :
    viewFilter === 'created' ? todos.filter(isAssignedByMe) :
    todos.filter(isAssignedToMe)
  )
    .filter(isTodoVisible)
    .filter(t => !['all', 'staffing', 'field_supervisors'].includes(viewFilter) || !staffFilter || todoAssigneeIds(t).includes(staffFilter))
    .filter(t => !search || t.title?.toLowerCase().includes(search) || t.description?.toLowerCase().includes(search));
  // Excludes done tasks -- once a watched task is completed it moves out of
  // this live column and into the Watching column of the Completed tab
  // instead (completedWatchingTodos below), rather than lingering here
  // forever with nothing left to actually watch for. Uses todoDisplayStatus,
  // not raw status, for the same reason completedTodos below does.
  const watchingTodos = todos.filter(isTodoVisible).filter(isWatchingByMe).filter(t => todoDisplayStatus(t, profile?.id) !== 'done')
    .filter(t => !search || t.title?.toLowerCase().includes(search) || t.description?.toLowerCase().includes(search));
  // todoDisplayStatus, not raw status -- a group task's shared status stays
  // 'in_progress' until every assignee finishes, but a viewer who's
  // completed their own part needs to find it here rather than it falling
  // into the gap between the live board (which already stops showing it
  // under Not Started/In Progress once todoDisplayStatus says done for
  // them) and this tab (which used to only ever check the shared status).
  const completedTodos = (canViewAll ? todos : todos.filter(isAssignedToMe))
    .filter(isTodoVisible)
    .filter(t => todoDisplayStatus(t, profile?.id) === 'done')
    .filter(t => !canViewAll || !staffFilter || todoAssigneeIds(t).includes(staffFilter))
    .filter(t => !search || t.title?.toLowerCase().includes(search) || t.description?.toLowerCase().includes(search))
    .sort(todoDoneSort);
  // Completed tasks someone is only watching (not assigned) never showed up
  // in completedTodos above -- that's scoped to assignment -- so without
  // this they'd just disappear once finished instead of landing anywhere.
  const completedWatchingTodos = todos.filter(isTodoVisible).filter(isWatchingByMe)
    .filter(t => todoDisplayStatus(t, profile?.id) === 'done')
    .filter(t => !search || t.title?.toLowerCase().includes(search) || t.description?.toLowerCase().includes(search))
    .sort(todoDoneSort);
  // Scheduled-for-later tasks are invisible everywhere else on the board
  // (that's the point -- isTodoVisible filters them out of every other tab,
  // including for their own creator), so without this tab a creator has no
  // way to check what they've got queued up or waiting to recur. Scoped to
  // tasks THEY created, same as "Assigned by Me", since that's who'd want
  // to find it again -- canViewAll additionally sees everyone's.
  const scheduledTodos = (canViewAll ? todos : todos.filter(isAssignedByMe))
    .filter(t => t.visible_from && new Date(t.visible_from) > new Date())
    .filter(t => !search || t.title?.toLowerCase().includes(search) || t.description?.toLowerCase().includes(search))
    .sort((a, b) => new Date(a.visible_from) - new Date(b.visible_from));

  const updateTodoInList = (updated) => {
    setTodos(prev => prev.map(t => t.id === updated.id ? updated : t));
    setSelected(updated);
  };

  // Shared by both sections of the Completed tab (assigned-to-me/all vs.
  // watching-only) so the card markup isn't duplicated.
  const renderCompletedTodoCard = (todo) => {
    const urgent = todo.priority === 'urgent';
    const daysLeft = todoDaysLeftLabel(todo);
    const assignees = (todo.todo_assignees || []).filter(a => a.profiles).map(a => ({ ...a.profiles, completed_at: a.completed_at || null }));
    return (
      <div
        key={todo.id}
        onClick={() => openTodo(todo)}
        className="rounded-xl border border-gray-200 bg-white p-3 cursor-pointer hover:border-[#2c4f26] hover:shadow-sm transition"
      >
        <div className="flex items-start gap-2 mb-1.5">
          <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${TODO_PRIORITY_MAP[todo.priority]?.color || 'bg-gray-400'}`} />
          <p className="text-sm font-semibold text-gray-900 flex-1">{todo.title}</p>
          {urgent && (
            <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-red-600 text-white">Urgent</span>
          )}
        </div>
        <p className="text-xs text-gray-400 mb-1">
          Assigned {todoFmtShort(todo.created_at)}{todo.creator?.full_name ? ` by ${todo.creator.full_name}` : ''}
          {todo.due_date && (
            <> · <span className="text-gray-500">Due {todoFmtDue(todo)}</span></>
          )}
        </p>
        {todo.completed_at && <p className="text-xs text-green-700 font-medium mb-1">Completed {todoFmtDateTime(todo.completed_at)}</p>}
        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
          {daysLeft && (
            <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${daysLeft.cls}`}>{daysLeft.text}</span>
          )}
          {todo.requires_all_assignees && assignees.length > 0 && (
            <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#f0f5f0] text-[#2c4f26]">
              Group · {assignees.filter(a => a.completed_at).length}/{assignees.length} done
            </span>
          )}
          {todo.recurrence && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
              <RefreshCw size={9} /> {todoRecurrenceLabel(todo.recurrence, todo.recurrence_interval)}
            </span>
          )}
          {!todoAssigneeIds(todo).includes(profile?.id) && todoWatcherIds(todo).includes(profile?.id) && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
              <Eye size={9} /> Watching
            </span>
          )}
        </div>
        {assignees.length > 0 && (
          <div className="flex -space-x-1.5 mt-1">
            {assignees.map(a => (
              <span key={a.id} title={a.full_name} className="w-6 h-6 rounded-full bg-[#2c4f26] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
                {todoInitials(a.full_name)}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Task Board</h1>
          <p className="text-sm text-gray-600">{viewFilter === 'completed' ? completedTodos.length + completedWatchingTodos.length : viewFilter === 'scheduled' ? scheduledTodos.length : visibleTodos.length + watchingTodos.length} shown</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-44 sm:w-56 pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
              placeholder="Search tasks…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            {[['assigned', 'Assigned to Me'], ['created', 'Assigned by Me'], ...(canViewAll ? [['all', 'All']] : []), ...(canViewStaffingTab ? [['staffing', 'Staffing']] : []), ...(canViewFieldSupervisorsTab ? [['field_supervisors', 'Field Supervisors']] : []), ...(scheduledTodos.length > 0 ? [['scheduled', 'Scheduled']] : []), ['completed', 'Completed']].map(([val, label]) => (
              <button key={val} onClick={() => { setViewFilter(val); setStaffFilter(''); }} className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${viewFilter === val ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                {label}
              </button>
            ))}
          </div>
          {(canViewAll || canViewStaffingTab || canViewFieldSupervisorsTab) && (() => {
            // 'completed' only filters when canViewAll (it shows everyone's
            // completed tasks then; otherwise it's just your own, nothing to
            // filter by person) -- staffing/field_supervisors filter
            // whenever that tab is visible at all, independent of canViewAll.
            const staffFilterTabs = [
              ...(canViewAll ? ['all', 'completed'] : []),
              ...(canViewStaffingTab ? ['staffing'] : []),
              ...(canViewFieldSupervisorsTab ? ['field_supervisors'] : []),
            ];
            const filterable = viewFilter === 'staffing' ? staffUsers.filter(s => s.department === 'Staffing')
              : viewFilter === 'field_supervisors' ? staffUsers.filter(s => s.department === 'Field Supervisors')
              : staffUsers;
            const allLabel = viewFilter === 'staffing' ? 'All Staffing' : viewFilter === 'field_supervisors' ? 'All Field Supervisors' : 'All Staff';
            return (
              <select
                value={staffFilter}
                onChange={e => setStaffFilter(e.target.value)}
                disabled={!staffFilterTabs.includes(viewFilter)}
                className={`px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition ${!staffFilterTabs.includes(viewFilter) ? 'invisible pointer-events-none' : ''}`}
              >
                <option value="">{allLabel}</option>
                {filterable.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select>
            );
          })()}
          <button onClick={() => setShowCreate(true)} className="bg-[#2c4f26] text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-[#1f3a1c] transition flex items-center gap-2 text-sm">
            <Plus size={16} /> New Task
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-16 text-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" /></div>
      ) : viewFilter === 'scheduled' ? (
        scheduledTodos.length === 0 ? (
          <div className="p-16 text-center text-gray-500 bg-white rounded-2xl border border-gray-100">
            <CalendarClock size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">{search ? 'No tasks match your search' : 'Nothing scheduled right now'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {scheduledTodos.map(todo => {
              const urgent = todo.priority === 'urgent';
              const assignees = (todo.todo_assignees || []).filter(a => a.profiles).map(a => a.profiles);
              return (
                <div
                  key={todo.id}
                  onClick={() => openTodo(todo)}
                  className="rounded-xl border border-gray-200 bg-white p-3 cursor-pointer hover:border-[#2c4f26] hover:shadow-sm transition"
                >
                  <div className="flex items-start gap-2 mb-1.5">
                    <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${TODO_PRIORITY_MAP[todo.priority]?.color || 'bg-gray-400'}`} />
                    <p className="text-sm font-semibold text-gray-900 flex-1">{todo.title}</p>
                    {urgent && (
                      <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-red-600 text-white">Urgent</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mb-1">
                    {todo.creator?.full_name ? `By ${todo.creator.full_name}` : 'Assigned'}
                    {todo.due_date && (
                      <> · <span className="text-gray-500">Due {todoFmtDue(todo)}</span></>
                    )}
                  </p>
                  <p className="text-xs text-amber-700 font-medium mb-1.5 flex items-center gap-1">
                    <CalendarClock size={11} /> Appears {todoFmtDateTime(todo.visible_from)}
                  </p>
                  {todo.recurrence && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                      <RefreshCw size={9} /> {todoRecurrenceLabel(todo.recurrence, todo.recurrence_interval)}
                    </span>
                  )}
                  {assignees.length > 0 && (
                    <div className="flex -space-x-1.5 mt-1.5">
                      {assignees.map(a => (
                        <span key={a.id} title={a.full_name} className="w-6 h-6 rounded-full bg-[#2c4f26] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
                          {todoInitials(a.full_name)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : viewFilter === 'completed' ? (
        completedTodos.length === 0 && completedWatchingTodos.length === 0 ? (
          <div className="p-16 text-center text-gray-500 bg-white rounded-2xl border border-gray-100">
            <ClipboardList size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">{search ? 'No tasks match your search' : 'Nothing completed yet'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 items-start">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {completedTodos.map(renderCompletedTodoCard)}
            </div>
            <div className="bg-gray-50 rounded-2xl p-3">
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3 px-1">Watching <span className="text-gray-400">{completedWatchingTodos.length}</span></h3>
              {completedWatchingTodos.length === 0 ? (
                <p className="text-xs text-gray-400 px-1">No completed tasks you're watching.</p>
              ) : (
                <div className="space-y-2">
                  {completedWatchingTodos.map(renderCompletedTodoCard)}
                </div>
              )}
            </div>
          </div>
        )
      ) : visibleTodos.length === 0 && watchingTodos.length === 0 ? (
        <div className="p-16 text-center text-gray-500 bg-white rounded-2xl border border-gray-100">
          <ClipboardList size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">{search ? 'No tasks match your search' : 'No tasks yet'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {boardColumns.map(col => {
            const colSource = col.key === 'watching' ? watchingTodos : visibleTodos;
            const colTodos = colSource.filter(col.match).sort(col.sort || todoBoardSort);
            return (
              <div key={col.key} className="bg-gray-50 rounded-2xl p-3">
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3 px-1">{col.label} <span className="text-gray-400">{colTodos.length}</span></h3>
                <div className="space-y-2">
                  {colTodos.map(todo => {
                    const overdue = todoIsOverdue(todo);
                    const urgent = todo.priority === 'urgent';
                    const urgentHighlight = urgent;
                    const daysLeft = todoDaysLeftLabel(todo);
                    const assignees = (todo.todo_assignees || []).filter(a => a.profiles).map(a => ({ ...a.profiles, completed_at: a.completed_at || null }));
                    return (
                      <div
                        key={todo.id}
                        onClick={() => openTodo(todo)}
                        className={`rounded-xl border p-3 cursor-pointer hover:shadow-sm transition ${
                          urgentHighlight ? 'bg-red-50 border-red-200 hover:border-red-400' : 'bg-white border-gray-200 hover:border-[#2c4f26]'
                        }`}
                        style={{ borderLeftWidth: 4, borderLeftColor: overdue ? '#dc2626' : urgentHighlight ? '#ef4444' : undefined }}
                      >
                        <div className="flex items-start gap-2 mb-1.5">
                          <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${TODO_PRIORITY_MAP[todo.priority]?.color || 'bg-gray-400'}`} />
                          <p className="text-sm font-semibold text-gray-900 flex-1">{todo.title}</p>
                          {urgent && (
                            <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-red-600 text-white">Urgent</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mb-1">
                          Assigned {todoFmtShort(todo.created_at)}{todo.creator?.full_name ? ` by ${todo.creator.full_name}` : ''}
                          {todo.due_date && (
                            <> · <span className={overdue ? 'text-red-600 font-semibold' : 'text-gray-500'}>Due {todoFmtDue(todo)}</span></>
                          )}
                        </p>
                        {todo.status === 'done' && todo.completed_at && (
                          <p className="text-xs text-green-700 font-medium mb-1">Completed {todoFmtDateTime(todo.completed_at)}</p>
                        )}
                        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                          {daysLeft && (
                            <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${daysLeft.cls}`}>{daysLeft.text}</span>
                          )}
                          {todo.requires_all_assignees && assignees.length > 0 && (
                            <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#f0f5f0] text-[#2c4f26]">
                              Group · {assignees.filter(a => a.completed_at).length}/{assignees.length} done
                            </span>
                          )}
                          {todo.recurrence && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                              <RefreshCw size={9} /> {todoRecurrenceLabel(todo.recurrence, todo.recurrence_interval)}
                            </span>
                          )}
                          {col.key !== 'watching' && !todoAssigneeIds(todo).includes(profile?.id) && todoWatcherIds(todo).includes(profile?.id) && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                              <Eye size={9} /> Watching
                            </span>
                          )}
                        </div>
                        {assignees.length > 0 && (
                          <div className="flex -space-x-1.5 mt-1">
                            {assignees.map(a => (
                              <span key={a.id} title={a.full_name} className="w-6 h-6 rounded-full bg-[#2c4f26] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
                                {todoInitials(a.full_name)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {colTodos.length === 0 && <p className="text-xs text-gray-400 text-center py-4">Nothing here</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateTodoModal
          staffUsers={staffUsers}
          profile={profile}
          showToast={showToast}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadTodos(); }}
        />
      )}

      {selected && (
        <TodoDetailModal
          todo={selected}
          staffUsers={staffUsers}
          profile={profile}
          permissions={permissions}
          showToast={showToast}
          onClose={() => { closeTodo(); loadTodos(); }}
          onUpdate={updateTodoInList}
          onDeleted={() => { closeTodo(); setTodos(prev => prev.filter(t => t.id !== selected.id)); }}
        />
      )}
    </div>
  );
};

// ── Meeting Agenda ────────────────────────────────────────────────────────────
// Occurrences are never materialized as rows -- the next/past occurrence
// dates are computed client-side from each meeting's recurrence rule
// (recurrence + anchor_date), and standing "template" topics are always
// shown for every occurrence by definition rather than being copied into
// one. meeting_topics only stores the ad-hoc, date-stamped topics anyone
// adds, which doubles as the agenda history once a date is in the past.
const MEETING_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const meetingRecurrenceDesc = (meeting) => {
  const anchor = new Date(meeting.anchor_date + 'T00:00:00');
  if (meeting.recurrence === 'once') return `One-time, ${meetingFmtDate(anchor)}`;
  if (meeting.recurrence === 'monthly') {
    const day = anchor.getDate();
    const suffix = (day % 10 === 1 && day !== 11) ? 'st' : (day % 10 === 2 && day !== 12) ? 'nd' : (day % 10 === 3 && day !== 13) ? 'rd' : 'th';
    return `Monthly on the ${day}${suffix}`;
  }
  const dayName = MEETING_DAY_NAMES[anchor.getDay()];
  return meeting.recurrence === 'biweekly' ? `Every other ${dayName}` : `Weekly on ${dayName}s`;
};

// Next occurrence on/after today. Weekly/biweekly use modular arithmetic off
// the anchor date; monthly re-anchors to the same day-of-month each step
// (rather than compounding setMonth calls) -- still imperfect for an anchor
// on the 29th-31st in a short month, same JS Date limitation as everywhere
// else, but good enough for how meetings actually get scheduled in practice.
const meetingNextOccurrence = (meeting) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const anchor = new Date(meeting.anchor_date + 'T00:00:00');
  if (meeting.recurrence === 'once') return anchor;
  if (meeting.recurrence === 'monthly') {
    const day = anchor.getDate();
    let d = anchor;
    while (d < today) d = new Date(d.getFullYear(), d.getMonth() + 1, day);
    return d;
  }
  const intervalDays = meeting.recurrence === 'biweekly' ? 14 : 7;
  const diffDays = Math.round((today - anchor) / 86400000);
  const cycles = Math.max(0, Math.ceil(diffDays / intervalDays));
  const d = new Date(anchor);
  d.setDate(d.getDate() + cycles * intervalDays);
  return d;
};

// Every occurrence of a meeting that falls within [rangeStart, rangeEnd]
// (inclusive, both at midnight) -- same recurrence math as
// meetingNextOccurrence, which only ever wants the single nearest-today
// date, walked forward to enumerate every date a calendar month actually
// needs to show.
const meetingOccurrencesInRange = (meeting, rangeStart, rangeEnd) => {
  const anchor = new Date(meeting.anchor_date + 'T00:00:00');
  const results = [];
  if (meeting.recurrence === 'once') {
    if (anchor >= rangeStart && anchor <= rangeEnd) results.push(anchor);
    return results;
  }
  if (meeting.recurrence === 'monthly') {
    const day = anchor.getDate();
    let d = new Date(anchor);
    while (d < rangeStart) d = new Date(d.getFullYear(), d.getMonth() + 1, day);
    while (d <= rangeEnd) {
      results.push(d);
      d = new Date(d.getFullYear(), d.getMonth() + 1, day);
    }
    return results;
  }
  const intervalDays = meeting.recurrence === 'biweekly' ? 14 : 7;
  const diffDays = Math.round((rangeStart - anchor) / 86400000);
  const cycles = Math.max(0, Math.ceil(diffDays / intervalDays));
  let d = new Date(anchor);
  d.setDate(d.getDate() + cycles * intervalDays);
  while (d <= rangeEnd) {
    results.push(new Date(d));
    d = new Date(d);
    d.setDate(d.getDate() + intervalDays);
  }
  return results;
};

// The next n occurrences starting from meetingNextOccurrence -- a count
// rather than a date range, for the list view's "show the next 10
// meetings" (topics can be added against any of them, not just the
// nearest one).
const meetingNextNOccurrences = (meeting, n) => {
  const first = meetingNextOccurrence(meeting);
  if (meeting.recurrence === 'once') return [first];
  const results = [first];
  if (meeting.recurrence === 'monthly') {
    const day = first.getDate();
    let d = first;
    while (results.length < n) {
      d = new Date(d.getFullYear(), d.getMonth() + 1, day);
      results.push(d);
    }
    return results;
  }
  const intervalDays = meeting.recurrence === 'biweekly' ? 14 : 7;
  let d = first;
  while (results.length < n) {
    d = new Date(d);
    d.setDate(d.getDate() + intervalDays);
    results.push(d);
  }
  return results;
};

const meetingDateKey = (d) => {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const meetingFmtDate = (d) => d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
const meetingFmtTime = (t) => {
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

const CreateMeetingModal = ({ onClose, onCreated, profile, staffUsers, showToast }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [recurrence, setRecurrence] = useState('once');
  const [anchorDate, setAnchorDate] = useState(() => meetingDateKey(new Date()));
  const [timeOfDay, setTimeOfDay] = useState('');
  const [saving, setSaving] = useState(false);

  // Attendees and standing topics picked here are just the starting set --
  // same add/remove either way, so there is nothing special about doing it
  // at creation time vs. from the meeting afterward.
  const [pendingAttendeeIds, setPendingAttendeeIds] = useState([]);
  const [pendingTopics, setPendingTopics] = useState([]);
  const [newTopic, setNewTopic] = useState('');

  const canSave = title.trim() && anchorDate;
  const availableToAdd = (staffUsers || []).filter(s => !pendingAttendeeIds.includes(s.id));

  const toggleAttendee = (id) => setPendingAttendeeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const addEveryoneAsAttendee = () => setPendingAttendeeIds((staffUsers || []).map(s => s.id));
  const addPendingTopic = () => {
    if (!newTopic.trim()) return;
    setPendingTopics(prev => [...prev, newTopic.trim()]);
    setNewTopic('');
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: meeting, error } = await supabase.from('meetings').insert({
        title: title.trim(), description: description.trim() || null, recurrence,
        anchor_date: anchorDate, time_of_day: timeOfDay || null, created_by: profile?.id || null,
      }).select().single();
      if (error) throw error;

      // The meeting itself is created at this point regardless of what
      // happens below -- attendees/topics are a best-effort add-on, not
      // part of the same all-or-nothing save.
      const followUpErrors = [];
      if (pendingAttendeeIds.length > 0) {
        const { error: attendeeError } = await supabase.from('meeting_attendees')
          .insert(pendingAttendeeIds.map(user_id => ({ meeting_id: meeting.id, user_id })));
        if (attendeeError) followUpErrors.push('attendees');
      }
      if (pendingTopics.length > 0) {
        const { error: topicError } = await supabase.from('meeting_agenda_templates')
          .insert(pendingTopics.map((topic, i) => ({ meeting_id: meeting.id, topic, sort_order: i, created_by: profile?.id || null })));
        if (topicError) followUpErrors.push('standing topics');
      }

      showToast(followUpErrors.length > 0
        ? `Meeting created, but failed to save ${followUpErrors.join(' and ')} -- add them from the meeting.`
        : 'Meeting created');
      onCreated();
    } catch (err) {
      showToast(err.message || 'Failed to create meeting', 'error');
    } finally {
      setSaving(false);
    }
  };

  const inp = 'w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm';
  const lbl = 'block text-xs font-semibold text-gray-600 mb-1';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 my-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">New Meeting</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className={lbl}>Title</label>
            <input autoFocus className={inp} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Weekly Staff Meeting" />
          </div>
          <div>
            <label className={lbl}>Description (optional)</label>
            <textarea rows={2} className={`${inp} resize-y`} value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Repeats</label>
              <select className={inp} value={recurrence} onChange={e => setRecurrence(e.target.value)}>
                <option value="once">Does not repeat</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every other week</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className={lbl}>Time (optional)</label>
              <input type="time" className={inp} value={timeOfDay} onChange={e => setTimeOfDay(e.target.value)} />
            </div>
          </div>
          <div>
            <label className={lbl}>
              {recurrence === 'once' ? 'Meeting date' : `A date this meeting falls on (sets the ${recurrence === 'monthly' ? 'day of month' : 'day of week'})`}
            </label>
            <input type="date" className={inp} value={anchorDate} onChange={e => setAnchorDate(e.target.value)} />
          </div>

          <div className="pt-1 border-t border-gray-100">
            <div className="flex items-center justify-between mt-4 mb-1">
              <label className={`${lbl} mb-0`}>Attendees (optional)</label>
              <button type="button" onClick={addEveryoneAsAttendee} className="text-xs font-semibold text-[#2c4f26] hover:underline">Add Everyone</button>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {pendingAttendeeIds.map(id => {
                const s = (staffUsers || []).find(x => x.id === id);
                return (
                  <span key={id} className="inline-flex items-center gap-1 bg-[#f0f5f0] text-[#2c4f26] text-xs font-semibold px-2.5 py-1 rounded-full">
                    {s?.full_name || 'Unknown'}
                    <button type="button" onClick={() => toggleAttendee(id)} className="hover:text-red-500 transition ml-0.5"><X size={11} /></button>
                  </span>
                );
              })}
              {pendingAttendeeIds.length === 0 && <span className="text-xs text-gray-400">No one yet</span>}
            </div>
            <AssigneePicker staffUsers={availableToAdd} onPick={toggleAttendee} placeholder="Add an attendee…" />
            <p className="text-xs text-gray-400 mt-1">Who can see this meeting. You can add more later.</p>
          </div>

          <div>
            <label className={`${lbl} mt-2`}>Standing Topics (optional)</label>
            {pendingTopics.length > 0 && (
              <div className="space-y-1.5 mb-2">
                {pendingTopics.map((topic, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm">
                    <span className="text-gray-800">{topic}</span>
                    <button onClick={() => setPendingTopics(prev => prev.filter((_, idx) => idx !== i))} className="text-gray-300 hover:text-red-500 transition shrink-0"><X size={13} /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input className={inp} placeholder="Add a standing topic…" value={newTopic} onChange={e => setNewTopic(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPendingTopic(); } }} />
              <button type="button" onClick={addPendingTopic} disabled={!newTopic.trim()} className="px-4 py-2 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50 shrink-0">Add</button>
            </div>
            <p className="text-xs text-gray-400 mt-1">Appear on every occurrence. You can add more or edit these later.</p>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">Cancel</button>
          <button onClick={save} disabled={!canSave || saving} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50">{saving ? 'Creating…' : 'Create Meeting'}</button>
        </div>
      </div>
    </div>
  );
};

const MeetingDetailView = ({ meeting, canManageAll, profile, staffUsers, showToast, onBack, onDeleted, onUpdated }) => {
  // Manage-all sees/edits everything; otherwise only the meeting's own
  // creator can edit/delete it and manage its attendees/standing topics --
  // Create Meetings alone (without being this meeting's creator) grants
  // neither.
  const canManageThis = canManageAll || meeting.created_by === profile?.id;
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [newTemplateTopic, setNewTemplateTopic] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const [topics, setTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [newTopic, setNewTopic] = useState('');
  const [postingTopic, setPostingTopic] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [occurrenceView, setOccurrenceView] = useState('list'); // 'list' | 'calendar'
  // Which occurrence's topics are expanded below the list, or shown below
  // the calendar -- defaults to the upcoming one so the page opens the
  // same way it always has. Standing topics only ever show for this one
  // (they're the template for the NEXT meeting, not a record of a past
  // one), and the add-topic box only appears here too, same constraint as
  // before: a topic can only ever be added against the next occurrence.
  const [activeDateKey, setActiveDateKey] = useState(() => meetingDateKey(meetingNextOccurrence(meeting)));
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; });
  // Editing the meeting (via EditMeetingModal) can change anchor_date or
  // recurrence, which moves which date counts as "upcoming" -- re-point
  // the expanded occurrence there rather than leaving it stuck on a date
  // that no longer means anything.
  useEffect(() => {
    setActiveDateKey(meetingDateKey(meetingNextOccurrence(meeting)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting.anchor_date, meeting.recurrence]);
  const [showEdit, setShowEdit] = useState(false);
  const [showAttendees, setShowAttendees] = useState(false);
  const [showStandingTopics, setShowStandingTopics] = useState(false);
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [editTopicText, setEditTopicText] = useState('');
  const [savingEditTopic, setSavingEditTopic] = useState(false);

  const [attendees, setAttendees] = useState([]);
  const [loadingAttendees, setLoadingAttendees] = useState(true);
  const [savingAttendee, setSavingAttendee] = useState(false);

  // Every other active meeting visible to the current viewer (same RLS
  // that already scopes the top-level Meeting Agenda list) -- shown on
  // the calendar for context/conflict-spotting only, never clickable
  // here. Full management of a different meeting stays on its own page,
  // reached from the top-level list.
  const [otherMeetings, setOtherMeetings] = useState([]);
  useEffect(() => {
    supabase.from('meetings').select('id, title, recurrence, anchor_date').eq('active', true).neq('id', meeting.id)
      .then(({ data }) => setOtherMeetings(data || []));
  }, [meeting.id]);

  const next = meetingNextOccurrence(meeting);
  const nextKey = meetingDateKey(next);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    const { data } = await supabase.from('meeting_agenda_templates').select('*').eq('meeting_id', meeting.id).order('sort_order').order('created_at');
    setTemplates(data || []);
    setLoadingTemplates(false);
  };
  const loadTopics = async () => {
    setLoadingTopics(true);
    const { data } = await supabase.from('meeting_topics').select('*, profiles(full_name)').eq('meeting_id', meeting.id).order('occurrence_date', { ascending: false }).order('created_at');
    setTopics(data || []);
    setLoadingTopics(false);
  };
  const loadAttendees = async () => {
    setLoadingAttendees(true);
    const { data } = await supabase.from('meeting_attendees').select('*, profiles(full_name)').eq('meeting_id', meeting.id);
    setAttendees(data || []);
    setLoadingAttendees(false);
  };
  useEffect(() => { loadTemplates(); loadTopics(); loadAttendees(); }, [meeting.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addAttendee = async (userId) => {
    if (!userId) return;
    setSavingAttendee(true);
    try {
      const { error } = await supabase.from('meeting_attendees').insert({ meeting_id: meeting.id, user_id: userId });
      if (error) throw error;
      loadAttendees();
    } catch (err) {
      showToast(err.message || 'Failed to add attendee', 'error');
    } finally {
      setSavingAttendee(false);
    }
  };
  const addEveryoneAsAttendee = async () => {
    if (availableToAdd.length === 0) return;
    setSavingAttendee(true);
    try {
      const { error } = await supabase.from('meeting_attendees').insert(availableToAdd.map(s => ({ meeting_id: meeting.id, user_id: s.id })));
      if (error) throw error;
      loadAttendees();
    } catch (err) {
      showToast(err.message || 'Failed to add attendees', 'error');
    } finally {
      setSavingAttendee(false);
    }
  };
  const removeAttendee = async (id) => {
    try {
      const { error } = await supabase.from('meeting_attendees').delete().eq('id', id);
      if (error) throw error;
      setAttendees(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      showToast(err.message || 'Failed to remove attendee', 'error');
    }
  };
  const availableToAdd = (staffUsers || []).filter(s => !attendees.some(a => a.user_id === s.id));

  const addTemplateTopic = async () => {
    if (!newTemplateTopic.trim()) return;
    setSavingTemplate(true);
    try {
      const { error } = await supabase.from('meeting_agenda_templates').insert({ meeting_id: meeting.id, topic: newTemplateTopic.trim(), sort_order: templates.length, created_by: profile?.id || null });
      if (error) throw error;
      setNewTemplateTopic('');
      loadTemplates();
    } catch (err) {
      showToast(err.message || 'Failed to add standing topic', 'error');
    } finally {
      setSavingTemplate(false);
    }
  };
  const removeTemplateTopic = async (id) => {
    try {
      const { error } = await supabase.from('meeting_agenda_templates').delete().eq('id', id);
      if (error) throw error;
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      showToast(err.message || 'Failed to remove topic', 'error');
    }
  };

  const addTopic = async () => {
    if (!newTopic.trim() || !activeDateKey) return;
    setPostingTopic(true);
    try {
      const { error } = await supabase.from('meeting_topics').insert({ meeting_id: meeting.id, occurrence_date: activeDateKey, topic: newTopic.trim(), added_by: profile?.id || null });
      if (error) throw error;
      setNewTopic('');
      loadTopics();
    } catch (err) {
      showToast(err.message || 'Failed to add topic', 'error');
    } finally {
      setPostingTopic(false);
    }
  };
  const removeTopic = async (id) => {
    try {
      const { error } = await supabase.from('meeting_topics').delete().eq('id', id);
      if (error) throw error;
      setTopics(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      showToast(err.message || 'Failed to remove topic', 'error');
    }
  };

  const startEditTopic = (t) => { setEditingTopicId(t.id); setEditTopicText(t.topic); };
  const cancelEditTopic = () => { setEditingTopicId(null); setEditTopicText(''); };
  const saveEditTopic = async (id) => {
    if (!editTopicText.trim()) return;
    setSavingEditTopic(true);
    try {
      const { error } = await supabase.from('meeting_topics').update({ topic: editTopicText.trim(), updated_at: new Date().toISOString() }).eq('id', id);
      if (error) throw error;
      setEditingTopicId(null);
      setEditTopicText('');
      loadTopics();
    } catch (err) {
      showToast(err.message || 'Failed to save topic', 'error');
    } finally {
      setSavingEditTopic(false);
    }
  };

  const deleteMeeting = async () => {
    if (!window.confirm(`Delete "${meeting.title}" and its entire agenda history? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from('meetings').delete().eq('id', meeting.id);
      if (error) throw error;
      showToast('Meeting deleted');
      onDeleted();
    } catch (err) {
      showToast(err.message || 'Failed to delete meeting', 'error');
      setDeleting(false);
    }
  };

  const todayKey = meetingDateKey(new Date());
  // The next 10 occurrences get their own row each, regardless of whether
  // they have topics yet -- lets staff queue up agenda items for meetings
  // well ahead of time, not just the very next one.
  const upcomingOccurrences = meetingNextNOccurrences(meeting, 10);
  const upcomingKeys = new Set(upcomingOccurrences.map(meetingDateKey));
  const pastDates = [...new Set(topics.filter(t => !upcomingKeys.has(t.occurrence_date) && t.occurrence_date < todayKey).map(t => t.occurrence_date))].sort((a, b) => b.localeCompare(a));
  const topicCountByDate = {};
  topics.forEach(t => { topicCountByDate[t.occurrence_date] = (topicCountByDate[t.occurrence_date] || 0) + 1; });
  const activeTopics = activeDateKey ? topics.filter(t => t.occurrence_date === activeDateKey) : [];
  // A topic can be added to today-or-later; anything before today is a
  // historical record only (can't retroactively schedule a discussion
  // item for a meeting that already happened).
  const isActiveFuture = !!activeDateKey && activeDateKey >= todayKey;

  const inp = 'flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm';

  // Shared between the list view's expanded row and the calendar view's
  // panel below the grid -- standing topics and the add-topic box only
  // ever apply to the upcoming occurrence (same constraint as before: a
  // topic can only be added against nextKey, not an arbitrary date).
  const renderOccurrencePanel = () => (
    <div>
      {isActiveFuture && templates.length > 0 && (
        <div className="mb-3 space-y-1.5">
          {templates.map(t => (
            <div key={t.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-700">
              <MessageSquare size={13} className="text-gray-400 shrink-0" /> {t.topic}
            </div>
          ))}
        </div>
      )}
      {loadingTopics ? (
        <div className="text-center py-3"><div className="w-5 h-5 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : activeTopics.length > 0 ? (
        <div className="mb-3 space-y-1.5">
          {activeTopics.map(t => {
            const canEditTopic = t.added_by === profile?.id || canManageThis;
            if (editingTopicId === t.id) {
              return (
                <div key={t.id} className="flex items-center gap-2 px-3 py-2 bg-[#f0f5f0] rounded-lg text-sm">
                  <input
                    autoFocus
                    className="flex-1 px-2 py-1 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm"
                    value={editTopicText}
                    onChange={e => setEditTopicText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEditTopic(t.id); if (e.key === 'Escape') cancelEditTopic(); }}
                  />
                  <button onClick={() => saveEditTopic(t.id)} disabled={savingEditTopic || !editTopicText.trim()} className="text-xs font-semibold text-[#2c4f26] hover:bg-white px-2 py-1 rounded-lg transition disabled:opacity-50 shrink-0">Save</button>
                  <button onClick={cancelEditTopic} className="text-xs font-medium text-gray-500 hover:bg-white px-2 py-1 rounded-lg transition shrink-0">Cancel</button>
                </div>
              );
            }
            return (
              <div key={t.id} className="flex items-center justify-between gap-2 px-3 py-2 bg-[#f0f5f0] rounded-lg text-sm">
                <div>
                  <span className="text-gray-800">{t.topic}</span>
                  <span className="text-[11px] text-gray-400 ml-2">
                    {t.profiles?.full_name || 'Unknown'} · {t.updated_at ? `edited ${hiringFmtSyncAgo(t.updated_at)}` : hiringFmtSyncAgo(t.created_at)}
                  </span>
                </div>
                {canEditTopic && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => startEditTopic(t)} className="text-gray-300 hover:text-[#2c4f26] transition"><Pencil size={12} /></button>
                    <button onClick={() => removeTopic(t.id)} className="text-gray-300 hover:text-red-500 transition"><X size={13} /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-gray-400 mb-3">{isActiveFuture ? 'No discussion topics added yet.' : 'No topics were recorded for this occurrence.'}</p>
      )}
      {isActiveFuture && (
        <div className="flex gap-2">
          <input className={inp} placeholder="Add a discussion topic…" value={newTopic} onChange={e => setNewTopic(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addTopic(); }} />
          <button onClick={addTopic} disabled={postingTopic || !newTopic.trim()} className="px-4 py-2 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50 shrink-0">Add</button>
        </div>
      )}
    </div>
  );

  // Calendar view's grid -- every occurrence of THIS meeting within the
  // visible month (including prev/next-month padding days), computed the
  // same way the recurrence description itself is derived.
  const calGridStart = new Date(calendarMonth);
  calGridStart.setDate(calGridStart.getDate() - calGridStart.getDay());
  const calMonthEnd = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
  const calGridEnd = new Date(calMonthEnd);
  calGridEnd.setDate(calGridEnd.getDate() + (6 - calGridEnd.getDay()));
  const occurrenceDateKeys = new Set(meetingOccurrencesInRange(meeting, calGridStart, calGridEnd).map(meetingDateKey));
  const calDays = [];
  for (let d = new Date(calGridStart); d <= calGridEnd; d.setDate(d.getDate() + 1)) calDays.push(new Date(d));
  // { [dateKey]: [{ id, title }] } -- other meetings' occurrences falling
  // in the same visible range, read-only context alongside this one.
  const otherOccurrencesByDate = {};
  otherMeetings.forEach(om => {
    meetingOccurrencesInRange(om, calGridStart, calGridEnd).forEach(d => {
      const k = meetingDateKey(d);
      (otherOccurrencesByDate[k] = otherOccurrencesByDate[k] || []).push(om);
    });
  });

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#2c4f26] font-medium transition mb-5">
        <ChevronLeft size={15} /> Meeting Agenda
      </button>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-5">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{meeting.title}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{meetingRecurrenceDesc(meeting)}{meeting.time_of_day ? ` at ${meetingFmtTime(meeting.time_of_day)}` : ''}</p>
          </div>
          {canManageThis && (
            <div className="flex items-center gap-3 shrink-0">
              <button onClick={() => setShowEdit(true)} title="Edit meeting" className="text-gray-400 hover:text-[#2c4f26] transition"><Pencil size={16} /></button>
              <button onClick={deleteMeeting} disabled={deleting} title="Delete meeting" className="text-gray-400 hover:text-red-600 transition disabled:opacity-50"><Trash2 size={16} /></button>
            </div>
          )}
        </div>
        {meeting.description && <p className="text-sm text-gray-700 mt-3">{meeting.description}</p>}
        <div className="flex items-center gap-2 mt-4">
          <button onClick={() => setShowAttendees(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition">
            <Users size={13} /> Attendees{attendees.length > 0 ? ` (${attendees.length})` : ''}
          </button>
          <button onClick={() => setShowStandingTopics(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition">
            <MessageSquare size={13} /> Standing Topics{templates.length > 0 ? ` (${templates.length})` : ''}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-5">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100">
          <h2 className="text-sm font-bold text-gray-900">Occurrences</h2>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button onClick={() => setOccurrenceView('list')} className={`px-3 py-1 text-xs font-medium rounded-lg transition ${occurrenceView === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>List</button>
            <button onClick={() => setOccurrenceView('calendar')} className={`px-3 py-1 text-xs font-medium rounded-lg transition ${occurrenceView === 'calendar' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>Calendar</button>
          </div>
        </div>

        {occurrenceView === 'list' ? (
          <div>
            {upcomingOccurrences.map((date, idx) => {
              const dateKey = meetingDateKey(date);
              return (
                <div key={dateKey} className={idx > 0 ? 'border-t border-gray-100' : ''}>
                  <button type="button" onClick={() => setActiveDateKey(k => k === dateKey ? null : dateKey)} className="w-full text-left px-4 md:px-6 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition">
                    <div className="flex items-center gap-2 flex-wrap">
                      {idx === 0 && <span className="text-[10px] font-bold uppercase tracking-wide text-[#2c4f26] bg-[#f0f5f0] px-2 py-0.5 rounded-full">Upcoming</span>}
                      <span className="text-sm font-semibold text-gray-900">{meetingFmtDate(date)}</span>
                      {topicCountByDate[dateKey] > 0 && (
                        <span title={`${topicCountByDate[dateKey]} additional discussion topic${topicCountByDate[dateKey] === 1 ? '' : 's'}`} className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                          <MessageSquare size={10} /> {topicCountByDate[dateKey]}
                        </span>
                      )}
                    </div>
                    {activeDateKey === dateKey ? <ChevronUp size={16} className="text-gray-400 shrink-0" /> : <ChevronDown size={16} className="text-gray-400 shrink-0" />}
                  </button>
                  {activeDateKey === dateKey && <div className="px-4 md:px-6 pb-4 md:pb-5">{renderOccurrencePanel()}</div>}
                </div>
              );
            })}

            {pastDates.length > 0 && (
              <>
                <div className="px-4 md:px-6 py-2 border-t border-gray-100 bg-gray-50/60">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Past Occurrences</p>
                </div>
                {pastDates.map(dateKey => (
                  <div key={dateKey} className="border-t border-gray-100">
                    <button type="button" onClick={() => setActiveDateKey(k => k === dateKey ? null : dateKey)} className="w-full text-left px-4 md:px-6 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-700">{meetingFmtDate(new Date(dateKey + 'T00:00:00'))}</span>
                        {topicCountByDate[dateKey] > 0 && (
                          <span title={`${topicCountByDate[dateKey]} additional discussion topic${topicCountByDate[dateKey] === 1 ? '' : 's'}`} className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                            <MessageSquare size={10} /> {topicCountByDate[dateKey]}
                          </span>
                        )}
                      </div>
                      {activeDateKey === dateKey ? <ChevronUp size={16} className="text-gray-400 shrink-0" /> : <ChevronDown size={16} className="text-gray-400 shrink-0" />}
                    </button>
                    {activeDateKey === dateKey && <div className="px-4 md:px-6 pb-4 md:pb-5">{renderOccurrencePanel()}</div>}
                  </div>
                ))}
              </>
            )}
          </div>
        ) : (
          <div className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <button onClick={() => setCalendarMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"><ChevronLeft size={18} /></button>
              <h3 className="text-sm font-bold text-gray-900">{calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
              <button onClick={() => setCalendarMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"><ChevronRight size={18} /></button>
            </div>
            <div className="grid grid-cols-7 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calDays.map(day => {
                const key = meetingDateKey(day);
                const inMonth = day.getMonth() === calendarMonth.getMonth();
                const isOccurrence = occurrenceDateKeys.has(key);
                const count = topicCountByDate[key] || 0;
                const others = otherOccurrencesByDate[key] || [];
                return (
                  <div key={key} className={`min-h-[64px] rounded-lg p-1 flex flex-col gap-0.5 ${!inMonth ? 'opacity-40' : ''}`}>
                    <button
                      type="button"
                      disabled={!isOccurrence}
                      onClick={() => setActiveDateKey(key)}
                      className={`w-full rounded-md px-1.5 py-1 text-xs flex items-center justify-between transition border ${
                        activeDateKey === key && isOccurrence ? 'bg-[#2c4f26] text-white font-bold border-[#2c4f26]' :
                        isOccurrence ? 'bg-[#e4efe2] text-[#1f3a1c] font-bold border-[#2c4f26]/40 hover:bg-[#d7e8d4]' :
                        key === todayKey ? 'bg-white text-[#2c4f26] font-semibold border-[#2c4f26]/40' :
                        'bg-transparent text-gray-400 border-transparent'
                      }`}
                    >
                      <span>{day.getDate()}</span>
                      {isOccurrence && count > 0 && (
                        <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold ${activeDateKey === key ? 'text-white' : 'text-[#2c4f26]'}`}>
                          <MessageSquare size={8} />{count}
                        </span>
                      )}
                    </button>
                    {others.length > 0 && (
                      <div className="space-y-0.5">
                        {others.map(om => (
                          <div key={om.id} title={om.title} className="px-1 py-0.5 rounded border border-gray-200 bg-white text-gray-400 text-[9px] font-medium truncate">
                            {om.title}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {activeDateKey && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-600 mb-2">
                  {activeDateKey === nextKey ? 'Upcoming — ' : ''}{meetingFmtDate(new Date(activeDateKey + 'T00:00:00'))}
                </p>
                {renderOccurrencePanel()}
              </div>
            )}
          </div>
        )}
      </div>

      {showEdit && (
        <EditMeetingModal
          meeting={meeting}
          showToast={showToast}
          onClose={() => setShowEdit(false)}
          onSaved={(updated) => { setShowEdit(false); onUpdated?.(updated); }}
        />
      )}

      {showAttendees && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAttendees(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Attendees</h2>
                {!canManageThis && <p className="text-xs text-gray-400 mt-0.5">Who can see this meeting</p>}
              </div>
              <div className="flex items-center gap-3">
                {canManageThis && availableToAdd.length > 0 && (
                  <button onClick={addEveryoneAsAttendee} disabled={savingAttendee} className="text-xs font-semibold text-[#2c4f26] hover:underline disabled:opacity-50">Add Everyone</button>
                )}
                <button onClick={() => setShowAttendees(false)} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
              </div>
            </div>
            {loadingAttendees ? (
              <div className="text-center py-3"><div className="w-5 h-5 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin mx-auto" /></div>
            ) : (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {attendees.map(a => (
                  <span key={a.id} className="inline-flex items-center gap-1 bg-[#f0f5f0] text-[#2c4f26] text-xs font-semibold px-2.5 py-1 rounded-full">
                    {a.profiles?.full_name || 'Unknown'}
                    {canManageThis && <button onClick={() => removeAttendee(a.id)} className="hover:text-red-500 transition ml-0.5"><X size={11} /></button>}
                  </span>
                ))}
                {attendees.length === 0 && <span className="text-xs text-gray-400">No one yet{canManageThis ? ' -- only you and anyone with Manage All Meetings can see this.' : '.'}</span>}
              </div>
            )}
            {canManageThis && (
              <AssigneePicker staffUsers={availableToAdd} onPick={addAttendee} disabled={savingAttendee} placeholder="Add an attendee…" />
            )}
          </div>
        </div>
      )}

      {showStandingTopics && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowStandingTopics(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Standing Topics</h2>
                {!canManageThis && <p className="text-xs text-gray-400 mt-0.5">Appear on every meeting</p>}
              </div>
              <button onClick={() => setShowStandingTopics(false)} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
            </div>
            {loadingTemplates ? (
              <div className="text-center py-3"><div className="w-5 h-5 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin mx-auto" /></div>
            ) : templates.length === 0 ? (
              <p className="text-xs text-gray-400 mb-3">No standing topics set{canManageThis ? ' -- add one below so it appears on every occurrence.' : '.'}</p>
            ) : (
              <div className="space-y-1.5 mb-3">
                {templates.map(t => (
                  <div key={t.id} className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm">
                    <span className="text-gray-800">{t.topic}</span>
                    {canManageThis && <button onClick={() => removeTemplateTopic(t.id)} className="text-gray-300 hover:text-red-500 transition shrink-0"><X size={13} /></button>}
                  </div>
                ))}
              </div>
            )}
            {canManageThis && (
              <div className="flex gap-2 mt-2">
                <input className={inp} placeholder="Add a standing topic…" value={newTemplateTopic} onChange={e => setNewTemplateTopic(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addTemplateTopic(); }} />
                <button onClick={addTemplateTopic} disabled={savingTemplate || !newTemplateTopic.trim()} className="px-4 py-2 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50 shrink-0">Add</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const EditMeetingModal = ({ meeting, showToast, onClose, onSaved }) => {
  const [title, setTitle] = useState(meeting.title);
  const [description, setDescription] = useState(meeting.description || '');
  const [timeOfDay, setTimeOfDay] = useState(meeting.time_of_day || '');
  const [anchorDate, setAnchorDate] = useState(meeting.anchor_date || '');
  const [saving, setSaving] = useState(false);

  const canSave = title.trim() && anchorDate;

  const save = async () => {
    setSaving(true);
    try {
      const update = { title: title.trim(), description: description.trim() || null, time_of_day: timeOfDay || null, anchor_date: anchorDate };
      const { data, error } = await supabase.from('meetings').update(update).eq('id', meeting.id).select().single();
      if (error) throw error;
      showToast('Meeting updated');
      onSaved({ ...meeting, ...data });
    } catch (err) {
      showToast(err.message || 'Failed to update meeting', 'error');
    } finally {
      setSaving(false);
    }
  };

  const inp = 'w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition text-sm';
  const lbl = 'block text-xs font-semibold text-gray-600 mb-1';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">Edit Meeting</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className={lbl}>Title</label>
            <input autoFocus className={inp} value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div>
            <label className={lbl}>Description (optional)</label>
            <textarea rows={2} className={`${inp} resize-y`} value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          <div>
            <label className={lbl}>Time (optional)</label>
            <input type="time" className={inp} value={timeOfDay} onChange={e => setTimeOfDay(e.target.value)} />
          </div>
          <div>
            <label className={lbl}>
              {meeting.recurrence === 'once' ? 'Meeting date' : `A date this meeting falls on (sets the ${meeting.recurrence === 'monthly' ? 'day of month' : 'day of week'})`}
            </label>
            <input type="date" className={inp} value={anchorDate} onChange={e => setAnchorDate(e.target.value)} />
          </div>
          <p className="text-xs text-gray-400">To change how often this meeting repeats (weekly, monthly, etc.), delete it and create a new one.</p>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">Cancel</button>
          <button onClick={save} disabled={!canSave || saving} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-lg transition disabled:opacity-50">{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
};

const MeetingAgendaTab = ({ showToast, profile, permissions }) => {
  const isOwner = profile?.role === 'owner';
  const canManageAll = isOwner || permissions?.can_manage_all_meetings;
  const canCreate = canManageAll || permissions?.can_create_meetings;
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);

  const loadMeetings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('meetings').select('*, meeting_agenda_templates(id)').eq('active', true).order('title');
      if (error) throw error;
      setMeetings(data || []);
    } catch (err) {
      showToast('Failed to load meetings', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadMeetings(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Excludes the current user -- you already have access to whatever
    // meeting you can see/create, so you're never an "attendee" to add.
    supabase.from('profiles').select('id, full_name').in('role', ['owner', 'admin_staff']).eq('status', 'active').order('full_name')
      .then(({ data }) => setStaffUsers((data || []).filter(s => s.id !== profile?.id)));
  }, [profile?.id]);

  if (selected) {
    return (
      <MeetingDetailView
        meeting={selected}
        canManageAll={canManageAll}
        profile={profile}
        staffUsers={staffUsers}
        showToast={showToast}
        onBack={() => { setSelected(null); loadMeetings(); }}
        onDeleted={() => { setSelected(null); loadMeetings(); }}
        onUpdated={(updated) => setSelected(updated)}
      />
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Meeting Agenda</h1>
          <p className="text-sm text-gray-600">{meetings.length} recurring meeting{meetings.length === 1 ? '' : 's'}</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowCreate(true)} className="bg-[#2c4f26] text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-[#1f3a1c] transition flex items-center gap-2 text-sm">
            <Plus size={16} /> New Meeting
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-16 text-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" /></div>
      ) : meetings.length === 0 ? (
        <div className="p-16 text-center text-gray-500 bg-white rounded-2xl border border-gray-100">
          <CalendarClock size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">No recurring meetings yet</p>
          {canCreate && <p className="text-xs text-gray-400 mt-1">Click "New Meeting" to set one up.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {meetings.map(m => {
            const next = meetingNextOccurrence(m);
            const templateCount = (m.meeting_agenda_templates || []).length;
            return (
              <div key={m.id} onClick={() => setSelected(m)} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 cursor-pointer hover:border-[#2c4f26] hover:shadow-md transition">
                <div className="flex items-center gap-2 mb-1">
                  <CalendarClock size={16} className="text-[#2c4f26] shrink-0" />
                  <h3 className="font-bold text-gray-900">{m.title}</h3>
                </div>
                <p className="text-xs text-gray-500 mb-3">{meetingRecurrenceDesc(m)}</p>
                <p className="text-sm text-gray-700 font-medium">Next: {meetingFmtDate(next)}</p>
                {templateCount > 0 && <p className="text-xs text-gray-400 mt-1">{templateCount} standing topic{templateCount === 1 ? '' : 's'}</p>}
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateMeetingModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadMeetings(); }} profile={profile} staffUsers={staffUsers} showToast={showToast} />
      )}
    </div>
  );
};

// ── Client Tasks ──────────────────────────────────────────────────────────────
// QA visits (every 6 weeks) and nurse reassessments (every 12 weeks) per
// client. Meant to sync from WellSky's AdminTask webhook (see
// supabase/functions/wellsky-webhook), but zero real tasks have arrived that
// way since the Subscriptions went live -- see wellsky_webhook_log and the
// WellSky-sync investigation notes -- so CSV import is back as a stopgap
// (re-added 2026-10-20) until that's resolved. Matching a CSV row to an
// existing client only, same as before -- auto-creating a client that
// doesn't exist yet is a known gap, intentionally deferred.

const TASK_TYPE_LABELS = { qa_visit: 'QA Visit', nurse_reassessment: 'Nurse Reassessment', other: 'Other' };

const ClientTasksView = ({ showToast }) => {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('upcoming'); // upcoming | overdue | completed | all
  const [searchTerm, setSearchTerm] = useState('');
  const [showImport, setShowImport] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [{ data: taskRows }, { data: clientRows }] = await Promise.all([
        supabase.from('client_tasks').select('*, clients (id, first_name, last_name, co_client_name, location)').order('due_date', { ascending: true }),
        supabase.from('clients').select('id, first_name, last_name, co_client_name, status').eq('status', 'active').order('last_name'),
      ]);
      setTasks(taskRows || []);
      setClients(clientRows || []);
    } catch (err) {
      console.error('Error loading client tasks:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadData(); }, []);

  const todayStr = new Date().toISOString().slice(0, 10);

  const matchesSearch = (t) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    const name = `${t.clients?.first_name || ''} ${t.clients?.last_name || ''} ${t.clients?.co_client_name || ''}`.toLowerCase();
    return name.includes(term);
  };

  const filtered = tasks.filter(t => {
    if (!matchesSearch(t)) return false;
    if (statusFilter === 'all') return true;
    if (statusFilter === 'completed') return t.status === 'completed';
    if (statusFilter === 'overdue') return t.status === 'pending' && t.due_date < todayStr;
    if (statusFilter === 'upcoming') return t.status === 'pending' && t.due_date >= todayStr;
    return true;
  });

  const counts = {
    upcoming: tasks.filter(t => t.status === 'pending' && t.due_date >= todayStr).length,
    overdue: tasks.filter(t => t.status === 'pending' && t.due_date < todayStr).length,
    completed: tasks.filter(t => t.status === 'completed').length,
    all: tasks.length,
  };

  const markComplete = async (task) => {
    const { error } = await supabase.from('client_tasks').update({ status: 'completed', completed_at: new Date().toISOString() }).eq('id', task.id);
    if (error) { showToast(error.message || 'Failed to update task', 'error'); return; }
    showToast('Task marked complete');
    loadData();
  };

  const reopenTask = async (task) => {
    const { error } = await supabase.from('client_tasks').update({ status: 'pending', completed_at: null }).eq('id', task.id);
    if (error) { showToast(error.message || 'Failed to update task', 'error'); return; }
    showToast('Task reopened');
    loadData();
  };

  const deleteTask = async (task) => {
    if (!window.confirm('Delete this task?')) return;
    const { error } = await supabase.from('client_tasks').delete().eq('id', task.id);
    if (error) { showToast(error.message || 'Failed to delete task', 'error'); return; }
    showToast('Task deleted');
    loadData();
  };

  const clientLabel = (c) => c ? `${c.first_name} ${c.last_name}${c.co_client_name ? ` & ${c.co_client_name}` : ''}` : 'Unknown client';

  const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Client Tasks</h1>
          <p className="text-sm text-gray-600">QA visits every 6 weeks · Nurse reassessments every 12 weeks</p>
        </div>
        <button
          onClick={() => setShowImport(true)}
          className="bg-[#2c4f26] text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-[#1f3a1c] transition flex items-center gap-2 text-sm w-full sm:w-auto justify-center"
        >
          <Upload size={16} /> Import Tasks CSV
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
              placeholder="Search by client name…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 shrink-0 overflow-x-auto">
            {[['upcoming', 'Upcoming'], ['overdue', 'Overdue'], ['completed', 'Completed'], ['all', 'All']].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setStatusFilter(val)}
                className={`px-4 py-1.5 text-sm font-medium rounded-lg transition whitespace-nowrap ${statusFilter === val ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {label} <span className="text-gray-400">{counts[val]}</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-gray-500">
            <CalendarClock size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">
              {tasks.length === 0 ? 'No tasks yet — import a CSV to get started' : 'No tasks match your filters'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Client</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Task</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Due Date</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Assigned To</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Status</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(t => {
                  const overdue = t.status === 'pending' && t.due_date < todayStr;
                  return (
                    <tr key={t.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4">
                        <p className="font-semibold text-gray-900 text-sm">{clientLabel(t.clients)}</p>
                        {t.clients?.location && <p className="text-xs text-gray-500">Location {t.clients.location}</p>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{TASK_TYPE_LABELS[t.task_type] || t.task_type}</td>
                      <td className={`px-6 py-4 text-sm ${overdue ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>{fmtDate(t.due_date)}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{t.assigned_to || <span className="text-gray-400">—</span>}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          t.status === 'completed' ? 'bg-green-100 text-green-700' : overdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {t.status === 'completed' ? 'Completed' : overdue ? 'Overdue' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {t.status === 'completed' ? (
                            <button onClick={() => reopenTask(t)} className="text-xs font-semibold text-gray-600 hover:text-gray-900">Reopen</button>
                          ) : (
                            <button onClick={() => markComplete(t)} className="text-xs font-semibold text-[#2c4f26] hover:underline">Mark Complete</button>
                          )}
                          <button onClick={() => deleteTask(t)} className="text-gray-400 hover:text-red-600 transition"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ImportTasksModal
        show={showImport}
        onClose={() => setShowImport(false)}
        clients={clients}
        showToast={showToast}
        onImported={() => { setShowImport(false); loadData(); }}
      />
    </div>
  );
};

const ImportTasksModal = ({ show, onClose, clients, showToast, onImported }) => {
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  if (!show) return null;

  // Also strips the "â" mojibake artifact that shows up in place of a
  // curly apostrophe in some exports (e.g. "OâNeill" for "O'Neill") --
  // the same encoding issue seen in the WellSky client roster earlier.
  const loose = (s) => (s || '').toLowerCase().trim().replace(/[.\-'’â]/g, ' ').replace(/\s+/g, ' ').trim();

  const findClient = (nameStr) => {
    const target = loose(nameStr);
    if (!target) return null;
    const exact = clients.find(c => loose(`${c.first_name} ${c.last_name}`) === target);
    if (exact) return exact;
    // A task can name either half of a shared profile (e.g. "Russell Tuck"
    // when the profile's own first/last name is "Marjorie Tuck") -- match
    // against co_client_name too, same household, same client_id.
    const coExact = clients.find(c => c.co_client_name && loose(c.co_client_name) === target);
    if (coExact) return coExact;
    const targetTokens = new Set(target.split(' '));
    const partial = clients.filter(c => {
      const cTokens = new Set(loose(`${c.first_name} ${c.last_name}`).split(' '));
      return [...cTokens].every(tok => targetTokens.has(tok));
    });
    if (partial.length === 1) return partial[0];
    const coPartial = clients.filter(c => {
      if (!c.co_client_name) return false;
      const cTokens = new Set(loose(c.co_client_name).split(' '));
      return [...cTokens].every(tok => targetTokens.has(tok));
    });
    return coPartial.length === 1 ? coPartial[0] : null;
  };

  const guessTaskType = (raw) => {
    const s = (raw || '').toLowerCase();
    if (s.includes('nurse') || s.includes('reassess') || /\brn\b/.test(s)) return 'nurse_reassessment';
    if (s.includes('qa')) return 'qa_visit';
    return 'other';
  };

  const guessDate = (raw) => {
    const s = (raw || '').trim();
    const m1 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m1) return `${m1[3]}-${m1[1].padStart(2, '0')}-${m1[2].padStart(2, '0')}`;
    const m2 = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (m2) return `${m2[1]}-${m2[2].padStart(2, '0')}-${m2[3].padStart(2, '0')}`;
    return null;
  };

  // Real WellSky/admin exports use quoted fields with embedded commas and
  // newlines (multi-line descriptions, tag lists) -- a naive split(',')
  // breaks on those, so this is a small proper CSV tokenizer (RFC 4180-ish:
  // "" inside a quoted field is a literal quote).
  const parseCsv = (text) => {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; }
          else inQuotes = false;
        } else field += ch;
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(field); field = '';
      } else if (ch === '\r') {
        // ignore
      } else if (ch === '\n') {
        row.push(field); field = '';
        rows.push(row); row = [];
      } else {
        field += ch;
      }
    }
    if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
    return rows.filter(r => r.some(c => c.trim().length > 0));
  };

  const guessStatus = (raw) => {
    const s = (raw || '').toLowerCase().trim();
    if (s.includes("won't") || s.includes('wont') || s === 'cancelled' || s === 'canceled') return 'cancelled';
    if (s === 'complete' || s === 'completed') return 'completed';
    return 'pending';
  };

  const handleFile = (file) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      // Strip a leading UTF-8 BOM (ï»¿) some exports include on the header.
      const text = e.target.result.replace(/^﻿/, '');
      const table = parseCsv(text);
      if (table.length < 2) { setParsedRows([]); return; }
      const header = table[0].map(h => h.trim().toLowerCase());
      const findCol = (...names) => header.findIndex(h => names.some(n => h.includes(n)));
      const clientCol = findCol('related to', 'client', 'name');
      const firstCol = findCol('first name');
      const lastCol = findCol('last name');
      const typeCol = findCol('task', 'type');
      const descriptionCol = findCol('description');
      const dateCol = findCol('due date', 'date');
      const assignedCol = findCol('assignees', 'assigned', 'staff', 'nurse');
      const statusCol = findCol('status');
      const notesCol = findCol('note');

      const rows = table.slice(1).map(cellsRaw => {
        const cells = cellsRaw.map(c => c.trim());
        const clientName = clientCol >= 0 ? cells[clientCol] : [cells[firstCol], cells[lastCol]].filter(Boolean).join(' ');
        const client = findClient(clientName);
        const dueRaw = dateCol >= 0 ? cells[dateCol] : '';
        const description = descriptionCol >= 0 ? cells[descriptionCol] : '';
        const typeSource = typeCol >= 0 ? cells[typeCol] : description;
        const status = guessStatus(statusCol >= 0 ? cells[statusCol] : '');
        return {
          clientName,
          client,
          task_type: guessTaskType(typeSource),
          isVisitTask: guessTaskType(typeSource) !== 'other',
          due_date: guessDate(dueRaw),
          due_raw: dueRaw,
          assigned_to: (assignedCol >= 0 ? cells[assignedCol] : '').split(',')[0].trim(),
          status,
          notes: notesCol >= 0 ? cells[notesCol] : (description || ''),
        };
      }).filter(r => r.clientName);
      setParsedRows(rows);
    };
    reader.readAsText(file);
  };

  // Ad-hoc admin rows (e.g. "Renew Driver's License", "Follow up with...")
  // aren't QA/Nurse visit tasks -- intentionally skipped, same as an
  // unmatched client or a bad date.
  const validRows = parsedRows.filter(r => r.client && r.due_date && r.isVisitTask);
  const invalidRows = parsedRows.filter(r => !(r.client && r.due_date && r.isVisitTask));

  const runImport = async () => {
    setImporting(true);
    try {
      const payload = validRows.map(r => ({
        client_id: r.client.id,
        task_type: r.task_type,
        due_date: r.due_date,
        assigned_to: r.assigned_to || null,
        notes: r.notes || null,
        status: r.status,
        completed_at: r.status === 'completed' ? new Date(r.due_date + 'T00:00:00').toISOString() : null,
      }));
      const { error } = await supabase.from('client_tasks').insert(payload);
      if (error) throw error;
      showToast(`Imported ${payload.length} task${payload.length === 1 ? '' : 's'}`);
      setParsedRows([]);
      setFileName('');
      onImported();
    } catch (err) {
      showToast(err.message || 'Import failed', 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Import Tasks CSV</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Recognizes a client column (Client, Client Name, or Related To — or First/Last Name), a due-date column (Due Date or Date), and infers the visit type from a Task Type or Description column (containing "QA" or "RN"/"Reassess"). Also picks up Assignees/Assigned To and Status (Not Complete/Complete/Won't Complete). Rows that aren't QA or Nurse visits (e.g. "Renew Driver's License") are skipped automatically.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full border-2 border-dashed border-gray-200 rounded-xl py-8 text-center text-sm text-gray-500 hover:border-[#2c4f26] hover:text-[#2c4f26] transition"
          >
            <Upload size={22} className="mx-auto mb-2" />
            {fileName || 'Click to choose a CSV file'}
          </button>

          {parsedRows.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-900">
                {validRows.length} ready to import
                {invalidRows.length > 0 && <span className="text-red-600"> · {invalidRows.length} need attention</span>}
              </p>
              <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-100">
                {parsedRows.map((r, i) => (
                  <div key={i} className="px-4 py-2.5 text-xs flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{r.clientName}</p>
                      <p className="text-gray-500">{TASK_TYPE_LABELS[r.task_type]} · {r.due_raw || 'no date'}</p>
                    </div>
                    {!r.client && <span className="shrink-0 text-red-600 font-semibold">No client match</span>}
                    {r.client && !r.due_date && <span className="shrink-0 text-red-600 font-semibold">Bad date</span>}
                    {r.client && r.due_date && !r.isVisitTask && <span className="shrink-0 text-gray-400 font-semibold">Not a visit task — skipped</span>}
                    {r.client && r.due_date && r.isVisitTask && <Check size={16} className="shrink-0 text-green-600" />}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-xl transition">Cancel</button>
          <button
            onClick={runImport}
            disabled={validRows.length === 0 || importing}
            className="px-5 py-2 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-xl transition disabled:opacity-50"
          >
            {importing ? 'Importing…' : `Import ${validRows.length} Task${validRows.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Activated Insights training/compliance tracking ───────────────────────────
// Fed by manual CSV upload (Activated Insights denied API access when asked)
// -- someone with a normal Activated Insights login downloads the Compliance
// Report from their web UI and uploads it here. Each upload is treated as a
// full point-in-time snapshot (same nature as a payroll report) and replaces
// the table's entire contents, rather than trying to merge/upsert against a
// row shape only partially known ahead of a real export file -- column
// detection below is intentionally flexible (several header-name guesses per
// field) for the same reason, and may need adjusting once a real export is
// seen.
const ACTIVATED_INSIGHTS_STATUS_LABELS = { complete: 'Complete', incomplete: 'Incomplete' };

const REFRESH_STATUS_META = {
  started: { label: 'Refreshing…', color: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500 animate-pulse' },
  waiting_2fa: { label: 'Waiting on 2FA', color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  done: { label: 'Refresh complete', color: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500' },
  failed: { label: 'Refresh failed', color: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
};

const ActivatedInsightsView = ({ showToast, profile }) => {
  const [rows, setRows] = useState([]);
  const [latestBatches, setLatestBatches] = useState([]); // most recent batch per location, with imported_at/profiles
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(null); // ms epoch, or null
  const [nowTick, setNowTick] = useState(Date.now());
  const [selectedCaregiverId, setSelectedCaregiverId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('hire-newest');

  // Shared 15-minute cooldown on Refresh Now, enforced server-side (see
  // trigger-activated-insights-refresh) so two people on different devices
  // can't both kick off a run at once -- this is just the UI reflecting
  // that. activity_log is owner-only to read directly, and admin_staff can
  // also reach this tab, so the cooldown state is asked for via the edge
  // function (service role) rather than queried straight from the table.
  useEffect(() => {
    supabase.functions.invoke('trigger-activated-insights-refresh', { body: { check: true } })
      .then(({ data }) => {
        if (data?.onCooldown) setCooldownUntil(Date.now() + data.retryAfterSeconds * 1000);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!cooldownUntil) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const cooldownSecondsLeft = cooldownUntil ? Math.max(0, Math.ceil((cooldownUntil - nowTick) / 1000)) : 0;
  const onCooldown = cooldownSecondsLeft > 0;
  const cooldownLabel = cooldownSecondsLeft > 60
    ? `Available in ${Math.ceil(cooldownSecondsLeft / 60)}m`
    : `Available in ${cooldownSecondsLeft}s`;

  // Kicks off Grok's on-demand pull of both Compliance Reports via a
  // server-side webhook call (see trigger-activated-insights-refresh) --
  // this only starts that run, which takes a few minutes on Grok's side, so
  // it can't report success/failure here. The actual result shows up as a
  // new batch (Import History) and an IMPORT_SUCCESS/IMPORT_FAILURE entry
  // in the Audit Log once Grok finishes and calls import-activated-insights.
  const triggerRefresh = async () => {
    setRefreshing(true);
    try {
      const { data, error } = await supabase.functions.invoke('trigger-activated-insights-refresh');
      if (error || data?.success === false) {
        if (data?.retryAfterSeconds) setCooldownUntil(Date.now() + data.retryAfterSeconds * 1000);
        throw new Error(data?.error || error?.message || 'Failed to start refresh');
      }
      setCooldownUntil(Date.now() + 15 * 60 * 1000);
      showToast('Refresh started — new data will appear here in a few minutes');
    } catch (err) {
      showToast(err.message || 'Failed to start refresh', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  // Each location's live roster is whatever its single most recent import
  // batch contains -- older batches are retained (up to 6 per location, see
  // ImportHistoryModal) purely so a staff member can compare the latest
  // import to a past one, not to be blended into this view. Supabase/
  // PostgREST caps an unbounded query at ~1000 rows -- with two franchises'
  // full exports easily exceeding that, page through with .range() until a
  // page comes back short to fetch the whole batch.
  const loadData = async () => {
    setLoading(true);
    try {
      const { data: batches, error: batchErr } = await supabase
        .from('activated_insights_import_batches')
        .select('*, profiles:imported_by(full_name)')
        .order('location')
        .order('imported_at', { ascending: false });
      if (batchErr) throw batchErr;
      const latest = [];
      const seenLocations = new Set();
      for (const b of (batches || [])) {
        if (seenLocations.has(b.location)) continue;
        seenLocations.add(b.location);
        latest.push(b);
      }
      setLatestBatches(latest);

      const batchIds = latest.map(b => b.id);
      let all = [];
      if (batchIds.length > 0) {
        const PAGE_SIZE = 1000;
        let from = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { data, error } = await supabase
            .from('activated_insights_training')
            .select('*, wellsky_caregivers(id, first_name, last_name)')
            .in('import_batch_id', batchIds)
            .order('course_name')
            .order('id')
            .range(from, from + PAGE_SIZE - 1);
          if (error) throw error;
          all = all.concat(data || []);
          if (!data || data.length < PAGE_SIZE) break;
          from += PAGE_SIZE;
        }
      }
      setRows(all);
    } catch (err) {
      console.error('Error loading Activated Insights training data:', err);
      showToast('Failed to load training data', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Live progress of an Activated Insights refresh, posted by Grok via
  // report-activated-insights-refresh-status at each step (started /
  // waiting_2fa / done / failed) -- subscribed over Realtime rather than
  // polled, same pattern as the phone-calls live board.
  const [refreshStatus, setRefreshStatus] = useState({}); // location -> { status, detail, updated_at }
  const refreshStatusChannelRef = useRef(null);
  useEffect(() => {
    supabase.from('activated_insights_refresh_status').select('*').then(({ data }) => {
      const map = {};
      (data || []).forEach(r => { map[r.location] = r; });
      setRefreshStatus(map);
    });

    const channel = supabase
      .channel('activated-insights-refresh-status')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'activated_insights_refresh_status' }, (payload) => {
        const row = payload.new;
        if (!row) return;
        setRefreshStatus(prev => ({ ...prev, [row.location]: row }));
        // A refresh finishing (done/failed) is exactly when a new import
        // batch (or a failure the Audit Log should already show) is likely
        // to have landed -- refresh the roster so "last imported" catches up
        // without the user needing to reload the tab themselves.
        if (row.status === 'done') loadData();
      })
      .subscribe();
    refreshStatusChannelRef.current = channel;

    return () => {
      if (refreshStatusChannelRef.current) {
        supabase.removeChannel(refreshStatusChannelRef.current);
        refreshStatusChannelRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const caregiverName = (c) => c ? `${c.first_name} ${c.last_name}` : null;
  const formatHireDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const formatLastLogin = (ts) => ts ? new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;

  // Group the flat per-course rows into one entry per caregiver (matched) or
  // per raw name (unmatched), so the roster shows one line per person with a
  // compliance summary rather than one line per course.
  const matched = new Map(); // caregiver_id -> { caregiverId, name, hireDate, lastLogin, rows: [] }
  const unmatched = new Map(); // employee_name_raw -> { name, rows: [] }
  for (const r of rows) {
    if (r.caregiver_id) {
      if (!matched.has(r.caregiver_id)) {
        matched.set(r.caregiver_id, {
          caregiverId: r.caregiver_id,
          name: caregiverName(r.wellsky_caregivers) || r.employee_name_raw,
          hireDate: r.hire_date || null,
          lastLogin: r.last_login || null,
          rows: [],
        });
      }
      matched.get(r.caregiver_id).rows.push(r);
    } else {
      if (!unmatched.has(r.employee_name_raw)) {
        unmatched.set(r.employee_name_raw, { name: r.employee_name_raw, rows: [] });
      }
      unmatched.get(r.employee_name_raw).rows.push(r);
    }
  }

  const complianceSummary = (personRows) => {
    const total = personRows.length;
    const complete = personRows.filter(r => r.status === 'complete').length;
    return { total, complete, allComplete: total > 0 && complete === total };
  };

  const sortComparator = (a, b) => {
    switch (sortBy) {
      case 'name-desc':
        return b.name.localeCompare(a.name);
      case 'hire-newest':
      case 'hire-oldest': {
        // No hire date on record (e.g. imported before hire-date tracking was
        // added) always sorts to the end, regardless of direction.
        if (!a.hireDate && !b.hireDate) return 0;
        if (!a.hireDate) return 1;
        if (!b.hireDate) return -1;
        return sortBy === 'hire-newest' ? b.hireDate.localeCompare(a.hireDate) : a.hireDate.localeCompare(b.hireDate);
      }
      case 'completion-low':
      case 'completion-high': {
        const aSum = complianceSummary(a.rows), bSum = complianceSummary(b.rows);
        const aPct = aSum.total > 0 ? aSum.complete / aSum.total : 0;
        const bPct = bSum.total > 0 ? bSum.complete / bSum.total : 0;
        return sortBy === 'completion-low' ? aPct - bPct : bPct - aPct;
      }
      case 'name-asc':
      default:
        return a.name.localeCompare(b.name);
    }
  };

  const term = searchTerm.trim().toLowerCase();
  const matchedPeople = [...matched.values()]
    .filter(p => !term || p.name.toLowerCase().includes(term))
    .sort(sortComparator);
  const unmatchedPeople = [...unmatched.values()]
    .filter(p => !term || p.name.toLowerCase().includes(term))
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedPerson = selectedCaregiverId ? matchedPeople.find(p => p.caregiverId === selectedCaregiverId) : null;

  if (selectedPerson) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <button onClick={() => setSelectedCaregiverId(null)} className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition mb-4">
          <ChevronLeft size={16} /> Activated Insights
        </button>
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h1 className="text-xl font-bold text-gray-900">{selectedPerson.name}</h1>
            {(selectedPerson.hireDate || selectedPerson.lastLogin) && (
              <p className="text-sm text-gray-500 mt-1">
                {selectedPerson.hireDate && `Hired ${formatHireDate(selectedPerson.hireDate)}`}
                {selectedPerson.hireDate && selectedPerson.lastLogin && ' · '}
                {selectedPerson.lastLogin && `Last login ${formatLastLogin(selectedPerson.lastLogin)}`}
              </p>
            )}
          </div>
          <div className="divide-y divide-gray-100">
            {selectedPerson.rows.map((r, i) => {
              const parts = [];
              if (r.is_initial) parts.push('Initial');
              if (r.is_annual) parts.push('Annual');
              if (r.completed_date) parts.push(`Completed ${new Date(r.completed_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`);
              if (r.total_time) parts.push(`Time: ${r.total_time}`);
              if (r.percentage != null) parts.push(`Progress: ${r.percentage}%`);
              if (r.score != null) parts.push(`Score: ${r.score}`);
              if (r.exam_result) parts.push(`Exam: ${r.exam_result}`);
              return (
                <div key={i} className="px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{r.course_name}</p>
                    {parts.length > 0 && <p className="text-xs text-gray-500 mt-0.5">{parts.join(' · ')}</p>}
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold shrink-0 ${r.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {ACTIVATED_INSIGHTS_STATUS_LABELS[r.status] || r.status || 'Unknown'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Activated Insights</h1>
          <div className="text-sm text-gray-600">
            {latestBatches.length === 0 ? 'Never imported' : latestBatches.map(b => (
              <p key={b.location}>
                {b.location}: last imported {new Date(b.imported_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                {b.profiles?.full_name ? ` by ${b.profiles.full_name}` : b.source === 'automated' ? ' (automated)' : ''}
              </p>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={() => setShowHistory(true)}
            className="flex-1 sm:flex-none border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl font-semibold hover:bg-gray-50 transition flex items-center gap-2 text-sm justify-center"
          >
            <ClipboardList size={16} /> Import History
          </button>
          <button
            onClick={triggerRefresh}
            disabled={refreshing || onCooldown}
            title={onCooldown ? 'A refresh was started recently — only one run at a time to avoid two people kicking it off at once' : undefined}
            className="flex-1 sm:flex-none border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl font-semibold hover:bg-gray-50 transition flex items-center gap-2 text-sm justify-center disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Starting…' : onCooldown ? cooldownLabel : 'Refresh Now'}
          </button>
          {profile?.role === 'owner' && (
            <button
              onClick={() => setShowImport(true)}
              className="flex-1 sm:flex-none bg-[#2c4f26] text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-[#1f3a1c] transition flex items-center gap-2 text-sm justify-center"
            >
              <Upload size={16} /> Upload Compliance Report
            </button>
          )}
        </div>
      </div>

      {Object.entries(refreshStatus).some(([, s]) => s.status && s.status !== 'idle') && (
        <div className="flex flex-col sm:flex-row gap-2 mb-6">
          {Object.entries(refreshStatus)
            .filter(([, s]) => s.status && s.status !== 'idle')
            .map(([loc, s]) => {
              const meta = REFRESH_STATUS_META[s.status] || { label: s.status, color: 'bg-gray-50 text-gray-600 border-gray-200', dot: 'bg-gray-400' };
              return (
                <div key={loc} className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border text-sm ${meta.color}`}>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${meta.dot}`} />
                  <span className="font-semibold shrink-0">{loc}:</span>
                  <span className="truncate">{s.status === 'failed' && s.detail ? `Refresh failed — ${s.detail}` : meta.label}</span>
                </div>
              );
            })}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-6">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
              placeholder="Search by caregiver name…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="border border-gray-200 rounded-xl text-sm px-3 py-2.5 bg-white focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition sm:w-56"
          >
            <option value="name-asc">Name (A–Z)</option>
            <option value="name-desc">Name (Z–A)</option>
            <option value="hire-newest">Hire Date (Newest)</option>
            <option value="hire-oldest">Hire Date (Oldest)</option>
            <option value="completion-low">Completion (Lowest First)</option>
            <option value="completion-high">Completion (Highest First)</option>
          </select>
        </div>

        {loading ? (
          <div className="p-16 text-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" /></div>
        ) : matchedPeople.length === 0 ? (
          <div className="p-16 text-center text-gray-500">
            <ClipboardList size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">
              {rows.length === 0
                ? 'No training data yet — upload a Compliance Report to get started'
                : term && unmatchedPeople.length > 0
                  ? 'No matched caregivers — see unmatched names below'
                  : 'No caregivers match your search'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {matchedPeople.map(p => {
              const { total, complete, allComplete } = complianceSummary(p.rows);
              return (
                <div key={p.caregiverId} onClick={() => setSelectedCaregiverId(p.caregiverId)} className="p-4 hover:bg-gray-50 transition cursor-pointer flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm">{p.name}</p>
                    {p.hireDate && <p className="text-xs text-gray-400 mt-0.5">Hired {formatHireDate(p.hireDate)}</p>}
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold shrink-0 ${allComplete ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {complete}/{total} complete
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {unmatchedPeople.length > 0 && (
        <div className="bg-white rounded-2xl border border-amber-200 overflow-hidden">
          <div className="p-4 border-b border-amber-100 bg-amber-50">
            <p className="text-sm font-semibold text-amber-800">{unmatchedPeople.length} name{unmatchedPeople.length === 1 ? '' : 's'} from the report didn't match a caregiver</p>
            <p className="text-xs text-amber-700 mt-0.5">Check for a typo or name mismatch, or they may not be in the Care Pros roster yet.</p>
          </div>
          <div className="divide-y divide-amber-100">
            {unmatchedPeople.map(p => (
              <div key={p.name} className="px-4 py-3 text-sm text-gray-700">{p.name} <span className="text-gray-400">· {p.rows.length} course{p.rows.length === 1 ? '' : 's'}</span></div>
            ))}
          </div>
        </div>
      )}

      <ImportComplianceReportModal
        show={showImport}
        onClose={() => setShowImport(false)}
        profile={profile}
        showToast={showToast}
        onImported={() => { setShowImport(false); loadData(); }}
      />
      <ImportHistoryModal
        show={showHistory}
        onClose={() => setShowHistory(false)}
        showToast={showToast}
      />
    </div>
  );
};

// Lets staff see the last up-to-6 imports per location (see BATCH_RETENTION
// in runImport/import-activated-insights) and compare any past one against
// the current live roster to see whether compliance is trending up --
// per-caregiver, not just an aggregate percentage, since "who's stuck" is
// usually the more actionable question.
const ActivatedInsightsLocationLabels = { '655': '655 — Lewis & Leigh, Inc.', '507': '507 — Jac Second, LLC' };

const ImportHistoryModal = ({ show, onClose, showToast }) => {
  const [location, setLocation] = useState('655');
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareBatchId, setCompareBatchId] = useState(null);
  const [compareRows, setCompareRows] = useState(null); // { before: [...], after: [...] } once loaded
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    if (!show) return;
    setCompareBatchId(null);
    setCompareRows(null);
    setLoading(true);
    supabase
      .from('activated_insights_import_batches')
      .select('*, profiles:imported_by(full_name)')
      .eq('location', location)
      .order('imported_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) { showToast('Failed to load import history', 'error'); setBatches([]); }
        else setBatches(data || []);
        setLoading(false);
      });
  }, [show, location]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!show) return null;

  const latest = batches[0] || null;

  const runCompare = async (pastBatch) => {
    if (!latest || pastBatch.id === latest.id) return;
    setCompareBatchId(pastBatch.id);
    setComparing(true);
    setCompareRows(null);
    try {
      const [{ data: afterRows, error: e1 }, { data: beforeRows, error: e2 }] = await Promise.all([
        supabase.from('activated_insights_training').select('caregiver_id, employee_name_raw, course_name, status, wellsky_caregivers(first_name, last_name)').eq('import_batch_id', latest.id),
        supabase.from('activated_insights_training').select('caregiver_id, employee_name_raw, course_name, status, wellsky_caregivers(first_name, last_name)').eq('import_batch_id', pastBatch.id),
      ]);
      if (e1) throw e1;
      if (e2) throw e2;
      setCompareRows({ before: beforeRows || [], after: afterRows || [] });
    } catch (err) {
      showToast(err.message || 'Failed to load comparison', 'error');
      setCompareBatchId(null);
    } finally {
      setComparing(false);
    }
  };

  // One line per caregiver who appears in either snapshot -- complete/total
  // course counts before vs. after, with the common "unmatched" rows (no
  // caregiver_id) grouped under their raw name the same way the main roster
  // does, so a name mismatch doesn't just vanish from the comparison.
  const buildPeopleSummary = (rowsForBatch) => {
    const people = new Map(); // key -> { name, total, complete }
    for (const r of rowsForBatch) {
      const key = r.caregiver_id || `raw:${r.employee_name_raw}`;
      const name = r.caregiver_id
        ? (r.wellsky_caregivers ? `${r.wellsky_caregivers.first_name} ${r.wellsky_caregivers.last_name}` : r.employee_name_raw)
        : r.employee_name_raw;
      if (!people.has(key)) people.set(key, { name, total: 0, complete: 0 });
      const p = people.get(key);
      p.total += 1;
      if (r.status === 'complete') p.complete += 1;
    }
    return people;
  };

  const comparisonList = compareRows ? (() => {
    const beforeMap = buildPeopleSummary(compareRows.before);
    const afterMap = buildPeopleSummary(compareRows.after);
    const keys = new Set([...beforeMap.keys(), ...afterMap.keys()]);
    const list = [...keys].map(key => {
      const before = beforeMap.get(key) || null;
      const after = afterMap.get(key) || null;
      const beforePct = before && before.total > 0 ? before.complete / before.total : null;
      const afterPct = after && after.total > 0 ? after.complete / after.total : null;
      let change = 'same';
      if (!before && after) change = 'new';
      else if (before && !after) change = 'removed';
      else if (beforePct != null && afterPct != null) {
        if (afterPct > beforePct) change = 'improved';
        else if (afterPct < beforePct) change = 'regressed';
      }
      return { key, name: (after || before).name, before, after, change };
    });
    const order = { regressed: 0, improved: 1, new: 2, removed: 3, same: 4 };
    return list.sort((a, b) => (order[a.change] - order[b.change]) || a.name.localeCompare(b.name));
  })() : [];

  const CHANGE_META = {
    improved: { label: 'Improved', color: 'bg-green-100 text-green-700' },
    regressed: { label: 'Regressed', color: 'bg-red-100 text-red-700' },
    new: { label: 'New', color: 'bg-blue-100 text-blue-700' },
    removed: { label: 'No longer in report', color: 'bg-gray-100 text-gray-500' },
    same: { label: 'No change', color: 'bg-gray-100 text-gray-500' },
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Import History</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex gap-2">
            {Object.entries(ActivatedInsightsLocationLabels).map(([val, label]) => (
              <button
                key={val}
                onClick={() => setLocation(val)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${location === val ? 'bg-[#2c4f26] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="p-10 text-center"><div className="animate-spin rounded-full h-8 w-8 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" /></div>
          ) : batches.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No imports yet for this location.</p>
          ) : (
            <div className="border border-gray-100 rounded-xl divide-y divide-gray-100">
              {batches.map((b, i) => (
                <div key={b.id} className="p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {i === 0 ? 'Current' : `${i + 1} imports ago`}
                      <span className="font-normal text-gray-500"> · {new Date(b.imported_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {b.total_people_count} caregivers · {b.complete_people_count} fully complete · {b.matched_count} matched / {b.unmatched_count} unmatched rows
                      {b.profiles?.full_name ? ` · by ${b.profiles.full_name}` : b.source === 'automated' ? ' · automated' : ''}
                    </p>
                  </div>
                  {i > 0 && (
                    <button
                      onClick={() => runCompare(b)}
                      disabled={comparing && compareBatchId === b.id}
                      className="text-xs font-semibold text-[#2c4f26] border border-[#2c4f26]/30 px-3 py-1.5 rounded-lg hover:bg-[#2c4f26]/5 transition disabled:opacity-50"
                    >
                      {comparing && compareBatchId === b.id ? 'Comparing…' : 'Compare to Current'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {compareRows && (
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-2">
                Current vs. {new Date(batches.find(b => b.id === compareBatchId)?.imported_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
              <div className="border border-gray-100 rounded-xl divide-y divide-gray-100 max-h-80 overflow-y-auto">
                {comparisonList.map(c => (
                  <div key={c.key} className="px-3 py-2.5 flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium text-gray-900 truncate">{c.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-gray-500 tabular-nums">
                        {c.before ? `${c.before.complete}/${c.before.total}` : '—'} → {c.after ? `${c.after.complete}/${c.after.total}` : '—'}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${CHANGE_META[c.change].color}`}>{CHANGE_META[c.change].label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ImportComplianceReportModal = ({ show, onClose, profile, showToast, onImported }) => {
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [importing, setImporting] = useState(false);
  const [location, setLocation] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    // Same unbounded-query cap as ActivatedInsightsView's loadData -- with
    // two franchises' caregivers in one table this can easily exceed
    // Supabase's ~1000-row default limit, silently truncating the roster
    // used for matching and pushing real matches into "unmatched" for no
    // reason other than their record never got fetched.
    (async () => {
      const PAGE_SIZE = 1000;
      let all = [];
      let from = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data } = await supabase.from('wellsky_caregivers').select('id, first_name, last_name, email').order('id').range(from, from + PAGE_SIZE - 1);
        all = all.concat(data || []);
        if (!data || data.length < PAGE_SIZE) break;
        from += PAGE_SIZE;
      }
      setCaregivers(all);
    })();
  }, [show]);

  if (!show) return null;

  const loose = (s) => (s || '').toLowerCase().trim().replace(/[.\-'’â]/g, ' ').replace(/\s+/g, ' ').trim();

  const findCaregiver = (nameStr) => {
    const target = loose(nameStr);
    if (!target) return null;
    const exact = caregivers.find(c => loose(`${c.first_name} ${c.last_name}`) === target);
    if (exact) return exact;
    const targetTokens = new Set(target.split(' '));
    const partial = caregivers.filter(c => {
      const cTokens = new Set(loose(`${c.first_name} ${c.last_name}`).split(' '));
      return [...cTokens].every(tok => targetTokens.has(tok));
    });
    return partial.length === 1 ? partial[0] : null;
  };

  // Some exports (e.g. a per-course custom report keyed by first name only)
  // have heavy name collisions -- multiple "Jennifer"s, "Ann"s, etc. with no
  // last name at all -- where name-based matching is unreliable or outright
  // ambiguous. Email is unique and far more trustworthy when the export has
  // one; name matching stays as the fallback for exports that don't.
  const findCaregiverByEmail = (email) => {
    const target = (email || '').trim().toLowerCase();
    if (!target) return null;
    return caregivers.find(c => (c.email || '').trim().toLowerCase() === target) || null;
  };

  // The CarePro training team only wants these specific courses tracked --
  // real exports also include one-off/legacy/location-specific courses
  // (old quiz series, Florida DOEA, WellSky Royalties, scheduling modules,
  // etc.) that aren't part of this checklist and would otherwise inflate
  // the "X/Y complete" counts with courses nobody is being held to. Rows
  // for anything not in this list are dropped entirely during import.
  // Patterns cover both export shapes seen live: the "Training Title"
  // column (e.g. "Home Instead - Safety for the Client") and the plainer
  // "Course" column from the per-course custom report (same text, same
  // patterns -- confirmed against both real files).
  const REQUIRED_COURSES = [
    { canon: 'Safety for the Client', test: /^home instead - safety for the client$/i },
    { canon: 'Quiz: Safety for the Client', test: /^quiz-?\s*safety for the client$/i },
    { canon: 'Safety for the Care Professional', test: /^home instead - safety for the care professional$/i },
    { canon: 'Quiz: Safety for the Care Professional', test: /^quiz-?\s*safety for the care professional$/i },
    { canon: 'Maintaining Professional Boundaries', test: /^home instead - maintaining professional boundaries$/i },
    { canon: 'Quiz: Maintaining Professional Boundaries', test: /^quiz-?\s*maintaining professional boundaries$/i },
    { canon: 'Relationship Fundamentals', test: /^home instead - relationship fundamentals$/i },
    { canon: 'Quiz: Relationship Fundamentals', test: /^quiz-?\s*relationship fundamentals$/i },
    { canon: 'Introduction to Dementia', test: /^home instead - introduction to dementia$/i },
    // Hyphen spacing varies by franchise -- "Dementia - Related Behaviors"
    // vs. "Dementia-related Behaviors" (confirmed against a second real
    // franchise's export) -- same flexible-hyphen treatment as the
    // Fundamentals pattern below.
    { canon: 'Dementia: Related Behaviors', test: /^dementia\s*-?\s*related behaviors$/i },
    { canon: 'Fundamentals of Person-Centered Care', test: /^fundamentals of person\s*-?\s*centered care$/i },
    { canon: "Basics of Alzheimer's Disease and Dementia", test: /^basics of alzheimer.s disease and dementia$/i },
    { canon: 'Care Pro Handbook Orientation Quiz', test: /^care pro handbook orientation quiz$/i },
    { canon: 'Joint Conditions & Adaptive Devices', test: /^hi-cg-joint-conditions-adaptive-devices$/i },
    { canon: 'Effective Communication', test: /^hi-cg-effectivecommunication$/i },
    { canon: 'Body Mechanics', test: /^hi-cg-body-mechanics$/i },
    { canon: 'Infection Control and Safety', test: /^hi-cg-infection-control-and-safety$/i },
    { canon: 'Personal Hygiene and Grooming', test: /^hi-cg-personal-hygiene-grooming$/i },
    { canon: 'Getting Started with Activated Insights (optional)', test: /^getting started with activated insights/i },
  ];
  const normalizeCourseName = (raw) => {
    const s = (raw || '').trim();
    const match = REQUIRED_COURSES.find(c => c.test.test(s));
    return match ? match.canon : null;
  };

  // Real WellSky/admin exports use quoted fields with embedded commas and
  // newlines -- a naive split(',') breaks on those, so this is a small
  // proper CSV tokenizer (RFC 4180-ish: "" inside a quoted field is a
  // literal quote). Same tokenizer as ImportTasksModal.
  const parseCsv = (text) => {
    const rowsOut = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; }
          else inQuotes = false;
        } else field += ch;
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(field); field = '';
      } else if (ch === '\r') {
        // ignore
      } else if (ch === '\n') {
        row.push(field); field = '';
        rowsOut.push(row); row = [];
      } else {
        field += ch;
      }
    }
    if (field.length > 0 || row.length > 0) { row.push(field); rowsOut.push(row); }
    return rowsOut.filter(r => r.some(c => c.trim().length > 0));
  };

  // Real Activated Insights exports carry a full timestamp here (e.g.
  // "09/05/2026 12:03 pm"), not a bare date -- confirmed against a real
  // Compliance Report export, which the original end-anchored regex
  // rejected outright, silently dropping every completion date. Matches
  // the leading date token and ignores whatever trails it.
  const guessDate = (raw) => {
    const s = (raw || '').trim();
    const m1 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m1) return `${m1[3]}-${m1[1].padStart(2, '0')}-${m1[2].padStart(2, '0')}`;
    const m2 = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m2) return `${m2[1]}-${m2[2].padStart(2, '0')}-${m2[3].padStart(2, '0')}`;
    return null;
  };

  // "Last Login" carries a time-of-day too ("10/21/2026 10:59"), unlike the
  // date-only columns guessDate handles.
  const guessDateTime = (raw) => {
    const m = (raw || '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})/);
    if (!m) return null;
    const [, mo, d, y, h, mi] = m;
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}T${h.padStart(2, '0')}:${mi}:00`;
  };

  const guessNumber = (raw) => {
    const s = (raw || '').trim();
    return s !== '' && !isNaN(Number(s)) ? Number(s) : null;
  };

  // Status isn't always its own column -- some exports only mark it via a
  // completion date being present/blank. Falls back to that when there's no
  // explicit complete/incomplete-shaped column.
  const guessStatus = (raw, completedDate) => {
    const s = (raw || '').toLowerCase().trim();
    if (s.includes('incomplete') || s === 'no' || s === 'false') return 'incomplete';
    if (s.includes('complete') || s === 'yes' || s === 'true') return 'complete';
    return completedDate ? 'complete' : 'incomplete';
  };

  const guessBool = (raw) => {
    const s = (raw || '').toLowerCase().trim();
    if (!s) return null;
    return s === 'yes' || s === 'true' || s === 'x' || s === '1';
  };

  const handleFile = (file) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result.replace(/^﻿/, ''); // strip leading UTF-8 BOM
      const table = parseCsv(text);
      if (table.length < 2) { setParsedRows([]); return; }
      const header = table[0].map(h => h.trim().toLowerCase());
      const findCol = (...names) => header.findIndex(h => names.some(n => h.includes(n)));
      const findExactCol = (name) => header.findIndex(h => h === name);
      // Real exports split Last Name / First Name into separate columns
      // (confirmed live) -- combined-name fallback kept for any export
      // variant that uses one column instead.
      const lastNameCol = findCol('last name');
      const firstNameCol = findCol('first name');
      // A bare 'name' substring match also hits 'Course Formatted Name' (which
      // holds the course, not the person) in the per-course custom report --
      // it sits earlier in the header row than the real 'Formatted Name'
      // column, so the exact header name is tried first (confirmed against a
      // real export).
      const nameCol = findExactCol('formatted name') >= 0
        ? findExactCol('formatted name')
        : findCol('employee name', 'caregiver name', 'practitioner name', 'name');
      const emailCol = findCol('email address', 'email');
      // Order matters: 'training title' and 'training completed date' both
      // contain "training", and 'due date'/'registration date'/etc all
      // contain "date" -- a bare 'training' or 'date' fallback would match
      // whichever of those colliding columns happens to come first
      // (confirmed live: 'training completed date' sits right before
      // 'training title' in the real header row, and 'due date' sits right
      // before it too), so the specific column names are matched first and
      // the generic fallbacks are deliberately left out rather than risk
      // silently grabbing the wrong column. Same reasoning for an exact
      // "Course" header -- a per-course custom report also has "Assigned
      // Courses (Count)" and "Completed Courses (Count)" columns, both of
      // which contain "course" as a substring and would otherwise win over
      // the real course column by sitting earlier in the header row
      // (confirmed live) -- so the exact column name is tried first.
      const courseCol = findExactCol('course') >= 0 ? findExactCol('course') : findCol('training title', 'course name');
      const statusCol = findCol('complete/incomplete', 'completion status', 'status');
      const dateCol = findCol('training completed date', 'completion date', 'completed date', 'date completed');
      const hireDateCol = findCol('hire date');
      const lastLoginCol = findCol('last login');
      const totalTimeCol = findCol('total time');
      const percentageCol = findCol('percentage');
      const scoreCol = findCol('score');
      const initialCol = findCol('is initial', 'initial training');
      const annualCol = findCol('is annual', 'annual training');
      const examCol = findCol('exam result', 'exam score');

      const rows = table.slice(1).map(cellsRaw => {
        const cells = cellsRaw.map(c => c.trim());
        const name = (lastNameCol >= 0 || firstNameCol >= 0)
          ? [firstNameCol >= 0 ? cells[firstNameCol] : '', lastNameCol >= 0 ? cells[lastNameCol] : ''].filter(Boolean).join(' ')
          // The per-course custom report's 'Formatted Name' column is
          // "Last, First" -- reorder to "First Last" to match the other
          // export shape's display/matching convention.
          : (nameCol >= 0 ? (cells[nameCol] || '').replace(/^([^,]+),\s*(.+)$/, '$2 $1') : '');
        const email = emailCol >= 0 ? cells[emailCol] : '';
        // Append the email for display/review purposes only (never fed to
        // the matchers below) -- some exports give only a first name, where
        // several different people can share the exact same raw name (e.g.
        // "Jennifer" alone appears 4+ times in a real export), so the bare
        // name is useless for telling unmatched rows apart in the review list.
        const employeeNameRaw = name + (email ? ` (${email})` : '');
        const rawCourse = courseCol >= 0 ? cells[courseCol] : '';
        const courseName = normalizeCourseName(rawCourse);
        const dateRaw = dateCol >= 0 ? cells[dateCol] : '';
        const completedDate = guessDate(dateRaw);
        const hireDate = hireDateCol >= 0 ? guessDate(cells[hireDateCol]) : null;
        const lastLogin = lastLoginCol >= 0 ? guessDateTime(cells[lastLoginCol]) : null;
        return {
          employeeNameRaw,
          caregiver: (email && findCaregiverByEmail(email)) || findCaregiver(name),
          courseName,
          status: guessStatus(statusCol >= 0 ? cells[statusCol] : '', completedDate),
          completedDate,
          hireDate,
          lastLogin,
          totalTime: totalTimeCol >= 0 ? (cells[totalTimeCol] || null) : null,
          percentage: percentageCol >= 0 ? guessNumber(cells[percentageCol]) : null,
          score: scoreCol >= 0 ? guessNumber(cells[scoreCol]) : null,
          isInitial: initialCol >= 0 ? guessBool(cells[initialCol]) : null,
          isAnnual: annualCol >= 0 ? guessBool(cells[annualCol]) : null,
          examResult: examCol >= 0 ? cells[examCol] : '',
        };
      }).filter(r =>
        r.employeeNameRaw && r.courseName &&
        // Some accounts get flagged "- DEACTIVATED" in the name rather than
        // removed from the export, alongside a second, current-but-reset
        // block of rows for the same email -- keeping both would double-
        // count every course for that person, so the stale deactivated
        // block is dropped entirely (confirmed against a real export).
        !/deactivated/i.test(r.employeeNameRaw)
      );

      // A real export can also list the same person+course twice outright
      // (e.g. duplicate pagination) with no deactivation tag -- collapse to
      // one row per person+course so the completion count is never inflated.
      // Prefer a 'complete' status over 'incomplete' so a stray duplicate
      // never masks a real completion.
      const deduped = new Map();
      for (const r of rows) {
        const key = `${r.caregiver?.id || r.employeeNameRaw}__${r.courseName}`;
        const existing = deduped.get(key);
        if (!existing || (existing.status !== 'complete' && r.status === 'complete')) {
          deduped.set(key, r);
        }
      }
      setParsedRows([...deduped.values()]);
    };
    reader.readAsText(file);
  };

  const matchedCount = parsedRows.filter(r => r.caregiver).length;
  const unmatchedCount = parsedRows.length - matchedCount;

  const runImport = async () => {
    setImporting(true);
    try {
      // Each upload becomes a new batch rather than replacing the prior
      // snapshot, so the Import History view can compare the latest import
      // to an earlier one (per location). BATCH_RETENTION caps how many
      // batches are kept per location; the live roster (ActivatedInsightsView)
      // only ever reads the newest batch per location, so this is invisible
      // day-to-day -- it just keeps the last few around for comparison.
      const matchedCount = parsedRows.filter(r => r.caregiver).length;
      const unmatchedCount = parsedRows.length - matchedCount;
      const perPerson = new Map();
      for (const r of parsedRows) {
        if (!r.caregiver) continue;
        const entry = perPerson.get(r.caregiver.id) || { total: 0, complete: 0 };
        entry.total += 1;
        if (r.status === 'complete') entry.complete += 1;
        perPerson.set(r.caregiver.id, entry);
      }
      const totalPeopleCount = perPerson.size;
      const completePeopleCount = [...perPerson.values()].filter(p => p.total > 0 && p.complete === p.total).length;

      const { data: batch, error: batchError } = await supabase
        .from('activated_insights_import_batches')
        .insert({
          location,
          source: 'manual',
          imported_by: profile?.id || null,
          total_records: parsedRows.length,
          matched_count: matchedCount,
          unmatched_count: unmatchedCount,
          total_people_count: totalPeopleCount,
          complete_people_count: completePeopleCount,
        })
        .select('id')
        .single();
      if (batchError) throw batchError;

      const payload = parsedRows.map(r => ({
        caregiver_id: r.caregiver?.id || null,
        employee_name_raw: r.employeeNameRaw,
        course_name: r.courseName,
        is_initial: r.isInitial,
        is_annual: r.isAnnual,
        status: r.status,
        completed_date: r.completedDate,
        hire_date: r.hireDate,
        last_login: r.lastLogin,
        total_time: r.totalTime,
        percentage: r.percentage,
        score: r.score,
        exam_result: r.examResult || null,
        location,
        imported_by: profile?.id || null,
        import_batch_id: batch.id,
      }));
      const { error: insertError } = await supabase.from('activated_insights_training').insert(payload);
      if (insertError) throw insertError;

      const BATCH_RETENTION = 6;
      const { data: oldBatches } = await supabase
        .from('activated_insights_import_batches')
        .select('id')
        .eq('location', location)
        .order('imported_at', { ascending: false })
        .range(BATCH_RETENTION, 1000);
      if (oldBatches && oldBatches.length > 0) {
        await supabase.from('activated_insights_import_batches').delete().in('id', oldBatches.map(b => b.id));
      }

      showToast(`Imported ${payload.length} record${payload.length === 1 ? '' : 's'}`);
      setParsedRows([]);
      setFileName('');
      setLocation('');
      onImported();
    } catch (err) {
      showToast(err.message || 'Import failed', 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Upload Compliance Report</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Upload a Compliance Report or per-course custom report CSV exported from Activated Insights. This replaces that location's existing training data with what's in this file — make sure it's the full, current export for the location selected below. Matches caregivers by email when the export has one (more reliable than name, since names alone collide often), falling back to name matching otherwise. Only the required course checklist is imported — other courses in the export (one-off, legacy, or location-specific) are skipped automatically so they don't inflate completion counts.
          </p>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Which location is this report for?</label>
            <select
              value={location}
              onChange={e => setLocation(e.target.value)}
              className="w-full border border-gray-200 rounded-xl text-sm px-3 py-2.5 bg-white focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
            >
              <option value="">Select location…</option>
              <option value="655">655 — Home Instead St. Clair (Lewis &amp; Leigh, Inc.)</option>
              <option value="507">507 — Home Instead St. Clair (Jac Second, LLC)</option>
            </select>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full border-2 border-dashed border-gray-200 rounded-xl py-8 text-center text-sm text-gray-500 hover:border-[#2c4f26] hover:text-[#2c4f26] transition"
          >
            <Upload size={22} className="mx-auto mb-2" />
            {fileName || 'Click to choose a CSV file'}
          </button>

          {parsedRows.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-900">
                {parsedRows.length} record{parsedRows.length === 1 ? '' : 's'} parsed — {matchedCount} matched to a caregiver
                {unmatchedCount > 0 && <span className="text-amber-600"> · {unmatchedCount} unmatched</span>}
              </p>
              <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-100">
                {parsedRows.map((r, i) => (
                  <div key={i} className="px-4 py-2.5 text-xs flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{r.employeeNameRaw}</p>
                      <p className="text-gray-500 truncate">{r.courseName}</p>
                    </div>
                    {r.caregiver ? <Check size={16} className="shrink-0 text-green-600" /> : <span className="shrink-0 text-amber-600 font-semibold">No match</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-xl transition">Cancel</button>
          <button
            onClick={runImport}
            disabled={parsedRows.length === 0 || !location || importing}
            className="px-5 py-2 text-sm font-semibold text-white bg-[#2c4f26] hover:bg-[#1f3a1c] rounded-xl transition disabled:opacity-50"
          >
            {importing ? 'Importing…' : !location && parsedRows.length > 0 ? 'Select a location first' : `Import ${parsedRows.length} Record${parsedRows.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Visit Planner ─────────────────────────────────────────────────────────────
// Map-based view over the same client_tasks data as the Tasks tab -- this is
// deliberately just a geographic lens on it, not a parallel system. See
// computeVisitStatus/VISIT_STATUS_META above for the shared status logic.

const NOVA_CENTER = [-77.45, 38.85]; // Northern Virginia -- roughly the middle of this client base

const daysUntil = (dateStr, today = new Date()) => {
  if (!dateStr) return null;
  const due = new Date(dateStr + 'T00:00:00');
  const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((due - todayMid) / 86400000);
};

const buildVisitInfo = (tasksForClient, visitType, settings) => {
  const cfg = settings[visitType] || { frequency_days: visitType === 'qa_visit' ? 84 : 42, early_window_days: visitType === 'qa_visit' ? 14 : 7 };
  const pending = tasksForClient.filter(t => t.task_type === visitType && t.status === 'pending').sort((a, b) => a.due_date.localeCompare(b.due_date));
  const completed = tasksForClient.filter(t => t.task_type === visitType && t.status === 'completed' && t.completed_at).sort((a, b) => b.completed_at.localeCompare(a.completed_at));
  const dueDate = pending[0]?.due_date || null;
  const lastCompleted = completed[0]?.completed_at || null;
  const status = computeVisitStatus(dueDate, cfg.early_window_days);
  const eligibleEarlyDate = dueDate ? new Date(new Date(dueDate + 'T00:00:00').getTime() - cfg.early_window_days * 86400000) : null;
  return {
    visitType, dueDate, lastCompleted, status,
    eligibleEarlyDate: eligibleEarlyDate ? eligibleEarlyDate.toISOString().slice(0, 10) : null,
    daysUntilDue: daysUntil(dueDate),
    task: pending[0] || null,
    assignedTo: pending[0]?.assigned_to || null,
  };
};

const combineWindow = (qaInfo, nurseInfo) => {
  if (!qaInfo.dueDate || !nurseInfo.dueDate) return null;
  const windowStart = qaInfo.eligibleEarlyDate > nurseInfo.eligibleEarlyDate ? qaInfo.eligibleEarlyDate : nurseInfo.eligibleEarlyDate;
  const windowEnd = qaInfo.dueDate < nurseInfo.dueDate ? qaInfo.dueDate : nurseInfo.dueDate;
  if (windowStart > windowEnd) return null; // windows don't overlap
  return { start: windowStart, end: windowEnd };
};

const fmtShortDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

const visitPlannerDisplayName = (c) => `${c.first_name} ${c.last_name}${c.co_client_name ? ` & ${c.co_client_name}` : ''}`;

const fmtShiftTime = (d) => d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '?';
const fmtShiftRange = (shift) => `${fmtShiftTime(shift.start)}${shift.end ? ` – ${fmtShiftTime(shift.end)}` : ''}`;

// Not required -- just nice when it lines up -- so this only ever adds an
// indicator/filter on top of the existing QA/Nurse due-date logic, never
// changes it. 'now' beats 'today' if a client happens to have both (e.g.
// back-to-back shifts).
const shiftStatusFor = (clientShifts, now, localTodayStart, localTodayEnd) => {
  if (!clientShifts || clientShifts.length === 0) return null;
  const sorted = [...clientShifts].sort((a, b) => a.start - b.start);
  const current = sorted.find(sh => sh.start <= now && (!sh.end || now <= sh.end));
  if (current) return { when: 'now', shift: current };
  const todays = sorted.filter(sh => sh.start < localTodayEnd && (!sh.end || sh.end > localTodayStart));
  if (todays.length > 0) return { when: 'today', shift: todays[0] };
  return null;
};

// Owns the MapLibre instance's full lifecycle in its own mount/unmount --
// kept as a separate component (not inlined in VisitPlannerView) so that
// switching to the "View Client" screen actually unmounts this and runs the
// map's cleanup properly. Previously the map lived directly in
// VisitPlannerView and only its JSX output was swapped for <ClientDetailView
// />; React still ran this component's own hooks every render regardless,
// so the marker-refresh effect kept firing against a MapLibre instance
// whose container div no longer existed in the DOM -- silently breaking the
// page (no working back button, nothing else interactive) the moment you
// clicked "View Client".
const VisitPlannerMap = ({ clients, selectedIds, onMarkerClick, loading, displayName }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    mapRef.current = new MapLibreMap({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      center: NOVA_CENTER,
      zoom: 10,
    });
    mapRef.current.addControl(new MapLibreNavigationControl(), 'top-right');
    return () => { mapRef.current?.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    clients.forEach(c => {
      // `el` is the element MapLibre itself owns and positions (via its own
      // stylesheet's `position: absolute` + a per-frame transform) -- it
      // must NOT get an inline `position` set here, since an inline style
      // silently overrides that stylesheet rule and breaks MapLibre's pixel
      // math on every pan/zoom (markers visibly drift). Anything needing
      // its own positioning context (the "both visit types" badge) goes on
      // a nested wrapper instead, never on `el` directly.
      const el = document.createElement('div');
      const isSelected = selectedIds.has(c.id);
      const color = VISIT_STATUS_META[c.combinedStatus].color;
      const bothRelevant = c.qa.dueDate && c.nurse.dueDate;
      const initials = `${c.first_name?.[0] || ''}${c.last_name?.[0] || ''}`.toUpperCase();

      el.style.cursor = 'pointer';
      const shiftLabel = c.shiftInfo
        ? ` — Caregiver ${c.shiftInfo.when === 'now' ? 'on-site now' : 'visiting today'} (${fmtShiftRange(c.shiftInfo.shift)}${c.shiftInfo.shift.caregiverName ? `, ${c.shiftInfo.shift.caregiverName}` : ''})`
        : '';
      el.title = (bothRelevant ? `${displayName(c)} — QA + Nurse both relevant` : displayName(c)) + shiftLabel;

      const circle = document.createElement('div');
      circle.style.position = 'relative';
      circle.style.width = '24px';
      circle.style.height = '24px';
      circle.style.borderRadius = '50%';
      circle.style.background = color;
      circle.style.border = isSelected ? '2.5px solid #2c4f26' : '1.5px solid white';
      circle.style.boxShadow = '0 1px 4px rgba(0,0,0,0.45)';
      circle.style.display = 'flex';
      circle.style.alignItems = 'center';
      circle.style.justifyContent = 'center';
      circle.style.fontSize = '9px';
      circle.style.color = 'white';
      circle.style.fontWeight = '700';
      circle.textContent = initials;
      el.appendChild(circle);

      if (bothRelevant) {
        const badge = document.createElement('div');
        badge.textContent = '2';
        badge.style.position = 'absolute';
        badge.style.top = '-3px';
        badge.style.right = '-3px';
        badge.style.width = '11px';
        badge.style.height = '11px';
        badge.style.borderRadius = '50%';
        badge.style.background = '#2c4f26';
        badge.style.border = '1.25px solid white';
        badge.style.color = 'white';
        badge.style.fontSize = '7px';
        badge.style.fontWeight = '700';
        badge.style.display = 'flex';
        badge.style.alignItems = 'center';
        badge.style.justifyContent = 'center';
        circle.appendChild(badge);
      }

      // Not a status signal (that's the circle's own color) -- just a nice-
      // to-have nudge that a caregiver is already there or will be later
      // today, in case the visit can be timed to overlap. Bottom-left so it
      // never collides with the "both visit types" badge above.
      if (c.shiftInfo) {
        const shiftBadge = document.createElement('div');
        shiftBadge.style.position = 'absolute';
        shiftBadge.style.bottom = '-3px';
        shiftBadge.style.left = '-3px';
        shiftBadge.style.width = '10px';
        shiftBadge.style.height = '10px';
        shiftBadge.style.borderRadius = '50%';
        shiftBadge.style.border = '1.5px solid #2563eb';
        shiftBadge.style.background = c.shiftInfo.when === 'now' ? '#2563eb' : 'white';
        circle.appendChild(shiftBadge);
      }

      el.addEventListener('click', () => onMarkerClick(c));

      const marker = new MapLibreMarker({ element: el, anchor: 'center' })
        .setLngLat([c.longitude, c.latitude])
        .addTo(mapRef.current);
      markersRef.current.push(marker);
    });
  }, [clients, selectedIds, onMarkerClick, displayName]);

  return (
    <div className="flex-1 bg-white rounded-2xl border border-gray-100 overflow-hidden relative" style={{ minHeight: 560 }}>
      {loading && (
        <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26]" />
        </div>
      )}
      <div ref={mapContainerRef} style={{ width: '100%', height: 560 }} />
    </div>
  );
};

const VisitPlannerView = ({ showToast }) => {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [tasksByClient, setTasksByClient] = useState({});
  const [settings, setSettings] = useState({});
  const [viewingClient, setViewingClient] = useState(null);
  const [detailClient, setDetailClient] = useState(null);
  const [historyClient, setHistoryClient] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [visitTypeFilter, setVisitTypeFilter] = useState('both');
  const [dueBucket, setDueBucket] = useState('all');
  const [showEligibleEarlyOnly, setShowEligibleEarlyOnly] = useState(false);
  const [eligibleWithinDays, setEligibleWithinDays] = useState('');
  const [staffFilter, setStaffFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('');
  const [zipFilter, setZipFilter] = useState('');
  const [clientStatusFilter, setClientStatusFilter] = useState('active');
  const [missingGeocodeClients, setMissingGeocodeClients] = useState([]);
  const [showMissingGeocodeModal, setShowMissingGeocodeModal] = useState(false);
  const [shiftsByClient, setShiftsByClient] = useState({});
  const [requireShiftToday, setRequireShiftToday] = useState(false);
  const [requireActiveShiftNow, setRequireActiveShiftNow] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      // Widened a day on each side of local midnight so an overnight shift
      // (e.g. 9pm-8am, common for home care) that's in progress right now
      // isn't missed just because its start_at falls on "yesterday".
      const localTodayStart = new Date(new Date().setHours(0, 0, 0, 0));
      const queryStart = new Date(localTodayStart.getTime() - 24 * 60 * 60 * 1000);
      const queryEnd = new Date(localTodayStart.getTime() + 2 * 24 * 60 * 60 * 1000);

      const [{ data: clientRows }, { data: settingsRows }, { data: missingRows }, { data: shiftRows }] = await Promise.all([
        supabase.from('clients').select('id, first_name, last_name, co_client_name, address_line1, city, state, postal_code, latitude, longitude, status').not('latitude', 'is', null),
        supabase.from('visit_type_settings').select('*'),
        supabase.from('clients').select('*').eq('status', 'active').is('latitude', null).not('address_line1', 'is', null).neq('address_line1', '').order('last_name'),
        supabase.from('wellsky_visits').select('client_id, caregiver_name, start_at, end_at')
          .eq('status', 'Scheduled').gte('start_at', queryStart.toISOString()).lt('start_at', queryEnd.toISOString()),
      ]);
      setMissingGeocodeClients(missingRows || []);
      const ids = (clientRows || []).map(c => c.id);
      let taskRows = [];
      if (ids.length > 0) {
        const { data } = await supabase.from('client_tasks').select('*').in('client_id', ids);
        taskRows = data || [];
      }
      const grouped = {};
      taskRows.forEach(t => { (grouped[t.client_id] = grouped[t.client_id] || []).push(t); });
      const settingsMap = {};
      (settingsRows || []).forEach(s => { settingsMap[s.visit_type] = s; });

      const shiftsGrouped = {};
      (shiftRows || []).forEach(v => {
        if (!v.client_id) return;
        (shiftsGrouped[v.client_id] = shiftsGrouped[v.client_id] || []).push({
          start: new Date(v.start_at), end: v.end_at ? new Date(v.end_at) : null, caregiverName: v.caregiver_name,
        });
      });

      setClients(clientRows || []);
      setTasksByClient(grouped);
      setSettings(settingsMap);
      setShiftsByClient(shiftsGrouped);
    } catch (err) {
      console.error('Error loading visit planner data:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadData(); }, []);

  // Enrich each client with computed QA/Nurse info once per data load, not
  // per render/filter pass.
  const enriched = React.useMemo(() => {
    const now = new Date();
    const localTodayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const localTodayEnd = new Date(localTodayStart.getTime() + 24 * 60 * 60 * 1000);
    return clients.map(c => {
      const tasksForClient = tasksByClient[c.id] || [];
      const qa = buildVisitInfo(tasksForClient, 'qa_visit', settings);
      const nurse = buildVisitInfo(tasksForClient, 'nurse_reassessment', settings);
      const worst = moreUrgent(qa.status, nurse.status);
      const combine = combineWindow(qa, nurse);
      const shiftInfo = shiftStatusFor(shiftsByClient[c.id], now, localTodayStart, localTodayEnd);
      return { ...c, qa, nurse, combinedStatus: worst, combine, shiftInfo };
    });
  }, [clients, tasksByClient, settings, shiftsByClient]);

  const staffOptions = Array.from(new Set(enriched.flatMap(c => [c.qa.assignedTo, c.nurse.assignedTo]).filter(Boolean))).sort();
  const cityOptions = Array.from(new Set(enriched.map(c => c.city).filter(Boolean))).sort();

  const relevantInfos = (c) => visitTypeFilter === 'both' ? [c.qa, c.nurse] : visitTypeFilter === 'qa_visit' ? [c.qa] : [c.nurse];

  const matchesFilters = (c) => {
    if (clientStatusFilter !== 'all' && c.status !== clientStatusFilter) return false;
    if (cityFilter && (c.city || '').toLowerCase() !== cityFilter.toLowerCase()) return false;
    if (zipFilter && !(c.postal_code || '').startsWith(zipFilter.trim())) return false;
    if (staffFilter !== 'all' && c.qa.assignedTo !== staffFilter && c.nurse.assignedTo !== staffFilter) return false;
    if (requireShiftToday && !c.shiftInfo) return false;
    if (requireActiveShiftNow && c.shiftInfo?.when !== 'now') return false;

    const infos = relevantInfos(c);
    const anyInfoHasDue = infos.some(i => i.dueDate);
    if (!anyInfoHasDue && (dueBucket !== 'all' || showEligibleEarlyOnly || eligibleWithinDays)) return false;

    if (dueBucket !== 'all') {
      const bucketDays = { overdue: 0, due_7: 7, due_14: 14, due_30: 30, due_60: 60 }[dueBucket];
      const matches = infos.some(i => {
        if (i.daysUntilDue == null) return false;
        if (dueBucket === 'overdue') return i.daysUntilDue < 0;
        return i.daysUntilDue >= 0 && i.daysUntilDue <= bucketDays;
      });
      if (!matches) return false;
    }
    if (showEligibleEarlyOnly && !infos.some(i => i.status === 'eligible_early')) return false;
    if (eligibleWithinDays) {
      const x = parseInt(eligibleWithinDays, 10);
      const cfgFor = (i) => settings[i.visitType] || { early_window_days: i.visitType === 'qa_visit' ? 14 : 7 };
      const soon = infos.some(i => i.daysUntilDue != null && (i.daysUntilDue - cfgFor(i).early_window_days) <= x);
      if (!soon) return false;
    }
    return true;
  };

  const visibleClients = enriched.filter(matchesFilters);
  // "of N" should track the status filter, not every geocoded client
  // regardless of status -- otherwise inactive clients (never shown by
  // default) read as if some other filter were hiding them.
  const statusMatchedClients = clients.filter(c => clientStatusFilter === 'all' || c.status === clientStatusFilter);

  const displayName = visitPlannerDisplayName;

  const toggleSelected = (clientId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId); else next.add(clientId);
      return next;
    });
  };

  if (viewingClient) {
    return (
      <ClientDetailView
        client={viewingClient}
        allClients={clients}
        onBack={() => setViewingClient(null)}
        onNavigate={(c) => setViewingClient(c)}
        onUpdate={(updated) => { setViewingClient(updated); loadData(); }}
        showToast={showToast}
        backLabel="Visit Planner"
      />
    );
  }

  const dueBucketOptions = [
    ['all', 'All'], ['overdue', 'Overdue'], ['due_7', 'Next 7 days'], ['due_14', 'Next 14 days'],
    ['due_30', 'Next 30 days'], ['due_60', 'Next 60 days'],
  ];

  return (
    <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-6 md:py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Visit Planner</h1>
          <p className="text-sm text-gray-600">
            {visibleClients.length} of {statusMatchedClients.length} geocoded clients shown
            {missingGeocodeClients.length > 0 && (
              <> · <button onClick={() => setShowMissingGeocodeModal(true)} className="text-amber-600 hover:underline font-medium">
                {missingGeocodeClients.length} client{missingGeocodeClients.length === 1 ? '' : 's'} with an address but no map location yet
              </button></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs flex-wrap">
          {Object.entries(VISIT_STATUS_META).filter(([k]) => k !== 'unscheduled').map(([k, meta]) => (
            <div key={k} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full shrink-0" style={{ background: meta.color }} />
              <span className="text-gray-600">{meta.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5 pl-2 border-l border-gray-200" title="Not required -- just convenient to pair a visit with a caregiver already on-site">
            <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: '#2563eb' }} />
            <span className="text-gray-600">Caregiver on-site now</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full shrink-0 bg-white" style={{ border: '1.5px solid #2563eb' }} />
            <span className="text-gray-600">Caregiver visiting today</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Visit type</label>
          <select value={visitTypeFilter} onChange={e => setVisitTypeFilter(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="both">Both</option>
            <option value="qa_visit">QA Visit</option>
            <option value="nurse_reassessment">Nurse Assessment</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Due date</label>
          <select value={dueBucket} onChange={e => setDueBucket(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            {dueBucketOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
          <input type="checkbox" checked={showEligibleEarlyOnly} onChange={e => setShowEligibleEarlyOnly(e.target.checked)} className="rounded" />
          Eligible early only
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-2" title="Not required -- just convenient to pair a visit with a caregiver already on-site">
          <input type="checkbox" checked={requireShiftToday} onChange={e => setRequireShiftToday(e.target.checked)} className="rounded" />
          Caregiver shift today
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-2" title="Caregiver is on-site with the client right now, not just scheduled later today">
          <input type="checkbox" checked={requireActiveShiftNow} onChange={e => setRequireActiveShiftNow(e.target.checked)} className="rounded" />
          Active shift now
        </label>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Eligible within</label>
          <select value={eligibleWithinDays} onChange={e => setEligibleWithinDays(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">—</option>
            {[7, 14, 21, 30].map(d => <option key={d} value={d}>{d} days</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Assigned staff</label>
          <select value={staffFilter} onChange={e => setStaffFilter(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="all">All</option>
            {staffOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">City</label>
          <select value={cityFilter} onChange={e => setCityFilter(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">All</option>
            {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">ZIP</label>
          <input value={zipFilter} onChange={e => setZipFilter(e.target.value)} placeholder="Any" className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Client status</label>
          <select value={clientStatusFilter} onChange={e => setClientStatusFilter(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <VisitPlannerMap
          clients={visibleClients}
          selectedIds={selectedIds}
          onMarkerClick={setDetailClient}
          loading={loading}
          displayName={displayName}
        />

        {/* Selected visit list */}
        <div className="w-full lg:w-80 shrink-0 bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={16} className="text-[#2c4f26]" />
            <h2 className="font-bold text-gray-900 text-sm">Planned Visit List</h2>
            <span className="text-xs text-gray-400 ml-auto">{selectedIds.size}</span>
          </div>
          {selectedIds.size === 0 ? (
            <p className="text-sm text-gray-400">Click a marker and "Select for visit" to build a list.</p>
          ) : (
            <div className="space-y-2">
              {enriched.filter(c => selectedIds.has(c.id)).map(c => (
                <div key={c.id} className="border border-gray-100 rounded-xl p-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900">{displayName(c)}</p>
                    <button onClick={() => toggleSelected(c.id)} className="text-gray-400 hover:text-red-600 transition shrink-0"><X size={14} /></button>
                  </div>
                  <p className="text-xs text-gray-500">{c.city}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {[c.qa, c.nurse].filter(i => i.dueDate).map(i => (
                      <span key={i.visitType} className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${VISIT_STATUS_META[i.status].badge}`}>
                        {TASK_TYPE_LABELS[i.visitType]} · {fmtShortDate(i.dueDate)}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {detailClient && (
        <VisitDetailModal
          client={detailClient}
          isSelected={selectedIds.has(detailClient.id)}
          onClose={() => setDetailClient(null)}
          onToggleSelect={() => toggleSelected(detailClient.id)}
          onViewClient={() => { setViewingClient(detailClient); setDetailClient(null); }}
          onViewHistory={() => { setHistoryClient(detailClient); setDetailClient(null); }}
        />
      )}

      {historyClient && (
        <VisitHistoryModal
          client={historyClient}
          tasks={tasksByClient[historyClient.id] || []}
          onClose={() => setHistoryClient(null)}
        />
      )}

      {showMissingGeocodeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowMissingGeocodeModal(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-gray-900">No Map Location Yet</h2>
              <button onClick={() => setShowMissingGeocodeModal(false)} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
            </div>
            <p className="px-6 pt-4 text-xs text-gray-500">
              These active clients have an address but haven't been geocoded yet, so they don't show up on the map. This usually resolves itself shortly after the address is saved -- if one has sat here a while, double-check the address is complete and valid.
            </p>
            <div className="p-4 space-y-1.5">
              {missingGeocodeClients.map(c => (
                <button
                  key={c.id}
                  onClick={() => { setViewingClient(c); setShowMissingGeocodeModal(false); }}
                  className="w-full text-left p-3 rounded-xl hover:bg-gray-50 transition flex items-center justify-between gap-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{visitPlannerDisplayName(c)}</p>
                    <p className="text-xs text-gray-500">{[c.address_line1, c.city, c.state, c.postal_code].filter(Boolean).join(', ') || 'No address on file'}</p>
                  </div>
                  <ArrowRight size={14} className="text-gray-300 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const VisitDetailModal = ({ client, isSelected, onClose, onToggleSelect, onViewClient, onViewHistory }) => {
  const displayName = `${client.first_name} ${client.last_name}${client.co_client_name ? ` & ${client.co_client_name}` : ''}`;
  const address = [client.address_line1, client.city, client.state, client.postal_code].filter(Boolean).join(', ');

  const block = (info, label) => (
    <div className="border border-gray-100 rounded-xl p-3">
      <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{label}</p>
      {info.dueDate ? (
        <div className="space-y-1 text-sm">
          <p className="text-gray-700">Last completed: <span className="font-medium">{info.lastCompleted ? fmtShortDate(info.lastCompleted.slice(0, 10)) : 'No record'}</span></p>
          <p className="text-gray-700">Next due: <span className="font-medium">{fmtShortDate(info.dueDate)}</span></p>
          <p className="text-gray-700">Eligible early: <span className="font-medium">{fmtShortDate(info.eligibleEarlyDate)}</span></p>
          {info.assignedTo && <p className="text-gray-700">Assigned: <span className="font-medium">{info.assignedTo}</span></p>}
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold mt-1 ${VISIT_STATUS_META[info.status].badge}`}>
            {VISIT_STATUS_META[info.status].label}
          </span>
        </div>
      ) : (
        <p className="text-sm text-gray-400">No visit scheduled</p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{displayName}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{address || 'No address on file'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition shrink-0"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-3">
          {client.shiftInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: client.shiftInfo.when === 'now' ? '#2563eb' : 'white', border: '1.5px solid #2563eb' }} />
              <p className="text-blue-900">
                Caregiver {client.shiftInfo.when === 'now' ? 'on-site now' : 'visiting today'} ({fmtShiftRange(client.shiftInfo.shift)}
                {client.shiftInfo.shift.caregiverName ? ` — ${client.shiftInfo.shift.caregiverName}` : ''})
              </p>
            </div>
          )}
          {block(client.qa, 'QA Visit')}
          {block(client.nurse, 'Nurse Assessment')}
          {client.combine && (
            <div className="bg-[#f0f5f0] border border-[#c5d8c3] rounded-xl p-3 text-sm">
              <p className="font-bold text-[#2c4f26]">Combine Nurse + QA</p>
              <p className="text-gray-700 mt-0.5">Both fall within {fmtShortDate(client.combine.start)} – {fmtShortDate(client.combine.end)}</p>
            </div>
          )}
        </div>
        <div className="p-6 border-t border-gray-100 flex flex-wrap gap-2 justify-end">
          <button onClick={onViewHistory} className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition">View History</button>
          <button onClick={onViewClient} className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition">View Client</button>
          <button onClick={onToggleSelect} className={`px-4 py-2 text-sm font-semibold rounded-xl transition ${isSelected ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-[#2c4f26] text-white hover:bg-[#1f3a1c]'}`}>
            {isSelected ? 'Deselect' : 'Select for Visit'}
          </button>
        </div>
      </div>
    </div>
  );
};

const VisitHistoryModal = ({ client, tasks, onClose }) => {
  const displayName = `${client.first_name} ${client.last_name}${client.co_client_name ? ` & ${client.co_client_name}` : ''}`;
  const completed = tasks.filter(t => t.status === 'completed').sort((a, b) => (b.completed_at || '').localeCompare(a.completed_at || ''));
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">{displayName} — Visit History</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition"><X size={20} /></button>
        </div>
        <div className="p-6">
          {completed.length === 0 ? (
            <p className="text-sm text-gray-400">No completed visits on record yet.</p>
          ) : (
            <div className="space-y-2">
              {completed.map(t => (
                <div key={t.id} className="flex items-center justify-between border-b border-gray-50 pb-2">
                  <span className="text-sm text-gray-700">{TASK_TYPE_LABELS[t.task_type] || t.task_type}</span>
                  <span className="text-sm text-gray-500">{t.completed_at ? new Date(t.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Live Call Popup ───────────────────────────────────────────────────────────

const LiveCallPopup = ({ activeCalls, deviceMappings }) => {
  const [dismissed, setDismissed] = useState(new Set());
  const dismiss = (id) => setDismissed(s => new Set([...s, id]));

  const calls = Object.values(activeCalls).filter(c =>
    (c.status === 'ringing' || c.status === 'answered') && !dismissed.has(c.id)
  );

  if (calls.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[200] flex flex-col gap-3 w-full max-w-xs pointer-events-none">
      {calls.map(call => {
        const device = deviceMappings[call.answered_phone_ip] || deviceMappings[call.phone_ip];
        const ringing = call.status === 'ringing';
        return (
          <div key={call.id} className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden pointer-events-auto">
            <div className={`px-4 py-3 flex items-center justify-between ${ringing ? 'bg-amber-50 border-b border-amber-100' : 'bg-[#f0f5f0] border-b border-[#c5d8c3]'}`}>
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${ringing ? 'bg-amber-500 animate-pulse' : 'bg-[#2c4f26]'}`} />
                <span className={`text-sm font-bold ${ringing ? 'text-amber-800' : 'text-[#2c4f26]'}`}>
                  {ringing ? 'Incoming Call' : 'Call Answered'}
                </span>
              </div>
              <button onClick={() => dismiss(call.id)} className="text-gray-400 hover:text-gray-600 transition p-0.5"><X size={15} /></button>
            </div>
            <div className="px-4 py-3 space-y-2">
              <div>
                <p className="text-base font-bold text-gray-900 leading-tight">{call.caller_name || 'Unknown Caller'}</p>
                <p className="text-sm font-semibold text-[#2c4f26]">{formatPhoneNumber(call.caller_number)}</p>
              </div>
              <div className="text-xs space-y-0.5">
                {device ? (
                  <>
                    <p className="font-semibold text-gray-700">{device.phone_name}</p>
                    {device.employee_name && <p className="text-gray-600">{device.employee_name}</p>}
                  </>
                ) : (
                  <p className="text-gray-500">Unmapped Phone</p>
                )}
                <p className="text-gray-400">{call.answered_phone_ip || call.phone_ip}</p>
                <p className="text-gray-400">ID: {call.call_id}</p>
              </div>
            </div>
            <div className="px-4 pb-3 flex gap-2">
              <button className="flex-1 py-1.5 text-xs font-semibold bg-[#2c4f26] text-white rounded-lg hover:bg-[#1f3a1c] transition">
                Search Portal
              </button>
              <button onClick={() => dismiss(call.id)} className="flex-1 py-1.5 text-xs font-semibold bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">
                Dismiss
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Phone Screen Tab ──────────────────────────────────────────────────────────

const PhoneScreenTab = ({
  activeCalls, deviceMappings, realtimeConnected, lastPhoneEventAt,
  profile, demoCallId, onSimulateIncoming, onSimulateAnswered, onClearDemo,
}) => {
  const [recentCalls, setRecentCalls] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [endingCallId, setEndingCallId] = useState(null);
  const [, setTick] = useState(0);
  const isOwner = profile?.role === 'owner';

  // Re-render every 30 s so the "X min ago" label stays current
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  const fmtAgo = (date) => {
    if (!date) return null;
    const secs = Math.floor((Date.now() - date.getTime()) / 1000);
    if (secs < 10)  return 'just now';
    if (secs < 60)  return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    return `${Math.floor(secs / 3600)}h ago`;
  };

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await supabase
        .from('phone_calls').select('*')
        .order('received_at', { ascending: false })
        .limit(25);
      setRecentCalls(data || []);
    } catch (err) {
      console.error('Error loading call history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const markCallEnded = async (id) => {
    setEndingCallId(id);
    try {
      await supabase.from('phone_calls').update({ status: 'ended', ended_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq('id', id);
      loadHistory();
    } catch (err) {
      console.error('Failed to mark call ended:', err);
    } finally {
      setEndingCallId(null);
    }
  };

  const activeCurrent = Object.values(activeCalls).filter(c => c.status === 'ringing' || c.status === 'answered');

  const fmtTime = (ts) => ts ? new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—';

  const statusBadge = (status) => {
    const styles = { ringing: 'bg-amber-100 text-amber-700', answered: 'bg-green-100 text-green-700', ended: 'bg-gray-100 text-gray-600', missed: 'bg-red-100 text-red-700' };
    const labels = { ringing: '● Ringing', answered: '● Answered', ended: 'Ended', missed: 'Missed' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`}>{labels[status] || status}</span>;
  };

  // For ring-group calls (rang on more than one phone), show who else it rang on.
  const ringingLabel = (call) => {
    const ips = call.ringing_phone_ips || [];
    if (ips.length <= 1) return null;
    const names = ips.map(ip => deviceMappings[ip]?.employee_name || deviceMappings[ip]?.phone_name || ip);
    return `Rang on: ${names.join(', ')}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Phone Screen</h1>
          <p className="text-sm text-gray-600">Live incoming call identification and history</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full shrink-0 ${realtimeConnected ? 'bg-green-500' : 'bg-gray-300 animate-pulse'}`} />
            <span>{realtimeConnected ? 'Realtime connected' : 'Connecting…'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full shrink-0 ${lastPhoneEventAt ? 'bg-blue-400' : 'bg-gray-200'}`} />
            <span>{lastPhoneEventAt ? `Last event: ${fmtAgo(lastPhoneEventAt)}` : 'No events yet'}</span>
          </div>
        </div>
      </div>

      {/* Active calls */}
      <div className="mb-6">
        <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Active Calls</h2>
        {activeCurrent.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
            <p className="text-sm text-gray-400">No active calls — waiting for next event</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {activeCurrent.map(call => {
              const device = deviceMappings[call.answered_phone_ip] || deviceMappings[call.phone_ip];
              const ringing = call.status === 'ringing';
              return (
                <div key={call.id} className={`bg-white rounded-2xl border p-5 ${ringing ? 'border-amber-200' : 'border-[#c5d8c3]'}`}>
                  <div className="flex items-start justify-between mb-3">
                    {statusBadge(call.status)}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{fmtTime(call.received_at)}</span>
                      <button
                        onClick={() => markCallEnded(call.id)}
                        disabled={endingCallId === call.id}
                        title="Mark as ended"
                        className="text-xs text-gray-400 hover:text-red-500 transition disabled:opacity-40"
                      >
                        {endingCallId === call.id ? <span className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin inline-block" /> : <X size={14} />}
                      </button>
                    </div>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{call.caller_name || 'Unknown Caller'}</p>
                  <p className="text-[#2c4f26] font-semibold mb-3">{formatPhoneNumber(call.caller_number)}</p>
                  <div className="text-xs text-gray-500 space-y-0.5 border-t border-gray-100 pt-3">
                    <p className="font-semibold text-gray-700">{device ? device.phone_name : 'Unmapped Phone'}</p>
                    {device?.employee_name && <p>{device.employee_name}</p>}
                    <p className="text-gray-400">{call.answered_phone_ip || call.phone_ip}</p>
                    <p className="text-gray-400">ID: {call.call_id}</p>
                    {ringingLabel(call) && <p className="text-gray-400 italic">{ringingLabel(call)}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent call history */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Recent Calls</h2>
          <button onClick={loadHistory} className="text-xs text-[#2c4f26] hover:underline font-medium">Refresh</button>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {loadingHistory ? (
            <div className="p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" /></div>
          ) : recentCalls.length === 0 ? (
            <div className="p-12 text-center text-gray-400 text-sm">No call history yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    {['Time', 'Caller', 'Number', 'Status', 'Answered By'].map(h => (
                      <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {recentCalls.map(call => {
                    const device = deviceMappings[call.answered_phone_ip] || deviceMappings[call.phone_ip];
                    return (
                      <tr key={call.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{fmtTime(call.received_at)}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {call.caller_name || <span className="text-gray-400 italic">Unknown</span>}
                          {ringingLabel(call) && <div className="text-xs text-gray-400 font-normal italic">{ringingLabel(call)}</div>}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-[#2c4f26]">{formatPhoneNumber(call.caller_number)}</td>
                        <td className="px-4 py-3">{statusBadge(call.status)}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {device ? (device.employee_name || device.phone_name) : <span className="text-gray-400">{call.answered_phone_ip || '—'}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Owner-only simulation controls */}
      {isOwner && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-0.5">Test Controls</h2>
          <p className="text-xs text-gray-500 mb-4">Simulates events locally in UI only — does not write to the database or require the Flask server</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={onSimulateIncoming} className="px-4 py-2 text-sm font-semibold bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition">
              Simulate Incoming
            </button>
            <button onClick={onSimulateAnswered} disabled={!demoCallId} className="px-4 py-2 text-sm font-semibold bg-[#2c4f26] text-white rounded-xl hover:bg-[#1f3a1c] transition disabled:opacity-40 disabled:cursor-not-allowed">
              Simulate Answered
            </button>
            {demoCallId && (
              <button onClick={onClearDemo} className="px-4 py-2 text-sm font-semibold bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition">
                Clear Demo
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CAREPRO_PAGE_SIZES = [100, 250, 500, 'all'];

// Archived: CarePro tab hidden from navigation (user no longer wants this
// manually-imported caregiver directory used -- the WellSky-synced
// "Care Pros" tab is the live source of truth now; unrelated despite the
// similar name). Left in place, unreferenced, rather than deleted, in case
// the data or tab needs restoring later.
// eslint-disable-next-line no-unused-vars
const CareProTab = ({ showToast }) => {
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [statusCounts, setStatusCounts] = useState({ active: 0, inactive: 0 });
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);

  // Debounce the search box so we don't fire a query on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Jump back to page 1 whenever the search, status filter, or page size changes.
  useEffect(() => { setPage(0); }, [debouncedSearch, statusFilter, pageSize]);

  useEffect(() => { loadCaregivers(); }, [debouncedSearch, statusFilter, pageSize, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadStatusCounts(); }, []);

  const loadStatusCounts = async () => {
    try {
      const [rActive, rInactive] = await Promise.all([
        supabase.from('caregivers').select('*', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('caregivers').select('*', { count: 'exact', head: true }).eq('status', 'inactive'),
      ]);
      setStatusCounts({ active: rActive.count || 0, inactive: rInactive.count || 0 });
    } catch (err) {
      console.error('Error loading caregiver status counts:', err);
    }
  };

  const buildQuery = () => {
    let q = supabase.from('caregivers').select('*', { count: 'exact' }).order('full_name');
    if (statusFilter !== 'all') {
      q = q.eq('status', statusFilter);
    }
    if (debouncedSearch) {
      const term = debouncedSearch.replace(/%|_/g, ' ').trim();
      if (term) {
        const orParts = [
          orIlike('full_name', term),
          orIlike('email', term),
          orIlike('city', term),
          orIlike('mobile_phone', term),
          orIlike('home_phone', term),
          orIlike('work_phone', term),
        ];
        // Digits-only match against generated columns so a fragment like
        // "038507" matches "(703) 850-7642" even though those digits aren't
        // contiguous in the punctuated value.
        const digits = term.replace(/\D/g, '');
        if (digits.length >= 3) {
          orParts.push(orIlike('mobile_phone_digits', digits));
          orParts.push(orIlike('home_phone_digits', digits));
          orParts.push(orIlike('work_phone_digits', digits));
        }
        q = q.or(orParts.join(','));
      }
    }
    return q;
  };

  const loadCaregivers = async () => {
    setLoading(true);
    try {
      if (pageSize === 'all') {
        // "All" still respects the search filter -- page through in chunks
        // of 1000 (PostgREST's per-request cap) until we've got everything.
        const chunk = 1000;
        let all = [];
        let from = 0;
        let total = 0;
        for (;;) {
          const { data, error, count } = await buildQuery().range(from, from + chunk - 1);
          if (error) throw error;
          if (typeof count === 'number') total = count;
          all = all.concat(data || []);
          if (!data || data.length < chunk) break;
          from += chunk;
        }
        setCaregivers(all);
        setTotalCount(total);
      } else {
        const from = page * pageSize;
        const to = from + pageSize - 1;
        const { data, error, count } = await buildQuery().range(from, to);
        if (error) throw error;
        setCaregivers(data || []);
        setTotalCount(count || 0);
      }
    } catch (err) {
      console.error('Error loading caregivers:', err);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (c) =>
    (c.full_name || '').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

  const toggleCaregiverStatus = async (caregiver) => {
    const nextStatus = caregiver.status === 'inactive' ? 'active' : 'inactive';
    setStatusSaving(true);
    try {
      const { error } = await supabase.from('caregivers').update({ status: nextStatus }).eq('id', caregiver.id);
      if (error) throw error;
      showToast && showToast(`Caregiver marked ${nextStatus}`);
      setSelected(s => (s ? { ...s, status: nextStatus } : s));
      loadCaregivers();
      loadStatusCounts();
    } catch (err) {
      showToast && showToast(err.message || 'Failed to update status', 'error');
    } finally {
      setStatusSaving(false);
    }
  };

  const rangeStart = totalCount === 0 ? 0 : page * (pageSize === 'all' ? totalCount : pageSize) + 1;
  const rangeEnd = pageSize === 'all' ? totalCount : Math.min((page + 1) * pageSize, totalCount);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">CarePro</h1>
          <p className="text-sm text-gray-600">{statusCounts.active} active · {statusCounts.inactive} inactive · reference directory</p>
        </div>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
              placeholder="Search by name, email, phone, or city…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 shrink-0">
            {[['active', 'Active'], ['inactive', 'Inactive'], ['all', 'All']].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setStatusFilter(val)}
                className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${statusFilter === val ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500 hidden sm:inline">Show</span>
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
              {CAREPRO_PAGE_SIZES.map(size => (
                <button
                  key={size}
                  onClick={() => setPageSize(size)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition ${pageSize === size ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  {size === 'all' ? 'All' : size}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto" />
          </div>
        ) : caregivers.length === 0 ? (
          <div className="p-16 text-center text-gray-500">
            <Heart size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">
              {searchTerm ? 'No caregivers match your search' : statusFilter !== 'all' ? `No ${statusFilter} caregivers` : 'No caregivers on file'}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Caregiver</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Location</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Contact</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {caregivers.map(c => (
                    <tr key={c.id} onClick={() => setSelected(c)} className="hover:bg-gray-50 transition cursor-pointer">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-[#2c4f26] rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0">
                            {getInitials(c)}
                          </div>
                          <p className="font-semibold text-gray-900 text-sm">{c.full_name}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-600">
                        {c.city ? `${c.city}${c.state ? ', ' + c.state : ''}` : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-0.5 text-xs text-gray-700">
                          {c.mobile_phone && <p>M: {c.mobile_phone}</p>}
                          {c.home_phone && <p>H: {c.home_phone}</p>}
                          {c.email && <p className="text-gray-500">{c.email}</p>}
                          {!c.mobile_phone && !c.home_phone && !c.email && <span className="text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {c.status || 'active'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {caregivers.map(c => (
                <div key={c.id} onClick={() => setSelected(c)} className="p-4 hover:bg-gray-50 transition cursor-pointer">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#2c4f26] rounded-full flex items-center justify-center text-white font-bold shrink-0">
                      {getInitials(c)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm">{c.full_name}</p>
                      {c.city && <p className="text-xs text-gray-500 mt-0.5">{c.city}{c.state ? `, ${c.state}` : ''}</p>}
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-gray-600">
                        {c.mobile_phone && <p>M: {c.mobile_phone}</p>}
                        {c.home_phone && <p>H: {c.home_phone}</p>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination footer */}
        {!loading && pageSize !== 'all' && totalCount > 0 && (
          <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              Showing {rangeStart}–{rangeEnd} of {totalCount}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page + 1 >= totalPages}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-[150] flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{selected.full_name}</h2>
                <span className={`inline-flex items-center px-2.5 py-0.5 mt-1.5 rounded-full text-xs font-semibold ${selected.status === 'inactive' ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700'}`}>
                  {selected.status === 'inactive' ? 'Inactive' : 'Active'}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => toggleCaregiverStatus(selected)}
                  disabled={statusSaving}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition disabled:opacity-50 ${selected.status === 'inactive' ? 'text-green-700 bg-green-50 hover:bg-green-100' : 'text-red-700 bg-red-50 hover:bg-red-100'}`}
                >
                  {statusSaving ? 'Saving…' : selected.status === 'inactive' ? 'Activate' : 'Deactivate'}
                </button>
                <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 transition"><X size={20} /></button>
              </div>
            </div>
            <div className="p-6 space-y-4 text-sm">
              {(selected.address_line1 || selected.city) && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Address</p>
                  <p className="text-gray-800">
                    {selected.address_line1}{selected.address_line1 && <br />}
                    {selected.address_line2 && <>{selected.address_line2}<br /></>}
                    {[selected.city, selected.state, selected.postal_code].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}
              {(selected.mobile_phone || selected.home_phone || selected.work_phone || selected.email) && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Contact</p>
                  <div className="space-y-0.5 text-gray-800">
                    {selected.mobile_phone && <p>Mobile: {selected.mobile_phone}</p>}
                    {selected.home_phone && <p>Home: {selected.home_phone}</p>}
                    {selected.work_phone && <p>Work: {selected.work_phone}</p>}
                    {selected.email && <p>{selected.email}</p>}
                  </div>
                </div>
              )}
              {selected.employment_type && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Employment Type</p>
                  <p className="text-gray-800">{selected.employment_type}</p>
                </div>
              )}
              {selected.notes && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Notes</p>
                  <p className="text-gray-800 whitespace-pre-wrap">{selected.notes}</p>
                </div>
              )}
              {!selected.address_line1 && !selected.city && !selected.mobile_phone && !selected.home_phone && !selected.work_phone && !selected.email && !selected.notes && (
                <p className="text-gray-400 text-sm">No additional details on file for this caregiver.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Care Consultation ────────────────────────────────────────────────────────

// Renders a name as a cursive-script signature image, same as SignaturePad's
// own "Type Name" mode below — pulled out standalone so an auto-fill flow
// (see CareProApplicationReview's AUTO_STAFF_FILL) can generate the exact
// same signature image a staff member would have gotten by typing it in
// themselves, without needing the interactive modal.
const generateTypedSignatureDataUrl = (name) => {
  const canvas = document.createElement('canvas');
  canvas.width = 600;
  canvas.height = 150;
  const ctx = canvas.getContext('2d');
  const fontStack = '"Brush Script MT", "Segoe Script", "Apple Chancery", "URW Chancery L", cursive';
  let fontSize = 58;
  ctx.font = `italic ${fontSize}px ${fontStack}`;
  while (ctx.measureText(name).width > 560 && fontSize > 22) {
    fontSize -= 2;
    ctx.font = `italic ${fontSize}px ${fontStack}`;
  }
  ctx.fillStyle = '#1a1a1a';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, 16, 80);
  return canvas.toDataURL('image/png');
};

// Signature pad component using canvas — supports draw and type modes
const SignaturePad = ({ onSave }) => {
  const [mode, setMode] = React.useState('draw');
  const [typedName, setTypedName] = React.useState('');

  const canvasRef = React.useRef(null);
  const isDrawing = React.useRef(false);
  const hasSignature = React.useRef(false);
  const lastPos = React.useRef(null);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const pos = getPos(e, canvas);
    lastPos.current = pos;
    isDrawing.current = true;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1.5, 0, Math.PI * 2);
    ctx.fillStyle = '#1a1a1a';
    ctx.fill();
  };

  const draw = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
    hasSignature.current = true;
  };

  const endDraw = () => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    if (hasSignature.current) {
      onSave(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clearDraw = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasSignature.current = false;
    onSave(null);
  };

  const generateTypedSig = (name) => {
    if (!name.trim()) { onSave(null); return; }
    onSave(generateTypedSignatureDataUrl(name));
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setTypedName('');
    onSave(null);
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      hasSignature.current = false;
    }
  };

  return (
    <div>
      {/* Mode toggle */}
      <div className="flex gap-1 mb-2">
        <button
          type="button"
          onClick={() => switchMode('draw')}
          className={`text-xs px-3 py-1 rounded-full border transition ${mode === 'draw' ? 'bg-[#2c4f26] text-white border-[#2c4f26]' : 'text-gray-600 border-gray-300 hover:border-gray-400'}`}
        >
          Draw
        </button>
        <button
          type="button"
          onClick={() => switchMode('type')}
          className={`text-xs px-3 py-1 rounded-full border transition ${mode === 'type' ? 'bg-[#2c4f26] text-white border-[#2c4f26]' : 'text-gray-600 border-gray-300 hover:border-gray-400'}`}
        >
          Type Name
        </button>
      </div>

      {mode === 'draw' ? (
        <div>
          <div className="border-2 border-gray-300 rounded-lg bg-white relative" style={{ touchAction: 'none' }}>
            <canvas
              ref={canvasRef}
              width={600}
              height={150}
              className="w-full rounded-lg cursor-crosshair"
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={endDraw}
            />
            <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center pointer-events-none">
              <div className="w-3/4 border-t border-dashed border-gray-300" />
            </div>
          </div>
          <div className="flex justify-between mt-2">
            <p className="text-xs text-gray-600">Sign above</p>
            <button type="button" onClick={clearDraw} className="text-xs text-red-500 hover:text-red-700">Clear</button>
          </div>
        </div>
      ) : (
        <div>
          <div className="border-2 border-gray-300 rounded-lg bg-white px-4 pt-3 pb-2 relative">
            <input
              className="w-full bg-transparent focus:outline-none text-gray-900"
              style={{ fontFamily: '"Brush Script MT", "Segoe Script", "Apple Chancery", cursive', fontSize: '40px', fontStyle: 'italic', lineHeight: '1.3' }}
              value={typedName}
              onChange={e => setTypedName(e.target.value)}
              onBlur={e => generateTypedSig(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { generateTypedSig(typedName); e.target.blur(); } }}
              placeholder="Type full name"
            />
            <div className="border-t border-dashed border-gray-300 mt-1" />
          </div>
          <p className="text-xs text-gray-600 mt-1.5">Your typed name will appear as a signature</p>
        </div>
      )}
    </div>
  );
};

// ─── Manassas Document Consultation Wizard (DocuSign-style, real franchise PDFs) ──

const MANASSAS_OFFICE_CODE = '655';

// The 3 documents a "Send 3 Priority Forms" remote link asks the client to
// sign in a time crunch, instead of the full 10 — kept in sync with the
// same constant in consultation-client-submit's edge function.
const PRIORITY_DOCUMENT_KEYS = ['service_agreement', 'ach_payment_form', 'late_payment_authorization_agreement'];

// pdf.js document rendering — lets the signer see and fill the real PDF instead
// of a generic form, since these are filled out together with the client.
// Loaded lazily (not at module top-level) so that a browser incompatible with
// pdf.js only breaks the Care Consultation feature, not the entire app on load.
let pdfjsLibPromise = null;
const getPdfjsLib = () => {
  if (!pdfjsLibPromise) {
    // "legacy" build — more conservative JS output, aimed at browsers
    // (notably older Safari/iOS) that don't support every bleeding-edge
    // feature the default build assumes.
    pdfjsLibPromise = import('pdfjs-dist/legacy/build/pdf.mjs').then(mod => {
      mod.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
      return mod;
    });
  }
  return pdfjsLibPromise;
};
const pdfDocCache = new Map();
const loadPdfDocument = async (url) => {
  if (pdfDocCache.has(url)) return pdfDocCache.get(url);
  const pdfjsLib = await getPdfjsLib();
  const promise = pdfjsLib.getDocument({ url }).promise;
  pdfDocCache.set(url, promise);
  return promise;
};

// iOS Safari auto-zooms the whole page in on focus for any text input whose
// *computed* font-size is under 16px — harmless on a normally-sized page,
// but our mobile-shrunk fields can go well below that, so tapping one (or
// hitting "Next field") triggered a jarring zoom-and-jump. Rendering the
// input at a real 16px font/box and visually scaling it back down with a
// CSS transform (anchored top-left, so left/top stay put) satisfies Safari's
// check while still rendering at the small size we actually want.
const ZOOM_SAFE_FONT = 16;
const zoomGuardBox = (fontSize, width, height) => {
  if (fontSize >= ZOOM_SAFE_FONT) return { fontSize, width, height, transform: undefined };
  const factor = fontSize / ZOOM_SAFE_FONT;
  return {
    fontSize: ZOOM_SAFE_FONT,
    width: width / factor,
    height: height / factor,
    transform: `scale(${factor})`,
  };
};

const PdfTextOverlayInput = ({ field, value, onChange, scale, onEnterKey, readOnly, showRequiredErrors }) => {
  const isDate = field.type === 'date';
  // scale >= 1 covers every normal desktop width (the container/page math
  // never produces a scale below ~1 on a real monitor) — floors below are
  // the exact original desktop constants in that range, so desktop output
  // is byte-for-byte unchanged. Below 1 (a shrunk mobile page) the floor
  // relaxes so the box keeps shrinking with the page instead of sitting at
  // a fixed pixel size that no longer fits its own PDF row.
  const baseWidth = (field.maxWidth || 140) * scale;
  const width = isDate ? Math.max(scale >= 1 ? 128 : 70, baseWidth) : Math.max(scale >= 1 ? 30 : 18, baseWidth);
  // A field placed with the picker tool carries an explicit height (box
  // bottom = yTop, matching the tool's own convention) — fields never
  // touched by the tool keep the old size-derived guess so they don't shift.
  const hasExplicitHeight = field.height !== undefined;
  const fontSize = Math.max(scale >= 1 ? 10 : 6, (field.size || 10) * scale);
  const height = Math.max(scale >= 1 ? 16 : fontSize + 4, (hasExplicitHeight ? field.height : (field.size || 10) * 1.7) * scale);
  const top = hasExplicitHeight
    ? (field.yTop - field.height) * scale
    : (field.yTop - (field.size || 10) * 1.15) * scale;
  const filled = !!String(value || '').trim();
  if (readOnly && !filled) return null;
  return (
    <input
      type={isDate ? 'date' : 'text'}
      value={value || ''}
      onChange={readOnly ? undefined : e => onChange(e.target.value)}
      readOnly={readOnly}
      placeholder={readOnly ? undefined : field.placeholder}
      data-field-key={field.key}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onEnterKey?.(field.key);
        }
      }}
      style={{
        position: 'absolute',
        left: field.x * scale,
        top,
        transformOrigin: 'top left',
        ...zoomGuardBox(fontSize, width, height),
      }}
      className={`px-1 border rounded-[3px] outline-none text-gray-900 leading-none ${readOnly ? 'border-gray-200 bg-gray-50 cursor-default' : 'focus:bg-white focus:border-[#2c4f26] focus:ring-1 focus:ring-[#2c4f26]'} ${filled ? 'border-green-400/80 bg-green-50' : readOnly ? '' : field.required && showRequiredErrors ? 'border-red-400 bg-red-50' : 'border-amber-400/80 bg-amber-100/50'}`}
    />
  );
};

// Hourly options only (12 AM–11 PM) — availability is tracked to the hour,
// not the minute, so a plain dropdown beats a full time picker here.
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => {
  const period = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:00 ${period}`;
});

const PdfTimeSelectOverlay = ({ field, value, onChange, scale, readOnly, showRequiredErrors }) => {
  const baseWidth = (field.maxWidth || 110) * scale;
  const width = Math.max(scale >= 1 ? 60 : 40, baseWidth);
  // See PdfTextOverlayInput — same scale >= 1 desktop-preserving floor.
  const hasExplicitHeight = field.height !== undefined;
  const fontSize = Math.max(scale >= 1 ? 10 : 6, (field.size || 10) * scale);
  const height = Math.max(scale >= 1 ? 16 : fontSize + 4, (hasExplicitHeight ? field.height : (field.size || 10) * 1.7) * scale);
  const top = hasExplicitHeight
    ? (field.yTop - field.height) * scale
    : (field.yTop - (field.size || 10) * 1.15) * scale;
  const filled = !!value;
  if (readOnly && !filled) return null;
  return (
    <select
      value={value || ''}
      onChange={readOnly ? undefined : e => onChange(e.target.value)}
      disabled={readOnly}
      data-field-key={field.key}
      style={{
        position: 'absolute',
        left: field.x * scale,
        top,
        transformOrigin: 'top left',
        ...zoomGuardBox(fontSize, width, height),
      }}
      className={`px-1 border rounded-[3px] outline-none text-gray-900 leading-none ${readOnly ? 'border-gray-200 bg-gray-50 cursor-default' : 'focus:bg-white focus:border-[#2c4f26] focus:ring-1 focus:ring-[#2c4f26] cursor-pointer'} ${filled ? 'border-green-400/80 bg-green-50' : readOnly ? '' : field.required && showRequiredErrors ? 'border-red-400 bg-red-50' : 'border-amber-400/80 bg-amber-100/50'}`}
    >
      <option value="">—</option>
      {HOUR_OPTIONS.map(h => <option key={h} value={h}>{h}</option>)}
    </select>
  );
};

// Composite date fields (e.g. "the ___ day of ______, 20__") print as one
// flowing phrase, which a native date input can't preview inline without
// overlapping the surrounding printed text — so these get a click-to-edit
// chip showing the formatted phrase instead.
const PdfDateSlotPiece = ({ slot, text, scale }) => (
  <span
    style={{
      position: 'absolute',
      left: slot.x * scale,
      top: (slot.yTop - 9 * 1.15) * scale,
      maxWidth: slot.maxWidth * scale,
      fontSize: Math.max(9, 9 * scale * 0.85),
    }}
    className="px-0.5 rounded-[3px] border border-green-400/70 bg-green-50 text-gray-900 truncate whitespace-nowrap overflow-hidden pointer-events-none"
  >
    {text}
  </span>
);

const PdfDateChipOverlay = ({ field, value, onChange, scale, readOnly }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const parts = field.daySlot ? formatDateParts(value) : null;
  if (readOnly && !value) return null;
  const clickAreaStyle = field.daySlot
    ? {
        position: 'absolute',
        left: field.daySlot.x * scale,
        top: (field.daySlot.yTop - 12) * scale,
        width: (field.yearSlot.x + field.yearSlot.maxWidth - field.daySlot.x) * scale,
        height: 16 * scale,
      }
    : {
        position: 'absolute',
        left: field.x * scale,
        top: (field.yTop - (field.size || 9) * 1.15) * scale,
        width: (field.maxWidth || 165) * scale,
        height: 16 * scale,
      };
  return (
    <>
      {!readOnly && <button type="button" onClick={() => setModalOpen(true)} style={clickAreaStyle} className="block" />}
      {field.daySlot ? (
        parts ? (
          <>
            <PdfDateSlotPiece slot={field.daySlot} text={parts.day} scale={scale} />
            <PdfDateSlotPiece slot={field.monthSlot} text={parts.month} scale={scale} />
            <PdfDateSlotPiece slot={field.yearSlot} text={parts.year} scale={scale} />
          </>
        ) : (
          <span
            style={{
              position: 'absolute',
              left: field.daySlot.x * scale,
              top: (field.daySlot.yTop - 10.35) * scale,
              fontSize: Math.max(9, 9 * scale * 0.85),
            }}
            className="px-1 rounded-[3px] text-[#2c4f26] font-medium border border-amber-400/80 bg-amber-100/50 whitespace-nowrap pointer-events-none"
          >
            Click to set date
          </span>
        )
      ) : (
        <span
          style={{ ...clickAreaStyle, height: undefined, fontSize: Math.max(9, (field.size || 9) * scale * 0.85) }}
          className={`px-1 rounded-[3px] border truncate whitespace-nowrap overflow-hidden pointer-events-none ${value ? 'text-gray-900 border-green-400/80 bg-green-50' : 'text-[#2c4f26] font-medium border-amber-400/80 bg-amber-100/50'}`}
        >
          {value ? formatDateValue(value, field.format) : 'Click to set date'}
        </span>
      )}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={() => setModalOpen(false)}>
          <div className="bg-white rounded-2xl p-5 max-w-xs w-full" onClick={e => e.stopPropagation()}>
            <p className="text-sm font-semibold text-gray-900 mb-3">{field.label}</p>
            <input
              type="date"
              autoFocus
              value={value || ''}
              onChange={e => onChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2c4f26]/30 focus:border-[#2c4f26]"
            />
            <p className="text-xs text-gray-500 mt-2">Will print as: <span className="font-medium text-gray-700">{value ? formatDateValue(value, field.format) : '—'}</span></p>
            <button onClick={() => setModalOpen(false)} className="mt-4 w-full bg-[#2c4f26] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#3a6b32] transition">
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const PdfTextareaOverlay = ({ field, value, onChange, scale, onEnterKey, readOnly, showRequiredErrors }) => {
  const filled = !!String(value || '').trim();
  if (readOnly && !filled) return null;
  return (
    <textarea
      value={value || ''}
      onChange={readOnly ? undefined : e => onChange(e.target.value)}
      readOnly={readOnly}
      data-field-key={field.key}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onEnterKey?.(field.key);
        }
      }}
      style={{
        position: 'absolute',
        left: field.x * scale,
        top: (field.yTop - field.size * 1.15) * scale,
        transformOrigin: 'top left',
        ...zoomGuardBox(
          Math.max(scale >= 1 ? 10 : 6, field.size * scale),
          (field.maxWidth || 300) * scale,
          (field.lines || 3) * (field.lineHeight || field.size + 2) * scale,
        ),
      }}
      className={`px-1 py-0.5 border rounded-[3px] outline-none text-gray-900 resize-none leading-tight ${readOnly ? 'border-gray-200 bg-gray-50 cursor-default' : 'focus:bg-white focus:border-[#2c4f26] focus:ring-1 focus:ring-[#2c4f26]'} ${filled ? 'border-green-400/80 bg-green-50' : readOnly ? '' : field.required && showRequiredErrors ? 'border-red-400 bg-red-50' : 'border-amber-400/80 bg-amber-100/50'}`}
    />
  );
};

// A real <input type="checkbox"> here rather than this custom button used
// to ignore its CSS width/height on iOS Safari — the browser fell back to
// its own (much larger) native control size, so a checkbox meant to sit
// in a tiny inline slot on the printed PDF blew up and overlapped its
// neighbors. A plain styled button sidesteps that entirely.
//
// The visible box itself has to shrink with the page rather than sit at a
// fixed minimum — these option rows are often only ~10-18pt apart in the
// original PDF, and a box with a fixed floor (previously 15px regardless
// of scale) ends up taller than its own row on a heavily-shrunk mobile
// page, visually colliding with the row above/below. So the drawn box
// tracks scale with no floor, but sits inside a separate, larger
// invisible tap target (fixed at 26px) so it's still easy to hit on
// mobile without needing to look proportionally huge.
const PdfCheckOverlay = ({ x, yTop, scale, checked, onToggle, disabled }) => {
  const visualSize = Math.max(8, 11 * scale);
  const hitSize = Math.max(visualSize, 26);
  const offset = (hitSize - visualSize) / 2;
  return (
    <button
      type="button"
      aria-pressed={!!checked}
      disabled={disabled}
      onClick={disabled ? undefined : () => onToggle({ target: { checked: !checked } })}
      style={{
        position: 'absolute',
        left: x * scale - 2 - offset,
        top: (yTop - 10) * scale - 2 - offset,
        width: hitSize,
        height: hitSize,
        padding: 0,
        background: 'transparent',
        border: 'none',
      }}
      className={`flex items-center justify-center ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        style={{
          width: visualSize,
          height: visualSize,
          boxShadow: checked ? '0 0 0 3px rgba(34,197,94,0.35)' : 'none',
        }}
        className={`rounded-sm border flex items-center justify-center leading-none ${disabled ? 'bg-gray-100 border-gray-300' : checked ? 'bg-green-600 border-green-600' : 'bg-white border-gray-400'}`}
      >
        {checked && <Check size={Math.max(6, visualSize - 3)} className="text-white" strokeWidth={3} />}
      </span>
    </button>
  );
};

// Standalone checkboxes (e.g. "Not Applicable") aren't printed anywhere on
// the original form, so they need their own visible label in the overlay —
// unlike normal checkboxes, which sit on top of a label the PDF already prints.
const PdfStandaloneCheckboxOverlay = ({ field, value, onChange, scale, disabled }) => (
  <label
    style={{
      position: 'absolute',
      left: field.x * scale,
      top: (field.yTop - (field.size || 9)) * scale,
      fontSize: Math.max(scale >= 1 ? 10 : 6, (field.size || 9) * scale),
    }}
    className={`flex items-center gap-1.5 px-2 py-1 rounded-full font-medium whitespace-nowrap shadow-sm ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} ${value ? 'bg-green-100/80 border border-green-400/80 text-green-900' : 'bg-amber-100/80 border border-amber-400/80 text-amber-900'}`}
  >
    <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} disabled={disabled} className={disabled ? 'cursor-not-allowed' : 'accent-green-600 cursor-pointer'} />
    {field.label}
  </label>
);

const PdfSignatureOverlay = ({ field, value, onChange, scale, cachedSignature, onSignatureCaptured, readOnly }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('draw');
  const style = {
    position: 'absolute',
    left: field.x * scale,
    top: (field.yTop - field.height) * scale,
    width: field.width * scale,
    height: field.height * scale,
  };
  const openModal = () => {
    setMode(cachedSignature ? 'choose' : 'draw');
    setModalOpen(true);
  };
  const useCached = () => {
    onChange(cachedSignature);
    setModalOpen(false);
  };
  const handleDrawSave = (dataUrl) => {
    onChange(dataUrl);
    if (dataUrl && field.shared) onSignatureCaptured(field.shared, dataUrl);
  };
  if (readOnly && !value) return null;
  if (readOnly) {
    return <div style={style} className="flex items-center justify-center overflow-hidden rounded-[3px] border-2 border-gray-200 bg-gray-50"><img src={value} alt="Signature" className="max-h-full max-w-full object-contain" /></div>;
  }
  return (
    <>
      <button
        type="button"
        onClick={openModal}
        style={style}
        className={`flex items-center justify-center overflow-hidden rounded-[3px] transition ${value ? 'border-2 border-green-400/80 bg-green-50/40 hover:border-green-500' : 'border-2 border-dashed border-[#2c4f26]/60 bg-[#2c4f26]/5 hover:bg-[#2c4f26]/10'}`}
      >
        {value ? <img src={value} alt="Signature" className="max-h-full max-w-full object-contain" /> : <span className="text-[10px] font-semibold text-[#2c4f26] px-1 text-center leading-tight">Click to sign{field.shared === 'client_signature' ? ' *' : ''}</span>}
      </button>
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={() => setModalOpen(false)}>
          <div className="bg-white rounded-2xl p-5 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <p className="text-sm font-semibold text-gray-900 mb-3">{field.label}</p>
            {mode === 'choose' && cachedSignature ? (
              <div>
                <p className="text-xs text-gray-600 mb-2">Use the signature already captured earlier in this consultation?</p>
                <div className="border-2 border-gray-200 rounded-lg p-3 bg-white flex justify-center mb-3">
                  <img src={cachedSignature} alt="Captured signature" className="h-16 object-contain" />
                </div>
                <button onClick={useCached} className="w-full bg-[#2c4f26] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#3a6b32] transition mb-2">
                  Use this signature
                </button>
                <button onClick={() => setMode('draw')} className="w-full border border-gray-200 text-gray-600 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition">
                  Sign again instead
                </button>
              </div>
            ) : (
              <>
                <SignaturePad onSave={handleDrawSave} />
                <button onClick={() => setModalOpen(false)} className="mt-4 w-full bg-[#2c4f26] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#3a6b32] transition">
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const PdfPageWithOverlay = ({ pdfDoc, pageIndex, fields, resolveValue, onFieldChange, containerWidth, signatureLibrary, onSignatureCaptured, onEnterKey, isFieldEditable, showRequiredErrors }) => {
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const [viewportSize, setViewportSize] = useState(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const page = await pdfDoc.getPage(pageIndex + 1);
      if (cancelled) return;
      const baseViewport = page.getViewport({ scale: 1 });
      // No lower floor here either — a 0.75 minimum meant a 612pt-wide page
      // never rendered narrower than ~459px, wider than most phones, which
      // defeated the containerWidth fix above. Legibility of the interactive
      // fields is handled separately (their font floor doesn't scale down
      // with the page), so the page itself can shrink to actually fit.
      const targetScale = Math.min(2, Math.max(0.3, containerWidth / baseViewport.width));
      const viewport = page.getViewport({ scale: targetScale });
      const dpr = window.devicePixelRatio || 1;
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;

      // pdf.js refuses to run two render() calls on the same canvas at once —
      // cancel whatever's still in flight (e.g. from a resize) before starting.
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      canvas.width = Math.round(viewport.width * dpr);
      canvas.height = Math.round(viewport.height * dpr);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const task = page.render({ canvasContext: ctx, viewport });
      renderTaskRef.current = task;
      try {
        await task.promise;
      } catch (err) {
        if (err?.name === 'RenderingCancelledException') return;
        throw err;
      }
      if (renderTaskRef.current === task) renderTaskRef.current = null;
      if (cancelled) return;
      setScale(targetScale);
      setViewportSize({ width: viewport.width, height: viewport.height });
    })();
    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, pageIndex, containerWidth]);

  const pageFields = fields.filter(f => f.page === pageIndex);

  return (
    <div className="relative bg-white shadow-sm border border-gray-200 mb-4 mx-auto" style={viewportSize ? { width: viewportSize.width, height: viewportSize.height } : { minHeight: 300 }}>
      <canvas ref={canvasRef} />
      {viewportSize && pageFields.map(field => {
        const editable = isFieldEditable ? isFieldEditable(field) : true;
        if (field.type === 'signature') {
          return (
            <PdfSignatureOverlay
              key={field.key}
              field={field}
              value={resolveValue(field)}
              onChange={v => onFieldChange(field, v)}
              scale={scale}
              cachedSignature={field.shared ? signatureLibrary[field.shared] : null}
              onSignatureCaptured={onSignatureCaptured}
              readOnly={!editable}
            />
          );
        }
        if (field.type === 'radio') {
          return field.options.map(opt => (
            <PdfCheckOverlay key={`${field.key}-${opt.value}`} x={opt.x} yTop={opt.yTop} scale={scale} checked={resolveValue(field) === opt.value} onToggle={() => onFieldChange(field, opt.value)} disabled={!editable} />
          ));
        }
        if (field.type === 'checkbox_group') {
          const selected = resolveValue(field) || [];
          const allValues = field.options.map(o => o.value);
          const allSelected = allValues.length > 0 && allValues.every(v => selected.includes(v));
          return [
            field.selectAll && editable && (
              <button
                key={`${field.key}-selectall`}
                type="button"
                onClick={() => onFieldChange(field, allSelected ? [] : allValues)}
                style={{
                  position: 'absolute',
                  left: field.selectAll.x * scale,
                  top: (field.selectAll.yTop - 9) * scale,
                  fontSize: Math.max(9, 9 * scale),
                }}
                className="px-2 py-0.5 rounded-full bg-[#2c4f26] text-white font-semibold hover:bg-[#3a6b32] transition shadow-sm whitespace-nowrap"
              >
                {allSelected ? 'Clear All' : 'Select All'}
              </button>
            ),
            ...field.options.map(opt => (
              <PdfCheckOverlay
                key={`${field.key}-${opt.value}`}
                x={opt.x}
                yTop={opt.yTop}
                scale={scale}
                checked={selected.includes(opt.value)}
                onToggle={() => onFieldChange(field, selected.includes(opt.value) ? selected.filter(v => v !== opt.value) : [...selected, opt.value])}
                disabled={!editable}
              />
            )),
          ];
        }
        if (field.type === 'checkbox') {
          if (field.standalone) {
            return <PdfStandaloneCheckboxOverlay key={field.key} field={field} value={!!resolveValue(field)} onChange={v => onFieldChange(field, v)} scale={scale} disabled={!editable} />;
          }
          return <PdfCheckOverlay key={field.key} x={field.x} yTop={field.yTop} scale={scale} checked={!!resolveValue(field)} onToggle={e => onFieldChange(field, e.target.checked)} disabled={!editable} />;
        }
        if (field.type === 'textarea') {
          return <PdfTextareaOverlay key={field.key} field={field} value={resolveValue(field)} onChange={v => onFieldChange(field, v)} scale={scale} onEnterKey={onEnterKey} readOnly={!editable} showRequiredErrors={showRequiredErrors} />;
        }
        if (field.type === 'date' && field.format) {
          return <PdfDateChipOverlay key={field.key} field={field} value={resolveValue(field)} onChange={v => onFieldChange(field, v)} scale={scale} readOnly={!editable} />;
        }
        if (field.type === 'time') {
          return <PdfTimeSelectOverlay key={field.key} field={field} value={resolveValue(field)} onChange={v => onFieldChange(field, v)} scale={scale} readOnly={!editable} showRequiredErrors={showRequiredErrors} />;
        }
        return <PdfTextOverlayInput key={field.key} field={field} value={resolveValue(field)} onChange={v => onFieldChange(field, v)} scale={scale} onEnterKey={onEnterKey} readOnly={!editable} showRequiredErrors={showRequiredErrors} />;
      })}
    </div>
  );
};

const TemplatePdfViewer = ({ template, resolveValue, onFieldChange, signatureLibrary, onSignatureCaptured, isFieldEditable, showRequiredErrors }) => {
  const containerRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setPdfDoc(null);
    setLoadError(null);
    loadPdfDocument(template.file)
      .then(doc => { if (!cancelled) setPdfDoc(doc); })
      .catch(err => {
        console.error('PDF load failed:', err);
        if (!cancelled) setLoadError(`${err?.name || 'Error'}: ${err?.message || String(err)}`);
      });
    return () => { cancelled = true; };
  }, [template.file]);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const el = containerRef.current;
    // No large floor here — on a phone (~350-400px wide) forcing a 650px
    // minimum made the page render nearly at full letter-page width,
    // guaranteeing horizontal scroll on every mobile screen. A small floor
    // just guards against a momentary 0-width read before layout settles.
    const update = () => setContainerWidth(Math.max(280, Math.min(el.clientWidth, 900)));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Jumps to the first still-empty, editable, text-like field at or after
  // startIndex in reading order — shared by Enter-to-advance (from the
  // current field) and the floating mobile "next field" button (from the
  // top, each tap finding whatever's still blank).
  const isTextLikeField = (f) => ['text', 'email', 'phone', 'currency', 'textarea', 'time'].includes(f.type) || (f.type === 'date' && !f.format);
  const jumpToEmptyFieldFrom = (startIndex) => {
    for (let i = startIndex; i < template.fields.length; i++) {
      const f = template.fields[i];
      if (!isTextLikeField(f)) continue;
      if (isFieldEditable && !isFieldEditable(f)) continue;
      if (String(resolveValue(f) || '').trim()) continue;
      const el = document.querySelector(`[data-field-key="${f.key}"]`);
      if (el) {
        el.focus();
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return true;
    }
    return false;
  };
  const focusNextEmptyField = (currentKey) => {
    const idx = template.fields.findIndex(f => f.key === currentKey);
    if (idx === -1) return;
    jumpToEmptyFieldFrom(idx + 1);
  };
  const hasAnyEmptyField = template.fields.some(f => isTextLikeField(f) && (!isFieldEditable || isFieldEditable(f)) && !String(resolveValue(f) || '').trim());

  return (
    <div ref={containerRef} className="w-full overflow-x-auto relative">
      {hasAnyEmptyField && (
        <button
          type="button"
          onClick={() => jumpToEmptyFieldFrom(0)}
          className="fixed bottom-5 right-5 z-30 flex items-center gap-1.5 bg-[#2c4f26] text-white pl-3 pr-4 py-2.5 rounded-full shadow-lg hover:bg-[#3a6b32] transition text-sm font-medium"
        >
          <ChevronDown size={16} /> Next field
        </button>
      )}
      {loadError ? (
        <div className="py-8 text-center">
          <p className="text-sm text-red-600">Could not load the document preview.</p>
          <p className="text-xs text-gray-500 mt-1 px-4 break-words">{loadError}</p>
        </div>
      ) : !pdfDoc ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        Array.from({ length: pdfDoc.numPages }).map((_, i) => (
          <PdfPageWithOverlay
            key={`${template.key}-${i}`}
            pdfDoc={pdfDoc}
            pageIndex={i}
            fields={template.fields}
            resolveValue={resolveValue}
            onFieldChange={onFieldChange}
            containerWidth={containerWidth}
            signatureLibrary={signatureLibrary}
            onSignatureCaptured={onSignatureCaptured}
            onEnterKey={focusNextEmptyField}
            isFieldEditable={isFieldEditable}
            showRequiredErrors={showRequiredErrors}
          />
        ))
      )}
    </div>
  );
};

// ── Public: Remote Care Consultation signing (no login — token in the URL) ───
// Mirrors CustomerTicketView's public-page shape: a read RPC on mount, then
// direct fetch() calls to a token-gated edge function for writes. Reuses the
// wizard's own PDF rendering/filling machinery (TemplatePdfViewer, fillTemplate)
// so the client sees and fills exactly the same real documents staff do — just
// restricted to whichever fields are marked `clientEditable` in documentTemplates.js.
const isFieldValueEmpty = (field, value) => {
  if (field.type === 'checkbox') return !value;
  if (field.type === 'checkbox_group') return !Array.isArray(value) || value.length === 0;
  return !String(value || '').trim();
};

// First required field on the template (matching `staffOnlyFilter`) whose
// resolved value is still empty, or null if everything required is filled.
const findMissingRequiredField = (template, resolveValueForField, staffOnlyFilter) =>
  template.fields.find(f => f.required && !!f.staffOnly === staffOnlyFilter && isFieldValueEmpty(f, resolveValueForField(f))) || null;

const bytesToBase64 = (bytes) => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const RemoteConsultationView = ({ sessionId, token }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [shared, setShared] = useState({});
  const [docOverrides, setDocOverrides] = useState({});
  const [docStatus, setDocStatus] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [signatureLibrary, setSignatureLibrary] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '' });

  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: '' }), 3000);
  };

  const loadSession = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('get_remote_consultation_session', { p_session_id: sessionId, p_token: token });
    if (error || !data) {
      setInvalid(true);
      setLoading(false);
      return;
    }
    if (data.status === 'completed') {
      setAllDone(true);
      setLoading(false);
      return;
    }
    let resolvedTemplates = getTemplatesForOffice(data.office_code || MANASSAS_OFFICE_CODE);
    // A "Send 3 Priority Forms" link only ever wants these three from the
    // client — showing the full 10-document set here would be confusing
    // (and the other 7 were never staged for their review anyway).
    if (data.remote_priority_only) {
      resolvedTemplates = resolvedTemplates.filter(t => PRIORITY_DOCUMENT_KEYS.includes(t.key));
    }
    setTemplates(resolvedTemplates);
    setShared(data.shared_data || {});
    const overrides = {}; const status = {};
    (data.documents || []).forEach(d => { overrides[d.template_key] = d.field_values || {}; status[d.template_key] = d.status; });
    setDocOverrides(overrides);
    setDocStatus(status);
    const firstUnsigned = resolvedTemplates.findIndex(t => status[t.key] !== 'signed');
    setActiveIndex(firstUnsigned === -1 ? 0 : firstUnsigned);
    setLoading(false);
  };

  useEffect(() => { loadSession(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeTemplate = templates[activeIndex];
  const activeSigned = activeTemplate && docStatus[activeTemplate.key] === 'signed';

  const resolveValue = (template, field) => {
    const override = docOverrides[template.key]?.[field.key];
    if (override !== undefined) return override;
    if (field.type !== 'signature' && field.shared && shared[field.shared] !== undefined) return shared[field.shared];
    if (field.type === 'checkbox_group') return [];
    return field.default !== undefined ? field.default : '';
  };

  const handleFieldChange = (template, field, value) => {
    if (!field.clientEditable) return; // belt-and-suspenders — the overlay itself won't fire this for locked fields
    setDocOverrides(prev => ({ ...prev, [template.key]: { ...prev[template.key], [field.key]: value } }));
    if (field.shared && field.type !== 'signature') setShared(prev => ({ ...prev, [field.shared]: value }));
  };

  const handleSignatureCaptured = (sharedKey, dataUrl) => {
    setSignatureLibrary(prev => ({ ...prev, [sharedKey]: dataUrl }));
  };

  const buildFieldValues = (template) => {
    const values = {};
    for (const field of template.fields) values[field.key] = resolveValue(template, field);
    return values;
  };

  const submitActiveDocument = async () => {
    if (!activeTemplate) return;
    const values = buildFieldValues(activeTemplate);
    const notApplicable = !!values.not_applicable;
    const sigField = activeTemplate.fields.find(f => f.shared === 'client_signature');
    if (!notApplicable && sigField && !values[sigField.key]) {
      showToast('Please sign before continuing');
      return;
    }
    setSubmitting(true);
    try {
      const pdfBytes = await fillTemplate(activeTemplate, values);
      const editableValues = {};
      activeTemplate.fields.forEach(f => { if (f.clientEditable) editableValues[f.key] = values[f.key]; });

      const res = await fetch(`${SUPABASE_URL}/functions/v1/consultation-client-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          sessionId, token, templateKey: activeTemplate.key,
          fieldValues: editableValues, pdfBase64: bytesToBase64(pdfBytes),
        }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Failed to submit');

      const updatedStatus = { ...docStatus, [activeTemplate.key]: 'signed' };
      setDocStatus(updatedStatus);

      if (result.completed) {
        setAllDone(true);
      } else {
        const nextIndex = templates.findIndex((t, i) => i > activeIndex && updatedStatus[t.key] !== 'signed');
        setActiveIndex(nextIndex !== -1 ? nextIndex : templates.findIndex(t => updatedStatus[t.key] !== 'signed'));
        showToast(`${activeTemplate.title} submitted`);
      }
    } catch (err) {
      console.error(err);
      showToast('Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const Header = () => (
    <div className="bg-[#2c4f26] sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-5 py-4 flex items-center gap-4">
        <img src={HiLargeLogo} alt="Home Instead" className="h-12 w-auto object-contain brightness-0 invert" />
        <div className="w-px h-8 bg-white/30" />
        <span className="text-xl font-bold text-white tracking-tight">Care Consultation Documents</span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f7f5]">
        <Header />
        <div className="flex justify-center py-24"><div className="w-8 h-8 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin" /></div>
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="min-h-screen bg-[#f5f7f5]">
        <Header />
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">This link is invalid or has expired</h1>
          <p className="text-sm text-gray-600">Please contact your Home Instead office for a new link.</p>
        </div>
      </div>
    );
  }

  if (allDone) {
    return (
      <div className="min-h-screen bg-[#f5f7f5]">
        <Header />
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <CheckCircle size={40} className="text-[#2c4f26] mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Thank you — your documents have been submitted</h1>
          <p className="text-sm text-gray-600">Your Home Instead team has been notified and will follow up if anything else is needed. You can close this page.</p>
        </div>
      </div>
    );
  }

  const signedCount = templates.filter(t => docStatus[t.key] === 'signed').length;

  return (
    <div className="min-h-screen bg-[#f5f7f5]">
      <Toast message={toast.message} show={toast.show} onClose={() => setToast({ show: false, message: '' })} />
      <Header />
      <div className="flex flex-col lg:flex-row gap-6 max-w-6xl mx-auto px-4 py-6">
        <div className="lg:w-64 shrink-0">
          <button
            onClick={() => setSidebarOpen(o => !o)}
            className="lg:hidden w-full flex items-center justify-between bg-white border border-gray-200 rounded-xl px-3 py-2.5 mb-2"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-gray-900 min-w-0">
              <ClipboardList size={16} className="text-[#2c4f26] shrink-0" />
              <span className="truncate">{activeTemplate.title}</span>
            </span>
            <span className="flex items-center gap-1.5 text-xs text-gray-500 shrink-0 ml-2">
              {signedCount}/{templates.length}
              <ChevronDown size={14} className={`transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
            </span>
          </button>

          <div className={`${sidebarOpen ? 'block' : 'hidden'} lg:block bg-white border border-gray-200 rounded-xl p-3`}>
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2 px-1">{signedCount}/{templates.length} Complete</p>
            <div className="space-y-1">
              {templates.map((t, i) => {
                const status = docStatus[t.key];
                const active = i === activeIndex;
                return (
                  <button
                    key={t.key}
                    onClick={() => { setActiveIndex(i); setSidebarOpen(false); }}
                    className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm transition ${active ? 'bg-[#2c4f26] text-white' : status === 'signed' ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-500 hover:bg-gray-50'}`}
                  >
                    {status === 'signed' ? <CheckCircle size={14} className={`shrink-0 ${active ? 'text-white' : 'text-green-600'}`} /> : <Clock size={14} className={`shrink-0 ${active ? 'text-white' : 'text-gray-400'}`} />}
                    <span className="truncate">{t.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-xl p-4 md:p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-1">{activeTemplate.title}</h2>
          <p className="text-xs text-gray-500 mb-4">
            {activeSigned ? 'Already completed — shown for your records.' : 'Fill in the highlighted fields and sign where indicated. Everything else has already been filled in by your Home Instead office.'}
          </p>

          <TemplatePdfViewer
            template={activeTemplate}
            resolveValue={(field) => resolveValue(activeTemplate, field)}
            onFieldChange={(field, value) => handleFieldChange(activeTemplate, field, value)}
            signatureLibrary={signatureLibrary}
            onSignatureCaptured={handleSignatureCaptured}
            isFieldEditable={(field) => !activeSigned && !!field.clientEditable}
          />

          {!activeSigned && (
            <div className="flex items-center justify-end mt-8 pt-5 border-t border-gray-100">
              <button
                onClick={submitActiveDocument}
                disabled={submitting}
                className="flex items-center gap-2 bg-[#2c4f26] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#3a6b32] disabled:opacity-50 transition"
              >
                {submitting ? 'Submitting…' : 'Submit This Document'}
                {!submitting && <ChevronRight size={16} />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Public: Care Pro Transfer onboarding application (general link, no
// per-person token — continuity is a small {id, token} blob this browser
// keeps in localStorage once it starts, not anything in the URL) ───────────
const CP_APPLICATION_STORAGE_KEY = 'cp_application';

const CareProApplicationView = ({ resumeId, resumeToken }) => {
  const templates = React.useMemo(() => getCareProTemplates(), []);
  const [loading, setLoading] = useState(true);
  const [allDone, setAllDone] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [applicationToken, setApplicationToken] = useState(null);
  const [shared, setShared] = useState({});
  const [docOverrides, setDocOverrides] = useState({});
  const [docStatus, setDocStatus] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [signatureLibrary, setSignatureLibrary] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '' });
  // Required fields only turn red after a failed submit attempt on the
  // document currently open — not on first view, and not for other docs.
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);

  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: '' }), 3000);
  };

  // Draft-autosave (below) needs the current applicationId/token/templates
  // inside a debounced callback that outlives any single render — refs
  // avoid stale closures without having to re-create the timer on every
  // keystroke.
  const applicationRef = useRef({ id: null, token: null });
  const draftTimerRef = useRef(null);
  const draftTemplateKeyRef = useRef(null);
  // Latest known full field-values snapshot per document, updated
  // synchronously on every change — read by the debounced save instead of
  // recomputing via resolveValue, which would otherwise read a stale
  // pre-this-keystroke docOverrides/shared closure (state updates aren't
  // applied until the next render, but this ref is).
  const draftValuesRef = useRef({});

  const saveDraftNow = (templateKey) => {
    const { id, token } = applicationRef.current;
    const values = draftValuesRef.current[templateKey];
    if (!id || !token || !values) return;
    fetch(`${SUPABASE_URL}/functions/v1/care-pro-application-save-draft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
      body: JSON.stringify({ applicationId: id, token, templateKey, fieldValues: values }),
    }).catch(() => { /* best-effort — the next debounce or the real submit will catch it up */ });
  };

  const flushDraftSave = () => {
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
      if (draftTemplateKeyRef.current) saveDraftNow(draftTemplateKeyRef.current);
    }
  };

  // Flush whatever's pending if they close the tab mid-edit — best-effort,
  // not guaranteed to complete, but better than nothing.
  useEffect(() => {
    window.addEventListener('pagehide', flushDraftSave);
    return () => { window.removeEventListener('pagehide', flushDraftSave); flushDraftSave(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOrStart = async () => {
    setLoading(true);
    let cached = null;
    try { cached = JSON.parse(localStorage.getItem(CP_APPLICATION_STORAGE_KEY) || 'null'); } catch (_) { /* ignore */ }
    // A resume link sent by staff (?id=&token=) takes priority over whatever
    // this browser already has cached — e.g. someone picking up a coworker's
    // link, or their own progress link opened on a new device.
    if (resumeId && resumeToken) cached = { id: resumeId, token: resumeToken };

    if (cached?.id && cached?.token) {
      const { data } = await supabase.rpc('get_care_pro_application', { p_id: cached.id, p_token: cached.token });
      if (data) {
        if (data.status === 'completed' || data.status === 'pending') {
          // Already submitted from this device previously — nothing left to fill.
          localStorage.removeItem(CP_APPLICATION_STORAGE_KEY);
          setAllDone(true);
          setLoading(false);
          return;
        }
        setApplicationId(data.id);
        setApplicationToken(cached.token);
        applicationRef.current = { id: data.id, token: cached.token };
        // Seeds/refreshes this device's own localStorage regardless of
        // whether it got here via a cached id or a staff-sent resume link,
        // so continuing later (even without the link) keeps working.
        localStorage.setItem(CP_APPLICATION_STORAGE_KEY, JSON.stringify({ id: data.id, token: cached.token }));
        // today_date always reflects the day they're actually filling this
        // out (not whatever day the application was started) — any field
        // already filled from it takes precedence via resolveValue's own
        // override check, so this never clobbers real input.
        setShared({ ...(data.shared_data || {}), today_date: new Date().toISOString().slice(0, 10) });
        const overrides = {}; const status = {};
        (data.documents || []).forEach(d => { overrides[d.template_key] = d.field_values || {}; status[d.template_key] = d.status; });
        setDocOverrides(overrides);
        setDocStatus(status);
        const firstUnsigned = templates.findIndex(t => status[t.key] !== 'applicant_signed' && status[t.key] !== 'completed');
        setActiveIndex(firstUnsigned === -1 ? 0 : firstUnsigned);
        setLoading(false);
        return;
      }
      // Cached id/token didn't resolve (deleted, or otherwise invalid) — fall through and start fresh.
      localStorage.removeItem(CP_APPLICATION_STORAGE_KEY);
    }

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/care-pro-application-start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({}),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Failed to start application');
      localStorage.setItem(CP_APPLICATION_STORAGE_KEY, JSON.stringify({ id: result.id, token: result.token }));
      setApplicationId(result.id);
      setApplicationToken(result.token);
      applicationRef.current = { id: result.id, token: result.token };
      setShared({ today_date: new Date().toISOString().slice(0, 10) });
    } catch (err) {
      console.error(err);
      showToast('Something went wrong starting your application — please refresh and try again.');
    }
    setLoading(false);
  };

  useEffect(() => { loadOrStart(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeTemplate = templates[activeIndex];
  const activeSigned = activeTemplate && (docStatus[activeTemplate.key] === 'applicant_signed' || docStatus[activeTemplate.key] === 'completed');

  const resolveValue = (template, field) => {
    const override = docOverrides[template.key]?.[field.key];
    const overrideIsMeaningful = override !== undefined && !(field.shared && override === '');
    if (overrideIsMeaningful) return override;
    if (field.type !== 'signature' && field.shared && shared[field.shared] !== undefined) return shared[field.shared];
    if (override !== undefined) return override;
    if (field.type === 'checkbox_group') return [];
    return field.default !== undefined ? field.default : '';
  };

  const handleFieldChange = (template, field, value) => {
    if (field.staffOnly) return; // belt-and-suspenders — the overlay itself won't fire this for locked fields
    setDocOverrides(prev => ({ ...prev, [template.key]: { ...prev[template.key], [field.key]: value } }));
    if (field.shared && field.type !== 'signature') setShared(prev => ({ ...prev, [field.shared]: value }));

    // Snapshot for the draft-autosave — every other field still reads
    // correctly off the current (pre-render) closure, only field.key itself
    // needs the fresh value substituted in directly.
    const snapshot = {};
    for (const f of template.fields) snapshot[f.key] = f.key === field.key ? value : resolveValue(template, f);
    draftValuesRef.current[template.key] = snapshot;
    draftTemplateKeyRef.current = template.key;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      draftTimerRef.current = null;
      saveDraftNow(template.key);
    }, 1800);
  };

  const handleSignatureCaptured = (sharedKey, dataUrl) => {
    setSignatureLibrary(prev => ({ ...prev, [sharedKey]: dataUrl }));
  };

  const buildFieldValues = (template) => {
    const values = {};
    for (const field of template.fields) values[field.key] = resolveValue(template, field);
    return values;
  };

  const submitActiveDocument = async () => {
    if (!activeTemplate) return;
    const values = buildFieldValues(activeTemplate);
    const sigField = activeTemplate.fields.find(f => f.type === 'signature' && !f.staffOnly);
    if (sigField && !values[sigField.key]) {
      showToast('Please sign before continuing');
      return;
    }
    const missing = findMissingRequiredField(activeTemplate, f => values[f.key], false);
    if (missing) {
      setShowRequiredErrors(true);
      showToast(`Please fill in "${missing.label}" before continuing`);
      return;
    }
    setSubmitting(true);
    try {
      const pdfBytes = await fillTemplate(activeTemplate, values);
      const submitValues = {};
      activeTemplate.fields.forEach(f => { if (!f.staffOnly) submitValues[f.key] = values[f.key]; });

      const res = await fetch(`${SUPABASE_URL}/functions/v1/care-pro-application-submit-document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          applicationId, token: applicationToken, templateKey: activeTemplate.key,
          fieldValues: submitValues, pdfBase64: bytesToBase64(pdfBytes),
        }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Failed to submit');

      const updatedStatus = { ...docStatus, [activeTemplate.key]: 'applicant_signed' };
      setDocStatus(updatedStatus);

      if (result.submitted) {
        localStorage.removeItem(CP_APPLICATION_STORAGE_KEY);
        setAllDone(true);
      } else {
        const nextIndex = templates.findIndex((t, i) => i > activeIndex && updatedStatus[t.key] !== 'applicant_signed' && updatedStatus[t.key] !== 'completed');
        setShowRequiredErrors(false);
        setActiveIndex(nextIndex !== -1 ? nextIndex : templates.findIndex(t => updatedStatus[t.key] !== 'applicant_signed' && updatedStatus[t.key] !== 'completed'));
        showToast(`${activeTemplate.title} submitted`);
      }
    } catch (err) {
      console.error(err);
      showToast('Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const Header = () => (
    <div className="bg-[#2c4f26] sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-5 py-4 flex items-center gap-4">
        <img src={HiLargeLogo} alt="Home Instead" className="h-12 w-auto object-contain brightness-0 invert" />
        <div className="w-px h-8 bg-white/30" />
        <span className="text-xl font-bold text-white tracking-tight">Care Pro Onboarding Application</span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f7f5]">
        <Header />
        <div className="flex justify-center py-24"><div className="w-8 h-8 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin" /></div>
      </div>
    );
  }

  if (allDone) {
    return (
      <div className="min-h-screen bg-[#f5f7f5]">
        <Header />
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <CheckCircle size={40} className="text-[#2c4f26] mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Thank you — we've received your application</h1>
          <p className="text-sm text-gray-600">Home Instead will review it and follow up with next steps. You can close this page.</p>
        </div>
      </div>
    );
  }

  if (!activeTemplate) {
    return (
      <div className="min-h-screen bg-[#f5f7f5]">
        <Header />
        <div className="max-w-lg mx-auto px-4 py-16 text-center text-sm text-gray-600">This application isn't available right now — please contact your Home Instead office.</div>
      </div>
    );
  }

  const signedCount = templates.filter(t => docStatus[t.key] === 'applicant_signed' || docStatus[t.key] === 'completed').length;

  return (
    <div className="min-h-screen bg-[#f5f7f5]">
      <Toast message={toast.message} show={toast.show} onClose={() => setToast({ show: false, message: '' })} />
      <Header />
      <div className="flex flex-col lg:flex-row gap-6 max-w-6xl mx-auto px-4 py-6">
        <div className="lg:w-64 shrink-0">
          <button
            onClick={() => setSidebarOpen(o => !o)}
            className="lg:hidden w-full flex items-center justify-between bg-white border border-gray-200 rounded-xl px-3 py-2.5 mb-2"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-gray-900 min-w-0">
              <ClipboardList size={16} className="text-[#2c4f26] shrink-0" />
              <span className="truncate">{activeTemplate.title}</span>
            </span>
            <span className="flex items-center gap-1.5 text-xs text-gray-500 shrink-0 ml-2">
              {signedCount}/{templates.length}
              <ChevronDown size={14} className={`transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
            </span>
          </button>

          <div className={`${sidebarOpen ? 'block' : 'hidden'} lg:block bg-white border border-gray-200 rounded-xl p-3`}>
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2 px-1">{signedCount}/{templates.length} Complete</p>
            <div className="space-y-1">
              {templates.map((t, i) => {
                const status = docStatus[t.key];
                const active = i === activeIndex;
                const done = status === 'applicant_signed' || status === 'completed';
                return (
                  <button
                    key={t.key}
                    onClick={() => { flushDraftSave(); setShowRequiredErrors(false); setActiveIndex(i); setSidebarOpen(false); }}
                    className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm transition ${active ? 'bg-[#2c4f26] text-white' : done ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-500 hover:bg-gray-50'}`}
                  >
                    {done ? <CheckCircle size={14} className={`shrink-0 ${active ? 'text-white' : 'text-green-600'}`} /> : <Clock size={14} className={`shrink-0 ${active ? 'text-white' : 'text-gray-400'}`} />}
                    <span className="truncate">{t.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-xl p-4 md:p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-1">{activeTemplate.title}</h2>
          <p className="text-xs text-gray-500 mb-4">
            {activeSigned ? 'Already submitted — shown for your records.' : 'Fill in the highlighted fields and sign where indicated.'}
          </p>

          <TemplatePdfViewer
            template={activeTemplate}
            resolveValue={(field) => resolveValue(activeTemplate, field)}
            onFieldChange={(field, value) => handleFieldChange(activeTemplate, field, value)}
            signatureLibrary={signatureLibrary}
            onSignatureCaptured={handleSignatureCaptured}
            isFieldEditable={(field) => !activeSigned && !field.staffOnly}
            showRequiredErrors={showRequiredErrors}
          />

          {!activeSigned && (
            <div className="flex items-center justify-end mt-8 pt-5 border-t border-gray-100">
              <button
                onClick={submitActiveDocument}
                disabled={submitting}
                className="flex items-center gap-2 bg-[#2c4f26] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#3a6b32] disabled:opacity-50 transition"
              >
                {submitting ? 'Submitting…' : 'Submit This Document'}
                {!submitting && <ChevronRight size={16} />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ManassasConsultationWizard = ({ showToast, profile, permissions, onBack, onCompleted, resumeSessionId, officeCode, onSendRemotely, onSendRemotelyPriority }) => {
  const canFinalizeConsultations = profile?.role === 'owner' || permissions?.can_finalize_consultations;
  // For a new session, the office is chosen up front by the caller. For a
  // resumed session, the office was already fixed when it was created — the
  // loaded session record's own office_code (not this prop) decides which
  // template set applies, since the caller may not know it ahead of resume.
  const [templates, setTemplates] = useState(() => getTemplatesForOffice(officeCode || MANASSAS_OFFICE_CODE));
  const [sessionId, setSessionId] = useState(resumeSessionId || null);
  const [shared, setShared] = useState({ service_state: 'VA' });
  const [docOverrides, setDocOverrides] = useState({});
  const [docStatus, setDocStatus] = useState({});
  // Which shared_data keys / per-document field keys THIS device has
  // actually edited (vs. just loaded for display) — consultationSync.js
  // only ever overwrites the server's copy of a dirty key, merging onto
  // whatever else the server has. Without this, two staff opening the same
  // consultation and saving would have whoever saves last silently wipe out
  // the other's work, even in fields they never touched.
  const [dirtySharedKeys, setDirtySharedKeys] = useState(() => new Set());
  const [dirtyFieldKeys, setDirtyFieldKeys] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [submittingFinal, setSubmittingFinal] = useState(false);
  // Last signature drawn per shared key this session — lets "click to sign"
  // on later documents offer a one-click reuse instead of a full redraw,
  // without ever silently auto-filling a signature onto an unsigned document.
  const [signatureLibrary, setSignatureLibrary] = useState({});
  const handleSignatureCaptured = (sharedKey, dataUrl) => {
    setSignatureLibrary(prev => ({ ...prev, [sharedKey]: dataUrl }));
  };

  // The actual sync loop runs app-wide (see OnboardingApp) so it keeps going
  // even after leaving this screen — this just mirrors the status for the pill.
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  useEffect(() => {
    const unsubscribe = onSyncStatusChange(setPendingSyncCount);
    return unsubscribe;
  }, []);

  useEffect(() => {
    (async () => {
      if (resumeSessionId) {
        // IndexedDB is only trusted over the server when it holds a genuine
        // unsynced edit (this device changed something and hasn't pushed it
        // yet). Otherwise the server wins — it can be ahead of this device's
        // cache now that a client can complete a session remotely, entirely
        // bypassing whichever staff device originally prepped it.
        const localSession = await getSessionLocal(resumeSessionId);
        let session = localSession;
        try {
          const { data } = await supabase.from('consultation_sessions').select('*').eq('id', resumeSessionId).single();
          if (data && (!localSession || localSession.synced !== false)) {
            session = data;
            await saveSessionLocal(data);
            await markSessionSynced(data.id);
          }
        } catch (_) {
          // offline — fall back to whatever's cached locally
        }
        let resolvedTemplates = templates;
        if (session) {
          resolvedTemplates = getTemplatesForOffice(session.office_code || MANASSAS_OFFICE_CODE);
          setTemplates(resolvedTemplates);
          setSessionId(session.id);
          setShared({
            ...session.shared_data,
            client_name: session.client_name || session.shared_data?.client_name || '',
            client_email: session.client_email || session.shared_data?.client_email || '',
            client_phone: session.client_phone || session.shared_data?.client_phone || '',
            service_address: session.service_address || session.shared_data?.service_address || '',
            service_city: session.service_city || session.shared_data?.service_city || '',
            service_state: session.service_state || 'VA',
            service_zip: session.service_zip || session.shared_data?.service_zip || '',
          });
          // Only a genuinely-unsynced local session (this device's own
          // pending edit, not yet pushed) carries dirty-key info worth
          // restoring — a synced local copy has nothing outstanding to merge.
          if (localSession && localSession.synced === false && localSession.dirtySharedKeys) {
            setDirtySharedKeys(new Set(localSession.dirtySharedKeys));
          }
        }

        const localDocs = await getDocumentsForSession(resumeSessionId);
        const localDocsByKey = {};
        localDocs.forEach(d => { localDocsByKey[d.templateKey] = d; });
        let docs = localDocs;
        try {
          const { data: serverDocs } = await supabase.from('consultation_documents').select('*').eq('session_id', resumeSessionId);
          if (serverDocs) {
            const mergedDocs = {};
            serverDocs.forEach(d => {
              mergedDocs[d.template_key] = { sessionId: d.session_id, templateKey: d.template_key, fieldValues: d.field_values, status: d.status, signedAt: d.signed_at, pdfPath: d.pdf_path };
            });
            // A genuinely-unsynced local document (this device's own pending
            // edit) still wins over the server's copy of that same document.
            Object.entries(localDocsByKey).forEach(([key, d]) => { if (d.synced === false) mergedDocs[key] = d; });
            docs = Object.values(mergedDocs);
            await Promise.all(serverDocs.map(d => {
              if (localDocsByKey[d.template_key]?.synced === false) return null;
              return saveDocumentLocal(d.session_id, d.template_key, { fieldValues: d.field_values, status: d.status, signedAt: d.signed_at, pdfPath: d.pdf_path })
                .then(() => markDocumentSynced(d.session_id, d.template_key));
            }));
          }
        } catch (_) {
          // offline — fall back to whatever's cached locally
        }
        const overrides = {}; const status = {};
        docs.forEach(d => { overrides[d.templateKey] = d.fieldValues || {}; status[d.templateKey] = d.status; });
        setDocOverrides(overrides);
        setDocStatus(status);
        // Same reasoning as the session's dirtySharedKeys above, per document.
        const fieldDirty = {};
        docs.forEach(d => { if (d.synced === false && d.dirtyFieldKeys) fieldDirty[d.templateKey] = d.dirtyFieldKeys; });
        setDirtyFieldKeys(fieldDirty);
        const firstUnsigned = resolvedTemplates.findIndex(t => status[t.key] !== 'signed');
        setActiveIndex(firstUnsigned === -1 ? 0 : firstUnsigned);
        // Every document already signed means this resume is staff opening a
        // pending_review session (most commonly one a client just finished
        // remotely) — land straight on the review screen instead of the
        // first document, since that's what they're here to do.
        if (firstUnsigned === -1) setShowReview(true);
      } else {
        const staffDefaults = { service_state: 'VA', staff_name: profile?.full_name || '', today_date: new Date().toISOString().slice(0, 10) };
        setShared(s => ({ ...s, ...staffDefaults }));
        // These are set outside handleFieldChange, so they need to be marked
        // dirty explicitly — otherwise the first sync of a brand-new session
        // would omit them from shared_data entirely.
        const initialDirtyKeys = new Set(Object.keys(staffDefaults));
        setDirtySharedKeys(initialDirtyKeys);
        // Generated locally so the wizard never has to wait on a network
        // round-trip (or fail outright) just to start — synced in the background.
        const newId = generateUUID();
        setSessionId(newId);
        await saveSessionLocal({
          id: newId,
          office_code: officeCode || MANASSAS_OFFICE_CODE,
          status: 'draft',
          created_by: profile?.id || null,
          client_name: '',
          client_email: null,
          client_phone: null,
          service_address: null,
          service_city: null,
          service_state: 'VA',
          service_zip: null,
          shared_data: staffDefaults,
          dirtySharedKeys: [...initialDirtyKeys],
          created_at: new Date().toISOString(),
        });
        syncPendingConsultations();
      }
      setInitializing(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeTemplate = templates[activeIndex];

  const resolveValue = (template, field) => {
    const override = docOverrides[template.key]?.[field.key];
    // An override that's blank doesn't count as "explicitly set" for a
    // shared-autofill field — otherwise, saving this document even once
    // before the shared value existed (e.g. this doc's own "Save Progress"
    // ran before the address was typed on the Service Agreement) freezes it
    // blank forever, even after the shared value is filled in elsewhere.
    const overrideIsMeaningful = override !== undefined && !(field.shared && override === '');
    if (overrideIsMeaningful) return override;
    // Signatures are never autofilled — each document gets its own explicit "click to sign".
    if (field.type !== 'signature' && field.shared && shared[field.shared] !== undefined) return shared[field.shared];
    if (override !== undefined) return override;
    if (field.type === 'checkbox_group') return [];
    return field.default !== undefined ? field.default : '';
  };

  const handleFieldChange = (template, field, value) => {
    setDocOverrides(prev => ({ ...prev, [template.key]: { ...prev[template.key], [field.key]: value } }));
    setDirtyFieldKeys(prev => ({ ...prev, [template.key]: [...new Set([...(prev[template.key] || []), field.key])] }));
    if (field.shared && field.type !== 'signature') {
      setShared(prev => ({ ...prev, [field.shared]: value }));
      setDirtySharedKeys(prev => new Set(prev).add(field.shared));
    }
  };

  const buildFieldValues = (template) => {
    const values = {};
    for (const field of template.fields) values[field.key] = resolveValue(template, field);
    return values;
  };

  // Local-first: every save writes to IndexedDB (and updates on-screen state)
  // immediately, then kicks off a background sync attempt — the UI never
  // blocks on, or fails because of, a network call.
  const persistSessionMeta = async (sid, nextShared) => {
    if (!sid) return;
    await saveSessionLocal({
      id: sid,
      client_name: nextShared.client_name || '',
      client_email: nextShared.client_email || null,
      client_phone: nextShared.client_phone || null,
      service_address: nextShared.service_address || null,
      service_city: nextShared.service_city || null,
      service_state: nextShared.service_state || 'VA',
      service_zip: nextShared.service_zip || null,
      shared_data: nextShared,
      dirtySharedKeys: [...dirtySharedKeys],
    });
    syncPendingConsultations();
  };

  const markComplete = async () => {
    if (!activeTemplate) return;
    const values = buildFieldValues(activeTemplate);
    const notApplicable = !!values.not_applicable;
    const sigField = activeTemplate.fields.find(f => f.shared === 'client_signature');
    const missingSignature = !notApplicable && sigField && !values[sigField.key];

    setSaving(true);
    try {
      if (missingSignature) {
        // No client signature yet (e.g. staff pre-filling sections ahead of
        // sending this consultation to the client remotely) — save whatever's
        // filled in so far without generating a final PDF or advancing.
        await saveDocumentLocal(sessionId, activeTemplate.key, { fieldValues: values, status: 'pending', dirtyFieldKeys: dirtyFieldKeys[activeTemplate.key] || [] });
        await persistSessionMeta(sessionId, shared);
        showToast('Progress saved — client signature still needed', 'success');
        return;
      }

      const pdfBytes = await fillTemplate(activeTemplate, values);
      const blob = bytesToBlob(pdfBytes);
      await saveDocumentLocal(sessionId, activeTemplate.key, {
        fieldValues: values, pdfBlob: blob, status: 'signed', signedAt: new Date().toISOString(),
        dirtyFieldKeys: dirtyFieldKeys[activeTemplate.key] || [],
      });

      const updatedStatus = { ...docStatus, [activeTemplate.key]: 'signed' };
      setDocStatus(updatedStatus);
      await persistSessionMeta(sessionId, shared);

      const isLast = templates.every(t => updatedStatus[t.key] === 'signed');
      if (isLast) {
        // Session stays a draft until staff explicitly submit from the
        // review screen — signing the last document isn't the same as
        // confirming everything's actually ready.
        showToast('All documents signed — review before submitting', 'success');
        setShowReview(true);
      } else {
        const nextIndex = templates.findIndex((t, i) => i > activeIndex && updatedStatus[t.key] !== 'signed');
        setActiveIndex(nextIndex !== -1 ? nextIndex : templates.findIndex(t => updatedStatus[t.key] !== 'signed'));
        showToast(`${activeTemplate.title} saved`, 'success');
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to save document', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveAndExit = async () => {
    // persistSessionMeta only covers session-level shared fields (name,
    // address, date, etc.) — it never touched the document actually on
    // screen, so anything typed into a non-shared field (rates, checkboxes,
    // free text) was silently lost on exit. Save that document's current
    // values too, preserving whatever status it already had (don't force a
    // signed doc back to pending, and don't require a signature to save).
    if (activeTemplate) {
      const values = buildFieldValues(activeTemplate);
      await saveDocumentLocal(sessionId, activeTemplate.key, {
        fieldValues: values,
        status: docStatus[activeTemplate.key] || 'pending',
        dirtyFieldKeys: dirtyFieldKeys[activeTemplate.key] || [],
      });
    }
    await persistSessionMeta(sessionId, shared);
    onBack();
  };

  const submitFinal = async () => {
    setSubmittingFinal(true);
    try {
      // Direct, targeted update rather than routing through the offline
      // queue/local-full-row-sync path — completing a session is a one-way
      // status change, and letting it queue offline risks a later sync
      // reverting someone else's more-advanced status if this device's local
      // copy is stale. So this now requires connectivity — fail clearly
      // rather than silently queue something that could corrupt shared state.
      const { error } = await supabase
        .from('consultation_sessions')
        .update({ status: 'completed', completed_at: new Date().toISOString(), completed_by: profile?.id || null, priority_only_completion: false })
        .eq('id', sessionId);
      if (error) throw error;
      await markSessionSynced(sessionId);
      onCompleted();
    } catch (err) {
      console.error(err);
      showToast('Could not submit — check your connection and try again', 'error');
    } finally {
      setSubmittingFinal(false);
    }
  };

  // A document explicitly marked "Not Applicable to this Client" (currently
  // only the LTC memorandum) is done as soon as that box is checked — none
  // of its other fields (initials, insurance details, signatures) are
  // expected to be filled in, and staff never needs to countersign it.
  const isNotApplicable = (template) => {
    const naField = template.fields.find(f => f.key === 'not_applicable');
    return !!naField && !!resolveValue(template, naField);
  };

  // "signed" only ever meant the client's required parts were done (including
  // via the remote flow, which can never fill this in) — it says nothing
  // about whether staff have countersigned their own line yet.
  const needsStaffSignature = (template) => {
    if (isNotApplicable(template)) return false;
    const staffSigField = template.fields.find(f => f.type === 'signature' && f.shared === 'staff_signature');
    return !!staffSigField && !resolveValue(template, staffSigField);
  };

  if (initializing) {
    return <div className="flex items-center justify-center py-24"><div className="w-8 h-8 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (showReview) {
    const isFieldBlank = (template, field) => {
      const value = resolveValue(template, field);
      if (field.type === 'checkbox_group') return !(value && value.length > 0);
      if (field.type === 'checkbox') return !value;
      return !value || (typeof value === 'string' && !value.trim());
    };
    const blankCount = (template) => isNotApplicable(template) ? 0 : template.fields.filter(f => isFieldBlank(template, f)).length;

    return (
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 bg-[#f0f5f0] rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={28} className="text-[#2c4f26]" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">
            Ready to submit{shared.client_name ? ` for ${shared.client_name}` : ''}
          </h1>
          <p className="text-sm text-gray-600 mb-6">All {templates.length} documents are signed, but this consultation is still a draft. Click any document below to review or make changes, then submit when everything checks out.</p>
          <div className="text-left divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden mb-6">
            {templates.map((t, i) => {
              const blanks = blankCount(t);
              const needsStaffSig = needsStaffSignature(t);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => { setActiveIndex(i); setShowReview(false); }}
                  className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-white hover:bg-gray-50 transition text-left"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {needsStaffSig ? <Pencil size={15} className="text-amber-600 shrink-0" /> : <CheckCircle size={15} className="text-green-600 shrink-0" />}
                    <span className="text-sm text-gray-800 truncate">{t.title}</span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    {needsStaffSig && <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Needs your sig</span>}
                    {blanks > 0 ? (
                      <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">{blanks} blank</span>
                    ) : (
                      <span className="text-xs text-gray-400">No blanks</span>
                    )}
                    <ChevronRight size={14} className="text-gray-300" />
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex gap-3 justify-center">
            <button
              onClick={saveAndExit}
              disabled={submittingFinal}
              className="border border-gray-200 text-gray-600 px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 transition"
            >
              Save &amp; Exit as Draft
            </button>
            {canFinalizeConsultations && (
              <button
                onClick={submitFinal}
                disabled={submittingFinal}
                className="bg-[#2c4f26] text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#3a6b32] disabled:opacity-50 transition"
              >
                {submittingFinal ? 'Submitting…' : 'Submit & Finish'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const signedCount = templates.filter(t => docStatus[t.key] === 'signed').length;
  const remainingCount = templates.length - signedCount;
  const isLastRemaining = remainingCount === 1 && docStatus[activeTemplate.key] !== 'signed';
  const activeSigField = activeTemplate.fields.find(f => f.shared === 'client_signature');
  const activeMissingSignature = !resolveValue(activeTemplate, { key: 'not_applicable' }) && activeSigField && !resolveValue(activeTemplate, activeSigField);
  const fullyDoneCount = templates.filter(t => docStatus[t.key] === 'signed' && !needsStaffSignature(t)).length;

  return (
    <div className="flex flex-col lg:flex-row gap-6 max-w-6xl mx-auto px-4 py-6">
      <div className="lg:w-64 shrink-0">
        <button onClick={saveAndExit} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-2">
          <ChevronLeft size={16} /> Save &amp; Exit
        </button>

        <p className={`text-xs font-medium mb-3 flex items-center gap-1.5 ${pendingSyncCount > 0 ? 'text-amber-600' : 'text-green-700'}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${pendingSyncCount > 0 ? 'bg-amber-500' : 'bg-green-600'}`} />
          {pendingSyncCount > 0 ? `Offline — ${pendingSyncCount} pending, will sync automatically` : 'All changes synced'}
        </p>

        {onSendRemotely && (
          <button
            type="button"
            onClick={() => sessionId && onSendRemotely(sessionId)}
            disabled={!sessionId}
            title="Copy a link the client can use to fill out and sign this consultation remotely"
            className="w-full flex items-center justify-center gap-1.5 text-sm font-medium text-[#2c4f26] border border-[#2c4f26]/30 px-3 py-2 rounded-lg hover:bg-[#2c4f26]/5 disabled:opacity-50 transition mb-2"
          >
            <Send size={14} /> Remote Link 10 Forms
          </button>
        )}

        {onSendRemotelyPriority && (
          <button
            type="button"
            onClick={() => sessionId && onSendRemotelyPriority(sessionId)}
            disabled={!sessionId}
            title="Copy a link with only the Service Agreement, ACH Payment Form, and Late Payment Authorization Agreement — for time-crunch situations"
            className="w-full flex items-center justify-center gap-1.5 text-sm font-medium text-blue-700 border border-blue-300 px-3 py-2 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition mb-4"
          >
            <Send size={14} /> Remote Link 3 Priority Forms
          </button>
        )}

        {signedCount === templates.length && (
          <button
            type="button"
            onClick={() => setShowReview(true)}
            className="w-full flex items-center justify-center gap-1.5 text-sm font-medium text-gray-600 border border-gray-200 px-3 py-2 rounded-lg hover:bg-gray-50 transition mb-4"
          >
            <CheckCircle size={14} /> Back to Review
          </button>
        )}

        {/* Compact toggle — collapses the document list on smaller screens so the preview has room */}
        <button
          onClick={() => setSidebarOpen(o => !o)}
          className="lg:hidden w-full flex items-center justify-between bg-white border border-gray-200 rounded-xl px-3 py-2.5 mb-2"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-gray-900 min-w-0">
            <ClipboardList size={16} className="text-[#2c4f26] shrink-0" />
            <span className="truncate">{activeTemplate.title}</span>
          </span>
          <span className="flex items-center gap-1.5 text-xs text-gray-500 shrink-0 ml-2">
            {signedCount}/{templates.length}
            <ChevronDown size={14} className={`transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
          </span>
        </button>

        <div className={`${sidebarOpen ? 'block' : 'hidden'} lg:block bg-white border border-gray-200 rounded-xl p-3`}>
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2 px-1">{fullyDoneCount}/{templates.length} Fully Signed</p>
          <div className="space-y-1">
            {templates.map((t, i) => {
              const status = docStatus[t.key];
              const needsStaffSig = status === 'signed' && needsStaffSignature(t);
              const active = i === activeIndex;
              return (
                <button
                  key={t.key}
                  onClick={() => { setActiveIndex(i); setSidebarOpen(false); }}
                  title={needsStaffSig ? 'Client signed — still needs your signature' : undefined}
                  className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm transition ${active ? 'bg-[#2c4f26] text-white' : status === 'signed' ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ${active ? 'bg-white/20 text-white' : needsStaffSig ? 'bg-amber-100 text-amber-700' : status === 'signed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {needsStaffSig ? <Pencil size={11} /> : status === 'signed' ? <Check size={12} /> : i + 1}
                  </span>
                  <span className="truncate">{t.title}</span>
                  {needsStaffSig && !active && <span className="text-[10px] font-medium text-amber-600 shrink-0 ml-auto">Needs your sig</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-bold text-gray-900">{activeTemplate.title}</h2>
            <span className="text-xs text-gray-500">Document {activeIndex + 1} of {templates.length}</span>
          </div>
          <p className="text-xs text-gray-600 mb-5">Fill this out with the client so they can read along. Highlighted boxes are the fillable fields — shared ones like name, address, and date autofill onto the other documents as you go.</p>

          {(() => {
            const naField = activeTemplate.fields.find(f => f.key === 'not_applicable');
            if (!naField) return null;
            const isNA = !!resolveValue(activeTemplate, naField);
            return (
              <label className={`flex items-center gap-3 mb-5 px-4 py-3 rounded-xl border cursor-pointer transition ${isNA ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300'}`}>
                <input
                  type="checkbox"
                  checked={isNA}
                  onChange={e => handleFieldChange(activeTemplate, naField, e.target.checked)}
                  className="w-5 h-5 accent-[#2c4f26] cursor-pointer shrink-0"
                />
                <span className={`text-sm font-semibold ${isNA ? 'text-green-800' : 'text-amber-800'}`}>
                  Not applicable to this client{isNA ? ' — document will be marked complete without a signature' : '?'}
                </span>
              </label>
            );
          })()}

          <TemplatePdfViewer
            template={activeTemplate}
            resolveValue={(field) => resolveValue(activeTemplate, field)}
            onFieldChange={(field, value) => handleFieldChange(activeTemplate, field, value)}
            signatureLibrary={signatureLibrary}
            onSignatureCaptured={handleSignatureCaptured}
          />

          <div className="flex items-center justify-between mt-8 pt-5 border-t border-gray-100">
            <button
              onClick={() => setActiveIndex(i => Math.max(0, i - 1))}
              disabled={activeIndex === 0}
              className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <ChevronLeft size={16} /> Previous
            </button>
            <button
              onClick={markComplete}
              disabled={saving}
              className="flex items-center gap-2 bg-[#2c4f26] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#3a6b32] disabled:opacity-50 transition"
            >
              {saving ? 'Saving…' : activeMissingSignature ? 'Save Progress' : isLastRemaining ? 'Finish & Save All' : docStatus[activeTemplate.key] === 'signed' ? 'Save Changes' : 'Mark Complete & Continue'}
              {!saving && <ChevronRight size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CareConsultationTab = ({ showToast, permissions, profile }) => {
  const canFinalizeConsultations = profile?.role === 'owner' || permissions?.can_finalize_consultations;
  const canDeleteConsultations = profile?.role === 'owner' || permissions?.can_delete_consultations;
  const canViewConsultationArchive = profile?.role === 'owner' || permissions?.can_view_consultation_archive;
  // Anyone with tab access can start and keep working their own still-draft
  // consultation (normal intake) — editing one someone else started, or one
  // that's moved past draft (Pending Review/Completed), requires an
  // explicit edit or finalize grant. Mirrors the RLS enforcement on
  // consultation_sessions/consultation_documents, not just a UI nicety.
  const canEditSession = (s) =>
    profile?.role === 'owner' ||
    permissions?.can_edit_consultations ||
    permissions?.can_finalize_consultations ||
    (s.created_by === profile?.id && s.status === 'draft');
  const [view, setView] = useState('list');
  const [loading, setLoading] = useState(true);

  const [manassasSessions, setManassasSessions] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [resumeSessionId, setResumeSessionId] = useState(null);
  const [newSessionOfficeCode, setNewSessionOfficeCode] = useState(null);
  const [showOfficePicker, setShowOfficePicker] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDocs, setSessionDocs] = useState([]);
  const [sessionDocsLoading, setSessionDocsLoading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [sendingDocs, setSendingDocs] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [deletingSession, setDeletingSession] = useState(false);
  const [reopeningSession, setReopeningSession] = useState(false);
  const [archivingSession, setArchivingSession] = useState(false);
  const [wellskyUploading, setWellskyUploading] = useState(false);
  const [wellskyMatchModal, setWellskyMatchModal] = useState(null); // { sessionId, searchTerm, searching, results }
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [syncingNow, setSyncingNow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getUnsyncedSessions().then(rows => { if (!cancelled) setPendingSyncCount(rows.length); });
    const unsubscribe = onSyncStatusChange((count) => { if (!cancelled) setPendingSyncCount(count); });
    return () => { cancelled = true; unsubscribe(); };
  }, []);

  const deleteManassasSession = async (sessionId) => {
    setDeletingSession(true);
    // Always clear the local copy first — this is what makes a fully-offline,
    // never-synced draft actually disappear even with no connection.
    await deleteSessionLocal(sessionId);
    try {
      const { data: docs } = await supabase.from('consultation_documents').select('pdf_path').eq('session_id', sessionId);
      const paths = (docs || []).map(d => d.pdf_path).filter(Boolean);
      if (paths.length > 0) {
        await supabase.storage.from('consultation-documents').remove(paths);
      }
      const { error } = await supabase.from('consultation_sessions').delete().eq('id', sessionId);
      if (error) throw error;
      showToast('Consultation deleted');
    } catch (err) {
      console.error('Delete session error:', err);
      showToast("Removed locally — will finish deleting once you're back online", 'error');
    } finally {
      setDeletingSessionId(null);
      setDeletingSession(false);
      setSelectedSession(null);
      loadData();
    }
  };

  const loadData = async () => {
    setLoading(true);
    const { data: sessions } = await supabase
      .from('consultation_sessions')
      .select('id, office_code, client_name, service_address, status, created_at, completed_at, delivery_mode, secure_token, link_sent_at, link_expires_at, payment_forms_sent_at, created_by, completed_by, priority_only_completion, wellsky_patient_id, wellsky_person_id, wellsky_documents_sent_at, archived_at, archived_by')
      .order('created_at', { ascending: false });
    // Merge in local-only/not-yet-synced sessions so a consultation started
    // fully offline is still visible and resumable before it ever syncs.
    // Local only wins when it holds a genuine unsynced edit — otherwise the
    // server copy is trusted, since it can be ahead of this device now that
    // a client can complete a session remotely without ever touching this
    // device's cache (e.g. status flips to 'completed' via the client's own
    // submission, which this device never locally recorded).
    const localSessions = await getAllLocalSessions();
    const merged = new Map();
    (sessions || []).forEach(s => merged.set(s.id, s));
    localSessions.forEach(s => {
      if (s.synced === false || !merged.has(s.id)) merged.set(s.id, { ...merged.get(s.id), ...s });
    });
    const mergedList = Array.from(merged.values()).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    setManassasSessions(mergedList);

    setLoading(false);
  };

  const loadStaff = async () => {
    const { data } = await supabase.from('profiles').select('id, full_name').in('role', ['owner', 'admin_staff']);
    setStaffUsers(data || []);
  };
  const staffName = (userId) => staffUsers.find(s => s.id === userId)?.full_name || 'Unknown staff';

  React.useEffect(() => { loadData(); loadStaff(); }, []);

  // No realtime subscription on this list — a client finishing a remote
  // consultation while staff already has this tab open wouldn't otherwise
  // show up until something reloads it. Refreshing on refocus covers the
  // common case (staff tabs away, client finishes, staff tabs back).
  React.useEffect(() => {
    const onFocus = () => { if (view === 'list') loadData(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Self-sufficient on just a sessionId (not a cached row) so it can be
  // called equally from the list rows and from inside the wizard, which
  // never loads delivery_mode/secure_token/link_expires_at itself.
  const copyRemoteLink = async (sessionId) => {
    try {
      const { data: session, error: fetchErr } = await supabase
        .from('consultation_sessions')
        .select('id, secure_token, delivery_mode, link_expires_at, remote_priority_only')
        .eq('id', sessionId)
        .single();
      if (fetchErr || !session) throw fetchErr || new Error('Session not found');

      let updated = session;
      // Also re-runs the update when remote_priority_only is still set from
      // an earlier "Send 3 Priority Forms" link on this same session --
      // otherwise the flag never clears and the client keeps seeing only
      // the 3 forms even after staff send this full-10 link instead.
      if (session.delivery_mode !== 'remote' || !session.link_expires_at || new Date(session.link_expires_at) < new Date() || session.remote_priority_only) {
        const linkExpiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
        const { data, error } = await supabase
          .from('consultation_sessions')
          .update({ delivery_mode: 'remote', link_sent_at: new Date().toISOString(), link_expires_at: linkExpiresAt, remote_priority_only: false })
          .eq('id', session.id)
          .select('id, secure_token, link_expires_at')
          .single();
        if (error) throw error;
        updated = { ...session, ...data, delivery_mode: 'remote' };
      }
      const url = `${window.location.origin}/consultation/${updated.id}?token=${updated.secure_token}`;
      await navigator.clipboard.writeText(url);
      showToast('Link copied — send it to the client');
      loadData();
    } catch (err) {
      console.error('Send for remote signature error:', err);
      showToast('Failed to create link', 'error');
    }
  };

  // Every INSERT/UPDATE/DELETE on consultation_sessions/consultation_documents
  // is already captured automatically by the log_activity trigger (see
  // add_consultation_audit_log_triggers.sql) — the one thing a row trigger
  // can't see is a plain read, so opening a session is logged explicitly
  // here. Permitted by activity_log's own "insert my own rows" RLS policy.
  const logSessionView = (sessionId) => {
    if (!profile?.id) return;
    supabase.from('activity_log').insert({
      user_id: profile.id, action: 'VIEW', entity_type: 'consultation_sessions', entity_id: sessionId,
    }).then(({ error }) => { if (error) console.error('Failed to log view:', error); });
  };

  // Time-crunch variant of copyRemoteLink above — same delivery mechanics,
  // but flags the session so the client only ever sees/signs the 3 most
  // urgent documents instead of the full 10 (see remote_priority_only /
  // consultation-client-submit's own copy of PRIORITY_DOCUMENT_KEYS).
  // Always refreshes delivery_mode/expiry/flag unconditionally, unlike
  // copyRemoteLink, since this is meant for the initial "first send" moment.
  const copyRemoteLinkPriorityOnly = async (sessionId) => {
    try {
      const linkExpiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('consultation_sessions')
        .update({ delivery_mode: 'remote', link_sent_at: new Date().toISOString(), link_expires_at: linkExpiresAt, remote_priority_only: true })
        .eq('id', sessionId)
        .select('id, secure_token')
        .single();
      if (error) throw error;
      const url = `${window.location.origin}/consultation/${data.id}?token=${data.secure_token}`;
      await navigator.clipboard.writeText(url);
      showToast('Link copied — only the 3 priority forms will show for the client');
      loadData();
    } catch (err) {
      console.error('Send priority-only remote link error:', err);
      showToast('Failed to create link', 'error');
    }
  };

  if (view === 'manassas_wizard') {
    return (
      <div className="min-h-screen bg-gray-50">
        <ManassasConsultationWizard
          showToast={showToast}
          profile={profile}
          permissions={permissions}
          resumeSessionId={resumeSessionId}
          officeCode={newSessionOfficeCode}
          onSendRemotely={copyRemoteLink}
          onSendRemotelyPriority={copyRemoteLinkPriorityOnly}
          onBack={() => { setResumeSessionId(null); setNewSessionOfficeCode(null); loadData(); setView('list'); }}
          onCompleted={() => { setResumeSessionId(null); setNewSessionOfficeCode(null); loadData(); setView('list'); }}
        />
      </div>
    );
  }

  const officeLabel = (code) => CONSULTATION_OFFICES.find(o => o.code === code)?.label || code;

  const matchesSearch = (s) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (s.client_name || '').toLowerCase().includes(term) || (s.service_address || '').toLowerCase().includes(term);
  };
  const manassasInProgress = manassasSessions.filter(s => s.status === 'draft' && s.delivery_mode !== 'remote' && matchesSearch(s));
  const manassasAwaitingClient = manassasSessions.filter(s => s.status === 'draft' && s.delivery_mode === 'remote' && matchesSearch(s));
  const manassasPendingReview = manassasSessions.filter(s => s.status === 'pending_review' && matchesSearch(s));
  const manassasCompleted = manassasSessions.filter(s => s.status === 'completed' && !s.archived_at && matchesSearch(s));
  const manassasCompleted3 = manassasCompleted.filter(s => s.priority_only_completion);
  const manassasCompleted10 = manassasCompleted.filter(s => !s.priority_only_completion);
  const manassasArchived = manassasSessions.filter(s => s.status === 'completed' && s.archived_at && matchesSearch(s));

  const openSessionDetail = async (session) => {
    if (permissions?.can_view_consultations === false) return;
    logSessionView(session.id);
    setSelectedSession(session);
    setSessionDocsLoading(true);
    setSessionDocs([]);
    const { data: docs } = await supabase
      .from('consultation_documents')
      .select('id, template_key, status, pdf_path, signed_at')
      .eq('session_id', session.id);
    const templates = getTemplatesForOffice(session.office_code || MANASSAS_OFFICE_CODE);
    const withUrls = await Promise.all((docs || []).map(async d => {
      let url = null;
      if (d.pdf_path) {
        const { data: signed } = await supabase.storage.from('consultation-documents').createSignedUrl(d.pdf_path, 3600);
        url = signed?.signedUrl || null;
      }
      const template = templates.find(t => t.key === d.template_key);
      return { ...d, title: template?.title || d.template_key, url };
    }));
    withUrls.sort((a, b) => {
      const ta = templates.findIndex(t => t.key === a.template_key);
      const tb = templates.findIndex(t => t.key === b.template_key);
      return ta - tb;
    });
    setSessionDocs(withUrls);
    setSessionDocsLoading(false);
  };

  // Reopens a "Finalize 3 Forms" completion so staff can go back and finish
  // the remaining 7 documents, then run the normal full Submit & Finish.
  // Only flips status + clears the priority flag -- payment_forms_sent_at is
  // left untouched on purpose so an already-sent "Sent to Sourced" record
  // doesn't need to be resent just because the session got reopened.
  const reopenSession = async (session) => {
    setReopeningSession(true);
    try {
      const { error } = await supabase
        .from('consultation_sessions')
        .update({ status: 'draft', priority_only_completion: false })
        .eq('id', session.id);
      if (error) throw error;
      await markSessionSynced(session.id);
      setSelectedSession(null);
      setResumeSessionId(session.id);
      setView('manassas_wizard');
    } catch (err) {
      console.error('Reopen session error:', err);
      showToast('Failed to reopen consultation', 'error');
    } finally {
      setReopeningSession(false);
    }
  };

  // Uploads a fully-finalized (10/10) consultation's signed PDFs to the
  // matching client's WellSky profile as DocumentReferences. Requires a
  // wellsky_person_id -- openWellskyTransfer resolves one first (either
  // already on the session, or via the manual-match picker) before this
  // runs. wellskyPatientId is passed along too, but only as display/record
  // metadata -- WellSky's upload reference itself is Person/<personId>
  // (confirmed live: Patient/<patientId> gets rejected with a 422).
  const uploadToWellsky = async (sessionId, wellskyPatientId, wellskyPersonId) => {
    setWellskyUploading(true);
    try {
      const { data, error } = await supabase.functions.invoke('wellsky-documents', {
        body: { action: 'upload', sessionId, wellskyPatientId, wellskyPersonId },
      });
      if (error) throw error;
      const uploadedCount = (data?.results || []).filter(r => r.status === 'uploaded' || r.status === 'already_uploaded').length;
      const total = (data?.results || []).length;
      if (data?.ok) {
        showToast(`Sent ${uploadedCount}/${total} documents to WellSky`);
        const sentAt = new Date().toISOString();
        setSelectedSession(prev => (prev && prev.id === sessionId ? { ...prev, wellsky_patient_id: wellskyPatientId, wellsky_person_id: wellskyPersonId, wellsky_documents_sent_at: sentAt } : prev));
        setManassasSessions(prev => prev.map(s => (s.id === sessionId ? { ...s, wellsky_patient_id: wellskyPatientId, wellsky_person_id: wellskyPersonId, wellsky_documents_sent_at: sentAt } : s)));
      } else {
        showToast(`Only ${uploadedCount}/${total} documents sent — try again to retry the rest`, 'error');
        setSelectedSession(prev => (prev && prev.id === sessionId ? { ...prev, wellsky_patient_id: wellskyPatientId, wellsky_person_id: wellskyPersonId } : prev));
        setManassasSessions(prev => prev.map(s => (s.id === sessionId ? { ...s, wellsky_patient_id: wellskyPatientId, wellsky_person_id: wellskyPersonId } : s)));
      }
    } catch (err) {
      console.error('WellSky document transfer error:', err);
      showToast('Failed to transfer documents to WellSky', 'error');
    } finally {
      setWellskyUploading(false);
    }
  };

  const openWellskyTransfer = (session) => {
    if (session.wellsky_person_id) {
      uploadToWellsky(session.id, session.wellsky_patient_id, session.wellsky_person_id);
    } else {
      setWellskyMatchModal({ sessionId: session.id, searchTerm: session.client_name || '', searching: false, results: [] });
    }
  };

  const searchWellskyPatients = async () => {
    if (!wellskyMatchModal) return;
    setWellskyMatchModal(m => ({ ...m, searching: true }));
    try {
      const { data, error } = await supabase.functions.invoke('wellsky-documents', {
        body: { action: 'search', query: wellskyMatchModal.searchTerm },
      });
      if (error) throw error;
      setWellskyMatchModal(m => (m ? { ...m, searching: false, results: data?.results || [] } : m));
    } catch (err) {
      console.error('WellSky patient search error:', err);
      showToast('Search failed', 'error');
      setWellskyMatchModal(m => (m ? { ...m, searching: false } : m));
    }
  };

  const confirmWellskyMatch = (result) => {
    const sessionId = wellskyMatchModal.sessionId;
    setWellskyMatchModal(null);
    uploadToWellsky(sessionId, result.wellskyPatientId, result.wellskyPersonId);
  };

  // Zipped into one file rather than firing off individual downloads —
  // Safari/iOS blocks multiple simultaneous downloads triggered from one click.
  const downloadAllDocuments = async () => {
    const available = sessionDocs.filter(d => d.url);
    if (available.length === 0) return;
    setDownloadingAll(true);
    try {
      const zip = new JSZip();
      // Keeps spaces/casing (unlike the aggressive hyphenated slug used for
      // the zip's own filename below) — just strips characters that are
      // actually illegal in a filename, so entries read as "Client Name -
      // Document Title.pdf" rather than a run-together slug.
      const readableName = (str) => (str || '').replace(/[/\\:*?"<>|]/g, '').trim();
      const clientName = readableName(selectedSession?.client_name) || 'Client';
      await Promise.all(available.map(async (doc) => {
        const res = await fetch(doc.url);
        if (!res.ok) return;
        const blob = await res.blob();
        const docTitle = readableName(doc.title || doc.template_key);
        zip.file(`${clientName} - ${docTitle}.pdf`, blob);
      }));
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const clientLabel = (selectedSession?.client_name || 'consultation').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = `${clientLabel}-documents.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('Download all error:', err);
      showToast('Failed to download documents', 'error');
    } finally {
      setDownloadingAll(false);
    }
  };

  // Staff-initiated only — deliberately not fired automatically on
  // completion (including a client's own remote signing) so a
  // payment-processing email never goes out until an admin has actually
  // reviewed the session and clicked this.
  const sendPaymentForms = async () => {
    if (!selectedSession) return;
    setSendingDocs(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-consultation-payment-forms', { body: { sessionId: selectedSession.id } });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || 'Failed to send');
      if (data.skipped) {
        showToast(data.reason === 'documents_not_ready' ? 'Documents still syncing — try again shortly' : 'Already sent', 'error');
        return;
      }
      showToast('Payment forms sent');
      const sentAt = new Date().toISOString();
      setSelectedSession(prev => (prev ? { ...prev, payment_forms_sent_at: sentAt } : prev));
      setManassasSessions(prev => prev.map(s => (s.id === selectedSession.id ? { ...s, payment_forms_sent_at: sentAt } : s)));
    } catch (err) {
      console.error('Send payment forms error:', err);
      showToast('Failed to send payment forms', 'error');
    } finally {
      setSendingDocs(false);
    }
  };

  // Only reachable once both Sent to Sourced and Sent to WellSky are done
  // (see the button's own guard below) -- moves the session off the main
  // Completed list onto the separate, permission-gated archive page.
  const archiveSession = async () => {
    if (!selectedSession) return;
    setArchivingSession(true);
    try {
      const archivedAt = new Date().toISOString();
      const { error } = await supabase
        .from('consultation_sessions')
        .update({ archived_at: archivedAt, archived_by: profile?.id })
        .eq('id', selectedSession.id);
      if (error) throw error;
      showToast('Consultation archived');
      setManassasSessions(prev => prev.map(s => (s.id === selectedSession.id ? { ...s, archived_at: archivedAt, archived_by: profile?.id } : s)));
      setSelectedSession(null);
    } catch (err) {
      console.error('Archive session error:', err);
      showToast('Failed to archive consultation', 'error');
    } finally {
      setArchivingSession(false);
    }
  };

  // Shared between the main list view and the archive view (both can open a
  // session's detail/delete modals), so defined once here rather than
  // duplicated in each view's JSX below.
  const sessionDetailModal = selectedSession && (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedSession(null)}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <div>
            <p className="font-semibold text-gray-900">
              {selectedSession.client_name}
              {selectedSession.priority_only_completion && (
                <span className="ml-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded align-middle">3 Forms Only</span>
              )}
              {selectedSession.payment_forms_sent_at && (
                <span className="ml-2 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded align-middle">Sent to Sourced</span>
              )}
              {selectedSession.wellsky_documents_sent_at && (
                wellskyClientUrl(selectedSession.wellsky_patient_id) ? (
                  <a
                    href={wellskyClientUrl(selectedSession.wellsky_patient_id)}
                    target="_blank"
                    rel="noreferrer"
                    title="Open this client's WellSky profile"
                    className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded align-middle hover:bg-teal-100 transition"
                  >
                    Sent to WellSky <ExternalLink size={10} />
                  </a>
                ) : (
                  <span className="ml-2 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded align-middle">Sent to WellSky</span>
                )
              )}
              {selectedSession.archived_at && (
                <span className="ml-2 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded align-middle">Archived</span>
              )}
            </p>
            <p className="text-xs text-gray-600">{selectedSession.service_address}</p>
          </div>
          <button onClick={() => setSelectedSession(null)} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </div>

        <div className="flex items-center gap-2 px-4 pt-4 flex-wrap">
          <button
            onClick={downloadAllDocuments}
            disabled={downloadingAll || sessionDocs.every(d => !d.url)}
            className="flex-1 flex items-center justify-center gap-1.5 text-sm font-medium bg-[#2c4f26] text-white px-3 py-2 rounded-lg hover:bg-[#3a6b32] disabled:opacity-50 transition"
          >
            <Download size={15} /> {downloadingAll ? 'Zipping…' : 'Download All'}
          </button>
          <button
            onClick={sendPaymentForms}
            disabled={sendingDocs}
            title={selectedSession.payment_forms_sent_at ? `Sent ${new Date(selectedSession.payment_forms_sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} — click to resend` : 'Email the ACH Payment Form + Late Payment Authorization Agreement to the payment-processing inbox'}
            className={`flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border transition disabled:opacity-50 ${selectedSession.payment_forms_sent_at ? 'text-[#2c4f26] border-[#2c4f26]/30 bg-[#f0f5f0] hover:bg-[#e5efe4]' : 'text-gray-700 border-gray-200 hover:bg-gray-50'}`}
          >
            <Send size={15} /> {sendingDocs ? 'Sending…' : selectedSession.payment_forms_sent_at ? 'Sent to Sourced' : 'Send to Sourced'}
          </button>
          {canFinalizeConsultations && selectedSession.priority_only_completion && (
            <button
              onClick={() => reopenSession(selectedSession)}
              disabled={reopeningSession}
              title="Reopen to fill out the remaining 7 documents, then re-submit all 10"
              className="flex items-center justify-center gap-1.5 text-sm font-medium text-blue-700 border border-blue-200 bg-blue-50 px-3 py-2 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition"
            >
              <Pencil size={15} /> {reopeningSession ? 'Reopening…' : 'Continue Forms'}
            </button>
          )}
          {canFinalizeConsultations && !selectedSession.priority_only_completion && (
            <button
              onClick={() => openWellskyTransfer(selectedSession)}
              disabled={wellskyUploading}
              title={selectedSession.wellsky_documents_sent_at ? `Sent ${new Date(selectedSession.wellsky_documents_sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} — click to resend` : 'Upload all 10 signed documents to this client\'s WellSky profile'}
              className={`flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border transition disabled:opacity-50 ${selectedSession.wellsky_documents_sent_at ? 'text-teal-700 border-teal-200 bg-teal-50 hover:bg-teal-100' : 'text-gray-700 border-gray-200 hover:bg-gray-50'}`}
            >
              <Upload size={15} /> {wellskyUploading ? 'Sending…' : selectedSession.wellsky_documents_sent_at ? 'Sent to WellSky' : 'Transfer to WellSky'}
            </button>
          )}
          {canFinalizeConsultations && selectedSession.payment_forms_sent_at && selectedSession.wellsky_documents_sent_at && !selectedSession.archived_at && (
            <button
              onClick={archiveSession}
              disabled={archivingSession}
              title="Move this consultation to the archive"
              className="flex items-center justify-center gap-1.5 text-sm font-medium text-gray-700 border border-gray-200 px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition"
            >
              <Archive size={15} /> {archivingSession ? 'Archiving…' : 'Archive'}
            </button>
          )}
          {canDeleteConsultations && (
            <button
              onClick={() => setDeletingSessionId(selectedSession.id)}
              className="flex items-center justify-center gap-1.5 text-sm font-medium text-red-600 border border-red-200 px-3 py-2 rounded-lg hover:bg-red-50 transition"
            >
              <Trash2 size={15} /> Delete
            </button>
          )}
        </div>

        <div className="p-4 space-y-2">
          {sessionDocsLoading ? (
            <div className="flex justify-center py-8"><div className="w-6 h-6 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin" /></div>
          ) : sessionDocs.length === 0 ? (
            <p className="text-sm text-gray-600 text-center py-6">No signed documents found.</p>
          ) : sessionDocs.map(doc => (
            <a
              key={doc.id}
              href={doc.url || undefined}
              target="_blank"
              rel="noreferrer"
              download={doc.url ? `${(selectedSession?.client_name || 'Client').replace(/[/\\:*?"<>|]/g, '').trim()} - ${(doc.title || doc.template_key).replace(/[/\\:*?"<>|]/g, '').trim()}.pdf` : undefined}
              className={`flex items-center gap-3 p-3 rounded-lg border ${doc.url ? 'border-gray-200 hover:border-[#2c4f26] hover:bg-[#f7faf6]' : 'border-gray-100 opacity-50 pointer-events-none'} transition`}
            >
              <FileText size={18} className="text-[#2c4f26] shrink-0" />
              <span className="flex-1 text-sm text-gray-800">{doc.title}</span>
              <Download size={15} className="text-gray-400" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );

  const deleteConfirmModal = deletingSessionId && (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !deletingSession && setDeletingSessionId(null)}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 text-center" onClick={e => e.stopPropagation()}>
        <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4"><Trash2 size={22} className="text-red-500" /></div>
        <h3 className="text-lg font-bold text-gray-900 mb-1">Delete this consultation?</h3>
        <p className="text-sm text-gray-700 mb-6">All signed documents in this consultation will be permanently deleted and <span className="font-semibold">cannot be recovered</span>. Make sure you've downloaded any copies you need before continuing.</p>
        <div className="flex gap-3">
          <button
            onClick={() => deleteManassasSession(deletingSessionId)}
            disabled={deletingSession}
            className="flex-1 bg-red-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition"
          >
            {deletingSession ? 'Deleting…' : 'Delete'}
          </button>
          <button onClick={() => setDeletingSessionId(null)} disabled={deletingSession} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition">Cancel</button>
        </div>
      </div>
    </div>
  );

  if (view === 'archive') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => setView('list')}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-1"
            >
              <ChevronLeft size={16} /> Back to Consultations
            </button>
            <h2 className="text-xl font-bold text-gray-900">Consultation Archive</h2>
            <p className="text-sm text-gray-700 mt-0.5">
              {manassasArchived.length} archived agreement{manassasArchived.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search name or address…"
              className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-[#2c4f26]/40"
            />
          </div>
        </div>

        {manassasArchived.length === 0 ? (
          <div className="text-center py-16 text-gray-500 text-sm">No archived consultations yet.</div>
        ) : (
          <div className="space-y-2">
            {manassasArchived.map(s => (
              <div
                key={s.id}
                onClick={() => openSessionDetail(s)}
                className={`bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 ${permissions?.can_view_consultations !== false ? 'cursor-pointer hover:border-[#2c4f26] hover:shadow-sm transition' : ''}`}
              >
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                  <Archive size={18} className="text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {s.client_name}
                    <span className="ml-2 text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded align-middle">{officeLabel(s.office_code)}</span>
                  </p>
                  <p className="text-xs text-gray-700 truncate">
                    {s.service_address || ''}
                    {s.archived_by ? `${s.service_address ? ' · ' : ''}Archived by ${staffName(s.archived_by)}` : ''}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-gray-600">
                    {s.archived_at ? new Date(s.archived_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {sessionDetailModal}
        {deleteConfirmModal}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Care Consultations</h2>
          <p className="text-sm text-gray-700 mt-0.5">
            {manassasCompleted.length} completed agreement{manassasCompleted.length !== 1 ? 's' : ''}
            {manassasInProgress.length > 0 && ` · ${manassasInProgress.length} in progress`}
            {manassasAwaitingClient.length > 0 && ` · ${manassasAwaitingClient.length} awaiting client`}
            {manassasPendingReview.length > 0 && ` · ${manassasPendingReview.length} pending review`}
            {pendingSyncCount > 0 && (
              <button
                type="button"
                onClick={async () => { setSyncingNow(true); await syncPendingConsultations(); setSyncingNow(false); }}
                disabled={syncingNow}
                className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full align-middle hover:bg-amber-100 disabled:opacity-60 transition"
              >
                <span className={`w-1.5 h-1.5 rounded-full bg-amber-500 ${syncingNow ? 'animate-pulse' : ''}`} />
                {syncingNow ? 'Syncing…' : `${pendingSyncCount} pending — tap to sync now`}
              </button>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search name or address…"
              className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-[#2c4f26]/40"
            />
          </div>
          <button
            onClick={async () => { setRefreshing(true); await loadData(); setRefreshing(false); }}
            disabled={refreshing}
            title="Refresh the list"
            className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          </button>
          {canViewConsultationArchive && (
            <button
              onClick={() => setView('archive')}
              title="View archived consultations"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
            >
              <Archive size={15} /> Archive
            </button>
          )}
          <button
            onClick={() => { setResumeSessionId(null); setShowOfficePicker(true); }}
            className="flex items-center gap-2 bg-[#2c4f26] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#3a6b32] transition"
          >
            <Plus size={16} /> New Consultation
          </button>
        </div>
      </div>

      {/* In-progress Manassas sessions */}
      {manassasInProgress.length > 0 && (
        <div className="mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-600 mb-2">In Progress</h3>
          <div className="space-y-2">
            {manassasInProgress.map(s => (
              <div
                key={s.id}
                onClick={() => {
                  if (!canEditSession(s)) { showToast("You don't have permission to edit this consultation", 'error'); return; }
                  logSessionView(s.id); setResumeSessionId(s.id); setView('manassas_wizard');
                }}
                className="w-full text-left bg-white border border-amber-200 rounded-xl p-4 hover:border-amber-400 hover:shadow-sm transition flex items-center gap-3 cursor-pointer"
              >
                <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
                  <ClipboardList size={18} className="text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {s.client_name?.trim() || 'Untitled Consultation'}
                    <span className="ml-2 text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded align-middle">{officeLabel(s.office_code)}</span>
                  </p>
                  <p className="text-xs text-gray-700 truncate">
                    {s.service_address || ''}
                    {s.created_at ? `${s.service_address ? ' · ' : ''}Started ${new Date(s.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
                    {s.created_by ? ` · Created by ${staffName(s.created_by)}` : ''}
                  </p>
                </div>
                <span className="text-xs text-amber-600 font-medium shrink-0">In Progress</span>
                {canDeleteConsultations && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setDeletingSessionId(s.id); }}
                    className="shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Delete this draft"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sent to client, not yet completed */}
      {manassasAwaitingClient.length > 0 && (
        <div className="mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-600 mb-2">Awaiting Client</h3>
          <div className="space-y-2">
            {manassasAwaitingClient.map(s => {
              const expired = s.link_expires_at && new Date(s.link_expires_at) < new Date();
              return (
                <div
                  key={s.id}
                  onClick={() => {
                  if (!canEditSession(s)) { showToast("You don't have permission to edit this consultation", 'error'); return; }
                  logSessionView(s.id); setResumeSessionId(s.id); setView('manassas_wizard');
                }}
                  className="w-full text-left bg-white border border-blue-200 rounded-xl p-4 hover:border-blue-400 hover:shadow-sm transition flex items-center gap-3 cursor-pointer"
                >
                  <div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center shrink-0">
                    <Send size={16} className="text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">
                      {s.client_name?.trim() || 'Untitled Consultation'}
                      <span className="ml-2 text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded align-middle">{officeLabel(s.office_code)}</span>
                    </p>
                    <p className="text-xs text-gray-700 truncate">
                      {s.link_sent_at ? `Sent ${new Date(s.link_sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : 'Link created'}
                      {expired ? ' · Link expired' : s.link_expires_at ? ` · Expires ${new Date(s.link_expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                      {s.created_by ? ` · Created by ${staffName(s.created_by)}` : ''}
                    </p>
                  </div>
                  <span className={`text-xs font-medium shrink-0 ${expired ? 'text-red-600' : 'text-blue-600'}`}>{expired ? 'Expired' : 'Awaiting Client'}</span>
                  {/* Copy/New Link intentionally removed from the list row —
                      the link buttons only live inside the wizard now, once
                      you've opened the client's consultation. */}
                  {canDeleteConsultations && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setDeletingSessionId(s.id); }}
                      className="shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Delete this draft"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Client finished their remote link, but staff hasn't reviewed/finalized it yet */}
      {manassasPendingReview.length > 0 && (
        <div className="mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-600 mb-2">Pending Review</h3>
          <div className="space-y-2">
            {manassasPendingReview.map(s => (
              <div
                key={s.id}
                onClick={() => {
                  if (!canEditSession(s)) { showToast("You don't have permission to edit this consultation", 'error'); return; }
                  logSessionView(s.id); setResumeSessionId(s.id); setView('manassas_wizard');
                }}
                className="w-full text-left bg-white border border-purple-200 rounded-xl p-4 hover:border-purple-400 hover:shadow-sm transition flex items-center gap-3 cursor-pointer"
              >
                <div className="w-10 h-10 rounded-full bg-purple-50 border border-purple-200 flex items-center justify-center shrink-0">
                  <Eye size={18} className="text-purple-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {s.client_name?.trim() || 'Untitled Consultation'}
                    <span className="ml-2 text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded align-middle">{officeLabel(s.office_code)}</span>
                  </p>
                  <p className="text-xs text-gray-700 truncate">
                    {s.service_address || ''}
                    {s.created_by ? `${s.service_address ? ' · ' : ''}Created by ${staffName(s.created_by)}` : ''}
                  </p>
                </div>
                <span className="text-xs text-purple-600 font-medium shrink-0">Client finished — needs review</span>
                {canDeleteConsultations && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setDeletingSessionId(s.id); }}
                    className="shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Delete this consultation"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Manassas sessions — split by how many forms were finalized */}
      {[
        { key: '3', label: '3 Finalized', sessions: manassasCompleted3 },
        { key: '10', label: '10 Finalized', sessions: manassasCompleted10 },
      ].map(group => group.sessions.length > 0 && (
        <div className="mb-4" key={group.key}>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-600 mb-2">{group.label}</h3>
          <div className="space-y-2">
            {group.sessions.map(s => (
              <div
                key={s.id}
                onClick={() => openSessionDetail(s)}
                className={`bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 ${permissions?.can_view_consultations !== false ? 'cursor-pointer hover:border-[#2c4f26] hover:shadow-sm transition' : ''}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#f0f5f0] flex items-center justify-center shrink-0">
                  <CheckCircle size={20} className="text-[#2c4f26]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {s.client_name}
                    <span className="ml-2 text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded align-middle">{officeLabel(s.office_code)}</span>
                    {s.priority_only_completion && (
                      <span className="ml-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded align-middle">3 Forms Only</span>
                    )}
                    {s.payment_forms_sent_at && (
                      <span className="ml-2 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded align-middle">Sent to Sourced</span>
                    )}
                    {s.wellsky_documents_sent_at && (
                      wellskyClientUrl(s.wellsky_patient_id) ? (
                        <a
                          href={wellskyClientUrl(s.wellsky_patient_id)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={e => e.stopPropagation()}
                          title="Open this client's WellSky profile"
                          className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded align-middle hover:bg-teal-100 transition"
                        >
                          Sent to WellSky <ExternalLink size={10} />
                        </a>
                      ) : (
                        <span className="ml-2 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded align-middle">Sent to WellSky</span>
                      )
                    )}
                  </p>
                  <p className="text-xs text-gray-700 truncate">
                    {s.service_address || ''}
                    {s.created_by ? `${s.service_address ? ' · ' : ''}Created by ${staffName(s.created_by)}` : ''}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {s.completed_by ? `Finalized by ${staffName(s.completed_by)}` : ''}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <span className="inline-flex items-center gap-1 text-xs bg-[#f0f5f0] text-[#2c4f26] font-medium px-2 py-0.5 rounded-full">
                    <CheckCircle size={11} /> Complete
                  </span>
                  <p className="text-xs text-gray-600 mt-1">
                    {s.completed_at ? new Date(s.completed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {sessionDetailModal}

      {/* Manual WellSky patient match — only needed the first time a session
          is transferred, since wellsky_patient_id is remembered afterward. */}
      {wellskyMatchModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setWellskyMatchModal(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
              <h3 className="text-lg font-bold text-gray-900">Match to WellSky Patient</h3>
              <button onClick={() => setWellskyMatchModal(null)} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
            </div>
            <div className="p-4 shrink-0">
              <p className="text-xs text-gray-500 mb-3">This consultation isn't linked to a WellSky patient yet. Search by name to find the right one — it'll be remembered for next time.</p>
              <div className="flex gap-2">
                <input
                  autoFocus
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                  placeholder="Search by name…"
                  value={wellskyMatchModal.searchTerm}
                  onChange={e => setWellskyMatchModal(m => ({ ...m, searchTerm: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') searchWellskyPatients(); }}
                />
                <button
                  onClick={searchWellskyPatients}
                  disabled={wellskyMatchModal.searching || !wellskyMatchModal.searchTerm.trim()}
                  className="px-4 py-2 text-sm font-medium bg-[#2c4f26] text-white rounded-lg hover:bg-[#3a6b32] disabled:opacity-50 transition"
                >
                  {wellskyMatchModal.searching ? 'Searching…' : 'Search'}
                </button>
              </div>
            </div>
            <div className="overflow-y-auto px-4 pb-4 space-y-2 flex-1">
              {wellskyMatchModal.results.length === 0 && !wellskyMatchModal.searching && (
                <p className="text-sm text-gray-400 text-center py-6">No results yet — search above.</p>
              )}
              {wellskyMatchModal.results.map(r => (
                <button
                  key={r.wellskyPatientId}
                  onClick={() => confirmWellskyMatch(r)}
                  className="w-full text-left p-3 border border-gray-200 rounded-xl hover:border-[#2c4f26] hover:bg-[#f7faf6] transition"
                >
                  <p className="font-semibold text-gray-900 text-sm">
                    {r.name}
                    <span className={`ml-2 text-xs font-medium px-1.5 py-0.5 rounded-full ${r.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{r.active ? 'Active' : 'Inactive'}</span>
                  </p>
                  {r.address && <p className="text-xs text-gray-600 mt-0.5">{r.address}</p>}
                  {r.birthDate && <p className="text-xs text-gray-400">DOB: {r.birthDate}</p>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {deleteConfirmModal}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-600 text-sm">Loading…</div>
      ) : manassasSessions.length === 0 ? (
        <div className="text-center py-16 text-gray-600">
          <ClipboardList size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No consultations yet. Click "New Consultation" to get started.</p>
        </div>
      ) : (manassasInProgress.length + manassasAwaitingClient.length + manassasPendingReview.length + manassasCompleted.length === 0) && (
        <div className="text-center py-16 text-gray-600">
          <Search size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No consultations match "{searchTerm}".</p>
        </div>
      )}

      {showOfficePicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowOfficePicker(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Which office?</h3>
            <p className="text-sm text-gray-700 mb-5">Choose the office whose documents this consultation uses.</p>
            <div className="space-y-2">
              {CONSULTATION_OFFICES.map(o => (
                <button
                  key={o.code}
                  onClick={() => { setNewSessionOfficeCode(o.code); setShowOfficePicker(false); setResumeSessionId(null); setView('manassas_wizard'); }}
                  className="w-full text-left flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl hover:border-[#2c4f26] hover:bg-[#2c4f26]/5 transition"
                >
                  <span className="font-semibold text-gray-900">{o.label}</span>
                  <span className="text-xs text-gray-500">{o.code}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setShowOfficePicker(false)} className="mt-4 w-full border border-gray-200 text-gray-600 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition">Cancel</button>
          </div>
        </div>
      )}

    </div>
  );
};

// ── Staff: Care Pro Transfer application review (open from "Pending Review") ─
// Deliberately much simpler than ManassasConsultationWizard — no offline
// queue, no IndexedDB. Staff reviews this at a desk, not door-to-door on an
// iPad with spotty signal, so plain direct Supabase reads/writes are enough.
const CareProApplicationReview = ({ application, profile, showToast, onBack }) => {
  const templates = React.useMemo(() => getCareProTemplates(), []);
  const [docOverrides, setDocOverrides] = useState({});
  const [docStatus, setDocStatus] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showReview, setShowReview] = useState(false);
  // Required fields only turn red after a failed "Mark Complete" attempt on
  // the document currently open — not on first view, and not for other docs.
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);

  // Most of these 22 documents are applicant-only (no staffOnly field at
  // all) — the applicant's own submission is already the final PDF, so
  // there's nothing for staff to add or verify beyond what they already
  // signed. Only documents with a real staffOnly field (a company
  // signature, employer-filled section, etc.) need a staff click here.
  const needsStaffAction = (template) => template.fields.some(f => f.staffOnly);

  // These documents' staff sections are always the same person/values —
  // rather than making staff open every application and click through them,
  // they're filled and completed automatically the moment the applicant's
  // own submission makes the document reviewable (status 'applicant_signed'
  // and not yet 'completed' — never fires before the applicant has actually
  // submitted their part, and never re-fires on an already-completed doc).
  const today = () => new Date().toISOString().slice(0, 10);
  const AUTO_STAFF_FILL = {
    pc_scope_of_services: () => ({
      instructor_signature: generateTypedSignatureDataUrl('Kathleen Narron'),
      instructor_date: today(),
    }),
    tb_risk_assessment: () => ({
      completed_by_name: 'Kathleen Narron',
      completed_by_signature: generateTypedSignatureDataUrl('Kathleen Narron'),
      completed_by_title: 'RN',
      assessment_date: today(),
    }),
    scope_of_services_acknowledgement: () => ({
      instructor_signature: generateTypedSignatureDataUrl('Jack St.Clair'),
      instructor_date: today(),
    }),
    driver_status: () => ({
      representative_signature: generateTypedSignatureDataUrl('Jack St.Clair'),
      representative_date: today(),
    }),
    employment_agreement: () => ({
      employer_name: 'Jack St.Clair',
      employer_date: today(),
      employer_signature: generateTypedSignatureDataUrl('Jack St.Clair'),
    }),
    job_description_pca: () => ({
      supervisor_signature: generateTypedSignatureDataUrl('Jack St.Clair'),
      supervisor_date: today(),
    }),
    sworn_disclosure_statement: () => ({
      staff_signature: generateTypedSignatureDataUrl('Jack St.Clair'),
      staff_signature_date: today(),
    }),
  };

  // Shared by the manual "Mark Complete" button and the auto-fill path
  // above — generates the final PDF, uploads it, and marks the document row
  // completed.
  const completeDocument = async (template, values) => {
    const pdfBytes = await fillTemplate(template, values);
    const blob = bytesToBlob(pdfBytes);
    const pdfPath = `applications/${application.id}/${template.key}.pdf`;
    const { error: uploadErr } = await supabase.storage
      .from('care-pro-application-documents')
      .upload(pdfPath, blob, { contentType: 'application/pdf', upsert: true });
    if (uploadErr) throw uploadErr;

    const { error } = await supabase.from('care_pro_application_documents').update({
      field_values: values, status: 'completed', completed_at: new Date().toISOString(), pdf_path: pdfPath,
    }).eq('application_id', application.id).eq('template_key', template.key);
    if (error) throw error;
  };

  const loadDocs = async () => {
    setLoading(true);
    const { data } = await supabase.from('care_pro_application_documents').select('*').eq('application_id', application.id);
    const overrides = {}; const status = {};
    (data || []).forEach(d => { overrides[d.template_key] = d.field_values || {}; status[d.template_key] = d.status; });
    templates.forEach(t => {
      if (!needsStaffAction(t) && status[t.key] !== 'completed') status[t.key] = 'completed';
    });

    for (const template of templates) {
      const autoFill = AUTO_STAFF_FILL[template.key];
      if (!autoFill || status[template.key] !== 'applicant_signed') continue;
      const mergedValues = { ...overrides[template.key], ...autoFill() };
      try {
        await completeDocument(template, mergedValues);
        overrides[template.key] = mergedValues;
        status[template.key] = 'completed';
      } catch (err) {
        console.error(`Auto-fill failed for ${template.key}:`, err);
        // Leave it as applicant_signed — staff can still complete it by hand.
      }
    }

    setDocOverrides(overrides);
    setDocStatus(status);
    const firstIncomplete = templates.findIndex(t => status[t.key] !== 'completed');
    setActiveIndex(firstIncomplete === -1 ? 0 : firstIncomplete);
    // If the auto-fill loop above just finished the last remaining
    // document, land on the same "ready to submit" screen markDocComplete
    // shows when a manual click finishes the last one — otherwise every
    // document ends up reviewed with no way to actually submit.
    if (firstIncomplete === -1) setShowReview(true);
    setLoading(false);
  };
  useEffect(() => { loadDocs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeTemplate = templates[activeIndex];

  const resolveValue = (template, field) => {
    const v = docOverrides[template.key]?.[field.key];
    if (v !== undefined) return v;
    if (field.type === 'checkbox_group') return [];
    return field.default !== undefined ? field.default : '';
  };

  const handleFieldChange = (template, field, value) => {
    if (!field.staffOnly) return; // applicant's fields are read-only here — staff only fills their own
    setDocOverrides(prev => ({ ...prev, [template.key]: { ...prev[template.key], [field.key]: value } }));
  };

  const markDocComplete = async () => {
    const missing = findMissingRequiredField(activeTemplate, f => resolveValue(activeTemplate, f), true);
    if (missing) {
      setShowRequiredErrors(true);
      showToast(`Please fill in "${missing.label}" before marking complete`, 'error');
      return;
    }
    setSaving(true);
    try {
      const values = {};
      activeTemplate.fields.forEach(f => { values[f.key] = resolveValue(activeTemplate, f); });
      await completeDocument(activeTemplate, values);

      const updatedStatus = { ...docStatus, [activeTemplate.key]: 'completed' };
      setDocStatus(updatedStatus);

      const isLast = templates.every(t => updatedStatus[t.key] === 'completed');
      if (isLast) {
        showToast('All documents reviewed — ready to submit', 'success');
        setShowReview(true);
      } else {
        const nextIndex = templates.findIndex((t, i) => i > activeIndex && updatedStatus[t.key] !== 'completed');
        setShowRequiredErrors(false);
        setActiveIndex(nextIndex !== -1 ? nextIndex : templates.findIndex(t => updatedStatus[t.key] !== 'completed'));
        showToast(`${activeTemplate.title} marked complete`, 'success');
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const submitFinal = async () => {
    setSaving(true);
    try {
      await supabase.from('care_pro_applications').update({
        status: 'completed', completed_at: new Date().toISOString(), completed_by: profile?.id || null,
      }).eq('id', application.id);

      // Email the completed document bundle — best-effort. The application
      // is already finalized above, so a failure here shouldn't block or
      // undo that; staff just gets a heads-up to send it manually.
      try {
        const { data: docs } = await supabase
          .from('care_pro_application_documents')
          .select('template_key, pdf_path')
          .eq('application_id', application.id);
        const readableName = (str) => (str || '').replace(/[/\\:*?"<>|]/g, '').trim();
        const applicantName = readableName(application.applicant_name) || 'Care Pro';
        const zip = new JSZip();
        await Promise.all((docs || []).filter(d => d.pdf_path).map(async (d) => {
          const { data: signed } = await supabase.storage.from('care-pro-application-documents').createSignedUrl(d.pdf_path, 3600);
          if (!signed?.signedUrl) return;
          const res = await fetch(signed.signedUrl);
          if (!res.ok) return;
          const blob = await res.blob();
          const template = templates.find(t => t.key === d.template_key);
          const docTitle = readableName(template?.title || d.template_key);
          zip.file(`${applicantName} - ${docTitle}.pdf`, blob);
        }));
        const zipBase64 = await zip.generateAsync({ type: 'base64' });
        const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-care-pro-transfer-documents', {
          body: { applicationId: application.id, applicantName, zipBase64 },
        });
        if (sendError || !sendResult?.ok) throw sendError || new Error(sendResult?.error || 'Send failed');
      } catch (emailErr) {
        console.error('Failed to email transfer documents:', emailErr);
        showToast('Application completed, but the document email failed to send', 'error');
      }

      onBack();
    } catch (err) {
      console.error(err);
      showToast('Failed to submit', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><div className="w-8 h-8 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (showReview) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 bg-[#f0f5f0] rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={28} className="text-[#2c4f26]" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Ready to submit{application.applicant_name ? ` for ${application.applicant_name}` : ''}</h1>
          <p className="text-sm text-gray-600 mb-6">All {templates.length} documents are reviewed, but this application is still pending. Click any document below to double-check it, then submit when everything checks out.</p>
          <div className="text-left divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden mb-6">
            {templates.map((t, i) => (
              <button
                key={t.key}
                type="button"
                onClick={() => { setShowRequiredErrors(false); setActiveIndex(i); setShowReview(false); }}
                className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-white hover:bg-gray-50 transition text-left"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <CheckCircle size={15} className="text-green-600 shrink-0" />
                  <span className="text-sm text-gray-800 truncate">{t.title}</span>
                </span>
                <ChevronRight size={14} className="text-gray-300" />
              </button>
            ))}
          </div>
          <div className="flex gap-3 justify-center">
            <button onClick={onBack} disabled={saving} className="border border-gray-200 text-gray-600 px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 transition">
              Leave Pending
            </button>
            <button onClick={submitFinal} disabled={saving} className="bg-[#2c4f26] text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#3a6b32] disabled:opacity-50 transition">
              {saving ? 'Submitting…' : 'Submit & Finish'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const completedCount = templates.filter(t => docStatus[t.key] === 'completed').length;
  const activeCompleted = docStatus[activeTemplate.key] === 'completed';

  return (
    <div className="flex flex-col lg:flex-row gap-6 max-w-6xl mx-auto px-4 py-6">
      <div className="lg:w-64 shrink-0">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ChevronLeft size={16} /> Back to List
        </button>
        <div className="bg-white border border-gray-200 rounded-xl p-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2 px-1">{completedCount}/{templates.length} Reviewed</p>
          <div className="space-y-1">
            {templates.map((t, i) => {
              const done = docStatus[t.key] === 'completed';
              const active = i === activeIndex;
              const needsSig = !done && needsStaffAction(t);
              return (
                <button
                  key={t.key}
                  onClick={() => { setShowRequiredErrors(false); setActiveIndex(i); }}
                  className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm transition ${active ? 'bg-[#2c4f26] text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  {done ? <CheckCircle size={14} className={`shrink-0 ${active ? 'text-white' : 'text-green-600'}`} /> : <Clock size={14} className={`shrink-0 ${active ? 'text-white' : needsSig ? 'text-amber-500' : 'text-gray-400'}`} />}
                  <span className="truncate flex-1">{t.title}</span>
                  {needsSig && (
                    <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'}`}>
                      Needs signature
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-xl p-4 md:p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-1">{activeTemplate.title}</h2>
        <p className="text-xs text-gray-500 mb-4">
          {activeCompleted ? 'Already reviewed — shown for your records.' : "Applicant's answers are read-only. Fill any of your own sections, then mark complete."}
        </p>

        <TemplatePdfViewer
          template={activeTemplate}
          resolveValue={(field) => resolveValue(activeTemplate, field)}
          onFieldChange={(field, value) => handleFieldChange(activeTemplate, field, value)}
          signatureLibrary={{}}
          onSignatureCaptured={() => {}}
          isFieldEditable={(field) => !activeCompleted && !!field.staffOnly}
          showRequiredErrors={showRequiredErrors}
        />

        {!activeCompleted && (
          <div className="flex items-center justify-end mt-8 pt-5 border-t border-gray-100">
            <button
              onClick={markDocComplete}
              disabled={saving}
              className="flex items-center gap-2 bg-[#2c4f26] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#3a6b32] disabled:opacity-50 transition"
            >
              {saving ? 'Saving…' : 'Mark Complete'}
              {!saving && <ChevronRight size={16} />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Staff: Care Pro Transfer tab ──────────────────────────────────────────────
const CareProTransferTab = ({ showToast, permissions, profile }) => {
  const isOwner = profile?.role === 'owner';
  const canEdit = isOwner || permissions?.can_edit_applications;
  const canDelete = isOwner || permissions?.can_delete_applications;
  const [view, setView] = useState('list');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailDocs, setDetailDocs] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [sendingDocs, setSendingDocs] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('care_pro_applications')
      .select('id, applicant_name, applicant_email, applicant_phone, status, created_at, submitted_at, completed_at, secure_token')
      .order('created_at', { ascending: false });
    setApplications(data || []);
    setLoading(false);
  };
  useEffect(() => { loadData(); }, []);
  useEffect(() => {
    const onFocus = () => { if (view === 'list') loadData(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const matchesSearch = (a) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (a.applicant_name || '').toLowerCase().includes(term) || (a.applicant_email || '').toLowerCase().includes(term);
  };

  // Every page load mints a row, even if the visitor never types anything
  // (a bot, an accidental double-open, someone who just looks and leaves) —
  // only surface ones that got far enough to actually identify who it is.
  const incomplete = applications.filter(a => a.status === 'incomplete' && a.applicant_name?.trim() && matchesSearch(a));
  const pending = applications.filter(a => a.status === 'pending' && matchesSearch(a));
  const completed = applications.filter(a => a.status === 'completed' && matchesSearch(a));

  // Lets staff hand an applicant a link straight back to THEIR in-progress
  // application (draft progress and all) instead of relying purely on that
  // applicant's own browser remembering it — covers a lost/cleared session,
  // a device switch, or staff picking up where someone got stuck.
  const copyResumeLink = async (application) => {
    if (!application.secure_token) { showToast('No resume token on this application', 'error'); return; }
    const url = `${window.location.origin}/onboarding-application?id=${application.id}&token=${application.secure_token}`;
    await navigator.clipboard.writeText(url);
    showToast('Resume link copied — send it to the applicant');
  };

  const openDetail = async (application) => {
    setSelectedDetail(application);
    setDetailLoading(true);
    setDetailDocs([]);
    const templates = getCareProTemplates();
    const { data: docs } = await supabase
      .from('care_pro_application_documents')
      .select('id, template_key, status, pdf_path, completed_at, applicant_signed_at')
      .eq('application_id', application.id);
    const withUrls = await Promise.all((docs || []).map(async d => {
      let url = null;
      if (d.pdf_path) {
        const { data: signed } = await supabase.storage.from('care-pro-application-documents').createSignedUrl(d.pdf_path, 3600);
        url = signed?.signedUrl || null;
      }
      const template = templates.find(t => t.key === d.template_key);
      return { ...d, title: template?.title || d.template_key, url };
    }));
    setDetailDocs(withUrls);
    setDetailLoading(false);
  };

  const downloadAllDocuments = async () => {
    const available = detailDocs.filter(d => d.url);
    if (available.length === 0) return;
    setDownloadingAll(true);
    try {
      const zip = new JSZip();
      // Keeps spaces/casing (unlike the aggressive hyphenated slug used for
      // the zip's own filename below) — just strips characters that are
      // actually illegal in a filename, so entries read as "Applicant Name -
      // Document Title.pdf" rather than a run-together slug.
      const readableName = (str) => (str || '').replace(/[/\\:*?"<>|]/g, '').trim();
      const applicantName = readableName(selectedDetail?.applicant_name) || 'Applicant';
      await Promise.all(available.map(async (doc) => {
        const res = await fetch(doc.url);
        if (!res.ok) return;
        const blob = await res.blob();
        const docTitle = readableName(doc.title || doc.template_key);
        zip.file(`${applicantName} - ${docTitle}.pdf`, blob);
      }));
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const nameLabel = (selectedDetail?.applicant_name || 'application').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = `${nameLabel}-documents.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error(err);
      showToast('Failed to download documents', 'error');
    } finally {
      setDownloadingAll(false);
    }
  };

  // Manual, repeatable trigger for the same email submitFinal fires once
  // automatically — lets staff (re)send a completed application's document
  // zip without needing to touch the one-time finalize action again.
  const sendDocuments = async () => {
    const available = detailDocs.filter(d => d.url);
    if (available.length === 0) return;
    setSendingDocs(true);
    try {
      const readableName = (str) => (str || '').replace(/[/\\:*?"<>|]/g, '').trim();
      const applicantName = readableName(selectedDetail?.applicant_name) || 'Care Pro';
      const zip = new JSZip();
      await Promise.all(available.map(async (doc) => {
        const res = await fetch(doc.url);
        if (!res.ok) return;
        const blob = await res.blob();
        const docTitle = readableName(doc.title || doc.template_key);
        zip.file(`${applicantName} - ${docTitle}.pdf`, blob);
      }));
      const zipBase64 = await zip.generateAsync({ type: 'base64' });
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-care-pro-transfer-documents', {
        body: { applicationId: selectedDetail.id, applicantName, zipBase64 },
      });
      if (sendError || !sendResult?.ok) throw sendError || new Error(sendResult?.error || 'Send failed');
      showToast('Documents emailed');
    } catch (err) {
      console.error('Send documents error:', err);
      showToast('Failed to email documents', 'error');
    } finally {
      setSendingDocs(false);
    }
  };

  const deleteApplication = async (id) => {
    setDeleting(true);
    try {
      const { data: docs } = await supabase.from('care_pro_application_documents').select('pdf_path').eq('application_id', id);
      const paths = (docs || []).map(d => d.pdf_path).filter(Boolean);
      if (paths.length > 0) await supabase.storage.from('care-pro-application-documents').remove(paths);
      const { error } = await supabase.from('care_pro_applications').delete().eq('id', id);
      if (error) throw error;
      showToast('Application deleted');
    } catch (err) {
      console.error('Delete application error:', err);
      showToast('Failed to delete', 'error');
    } finally {
      setDeletingId(null);
      setDeleting(false);
      setSelectedDetail(null);
      loadData();
    }
  };

  if (view === 'review' && selectedApplication) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CareProApplicationReview
          application={selectedApplication}
          profile={profile}
          showToast={showToast}
          onBack={() => { setSelectedApplication(null); setView('list'); loadData(); }}
        />
      </div>
    );
  }

  const fmt = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Care Pro Transfer</h1>
          <p className="text-sm text-gray-500 mt-0.5">{pending.length} pending review · {completed.length} completed</p>
        </div>
        <button
          onClick={async () => { setRefreshing(true); await loadData(); setRefreshing(false); }}
          disabled={refreshing}
          title="Refresh the list"
          className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
        />
      </div>

      <div className="bg-[#f0f5f0] border border-[#2c4f26]/20 rounded-xl p-4 mb-6">
        <p className="text-sm text-gray-700">Send care pros this link to fill out and sign their onboarding paperwork:</p>
        <div className="flex items-center gap-2 mt-2">
          <code className="flex-1 text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 text-gray-800 overflow-x-auto">{window.location.origin}/onboarding-application</code>
          <button
            onClick={async () => { await navigator.clipboard.writeText(`${window.location.origin}/onboarding-application`); showToast('Link copied'); }}
            className="shrink-0 flex items-center gap-1.5 text-sm font-medium text-[#2c4f26] border border-[#2c4f26]/30 px-3 py-2 rounded-lg hover:bg-white transition"
          >
            <Send size={13} /> Copy Link
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-600 text-sm">Loading…</div>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-600 mb-2">Pending Review</h3>
              <div className="space-y-2">
                {pending.map(a => (
                  <div
                    key={a.id}
                    onClick={() => { if (canEdit) { setSelectedApplication(a); setView('review'); } }}
                    className={`w-full text-left bg-white border border-amber-200 rounded-xl p-4 hover:border-amber-400 hover:shadow-sm transition flex items-center gap-3 ${canEdit ? 'cursor-pointer' : ''}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
                      <ClipboardList size={18} className="text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{a.applicant_name?.trim() || 'Unnamed applicant'}</p>
                      <p className="text-xs text-gray-700 truncate">{a.applicant_email || ''}{a.submitted_at ? `${a.applicant_email ? ' · ' : ''}Submitted ${fmt(a.submitted_at)}` : ''}</p>
                    </div>
                    <span className="text-xs text-amber-600 font-medium shrink-0">Pending Review</span>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setDeletingId(a.id); }}
                        className="shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Delete this application"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {incomplete.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-600 mb-2">Incomplete</h3>
              <div className="space-y-2">
                {incomplete.map(a => (
                  <div key={a.id} className="w-full bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3 opacity-75">
                    <div className="w-10 h-10 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center shrink-0">
                      <Clock size={18} className="text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{a.applicant_name?.trim() || 'Not yet identified'}</p>
                      <p className="text-xs text-gray-700 truncate">{a.applicant_email || 'Started but not finished'} · Started {fmt(a.created_at)}</p>
                    </div>
                    <span className="text-xs text-gray-500 font-medium shrink-0">Incomplete</span>
                    <button
                      type="button"
                      onClick={() => copyResumeLink(a)}
                      className="shrink-0 p-1.5 text-gray-400 hover:text-[#2c4f26] hover:bg-green-50 rounded-lg transition"
                      title="Copy a link so they can resume this application"
                    >
                      <Send size={16} />
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => setDeletingId(a.id)}
                        className="shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Delete this application"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {completed.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-600 mb-2">Completed</h3>
              <div className="space-y-2">
                {completed.map(a => (
                  <div
                    key={a.id}
                    onClick={() => openDetail(a)}
                    className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:border-[#2c4f26] hover:shadow-sm transition"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#f0f5f0] flex items-center justify-center shrink-0">
                      <CheckCircle size={20} className="text-[#2c4f26]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{a.applicant_name}</p>
                      <p className="text-xs text-gray-700 truncate">{a.applicant_email || ''}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="inline-flex items-center gap-1 text-xs bg-[#f0f5f0] text-[#2c4f26] font-medium px-2 py-0.5 rounded-full">
                        <CheckCircle size={11} /> Complete
                      </span>
                      <p className="text-xs text-gray-600 mt-1">{fmt(a.completed_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {applications.length === 0 && (
            <div className="text-center py-16 text-gray-600">
              <ClipboardList size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No applications yet. Share the link above to get started.</p>
            </div>
          )}
        </>
      )}

      {selectedDetail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedDetail(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100 flex items-center justify-between shrink-0">
              <div>
                <h3 className="font-bold text-gray-900">{selectedDetail.applicant_name}</h3>
                <p className="text-xs text-gray-500">{selectedDetail.applicant_email}</p>
              </div>
              <button onClick={() => setSelectedDetail(null)} className="text-gray-400 hover:text-gray-600 transition"><X size={20} /></button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {detailLoading ? (
                <div className="flex justify-center py-8"><div className="w-6 h-6 border-4 border-[#2c4f26] border-t-transparent rounded-full animate-spin" /></div>
              ) : (
                <div className="space-y-2">
                  {detailDocs.map(d => (
                    <div key={d.id} className="flex items-center justify-between px-3 py-2.5 border border-gray-100 rounded-lg">
                      <span className="text-sm text-gray-800">{d.title}</span>
                      {d.url ? (
                        <a
                          href={d.url}
                          target="_blank"
                          rel="noreferrer"
                          download={`${(selectedDetail?.applicant_name || 'Applicant').replace(/[/\\:*?"<>|]/g, '').trim()} - ${(d.title || d.template_key).replace(/[/\\:*?"<>|]/g, '').trim()}.pdf`}
                          className="text-xs font-medium text-[#2c4f26] hover:underline flex items-center gap-1"
                        ><Download size={12} /> Download</a>
                      ) : (
                        <span className="text-xs text-gray-400">No file</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-5 border-t border-gray-100 shrink-0 flex gap-3">
              <button
                onClick={downloadAllDocuments}
                disabled={downloadingAll || detailDocs.every(d => !d.url)}
                className="flex-1 flex items-center justify-center gap-2 bg-[#2c4f26] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#3a6b32] disabled:opacity-50 transition"
              >
                {downloadingAll ? 'Zipping…' : <><Download size={14} /> Download All</>}
              </button>
              {selectedDetail.status === 'completed' && (
                <button
                  onClick={sendDocuments}
                  disabled={sendingDocs || detailDocs.every(d => !d.url)}
                  className="flex-1 flex items-center justify-center gap-2 border border-[#2c4f26] text-[#2c4f26] py-2.5 rounded-lg text-sm font-semibold hover:bg-[#f0f5f0] disabled:opacity-50 transition"
                >
                  {sendingDocs ? 'Sending…' : <><Send size={14} /> Send Docs</>}
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => setDeletingId(selectedDetail.id)}
                  className="px-4 py-2.5 border border-red-200 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-50 transition"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {deletingId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !deleting && setDeletingId(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 text-center" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4"><Trash2 size={22} className="text-red-500" /></div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Delete this application?</h3>
            <p className="text-sm text-gray-700 mb-6">All submitted documents will be permanently deleted and <span className="font-semibold">cannot be recovered</span>. Make sure you've downloaded any copies you need before continuing.</p>
            <div className="flex gap-3">
              <button
                onClick={() => deleteApplication(deletingId)}
                disabled={deleting}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button onClick={() => setDeletingId(null)} disabled={deleting} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Internal-only tracker for candidates moving through Paradox's hiring
// pipeline (offer -> onboarding -> hired), synced by the paradox-sync edge
// function on a schedule. No candidate ever sees this; it never writes back
// to Paradox. Stall thresholds are starting guesses, not tuned yet.
const HIRING_STAGE_ORDER = ['offer', 'onboarding', 'hired'];
const HIRING_STAGE_LABELS = { offer: 'Offer Sent', onboarding: 'Onboarding', hired: 'Hired', rejected: 'Rejected', early_pipeline: 'Earlier in Pipeline' };
const HIRING_STALL_DAYS = { offer: 5, onboarding: 7 };

const hiringDaysSince = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
};

const hiringFmtDuration = (iso) => {
  const days = hiringDaysSince(iso);
  if (days === null) return '—';
  if (days === 0) return 'Today';
  if (days === 1) return '1 day';
  return `${days} days`;
};

const hiringFmtAgo = (iso) => {
  const days = hiringDaysSince(iso);
  if (days === null) return '—';
  if (days === 0) return 'just now';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
};

// Minute/hour-precision version, specifically for the Paradox sync
// freshness indicator -- the sync runs every 30 minutes, so hiringFmtAgo's
// day-level rounding would show "just now" for up to 24 hours regardless of
// whether the last sync was 2 minutes or 23 hours ago, silently hiding a
// stalled cron job.
const hiringFmtSyncAgo = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / (1000 * 60));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

// Position within Paradox's own hiring funnel, derived from the live
// candidate_journey_status prefixes seen in the account (Capture/Application
// collapse into "Applied" since both just mean "hasn't reached an interview
// yet"). Rejected/unknown statuses have no place on this bar — we only know
// the CURRENT status, not which step a rejected candidate had reached.
const HIRING_FUNNEL_STEPS = ['Applied', 'Interview', 'Offer', 'Onboarding', 'Hired'];
const hiringFunnelStepIndex = (status) => {
  if (!status) return null;
  if (status.startsWith('Capture') || status.startsWith('Application')) return 0;
  if (status.startsWith('Interview') || status.startsWith('2nd Interview') || status.startsWith('Candidate Review')) return 1;
  if (status.startsWith('Offers')) return 2;
  if (status.startsWith('Onboarding')) return 3;
  if (status === 'Hire: Hired') return 4;
  return null;
};

const HiringProgressBar = ({ status, size = 'sm' }) => {
  const idx = hiringFunnelStepIndex(status);
  if (idx === null) {
    return <span className="text-xs text-red-600 font-medium">No longer active — {status || 'unknown status'}</span>;
  }
  const steps = HIRING_FUNNEL_STEPS;
  const lastI = steps.length - 1;
  const pct = (idx / lastI) * 100;
  const dotSize = size === 'lg' ? 12 : 8;
  return (
    <div className={size === 'lg' ? 'w-full' : 'w-32'}>
      <div className="relative" style={{ height: dotSize }}>
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-0.5 bg-gray-200 rounded" />
        <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 bg-teal-600 rounded" style={{ width: `${pct}%` }} />
        {steps.map((label, i) => (
          <div
            key={label}
            title={label}
            className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full ${i <= idx ? 'bg-teal-600' : 'bg-gray-200'}`}
            style={{ left: `${(i / lastI) * 100}%`, width: dotSize, height: dotSize }}
          />
        ))}
      </div>
      {size === 'lg' && (
        <div className="relative mt-1.5" style={{ height: 14 }}>
          {steps.map((label, i) => (
            <span
              key={label}
              className="absolute text-[11px] text-gray-500 whitespace-nowrap"
              style={{ left: `${(i / lastI) * 100}%`, transform: `translateX(${i === 0 ? '0%' : i === lastI ? '-100%' : '-50%'})` }}
            >
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const HIRING_DETAIL_FIELDS = [
  ['job_req_id', 'Job Req ID'],
  ['referred_by', 'Referred By'],
  ['referrer_name', 'Referrer Name'],
  ['referrer_email', 'Referrer Email'],
  ['talent_community', 'Talent Community'],
  ['language_preference', 'Language Preference'],
  ['primary_contact_method', 'Preferred Contact'],
  ['hirevue_link', 'HireVue Link'],
  ['pymetrics_link', 'Pymetrics Link'],
  ['created_at', 'Created In Paradox'],
];

const HiringCandidateModal = ({ candidate, onClose, onScheduleOrientation, alreadyScheduled }) => {
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRaw, setShowRaw] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [savingAnswers, setSavingAnswers] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [downloadState, setDownloadState] = useState({ status: 'idle', message: '' });

  // Calls paradox-fetch-docs, which fetches the candidate's full document
  // zip directly from Paradox's own API server-side (no local helper, no
  // browser automation -- see that function's own comments for how the
  // underlying Paradox flow works). Works for any authorized staff member
  // from anywhere, since it's a plain authenticated HTTP call.
  const downloadApplication = async () => {
    if (!candidate.paradox_uuid) return;
    setDownloadState({ status: 'loading', message: '' });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/paradox-fetch-docs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ uuid: candidate.paradox_uuid }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const error = new Error(err.error || `Download failed (${res.status})`);
        error.code = err.code;
        throw error;
      }
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `${candidate.name || 'candidate'}-documents.zip`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDownloadState({ status: 'ok', message: `Downloaded ${filename}` });
    } catch (err) {
      if (err.code === 'paradox_session_expired') {
        setDownloadState({ status: 'session_expired', message: '' });
      } else {
        setDownloadState({ status: 'error', message: err.message || 'Download failed' });
      }
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.from('paradox_candidates').select('raw').eq('id', candidate.id).single();
      if (active) { setRaw(data?.raw?.candidate || null); setLoading(false); }
    })();
    return () => { active = false; };
  }, [candidate.id]);

  useEffect(() => {
    let active = true;
    (async () => {
      const [{ data: q }, { data: a }] = await Promise.all([
        supabase.from('interview_question_templates').select('*').order('order_index'),
        supabase.from('candidate_interview_answers').select('question_id, answer_text').eq('paradox_candidate_id', candidate.id),
      ]);
      if (!active) return;
      setQuestions(q || []);
      const answerMap = {};
      (a || []).forEach(row => { answerMap[row.question_id] = row.answer_text; });
      setAnswers(answerMap);
    })();
    return () => { active = false; };
  }, [candidate.id]);

  const saveAnswers = async () => {
    setSavingAnswers(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const rows = questions.map(q => ({
        paradox_candidate_id: candidate.id,
        question_id: q.id,
        answer_text: answers[q.id] || '',
        answered_by: user?.id || null,
        answered_at: new Date().toISOString(),
      }));
      const { error } = await supabase.from('candidate_interview_answers').upsert(rows, { onConflict: 'paradox_candidate_id,question_id' });
      if (error) throw error;
    } catch (err) {
      console.error('saveAnswers error:', err);
    } finally {
      setSavingAnswers(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{candidate.name || '(no name)'}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{candidate.job_title || '—'}</p>
            {candidate.paradox_uuid && (
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <a
                  href={`https://olivia.paradox.ai/candidates/all-candidates?selected=${candidate.paradox_uuid}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-teal-700 hover:underline"
                >
                  Open in Paradox <ArrowRight size={11} />
                </a>
                <button
                  onClick={downloadApplication}
                  disabled={downloadState.status === 'loading'}
                  type="button"
                  title="Downloads every form on file for this candidate as a zip -- can take ~10-15s while Paradox generates it"
                  className="inline-flex items-center gap-1 text-xs text-teal-700 hover:underline disabled:opacity-50"
                >
                  {downloadState.status === 'loading' ? 'Downloading…' : 'Download Application'} <Download size={11} />
                </button>
              </div>
            )}
            {downloadState.status === 'ok' && <p className="text-xs text-green-600 mt-1">{downloadState.message}</p>}
            {downloadState.status === 'error' && <p className="text-xs text-red-600 mt-1">{downloadState.message}</p>}
            {downloadState.status === 'session_expired' && (
              <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <span className="font-semibold">Paradox session expired.</span> Ask an admin to run <code className="bg-amber-100 px-1 rounded">npm run refresh-token</code> in <code className="bg-amber-100 px-1 rounded">scripts/paradox-download-docs</code>, then try again.
              </div>
            )}
          </div>
          <button onClick={onClose} type="button" className="text-gray-400 hover:text-gray-600 transition"><X size={22} /></button>
        </div>
        <div className="overflow-y-auto p-6 space-y-5 flex-1">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Paradox Progress</p>
            <HiringProgressBar status={candidate.candidate_journey_status} size="lg" />
            <p className="text-sm text-gray-600 mt-2">{candidate.candidate_journey_status || '—'}</p>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-400">Email</p>
              <p className="text-gray-900 break-words">{candidate.email || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Phone</p>
              <p className="text-gray-900">{candidate.phone || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Time in current status</p>
              <p className="text-gray-900">{hiringFmtDuration(candidate.status_changed_at)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Last activity in Paradox</p>
              <p className="text-gray-900">{hiringFmtAgo(candidate.paradox_updated_at)}</p>
            </div>
          </div>

          {candidate.interview_history?.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Interview History</p>
              <div className="space-y-2">
                {candidate.interview_history.map((iv, i) => (
                  <div key={i} className="text-sm bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 capitalize">{iv.interview_type || 'Interview'}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${
                        iv.interview_status === 'completed' ? 'bg-green-100 text-green-700' :
                        iv.interview_status === 'canceled' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{iv.interview_status || 'pending'}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {iv.scheduled_at ? new Date(iv.scheduled_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Not yet scheduled'}
                      {iv.attendees?.[0]?.name ? ` · with ${iv.attendees[0].name}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {questions.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Interview Questions</p>
              <div className="space-y-3">
                {questions.map(q => (
                  <div key={q.id}>
                    <label className="text-sm text-gray-700">{q.question_text}</label>
                    <textarea
                      value={answers[q.id] || ''}
                      onChange={(e) => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                      rows={2}
                      className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                ))}
              </div>
              <button
                onClick={saveAnswers}
                disabled={savingAnswers}
                className="mt-2 text-xs font-medium text-teal-700 border border-teal-300 px-3 py-1.5 rounded-lg hover:bg-teal-50 disabled:opacity-50 transition"
              >
                {savingAnswers ? 'Saving…' : 'Save Answers'}
              </button>
            </div>
          )}

          {onScheduleOrientation && (
            <div className="border-t border-gray-100 pt-4">
              {alreadyScheduled ? (
                <span className="inline-flex items-center gap-1 text-sm text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg">
                  <CheckCircle size={14} /> Already scheduled into orientation
                </span>
              ) : (
                <button
                  onClick={async () => { setScheduling(true); await onScheduleOrientation(candidate); setScheduling(false); onClose(); }}
                  disabled={scheduling}
                  className="w-full bg-[#2c4f26] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#3a6b32] disabled:opacity-50 transition"
                >
                  {scheduling ? 'Scheduling…' : 'Schedule into Orientation'}
                </button>
              )}
            </div>
          )}

          {loading ? (
            <p className="text-sm text-gray-400">Loading more from Paradox…</p>
          ) : raw ? (
            <div className="grid grid-cols-2 gap-4 text-sm border-t border-gray-100 pt-4">
              {HIRING_DETAIL_FIELDS.filter(([key]) => raw[key]).map(([key, label]) => (
                <div key={key}>
                  <p className="text-xs text-gray-400">{label}</p>
                  <p className="text-gray-900 break-words">{raw[key]}</p>
                </div>
              ))}
            </div>
          ) : null}
          <div className="border-t border-gray-100 pt-4">
            <button onClick={() => setShowRaw(v => !v)} className="text-xs text-teal-700 font-medium hover:underline">
              {showRaw ? 'Hide' : 'Show'} raw Paradox data
            </button>
            {showRaw && (
              <pre className="mt-2 p-3 bg-gray-50 rounded-lg text-[11px] text-gray-600 overflow-x-auto max-h-64">
                {JSON.stringify(raw, null, 2)}
              </pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const InterviewQuestionsModal = ({ onClose, showToast }) => {
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await supabase.from('interview_question_templates').select('*').order('order_index');
    setQuestions(data || []);
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const addQuestion = async () => {
    const text = newQuestion.trim();
    if (!text) return;
    const { error } = await supabase.from('interview_question_templates').insert({ question_text: text, order_index: questions.length });
    if (error) { showToast('Failed to add question', 'error'); return; }
    setNewQuestion('');
    load();
  };

  const deleteQuestion = async (id) => {
    const { error } = await supabase.from('interview_question_templates').delete().eq('id', id);
    if (error) { showToast('Failed to delete question', 'error'); return; }
    load();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100 flex items-center justify-between shrink-0">
          <h2 className="text-xl font-bold text-gray-900">Interview Questions</h2>
          <button onClick={onClose} type="button" className="text-gray-400 hover:text-gray-600 transition"><X size={22} /></button>
        </div>
        <div className="overflow-y-auto p-6 space-y-3 flex-1">
          {loading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : questions.length === 0 ? (
            <p className="text-sm text-gray-400">No interview questions defined yet.</p>
          ) : questions.map(q => (
            <div key={q.id} className="flex items-center justify-between gap-2 border border-gray-200 rounded-lg p-3">
              <p className="text-sm text-gray-800">{q.question_text}</p>
              <button onClick={() => deleteQuestion(q.id)} className="shrink-0 text-gray-400 hover:text-red-600 transition"><Trash2 size={16} /></button>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-2">
            <input
              type="text"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              placeholder="New interview question…"
              className="flex-1 border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <button onClick={addQuestion} className="shrink-0 bg-[#2c4f26] text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#3a6b32] transition">Add</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CALENDAR_START_HOUR = 7;
const CALENDAR_END_HOUR = 19;
const CALENDAR_HOUR_HEIGHT = 100;
const CALENDAR_DAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

// One color per interviewer, assigned deterministically (same person always
// gets the same color, no matter how many are in view) via a simple string
// hash rather than assignment order, which would shift every time the set
// of interviewers in view changes.
const INTERVIEWER_COLORS = [
  'bg-teal-600', 'bg-blue-600', 'bg-purple-600', 'bg-rose-600', 'bg-amber-600',
  'bg-indigo-600', 'bg-emerald-600', 'bg-orange-600', 'bg-cyan-600', 'bg-fuchsia-600',
];

const colorForInterviewer = (name) => {
  if (!name) return 'bg-gray-500';
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return INTERVIEWER_COLORS[hash % INTERVIEWER_COLORS.length];
};

const startOfWeek = (d) => {
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  s.setDate(s.getDate() - s.getDay());
  return s;
};

// Same-day events that overlap in time get placed side-by-side (like a
// normal calendar) instead of stacked on top of each other at full width —
// group into overlapping clusters, greedily assign each a column within
// its cluster, then size every event in that cluster to 1/columnCount wide.
const layoutDayEvents = (events) => {
  const sorted = [...events].sort((a, b) => a.startMinutes - b.startMinutes);
  const clusters = [];
  let current = [];
  let clusterEnd = -Infinity;
  sorted.forEach(e => {
    if (current.length === 0 || e.startMinutes < clusterEnd) {
      current.push(e);
      clusterEnd = Math.max(clusterEnd, e.endMinutes);
    } else {
      clusters.push(current);
      current = [e];
      clusterEnd = e.endMinutes;
    }
  });
  if (current.length) clusters.push(current);

  const positioned = [];
  clusters.forEach(cluster => {
    const colEnds = [];
    const withCols = cluster.map(e => {
      let col = colEnds.findIndex(end => end <= e.startMinutes);
      if (col === -1) { col = colEnds.length; colEnds.push(e.endMinutes); }
      else colEnds[col] = e.endMinutes;
      return { ...e, col };
    });
    const totalCols = colEnds.length;
    withCols.forEach(e => positioned.push({ ...e, totalCols }));
  });
  return positioned;
};

// Real week-grid calendar over the interview_history Paradox already syncs
// onto each candidate (see paradox-sync) — no new data source needed, just
// laying out what's already cached the way Paradox's own calendar does.
const InterviewCalendarView = ({ profile, showToast }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [interviewerFilter, setInterviewerFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('paradox_candidates')
      .select('id, name, job_title, interview_history')
      .not('interview_history', 'is', null);
    if (error) { showToast('Failed to load interviews', 'error'); setLoading(false); return; }
    const flat = [];
    (data || []).forEach(c => {
      (c.interview_history || []).forEach(iv => {
        flat.push({
          candidateId: c.id,
          name: c.name,
          job_title: c.job_title,
          interview_type: iv.interview_type,
          interview_status: iv.interview_status,
          interview_duration: iv.interview_duration || 30,
          scheduled_at: iv.scheduled_at || null,
          attendeeName: iv.attendees?.[0]?.name || null,
        });
      });
    });
    setEntries(flat);
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openCandidate = async (candidateId) => {
    const { data } = await supabase
      .from('paradox_candidates')
      .select('id, paradox_oid, paradox_uuid, name, email, phone, job_title, candidate_journey_status, stage_category, paradox_updated_at, status_changed_at, last_synced_at, interview_history, applicants(id)')
      .eq('id', candidateId)
      .single();
    if (data) setSelectedCandidate(data);
  };

  // Paradox uses a couple of generic placeholder labels when no specific
  // person is assigned yet — those are not real interviewers, exclude them.
  const NON_PERSON_ATTENDEE_LABELS = ['Interviewers', 'Interviewer(s) on Job'];
  const interviewers = Array.from(new Set(entries.map(e => e.attendeeName).filter(name => name && !NON_PERSON_ATTENDEE_LABELS.includes(name)))).sort();
  const matchesInterviewer = (e) => interviewerFilter === 'all' || e.attendeeName === interviewerFilter;

  const awaitingScheduling = entries.filter(e => !e.scheduled_at && e.interview_status === 'pending' && matchesInterviewer(e));

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const today = new Date();

  const scheduledInWeek = entries.filter(e => {
    if (!e.scheduled_at || !matchesInterviewer(e)) return false;
    const d = new Date(e.scheduled_at);
    return d >= weekStart && d < new Date(weekEnd.getFullYear(), weekEnd.getMonth(), weekEnd.getDate() + 1);
  });

  // Canceled interviews stay visually muted/struck-through regardless of who
  // they were with -- that status matters more than color-coding a dead
  // slot. Everything else (pending or completed) is colored by interviewer
  // so a glance at the week shows whose schedule is whose.
  const eventStyle = (status, attendeeName) => {
    if (status === 'canceled') return 'bg-red-100 text-red-700 line-through';
    const base = `${colorForInterviewer(attendeeName)} text-white`;
    return status === 'completed' ? `${base} opacity-70` : base;
  };

  const gridHeight = (CALENDAR_END_HOUR - CALENDAR_START_HOUR) * CALENDAR_HOUR_HEIGHT;

  return (
    <div className="max-w-[1600px] mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Interview Calendar</h1>
          <p className="text-sm text-gray-500 mt-1">Synced from Paradox every 30 minutes.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={interviewerFilter}
            onChange={(e) => setInterviewerFilter(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 bg-white max-w-[160px]"
          >
            <option value="all">All Interviewers</option>
            {interviewers.map(name => <option key={name} value={name}>{name}</option>)}
          </select>
          <button
            onClick={() => setWeekStart(startOfWeek(new Date()))}
            className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
          >
            Today
          </button>
          <button
            onClick={() => setWeekStart(prev => { const d = new Date(prev); d.setDate(d.getDate() - 7); return d; })}
            className="p-2 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 transition"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => setWeekStart(prev => { const d = new Date(prev); d.setDate(d.getDate() + 7); return d; })}
            className="p-2 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 transition"
          >
            <ChevronRight size={16} />
          </button>
          <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
            {weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
          <button onClick={load} className="p-2 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 transition">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {interviewers.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {interviewers.map(name => (
            <span key={name} className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className={`w-2.5 h-2.5 rounded-full ${colorForInterviewer(name)}`} />
              {name}
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-gray-200">
            <div />
            {weekDays.map((d, i) => {
              const isToday = d.toDateString() === today.toDateString();
              return (
                <div key={i} className={`text-center py-2 border-l border-gray-100 ${isToday ? 'bg-teal-50' : ''}`}>
                  <p className="text-[10px] font-semibold text-gray-400">{CALENDAR_DAY_LABELS[i]}</p>
                  <p className={`text-sm font-bold ${isToday ? 'text-teal-700' : 'text-gray-900'}`}>{d.getDate()}</p>
                </div>
              );
            })}
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: 600 }}>
            <div className="grid grid-cols-[56px_repeat(7,1fr)] relative" style={{ height: gridHeight }}>
              <div className="relative">
                {Array.from({ length: CALENDAR_END_HOUR - CALENDAR_START_HOUR }, (_, i) => (
                  <div key={i} className="text-[10px] text-gray-400 text-right pr-1.5 -mt-1.5" style={{ position: 'absolute', top: i * CALENDAR_HOUR_HEIGHT, right: 0 }}>
                    {new Date(2000, 0, 1, CALENDAR_START_HOUR + i).toLocaleTimeString('en-US', { hour: 'numeric' })}
                  </div>
                ))}
              </div>
              {weekDays.map((d, dayIdx) => {
                const isToday = d.toDateString() === today.toDateString();
                const rawDayEvents = scheduledInWeek
                  .filter(e => new Date(e.scheduled_at).toDateString() === d.toDateString())
                  .map(e => {
                    const dt = new Date(e.scheduled_at);
                    const startMinutes = dt.getHours() * 60 + dt.getMinutes();
                    return { ...e, dt, startMinutes, endMinutes: startMinutes + (e.interview_duration || 30) };
                  });
                const dayEvents = layoutDayEvents(rawDayEvents);
                return (
                  <div key={dayIdx} className={`relative border-l border-gray-100 ${isToday ? 'bg-teal-50/30' : ''}`}>
                    {Array.from({ length: CALENDAR_END_HOUR - CALENDAR_START_HOUR }, (_, i) => (
                      <div key={i} className="border-t border-gray-100" style={{ position: 'absolute', top: i * CALENDAR_HOUR_HEIGHT, left: 0, right: 0 }} />
                    ))}
                    {dayEvents.map((e, i) => {
                      const hourFraction = e.startMinutes / 60 - CALENDAR_START_HOUR;
                      const top = Math.max(0, hourFraction * CALENDAR_HOUR_HEIGHT);
                      // Capped, not just floored -- a min-height taller than the
                      // real slot would visually bleed into the next event in
                      // the same column when back-to-back interviews are only
                      // ~20 minutes apart.
                      const naturalHeight = ((e.endMinutes - e.startMinutes) / 60) * CALENDAR_HOUR_HEIGHT;
                      const height = Math.max(30, naturalHeight);
                      const widthPct = 100 / e.totalCols;
                      return (
                        <div
                          key={i}
                          onClick={() => openCandidate(e.candidateId)}
                          className={`absolute rounded px-1.5 py-0.5 text-[11px] leading-tight cursor-pointer overflow-hidden hover:opacity-90 transition border border-white/40 ${eventStyle(e.interview_status, e.attendeeName)}`}
                          style={{ top, height, left: `calc(${e.col * widthPct}% + 1px)`, width: `calc(${widthPct}% - 2px)` }}
                          title={`${e.name} · ${e.dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}${e.attendeeName ? ` · with ${e.attendeeName}` : ''}`}
                        >
                          <p className="font-medium truncate">{e.name || '(no name)'}</p>
                          <p className="truncate opacity-80">{e.dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</p>
                          {e.attendeeName && height >= 45 && <p className="truncate opacity-70">w/ {e.attendeeName}</p>}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {awaitingScheduling.length > 0 && (
        <div className="border border-amber-200 rounded-xl overflow-hidden mt-4">
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-200">
            <h2 className="text-sm font-semibold text-amber-800">Awaiting Scheduling ({awaitingScheduling.length})</h2>
          </div>
          <div className="flex flex-wrap gap-2 p-3">
            {awaitingScheduling.map((e, i) => (
              <button
                key={i}
                onClick={() => openCandidate(e.candidateId)}
                className="text-xs bg-white border border-amber-200 text-amber-800 px-2.5 py-1 rounded-full hover:bg-amber-50 transition"
              >
                {e.name || '(no name)'}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedCandidate && (
        <HiringCandidateModal
          candidate={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
          alreadyScheduled={selectedCandidate.applicants?.length > 0}
        />
      )}
    </div>
  );
};

const OrientationGroupsView = ({ profile, showToast }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [form, setForm] = useState({ name: '', office_code: '655', session_datetime: '', zoom_link: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('orientation_groups')
      .select('*, applicants(id, applicant_name, status)')
      .order('session_datetime', { ascending: true });
    setGroups(data || []);
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingGroup(null);
    setForm({ name: '', office_code: '655', session_datetime: '', zoom_link: '' });
    setShowForm(true);
  };

  const openEdit = (g) => {
    setEditingGroup(g);
    setForm({
      name: g.name || '',
      office_code: g.office_code || '655',
      session_datetime: g.session_datetime ? g.session_datetime.slice(0, 16) : '',
      zoom_link: g.zoom_link || '',
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { showToast('Name is required', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        office_code: form.office_code,
        session_datetime: form.session_datetime ? new Date(form.session_datetime).toISOString() : null,
        zoom_link: form.zoom_link.trim() || null,
      };
      if (editingGroup) {
        const { error } = await supabase.from('orientation_groups').update(payload).eq('id', editingGroup.id);
        if (error) throw error;
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        const { error } = await supabase.from('orientation_groups').insert({ ...payload, created_by: user?.id || null });
        if (error) throw error;
      }
      setShowForm(false);
      load();
    } catch (err) {
      console.error('save orientation group error:', err);
      showToast('Failed to save orientation group', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm('Delete this orientation group? Assigned people are not deleted, just unassigned.')) return;
    const { error } = await supabase.from('orientation_groups').delete().eq('id', id);
    if (error) { showToast('Failed to delete group', 'error'); return; }
    load();
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Orientation Groups</h1>
          <p className="text-sm text-gray-500 mt-1">Cohorts sharing one orientation Zoom session.</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 bg-[#2c4f26] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#3a6b32] transition"
        >
          <Plus size={16} /> New Group
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="text-center py-16 text-gray-400">No orientation groups yet.</div>
      ) : (
        <div className="space-y-3">
          {groups.map(g => (
            <div key={g.id} className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold text-gray-900">{g.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {g.session_datetime ? new Date(g.session_datetime).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'No date set'}
                    {g.office_code ? ` · Office ${g.office_code}` : ''}
                    {' · '}{g.applicants?.length || 0} assigned
                  </p>
                  {g.zoom_link && (
                    <a href={g.zoom_link} target="_blank" rel="noreferrer" className="text-xs text-teal-700 hover:underline break-all">{g.zoom_link}</a>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => openEdit(g)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition"><Pencil size={16} /></button>
                  <button onClick={() => deleteGroup(g.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"><Trash2 size={16} /></button>
                </div>
              </div>
              {g.applicants?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap gap-1.5">
                  {g.applicants.map(a => (
                    <span key={a.id} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{a.applicant_name}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-gray-900 mb-4">{editingGroup ? 'Edit Group' : 'New Orientation Group'}</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-700">Group Name</label>
                <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
              </div>
              <div>
                <label className="text-sm text-gray-700">Office</label>
                <select value={form.office_code} onChange={(e) => setForm(f => ({ ...f, office_code: e.target.value }))}
                  className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm bg-white">
                  <option value="655">Manassas</option>
                  <option value="507">Leesburg</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-700">Session Date/Time</label>
                <input type="datetime-local" value={form.session_datetime} onChange={(e) => setForm(f => ({ ...f, session_datetime: e.target.value }))}
                  className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm" />
              </div>
              <div>
                <label className="text-sm text-gray-700">Zoom Link</label>
                <input type="text" value={form.zoom_link} onChange={(e) => setForm(f => ({ ...f, zoom_link: e.target.value }))}
                  placeholder="https://zoom.us/j/…"
                  className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowForm(false)} disabled={saving} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition">Cancel</button>
              <button onClick={save} disabled={saving} className="flex-1 bg-[#2c4f26] text-white py-2 rounded-lg text-sm font-semibold hover:bg-[#3a6b32] disabled:opacity-50 transition">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const HiringPipelineView = ({ profile, showToast, onScheduleOrientation }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);

  const loadCandidates = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('paradox_candidates')
      .select('id, paradox_oid, paradox_uuid, name, email, phone, job_title, candidate_journey_status, stage_category, paradox_updated_at, status_changed_at, last_synced_at, interview_history, applicants(id)')
      .order('status_changed_at', { ascending: true });
    if (error) { showToast('Failed to load hiring pipeline', 'error'); setLoading(false); return; }
    setCandidates(data || []);
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCandidates(); }, []);

  const matchesSearch = (c) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (c.name || '').toLowerCase().includes(term) || (c.job_title || '').toLowerCase().includes(term) || (c.email || '').toLowerCase().includes(term);
  };

  const visibleStages = showAll ? [...HIRING_STAGE_ORDER, 'rejected', 'early_pipeline'] : HIRING_STAGE_ORDER;
  const lastSyncedAt = candidates.reduce((max, c) => (!max || (c.last_synced_at && c.last_synced_at > max)) ? c.last_synced_at : max, null);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hiring Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1">
            Internal tracking only — synced from Paradox every 30 minutes{lastSyncedAt ? ` · last synced ${hiringFmtSyncAgo(lastSyncedAt)}` : ''}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search name, job, email…"
              className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <button
            onClick={() => setShowAll(v => !v)}
            className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${showAll ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >
            {showAll ? 'Showing All' : 'Show Rejected/Earlier'}
          </button>
          <button
            onClick={() => setShowQuestionsModal(true)}
            className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
          >
            Interview Questions
          </button>
          <button onClick={loadCandidates} className="p-2 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 transition">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : (
        <div className="space-y-6">
          {visibleStages.map(stage => {
            const rows = candidates.filter(c => c.stage_category === stage && matchesSearch(c));
            if (rows.length === 0) return null;
            const stallDays = HIRING_STALL_DAYS[stage];
            return (
              <div key={stage} className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                  <h2 className="font-semibold text-gray-900">{HIRING_STAGE_LABELS[stage] || stage}</h2>
                  <span className="text-xs text-gray-400">({rows.length})</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {rows.map(c => {
                    const days = hiringDaysSince(c.status_changed_at);
                    const stalled = stallDays != null && days != null && days >= stallDays;
                    return (
                      <div
                        key={c.id}
                        onClick={() => setSelectedCandidate(c)}
                        className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap cursor-pointer hover:bg-gray-50 transition"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{c.name || '(no name)'}</p>
                          <p className="text-xs text-gray-500 truncate mb-1.5">{c.job_title || '—'} · {c.candidate_journey_status || '—'}</p>
                          <HiringProgressBar status={c.candidate_journey_status} />
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="text-right">
                            <p className={`font-medium ${stalled ? 'text-amber-700' : 'text-gray-700'}`}>{hiringFmtDuration(c.status_changed_at)} in status</p>
                            <p className="text-xs text-gray-400">Last activity {hiringFmtAgo(c.paradox_updated_at)}</p>
                          </div>
                          {stalled && (
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                              <AlertTriangle size={12} /> Stalled
                            </span>
                          )}
                          {stage === 'hired' && !(c.applicants?.length > 0) && (
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                              Ready for Orientation
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {candidates.filter(c => visibleStages.includes(c.stage_category) && matchesSearch(c)).length === 0 && (
            <div className="text-center py-16 text-gray-400">No candidates match.</div>
          )}
        </div>
      )}
      {selectedCandidate && (
        <HiringCandidateModal
          candidate={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
          onScheduleOrientation={onScheduleOrientation}
          alreadyScheduled={selectedCandidate.applicants?.length > 0}
        />
      )}
      {showQuestionsModal && (
        <InterviewQuestionsModal onClose={() => setShowQuestionsModal(false)} showToast={showToast} />
      )}
    </div>
  );
};

// Authenticated-app URL routing -- a tab like Task Board or FAQ gets its own
// bare path (e.g. /taskboard, /faq) that the nav keeps in sync with via
// history.pushState as activeTab changes, so the address bar, back/forward,
// and bookmarks all work normally. "support" maps to /tickets rather than
// /support specifically because /support is already the public customer
// ticket-submission route (see publicRoute parsing above) -- can't reuse it
// for the authenticated Support tab without colliding.
const TAB_SLUGS = {
  onboarding: 'onboarding',
  clients: 'clients',
  wellsky_caregivers: 'carepros',
  templates: 'templates',
  faq: 'faq',
  jotform_submissions: 'forms',
  todos: 'taskboard',
  meeting_agenda: 'meetings',
  phone_screen: 'phone',
  care_consultation: 'consultations',
  care_pro_transfer: 'transfers',
  hiring_pipeline: 'hiring',
  support: 'tickets',
  admin_panel: 'admin',
};
const SLUG_TO_TAB = Object.fromEntries(Object.entries(TAB_SLUGS).map(([k, v]) => [v, k]));
// Same tab -> permission-key mapping as each TABS entry in PortalLayout and
// the admin_staff permission loader below -- kept as one shared constant so
// URL-driven navigation (initial load and browser back/forward) can check
// access without duplicating the list a third time.
const TAB_PERMISSION_MAP = {
  onboarding: 'can_view_onboarding',
  clients: 'can_view_clients',
  wellsky_caregivers: 'can_view_wellsky_caregivers',
  templates: 'can_view_incident_reports',
  faq: 'can_view_faq',
  jotform_submissions: 'can_view_jotform_submissions',
  todos: 'can_view_todos',
  meeting_agenda: 'can_view_meetings',
  phone_screen: 'can_view_phone_screen',
  care_consultation: 'can_view_care_consultation',
  care_pro_transfer: 'can_view_care_pro_transfer',
  hiring_pipeline: 'can_view_hiring_pipeline',
  support: 'can_view_support',
  admin_panel: 'can_view_admin',
};
// Parses a path like "/taskboard" or "/taskboard/<id>" into the tab it
// belongs to plus an optional deep-linked item id -- only used to pick the
// initial activeTab on load; each tab component resolves its own item id
// independently (see TodosTab/AdminTicketsView) rather than this function
// threading it all the way down, since that's simpler than prop-drilling
// through PortalLayout for something only two tabs currently use.
const parseTabPath = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return null;
  const tabId = SLUG_TO_TAB[parts[0]];
  if (!tabId) return null;
  return { tabId, itemId: parts[1] || null };
};

// Main App Component
const OnboardingApp = () => {
  // Public URL routing (ticket pages don't require auth)
  const [publicRoute, setPublicRoute] = useState(null); // null | 'support' | 'ticket' | 'consultation' | 'carepro_application' | 'transfer_letter' | 'privacy' | 'terms'
  const [publicTicketNum, setPublicTicketNum] = useState(null);
  const [publicTicketToken, setPublicTicketToken] = useState(null);
  const [publicConsultationSessionId, setPublicConsultationSessionId] = useState(null);
  const [publicConsultationToken, setPublicConsultationToken] = useState(null);
  const [publicCareProApplicationId, setPublicCareProApplicationId] = useState(null);
  const [publicCareProApplicationToken, setPublicCareProApplicationToken] = useState(null);

  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    if (path === '/support') {
      setPublicRoute('support');
    } else if (path.startsWith('/ticket/')) {
      const num = parseInt(path.replace('/ticket/', ''), 10);
      const tok = params.get('token');
      if (num && tok) { setPublicRoute('ticket'); setPublicTicketNum(num); setPublicTicketToken(tok); }
    } else if (path.startsWith('/consultation/')) {
      const sid = path.replace('/consultation/', '');
      const tok = params.get('token');
      if (sid && tok) { setPublicRoute('consultation'); setPublicConsultationSessionId(sid); setPublicConsultationToken(tok); }
    } else if (path === '/onboarding-application') {
      setPublicRoute('carepro_application');
      // Optional resume link (?id=&token=) staff can send an applicant so
      // they can pick up their progress on any device — plain visits to the
      // bare link fall back to this browser's own localStorage as before.
      const rid = params.get('id');
      const rtok = params.get('token');
      if (rid && rtok) { setPublicCareProApplicationId(rid); setPublicCareProApplicationToken(rtok); }
    } else if (path === '/transfer-letter') {
      setPublicRoute('transfer_letter');
    } else if (path === '/privacy') {
      setPublicRoute('privacy');
    } else if (path === '/terms') {
      setPublicRoute('terms');
    }
  }, []);

  // Authenticated-app tab routing -- captured once on mount (not re-parsed
  // on every render), used below to pick the initial activeTab in place of
  // (or alongside) the localStorage-remembered one. See TAB_SLUGS.
  const [urlTabLink] = useState(() => parseTabPath(window.location.pathname));

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applicants, setApplicants] = useState([]);
  const [currentApplicant, setCurrentApplicant] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [view, setView] = useState('home');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [showAddHireModal, setShowAddHireModal] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '' });
  const [templates, setTemplates] = useState([]);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  // Supabase only fires a distinct PASSWORD_RECOVERY event for type=recovery
  // links — an invite link (type=invite) just fires a normal SIGNED_IN, so
  // without this an invited staff member gets silently logged in with no
  // password ever set, and no way back in once their session ends.
  const [isInviteFlow, setIsInviteFlow] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('portal_active_tab') || null);
  // Browser back/forward doesn't re-render on its own -- re-derive the tab
  // from the URL and re-check permission the same way the initial load does,
  // so Back can't be used to land on a tab the URL-sync effect above would
  // never have navigated to in the first place.
  useEffect(() => {
    const onPopState = () => {
      const parsed = parseTabPath(window.location.pathname);
      if (!parsed?.tabId) return;
      const allowed = profile?.role === 'owner' || !!permissions?.[TAB_PERMISSION_MAP[parsed.tabId]];
      if (!allowed) return;
      setActiveTab(parsed.tabId);
      localStorage.setItem('portal_active_tab', parsed.tabId);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [profile, permissions]);
  const loadedUserId = useRef(null);
  const phoneChannelRef = useRef(null);
  const [activeCalls, setActiveCalls] = useState({});
  const [phoneDeviceMappings, setPhoneDeviceMappings] = useState({});
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [lastPhoneEventAt, setLastPhoneEventAt] = useState(null);
  const [demoCallId, setDemoCallId] = useState(null);
  const [generatedApplicationUrl, setGeneratedApplicationUrl] = useState('');
  const [newHireForm, setNewHireForm] = useState({
  fullName: '',
  email: '',
  phone: '',
  position: '',
  startDate: ''
});

  const showToast = (message) => {
    setToast({ show: true, message });
  };

  const simulatePhoneIncoming = () => {
    const id = `DEMO-${Date.now()}`;
    setDemoCallId(id);
    setActiveCalls(prev => ({
      ...prev,
      [id]: { id, call_id: id, caller_name: 'Demo Caller', caller_number: '5405550199', phone_ip: '192.168.1.29', status: 'ringing', received_at: new Date().toISOString() },
    }));
  };

  const simulatePhoneAnswered = () => {
    if (!demoCallId) return;
    setActiveCalls(prev => ({
      ...prev,
      [demoCallId]: { ...prev[demoCallId], status: 'answered', answered_phone_ip: '192.168.1.29', answered_at: new Date().toISOString() },
    }));
  };

  const clearPhoneDemo = () => {
    if (!demoCallId) return;
    setActiveCalls(prev => { const n = { ...prev }; delete n[demoCallId]; return n; });
    setDemoCallId(null);
  };

  // Phone Realtime subscription — starts when admin/owner is logged in
  useEffect(() => {
    const role = profile?.role;
    const hasPhoneScreenAccess = role === 'owner' || !!permissions?.can_view_phone_screen;
    if (!user || !role || !['owner', 'admin_staff'].includes(role) || !hasPhoneScreenAccess) {
      if (phoneChannelRef.current) {
        supabase.removeChannel(phoneChannelRef.current);
        phoneChannelRef.current = null;
      }
      setRealtimeConnected(false);
      return;
    }

    const loadMappings = async () => {
      const { data } = await supabase.from('phone_device_mappings').select('*').eq('active', true);
      const map = {};
      (data || []).forEach(m => { map[m.phone_ip] = m; });
      setPhoneDeviceMappings(map);
    };

    const loadActiveCalls = async () => {
      const cutoff = new Date(Date.now() - 15 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from('phone_calls').select('*')
        .in('status', ['ringing', 'answered'])
        .gte('received_at', cutoff);
      const map = {};
      (data || []).forEach(c => { map[c.id] = c; });
      setActiveCalls(map);
    };

    loadMappings();
    loadActiveCalls();

    const channel = supabase
      .channel('phone-calls-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'phone_calls' }, (payload) => {
        const call = payload.new;
        if (!call) return;
        setActiveCalls(prev => ({ ...prev, [call.id]: call }));
        setLastPhoneEventAt(new Date());
      })
      .subscribe((status) => setRealtimeConnected(status === 'SUBSCRIBED'));

    phoneChannelRef.current = channel;

    return () => {
      if (phoneChannelRef.current) {
        supabase.removeChannel(phoneChannelRef.current);
        phoneChannelRef.current = null;
      }
      setRealtimeConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, profile?.role, permissions?.can_view_phone_screen]);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) throw error;

      showToast('Password updated successfully!');
      setIsPasswordReset(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.message || 'Failed to update password');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Runs for as long as the app itself is open — not just while the Care
  // Consultation wizard is on screen — so anything saved offline keeps trying
  // to sync even if staff back out to another tab before it catches up.
  useEffect(() => {
    startAutoSync();
    return () => stopAutoSync();
  }, []);

  // If there's no network to fetch it fresh (getSession's token refresh, or the
  // profiles/admin_permissions queries), fall back to whatever was cached from
  // the last successful load rather than hanging on the loading spinner forever.
  const applyCachedProfileFallback = (userId) => {
    const cached = getCachedProfile(userId);
    if (!cached) return false;
    setUser({ id: userId });
    setProfile(cached.profile);
    setPermissions(cached.permissions);
    setIsOfflineMode(true);
    loadedUserId.current = userId;
    setView(cached.profile.role === 'applicant' ? 'applicant' : 'admin');
    setLoading(false);
    return true;
  };

  useEffect(() => {
    const urlToken = new URLSearchParams(window.location.search).get('token');
    if (urlToken) {
      loadApplicantByToken(urlToken);
      return;
    }

    // Read this synchronously, before Supabase's own async hash processing
    // has a chance to run and strip it — captured in this closure so the
    // SIGNED_IN branch below can tell an invite apart from a normal login.
    const isInviteLink = window.location.hash.includes('type=invite');

    withTimeout(supabase.auth.getSession()).then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setLoading(false);
      }
    }).catch(() => {
      const lastUserId = getLastKnownUserId();
      if (!lastUserId || !applyCachedProfileFallback(lastUserId)) {
        setError('Unable to connect. Please check your internet connection and try again.');
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordReset(true);
        setLoading(false);
      } else if (event === 'SIGNED_IN') {
        if (isInviteLink) {
          setIsPasswordReset(true);
          setIsInviteFlow(true);
          setLoading(false);
        }
        if (session.user.id !== loadedUserId.current) {
          loadProfile(session.user.id);
        }
      } else if (event === 'SIGNED_OUT') {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadApplicantByToken = async (token) => {
    try {
      const { data: applicantData, error } = await supabase
        .from('applicants')
        .select('*, tasks(*)')
        .eq('application_token', token)
        .single();

      if (error || !applicantData) {
        setError('Application not found. Please check your link.');
        setLoading(false);
        return;
      }

      const { data: documentsData } = await supabase
        .from('documents')
        .select('*')
        .eq('applicant_id', applicantData.id);

      const tasksWithDocuments = (applicantData.tasks || [])
        .sort((a, b) => a.order_index - b.order_index)
        .map(task => ({
          ...task,
          document: documentsData?.find(d => d.task_id === task.id) || null
        }));

      setCurrentApplicant({
        id: applicantData.id,
        name: applicantData.applicant_name,
        email: applicantData.applicant_email,
        phone: applicantData.applicant_phone,
        position: applicantData.position,
        startDate: applicantData.start_date,
        status: applicantData.status,
        application_token: token,
        tasks: tasksWithDocuments
      });

      setView('applicant');
    } catch (err) {
      setError('Failed to load application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadProfile = async (userId) => {
    let resolvedPermissions = null;
    try {
      console.log('Loading profile for user ID:', userId);

      const { data, error } = await withTimeout(
        supabase.from('profiles').select('*').eq('id', userId).single()
      );

      console.log('Profile data:', data);
      console.log('Profile error:', error);

      if (error) throw error;

      setProfile(data);

      console.log('Profile role:', data.role);

      // Load permissions based on role
      if (data.role === 'owner') {
        // Owners have all permissions
        resolvedPermissions = {
          can_add_hires: true, can_edit_hires: true, can_delete_hires: true, can_manage_templates: true,
          can_view_onboarding: true, can_view_clients: true, can_view_wellsky_caregivers: true, can_view_incident_reports: true, can_view_faq: true, can_create_faq_articles: true, can_edit_faq_articles: true, can_suggest_faq_edits: true, can_delete_faq_articles: true, can_pin_faq_articles: true, can_comment_faq_articles: true, can_view_jotform_submissions: true, can_view_todos: true, can_view_all_todos: true, can_delete_todos: true, can_view_staffing_tab: true, can_view_field_supervisors_tab: true, can_view_meetings: true, can_create_meetings: true, can_manage_all_meetings: true, can_view_carepro: true, can_view_phone_screen: true, can_receive_phone_popups: true,
          can_view_care_consultation: true, can_view_consultations: true, can_edit_consultations: true, can_delete_consultations: true, can_finalize_consultations: true, can_view_consultation_archive: true,
          can_view_care_pro_transfer: true, can_edit_applications: true, can_delete_applications: true,
          can_view_hiring_pipeline: true, can_view_onboarding_dashboard: true, can_view_applicants: true, can_view_orientation_groups: true, can_view_onboarding_templates: true, can_view_activated_insights: true,
          can_view_admin: true, can_invite_staff: true,
          can_view_support: true, can_reply_tickets: true, can_add_internal_notes: true, can_close_tickets: true, can_manage_tickets: true, can_receive_ticket_notifications: true, can_receive_sms_notifications: true,
        };
        setPermissions(resolvedPermissions);
        if (urlTabLink?.tabId) {
          // Owners can see every tab, so no permission check needed here.
          setActiveTab(urlTabLink.tabId);
        } else if (!localStorage.getItem('portal_active_tab')) {
          setActiveTab('onboarding');
          localStorage.setItem('portal_active_tab', 'onboarding');
        }
      } else if (data.role === 'admin_staff') {
        // Fetch permissions for admin staff
        const { data: permsData } = await withTimeout(
          supabase.from('admin_permissions').select('*').eq('user_id', userId).single()
        );

        const resolved = {
          can_add_hires: permsData?.can_add_hires || false,
          can_edit_hires: permsData?.can_edit_hires || false,
          can_delete_hires: permsData?.can_delete_hires || false,
          can_manage_templates: permsData?.can_manage_templates || false,
          can_view_onboarding: permsData?.can_view_onboarding || false,
          can_view_clients: permsData?.can_view_clients || false,
          can_view_wellsky_caregivers: permsData?.can_view_wellsky_caregivers || false,
          can_view_incident_reports: permsData?.can_view_incident_reports || false,
          can_view_faq: permsData?.can_view_faq || false,
          can_create_faq_articles: permsData?.can_create_faq_articles || false,
          can_edit_faq_articles: permsData?.can_edit_faq_articles || false,
          can_suggest_faq_edits: permsData?.can_suggest_faq_edits || false,
          can_delete_faq_articles: permsData?.can_delete_faq_articles || false,
          can_pin_faq_articles: permsData?.can_pin_faq_articles || false,
          can_comment_faq_articles: permsData?.can_comment_faq_articles || false,
          can_view_jotform_submissions: permsData?.can_view_jotform_submissions || false,
          can_view_todos: permsData?.can_view_todos || false,
          can_view_all_todos: permsData?.can_view_all_todos || false,
          can_delete_todos: permsData?.can_delete_todos || false,
          can_view_staffing_tab: permsData?.can_view_staffing_tab || false,
          can_view_field_supervisors_tab: permsData?.can_view_field_supervisors_tab || false,
          can_view_meetings: permsData?.can_view_meetings || false,
          can_create_meetings: permsData?.can_create_meetings || false,
          can_manage_all_meetings: permsData?.can_manage_all_meetings || false,
          can_view_carepro: permsData?.can_view_carepro || false,
          can_view_phone_screen: permsData?.can_view_phone_screen || false,
          can_receive_phone_popups: permsData?.can_receive_phone_popups || false,
          can_view_care_consultation: permsData?.can_view_care_consultation || false,
          can_view_consultations: permsData?.can_view_consultations || false,
          can_edit_consultations: permsData?.can_edit_consultations || false,
          can_delete_consultations: permsData?.can_delete_consultations || false,
          can_finalize_consultations: permsData?.can_finalize_consultations || false,
          can_view_consultation_archive: permsData?.can_view_consultation_archive || false,
          can_view_care_pro_transfer: permsData?.can_view_care_pro_transfer || false,
          can_edit_applications: permsData?.can_edit_applications || false,
          can_delete_applications: permsData?.can_delete_applications || false,
          can_view_hiring_pipeline: permsData?.can_view_hiring_pipeline || false,
          can_view_onboarding_dashboard: permsData?.can_view_onboarding_dashboard || false,
          can_view_applicants: permsData?.can_view_applicants || false,
          can_view_orientation_groups: permsData?.can_view_orientation_groups || false,
          can_view_onboarding_templates: permsData?.can_view_onboarding_templates || false,
          can_view_activated_insights: permsData?.can_view_activated_insights || false,
          can_view_admin: permsData?.can_view_admin || false,
          can_invite_staff: permsData?.can_invite_staff || false,
          can_view_support: permsData?.can_view_support || false,
          can_reply_tickets: permsData?.can_reply_tickets || false,
          can_add_internal_notes: permsData?.can_add_internal_notes || false,
          can_close_tickets: permsData?.can_close_tickets || false,
          can_manage_tickets: permsData?.can_manage_tickets || false,
          can_receive_ticket_notifications: permsData?.can_receive_ticket_notifications || false,
          can_receive_sms_notifications: permsData?.can_receive_sms_notifications || false,
          notify_categories: permsData?.notify_categories || [],
        };
        setPermissions(resolved);
        resolvedPermissions = resolved;

        const tabOrder = ['onboarding', 'clients', 'wellsky_caregivers', 'templates', 'faq', 'jotform_submissions', 'todos', 'meeting_agenda', 'phone_screen', 'care_consultation', 'care_pro_transfer', 'hiring_pipeline', 'support', 'admin_panel'];
        const permMap = {
          onboarding: 'can_view_onboarding',
          clients: 'can_view_clients',
          wellsky_caregivers: 'can_view_wellsky_caregivers',
          templates: 'can_view_incident_reports',
          faq: 'can_view_faq',
          jotform_submissions: 'can_view_jotform_submissions',
          todos: 'can_view_todos',
          meeting_agenda: 'can_view_meetings',
          phone_screen: 'can_view_phone_screen',
          care_consultation: 'can_view_care_consultation',
          care_pro_transfer: 'can_view_care_pro_transfer',
          hiring_pipeline: 'can_view_hiring_pipeline',
          support: 'can_view_support',
          admin_panel: 'can_view_admin',
        };
        const firstTab = tabOrder.find(t => resolved[permMap[t]]) || null;
        const savedTab = localStorage.getItem('portal_active_tab');
        const validSaved = savedTab && tabOrder.includes(savedTab) && resolved[permMap[savedTab]] ? savedTab : null;
        const validUrlTab = urlTabLink?.tabId && tabOrder.includes(urlTabLink.tabId) && resolved[permMap[urlTabLink.tabId]] ? urlTabLink.tabId : null;
        if (urlTabLink?.tabId && !validUrlTab) {
          // They landed on a URL for a tab they don't have permission for --
          // clean the address bar back to / rather than leave it claiming a
          // page they can't actually see.
          window.history.replaceState({}, '', '/');
        }
        if (validUrlTab) {
          setActiveTab(validUrlTab);
        } else if (!validSaved) {
          setActiveTab(firstTab);
          if (firstTab) localStorage.setItem('portal_active_tab', firstTab);
        }
      }

      loadedUserId.current = userId;
      setIsOfflineMode(false);
      if (resolvedPermissions) saveCachedProfile(userId, data, resolvedPermissions);

      if (data.role === 'applicant') {
        loadApplicantData(userId);
        setView('applicant');
      } else {
        console.log('Loading admin data...');
        loadAdminData();
        loadTemplates();
        setView('admin');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading profile:', error);
      if (applyCachedProfileFallback(userId)) return;
      setError('Profile not found. Please create a profile in the profiles table.');
      setLoading(false);
    }
  };

  const loadApplicantData = async (userId) => {
    try {
      const { data: applicantData, error: applicantError } = await supabase
        .from('applicants')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (applicantError) throw applicantError;

      const { data: tasksData, error: tasksError } = await supabase
        .from('tasks')
        .select('*')
        .eq('applicant_id', applicantData.id)
        .order('order_index');

      if (tasksError) throw tasksError;

      // Fetch documents for each task
      const { data: documentsData, error: documentsError } = await supabase
        .from('documents')
        .select('*')
        .eq('applicant_id', applicantData.id);

      if (documentsError) throw documentsError;

      // Attach documents to their respective tasks
      const tasksWithDocuments = tasksData.map(task => ({
        ...task,
        document: documentsData?.find(doc => doc.task_id === task.id) || null
      }));

      setCurrentApplicant({ ...applicantData, tasks: tasksWithDocuments });
    } catch (error) {
      console.error('Error loading applicant data:', error);
    }
  };

  const loadAdminData = async () => {
    try {
      const { data: applicantsData, error: applicantsError } = await supabase
        .from('applicants')
        .select(`
          *,
          profile:user_id (full_name, email, phone),
          tasks (*)
        `)
        .order('created_at', { ascending: false });

      if (applicantsError) throw applicantsError;

      // Fetch all documents
      const { data: documentsData, error: documentsError } = await supabase
        .from('documents')
        .select('*');

      if (documentsError) throw documentsError;

      const formattedApplicants = applicantsData.map(app => {
        // Attach documents to their respective tasks
        const tasksWithDocuments = app.tasks
          .sort((a, b) => a.order_index - b.order_index)
          .map(task => ({
            ...task,
            document: documentsData?.find(doc => doc.task_id === task.id) || null
          }));

        return {
          id: app.id,
          name: app.applicant_name || app.profile?.full_name || 'Unknown',
          email: app.applicant_email || app.profile?.email || '',
          phone: app.applicant_phone || app.profile?.phone || '',
          position: app.position,
          startDate: app.start_date,
          status: app.status,
          created_at: app.created_at,
          approved_at: app.approved_at,
          application_token: app.application_token || null,
          source: app.source,
          paradox_candidate_id: app.paradox_candidate_id,
          orientation_group_id: app.orientation_group_id,
          drug_test_status: app.drug_test_status,
          ai_training_complete: app.ai_training_complete,
          tasks: tasksWithDocuments
        };
      });

      setApplicants(formattedApplicants);

      if (profile?.role === 'owner') {
        loadActivityLog();
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  const loadActivityLog = async () => {
    try {
      const { data, error } = await supabase
        .from('activity_log')
        .select('*, profile:user_id(full_name)')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setActivityLog(data);
    } catch (error) {
      console.error('Error loading activity log:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('task_templates')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const toggleTask = async (taskId, currentStatus) => {
    // Prevent applicants from modifying tasks after approval
    if (profile.role === 'applicant' && currentApplicant?.status === 'approved') {
      showToast('Tasks cannot be modified after approval');
      return;
    }

    try {
      const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';

      // Optimistically update the UI first
      if (selectedApplicant) {
        const updatedTasks = selectedApplicant.tasks.map(task =>
          task.id === taskId ? { ...task, status: newStatus } : task
        );
        setSelectedApplicant({ ...selectedApplicant, tasks: updatedTasks });
      }

      // Update the database
      const { error } = await supabase
        .from('tasks')
        .update({ status: newStatus })
        .eq('id', taskId);

      if (error) throw error;

      // Refresh the data
      if (currentApplicant?.application_token) {
        loadApplicantByToken(currentApplicant.application_token);
      } else if (profile?.role === 'applicant') {
        loadApplicantData(user.id);
      } else {
        loadAdminData();
      }
    } catch (error) {
      console.error('Error toggling task:', error);
      if (currentApplicant?.application_token) {
        loadApplicantByToken(currentApplicant.application_token);
      } else if (profile?.role === 'applicant') {
        loadApplicantData(user.id);
      } else {
        loadAdminData();
      }
    }
  };

  const uploadDocument = async (file, taskId) => {
    // Prevent document uploads after approval
    if (currentApplicant?.status === 'approved') {
      showToast('Documents cannot be uploaded after approval');
      return;
    }

    try {
      const fileExt = file.name.split('.').pop();

      // Get the task title for the filename
      const task = currentApplicant.tasks.find(t => t.id === taskId);
      const taskTitle = task?.title || 'Document';

      // Parse applicant name (assumes "First Last" format)
      const applicantName = currentApplicant?.name || profile?.full_name || '';
      const nameParts = applicantName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join('') || firstName;

      const sanitize = (str) => str.replace(/[^a-zA-Z0-9]/g, '');
      const formattedName = `${sanitize(lastName)}${sanitize(firstName)}.${sanitize(taskTitle)}`;

      const storagePath = `${user?.id || currentApplicant.id}/${formattedName}.${fileExt}`;
      // Display name shown to users
      const displayName = `${formattedName}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('applicant-documents')
        .upload(storagePath, file);

      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase
        .from('documents')
        .insert({
          applicant_id: currentApplicant.id,
          task_id: taskId,
          file_name: displayName,
          file_path: storagePath,
          file_size: file.size,
          file_type: file.type
        });

      if (dbError) throw dbError;

      // Auto-complete the task after successful document upload
      const { error: taskError } = await supabase
        .from('tasks')
        .update({ status: 'completed' })
        .eq('id', taskId);

      if (taskError) throw taskError;

      showToast('Document uploaded successfully!');

      if (currentApplicant?.application_token) {
        loadApplicantByToken(currentApplicant.application_token);
      } else {
        loadApplicantData(user.id);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setError('Error uploading document: ' + error.message);
    }
  };

  const handleCloseModal = () => {
    setShowAddHireModal(false);
    setGeneratedApplicationUrl('');
    setError('');
    setNewHireForm({ fullName: '', email: '', phone: '', position: '', startDate: '' });
  };
// ...

// Moves a Paradox candidate into the Orientation phase: creates a real
// applicant row (token-based, same shape as a manually-added hire) and
// clones the current task_templates onto it, exactly like handleAddNewHire
// below. A unique index on applicants.paradox_candidate_id means a second
// attempt for the same candidate fails loudly instead of duplicating them.
const scheduleIntoOrientation = async (candidate) => {
  try {
    const token = generateUUID();
    const { data: newApplicant, error: applicantError } = await supabase
      .from('applicants')
      .insert({
        applicant_name: candidate.name || 'Unknown',
        applicant_email: candidate.email || null,
        applicant_phone: candidate.phone || null,
        position: candidate.job_title || 'Care Pro',
        start_date: new Date().toISOString().slice(0, 10),
        status: 'in_progress',
        application_token: token,
        source: 'paradox',
        paradox_candidate_id: candidate.id,
      })
      .select()
      .single();
    if (applicantError) {
      if (applicantError.code === '23505') {
        showToast('This candidate has already been scheduled into orientation', 'error');
        return;
      }
      throw applicantError;
    }

    if (templates.length > 0) {
      const taskInserts = templates.map(t => ({
        applicant_id: newApplicant.id,
        title: t.title,
        description: t.description || null,
        status: 'pending',
        allow_upload: t.allow_upload || false,
        is_admin_task: t.is_admin_task || false,
        order_index: t.order_index ?? 0,
      }));
      const { error: tasksError } = await supabase.from('tasks').insert(taskInserts);
      if (tasksError) throw tasksError;
    }

    await loadAdminData();
    showToast(`${candidate.name || 'Candidate'} scheduled into orientation`);
    setActiveTab('onboarding');
    setView('applicants');
  } catch (err) {
    console.error('scheduleIntoOrientation error:', err);
    showToast('Failed to schedule into orientation', 'error');
  }
};

const handleAddNewHire = async (e) => {
  e?.preventDefault?.();
  try {
    setError('');

    const fullName = (newHireForm.fullName || '').trim();
    const email = (newHireForm.email || '').trim();
    const phone = (newHireForm.phone || '').trim();
    const position = (newHireForm.position || '').trim();
    const startDate = (newHireForm.startDate || '').trim();

    if (!fullName || !email || !position || !startDate) {
      setError('Please fill out Full Name, Email, Position, and Start Date.');
      return;
    }

    const token = generateUUID();

    const { data: newApplicant, error: applicantError } = await supabase
      .from('applicants')
      .insert({
        applicant_name: fullName,
        applicant_email: email,
        applicant_phone: phone || null,
        position,
        start_date: startDate,
        status: 'in_progress',
        application_token: token,
      })
      .select()
      .single();

    if (applicantError) throw applicantError;

    if (templates.length > 0) {
      const taskInserts = templates.map(t => ({
        applicant_id: newApplicant.id,
        title: t.title,
        description: t.description || null,
        status: 'pending',
        allow_upload: t.allow_upload || false,
        is_admin_task: t.is_admin_task || false,
        order_index: t.order_index ?? 0,
      }));
      const { error: tasksError } = await supabase.from('tasks').insert(taskInserts);
      if (tasksError) throw tasksError;
    }

    await loadAdminData();

    const appUrl = `${window.location.origin}${window.location.pathname}?token=${token}`;
    setGeneratedApplicationUrl(appUrl);
  } catch (err) {
    console.error('handleAddNewHire error:', err);
    setError(err?.message || 'Something went wrong while adding the new hire.');
  }
};



  const handleLogin = async (email, password) => {
    setError('');
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      // Update last sign-in timestamp on the profile
      const { data: { user: u } } = await supabase.auth.getUser();
      if (u) await supabase.from('profiles').update({ last_sign_in_at: new Date().toISOString() }).eq('id', u.id);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleSignUp = async (fullName, email, password) => {
    // This project requires email confirmation, so signUp() never returns a
    // session for a brand-new user — there's no way for the browser itself
    // to then write role/status onto that user's own profile (RLS needs
    // auth.uid() = id, and there's no session yet to satisfy that). A DB
    // trigger auto-creates the profiles row regardless, always as role
    // 'applicant' with no approval gate — caregiver-signup-finalize
    // (service role) turns that into the intended pending-caregiver state
    // instead. Previously this tried to write the role directly from here
    // and silently failed every time, leaving real accounts stuck as fully
    // active, ungated "applicant" logins nobody ever reviewed.
    const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: fullName } } });
    if (error) throw error;
    if (!data.user) throw new Error('Sign up failed. Please try again.');
    const { data: finalizeData, error: finalizeError } = await supabase.functions.invoke('caregiver-signup-finalize', {
      body: { userId: data.user.id, fullName },
    });
    if (finalizeError || !finalizeData?.success) throw new Error('Account created but profile setup failed. Please contact support.');
    // Sign out immediately — account needs approval before access
    await supabase.auth.signOut();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setView('home');
    setProfile(null);
    setApplicants([]);
    setCurrentApplicant(null);
    setPermissions(null);
    setActiveTab(null);
    localStorage.removeItem('portal_active_tab');
    loadedUserId.current = null;
    setSelectedApplicant(null);
  };

  // Public routes — render before auth check
  if (publicRoute === 'support') return <SupportPage />;
  if (publicRoute === 'ticket') return <CustomerTicketView ticketNumber={publicTicketNum} token={publicTicketToken} />;
  if (publicRoute === 'consultation') return <RemoteConsultationView sessionId={publicConsultationSessionId} token={publicConsultationToken} />;
  if (publicRoute === 'carepro_application') return <CareProApplicationView resumeId={publicCareProApplicationId} resumeToken={publicCareProApplicationToken} />;
  if (publicRoute === 'transfer_letter') return <TransferLetterPage />;
  if (publicRoute === 'privacy') return <PrivacyPolicyPage />;
  if (publicRoute === 'terms') return <TermsPage />;

  if (loading) {
    return (
      <>
        <Toast message={toast.message} show={toast.show} onClose={() => setToast({ show: false, message: '' })} />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#c5d8c3] border-t-[#2c4f26] mx-auto"></div>
            <p className="mt-4 text-gray-600 font-medium">Loading...</p>
          </div>
        </div>
      </>
    );
  }

  if (isPasswordReset) {
    return (
      <>
        <Toast message={toast.message} show={toast.show} onClose={() => setToast({ show: false, message: '' })} />
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-md border border-gray-100">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <img
                  src={HiLargeLogo}
                  alt="Home Instead Logo"
                  className="h-24 w-auto object-contain"
                />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">{isInviteFlow ? 'Welcome — Set Your Password' : 'Set New Password'}</h1>
              <p className="text-gray-700 mt-2">{isInviteFlow ? "You've been invited to Home Instead Portal — create a password to get started." : 'Enter your new password below'}</p>
            </div>

            <form onSubmit={handleUpdatePassword} className="space-y-5">
              {passwordError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  {passwordError}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2c4f26] focus:border-transparent transition"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>

              <button
                type="submit"
                disabled={isUpdatingPassword}
                className="w-full bg-[#2c4f26] text-white py-3.5 rounded-xl font-semibold hover:bg-[#1f3a1c] transition shadow-lg shadow-[#2c4f26]/30 disabled:opacity-50"
              >
                {isUpdatingPassword ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      </>
    );
  }

  if (!user) {
    if (view === 'newApplicant') {
      return (
        <>
          <Toast message={toast.message} show={toast.show} onClose={() => setToast({ show: false, message: '' })} />
          <NewApplicantFlow
            onBack={() => setView('home')}
            onComplete={() => setView('home')}
            showToast={showToast}
          />
        </>
      );
    }

    if (view === 'login') {
      return (
        <>
          <Toast message={toast.message} show={toast.show} onClose={() => setToast({ show: false, message: '' })} />
          <LoginScreen
            onSubmit={handleLogin}
            onSignUp={handleSignUp}
            onNewApplicant={() => setView('newApplicant')}
            onBack={() => setView('home')}
            error={error}
          />
        </>
      );
    }

    return (
      <>
        <Toast message={toast.message} show={toast.show} onClose={() => setToast({ show: false, message: '' })} />
        <HomePage onLogin={() => setView('login')} />
      </>
    );
  }

  // Caregiver pending/denied screens (logged in but not yet approved)
  if (profile?.role === 'caregiver' && profile?.status !== 'active') {
    const isPending = profile.status === 'pending' || !profile.status;
    return (
      <>
        <Toast message={toast.message} show={toast.show} onClose={() => setToast({ show: false, message: '' })} />
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-md border border-gray-100 text-center">
            <img src={HiLargeLogo} alt="Home Instead Logo" className="h-20 w-auto object-contain mx-auto mb-6" />
            {isPending ? (
              <>
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Account Pending Approval</h2>
                <p className="text-gray-600 text-sm">Your account is awaiting administrator approval. You'll have access to the portal once approved.</p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X size={32} className="text-red-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
                <p className="text-gray-600 text-sm">Your access request was not approved. Please contact an administrator if you believe this is an error.</p>
              </>
            )}
            <button onClick={handleLogout} className="mt-8 text-sm text-gray-500 hover:text-gray-700 font-medium">Sign Out</button>
          </div>
        </div>
      </>
    );
  }

  if (view === 'applicant' && profile?.role === 'applicant') {
    return (
      <>
        <Toast message={toast.message} show={toast.show} onClose={() => setToast({ show: false, message: '' })} />
        <ApplicantPortal
          currentApplicant={currentApplicant}
          profile={profile}
          toggleTask={toggleTask}
          uploadDocument={uploadDocument}
          handleLogout={handleLogout}
          setView={setView}
        />
      </>
    );
  }

  if (profile?.role === 'admin_staff' || profile?.role === 'owner') {
    const renderOnboardingContent = () => {
      // `view` can land on a sub-tab the current profile isn't permitted to
      // see -- its initial default ('home') and 'admin' (also used as the
      // post-selection landing spot after viewing an applicant) both fall
      // through to the AdminPortal/Dashboard branch below with no
      // permission check of its own, so simply clicking the top-level
      // Onboarding tab always showed Dashboard regardless of whether this
      // profile actually has can_view_onboarding_dashboard. Redirect to the
      // first sub-tab this profile does have instead of silently showing
      // one they don't.
      const canSeeOnboardingView = (v) => {
        if (profile?.role === 'owner') return true;
        if (v === 'admin') return !!permissions?.can_view_onboarding_dashboard;
        if (v === 'applicants') return !!permissions?.can_view_applicants;
        if (v === 'hiring_pipeline' || v === 'interview_calendar') return !!permissions?.can_view_hiring_pipeline;
        if (v === 'orientation_groups') return !!permissions?.can_view_orientation_groups;
        if (v === 'templates') return !!permissions?.can_view_onboarding_templates;
        if (v === 'activated_insights') return !!permissions?.can_view_activated_insights;
        return true; // unrecognized values (e.g. a future view) aren't blocked here
      };
      const ONBOARDING_VIEW_FALLBACK_ORDER = ['admin', 'applicants', 'hiring_pipeline', 'orientation_groups', 'templates', 'activated_insights'];
      const effectiveView = (selectedApplicant || canSeeOnboardingView(view))
        ? view
        : (ONBOARDING_VIEW_FALLBACK_ORDER.find(canSeeOnboardingView) || view);

      if (effectiveView === 'hiring_pipeline') {
        return <HiringPipelineView profile={profile} showToast={showToast} onScheduleOrientation={scheduleIntoOrientation} />;
      }
      if (effectiveView === 'orientation_groups') {
        return <OrientationGroupsView profile={profile} showToast={showToast} />;
      }
      if (effectiveView === 'interview_calendar') {
        return <InterviewCalendarView profile={profile} showToast={showToast} />;
      }
      if (effectiveView === 'applicants') {
        return (
          <ApplicantsView
            applicants={applicants}
            setView={setView}
            handleLogout={handleLogout}
            onSelectApplicant={(applicant) => {
              setSelectedApplicant(applicant);
              setView('admin');
            }}
            profile={profile}
          />
        );
      }
      if (effectiveView === 'templates') {
        return (
          <TemplatesView
            templates={templates}
            setTemplates={setTemplates}
            setView={setView}
            handleLogout={handleLogout}
            showToast={showToast}
            profile={profile}
            permissions={permissions}
          />
        );
      }
      if (effectiveView === 'activated_insights') {
        return <ActivatedInsightsView showToast={showToast} profile={profile} />;
      }
      if (effectiveView === 'analytics' && profile?.role === 'owner') {
        return <AnalyticsView activityLog={activityLog} applicants={applicants} setView={setView} handleLogout={handleLogout} />;
      }
      if (selectedApplicant) {
        return (
          <ApplicantDetailView
            applicant={selectedApplicant}
            profile={profile}
            permissions={permissions}
            onBack={() => setSelectedApplicant(null)}
            toggleTask={toggleTask}
            handleLogout={handleLogout}
            onUpdate={async () => {
              await loadAdminData();
              // Was joining profile:user_id — every hire this system actually
              // creates goes through the token-based flow (user_id is null),
              // so that join always came back null and this crashed on
              // data.profile.full_name. Applicant contact info lives directly
              // on the row (applicant_name/email/phone) regardless of source.
              const { data } = await supabase
                .from('applicants')
                .select(`*, tasks (*)`)
                .eq('id', selectedApplicant.id)
                .single();
              if (data) {
                const { data: docs } = await supabase.from('documents').select('*').eq('applicant_id', data.id);
                const tasksWithDocs = data.tasks.sort((a, b) => a.order_index - b.order_index).map(t => ({
                  ...t,
                  document: docs?.find(d => d.task_id === t.id) || null
                }));
                setSelectedApplicant({
                  id: data.id,
                  name: data.applicant_name,
                  email: data.applicant_email,
                  phone: data.applicant_phone,
                  position: data.position,
                  startDate: data.start_date,
                  status: data.status,
                  created_at: data.created_at,
                  approved_at: data.approved_at,
                  source: data.source,
                  paradox_candidate_id: data.paradox_candidate_id,
                  orientation_group_id: data.orientation_group_id,
                  drug_test_status: data.drug_test_status,
                  ai_training_complete: data.ai_training_complete,
                  tasks: tasksWithDocs
                });
              }
            }}
            showToast={showToast}
          />
        );
      }
      return (
        <>
          <AdminPortal
            applicants={applicants}
            profile={profile}
            permissions={permissions}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setView={setView}
            setShowAddHireModal={setShowAddHireModal}
            setSelectedApplicant={setSelectedApplicant}
            handleLogout={handleLogout}
          />
          <AddHireModal
            show={showAddHireModal}
            onClose={handleCloseModal}
            onSubmit={handleAddNewHire}
            formData={newHireForm}
            setFormData={setNewHireForm}
            error={error}
            applicationUrl={generatedApplicationUrl}
            onDone={handleCloseModal}
          />
        </>
      );
    };

    const renderTabContent = () => {
      if (activeTab === 'onboarding') return renderOnboardingContent();
      if (activeTab === 'clients') {
        if (view === 'client_tasks') return <ClientTasksView showToast={showToast} />;
        if (view === 'visit_planner') return <VisitPlannerView showToast={showToast} />;
        return <ClientsTab showToast={showToast} />;
      }
      if (activeTab === 'wellsky_caregivers') return <WellskyCaregiversTab showToast={showToast} />;
      if (activeTab === 'templates') return <ActionTemplatesTab showToast={showToast} profile={profile} permissions={permissions} />;
      if (activeTab === 'faq') return <FaqTab showToast={showToast} profile={profile} permissions={permissions} />;
      if (activeTab === 'jotform_submissions') return <JotformSubmissionsTab showToast={showToast} profile={profile} />;
      if (activeTab === 'todos') return <TodosTab showToast={showToast} profile={profile} permissions={permissions} />;
      if (activeTab === 'meeting_agenda') return <MeetingAgendaTab showToast={showToast} profile={profile} permissions={permissions} />;
      if (activeTab === 'phone_screen') return (
        <PhoneScreenTab
          activeCalls={activeCalls}
          deviceMappings={phoneDeviceMappings}
          realtimeConnected={realtimeConnected}
          lastPhoneEventAt={lastPhoneEventAt}
          profile={profile}
          demoCallId={demoCallId}
          onSimulateIncoming={simulatePhoneIncoming}
          onSimulateAnswered={simulatePhoneAnswered}
          onClearDemo={clearPhoneDemo}
        />
      );
      if (activeTab === 'care_consultation') return <CareConsultationTab profile={profile} permissions={permissions} showToast={showToast} />;
      if (activeTab === 'care_pro_transfer') return <CareProTransferTab profile={profile} permissions={permissions} showToast={showToast} />;
      if (activeTab === 'support') return <AdminTicketsView profile={profile} permissions={permissions} showToast={showToast} />;
      if (activeTab === 'admin_panel') return <AdminManagementView setView={setView} handleLogout={handleLogout} showToast={showToast} profile={profile} permissions={permissions} />;
      return (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Lock size={40} className="text-gray-300" />
            </div>
            <h2 className="text-xl font-bold text-gray-700 mb-2">No Access</h2>
            <p className="text-gray-600 max-w-sm">You don't have permission to view any sections. Contact an administrator to request access.</p>
          </div>
        </div>
      );
    };

    return (
      <>
        <Toast message={toast.message} show={toast.show} onClose={() => setToast({ show: false, message: '' })} />
        <PortalLayout
          profile={profile}
          permissions={permissions}
          isOfflineMode={isOfflineMode}
          activeTab={activeTab}
          setActiveTab={(tab) => {
            setActiveTab(tab);
            localStorage.setItem('portal_active_tab', tab);
            setSelectedApplicant(null);
            setView('admin');
            const slug = TAB_SLUGS[tab];
            if (slug) window.history.pushState({}, '', `/${slug}`);
          }}
          view={view}
          setView={setView}
          setSelectedApplicant={setSelectedApplicant}
          handleLogout={handleLogout}
        >
          {renderTabContent()}
          {permissions?.can_view_phone_screen && permissions?.can_receive_phone_popups && (
            <LiveCallPopup activeCalls={activeCalls} deviceMappings={phoneDeviceMappings} />
          )}
        </PortalLayout>
      </>
    );
  }

  // Profile still loading after login — show spinner instead of flashing "Access denied"
  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#c5d8c3] border-t-[#2c4f26]" />
      </div>
    );
  }

  return (
    <>
      <Toast message={toast.message} show={toast.show} onClose={() => setToast({ show: false, message: '' })} />
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Access denied or profile not configured</p>
          <button onClick={handleLogout} className="mt-4 text-[#2c4f26] hover:text-[#1f3a1c]">
            Back to login
          </button>
        </div>
      </div>
    </>
  );
};

export default OnboardingApp;
